/**
 * GarageIQ — TJ Wrangler Guide Data Library
 *
 * 2003-2006 Jeep Wrangler TJ Repair & Maintenance Guides
 *
 * This file contains all repair and maintenance guides for the 2003-2006 (TJ2) Jeep Wrangler,
 * covering both 2.4L I4 and 4.0L inline-6 engines with manual and automatic transmissions.
 *
 * Guide generation follows the Automotive Repair Guide Generation System v9:
 * - Fact extraction phase: Technical specifications verified through multiple sources
 * - Procedure research phase: Independent research through technician forums, YouTube, NHTSA databases
 * - Synthesis phase: Original expression through 3-pass method (facts → understanding → synthesis)
 * - Human editorial review and verification before publication
 *
 * All published guides are original works created through independent research and synthesis.
 */

export const tjGuidesP1 = [
  // ═══════════════════════════════════════════════════════════════════════════════
  // P1 — CRITICAL SAFETY REPAIRS
  // ═══════════════════════════════════════════════════════════════════════════════

  {
    id: 'tj-front-brake-pads',
    title: 'Front Brake Pad Replacement',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [
        { startYear: 2003, endYear: 2006 }
      ],
    },
    video: {
      youtubeId: 'gm-S_QA_EfQ',
      title: 'How to Replace Front Brakes 1997-2006 Jeep Wrangler',
      channel: 'TRQ',
    },
    source: 'Original guide created through independent research, technician forums, YouTube tutorials, and NHTSA databases. Verified through the Automotive Repair Guide Generation System v9 methodology.',
    category: 'Brakes & Steering',
    difficulty: 'beginner',
    duration: '45 min',
    interval: 'Every 25,000-50,000 miles (as needed)',
    description:
      'Replace worn front brake pads to maintain stopping power and prevent rotor damage. This is a foundational brake maintenance task that every Wrangler owner should be comfortable performing. Front brakes do 60-70% of the stopping work, making their condition critical for safety.',
    overview: {
      difficulty: 'Beginner-level mechanical work suitable for first-time DIYers',
      estimatedTime: '45 minutes to 1 hour',
      estimatedCost: '$80-150 (pads) vs $250+ shop labor',
      whyMatters: 'Worn brake pads reduce stopping power and accelerate rotor wear, creating a much more expensive repair. Brake failure creates immediate safety risk.',
      safetyNote: 'Never drive with brake warning light on. Always work with vehicle on jack stands, not just a floor jack.',
    },
    tools: [
      '17mm socket wrench (caliper bolt removal)',
      'C-clamp or old brake pad (to retract piston)',
      'Brake fluid (DOT 3, for bleeding if needed)',
      'Torque wrench (to spec calipers)',
      'Jack and jack stands',
      'Lug wrench for wheel removal',
    ],
    parts: [
      {
        id: 'semi-metallic-pads-front',
        name: 'Front Brake Pad Set',
        partNumber: 'OEM-specific (see notes)',
        quantity: 1,
        price: 45,
        category: 'required',
        notes: 'Mopar OEM part number will be shown once configuration confirmed. Aftermarket options from Akebono, Wagner, or Centric available at $40-80 per set.',
      },
      {
        id: 'brake-fluid-dot3',
        name: 'DOT 3 Brake Fluid (if bleeding needed)',
        partNumber: 'ATE or Valvoline',
        quantity: 0.5,
        price: 8,
        category: 'optional',
        notes: 'Only needed if brake lines are opened. Keep cap on fluid when not in use—DOT 3 absorbs moisture.',
      },
    ],
    safetyInformation:
      'Vehicle repair involves inherent risks including property damage, personal injury, and in extreme cases death. This guide is provided for informational and educational purposes only and does not constitute professional mechanical advice. Always work in a safe environment with the vehicle on stable ground and properly supported on jack stands. Never work under a vehicle supported only by a floor jack. Wear appropriate personal protective equipment. Disconnect the battery before working on electrical components. Allow the engine and brakes to cool completely before working. Never ignore warning lights or unusual vehicle behavior.',
    beforeYouStart: [
      'Pump the brake pedal 10 times with the engine off to release system pressure',
      'Ensure vehicle is on level ground and parked in a safe location',
      'Gather all tools and parts before starting',
      'Wear safety glasses to protect from dust and debris',
      'Allow at least 30 minutes for brakes to cool after driving',
    ],
    steps: [
      {
        number: 1,
        title: 'Raise vehicle and secure on jack stands',
        description:
          'Position vehicle on level ground. Using a floor jack, carefully lift the front end. Once lifted, place jack stands under the frame rails—never rely on a floor jack alone. Lower the vehicle onto the stands slowly. Verify stands are fully supporting the weight before proceeding.',
        why: 'A floor jack can fail or slip, causing the vehicle to fall. Jack stands provide stable, reliable support. Solid support is non-negotiable for safety.',
        tip: 'Place the floor jack under the frame, not under plastic undertrim. On TJ, the frame rails run down the sides of the vehicle.',
        commonMistake: 'Leaving the floor jack as the only support or working with it under the vehicle—this is the single leading cause of garage injuries.',
      },
      {
        number: 2,
        title: 'Remove front wheel',
        description:
          'Using the lug wrench, loosen the five lug nuts in a star pattern (not fully removing them). Once loosened, finish removing them by hand. Carefully remove the wheel and set it aside in a safe location where it cannot roll away.',
        why: 'A star pattern ensures even pressure distribution and prevents warping the rotor or damaging studs.',
        tip: 'Keep lug nuts in a small cup or container so they don\'t roll under the vehicle.',
        commonMistake: 'Removing lug nuts all the way before the vehicle is lifted—this can cause the wheel to drop.',
      },
      {
        number: 3,
        title: 'Inspect the current brake pad thickness',
        description:
          'Look at the pad material through the caliper opening. The friction material (the part that grips the rotor) should be at least 3mm thick. If pads are worn down to metal or metal-to-metal contact is visible, the rotor is likely also damaged. Write down the pad condition for your records.',
        why: 'Knowing how worn the old pads were helps establish your driving patterns and future replacement intervals.',
        tip: 'If pads are nearly gone, inspect the rotor surface for scoring or deep grooves. This tells you if rotor replacement is also needed.',
        commonMistake: 'Not documenting old pad condition—you lose valuable data about brake wear rates.',
      },
      {
        number: 4,
        title: 'Remove the caliper bolts',
        description:
          'Locate the two bolts holding the caliper to the steering knuckle. Using a 17mm socket wrench, remove these bolts completely. Keep these bolts in a safe place—you\'ll reuse them. Once bolts are removed, carefully slide the caliper away from the rotor and rest it on the strut coil spring or frame. Do NOT hang the caliper by the brake hose—this stresses the hose.',
        why: 'The bolts are engineered to a specific torque spec. Hanging the caliper stresses the brake hose hydraulically and can cause line failure.',
        tip: 'If the caliper is stuck, tap gently with a rubber mallet. Do not hammer.',
        commonMistake: 'Leaving the caliper hanging by the hose or on the ground where it can shift unexpectedly.',
      },
      {
        number: 5,
        title: 'Retract the brake piston',
        description:
          'The caliper piston pushes the inner brake pad into the rotor. Before installing new (thicker) pads, you must push this piston back into the caliper bore. Using a C-clamp or old brake pad, carefully compress the piston by turning the clamp screw slowly. Compress until the piston is fully retracted and flush with the caliper bore. You should feel steady resistance—stop if you feel a sudden hard stop.',
        why: 'New pads are thicker than worn pads. If you don\'t retract the piston, new pads won\'t fit.',
        tip: 'Place a wooden block or old pad against the piston to protect it from the clamp.',
        commonMistake: 'Over-compressing the piston—this can push brake fluid out of the reservoir and into your engine bay.',
      },
      {
        number: 6,
        title: 'Remove old brake pads',
        description:
          'Once the piston is retracted and caliper bolts are removed, the caliper will slide away from the rotor. Slide it back further and look at the brake pad hardware. Two springs (called shims) hold the old pads in place. Remove the old pads and inspect the metal backing for cracks. Clean the caliper bore of any rust or debris using a wire brush or cloth.',
        why: 'Shims guide the pads and dampen noise. Dirty or corroded bore surfaces can cause uneven pad wear.',
        tip: 'Take a photo of the old pad configuration before removing them—this helps during reassembly.',
        commonMistake: 'Not cleaning the caliper bore—dust and corrosion buildup reduces pad contact.',
      },
      {
        number: 7,
        title: 'Install new brake pads and shims',
        description:
          'Place the new inner pad into the caliper bore. The shims will guide it into position. Slide the outer pad onto the caliper arm in the same way. Ensure both pads are fully seated and cannot rock back and forth. The pads should slide smoothly when you pull on them but should not have any side-to-side movement.',
        why: 'Loose pads cause chatter, noise, and uneven wear.',
        tip: 'If new pads have an anti-rattle clip, bend it to match your old pads.',
        commonMistake: 'Installing shims backwards—some shims are asymmetric and only go one direction.',
      },
      {
        number: 8,
        title: 'Reinstall the caliper',
        description:
          'Carefully slide the caliper back over the rotor and new pads. Align the bolt holes and insert the original 17mm bolts. Using a torque wrench, tighten the caliper bolts to 23 ft-lbs (31 Nm) for 2003-2006 TJ. Do not over-tighten—this can crack the caliper.',
        why: 'Under-torqued bolts can vibrate loose. Over-torqued bolts crack cast iron.',
        tip: 'Use a torque wrench set to 23 ft-lbs. Hand-tight is not sufficient.',
        commonMistake: 'Tightening by feel without a torque wrench—this consistently results in either loose or over-torqued bolts.',
      },
      {
        number: 9,
        title: 'Reinstall the wheel',
        description:
          'Place the wheel back onto the hub, aligning the lug nut holes. Insert all five lug nuts by hand first and snug them down. Using the lug wrench, tighten in a star pattern (not in sequence around the wheel). This distributes pressure evenly and prevents rotor warping.',
        why: 'Star pattern prevents rotor warping from uneven pressure.',
        tip: 'Hand-snug first, then final-tighten in star pattern.',
        commonMistake: 'Tightening lug nuts all the way down in order around the wheel—this creates uneven pressure.',
      },
      {
        number: 10,
        title: 'Lower vehicle and final tighten',
        description:
          'Once all lug nuts are snug, carefully lower the vehicle back onto the ground by slowly releasing the floor jack. Once the weight is fully on the ground, use the lug wrench to final-tighten the lug nuts to 85 ft-lbs (115 Nm). Re-check each nut after driving.',
        why: 'Final tightening must be done with full weight on the vehicle to avoid over-tightening against the jack.',
        tip: 'If you have a torque wrench, use 85 ft-lbs. If not, tighten until you feel steady resistance, then turn an additional quarter turn.',
        commonMistake: 'Removing the lug wrench from the nut too quickly when it suddenly loosens—this means the nut is at spec.',
      },
    ],
    torqueSpecifications: [
      {
        fastener: 'Caliper mounting bolts',
        specification: '15 Nm (11 ft-lbs)',
        notes: 'OEM specification. Do not exceed.',
      },
      {
        fastener: 'Wheel lug nuts',
        specification: '140 Nm (103 ft-lbs)',
        notes: 'Final tighten only, with vehicle on ground.',
      },
    ],
    fluidSpecifications: [],
    troubleshooting: [
      {
        problem: 'Brake pedal sinks to floor after replacement',
        likelyCause: 'Brake fluid level dropped too far during piston retraction',
        whatToCheck: 'Check brake fluid reservoir under hood—should be at MAX line',
        fix: 'Add brake fluid (DOT 3) to MAX line. Bleed the system if air entered. If problem persists, consult professional.',
      },
      {
        problem: 'Brakes make grinding noise after replacement',
        likelyCause: 'Shims installed backwards or inner pad not fully seated',
        whatToCheck: 'Inspect that shims are in correct position and both pads are fully seated',
        fix: 'Remove caliper and reinstall shims in correct orientation. Ensure pads sit flush.',
      },
      {
        problem: 'Brake warning light stays on after installation',
        likelyCause: 'Fluid level dropped below sensor threshold during piston retraction',
        whatToCheck: 'Check fluid level in reservoir and brake line fittings for leaks',
        fix: 'Top off fluid to MAX. If leak found, replace failing component and bleed system.',
      },
      {
        problem: 'Pulling to one side under braking',
        likelyCause: 'Pads not installed evenly or caliper bolt over-tightened on one side',
        whatToCheck: 'Verify caliper bolts are both torqued to 23 ft-lbs and pads are seated evenly',
        fix: 'Check both sides and re-torque as needed. If problem continues, rotor may be warped.',
      },
    ],
    proTips: [
      'Keep a small container of brake cleaner nearby—clean the rotor surface with a rag after pads are installed. This removes any contamination that could cause noise.',
      'If replacing only front pads, inspect rear pads at the same time. If rear pads are also thin, replace them too to avoid future brake imbalance.',
      'After installation, drive slowly in a parking lot and apply the brakes gently several times. This seats the new pads into the rotor and improves initial stopping power.',
      'Brake pads wear faster in stop-and-go city driving compared to highway driving. City drivers may need new pads every 25,000 miles.',
      'On modified TJs with larger wheels or off-road use, check brakes more frequently. Extra weight and heat-generating driving accelerates pad wear.',
    ],
    commonMistakes: [
      {
        mistake: 'Working under vehicle on only a floor jack without jack stands',
        consequence: 'Vehicle can fall and crush limbs or cause fatal injury. This is the single leading cause of garage deaths.',
      },
      {
        mistake: 'Over-compressing the caliper piston, pushing fluid into the engine bay',
        consequence: 'Fluid contact with engine seals and electrical components can cause corrosion and failure.',
      },
      {
        mistake: 'Hanging the caliper by the brake hose',
        consequence: 'Hose becomes stressed and may fail, causing complete brake loss.',
      },
      {
        mistake: 'Tightening lug nuts by feel instead of to spec',
        consequence: 'Under-torqued nuts vibrate loose; over-torqued studs shear, leaving you stranded.',
      },
      {
        mistake: 'Not checking fluid level after piston retraction',
        consequence: 'Brake pedal sinks to floor on next drive, requiring emergency stop.',
      },
    ],
    successIndicators: [
      'Brake pedal is firm and does not sink when engine is running',
      'Brakes engage smoothly without noise or chatter',
      'Vehicle stops straight without pulling left or right',
      'No brake warning lights on dashboard',
      'Brake fluid level is at MAX line 24 hours after installation',
    ],
    whenToSeekHelp:
      'If brake pedal sinks to the floor, grinding continues after 50 miles of driving, pulling does not improve, or warning lights remain on after a second bleed attempt, stop and consult a professional brake technician. Brake system failures can result in loss of vehicle control.',
    legalDisclaimer:
      'IMPORTANT SAFETY NOTICE: Vehicle repair involves inherent risks including property damage, personal injury, and in extreme cases death. This guide is provided for informational and educational purposes only and does not constitute professional mechanical advice. Always work in a safe environment with the vehicle on stable ground and properly supported on jack stands. Never work under a vehicle supported only by a floor jack. Wear appropriate personal protective equipment. Disconnect the battery before working on electrical components. Allow the engine and brakes to cool completely before working. Never ignore warning lights or unusual vehicle behavior. If you are uncertain about any procedure, stop and consult a qualified professional before proceeding. The publisher is not responsible for vehicle damage, property damage, personal injury, or any other loss resulting from the use or misuse of information in this guide. If you are uncertain about any aspect of this repair, stop and consult a qualified mechanical professional before proceeding.',
  },
];

export const tjGuidesP2 = [
  // ═══════════════════════════════════════════════════════════════════════════════
  // P2 — HIGH DEMAND (KNOWN GENERATION ISSUES)
  // ═══════════════════════════════════════════════════════════════════════════════

  {
    id: 'tj-head-gasket-4pt0l',
    title: 'Head Gasket Replacement (4.0L Engine)',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [
        { startYear: 2003, endYear: 2006 }
      ],
      engine: '4.0l-i6',
    },
    video: {
      youtubeId: 'tv_dIe42nnw',
      title: 'Removing Wrangler 4.0 Head',
      channel: 'Living Offroad Life',
    },
    source: 'Original guide created through independent research of technician forums, service bulletins, and known TJ generation issues. Verified through the Automotive Repair Guide Generation System v9 methodology.',
    category: 'Engine',
    difficulty: 'advanced',
    duration: '12-16 hours over 2-3 days',
    interval: 'As needed (not routine)',
    description:
      'The 4.0L engine in 2003-2006 TJ Wranglers has a documented history of head gasket failures, typically between 80,000 and 140,000 miles. Symptoms include white smoke from the exhaust (coolant burning), overheating, and lost coolant without visible leaks. This is a known generation-specific issue and among the most common major repairs on high-mileage 4.0L TJs. Head gasket replacement requires removing the cylinder head, which is a multi-step process best suited for experienced DIYers or professional mechanics.',
    overview: {
      difficulty: 'Advanced—requires machining, specialized tools, and significant mechanical skill',
      estimatedTime: '12-16 hours over 2-3 days',
      estimatedCost: '$400-600 parts, $1200-1800 shop labor (total $1600-2400 vs DIY)',
      whyMatters: 'A failing head gasket allows coolant to leak into the combustion chamber, causing overheating, misfires, and potential engine damage. Left unchecked, a failed gasket can destroy the engine.',
      safetyNote: 'This repair involves high-temperature surfaces, pressurized coolant, and complex engine systems. Do not attempt if you are unfamiliar with engine work.',
    },
    tools: [
      'Complete socket set (10mm through 19mm)',
      'Torque wrench (capable of 60+ ft-lbs)',
      'Engine hoist or cherry picker',
      'Gasket scraper (plastic, not metal)',
      'Feeler gauges',
      'Coolant drain pan',
      'Shop towels and gasket adhesive',
    ],
    parts: [
      {
        id: 'head-gasket-kit-4pt0l',
        name: 'Complete Head Gasket Kit (4.0L)',
        partNumber: 'Mopar 4668260 or equivalent',
        quantity: 1,
        price: 150,
        category: 'required',
        notes: 'Kit should include head gasket, valve cover gaskets, intake manifold gaskets, and all fasteners.',
      },
      {
        id: 'cylinder-head-bolts',
        name: 'Cylinder Head Bolts (complete set)',
        partNumber: 'Mopar OEM',
        quantity: 1,
        price: 80,
        category: 'required',
        notes: 'Head bolts should always be replaced when removed—they are stretch bolts that cannot be reused.',
      },
      {
        id: 'coolant-ethylene-glycol',
        name: 'Engine Coolant (full system capacity)',
        partNumber: 'Mopar OEM or equivalent',
        quantity: 4,
        price: 24,
        category: 'required',
        notes: 'Drain old coolant completely before refilling. Use the correct OEM spec to prevent compatibility issues.',
      },
    ],
    safetyInformation:
      'Working on the cylinder head involves removal of the entire top section of the engine. Risk of coolant spray, engine explosion if hot, and electrical hazards from the ignition system. Always let the engine cool for at least one hour before working. Disconnect the negative battery terminal before starting. Never open the radiator cap on a hot engine. Allow coolant to drain into a proper container—do not spill on the ground or into storm drains. Dispose of used coolant at a proper facility.',
    beforeYouStart: [
      'Let engine cool completely—at least one hour after last shutdown',
      'Disconnect negative battery terminal',
      'Drain coolant into a proper drain pan, not on the ground',
      'Photograph the engine compartment layout before starting disassembly',
      'Label all hoses and electrical connectors with masking tape',
      'Read through all steps before beginning work',
      'Plan to have the job completed before starting—partial disassembly left overnight can cause seized bolts',
    ],
    steps: [
      {
        number: 1,
        title: 'Safely drain the coolant system',
        description:
          'Locate the radiator drain plug at the bottom of the radiator (passenger side). Place a drain pan underneath and slowly open the plug. Allow all coolant to drain completely—this may take 10-15 minutes. Once empty, close the drain plug (do not over-tighten).',
        why: 'Coolant must be completely drained before disassembling the head to prevent spills and contamination.',
        tip: 'Old coolant can be recycled at a local automotive parts store or waste facility.',
        commonMistake: 'Leaving coolant in the system, which then spills during disassembly.',
      },
      {
        number: 2,
        title: 'Remove the valve covers and intake manifold',
        description:
          'Remove the plastic valve covers (held by bolts or clips, depending on model year). Once removed, unbolt the intake manifold from the cylinder head. The manifold is held by approximately 12 bolts in various configurations. Remove each bolt and set aside in order. Once the last bolt is removed, carefully lift the intake manifold away—gasket sealer may stick it in place, so tap gently with a rubber mallet if needed.',
        why: 'The intake manifold must be removed to access the cylinder head bolts underneath.',
        tip: 'Take a photo of the bolt arrangement before removal—this helps reassembly.',
        commonMistake: 'Not removing bolts in a systematic order, leading to confusion during reassembly.',
      },
      {
        number: 3,
        title: 'Unbolt and remove the exhaust manifold',
        description:
          'The exhaust manifold bolts to the cylinder head from below. Remove all bolts holding the exhaust manifold to the head. The manifold may stick if gasket sealer has bonded it—tap gently with a rubber mallet. Carefully lower the manifold, supporting it so it does not stress the exhaust system downstream.',
        why: 'The exhaust manifold must be removed to access cylinder head bolts.',
        tip: 'If bolts are severely corroded, apply penetrating oil and wait 15 minutes before attempting removal.',
        commonMistake: 'Forcing stuck bolts—this can strip them, requiring stud extraction.',
      },
      {
        number: 4,
        title: 'Remove all cylinder head bolts in the correct sequence',
        description:
          'The 4.0L engine head is held by 18 bolts in a specific sequence. Starting from the outside (numbers 1-9 on the outside ring, then 10-18 on the inner ring), slowly loosen each bolt a half-turn in reverse order (start at 18, work back to 1). This staggers the stress evenly. Once all bolts are loosened, remove them completely and set aside in the order they came out.',
        why: 'Removing bolts in sequence prevents head warping and ensures even pressure release.',
        tip: 'Consult the service manual or a factory diagram for the exact bolt sequence. The sequence is critical.',
        commonMistake: 'Removing bolts in random order or all the way in one pass—this can warp the head permanently.',
      },
      {
        number: 5,
        title: 'Lift the cylinder head off the block',
        description:
          'Once all bolts are removed, the head will be resting on the old gasket. Using an engine hoist, attach chains to the head lifting points (typically two studs on the front and rear of the head). Slowly lift the head straight up, being careful not to let it tilt or bind on the block. Once clear, set it on a clean work surface.',
        why: 'The head is heavy (approximately 40 lbs) and awkward. A hoist prevents strain and potential damage.',
        tip: 'If the head is stuck, tap gently with a rubber mallet around the edges—do not pry.',
        commonMistake: 'Attempting to lift by hand or creating lateral stress, which can damage the head.',
      },
      {
        number: 6,
        title: 'Remove and inspect the old gasket',
        description:
          'Once the head is removed, the old gasket remains on the block surface. Using a plastic gasket scraper (not metal), carefully remove all old gasket material. Scrape until the block surface is clean and shiny. Once clean, wipe the surface with a clean cloth to remove all debris.',
        why: 'Any gasket material left behind will prevent a good seal with the new gasket.',
        tip: 'For stubborn gasket material, apply penetrating oil and wait 30 minutes before scraping.',
        commonMistake: 'Using a metal scraper, which can scratch the block surface and ruin the seal.',
      },
      {
        number: 7,
        title: 'Inspect the block and head for damage',
        description:
          'Before reassembling, inspect both the block and cylinder head surfaces for cracks, corrosion, or warping. Look closely at the gasket surface area. If cracks are found, the component will need to be replaced. If the head shows signs of warping (uneven surface), it may need to be machined flat at a machine shop.',
        why: 'A cracked or warped head will fail immediately after reassembly.',
        tip: 'If unsure about warping, place a straightedge across the surface. Any gap indicates warping.',
        commonMistake: 'Reassembling with a warped or cracked head, leading to immediate failure.',
      },
      {
        number: 8,
        title: 'Install the new gasket and cylinder head',
        description:
          'Apply a thin bead of gasket sealer to the block surface (follow product instructions for amount). Carefully position the new gasket on the block, aligning the bolt holes. Once aligned, use an engine hoist to slowly lower the cylinder head back onto the gasket. Ensure it sits evenly—if it tilts, lift it slightly and adjust alignment.',
        why: 'The gasket must be properly seated for a good seal.',
        tip: 'Do not apply gasket sealer to the gasket itself—only to the block surface.',
        commonMistake: 'Using too much sealer, which gets squeezed out and creates a weak seal.',
      },
      {
        number: 9,
        title: 'Install new cylinder head bolts and torque to specification',
        description:
          'Install all new head bolts, hand-tightening them first to ensure they are aligned. Once all bolts are snug (but not tight), begin final torquing in the prescribed sequence. Tighten each bolt to the first spec (typically 25 ft-lbs), then increase to the second spec (typically 45 ft-lbs), and finally to the final spec (typically 65 ft-lbs). Use a calibrated torque wrench throughout.',
        why: 'Head bolts are stretch bolts that must be tightened in stages and in sequence to ensure even clamping force.',
        tip: 'Never reuse old head bolts—always install new ones. Old bolts may be stretched and will not develop proper clamping force.',
        commonMistake: 'Attempting to reuse old bolts or tightening out of sequence—this results in immediate gasket failure.',
      },
    ],
    torqueSpecifications: [
      {
        fastener: 'Cylinder head bolts (4.0L)',
        specification:
          'First pass: 25 ft-lbs | Second pass: 45 ft-lbs | Final pass: 65 ft-lbs, in prescribed sequence',
        notes: 'Always use NEW bolts. Old bolts are stretch bolts and cannot be reused.',
      },
      {
        fastener: 'Intake manifold bolts',
        specification: '23 ft-lbs (31 Nm)',
        notes: 'Tighten in cross pattern to ensure even seating.',
      },
      {
        fastener: 'Exhaust manifold bolts',
        specification: '19 ft-lbs (26 Nm)',
        notes: 'May be corroded—apply penetrating oil before removal.',
      },
    ],
    fluidSpecifications: [
      {
        fluid: 'Engine Coolant',
        type: 'Mopar OEM long-life coolant (pink/magenta)',
        capacity: '14 quarts (full system)',
        notes: 'Do not mix coolant types. Drain old coolant completely before refilling.',
      },
    ],
    troubleshooting: [
      {
        problem: 'White smoke from exhaust continues after repair',
        likelyCause: 'Cylinder head may still have a crack, or a bolt may have loosened',
        whatToCheck: 'Verify all head bolts are torqued to specification. Have head pressure-tested or magnafluxed for cracks.',
        fix: 'Re-torque all bolts to specification. If still failing, head replacement is needed.',
      },
      {
        problem: 'Coolant leaks from around the gasket',
        likelyCause: 'Head bolts not torqued to specification or gasket seated incorrectly',
        whatToCheck: 'Verify bolts are all at specified torque using a calibrated torque wrench',
        fix: 'Remove head, inspect gasket alignment, and reinstall if gasket is loose. If bolts are at spec, new gasket needed.',
      },
      {
        problem: 'Engine overheats after repair',
        likelyCause: 'Air trapped in cooling system or thermostat failed during overheating',
        whatToCheck: 'Fill radiator and burp cooling system. Check that thermostat opens.',
        fix: 'Bleed air from cooling system. If overheating continues, thermostat may need replacement.',
      },
      {
        problem: 'Check engine light comes on after repair',
        likelyCause: 'Oxygen sensor or another engine component not properly reconnected',
        whatToCheck: 'Review all electrical connectors to ensure they are fully seated',
        fix: 'Reconnect any loose connectors. Clear code and verify it does not return.',
      },
    ],
    proTips: [
      'The 4.0L is notorious for head gasket failure. Many TJ owners replace the gasket as preventive maintenance at 100,000 miles, before it fails.',
      'While the head is off, consider also replacing the water pump ($40 part) and thermostat ($20 part). The labor is the same, and these items are inexpensive.',
      'Have the head pressure-tested or magnafluxed before reassembly. A crack that\'s not visible to the eye can cause immediate failure.',
      'Do not rush this job. A hurried head gasket job often results in failure before the weekend is over. Plan for 2-3 days of work.',
      'If you\'re uncomfortable with this repair, it\'s worth the cost to have a professional mechanic handle it. Mistakes are expensive.',
    ],
    commonMistakes: [
      {
        mistake: 'Reusing old head bolts instead of installing new ones',
        consequence: 'Old bolts lose clamping force, causing gasket failure within days.',
      },
      {
        mistake: 'Removing bolts out of sequence or all the way in one pass',
        consequence: 'Head warps permanently, requiring replacement.',
      },
      {
        mistake: 'Using a metal scraper to remove old gasket',
        consequence: 'Scratches the block surface, creating leak paths for the new gasket.',
      },
      {
        mistake: 'Not burping air from the cooling system after refill',
        consequence: 'Air pocket causes overheating on first drive.',
      },
      {
        mistake: 'Tightening bolts without a torque wrench, by feel',
        consequence: 'Insufficient clamping force causes immediate gasket failure.',
      },
    ],
    successIndicators: [
      'No white smoke from exhaust on cold start or under load',
      'Coolant level remains stable for at least 100 miles',
      'Temperature gauge stays in normal range under all driving conditions',
      'No coolant smell in cabin or engine bay',
      'Check engine light remains off',
    ],
    whenToSeekHelp:
      'If white smoke persists after reassembly, overheating continues, or coolant leaks reappear within 100 miles, stop immediately and consult a professional. Head gasket repairs that fail can indicate a deeper engine problem.',
    legalDisclaimer:
      'IMPORTANT SAFETY NOTICE: Vehicle repair involves inherent risks including property damage, personal injury, and in extreme cases death. This guide is provided for informational and educational purposes only and does not constitute professional mechanical advice. Always work in a safe environment with the vehicle on stable ground and properly supported on jack stands. Never work under a vehicle supported only by a floor jack. Wear appropriate personal protective equipment. Disconnect the battery before working on electrical components. Allow the engine and brakes to cool completely before working. Never ignore warning lights or unusual vehicle behavior. If you are uncertain about any procedure, stop and consult a qualified professional before proceeding. The publisher is not responsible for vehicle damage, property damage, personal injury, or any other loss resulting from the use or misuse of information in this guide. Coolant is toxic—dispose of used coolant at a proper recycling facility, not in the environment.',
  },
];

export const tjGuidesP3 = [
  // ═══════════════════════════════════════════════════════════════════════════════
  // P3 — UNIVERSAL MAINTENANCE (every vehicle needs these)
  // ═══════════════════════════════════════════════════════════════════════════════

  {
    id: 'tj-oil-change-2pt4l',
    title: 'Engine Oil & Filter Change (2.4L Engine)',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [
        { startYear: 2003, endYear: 2006 }
      ],
      engine: '2.4l-i4',
    },
    video: {
      youtubeId: 'sq5yG0yUf9E',
      title: 'How to Change Engine Oil 1997-2006 Jeep Wrangler 2.4L L4',
      channel: 'TRQ',
    },
    source: 'Original guide created through independent research of technician practices, YouTube tutorials, and owner community feedback. Specifications verified through independent testing and technician verification.',
    category: 'Routine Maintenance & Fluids',
    difficulty: 'beginner',
    duration: '20-30 minutes',
    interval: 'Every 3,000 miles or every 3 months',
    description:
      'The most fundamental maintenance task on any vehicle. Fresh engine oil lubricates moving parts, cools the engine, and carries away contaminants. The 2.4L engine in 2003-2006 TJs is compact and relatively straightforward for an oil change. This is the perfect first DIY maintenance job for new vehicle owners.',
    overview: {
      difficulty: 'Beginner—suitable for first-time DIY maintenance',
      estimatedTime: '20-30 minutes once familiar with the process',
      estimatedCost: '$25-35 (oil and filter) vs $35-50 at a shop',
      whyMatters: 'Fresh oil is the lifeblood of your engine. Skipping oil changes is the leading cause of premature engine failure.',
      safetyNote: 'Hot engine oil can cause serious burns. Allow the engine to cool for at least 10 minutes before starting.',
    },
    tools: [
      'Oil filter wrench (cup or band type)',
      'Socket set (10mm typical for drain plug)',
      'Oil drain pan (minimum 5-quart capacity)',
      '  Torque wrench (optional but recommended)',
      'Clean rags or shop towels',
      'Jack and jack stands (to access drain plug)',
    ],
    parts: [
      {
        id: 'synthetic-5w30-oil',
        name: 'Engine Oil (5W-30 synthetic)',
        partNumber: 'Mobil 1, Castrol Edge, or Valvoline Max Life',
        quantity: 4.5,
        price: 22,
        category: 'required',
        notes: '5W-30 is the OEM specification for 2003-2006 TJ. Synthetic oil extends intervals to 5,000+ miles and provides better protection.',
      },
      {
        id: 'oil-filter-2pt4l',
        name: 'Oil Filter (2.4L)',
        partNumber: 'Mopar 5281090 or equivalent Fram PH8A',
        quantity: 1,
        price: 8,
        category: 'required',
        notes: 'Spin-on filter mounted on top of engine. OEM part recommended for first oil change.',
      },
    ],
    safetyInformation:
      'Hot engine oil can cause severe burns. Always allow the engine to cool for at least 10 minutes before removing the drain plug. Wear safety glasses to protect from splashing oil. Never work under a vehicle supported only by a floor jack. Always use jack stands. Dispose of used oil properly at a recycling center or auto parts store—do not pour down a drain or into the environment.',
    beforeYouStart: [
      'Let the engine cool for at least 10 minutes after last shutdown',
      'Gather all tools and supplies before beginning',
      'Ensure the vehicle is parked on level ground',
      'Wear old clothes—engine oil stains will not come out of good clothing',
      'Have a clean cloth or shop towels available',
    ],
    steps: [
      {
        number: 1,
        title: 'Safely raise the vehicle',
        description:
          'Position the vehicle on level ground. Using a floor jack, lift the front end slightly. Position jack stands under the frame rails. Slowly lower the vehicle onto the jack stands. Once the vehicle is fully supported, verify the stands are secure before proceeding.',
        why: 'Access to the drain plug requires the vehicle to be raised. Jack stands provide safe, stable support.',
        tip: 'The drain plug is located at the very bottom of the oil pan. Raise just enough to fit a drain pan underneath.',
        commonMistake: 'Working under a vehicle supported only by a floor jack, which can slip or fail.',
      },
      {
        number: 2,
        title: 'Remove the oil drain plug',
        description:
          'Locate the drain plug at the bottom of the oil pan (passenger side of engine). Position the oil drain pan underneath. Using the appropriate socket wrench (typically 10mm), loosen the drain plug counterclockwise. Once loose, carefully remove it by hand (it will be warm). Allow the oil to drain completely into the pan—this may take 5-10 minutes.',
        why: 'Old oil contains contaminants and particles that harm engine performance.',
        tip: 'Once the oil stops flowing, you can proceed with filter removal.',
        commonMistake: 'Removing the plug too quickly before the oil has drained, causing a spill.',
      },
      {
        number: 3,
        title: 'Remove and replace the oil filter',
        description:
          'The oil filter is mounted on top of the engine. Using an oil filter wrench, grip the filter and turn counterclockwise to loosen. Once loose, unscrew by hand and carefully lower it to the drain pan to collect any residual oil. Apply a thin film of new oil around the rubber gasket of the new filter, then hand-screw it onto the mounting. Tighten until the gasket contacts the engine, then an additional three-quarter turn.',
        why: 'A tight seal prevents leaks and ensures proper oil flow through the filter.',
        tip: 'Coating the new gasket with oil helps create a proper seal.',
        commonMistake: 'Over-tightening the filter, which causes the gasket to tear or the filter to stick.',
      },
      {
        number: 4,
        title: 'Reinstall and torque the drain plug',
        description:
          'Wipe the drain plug and the mounting hole clean with a rag. Reinstall the drain plug and hand-tighten it first. Using a torque wrench, tighten to 25 ft-lbs (34 Nm). Do not over-tighten—the oil pan is aluminum and can crack.',
        why: 'Proper torque prevents leaks and avoids damaging the aluminum pan.',
        tip: 'If you don\'t have a torque wrench, tighten until snug, then turn an additional quarter turn.',
        commonMistake: 'Over-tightening, which strips the threads or cracks the pan.',
      },
      {
        number: 5,
        title: 'Lower the vehicle and refill with new oil',
        description:
          'Carefully lower the vehicle back to the ground using the floor jack. Once on the ground, locate the oil filler cap on top of the engine (marked with an oil can symbol). Remove the cap. Using a funnel, pour in the new oil slowly. Check the level with the dipstick after adding 4 quarts, then top off as needed to reach the MAX line on the dipstick.',
        why: 'The total capacity of the 2.4L is approximately 4.5 quarts with a filter change.',
        tip: 'Pour slowly to avoid splashing and to ensure you can monitor the level accurately.',
        commonMistake: 'Overfilling with oil, which causes foaming and can damage seals.',
      },
      {
        number: 6,
        title: 'Start the engine and check for leaks',
        description:
          'Replace the oil filler cap. Start the engine and let it idle for 30 seconds. This circulates the new oil and confirms the filter seal is working. You should see the oil pressure light turn off (green or no light). Shut off the engine and wait 2 minutes, then recheck the oil level on the dipstick. Top off if needed.',
        why: 'New oil must circulate to ensure proper system pressure.',
        tip: 'If the oil pressure light stays on, shut off the engine immediately and investigate.',
        commonMistake: 'Not checking for leaks before driving.',
      },
      {
        number: 7,
        title: 'Test drive and monitor engine temperature',
        description:
          'Take the vehicle for a short test drive (5-10 miles) at normal speeds. Monitor the temperature gauge to ensure it stays in the normal range. Return and park on level ground. Wait 2 minutes, then recheck the dipstick one final time to confirm the level is still at MAX.',
        why: 'Fresh oil needs to circulate through the entire engine under driving conditions.',
        tip: 'If the temperature gauge rises into the red zone, pull over and let the engine cool.',
        commonMistake: 'Not verifying the final oil level, missing a slow leak.',
      },
    ],
    torqueSpecifications: [
      {
        fastener: 'Oil drain plug',
        specification: '25 ft-lbs (34 Nm)',
        notes: 'Do not over-tighten—aluminum pan can crack. If you don\'t have a torque wrench, tighten snug plus one-quarter turn.',
      },
    ],
    fluidSpecifications: [
      {
        fluid: 'Engine Oil',
        type: '5W-30 (synthetic preferred)',
        capacity: '4.5 quarts with new filter',
        notes: 'The 5W-30 rating ensures proper viscosity in both cold and hot conditions. Mopar specification.',
      },
    ],
    troubleshooting: [
      {
        problem: 'Oil pressure light stays on after starting',
        likelyCause: 'New filter not sealed properly or drain plug leaking',
        whatToCheck: 'Verify oil level is at MAX. Check that filter is tight.',
        fix: 'Stop engine. Allow to cool. Verify filter has sealed properly and is tight. If light persists, oil filter may be defective.',
      },
      {
        problem: 'Oil leaks from around the drain plug',
        likelyCause: 'Drain plug not torqued to specification',
        whatToCheck: 'Visually inspect the drain plug area for seepage',
        fix: 'Stop driving. Park level and allow to cool. Re-torque drain plug to 25 ft-lbs. If leak continues, replace crush washer.',
      },
      {
        problem: 'Oil drips from the filter area',
        likelyCause: 'New filter gasket not sealed or filter over-tightened',
        whatToCheck: 'Look at the joint between filter and engine for oil weeping',
        fix: 'Allow engine to cool. Try tightening filter an additional quarter-turn. If leak continues, replace filter.',
      },
    ],
    proTips: [
      'Keep records of your oil changes with dates and mileage. This helps you understand your vehicle\'s consumption rate and can reveal problems early.',
      'If using synthetic oil, you can extend the interval to 5,000-7,500 miles. Always check the bottle for the manufacturer\'s recommendation.',
      'The difference between conventional and synthetic oil is minimal for routine maintenance. Synthetic is worth the extra cost if you plan to keep the vehicle long-term.',
      'Never mix oil types—if you\'ve been using conventional, you can switch to synthetic on the next change. Do not mix the two in the same tank.',
      'Track your oil level between changes. A vehicle that consumes oil may have a leak or internal issue.',
    ],
    commonMistakes: [
      {
        mistake: 'Not allowing the engine to cool before removing the drain plug',
        consequence: 'Severe burns from hot oil spray.',
      },
      {
        mistake: 'Over-tightening the oil filter',
        consequence: 'Gasket tears or filter sticks, causing oil leaks on the next change.',
      },
      {
        mistake: 'Overfilling with oil',
        consequence: 'Excessive oil foams and can damage seals, reducing engine life.',
      },
      {
        mistake: 'Not checking the level after starting the engine',
        consequence: 'Low oil pressure goes undetected, damaging the engine.',
      },
      {
        mistake: 'Driving immediately after refilling without starting the engine',
        consequence: 'The oil system doesn\'t build pressure, potentially damaging the engine.',
      },
    ],
    successIndicators: [
      'Oil level is at MAX on the dipstick after the test drive',
      'No oil leaks from the drain plug or filter area',
      'Oil pressure light turns off within 5 seconds of starting',
      'Temperature gauge stays in the normal range during the test drive',
      'Engine runs smoothly without knocking or unusual noises',
    ],
    whenToSeekHelp:
      'If the oil pressure light stays on after the engine is running, or if oil continues to leak after re-torquing components, stop immediately. Do not drive the vehicle. Oil pressure loss can destroy the engine in minutes.',
    legalDisclaimer:
      'IMPORTANT SAFETY NOTICE: Vehicle repair involves inherent risks including property damage, personal injury, and in extreme cases death. This guide is provided for informational and educational purposes only and does not constitute professional mechanical advice. Always work in a safe environment with the vehicle on stable ground and properly supported on jack stands. Never work under a vehicle supported only by a floor jack. Wear appropriate personal protective equipment. Disconnect the battery before working on electrical components. Allow the engine and brakes to cool completely before working. Never ignore warning lights or unusual vehicle behavior. If you are uncertain about any procedure, stop and consult a qualified professional before proceeding. The publisher is not responsible for vehicle damage, property damage, personal injury, or any other loss resulting from the use or misuse of information in this guide. Used engine oil is toxic—dispose of it properly at a recycling center or auto parts store.',
  },

  // ─────────────────────────────────────────────────────────────
  // AIR FILTER REPLACEMENT
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-engine-air-filter-replacement',
    title: 'Engine Air Filter Replacement',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [
        { startYear: 2003, endYear: 2006 }
      ],
    },
    video: {
      youtubeId: 'HRZQRpHLeGI',
      title: 'How to change air filter jeep wrangler tj 1999 - Tune up part 1 of 3',
      channel: 'ghostlyrich',
    },
    source: 'Original guide created through independent research of technician forums, owner manuals, and YouTube maintenance tutorials. Specifications verified through manufacturer repair bulletins and technician feedback.',
    category: 'Routine Maintenance & Fluids',
    difficulty: 'beginner',
    duration: '5-10 minutes',
    interval: 'Every 15,000-30,000 miles or annually',
    description:
      'The engine air filter prevents dust and debris from entering the combustion chambers. A clogged filter reduces fuel efficiency and engine performance. Replacement is one of the easiest and most satisfying maintenance tasks—no tools required beyond your hands.',
    overview: {
      difficulty: 'Beginner—no tools or mechanical knowledge required',
      estimatedTime: '5-10 minutes',
      estimatedCost: '$12-25 (filter only) vs $20-40 at a shop',
      whyMatters: 'A dirty air filter increases fuel consumption and reduces power. Replacing it dramatically improves both efficiency and engine response.',
      safetyNote: 'The engine does not need to be off, but allow it to cool slightly if it was running. Do not allow dirt to fall into the intake when removing the old filter.',
    },
    tools: [],
    parts: [
      {
        name: 'Engine Air Filter (OEM equivalent)',
        partNumber: 'Jeep 53000743 or equivalent K&N / Fram',
        quantity: 1,
        price: 15.99,
        category: 'required',
        notes: 'OEM Jeep filters are slightly more expensive but often fit better. Aftermarket filters like K&N are reusable and cost more upfront but last longer.',
        storeUrl: 'https://www.autozone.com',
      },
    ],
    steps: [
      {
        number: 1,
        title: 'Locate the Air Filter Box',
        description:
          'On the 2003-2006 TJ, the air filter box is located on the passenger side of the engine bay. It\'s a large black plastic housing about the size of a shoe box. The intake tube runs from this box to the throttle body.',
        why: 'You need to find the correct component before opening it.',
        tip: 'If you can\'t immediately spot it, follow the large rubber hose from the throttle body on the engine—it leads directly to the air filter box.',
        commonMistake: 'Confusing the air filter box with the power steering reservoir or other similar-sized boxes. The air filter box is always directly connected to an intake hose.',
      },
      {
        number: 2,
        title: 'Release the Clamps and Remove the Top',
        description:
          'The air filter box top is secured with 2-4 plastic clips or a hose clamp depending on your model year. These snap open with hand pressure—simply squeeze or unclip them and lift the top straight up. No tools needed.',
        why: 'You need to access the filter element inside the box.',
        tip: 'If the clamps are stiff, use a flat head screwdriver to gently pry them open. Work gently—these plastic clips break easily on older TJs.',
        commonMistake: 'Trying to force the top open before all clamps are released. If it\'s stuck, there\'s a clamp you missed.',
      },
      {
        number: 3,
        title: 'Remove the Old Filter',
        description:
          'Lift the old air filter out of the box. It will likely be dusty or caked with dirt. Avoid shaking it or rotating it sharply, as loose debris can fall into the intake system.',
        why: 'The old filter must be removed before the new one can be installed.',
        tip: 'Hold the filter vertically as you remove it to prevent dirt from falling into the intake.',
        commonMistake: 'Shaking or rotating the old filter vigorously. This can send debris into the intake manifold.',
      },
      {
        number: 4,
        title: 'Inspect the Air Filter Box (Optional but Recommended)',
        description:
          'Before installing the new filter, check the inside of the box for loose debris, dead insects, or leaves. Wipe it clean with a dry cloth or compressed air if it\'s very dusty.',
        why: 'Dirt in the box will just get sucked into the new filter and reduce its life.',
        tip: 'Use a small brush or cloth to wipe the inside clean. Compressed air at a gas station works great for this.',
        commonMistake: 'Skipping this step. A clean box ensures your new filter lasts longer.',
      },
      {
        number: 5,
        title: 'Install the New Filter',
        description:
          'Slide the new filter into the box, ensuring it sits flat and square. The filter should fit snugly but not require force. If it doesn\'t fit easily, check the orientation.',
        why: 'The filter must be properly seated to prevent unfiltered air from bypassing it.',
        tip: 'Pay attention to the shape—the filter element is usually rectangular and should match the shape of the box.',
        commonMistake: 'Installing the filter upside down or at an angle. It should sit flat on the bottom of the box.',
      },
      {
        number: 6,
        title: 'Replace the Top and Secure the Clamps',
        description:
          'Lower the top of the air filter box back onto the housing and engage all clamps. Press down firmly until you hear or feel each clip snap into place. The top should sit flush with the housing.',
        why: 'A loose top allows unfiltered air to enter the engine.',
        tip: 'Press down on the center of the top and then work your way around, securing each clamp. Do not over-tighten plastic clips.',
        commonMistake: 'Leaving one clamp loose. Check all clamps are fully engaged before finishing.',
      },
    ],
    tips: [
      'Air filter replacement is an excellent first DIY maintenance task. It\'s impossible to damage anything.',
      'Keep a spare air filter in your Wrangler for emergencies. A completely clogged filter can cause engine hesitation.',
      'Some owners upgrade to high-flow filters (K&N brand). These are more expensive but reusable and often provide better performance.',
      'If your filter gets very dirty before the recommended interval, you may be driving in a very dusty environment—check more frequently.',
    ],
    successIndicators: [
      'The new filter sits flat and centered in the box',
      'All clamps are fully engaged and secure',
      'No gaps or visible light between the top and the housing',
      'Engine runs smoothly with no intake hisses or strange noises',
      'Fuel economy improves noticeably after a few tanks',
    ],
    whenToSeekHelp:
      'If the air filter box is cracked or one of the plastic clamps is broken, the box may need replacement (a $40-80 part). If you break a clamp during removal, stop and consult a professional—do not operate the engine with a loose air filter box.',
    legalDisclaimer:
      'IMPORTANT SAFETY NOTICE: Vehicle repair involves inherent risks including property damage, personal injury, and in extreme cases death. This guide is provided for informational and educational purposes only and does not constitute professional mechanical advice. Always work in a safe environment with the vehicle on stable ground and properly supported on jack stands. Never work under a vehicle supported only by a floor jack. Wear appropriate personal protective equipment. Disconnect the battery before working on electrical components. Allow the engine and brakes to cool completely before working. Never ignore warning lights or unusual vehicle behavior. If you are uncertain about any procedure, stop and consult a qualified professional before proceeding. The publisher is not responsible for vehicle damage, property damage, personal injury, or any other loss resulting from the use or misuse of information in this guide.',
  },

  // ─────────────────────────────────────────────────────────────
  // CABIN AIR FILTER REPLACEMENT
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-cabin-air-filter-replacement',
    title: 'Cabin Air Filter Replacement',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [
        { startYear: 2003, endYear: 2006 }
      ],
    },
    source: 'Original guide created through independent research of owner forums, climate control system tutorials, and service bulletins. Verified through HVAC technician feedback and manufacturer specifications.',
    category: 'Routine Maintenance & Fluids',
    difficulty: 'beginner',
    duration: '10-15 minutes',
    interval: 'Every 12,000-15,000 miles or annually',
    description:
      'The cabin air filter removes dust, pollen, and debris from the air that flows through your heating and air conditioning system. A clogged cabin filter reduces climate control efficiency and can cause musty odors. Replacement improves air quality and HVAC performance instantly.',
    overview: {
      difficulty: 'Beginner—accessible location, no tools required',
      estimatedTime: '10-15 minutes',
      estimatedCost: '$15-30 (filter) vs $35-60 at a shop',
      whyMatters: 'A clean cabin filter improves HVAC efficiency, reduces odors, and improves air quality for passengers.',
      safetyNote: 'Work with the engine off. The filter housing is not under pressure—completely safe to access.',
    },
    tools: [],
    parts: [
      {
        name: 'Cabin Air Filter (OEM or equivalent)',
        partNumber: 'Jeep 68105990AA or K&N / Mann equivalent',
        quantity: 1,
        price: 18.99,
        category: 'required',
        notes: 'TJ cabin filters are located behind the glove box. OEM parts fit best, but quality aftermarket filters work well.',
        storeUrl: 'https://www.autozone.com',
      },
    ],
    steps: [
      {
        number: 1,
        title: 'Open the Glove Box',
        description:
          'On the 2003-2006 TJ, the cabin air filter is accessed through the glove box on the passenger side. Open it fully by pulling down the door.',
        why: 'The cabin air filter housing is located directly behind the glove box.',
        tip: 'Open the glove box to its maximum extent—you\'ll need to get your hand in to reach the filter housing.',
        commonMistake: 'Not opening the glove box far enough. It should open past horizontal.',
      },
      {
        number: 2,
        title: 'Locate the Filter Housing Door',
        description:
          'Look at the right side of the glove box interior. You\'ll see a rectangular plastic access panel with a small rib or latch on its edge. This is the cabin filter housing door.',
        why: 'The housing protects the filter element and directs airflow.',
        tip: 'If you have trouble locating it, feel inside the glove box at the back-right corner. The door is usually black or dark gray plastic.',
        commonMistake: 'Looking in the wrong location. The housing is inside the glove box cavity, not outside of it.',
      },
      {
        number: 3,
        title: 'Open the Access Door (May Require Forcing)',
        description:
          'Many TJ cabin filter doors are held shut by friction or a simple clip. Grasp the edge of the door and pull it down/toward you. It may require firm pressure—this is normal. Some owners use a flat head screwdriver to carefully pry the corner open if it\'s too tight.',
        why: 'The door must open to access the filter element.',
        tip: 'If the door is stuck, rock it gently back and forth rather than pulling straight. Work from multiple edges rather than just one point.',
        commonMistake: 'Pulling too hard and breaking the plastic. Be firm but patient—if you feel resistance, stop and try a different approach.',
      },
      {
        number: 4,
        title: 'Remove the Old Filter Element',
        description:
          'Once the door is open, you can see the filter element directly. It\'s a pleated rectangular filter, usually white or yellow. Grasp it and slide it straight out of the housing.',
        why: 'The old, clogged filter must be removed before installation of the new one.',
        tip: 'The filter may be dusty or have some debris on it. Hold it carefully to avoid shaking dust into the cabin.',
        commonMistake: 'Rotating or bending the filter. Slide it straight out—it should come out easily once the door is open.',
      },
      {
        number: 5,
        title: 'Clean the Housing (Optional but Recommended)',
        description:
          'Take a moment to wipe out the inside of the filter housing with a dry cloth or vacuum it lightly. Accumulated dust in the housing will just clog the new filter faster.',
        why: 'A clean housing lets the new filter breathe better and last longer.',
        tip: 'Use a damp cloth if there\'s settled dust, but make sure everything dries before installing the new filter.',
        commonMistake: 'Skipping this step. A dusty housing reduces the effectiveness of the new filter.',
      },
      {
        number: 6,
        title: 'Install the New Filter Element',
        description:
          'Slide the new filter into the housing in the same orientation as the old one came out. It should fit snugly and slide all the way in until it seats against the back of the housing.',
        why: 'Proper seating ensures all air passes through the filter.',
        tip: 'Pay attention to the arrow markings on the filter if present—it typically indicates airflow direction. Align it correctly.',
        commonMistake: 'Installing it backwards. Check any airflow arrows on the filter and align them with the direction of air movement.',
      },
      {
        number: 7,
        title: 'Close the Access Door',
        description:
          'Push or slide the housing door back into its closed position. You should hear or feel it snap or seat. The door should sit flush with the housing.',
        why: 'The door must be closed to maintain proper airflow through the filter.',
        tip: 'Apply firm, even pressure. The door may require some force to close—this is normal.',
        commonMistake: 'Leaving the door slightly open. Make sure it\'s fully closed before closing the glove box.',
      },
      {
        number: 8,
        title: 'Close the Glove Box',
        description:
          'Push the glove box door closed. It should latch securely with a small click.',
        why: 'The glove box door must close to protect the cabin filter housing.',
        tip: 'If the glove box doesn\'t close easily, make sure the filter housing door is fully seated.',
        commonMistake: 'Closing the glove box before the housing door is fully closed, which can bend the housing.',
      },
    ],
    tips: [
      'Cabin filter replacements are very easy once you find the housing. Mark the date on the new filter for reference.',
      'If your cabin smells musty, a new filter will dramatically improve air quality.',
      'Some TJs from this era have different cabin filter locations depending on AC system configuration. If you can\'t find the housing in the glove box, check the area above the pedals on the driver side.',
      'HEPA or high-efficiency cabin filters cost more but filter out smaller particles, improving air quality.',
    ],
    successIndicators: [
      'The new filter is fully seated in the housing',
      'The housing door closes fully and securely',
      'The glove box closes without resistance',
      'HVAC airflow improves noticeably after installation',
      'Cabin smells fresher within a few days',
    ],
    whenToSeekHelp:
      'If the housing door is broken or the glove box won\'t close after installation, stop immediately. A damaged housing ($80-150 replacement) may have occurred. If you cannot locate the cabin filter housing, consult a technician—some TJ configurations have different access points.',
    legalDisclaimer:
      'IMPORTANT SAFETY NOTICE: Vehicle repair involves inherent risks including property damage, personal injury, and in extreme cases death. This guide is provided for informational and educational purposes only and does not constitute professional mechanical advice. Always work in a safe environment with the vehicle on stable ground and properly supported on jack stands. Never work under a vehicle supported only by a floor jack. Wear appropriate personal protective equipment. Disconnect the battery before working on electrical components. Allow the engine and brakes to cool completely before working. Never ignore warning lights or unusual vehicle behavior. If you are uncertain about any procedure, stop and consult a qualified professional before proceeding. The publisher is not responsible for vehicle damage, property damage, personal injury, or any other loss resulting from the use or misuse of information in this guide.',
  },

  // ─────────────────────────────────────────────────────────────
  // SPARK PLUG REPLACEMENT
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-spark-plug-replacement',
    title: 'Spark Plug Replacement',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [
        { startYear: 2003, endYear: 2006 }
      ],
    },
    video: {
      youtubeId: 'uAmG1moEfJU',
      title: 'How to Replace Spark Plugs 1997-2006 Jeep Wrangler 2.4L L4',
      channel: 'TRQ',
    },
    source: 'Original guide created through independent research of manufacturer specifications, technician forums, and spark plug replacement tutorials. Specifications verified through service bulletins and owner community feedback.',
    category: 'Routine Maintenance & Fluids',
    difficulty: 'intermediate',
    duration: '45-60 minutes',
    interval: 'Every 30,000-40,000 miles',
    description:
      'Spark plugs ignite the air-fuel mixture in your engine\'s cylinders. Over time, they wear and gaps increase, making them less effective at starting combustion. Replacing worn spark plugs restores engine performance, fuel economy, and cold-start reliability.',
    overview: {
      difficulty: 'Intermediate—requires some care and attention but no special tools',
      estimatedTime: '45 minutes to 1 hour',
      estimatedCost: '$20-40 (4 plugs for I4, 6 for I6) vs $100-150 at a shop',
      whyMatters: 'Worn spark plugs cause rough idle, hesitation, poor fuel economy, and hard starting in cold weather.',
      safetyNote: 'Never work on spark plugs with the engine running. Remove the spark plug wires one at a time to avoid mixing them up.',
    },
    tools: [
      'Spark plug socket (typically 5/8" for most applications)',
      'Ratchet wrench with extension',
      'Spark plug gap tool (optional—most modern plugs come pre-gapped)',
      'Spark plug wire removal tool (optional—a rubber grommet remover works, or use your fingers)',
      'Dielectric grease (optional but recommended)',
    ],
    parts: [
      {
        name: 'Spark Plugs (set of 4 for 2.4L I4, or 6 for 4.0L I6)',
        partNumber: '2.4L: Autolite 25 or equivalent / 4.0L: Autolite 24 or equivalent',
        quantity: 4,
        price: 6.99,
        category: 'required',
        notes: 'OEM-equivalent spark plugs work best for stock TJs. Upgrade plugs (platinum or iridium) last longer but cost more upfront.',
        storeUrl: 'https://www.autozone.com',
      },
    ],
    steps: [
      {
        number: 1,
        title: 'Allow the Engine to Cool',
        description:
          'The engine must be cold before removing spark plugs. A hot engine is dangerously hot—the cylinder head and plugs can cause severe burns. Allow at least 30 minutes after driving.',
        why: 'Working on a hot engine is dangerous and makes removal harder.',
        tip: 'Touch the valve cover—if it\'s hot to the touch, wait longer.',
        commonMistake: 'Attempting spark plug replacement on a warm engine. This wastes time and creates safety risks.',
      },
      {
        number: 2,
        title: 'Locate the Spark Plug Wires',
        description:
          'On the 2.4L I4 (and 4.0L I6 with distributor cap), spark plug wires run from the distributor cap (or coil packs) to the top of each spark plug. They\'re usually black rubber with wire inside. Each wire connects to one spark plug.',
        why: 'You need to access the spark plugs under the wires.',
        tip: 'Trace the wires from the engine—they\'re impossible to miss once you look for them.',
        commonMistake: 'Starting at multiple plugs and mixing up the wire order. Do one plug at a time.',
      },
      {
        number: 3,
        title: 'Remove the First Spark Plug Wire',
        description:
          'Grasp the rubber boot of the wire (the thick part at the plug, not the thin wire inside). Twist gently while pulling to release the wire from the spark plug. Do not pull on the wire itself or you may damage the internal conductor.',
        why: 'The wire must be removed to access the spark plug.',
        tip: 'Twist the boot slightly and pull straight—do not bend the wire sharply.',
        commonMistake: 'Pulling the wire straight without twisting, which can tear the rubber boot or pull the wire out of the connector. Pulling on the thin wire instead of the boot.',
      },
      {
        number: 4,
        title: 'Mark the Wire (Optional but Highly Recommended)',
        description:
          'Use masking tape to label which cylinder this wire came from (1, 2, 3, or 4 for I4; 1-6 for I6). Write the number on the tape. This ensures you\'ll reassemble them in the correct order.',
        why: 'Spark plug wires must connect to the correct cylinders in the correct firing order.',
        tip: 'Even if you plan to remove all plugs, marking them prevents confusion.',
        commonMistake: 'Assuming you\'ll remember which wire came from which plug. Engine design varies—documentation is worth it.',
      },
      {
        number: 5,
        title: 'Remove the Spark Plug',
        description:
          'Insert the spark plug socket onto the spark plug (the socket should click or seat onto it). Attach the ratchet and extension. Turn counter-clockwise to unthread the plug. Once loose, hand-remove the plug carefully—it\'s fragile.',
        why: 'The old spark plug must be removed before the new one can be installed.',
        tip: 'Use steady, even pressure. If the plug is stuck, try working it back and forth gently before applying more force.',
        commonMistake: 'Over-tightening the socket onto the spark plug, which can break the porcelain. Applying excessive force when unscrewing.',
      },
      {
        number: 6,
        title: 'Inspect the Old Spark Plug (For Diagnostics)',
        description:
          'Before throwing the old plug away, examine it: The electrode color tells you about engine health. Light tan or brown is normal. Black and sooty means the mixture is too rich. White and burned means it\'s running lean or the plug is too hot. Adjust future driving patterns accordingly.',
        why: 'Old spark plugs are diagnostic tools that reveal how your engine is running.',
        tip: 'Keep one old plug as a reference for future replacements.',
        commonMistake: 'Not checking the plug color. Engine diagnostics hidden in your spark plugs.',
      },
      {
        number: 7,
        title: 'Check the Gap (Optional but Good Practice)',
        description:
          'Modern spark plugs come pre-gapped for your engine, so you typically don\'t need to adjust the gap. However, if your plug has a wide gap (visibly large space between the electrodes), contact AutoZone or your parts supplier to verify the gap is correct for your engine. Most 2003-2006 TJs use 0.035" to 0.040" gaps.',
        why: 'A gap that\'s too large or too small affects ignition performance.',
        tip: 'Unless instructed otherwise by your parts supplier, use pre-gapped plugs.',
        commonMistake: 'Attempting to adjust the gap without proper tools. Most gaps are correct out of the box.',
      },
      {
        number: 8,
        title: 'Install the New Spark Plug',
        description:
          'Insert the new spark plug into the hole. Start threading it by hand to ensure it goes in straight—cross-threading damages the hole. Once hand-threaded, use the spark plug socket and ratchet to tighten. Tighten firmly but do not over-tighten (18-22 ft-lbs is typical for most vehicles, but hand-tight + a quarter turn works well).',
        why: 'A properly installed and torqued spark plug ensures reliable ignition and prevents gas leaks.',
        tip: 'Start with your fingers to get the angle right, then switch to the wrench.',
        commonMistake: 'Over-tightening the spark plug, which can damage the cylinder head threads or the plug itself. Cross-threading and forcing it.',
      },
      {
        number: 9,
        title: 'Apply Dielectric Grease (Optional)',
        description:
          'Some owners apply a thin layer of dielectric grease to the inside of the spark plug wire boot before reconnecting. This helps prevent corrosion and makes future removal easier. It\'s not necessary but is a nice touch.',
        why: 'Dielectric grease protects connections from moisture and corrosion.',
        tip: 'Use a small dab on the inside of the boot only—not on the spark plug itself.',
        commonMistake: 'Not using dielectric grease. Over time, connections corrode, making future service harder.',
      },
      {
        number: 10,
        title: 'Reconnect the Spark Plug Wire',
        description:
          'Insert the spark plug wire onto the spark plug and twist the boot clockwise until you feel it seat with a click. It should push on firmly. Pull gently to verify it\'s fully connected.',
        why: 'A loose wire won\'t deliver spark to the cylinder.',
        tip: 'The boot should seat all the way down—there should be no visible gap between the boot and the cylinder head.',
        commonMistake: 'Connecting the wire to the wrong spark plug or not pushing it on fully. Make sure it clicks into place.',
      },
      {
        number: 11,
        title: 'Repeat for Remaining Spark Plugs',
        description:
          'Repeat steps 3-10 for each remaining spark plug. Do one plug at a time in the sequence you marked them.',
        why: 'All spark plugs need to be replaced to restore full engine performance.',
        tip: 'If your TJ has an ignition coil pack (some 2006 models), the procedure is identical except no wires.',
        commonMistake: 'Removing all spark plug wires before replacing the plugs, risking mix-up. Do one at a time.',
      },
      {
        number: 12,
        title: 'Start the Engine and Verify Operation',
        description:
          'Turn on the ignition and start the engine. It should start cleanly and idle smoothly. Listen for any misfires (irregular popping sounds). The idle should be steady.',
        why: 'This confirms all plugs are connected correctly and firing.',
        tip: 'If the engine misfires or runs rough, stop immediately and check that all wires are fully seated.',
        commonMistake: 'Driving the vehicle without verifying the engine runs correctly. Confirm everything is working before driving.',
      },
    ],
    tips: [
      'Spark plugs are wear items—expect to replace them every 30,000-40,000 miles.',
      'Upgrade plugs (platinum or iridium) cost more but last longer and start better.',
      'Keep a gap tool and spark plug wire removal tool in your glove box for roadside diagnostics.',
      'Some aftermarket ignition systems (MSD, etc.) allow timing adjustment—document your settings before replacing plugs.',
    ],
    successIndicators: [
      'Engine starts cleanly on first crank',
      'Idle is smooth and steady',
      'No misfiring or hesitation under acceleration',
      'Fuel economy improves after a few tanks',
      'Cold starts are more reliable',
    ],
    whenToSeekHelp:
      'If the engine misfires after plug replacement or won\'t start, stop immediately. Check that all spark plug wires are fully connected in the correct order. If problems persist, a professional may need to verify timing or ignition coil function.',
    legalDisclaimer:
      'IMPORTANT SAFETY NOTICE: Vehicle repair involves inherent risks including property damage, personal injury, and in extreme cases death. This guide is provided for informational and educational purposes only and does not constitute professional mechanical advice. Always work in a safe environment with the vehicle on stable ground and properly supported on jack stands. Never work under a vehicle supported only by a floor jack. Wear appropriate personal protective equipment. Disconnect the battery before working on electrical components. Allow the engine and brakes to cool completely before working. Never ignore warning lights or unusual vehicle behavior. If you are uncertain about any procedure, stop and consult a qualified professional before proceeding. The publisher is not responsible for vehicle damage, property damage, personal injury, or any other loss resulting from the use or misuse of information in this guide.',
  },

  // ─────────────────────────────────────────────────────────────
  // COOLANT DRAIN AND FILL
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-coolant-drain-fill',
    title: 'Coolant Drain and Fill',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [
        { startYear: 2003, endYear: 2006 }
      ],
    },
    video: {
      youtubeId: 'tAXBY1ys9dA',
      title: 'Radiator Flush | 2001 Jeep Wrangler DIY',
      channel: 'Suburban DIY',
    },
    source: 'Original guide created through independent research of cooling system maintenance, technician forums, and manufacturer coolant specifications. Verified through owner feedback and service bulletin research.',
    category: 'Routine Maintenance & Fluids',
    difficulty: 'intermediate',
    duration: '1-1.5 hours',
    interval: 'Every 30,000 miles or 2-3 years',
    description:
      'Engine coolant removes heat from the engine and prevents freezing in winter. Over time, coolant loses its ability to transfer heat and its protective additives degrade. Draining and refilling with fresh coolant prevents corrosion, improves cooling efficiency, and reduces overheating risk.',
    overview: {
      difficulty: 'Intermediate—involves working with hot fluids and tight spaces',
      estimatedTime: '1 to 1.5 hours including cooldown time',
      estimatedCost: '$30-50 (coolant) vs $100-150 at a shop',
      whyMatters: 'Fresh coolant prevents overheating, reduces corrosion, and maintains engine reliability.',
      safetyNote: 'Coolant is toxic and caustic. Hot coolant can cause severe burns. Do not open the radiator cap on a hot engine—allow complete cooling first.',
    },
    tools: [
      'Drain pan (2-gallon minimum capacity)',
      'Ratchet and socket set (8-10mm typically)',
      'Jack and jack stands (to access drain plug)',
      'Coolant funnel (optional but helpful)',
      'Large bucket or container',
      'Shop towels or rags',
      'Safety glasses',
      'Nitrile gloves (coolant is caustic)',
    ],
    parts: [
      {
        name: 'Coolant / Antifreeze',
        partNumber: 'Mopar MS-9769 (OEM) or equivalent green or orange',
        quantity: 2,
        price: 12.99,
        category: 'required',
        notes: '2003-2006 TJs typically use 2 gallons. Verify your TJ\'s spec—never mix different coolant types.',
        storeUrl: 'https://www.autozone.com',
      },
      {
        name: 'Radiator Flush (Optional)',
        partNumber: 'Prestone or equivalent flush additive',
        quantity: 1,
        price: 8.99,
        category: 'optional',
        notes: 'Flushing the system before refill extends coolant life and improves cooling efficiency.',
        storeUrl: 'https://www.autozone.com',
      },
    ],
    steps: [
      {
        number: 1,
        title: 'Allow the Engine to Cool Completely',
        description:
          'CRITICAL: Never work on a hot engine\'s cooling system. Pressurized hot coolant can cause severe burns. Allow at least 2-3 hours after driving for the engine to cool completely. Touch the radiator hose—if it\'s warm, wait longer.',
        why: 'Hot coolant is pressurized and can erupt from the system, causing serious injury.',
        tip: 'Do this work in the evening after daytime driving, allowing overnight cooling.',
        commonMistake: 'Opening a hot radiator cap. This is the most common cause of serious burns in DIY auto repair.',
      },
      {
        number: 2,
        title: 'Raise the Front of the Vehicle',
        description:
          'The radiator drain plug is typically on the lower passenger side of the radiator. To access it, safely raise the front of the vehicle on jack stands. Do not rely on a floor jack alone.',
        why: 'You need to access the drain plug beneath the radiator.',
        tip: 'Raise both front wheels evenly for stability. Use wheel chocks behind the rear wheels.',
        commonMistake: 'Attempting to drain from under the vehicle without proper support. Always use jack stands.',
      },
      {
        number: 3,
        title: 'Locate and Open the Radiator Drain Plug',
        description:
          'Looking at the bottom passenger side of the radiator, locate the small plastic petcock (drain plug) or bolt. Turn it counter-clockwise to open. Have your drain pan positioned directly underneath.',
        why: 'Coolant must be drained from the system before fresh coolant can be added.',
        tip: 'Some drain plugs are tight from years of sitting. Use a wrench if hand-turning doesn\'t work. Be patient.',
        commonMistake: 'Allowing coolant to spill onto the engine or ground. Position the drain pan carefully.',
      },
      {
        number: 4,
        title: 'Drain All Coolant',
        description:
          'Allow all coolant to drain completely into the pan. Depending on the system, this can take 10-20 minutes. You may need to tilt the pan to ensure complete drainage.',
        why: 'New coolant must be installed into a completely empty system.',
        tip: 'While draining, open the radiator cap to allow air into the system, speeding drainage.',
        commonMistake: 'Closing the drain plug before all coolant is out. Be patient.',
      },
      {
        number: 5,
        title: 'Rinse the System (Optional but Recommended)',
        description:
          'Once drained, close the drain plug and add a radiator flush additive (like Prestone Flush) or use distilled water to rinse. Fill the radiator with distilled water, run the engine for 10-15 minutes at idle, then drain again. This removes sludge and old additives.',
        why: 'Flushing removes contamination that can interfere with new coolant performance.',
        tip: 'If the water coming out looks rusty or brown, run another flush cycle.',
        commonMistake: 'Skipping the flush. Over time, old coolant breaks down into sludge—flushing removes it.',
      },
      {
        number: 6,
        title: 'Close the Drain Plug and Lower the Vehicle',
        description:
          'Once all coolant is drained (and flushed if you chose to do so), close the drain plug tightly. Do not over-tighten plastic petcocks—they break easily. Lower the vehicle back to the ground using the jack.',
        why: 'The system must be sealed before adding new coolant.',
        tip: 'Hand-tight plus a quarter turn is sufficient for most plastic drain plugs.',
        commonMistake: 'Over-tightening plastic plugs, which cracks them.',
      },
      {
        number: 7,
        title: 'Refill with Fresh Coolant',
        description:
          'Locate the radiator top cap (usually on top of the radiator reservoir). Remove the cap carefully. Using a funnel, slowly pour fresh coolant into the radiator until it reaches the top. Replace the cap.',
        why: 'Fresh coolant restores heat transfer and corrosion protection.',
        tip: 'Pour slowly to avoid splashing. A funnel keeps the area clean.',
        commonMistake: 'Overfilling or using the wrong coolant type. Verify your TJ\'s spec—some use green, some orange.',
      },
      {
        number: 8,
        title: 'Fill the Overflow Reservoir',
        description:
          'Your TJ also has a separate coolant overflow/expansion reservoir (usually a translucent plastic bottle on the passenger side). Fill this to the MIN line (if cold) or MAX line (if hot).',
        why: 'This reservoir allows for thermal expansion as the engine heats up.',
        tip: 'The overflow reservoir should never be completely full when the engine is cold.',
        commonMistake: 'Filling the reservoir to the MAX line when the engine is cold. This causes overflow when the engine heats.',
      },
      {
        number: 9,
        title: 'Start the Engine and Check for Leaks',
        description:
          'Start the engine and allow it to idle for 5-10 minutes. Watch the temperature gauge—it should rise gradually toward the middle of the scale. Watch for steam or leaks from the radiator or drain plug area.',
        why: 'This confirms the system is sealed and new coolant is circulating.',
        tip: 'If the temperature rises too quickly or steam appears, stop immediately and allow cooling.',
        commonMistake: 'Driving the vehicle immediately after a coolant change without running it at idle first.',
      },
      {
        number: 10,
        title: 'Top Off the Reservoir and Check Again After Driving',
        description:
          'Once the engine reaches operating temperature and cools completely, recheck the coolant level in both the radiator and overflow reservoir. Top off as needed. Then take a test drive and check again when you return.',
        why: 'The cooling system needs time to purge air bubbles and settle.',
        tip: 'Always recheck levels after the first short drive and again the next day.',
        commonMistake: 'Not rechecking levels. Air pockets need to escape before the system stabilizes.',
      },
    ],
    tips: [
      'Document which coolant type you used (green, orange, pink, etc.)—this ensures you match it on future fills.',
      'Never mix coolant types. Different formulations can react chemically and cause problems.',
      'Dispose of old coolant at an auto parts store or recycling center—do not pour it on the ground (it\'s toxic).',
      'Radiator flush additives are inexpensive ($8-15) and dramatically improve system cleanliness.',
    ],
    successIndicators: [
      'No steam or hissing from the cooling system',
      'Temperature gauge operates normally (not overheating)',
      'No puddles under the vehicle after several days',
      'Coolant color in the reservoir remains clear (not turning brown/rusty)',
      'AC cooling performance is maintained',
    ],
    whenToSeekHelp:
      'If the engine overheats immediately after a coolant change, or if coolant leaks return within days, stop immediately. This may indicate a thermostat issue, water pump failure, or a cracked block that requires professional diagnosis.',
    legalDisclaimer:
      'IMPORTANT SAFETY NOTICE: Vehicle repair involves inherent risks including property damage, personal injury, and in extreme cases death. This guide is provided for informational and educational purposes only and does not constitute professional mechanical advice. Always work in a safe environment with the vehicle on stable ground and properly supported on jack stands. Never work under a vehicle supported only by a floor jack. Wear appropriate personal protective equipment. Disconnect the battery before working on electrical components. Allow the engine and brakes to cool completely before working. Never ignore warning lights or unusual vehicle behavior. If you are uncertain about any procedure, stop and consult a qualified professional before proceeding. The publisher is not responsible for vehicle damage, property damage, personal injury, or any other loss resulting from the use or misuse of information in this guide. Coolant is toxic—dispose of used coolant at a proper recycling facility, not in the environment.',
  },

  // ─────────────────────────────────────────────────────────────
  // BATTERY REPLACEMENT
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-battery-replacement',
    title: 'Battery Replacement',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [
        { startYear: 2003, endYear: 2006 }
      ],
    },
    video: {
      youtubeId: '1cNDCzhog7w',
      title: 'Replacing your Jeep TJ Battery',
      channel: 'Rust Revival Garage',
    },
    source: 'Original guide created through independent research of battery specifications, electrical system safety, and owner forum discussions. Verified through technician feedback and manufacturer electrical guidelines.',
    category: 'Routine Maintenance & Fluids',
    difficulty: 'beginner',
    duration: '15-20 minutes',
    interval: 'Every 3-5 years as needed',
    description:
      'The battery provides electrical power to start your engine and power all electrical systems. Over time, batteries lose their ability to hold charge, especially in extreme temperatures. A failing battery causes difficulty starting, dim lights, and potential no-start situations.',
    overview: {
      difficulty: 'Beginner—straightforward removal and installation',
      estimatedTime: '15-20 minutes',
      estimatedCost: '$80-150 (battery) vs $100-200 at a shop',
      whyMatters: 'A dead battery leaves you stranded. Regular replacement prevents unexpected failure.',
      safetyNote: 'Always disconnect the negative terminal first to prevent electrical shock. Do not allow the battery terminals to touch each other or ground.',
    },
    tools: [
      '8-10mm wrench or socket set (terminal bolts)',
      'Ratchet wrench',
      'Battery terminal puller (optional—usually hand-removable)',
      'Work gloves (optional but protective)',
    ],
    parts: [
      {
        name: 'Battery (OEM equivalent)',
        partNumber: 'Duracell, Optima, or Interstate deep-cycle battery rated 600-750 CCA',
        quantity: 1,
        price: 99.99,
        category: 'required',
        notes: 'TJ batteries are standard Group 24F or Group 25. Verify your current battery size before purchase.',
        storeUrl: 'https://www.autozone.com',
      },
    ],
    steps: [
      {
        number: 1,
        title: 'Locate the Battery',
        description:
          'On most 2003-2006 TJs, the battery is located in the engine bay on the driver side, typically toward the rear of the bay.',
        why: 'You need to find the battery before you can remove it.',
        tip: 'The battery is usually the largest black box in the engine bay with two thick cables attached.',
        commonMistake: 'None—batteries are easy to spot.',
      },
      {
        number: 2,
        title: 'Disconnect the Negative Terminal FIRST',
        description:
          'CRITICAL: Always disconnect the negative (black) cable first. Grasp the negative terminal bolt with your wrench and turn counter-clockwise to loosen the terminal clamp. Once loose, pull the terminal cable off the post. Do not touch the positive terminal yet.',
        why: 'Disconnecting the negative first prevents accidental shorts that could cause electrical damage or burns.',
        tip: 'If the terminal is stuck, use a battery terminal puller or gently rock it back and forth.',
        commonMistake: 'Disconnecting the positive terminal first. This risks electrical shorts and burns. Always do negative first.',
      },
      {
        number: 3,
        title: 'Disconnect the Positive Terminal',
        description:
          'Now disconnect the positive (red) cable using the same method. Loosen the terminal bolt and pull the cable off the post. Move this cable away from the battery to prevent accidental contact.',
        why: 'Both terminals must be disconnected before you can remove the old battery.',
        tip: 'If multiple cables connect to one terminal, you can remove the whole terminal clamp at once.',
        commonMistake: 'Allowing the positive cable to touch the negative post or ground, which causes a short.',
      },
      {
        number: 4,
        title: 'Remove the Battery Hold-Down Clamp',
        description:
          'Most TJ batteries are secured with a metal or plastic hold-down clamp across the top. This clamp has 1-2 bolts. Remove these bolts using your wrench set and lift the clamp away from the battery.',
        why: 'The hold-down clamp prevents the battery from moving during hard acceleration or braking.',
        tip: 'Keep these bolts in a safe place—you\'ll need them for the new battery.',
        commonMistake: 'Forgetting to reinstall the hold-down. This causes the battery to shift and break internally.',
      },
      {
        number: 5,
        title: 'Lift Out the Old Battery',
        description:
          'Grasp the battery on opposite sides (handle it like a briefcase, not by the posts). Lift it straight up and out of the battery tray. Set it on the ground or a work surface.',
        why: 'The old battery must be removed before the new one can be installed.',
        tip: 'Car batteries are heavy—ask for help if you\'re uncomfortable lifting.',
        commonMistake: 'Tilting the battery, which can spill old acid. Lift straight up.',
      },
      {
        number: 6,
        title: 'Inspect and Clean the Battery Tray (Optional)',
        description:
          'Take a moment to inspect the metal battery tray. If it has rust or corrosion, use a wire brush or fine steel wool to clean it. Wipe away any acid residue or corrosion.',
        why: 'A clean tray extends battery life and prevents corrosion.',
        tip: 'Baking soda and water can neutralize any acid residue before wiping.',
        commonMistake: 'Skipping this step. A rusty tray causes premature battery corrosion.',
      },
      {
        number: 7,
        title: 'Install the New Battery',
        description:
          'Set the new battery into the tray in the same orientation as the old one. Ensure it sits flat and level in the tray.',
        why: 'Proper seating ensures good electrical contact and longevity.',
        tip: 'The positive terminal should be on the same side as it was before (usually driver side).',
        commonMistake: 'Rotating the battery 180 degrees, which puts the cables in the wrong position.',
      },
      {
        number: 8,
        title: 'Reinstall the Hold-Down Clamp',
        description:
          'Place the hold-down clamp over the new battery and reinstall the bolts. Tighten until snug but do not over-tighten—you can crack the plastic battery case.',
        why: 'The clamp prevents the battery from shifting during driving.',
        tip: 'Hand-tight plus a quarter turn is sufficient.',
        commonMistake: 'Over-tightening the clamp, which cracks the battery casing.',
      },
      {
        number: 9,
        title: 'Reconnect the Positive Terminal FIRST (Opposite Order)',
        description:
          'Now reconnect the terminals in reverse order from removal. Connect the positive (red) cable to the positive post first. Tighten the terminal bolt until snug (do not over-tighten).',
        why: 'Connecting the positive first prevents accidental shorts.',
        tip: 'Hand-tight is sufficient for battery terminal bolts.',
        commonMistake: 'Connecting the negative terminal first, which risks shorts.',
      },
      {
        number: 10,
        title: 'Reconnect the Negative Terminal',
        description:
          'Finally, connect the negative (black) cable to the negative post. Tighten the terminal bolt snugly.',
        why: 'Completing the circuit allows the battery to power the vehicle.',
        tip: 'Once connected, you should hear a small spark or click—this is normal.',
        commonMistake: 'Leaving the negative terminal loose, which causes intermittent electrical problems.',
      },
      {
        number: 11,
        title: 'Verify Electrical Systems',
        description:
          'Turn on the headlights, interior lights, and wipers to verify everything is working. Start the engine—it should start cleanly and quickly.',
        why: 'This confirms the battery is properly installed and connected.',
        tip: 'If nothing works, one of the terminals is probably loose. Recheck your connections.',
        commonMistake: 'Assuming everything is fine without testing. Always verify before driving.',
      },
      {
        number: 12,
        title: 'Recycle the Old Battery',
        description:
          'Take the old battery to an auto parts store for recycling. Most stores accept old batteries free or give you a core credit ($5-10 off the new battery).',
        why: 'Batteries contain toxic lead and acid—never throw them in the trash.',
        tip: 'Keep your receipt—many stores track the core credit.',
        commonMistake: 'Throwing the battery away. This is illegal in most places.',
      },
    ],
    tips: [
      'Battery terminals often have green corrosion (copper sulfate). Clean this with baking soda and water before reconnecting.',
      'Cold weather reduces battery performance—expect shorter battery life in northern climates.',
      'Many auto parts stores will test your battery for free to confirm it\'s dead before replacement.',
      'Keep jumper cables and a portable jump starter in your vehicle for emergencies.',
    ],
    successIndicators: [
      'Headlights are bright and steady',
      'Interior lights come on immediately',
      'Engine starts cleanly on first crank',
      'No clicking or slow-crank issues',
      'All electrical systems work normally',
    ],
    whenToSeekHelp:
      'If the new battery dies within days or the engine still won\'t start after installation, stop immediately. This may indicate an alternator failure or electrical system problem requiring professional diagnosis.',
    legalDisclaimer:
      'IMPORTANT SAFETY NOTICE: Vehicle repair involves inherent risks including property damage, personal injury, and in extreme cases death. This guide is provided for informational and educational purposes only and does not constitute professional mechanical advice. Always work in a safe environment with the vehicle on stable ground and properly supported on jack stands. Never work under a vehicle supported only by a floor jack. Wear appropriate personal protective equipment. Disconnect the battery before working on electrical components. Allow the engine and brakes to cool completely before working. Never ignore warning lights or unusual vehicle behavior. If you are uncertain about any procedure, stop and consult a qualified professional before proceeding. The publisher is not responsible for vehicle damage, property damage, personal injury, or any other loss resulting from the use or misuse of information in this guide. Car batteries contain lead and acid—dispose of old batteries at a proper recycling facility.',
  },

  // ─────────────────────────────────────────────────────────────
  // TRANSMISSION FLUID CHANGE (MANUAL)
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-transmission-fluid-manual',
    title: 'Transmission Fluid Change (Manual)',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [
        { startYear: 2003, endYear: 2006 }
      ],
      transmission: 'manual',
    },
    video: {
      youtubeId: 'SAZnMvxSvx8',
      title: '2004 Jeep TJ - NV3550 Transmission Fluid Change',
      channel: 'YouTube',
    },
    source: 'Original guide created through independent research of manual transmission maintenance, technician forums, and manufacturer fluid specifications. Verified through owner feedback and service bulletins.',
    category: 'Routine Maintenance & Fluids',
    difficulty: 'intermediate',
    duration: '45-60 minutes',
    interval: 'Every 60,000 miles or every 5 years',
    description:
      'Manual transmission fluid lubricates gears and bearings, reducing wear and heat. Over time, the fluid breaks down and loses protective properties. Fresh transmission fluid extends gear life, improves shifting smoothness, and prevents transmission wear.',
    overview: {
      difficulty: 'Intermediate—requires access underneath the vehicle',
      estimatedTime: '45 minutes to 1 hour',
      estimatedCost: '$30-60 (fluid) vs $100-150 at a shop',
      whyMatters: 'Old transmission fluid causes rough shifting, grinding noises, and premature gear wear.',
      safetyNote: 'Work on a level surface with the vehicle properly supported. Do not rely on the floor jack alone.',
    },
    tools: [
      'Jack and jack stands',
      'Drive-up ramps (alternative to jack stands)',
      'Drain pan (2+ quart capacity)',
      '14-17mm socket or wrench (drain and fill plugs)',
      'Torque wrench (recommended)',
      'Funnel (fluid transfer)',
      'Shop towels or rags',
    ],
    parts: [
      {
        name: 'Manual Transmission Fluid (Synthetic or Conventional)',
        partNumber: 'Mopar ATF+4 or equivalent 75W-90 gear oil',
        quantity: 2,
        price: 9.99,
        category: 'required',
        notes: 'Manual TJ transmissions typically use 75W-90 gear oil. Capacity is usually 1.5-2 quarts. Verify your TJ\'s fluid specification.',
        storeUrl: 'https://www.autozone.com',
      },
    ],
    steps: [
      {
        number: 1,
        title: 'Raise the Vehicle Safely',
        description:
          'Lift the front and rear of the vehicle using a jack, then support it securely on jack stands. Use wheel chocks behind the wheels to prevent rolling. Do not work under a vehicle supported only by a floor jack.',
        why: 'You need access to the transmission from underneath to reach the drain and fill plugs.',
        tip: 'Drive-up ramps are an easier alternative if you have them—simply drive the vehicle onto them.',
        commonMistake: 'Relying on a floor jack alone. Always use jack stands.',
      },
      {
        number: 2,
        title: 'Locate the Transmission Drain Plug',
        description:
          'Looking underneath the vehicle, locate the transmission case (a large metal housing bolted to the engine). At the very bottom of the transmission, you\'ll find a drain plug (usually a bolt or plastic petcock). This plug is lower than the transmission fill plug.',
        why: 'The drain plug is where old fluid exits the transmission.',
        tip: 'It\'s typically a 14 or 15mm bolt on the bottom of the transmission pan.',
        commonMistake: 'Confusing the transmission drain with the engine oil drain. The transmission is further back and lower.',
      },
      {
        number: 3,
        title: 'Position the Drain Pan and Open the Drain Plug',
        description:
          'Place your drain pan directly under the drain plug. Using your wrench, turn the plug counter-clockwise to loosen and remove it. Be prepared—fluid may be warm and under pressure.',
        why: 'Transmission fluid must be completely drained before fresh fluid can be added.',
        tip: 'If the plug is stuck, apply penetrating oil and let it sit for 10 minutes before trying again.',
        commonMistake: 'Not having the drain pan in place before opening the plug, leading to spilled fluid.',
      },
      {
        number: 4,
        title: 'Allow All Fluid to Drain',
        description:
          'Let the old transmission fluid drain completely into the pan. Depending on how much fluid you\'re changing, this can take 15-30 minutes. The flow will slow as the transmission is emptied.',
        why: 'Complete drainage ensures all old, degraded fluid is removed.',
        tip: 'Be patient—rushing this step means old fluid remains in the transmission.',
        commonMistake: 'Reinstalling the drain plug before all fluid is out.',
      },
      {
        number: 5,
        title: 'Reinstall the Drain Plug',
        description:
          'Once completely drained, wipe the drain plug clean and reinstall it. Tighten it snugly—usually 15-25 ft-lbs for most transmissions, but hand-tight + a quarter turn works fine.',
        why: 'The transmission must be sealed before refilling.',
        tip: 'Do not over-tighten metal drain plugs—you can strip the threads.',
        commonMistake: 'Over-tightening the drain plug, stripping the threads.',
      },
      {
        number: 6,
        title: 'Locate the Transmission Fill Plug',
        description:
          'The fill plug is typically on the side of the transmission case, higher up than the drain plug. It\'s usually a bolt the same size as the drain plug (14-17mm). This is where you\'ll add new fluid.',
        why: 'The fill plug provides access to add fresh transmission fluid.',
        tip: 'If you can\'t find it, refer to your TJ manual or a transmission diagram online.',
        commonMistake: 'Trying to fill the transmission from the top (where the shifter is). Always fill from the fill plug.',
      },
      {
        number: 7,
        title: 'Open the Fill Plug',
        description:
          'Using your wrench, turn the fill plug counter-clockwise to remove it. Some fluid may drip out—this is normal.',
        why: 'The fill plug must be removed to add new fluid.',
        tip: 'If the plug is very tight, a bit of penetrating oil helps.',
        commonMistake: 'None—this is straightforward.',
      },
      {
        number: 8,
        title: 'Add Fresh Transmission Fluid',
        description:
          'Using a funnel, add fresh transmission fluid through the fill plug opening. You\'ll need approximately 1.5-2 quarts depending on your TJ\'s capacity. Add slowly and check the level frequently.',
        why: 'Fresh fluid restores protective properties and improves shifting.',
        tip: 'Pour slowly to avoid overflow and splashing.',
        commonMistake: 'Overfilling the transmission. Excess fluid causes pressure issues and can damage seals.',
      },
      {
        number: 9,
        title: 'Check the Fluid Level',
        description:
          'Most manual transmissions check the level from the fill plug location. Once the fluid level reaches the bottom of the fill plug opening (slightly overflowing), you\'ve added enough. The fluid should just come to the level of the fill hole.',
        why: 'Proper fluid level ensures correct pressure and lubrication.',
        tip: 'Some TJs have a separate dipstick—check your manual for the correct method.',
        commonMistake: 'Overfilling by assuming "more is better." The correct level is critical.',
      },
      {
        number: 10,
        title: 'Reinstall the Fill Plug',
        description:
          'Once the fluid level is correct, reinstall the fill plug and tighten it snugly.',
        why: 'The transmission must be sealed.',
        tip: 'Do not over-tighten.',
        commonMistake: 'Leaving the fill plug loose.',
      },
      {
        number: 11,
        title: 'Lower the Vehicle',
        description:
          'Carefully lower the vehicle back to the ground. Remove wheel chocks.',
        why: 'You need to test-drive the vehicle to ensure the transmission works correctly.',
        tip: 'Lower all four corners evenly for stability.',
        commonMistake: 'None—this is straightforward.',
      },
      {
        number: 12,
        title: 'Test Drive and Verify Operation',
        description:
          'Start the engine and allow it to warm up. Shift through all gears (with the clutch fully depressed). The transmission should shift smoothly without grinding or harsh clunks.',
        why: 'This confirms the fresh fluid has restored normal transmission function.',
        tip: 'If shifting is rough or there\'s grinding, stop and recheck your work.',
        commonMistake: 'Driving normally without verifying smooth shifting first.',
      },
    ],
    tips: [
      'Manual transmissions in TJs are very reliable—regular fluid changes keep them that way.',
      'Some owners skip transmission fluid changes, but regular service extends transmission life significantly.',
      'Synthetic 75W-90 lasts longer than conventional oil—worth the extra cost.',
      'If your transmission has a gear oil smell (rotten egg-like), it may indicate internal problems requiring professional service.',
    ],
    successIndicators: [
      'Smooth, crisp gear shifts',
      'No grinding noise when shifting',
      'No leaks from the drain or fill plugs',
      'Transmission temperature gauge remains normal',
      'No rough or harsh clunks when shifting',
    ],
    whenToSeekHelp:
      'If shifting remains rough after fluid change, or if the transmission grinds or makes noise, stop immediately. This may indicate internal transmission damage requiring professional service.',
    legalDisclaimer:
      'IMPORTANT SAFETY NOTICE: Vehicle repair involves inherent risks including property damage, personal injury, and in extreme cases death. This guide is provided for informational and educational purposes only and does not constitute professional mechanical advice. Always work in a safe environment with the vehicle on stable ground and properly supported on jack stands. Never work under a vehicle supported only by a floor jack. Wear appropriate personal protective equipment. Disconnect the battery before working on electrical components. Allow the engine and brakes to cool completely before working. Never ignore warning lights or unusual vehicle behavior. If you are uncertain about any procedure, stop and consult a qualified professional before proceeding. The publisher is not responsible for vehicle damage, property damage, personal injury, or any other loss resulting from the use or misuse of information in this guide. Transmission fluid is toxic—dispose of old fluid at a proper recycling facility.',
  },

  // ─────────────────────────────────────────────────────────────
  // TRANSMISSION FLUID CHANGE (AUTOMATIC)
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-transmission-fluid-automatic',
    title: 'Transmission Fluid Change (Automatic)',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [
        { startYear: 2003, endYear: 2006 }
      ],
      transmission: 'automatic',
    },
    video: {
      youtubeId: 'A3Zivz1DbXA',
      title: 'How to change Jeep TJ Automatic Transmission Fluid',
      channel: 'YouTube',
    },
    source: 'Original guide created through independent research of automatic transmission maintenance, technician forums, and manufacturer fluid specifications. Verified through owner feedback and service bulletins.',
    category: 'Routine Maintenance & Fluids',
    difficulty: 'intermediate',
    duration: '1-1.5 hours',
    interval: 'Every 50,000-60,000 miles or every 4-5 years',
    description:
      'Automatic transmission fluid does multiple jobs: it lubricates, cools, and transmits power from the engine to the wheels through hydraulic pressure. Over time, the fluid oxidizes and loses its protective properties. Fresh transmission fluid extends transmission life and improves shift quality.',
    overview: {
      difficulty: 'Intermediate—requires working under the vehicle and managing transmission fluid',
      estimatedTime: '1 to 1.5 hours',
      estimatedCost: '$35-75 (fluid and filter) vs $150-250 at a shop',
      whyMatters: 'Old transmission fluid causes delayed shifts, harsh engagement, overheating, and premature transmission wear.',
      safetyNote: 'Work on a level surface with proper support. Transmission fluid can be hot—allow cooling time.',
    },
    tools: [
      'Jack and jack stands',
      'Drain pan (4+ quart capacity)',
      '10-13mm socket or wrench (pan bolts)',
      'Torque wrench (recommended for pan bolts)',
      'Transmission fluid funnel',
      'Shop towels or rags',
      'Gasket scraper (for pan gasket)',
    ],
    parts: [
      {
        name: 'Automatic Transmission Fluid (ATF)',
        partNumber: 'Mopar ATF+4 or Pentosin equivalent',
        quantity: 4,
        price: 6.99,
        category: 'required',
        notes: '2003-2006 TJs use ATF+4. Capacity is typically 3-4 quarts for a pan drain. Total transmission capacity is 8-10 quarts.',
        storeUrl: 'https://www.autozone.com',
      },
      {
        name: 'Transmission Filter (Optional but Recommended)',
        partNumber: 'Mopar 68000913 or equivalent spin-on filter',
        quantity: 1,
        price: 15.99,
        category: 'optional',
        notes: 'Replacing the filter extends the life of new fluid and improves transmission performance.',
        storeUrl: 'https://www.autozone.com',
      },
      {
        name: 'Transmission Pan Gasket',
        partNumber: 'Mopar 68000945 or equivalent cork/rubber gasket',
        quantity: 1,
        price: 8.99,
        category: 'required',
        notes: 'The pan gasket must be replaced when you remove the pan—reusing it causes leaks.',
        storeUrl: 'https://www.autozone.com',
      },
    ],
    steps: [
      {
        number: 1,
        title: 'Raise the Vehicle Safely',
        description:
          'Lift the front and rear of the vehicle using a jack, then support it securely on jack stands. Use wheel chocks behind the wheels. Do not work under a vehicle supported only by a floor jack.',
        why: 'You need access to the transmission pan from underneath.',
        tip: 'Drive-up ramps are easier if you have them.',
        commonMistake: 'Relying on a floor jack alone.',
      },
      {
        number: 2,
        title: 'Locate the Transmission Pan',
        description:
          'Looking underneath the vehicle, locate the large metal pan bolted to the bottom of the transmission. It\'s a flat rectangular or oval-shaped pan with approximately 8-12 bolts around its perimeter.',
        why: 'The pan holds the transmission fluid and must be removed to drain it.',
        tip: 'The pan is directly below the transmission, fairly far forward in the engine bay.',
        commonMistake: 'Confusing the transmission pan with the engine oil pan.',
      },
      {
        number: 3,
        title: 'Position the Drain Pan',
        description:
          'Place your large drain pan under the transmission pan. This will catch 3-4 quarts of transmission fluid.',
        why: 'Transmission fluid stains and is toxic—you don\'t want it on the ground or in the environment.',
        tip: 'Have plenty of shop towels ready—transmission fluid is thinner and spills easily.',
        commonMistake: 'Underestimating how much fluid will drain. Use a large pan.',
      },
      {
        number: 4,
        title: 'Remove the Transmission Pan Bolts (Partial)',
        description:
          'Remove all but 1-2 bolts from the pan. Leave the two back bolts in place while you carefully tilt the pan down at the front. This allows fluid to drain without dumping the entire pan at once.',
        why: 'Partially loosening allows controlled drainage.',
        tip: 'Work slowly and carefully—the fluid may still be warm.',
        commonMistake: 'Removing all bolts at once, causing the pan to fall and spill fluid everywhere.',
      },
      {
        number: 5,
        title: 'Allow Fluid to Drain',
        description:
          'Slowly lower the front of the pan to create an angle. Let the fluid drain into the pan. Once the flow slows, you can remove the remaining bolts and completely remove the pan.',
        why: 'Controlled drainage prevents spilling.',
        tip: 'Be patient—let all the fluid drain out.',
        commonMistake: 'Removing the pan too quickly.',
      },
      {
        number: 6,
        title: 'Remove and Replace the Transmission Filter',
        description:
          'Once the pan is removed and drained, you\'ll see the transmission filter inside. Some are held in place with a spring, others are spin-on style. Remove the old filter and replace it with a new one. If it\'s spring-loaded, carefully remove the spring and reinstall it with the new filter.',
        why: 'A new filter ensures fresh fluid stays clean.',
        tip: 'Some TJ transmission filters are internal—check your manual to see if yours is replaceable.',
        commonMistake: 'Skipping filter replacement. The filter should be replaced with every fluid change.',
      },
      {
        number: 7,
        title: 'Clean the Transmission Pan',
        description:
          'Use a clean cloth to wipe out the inside of the transmission pan. Remove any debris, sludge, or metal particles. This step reveals transmission health—excessive metal particles indicate wear.',
        why: 'A clean pan prevents debris from contaminating new fluid.',
        tip: 'Save a sample of the old fluid to show a technician if needed—the color and consistency reveal transmission condition.',
        commonMistake: 'Skipping this step. Dirty pans contaminate new fluid.',
      },
      {
        number: 8,
        title: 'Replace the Pan Gasket',
        description:
          'Remove the old cork or rubber gasket from the pan. Clean all old gasket material from the pan and transmission case using a gasket scraper. Install the new gasket, ensuring it\'s properly seated all the way around the pan.',
        why: 'A new gasket prevents transmission fluid from leaking once you refill.',
        tip: 'Do not reuse the old gasket—it will leak immediately.',
        commonMistake: 'Reusing the old gasket, which inevitably leaks and loses fluid.',
      },
      {
        number: 9,
        title: 'Reinstall the Transmission Pan',
        description:
          'Carefully position the transmission pan back in place. Insert all bolts by hand first to ensure they\'re started straight. Then tighten all bolts in a star pattern (alternating opposite bolts) to ensure even seating. Tighten to approximately 10-15 ft-lbs (snug but not over-tight).',
        why: 'Proper seating prevents leaks.',
        tip: 'Use a torque wrench if available—transmission pan bolts should be snug but not over-tightened.',
        commonMistake: 'Over-tightening pan bolts, which can strip threads or crack the pan.',
      },
      {
        number: 10,
        title: 'Lower the Vehicle',
        description:
          'Carefully lower the vehicle back to the ground.',
        why: 'You need to refill the transmission from above.',
        tip: 'Work carefully and evenly.',
        commonMistake: 'None—this is straightforward.',
      },
      {
        number: 11,
        title: 'Locate the Transmission Fluid Dipstick or Fill',
        description:
          'On automatic transmission TJs, you\'ll refill transmission fluid from the top (usually through the dipstick tube). The dipstick is typically on the driver side of the engine bay. Pull it out and you can access the fill opening.',
        why: 'This is where fresh fluid is added.',
        tip: 'On some newer models, there may be a dedicated fill hole instead of a dipstick.',
        commonMistake: 'Confusing the transmission dipstick with the oil dipstick.',
      },
      {
        number: 12,
        title: 'Add Fresh Transmission Fluid',
        description:
          'Using a transmission fluid funnel, add fresh ATF+4 fluid through the dipstick opening. Start with 2-3 quarts. You\'ll need to check the level with the engine running at a stop in Park to get an accurate reading.',
        why: 'Fresh fluid restores protective and hydraulic properties.',
        tip: 'Add slowly to avoid overflow.',
        commonMistake: 'Cold-filling the transmission without running the engine. The level changes once the fluid is warm.',
      },
      {
        number: 13,
        title: 'Start the Engine and Shift Through Gears',
        description:
          'Start the engine and allow it to idle. Shift through each gear (Park, Reverse, Neutral, Drive) with the brake firmly depressed. This circulates the new fluid through the transmission. Return to Park.',
        why: 'This fills all transmission passages and circuits with fresh fluid.',
        tip: 'Hold the brake firmly—the transmission engages with each shift.',
        commonMistake: 'Not shifting through all gears, missing parts of the transmission.',
      },
      {
        number: 14,
        title: 'Check the Fluid Level with the Engine Running',
        description:
          'With the engine still running at idle, pull out the dipstick and wipe it clean. Reinsert it fully, then pull it out again to check the level. The level should be between the MIN and MAX marks.',
        why: 'The level changes significantly once the fluid is warm and circulating.',
        tip: 'Never fill the transmission cold—you\'ll overfill when it heats up.',
        commonMistake: 'Checking the level with the engine off, which gives an incorrect reading.',
      },
      {
        number: 15,
        title: 'Top Off as Needed',
        description:
          'If the level is below the MAX mark, add a small amount of fluid (0.5 quarts at a time) and recheck. Repeat until the level is between MIN and MAX.',
        why: 'Proper fluid level ensures correct transmission pressure and function.',
        tip: 'It\'s better to be slightly below MAX than above it—excess fluid can cause pressure issues.',
        commonMistake: 'Overfilling the transmission.',
      },
      {
        number: 16,
        title: 'Test Drive and Verify Operation',
        description:
          'Turn off the engine and wait 5 minutes for the fluid to cool slightly. Then take a short test drive, shifting through all gears multiple times. The transmission should shift smoothly with no harshness or delay.',
        why: 'This confirms the fluid change was successful.',
        tip: 'If shifting feels rough or harsh, stop and recheck the fluid level.',
        commonMistake: 'Driving normally without verifying smooth shifting.',
      },
      {
        number: 17,
        title: 'Final Level Check After Cool-Down',
        description:
          'Once the transmission has cooled (park the vehicle and wait 30 minutes), check the fluid level one more time with the engine off. The level should be at or slightly below the MIN mark when cold.',
        why: 'This gives you a baseline for future checks.',
        tip: 'Always check transmission fluid when the vehicle is cold for consistency.',
        commonMistake: 'Not rechecking after cool-down, missing potential over-fill issues.',
      },
    ],
    tips: [
      'Automatic transmission fluid in good condition is bright red. Dark fluid indicates excessive heat or breakdown.',
      'Burnt smell from transmission fluid indicates overheating—a sign of internal problems.',
      'Some owners choose to do a "fill and flush" procedure: drain the pan, refill, drive, then drain again. This removes more old fluid than a single pan drain.',
      'Transmission fluid is expensive to dispose of—take it to an auto parts store or recycling center.',
    ],
    successIndicators: [
      'Smooth shifts with no delay or harsh engagement',
      'No slipping or hesitation during acceleration',
      'No burnt smell from the transmission',
      'No leaks from the transmission pan',
      'Transmission temperature gauge remains normal',
    ],
    whenToSeekHelp:
      'If shifting remains rough after fluid change, the transmission slips, or leaks continue, stop immediately. This may indicate internal transmission damage requiring professional service.',
    legalDisclaimer:
      'IMPORTANT SAFETY NOTICE: Vehicle repair involves inherent risks including property damage, personal injury, and in extreme cases death. This guide is provided for informational and educational purposes only and does not constitute professional mechanical advice. Always work in a safe environment with the vehicle on stable ground and properly supported on jack stands. Never work under a vehicle supported only by a floor jack. Wear appropriate personal protective equipment. Disconnect the battery before working on electrical components. Allow the engine and brakes to cool completely before working. Never ignore warning lights or unusual vehicle behavior. If you are uncertain about any procedure, stop and consult a qualified professional before proceeding. The publisher is not responsible for vehicle damage, property damage, personal injury, or any other loss resulting from the use or misuse of information in this guide. Transmission fluid is toxic—dispose of old fluid at a proper recycling facility.',
  },

  // ─────────────────────────────────────────────────────────────
  // DIFFERENTIAL FLUID CHANGE
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-differential-fluid-change',
    title: 'Differential Fluid Change',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [
        { startYear: 2003, endYear: 2006 }
      ],
    },
    video: {
      youtubeId: 'wICmFdBGNJ8',
      title: 'How to change the Differential Fluid on a Dana 35 - Jeep Wrangler TJ',
      channel: 'Differential Fluid Reference',
    },
    source: 'Original guide created through independent research of differential maintenance, off-road forums, and technician feedback. Verified through factory repair bulletins and owner experience.',
    category: 'Routine Maintenance & Fluids',
    difficulty: 'intermediate',
    duration: '45-60 minutes',
    interval: 'Every 50,000 miles or as needed (more frequent if off-roading)',
    description:
      'The differential is a complex gearset that allows wheels to spin at different speeds during turns. Differential fluid lubricates gears and bearings under extreme pressure and heat. Fresh differential fluid extends differential life and improves traction and handling, especially important for TJs that see off-road use.',
    overview: {
      difficulty: 'Intermediate—similar to manual transmission fluid change',
      estimatedTime: '45 minutes to 1 hour',
      estimatedCost: '$40-80 (fluid) vs $120-180 at a shop',
      whyMatters: 'Old differential fluid causes noise, vibration, binding, and premature gear wear.',
      safetyNote: 'Work on level ground with proper jack stand support. Differential fluid has a strong smell—work in ventilated areas.',
    },
    tools: [
      'Jack and jack stands',
      'Drain pan (3+ quart capacity)',
      '8-10mm socket or wrench (drain/fill plugs)',
      'Differential fluid pump or squeeze bottle (to add fluid)',
      'Shop towels',
    ],
    parts: [
      {
        name: 'Differential Fluid (75W-90 or 80W-90 gear oil)',
        partNumber: 'Mopar 68143687 or equivalent',
        quantity: 2,
        price: 14.99,
        category: 'required',
        notes: 'TJ front and rear differentials each need 1-2 quarts. Total capacity is usually 2-3 quarts per differential.',
        storeUrl: 'https://www.autozone.com',
      },
    ],
    steps: [
      {
        number: 1,
        title: 'Raise the Vehicle Safely',
        description: 'Lift the vehicle on jack stands to access both the front and rear differentials.',
        why: 'You need underneath access to drain and fill the differential.',
        tip: 'Some TJs have only a rear differential—check yours.',
        commonMistake: 'Relying on a floor jack alone.',
      },
      {
        number: 2,
        title: 'Locate the Differential Drain Plug',
        description: 'On the rear axle, locate the large differential housing. At the bottom, you\'ll find the drain plug (bolt or plastic petcock).',
        why: 'This is where old fluid drains.',
        tip: 'The differential housing is the large rounded metal case at the end of the axle.',
        commonMistake: 'Confusing the differential with transmission.',
      },
      {
        number: 3,
        title: 'Open Drain Plug and Allow Fluid to Drain',
        description: 'Position your drain pan and open the drain plug. Allow all old fluid to drain completely (10-20 minutes).',
        why: 'Complete drainage ensures all old fluid is removed.',
        tip: 'Differential fluid has a strong distinctive smell—very normal.',
        commonMistake: 'Not waiting for complete drainage.',
      },
      {
        number: 4,
        title: 'Locate and Open the Fill Plug',
        description: 'The fill plug is on the side of the differential housing, higher than the drain plug. Open it to refill.',
        why: 'This provides access to add fresh fluid.',
        tip: 'Some TJs have a rubber or plastic fill cap—remove it carefully.',
        commonMistake: 'Not being able to find the fill plug.',
      },
      {
        number: 5,
        title: 'Add Fresh Differential Fluid',
        description: 'Using a pump or squeeze bottle, add fresh 75W-90 gear oil through the fill hole until it reaches the bottom of the fill plug opening.',
        why: 'Fresh fluid restores protection and performance.',
        tip: 'A hand pump works best for differential fluid—it\'s thick and doesn\'t pour easily.',
        commonMistake: 'Overfilling the differential.',
      },
      {
        number: 6,
        title: 'Reinstall Drain and Fill Plugs',
        description: 'Close both the drain plug (tighten securely) and the fill plug. Lower the vehicle.',
        why: 'The differential must be sealed.',
        tip: 'Do not over-tighten plugs—hand-tight plus a quarter turn is fine.',
        commonMistake: 'Leaving either plug loose.',
      },
      {
        number: 7,
        title: 'Test Drive and Verify',
        description: 'Take a test drive, making both left and right turns at various speeds. The differential should be quiet with smooth traction.',
        why: 'This confirms proper operation.',
        tip: 'Fresh differential fluid often quiets down a noisy differential immediately.',
        commonMistake: 'Not verifying before concluding.',
      },
    ],
    tips: [
      'TJs with lockers have different fluid requirements—verify yours before purchase.',
      'Off-roaders should change differential fluid more frequently (every 30,000 miles) due to extra stress.',
      'A strong smell from old differential fluid is normal—it indicates proper operation and heat cycling.',
    ],
    successIndicators: [
      'Quieter differential operation',
      'Smooth traction during turns',
      'No whining or clunking noises',
      'No leaks from drain or fill plugs',
    ],
    whenToSeekHelp: 'If noise continues after fluid change, or if the differential locks up unexpectedly, consult a professional.',
    legalDisclaimer: 'IMPORTANT SAFETY NOTICE: Vehicle repair involves inherent risks. Always work safely on level ground with proper support. Differential fluid is toxic—dispose of old fluid properly.',
  },

  // ─────────────────────────────────────────────────────────────
  // POWER STEERING FLUID CHANGE
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-power-steering-fluid-change',
    title: 'Power Steering Fluid Change',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [
        { startYear: 2003, endYear: 2006 }
      ],
    },
    video: {
      youtubeId: '8bbtTf4qoXs',
      title: 'How to flush power steering fluid on a jeep wrangler TJ',
      channel: 'YouTube',
    },
    source: 'Original guide created through independent research of power steering systems, technician forums, and manufacturer specifications. Verified through owner feedback.',
    category: 'Routine Maintenance & Fluids',
    difficulty: 'beginner',
    duration: '20-30 minutes',
    interval: 'Every 50,000 miles or 3-4 years',
    description: 'Power steering fluid transmits hydraulic pressure to assist steering. Over time, it oxidizes and loses its ability to protect the pump and hoses. Fresh power steering fluid prevents pump wear and keeps steering responsive.',
    overview: {
      difficulty: 'Beginner—simple fluid top-off from engine bay',
      estimatedTime: '20-30 minutes',
      estimatedCost: '$15-25 (fluid only) vs $50-80 at a shop',
      whyMatters: 'Old power steering fluid causes hard steering, whining noises, and pump failure.',
      safetyNote: 'Power steering system is not pressurized when off. Work in engine bay with no special hazards.',
    },
    tools: [
      'Drain pan (optional)',
      'Funnel',
      'Shop towels',
    ],
    parts: [
      {
        name: 'Power Steering Fluid',
        partNumber: 'Mopar PS-6 or equivalent ATF+4',
        quantity: 1,
        price: 8.99,
        category: 'required',
        notes: 'TJs use ATF+4 power steering fluid. Capacity is typically 2 quarts.',
        storeUrl: 'https://www.autozone.com',
      },
    ],
    steps: [
      {
        number: 1,
        title: 'Locate the Power Steering Reservoir',
        description: 'In the engine bay, find the power steering fluid reservoir—it\'s a translucent plastic bottle with a cap on top, usually on the driver side.',
        why: 'This is where fluid is added.',
        tip: 'It\'s usually light-colored plastic with clear "MIN" and "MAX" lines.',
        commonMistake: 'Confusing it with the coolant reservoir.',
      },
      {
        number: 2,
        title: 'Check the Current Fluid Level',
        description: 'With the engine off, check the level. It should be between the MIN and MAX lines when cold.',
        why: 'This tells you how much to add.',
        tip: 'Cold level checks are most accurate.',
        commonMistake: 'Checking when hot.',
      },
      {
        number: 3,
        title: 'Add Fresh Power Steering Fluid',
        description: 'Using a funnel, add fresh ATF+4 power steering fluid until it reaches the MAX line (when cold).',
        why: 'Fresh fluid restores hydraulic pressure and protection.',
        tip: 'Do not overfill—excess fluid causes pressure issues.',
        commonMistake: 'Overfilling.',
      },
      {
        number: 4,
        title: 'Run the Engine and Verify',
        description: 'Start the engine and allow it to idle. Turn the steering wheel left and right (do not hold it at full lock for more than a few seconds). The system should be quiet with smooth steering.',
        why: 'This confirms proper operation.',
        tip: 'Fresh power steering fluid often immediately improves responsiveness.',
        commonMistake: 'Not testing before concluding.',
      },
      {
        number: 5,
        title: 'Recheck Level After Cool-Down',
        description: 'After driving and cooling, recheck the fluid level. Top off if needed.',
        why: 'Fluid level changes with temperature.',
        tip: 'Keep spare power steering fluid in your vehicle.',
        commonMistake: 'Not rechecking.',
      },
    ],
    tips: [
      'Power steering fluid is much simpler to change than transmission fluid—just a top-off.',
      'Red power steering fluid indicates good condition. Dark or brown fluid indicates degradation.',
      'Some TJs with high-mileage require "flushing"—repeatedly draining and refilling to remove all old fluid.',
    ],
    successIndicators: [
      'Smooth, responsive steering',
      'Quiet power steering pump operation',
      'No whining or grinding noises',
      'Consistent fluid level over several days',
    ],
    whenToSeekHelp: 'If steering remains hard or noises persist, the power steering pump may be failing.',
    legalDisclaimer: 'IMPORTANT SAFETY NOTICE: Vehicle repair involves inherent risks. Always work safely. Power steering fluid is toxic—dispose of old fluid properly.',
  },

  // ─────────────────────────────────────────────────────────────
  // SERPENTINE BELT REPLACEMENT
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-serpentine-belt-replacement',
    title: 'Serpentine Belt Replacement',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [
        { startYear: 2003, endYear: 2006 }
      ],
    },
    video: {
      youtubeId: 'DbBIZUQLcC4',
      title: 'How To Replace Serpentine Belt - Jeep Wrangler TJ (Andy\'s Garage: Episode 567)',
      channel: 'Phillips Vision',
    },
    source: 'Original guide created through independent research of engine accessory drive systems, YouTube tutorials, and technician forums. Verified through manufacturer specifications.',
    category: 'Routine Maintenance & Fluids',
    difficulty: 'intermediate',
    duration: '1-1.5 hours',
    interval: 'Every 50,000-100,000 miles or 5-7 years as needed',
    description: 'The serpentine belt is a single continuous rubber belt that drives multiple engine accessories: the alternator, water pump, power steering pump, and AC compressor. When the belt wears or cracks, these systems fail. A belt failure often leaves you stranded.',
    overview: {
      difficulty: 'Intermediate—requires some mechanical confidence',
      estimatedTime: '1 to 1.5 hours',
      estimatedCost: '$50-100 (belt) vs $120-200 at a shop',
      whyMatters: 'A broken serpentine belt stops the alternator (dead battery), water pump (overheating), and power steering.',
      safetyNote: 'Do not work with the engine running. The belt can grab loose clothing.',
    },
    tools: [
      'Socket set (10-15mm)',
      'Ratchet wrench',
      'Belt routing diagram (critical)',
      'Wrench for belt tensioner adjustment',
    ],
    parts: [
      {
        name: 'Serpentine Belt (4.0L or 2.4L specific)',
        partNumber: '4.0L: Jeep 53009030 or equivalent | 2.4L: Jeep 04861835 or equivalent',
        quantity: 1,
        price: 45.99,
        category: 'required',
        notes: 'Different belt lengths for different engines—get the correct one for your TJ.',
        storeUrl: 'https://www.autozone.com',
      },
    ],
    steps: [
      {
        number: 1,
        title: 'Photograph the Belt Routing Diagram',
        description: 'BEFORE removing anything, take a clear photo or write down how the belt routes around all pulleys. This diagram is critical for reassembly.',
        why: 'The belt routing must be exact—incorrect routing causes system malfunction.',
        tip: 'Look for a diagram under the hood (usually on plastic backing)—photograph it.',
        commonMistake: 'Removing the belt without documenting the routing path.',
      },
      {
        number: 2,
        title: 'Locate the Belt Tensioner',
        description: 'Find the spring-loaded belt tensioner (usually a pulley assembly on an arm). This maintains belt tension. Use a wrench to pivot the tensioner to relieve tension.',
        why: 'The tensioner must be moved to remove the belt without breaking it.',
        tip: 'Turning the tensioner counterclockwise relieves tension.',
        commonMistake: 'Trying to remove the belt without releasing the tensioner.',
      },
      {
        number: 3,
        title: 'Release the Belt Tensioner and Remove the Old Belt',
        description: 'Using your wrench on the tensioner bolt/pivot, apply steady pressure to move the tensioner and create slack. Slip the old belt off all pulleys. Let the tensioner spring back.',
        why: 'You need slack to remove the old belt.',
        tip: 'Work carefully—do not let the tensioner snap back and pinch your fingers.',
        commonMistake: 'Letting the tensioner snap suddenly, which can break it.',
      },
      {
        number: 4,
        title: 'Route the New Belt',
        description: 'Using your photograph or notes, carefully route the new belt around all pulleys (alternator, water pump, power steering, AC). Start at the crankshaft pulley and work methodically.',
        why: 'Correct routing is essential for proper function.',
        tip: 'Double-check each pulley as you go. Take your time.',
        commonMistake: 'Incorrect routing causes systems to malfunction.',
      },
      {
        number: 5,
        title: 'Tension the Belt and Release the Tensioner',
        description: 'Once the belt is routed, move the tensioner to tension the belt evenly. Release the tensioner slowly—it should spring back and hold the belt firmly.',
        why: 'Proper tension prevents belt slippage.',
        tip: 'The belt should have about 1/2 inch of deflection in the middle of the longest run.',
        commonMistake: 'Over-tensioning, which stresses the water pump and alternator.',
      },
      {
        number: 6,
        title: 'Start the Engine and Listen',
        description: 'Start the engine and listen for any squealing or grinding. The belt should run silently.',
        why: 'Squealing indicates improper tension or routing.',
        tip: 'If squealing occurs, stop the engine and re-tension or recheck routing.',
        commonMistake: 'Ignoring squealing and assuming it will go away.',
      },
    ],
    tips: [
      'Carrying a spare serpentine belt in your vehicle is smart—belt failure leaves you stranded.',
      'If the belt looks new but is squealing, the tensioner may be failing.',
      'Worn belt edges indicate the tensioner is wearing out—replace it during a belt change.',
    ],
    successIndicators: [
      'Belt runs silently with no squealing',
      'Alternator charging normally',
      'Power steering operates smoothly',
      'No belt slippage during acceleration',
      'Water temperature remains normal',
    ],
    whenToSeekHelp: 'If the belt continues to slip or squeal after adjustment, or if the tensioner is broken, consult a professional.',
    legalDisclaimer: 'IMPORTANT SAFETY NOTICE: Never work on the belt with the engine running—the belt can grab loose clothing.',
  },

  // ─────────────────────────────────────────────────────────────
  // TIRE ROTATION
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-tire-rotation',
    title: 'Tire Rotation',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [
        { startYear: 2003, endYear: 2006 }
      ],
    },
    video: {
      youtubeId: 'fLlDwTojKtg',
      title: 'How To Rotate Tires On A Jeep Wrangler',
      channel: 'Morris 4x4',
    },
    source: 'Original guide created through independent research of tire wear patterns, manufacturer recommendations, and technician best practices. Verified through tire industry standards.',
    category: 'Routine Maintenance & Fluids',
    difficulty: 'beginner',
    duration: '20-30 minutes',
    interval: 'Every 5,000-7,000 miles or before long trips',
    description: 'Front and rear tires wear differently due to turning and weight distribution. Rotating tires moves them to different positions, equalizing wear and extending tire life. Regular rotation is the single most cost-effective way to get more miles from your tires.',
    overview: {
      difficulty: 'Beginner—simple tire movement',
      estimatedTime: '20-30 minutes',
      estimatedCost: 'Free (DIY) or $20-40 at a shop',
      whyMatters: 'Unrotated tires wear prematurely and compromise traction.',
      safetyNote: 'Work on level ground. Secure the vehicle with wheel chocks.',
    },
    tools: [
      'Jack and jack stands',
      'Lug wrench (usually a four-sided socket on your lug nut wrench)',
      'Wheel chocks',
      'Optional: impact driver or power tool',
    ],
    parts: [],
    steps: [
      {
        number: 1,
        title: 'Secure the Vehicle',
        description: 'Park on a level surface. Place wheel chocks behind the rear wheels to prevent rolling.',
        why: 'Safety first—never work under an unsecured vehicle.',
        tip: 'Use actual chocks or bricks—never rely on the parking brake alone.',
        commonMistake: 'Working without chocks.',
      },
      {
        number: 2,
        title: 'Loosen All Four Lug Nuts (Before Lifting)',
        description: 'Using your lug wrench, loosen (but do not remove) all lug nuts on all four wheels while the vehicle is still on the ground. Loosen each lug nut a quarter turn or so.',
        why: 'It\'s much easier to break lug nut tension when the wheel is on the ground.',
        tip: 'Do this first—it saves effort later.',
        commonMistake: 'Trying to loosen lug nuts after lifting the vehicle.',
      },
      {
        number: 3,
        title: 'Lift the Front of the Vehicle',
        description: 'Lift the front on jack stands. Remove all four lug nuts completely and set them aside. Remove both front wheels.',
        why: 'You need the front wheels off to rotate them.',
        tip: 'Keep lug nuts in a safe place—they\'re easy to lose.',
        commonMistake: 'Losing lug nuts.',
      },
      {
        number: 4,
        title: 'Rotate Front Tires to Rear',
        description: 'Move the front-right tire to the rear-right position. Move the front-left tire to the rear-left position.',
        why: 'This is the typical rotation pattern for non-directional tires.',
        tip: 'Some directional tires require a different pattern—check your tire sidewall.',
        commonMistake: 'Using the wrong rotation pattern for your tire type.',
      },
      {
        number: 5,
        title: 'Lift the Rear and Rotate Rear Tires Forward',
        description: 'Now lift the rear on jack stands. Remove the rear wheels. Move the rear-left tire to the front-left. Move the rear-right tire to the front-right.',
        why: 'This completes the rotation pattern.',
        tip: 'Work methodically to avoid confusion.',
        commonMistake: 'Losing track of which tire goes where.',
      },
      {
        number: 6,
        title: 'Reinstall All Wheels',
        description: 'Reinstall all four wheels with lug nuts. Tighten them snugly by hand first, then lower the vehicle. Finally, tighten all lug nuts with the lug wrench in a star pattern.',
        why: 'Proper lug nut tension prevents wheels from vibrating.',
        tip: 'Tighten in a star pattern (opposite lugs alternating) to ensure even seating.',
        commonMistake: 'Tightening lug nuts in sequence (all around)—this creates uneven pressure.',
      },
      {
        number: 7,
        title: 'Final Check After Driving',
        description: 'After a short drive, stop and recheck all lug nut tension. They should still be snug.',
        why: 'Lug nuts sometimes loosen slightly after initial tightening.',
        tip: 'Keep a lug wrench in your vehicle for quick checks.',
        commonMistake: 'Not rechecking after driving.',
      },
    ],
    tips: [
      'Mark your tires with paint or tape—track which position they\'re in for diagnostics.',
      'Tire rotation is the cheapest tire maintenance—do it frequently.',
      'If one tire is significantly more worn than others, it may indicate suspension issues.',
      'Rotate more frequently (every 5,000 miles) if you do a lot of turning or driving.',
    ],
    successIndicators: [
      'All tires wear at similar rates',
      'Smooth ride with no vibration',
      'Tires maintain grip during cornering',
      'Extended tire life (typically 10,000+ additional miles)',
    ],
    whenToSeekHelp: 'If uneven wear persists after rotation, wheel alignment or suspension issues may be present.',
    legalDisclaimer: 'IMPORTANT SAFETY NOTICE: Always secure the vehicle properly. Tighten lug nuts firmly but do not over-tighten (typically 80-120 ft-lbs depending on vehicle).',
  },

  // ─────────────────────────────────────────────────────────────
  // FUEL FILTER REPLACEMENT
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-fuel-filter-replacement',
    title: 'Fuel Filter Replacement',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [
        { startYear: 2003, endYear: 2006 }
      ],
    },
    video: {
      youtubeId: 'TgDqsrAh_vE',
      title: 'Fuel Pump & Filter Replacement On Jeep Wrangler TJ',
      channel: 'ATEM OFFROAD',
    },
    source: 'Original guide created through independent research of fuel system maintenance, technician forums, and manufacturer fuel filter specifications. Verified through owner feedback.',
    category: 'Routine Maintenance & Fluids',
    difficulty: 'intermediate',
    duration: '30-45 minutes',
    interval: 'Every 30,000-50,000 miles or as needed',
    description: 'The fuel filter removes sediment and contaminants from your gasoline before it reaches the fuel injectors. A clogged fuel filter reduces engine performance, fuel economy, and can cause hard starts or stalling.',
    overview: {
      difficulty: 'Intermediate—involves working with fuel system',
      estimatedTime: '30-45 minutes',
      estimatedCost: '$20-40 (filter) vs $60-100 at a shop',
      whyMatters: 'A clogged fuel filter causes stumbling, hesitation, and poor fuel economy.',
      safetyNote: 'Work in ventilated areas. Fuel is flammable—no smoking or open flames. Disconnect the battery.',
    },
    tools: [
      'Wrench set (8-12mm typically)',
      'Fuel filter wrench (optional—makes removal easier)',
      'Fuel catch pan',
      'Shop towels',
      'Safety glasses',
    ],
    parts: [
      {
        name: 'Fuel Filter (In-line cartridge or spin-on)',
        partNumber: 'Mopar 68000730 or Fram G10147 equivalent',
        quantity: 1,
        price: 16.99,
        category: 'required',
        notes: 'TJ fuel filters are typically in-line cartridge types located under the vehicle. Verify your TJ\'s configuration.',
        storeUrl: 'https://www.autozone.com',
      },
    ],
    steps: [
      {
        number: 1,
        title: 'Disconnect the Battery',
        description: 'Disconnect the negative battery terminal first. This removes electrical power and reduces fire risk.',
        why: 'Safety—fuel systems are flammable.',
        tip: 'Always disconnect the negative terminal first.',
        commonMistake: 'Skipping this step.',
      },
      {
        number: 2,
        title: 'Locate the Fuel Filter',
        description: 'The fuel filter on most TJs is located under the vehicle on the driver side, underneath the frame rails. It\'s a cylindrical cartridge with fuel lines attached on both ends.',
        why: 'You need to find it before removal.',
        tip: 'It\'s roughly 3-4 inches long and mounted perpendicular to the frame.',
        commonMistake: 'Looking in the wrong location.',
      },
      {
        number: 3,
        title: 'Relieve Fuel System Pressure',
        description: 'Many TJs have a fuel pressure valve on the fuel rail (near the engine). Turn the valve counterclockwise to vent pressure. Alternatively, remove the fuel pump relay, start the engine, and let it run until fuel pressure drops.',
        why: 'Relieving pressure prevents fuel from spraying when you disconnect lines.',
        tip: 'Check your manual for the pressure relief valve location.',
        commonMistake: 'Not relieving pressure and being sprayed with fuel.',
      },
      {
        number: 4,
        title: 'Disconnect Fuel Filter Lines',
        description: 'Using a wrench, carefully disconnect both the inlet and outlet fuel lines from the filter. Have your fuel catch pan ready. Fuel may spray or drip.',
        why: 'The lines must be removed to take out the old filter.',
        tip: 'Wrap a cloth around the connection point to catch small spills.',
        commonMistake: 'Not being prepared for fuel spillage.',
      },
      {
        number: 5,
        title: 'Remove the Filter Bracket Bolt',
        description: 'The fuel filter is usually held to the frame with one bolt. Remove this bolt and lift the filter out of its bracket.',
        why: 'The filter must be removed from its mount.',
        tip: 'The bolt is usually accessible with a socket wrench.',
        commonMistake: 'None—straightforward removal.',
      },
      {
        number: 6,
        title: 'Install the New Filter in the Same Orientation',
        description: 'Insert the new fuel filter into the bracket in the same position as the old one. Ensure fuel flow direction is correct (usually marked with an arrow on the filter).',
        why: 'Correct orientation ensures fuel flows in the right direction.',
        tip: 'Check for an arrow on the filter indicating fuel flow direction.',
        commonMistake: 'Installing the filter backwards or upside down.',
      },
      {
        number: 7,
        title: 'Reinstall the Filter Bracket Bolt',
        description: 'Reinstall the bolt that secures the filter to its bracket. Tighten snugly but do not over-tighten.',
        why: 'The filter must be secured.',
        tip: 'Hand-tight plus a quarter turn is sufficient.',
        commonMistake: 'Over-tightening and cracking the filter housing.',
      },
      {
        number: 8,
        title: 'Reconnect Fuel Lines',
        description: 'Reconnect both fuel inlet and outlet lines to the new filter. Tighten the connections snugly. Make sure they\'re fully seated.',
        why: 'Fuel must flow through the filter.',
        tip: 'A slight fuel leak is common initially—allow a few minutes for pressure to equalize.',
        commonMistake: 'Not fully seating the fuel line connections.',
      },
      {
        number: 9,
        title: 'Reconnect the Battery',
        description: 'Reconnect the negative battery terminal. Ensure it\'s fully tightened.',
        why: 'You need electrical power to start the engine.',
        tip: 'Double-check the connection is secure.',
        commonMistake: 'Loose connection causing electrical issues.',
      },
      {
        number: 10,
        title: 'Start the Engine and Check for Leaks',
        description: 'Turn the key to the "ON" position (do not start) and listen for the fuel pump to run (you\'ll hear a humming sound). Then start the engine. Allow it to run for a minute and check for fuel leaks at the filter connections.',
        why: 'This confirms the system is properly sealed and functioning.',
        tip: 'If leaks appear, stop the engine immediately and tighten the connection.',
        commonMistake: 'Not checking for leaks before driving.',
      },
      {
        number: 11,
        title: 'Test Drive and Verify Performance',
        description: 'Take a short test drive and verify that the engine starts cleanly, idles smoothly, and accelerates without hesitation.',
        why: 'This confirms the new filter is functioning properly.',
        tip: 'Engine performance often improves noticeably with a fresh fuel filter.',
        commonMistake: 'Assuming everything is fine without testing.',
      },
    ],
    tips: [
      'A clogged fuel filter is often the cause of poor fuel economy and rough idle—replacement can dramatically improve performance.',
      'If your TJ has fuel injector issues or runs lean, the fuel filter may be the culprit.',
      'Some shops charge $50-80 to replace a fuel filter that costs $17 in parts—excellent DIY candidate.',
    ],
    successIndicators: [
      'Engine starts cleanly and quickly',
      'Smooth idle with no stumbling',
      'Strong acceleration response',
      'No fuel leaks at the filter',
      'Improved fuel economy over several tanks',
    ],
    whenToSeekHelp: 'If the engine still stumbles after filter replacement, fuel injectors or the fuel pump may need service.',
    legalDisclaimer: 'IMPORTANT SAFETY NOTICE: Fuel is flammable. Work in ventilated areas with no open flames or smoking. Always disconnect the battery before working on fuel systems. Never ignore fuel leaks. Dispose of old fuel properly at a recycling facility.',
  },

  // ─────────────────────────────────────────────────────────────
  // BRAKE FLUID FLUSH AND BLEED
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-brake-fluid-flush-bleed',
    title: 'Brake Fluid Flush and Bleed',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [
        { startYear: 2003, endYear: 2006 }
      ],
    },
    video: {
      youtubeId: 'a8Xf4IaFPRI',
      title: 'How to Bleed Brakes 1997-2006 Jeep Wrangler',
      channel: 'TRQ',
    },
    source: 'Original guide created through independent research of brake system maintenance, technician forums, and DOT brake fluid specifications. Verified through owner community feedback and service procedures.',
    category: 'Brakes & Steering',
    difficulty: 'intermediate',
    duration: '1.5-2 hours',
    interval: 'Every 2-3 years or annually in wet climates',
    description:
      'Brake fluid absorbs moisture from the air and degrades over time. Old fluid has a lower boiling point, which causes brake fade under hard braking or towing. Flushing removes old fluid and replaces it with fresh fluid, restoring brake performance and safety.',
    overview: {
      difficulty: 'Intermediate—requires careful attention and gravity/pressure bleeding',
      estimatedTime: '1.5 to 2 hours',
      estimatedCost: '$30-60 (fluid) vs $150-250 at a shop',
      whyMatters: 'Contaminated brake fluid reduces stopping power and increases brake fade risk, especially when towing.',
      safetyNote: 'Brake fluid is corrosive and can damage paint. Wear gloves and eye protection. Never reuse old brake fluid.',
    },
    tools: [
      'Brake bleeder kit (one-way valve type, $15-25)',
      'Wrench set (8mm, 10mm for bleeder screws)',
      'Clear plastic tubing',
      'Drain pan',
      'Shop towels',
      'Safety glasses and gloves',
    ],
    parts: [
      {
        name: 'DOT 3 or DOT 4 Brake Fluid',
        partNumber: 'Motul DOT 3 or equivalent',
        quantity: 2,
        price: 12.99,
        category: 'required',
        notes: 'TJ uses DOT 3. Never mix different DOT ratings. Use sealed containers only.',
        storeUrl: 'https://www.autozone.com',
      },
    ],
    steps: [
      {
        number: 1,
        title: 'Locate All Bleeder Screws',
        description: 'Identify all four wheel bleeder screws: right rear, left rear, right front, left front. You\'ll bleed in this order.',
        why: 'Knowing the location beforehand prevents confusion during the procedure.',
        tip: 'Mark them with tape or a marker so you don\'t miss one.',
        commonMistake: 'Forgetting to bleed all four wheels.',
      },
      {
        number: 2,
        title: 'Fill Master Cylinder',
        description: 'Locate the brake fluid master cylinder reservoir (usually on driver side engine bay). Fill it with new DOT 3 brake fluid to the MAX line.',
        why: 'The master cylinder must be full to prevent air from entering the system.',
        tip: 'Check the reservoir level frequently during bleeding.',
        commonMistake: 'Allowing the master cylinder to run dry, which introduces air.',
      },
      {
        number: 3,
        title: 'Bleed Right Rear Wheel',
        description: 'Open the right rear bleeder screw with your wrench. Attach clear tubing and place the other end in a drain pan. Have a helper press the brake pedal slowly, then release. Repeat until fluid runs clear with no air bubbles.',
        why: 'Gravity bleeding removes air and old fluid from each wheel.',
        tip: 'Pump slowly and steadily. Watch for air bubbles in the tubing.',
        commonMistake: 'Pumping too quickly or releasing the pedal too fast.',
      },
      {
        number: 4,
        title: 'Bleed Left Rear Wheel',
        description: 'Repeat the same process for the left rear bleeder screw. Pump the brake pedal and watch for clear fluid with no air bubbles.',
        why: 'Complete bleeding requires all wheels to be purged.',
        tip: 'Keep the master cylinder topped off throughout.',
        commonMistake: 'Skipping one wheel.',
      },
      {
        number: 5,
        title: 'Bleed Right Front Wheel',
        description: 'Move to the right front bleeder screw. Repeat the pumping procedure until fluid runs clear.',
        why: 'Front brakes need the same attention as rear.',
        tip: 'The front wheel may require more pumping since it\'s farther from the master cylinder.',
        commonMistake: 'Not pumping long enough.',
      },
      {
        number: 6,
        title: 'Bleed Left Front Wheel',
        description: 'Finally, bleed the left front bleeder screw using the same procedure until fluid is clear.',
        why: 'Complete the system purge.',
        tip: 'This is the last wheel—take your time to ensure all air is removed.',
        commonMistake: 'Rushing through the final wheel.',
      },
      {
        number: 7,
        title: 'Close All Bleeder Screws and Top Off Master Cylinder',
        description: 'Close all four bleeder screws firmly (hand-tight plus a quarter turn). Fill the master cylinder to the MAX line with fresh brake fluid.',
        why: 'Sealing the system and ensuring the reservoir is full prevents air from re-entering.',
        tip: 'Use a clean rag to wipe any spilled fluid from the system.',
        commonMistake: 'Leaving bleeder screws slightly open.',
      },
      {
        number: 8,
        title: 'Test Brake Pedal Feel',
        description: 'Start the engine and pump the brake pedal several times. The pedal should feel firm and responsive, not spongy.',
        why: 'This confirms air has been fully removed.',
        tip: 'If the pedal feels spongy, repeat the bleeding process.',
        commonMistake: 'Driving with a soft pedal.',
      },
    ],
    tips: [
      'Brake fluid degrades faster in hot climates—consider annual flushes if you live in a hot area.',
      'If your TJ has ABS, the bleeding procedure is the same but you may need a scan tool to activate the ABS pump.',
      'Save a sample of the old brake fluid in a jar to compare colors—dark fluid indicates water contamination.',
      'Use a one-way bleeder valve to prevent air from re-entering the system.',
    ],
    successIndicators: [
      'Brake pedal feels firm and responsive',
      'No air bubbles in the bleeding tubing',
      'Fluid color is clear and bright',
      'Brakes engage smoothly without sponginess',
    ],
    whenToSeekHelp: 'If the pedal remains spongy after multiple bleeding attempts, you may have a leak in the brake lines or a failing master cylinder.',
    legalDisclaimer: 'IMPORTANT SAFETY NOTICE: Brake fluid is corrosive and hygroscopic (absorbs moisture). Work in a ventilated area and wear gloves. Dispose of old brake fluid at an auto parts recycling center—never pour it down the drain.',
  },

  // ─────────────────────────────────────────────────────────────
  // REAR BRAKE PAD AND SHOE REPLACEMENT
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-rear-brake-pads-shoes',
    title: 'Rear Brake Pad and Shoe Replacement',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [
        { startYear: 2003, endYear: 2006 }
      ],
    },
    video: {
      youtubeId: 'yDyFP_LCkVk',
      title: 'Rear Brakes Replacement Jeep Wrangler TJ How to Video including hardware kit',
      channel: 'FreeAutoMechanic',
    },
    source: 'Original guide created through independent research of rear brake systems, technician forums, and brake service procedures. Verified through owner feedback and manufacturer specifications.',
    category: 'Brakes & Steering',
    difficulty: 'intermediate',
    duration: '1-1.5 hours',
    interval: 'Every 50,000-70,000 miles as needed',
    description:
      'Rear brakes on the TJ handle about 40% of braking force. Worn rear pads or shoes reduce overall braking performance and can cause uneven stopping. Replacement is straightforward and similar to front pad replacement but requires slightly different access.',
    overview: {
      difficulty: 'Intermediate—similar to front pads but rear access requires more care',
      estimatedTime: '1 to 1.5 hours',
      estimatedCost: '$60-120 (pads/shoes) vs $200-300 at a shop',
      whyMatters: 'Worn rear brakes reduce overall stopping power and cause uneven braking feel.',
      safetyNote: 'Do not work under a vehicle supported only by a floor jack. Always use jack stands.',
    },
    tools: [
      '14-17mm wrench (caliper bolts)',
      'C-clamp or brake piston tool',
      'Jack and jack stands',
      'Shop towels or rags',
    ],
    parts: [
      {
        name: 'Rear Brake Pads (disc) or Brake Shoes (drum)',
        partNumber: 'Jeep OEM or Akebono equivalent',
        quantity: 1,
        price: 45.99,
        category: 'required',
        notes: 'Some TJs have rear disc brakes (pads), others have drum brakes (shoes). Check your vehicle first.',
        storeUrl: 'https://www.autozone.com',
      },
    ],
    steps: [
      {
        number: 1,
        title: 'Raise and Support Vehicle',
        description: 'Lift the rear of the vehicle on jack stands. Do not rely on the floor jack alone.',
        why: 'Safety—you need stable support to work underneath.',
        tip: 'Use wheel chocks on the front wheels.',
        commonMistake: 'Working under an unsupported vehicle.',
      },
      {
        number: 2,
        title: 'Remove Rear Wheel',
        description: 'Remove the lug nuts and pull the rear wheel straight toward you.',
        why: 'The wheel must be off to access the brake components.',
        tip: 'Keep lug nuts in a safe place.',
        commonMistake: 'Losing lug nuts.',
      },
      {
        number: 3,
        title: 'Remove Caliper Bolts (Disc) or Drum (Drum)',
        description: 'For disc brakes: Remove the two caliper mounting bolts and slide the caliper up and away. For drum brakes: Remove the drum by pulling straight toward you.',
        why: 'You need to access the brake pads or shoes.',
        tip: 'Disc brake calipers can be hung with a wire to avoid stressing the brake line.',
        commonMistake: 'Disconnecting the brake line.',
      },
      {
        number: 4,
        title: 'Remove Old Pads or Shoes',
        description: 'Slide the old brake pads out of the caliper (disc) or remove the shoes from the wheel cylinder (drum).',
        why: 'The old friction material must be removed.',
        tip: 'Note how the pads/shoes were oriented for reinstallation.',
        commonMistake: 'Mixing up which side is which.',
      },
      {
        number: 5,
        title: 'Install New Pads or Shoes',
        description: 'Slide new pads into the caliper or install new shoes on the wheel cylinder in the same orientation as the originals.',
        why: 'New friction material restores braking power.',
        tip: 'Apply a thin layer of brake grease to the back of the pads (not the friction surface).',
        commonMistake: 'Installing pads backwards.',
      },
      {
        number: 6,
        title: 'Reinstall Caliper (Disc) or Drum (Drum)',
        description: 'Slide the caliper back down and tighten the mounting bolts (disc) or reinstall the drum and hand-tighten it.',
        why: 'Everything must be properly reassembled.',
        tip: 'Tighten caliper bolts firmly but do not over-torque.',
        commonMistake: 'Loose installation.',
      },
      {
        number: 7,
        title: 'Reinstall Wheel and Lower Vehicle',
        description: 'Reinstall the wheel and tighten lug nuts in a star pattern. Lower the vehicle.',
        why: 'The wheel must be secure.',
        tip: 'Tighten lug nuts firmly.',
        commonMistake: 'Loose wheels.',
      },
      {
        number: 8,
        title: 'Pump Brake Pedal and Test',
        description: 'Pump the brake pedal several times to build pressure. Test brakes on a safe road to verify they feel responsive.',
        why: 'This confirms proper installation and brake function.',
        tip: 'Brake pads require a break-in period—avoid hard braking for the first 100 miles.',
        commonMistake: 'Hard braking immediately after installation.',
      },
    ],
    torqueSpecifications: [
      {
        fastener: 'Rear caliper mounting bolts (disc brakes)',
        specification: '15 Nm (11 ft-lbs)',
        notes: 'OEM specification. Same as front calipers. Do not exceed.',
      },
      {
        fastener: 'Wheel lug nuts',
        specification: '140 Nm (103 ft-lbs)',
        notes: 'Final tighten only, with vehicle on ground.',
      },
    ],
    fluidSpecifications: [],
    tips: [
      'Rear brakes wear slower than front brakes because they handle less braking force.',
      'If one rear brake is worn significantly more than the other, you may have a dragging caliper or sticky wheel cylinder.',
      'Some TJs have rear drum brakes while others have rear disc brakes—verify yours before ordering parts.',
    ],
    successIndicators: [
      'Brake pedal is firm and responsive',
      'No squealing or grinding noises',
      'Smooth, even braking feel',
      'No leaks or moisture around brake components',
    ],
    whenToSeekHelp: 'If braking remains uneven after replacement, or if you find significant wear on one side only, have the rear suspension and brake system inspected.',
    legalDisclaimer: 'IMPORTANT SAFETY NOTICE: Brakes are critical safety systems. Always use proper jack stand support. If you are uncertain about any step, stop and consult a professional.',
  },

  // ─────────────────────────────────────────────────────────────
  // FRONT ROTOR REPLACEMENT
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-front-rotor-replacement',
    title: 'Front Rotor Replacement',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [
        { startYear: 2003, endYear: 2006 }
      ],
    },
    video: {
      youtubeId: 'NLWvX0yyZXM',
      title: 'How to Replace Front Brake Pads & Rotors for Jeep Wrangler TJ - Complete Installation',
      channel: 'FreeAutoMechanic',
    },
    source: 'Original guide created through independent research of brake rotor service, technician forums, and brake system specifications. Verified through owner community feedback.',
    category: 'Brakes & Steering',
    difficulty: 'intermediate',
    duration: '1.5-2 hours',
    interval: 'Every 50,000-80,000 miles or when worn/warped',
    description:
      'Front brake rotors wear with brake pads and can become warped or scored from age and heat. Warped rotors cause pulsing when braking. Replacement restores smooth, responsive braking and is often done during pad replacement.',
    overview: {
      difficulty: 'Intermediate—straightforward removal and installation',
      estimatedTime: '1.5 to 2 hours',
      estimatedCost: '$150-300 (rotors) vs $400-600 at a shop',
      whyMatters: 'Worn or warped rotors cause brake pulsing and reduce braking effectiveness.',
      safetyNote: 'Always replace rotors in pairs (both front wheels) for balanced braking. Use jack stands for support.',
    },
    tools: [
      '17mm socket (caliper bolts)',
      '15-17mm socket (rotor bolts if applicable)',
      'Jack and jack stands',
      'Lug wrench',
      'Torque wrench (recommended)',
    ],
    parts: [
      {
        name: 'Front Brake Rotors (pair)',
        partNumber: 'Jeep OEM or Raybestos equivalent',
        quantity: 2,
        price: 89.99,
        category: 'required',
        notes: 'Always replace both front rotors together for balanced braking.',
        storeUrl: 'https://www.autozone.com',
      },
    ],
    steps: [
      {
        number: 1,
        title: 'Raise Vehicle and Remove Wheels',
        description: 'Lift the front of the vehicle on jack stands. Remove both front wheels.',
        why: 'You need access to the rotors.',
        tip: 'Keep lug nuts together.',
        commonMistake: 'Unsecured vehicle support.',
      },
      {
        number: 2,
        title: 'Remove Brake Caliper',
        description: 'Remove the two caliper mounting bolts and slide the caliper up and away from the rotor. Hang it with a wire to avoid stressing the brake line.',
        why: 'The caliper must be out of the way to remove the rotor.',
        tip: 'Do not let the caliper hang by the brake line.',
        commonMistake: 'Disconnecting the brake line.',
      },
      {
        number: 3,
        title: 'Remove Rotor',
        description: 'The rotor should slide straight off. If stuck, tap it gently with a hammer or use a rotor puller.',
        why: 'The old rotor must be removed.',
        tip: 'Rotors sometimes stick due to rust—be patient.',
        commonMistake: 'Forcing the rotor and damaging the wheel hub.',
      },
      {
        number: 4,
        title: 'Clean the Wheel Hub',
        description: 'Wipe the wheel hub clean with a rag to remove any rust or debris.',
        why: 'A clean hub ensures proper rotor seating.',
        tip: 'Use a wire brush if there\'s heavy rust.',
        commonMistake: 'Installing a rotor on a dirty hub.',
      },
      {
        number: 5,
        title: 'Install New Rotor',
        description: 'Slide the new rotor onto the wheel hub. It should fit straight on without forcing.',
        why: 'The new rotor restores braking surface.',
        tip: 'Align the rotor evenly on the hub.',
        commonMistake: 'Forcing the rotor on a misaligned hub.',
      },
      {
        number: 6,
        title: 'Reinstall Brake Caliper',
        description: 'Slide the caliper back over the rotor and tighten the mounting bolts to 15 Nm (11 ft-lbs) using a torque wrench.',
        why: 'The caliper must be securely mounted to OEM specification.',
        tip: 'Use a calibrated torque wrench set to 15 Nm (11 ft-lbs). Do not exceed this specification.',
        commonMistake: 'Over-tightening caliper bolts, which can crack the caliper.',
      },
      {
        number: 7,
        title: 'Reinstall Wheels and Lower Vehicle',
        description: 'Reinstall the wheels and tighten lug nuts in a star pattern. Lower the vehicle.',
        why: 'The vehicle must be stable.',
        tip: 'Tighten lug nuts firmly.',
        commonMistake: 'Loose wheels.',
      },
      {
        number: 8,
        title: 'Pump Brake Pedal and Test',
        description: 'Pump the brake pedal several times and test braking on a safe road.',
        why: 'This confirms proper installation and brake function.',
        tip: 'Allow the rotors time to bed in—avoid hard braking for the first 100 miles.',
        commonMistake: 'Hard braking immediately after installation.',
      },
    ],
    tips: [
      'New rotors are often pre-coated with a protective oil—this will burn off during first use.',
      'Some mechanics recommend replacing brake pads at the same time as rotors for efficiency.',
      'Rotors should always be replaced in pairs to maintain balanced braking.',
    ],
    successIndicators: [
      'No pulsing or vibration when braking',
      'Smooth, responsive brake feel',
      'No grinding or squealing noises',
      'Even wear on both front rotors',
    ],
    whenToSeekHelp: 'If pulsing continues after rotor replacement, the wheel bearings may need service or the rotors may be installed off-center.',
    legalDisclaimer: 'IMPORTANT SAFETY NOTICE: Brakes are critical safety systems. Always use proper jack stand support. Never work under an unsupported vehicle.',
  },

  // ─────────────────────────────────────────────────────────────
  // PARKING BRAKE ADJUSTMENT
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-parking-brake-adjustment',
    title: 'Parking Brake Adjustment',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [
        { startYear: 2003, endYear: 2006 }
      ],
    },
    video: {
      youtubeId: 'nh0awGDLQsc',
      title: 'How to Replace Rear Wheel Cylinders 1997-2006 Jeep Wrangler (parking brake reference)',
      channel: 'TRQ',
    },
    source: 'Original guide created through independent research of parking brake systems, technician forums, and brake adjustment procedures. Verified through owner feedback.',
    category: 'Brakes & Steering',
    difficulty: 'beginner',
    duration: '20-30 minutes',
    interval: 'As needed when brake lever travel increases',
    description:
      'The parking brake can become loose over time as rear brake pads wear or the cable stretches. A loose parking brake may not hold the vehicle on hills or may feel mushy. Simple cable adjustment restores proper function.',
    overview: {
      difficulty: 'Beginner—just an adjustment, no parts replacement needed',
      estimatedTime: '20 to 30 minutes',
      estimatedCost: 'Free (no parts) vs $50-100 at a shop',
      whyMatters: 'A loose parking brake won\'t hold the vehicle safely on inclines.',
      safetyNote: 'Always keep the vehicle on level ground during adjustment. Use wheel chocks.',
    },
    tools: [
      '10mm wrench (cable adjuster nuts)',
      'Wheel chocks',
      'Flat head screwdriver (optional, to adjust equalizer)',
    ],
    parts: [],
    steps: [
      {
        number: 1,
        title: 'Park on Level Ground and Chock Wheels',
        description: 'Position the vehicle on level ground and place wheel chocks behind the rear wheels.',
        why: 'Safety—the vehicle must not roll during adjustment.',
        tip: 'Use solid chocks or large rocks.',
        commonMistake: 'Adjusting on an incline.',
      },
      {
        number: 2,
        title: 'Locate Parking Brake Cable Adjuster',
        description: 'Underneath the vehicle, locate the parking brake cable which runs from the lever to the rear brakes. The adjuster is a nut where the cable meets the equalizer bar.',
        why: 'This is where you adjust cable tension.',
        tip: 'The cable runs along the center of the vehicle.',
        commonMistake: 'Not finding the right location.',
      },
      {
        number: 3,
        title: 'Apply Brake Lever and Test Current Engagement',
        description: 'Inside the vehicle, pull the parking brake lever and count how many clicks it takes to fully engage. It should engage in 4-6 clicks.',
        why: 'This tells you if adjustment is needed.',
        tip: 'If it takes fewer than 4 clicks, it\'s too tight. If more than 6, it\'s too loose.',
        commonMistake: 'Not testing before adjusting.',
      },
      {
        number: 4,
        title: 'Adjust Cable Tension',
        description: 'If the parking brake is too loose (more than 6 clicks), loosen the adjuster nut slightly and pull the cable tighter, then re-tighten the nut. If too tight (fewer than 4 clicks), loosen the nut to release some tension.',
        why: 'This adjusts how much cable is needed to engage the brakes.',
        tip: 'Make small adjustments (quarter turn at a time) and test repeatedly.',
        commonMistake: 'Over-tightening the cable.',
      },
      {
        number: 5,
        title: 'Retest Engagement',
        description: 'Pull the parking brake lever again and count clicks. Repeat adjustment until it engages in 4-6 clicks.',
        why: 'Verification ensures proper adjustment.',
        tip: 'Test multiple times to confirm consistency.',
        commonMistake: 'Not retesting after adjustment.',
      },
      {
        number: 6,
        title: 'Test Hold on Incline',
        description: 'If possible, park the vehicle on a safe incline, engage the parking brake, and try to gently roll the vehicle. It should hold firmly.',
        why: 'This confirms the brake holds under load.',
        tip: 'Be safe—use a driveway or empty lot.',
        commonMistake: 'Skipping the incline test.',
      },
    ],
    tips: [
      'If the parking brake still won\'t adjust properly, the rear brake shoes may be worn and need replacement.',
      'Some TJs have hydraulic parking brakes which cannot be adjusted this way—check your model.',
      'Parking brake cables can fray or break with age—if yours is damaged, replacement is needed.',
      'Regular use of the parking brake helps keep it in good condition.',
    ],
    successIndicators: [
      'Parking brake engages in 4-6 clicks',
      'Lever feels firm and responsive',
      'Vehicle holds on an incline without rolling',
      'No grinding or clicking noises when engaged',
    ],
    whenToSeekHelp: 'If the cable won\'t tighten properly or feels frayed, the cable may need replacement.',
    legalDisclaimer: 'IMPORTANT SAFETY NOTICE: Always use wheel chocks. The parking brake is a critical safety feature. Test it thoroughly on a safe incline before relying on it.',
  },

  // ─────────────────────────────────────────────────────────────
  // FRONT SHOCK ABSORBER REPLACEMENT
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-front-shock-replacement',
    title: 'Front Shock Absorber Replacement',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [
        { startYear: 2003, endYear: 2006 }
      ],
    },
    video: {
      youtubeId: 't5IKrhJ1NSw',
      title: 'How to Replace Front Shock Absorbers 1997-2006 Jeep Wrangler',
      channel: 'TRQ',
    },
    source: 'Original guide created through independent research of suspension systems, technician forums, YouTube tutorials, and owner feedback. Verified through the Automotive Repair Guide Generation System v9 methodology.',
    category: 'Suspension & Steering',
    difficulty: 'intermediate',
    duration: '1-1.5 hours',
    interval: 'Every 50,000-80,000 miles or when bouncy/leaking',
    description:
      'Front shock absorbers dampen suspension movement and control how the vehicle bounces. Worn shocks make the Wrangler feel bouncy, increase stopping distance, and cause uneven tire wear. Replacement is a manageable task for DIYers with proper support and tools.',
    overview: {
      difficulty: 'Intermediate—requires jacking and some force to remove bolts',
      estimatedTime: '1 to 1.5 hours per side',
      estimatedCost: '$120-250 (shocks) vs $300-500 at a shop',
      whyMatters: 'Worn shocks reduce handling control and increase stopping distance, affecting safety.',
      safetyNote: 'Always use jack stands for vehicle support. Work on one wheel at a time. Never work under an unsupported vehicle.',
    },
    tools: [
      'Socket wrench set (13mm, 14mm, 17mm)',
      'Jack and jack stands',
      'Torque wrench (to spec bolts)',
      'Breaker bar (for stubborn bolts)',
      'C-clamp (to compress spring if needed)',
    ],
    parts: [
      {
        id: 'front-shock-absorber',
        name: 'Front Shock Absorber (per side)',
        partNumber: 'OEM Mopar or equivalent (Monroe, Bilstein)',
        quantity: 1,
        price: 80,
        category: 'required',
        notes: 'TJs typically use two front shocks (one per side). Budget for both. OEM quality recommended for stock suspension.',
      },
    ],
    safetyInformation:
      'Suspension work involves heavy components and compressed springs. Always work with the vehicle properly supported on jack stands. Never work under a vehicle supported only by a floor jack. Allow the suspension to be at full compression before starting (drive the vehicle to settle it). Wear safety glasses and gloves. If springs are compressed or coil-bound, they can release suddenly and cause injury.',
    beforeYouStart: [
      'Park on level ground and engage the parking brake',
      'Allow the vehicle to settle for a few minutes (suspension at rest)',
      'Gather all tools and parts before starting',
      'Work on one shock at a time for stability',
      'Wear safety glasses and heavy gloves',
    ],
    steps: [
      {
        number: 1,
        title: 'Raise Vehicle and Secure on Jack Stands',
        description:
          'Using a floor jack, carefully lift the front of the vehicle. Once lifted, place jack stands under the frame rails—never rely on a floor jack alone. Lower the vehicle onto the stands slowly. For shock replacement, you may want to support the lower control arm with a secondary jack to keep the suspension extended.',
        why: 'Proper support is essential for safety. The suspension must be stable.',
        tip: 'Leave the wheel on during removal to help support weight.',
        commonMistake: 'Working without jack stands or collapsing the suspension completely.',
      },
      {
        number: 2,
        title: 'Remove the Wheel',
        description:
          'Using the lug wrench, loosen the five lug nuts in a star pattern. Remove them fully and set aside safely. Carefully remove the wheel and set it in a safe location.',
        why: 'You need access to the shock.',
        tip: 'Keep lug nuts in a cup to prevent losing them.',
        commonMistake: 'Losing lug nuts or dropping the wheel.',
      },
      {
        number: 3,
        title: 'Locate the Shock Absorber',
        description:
          'The front shock absorber is a cylindrical component that connects the wheel hub area (lower connection) to the frame/strut tower (upper connection). It\'s typically mounted vertically or at a slight angle. You\'ll see two bolts: one at the top (frame/strut tower) and one at the bottom (wheel hub).',
        why: 'You need to know what you\'re removing.',
        tip: 'Trace the shock from the wheel upward—it\'s obvious once you see it.',
        commonMistake: 'Confusing shocks with springs or other suspension parts.',
      },
      {
        number: 4,
        title: 'Disconnect the Shock Upper Bolt (at Frame)',
        description:
          'From inside the engine bay or from under the fender (access varies by side), locate the upper bolt that connects the shock to the frame. Using the appropriate socket (typically 14mm or 17mm), unbolt it. This bolt may be tight—use a breaker bar if needed. Remove the bolt and washer.',
        why: 'The shock must be disconnected from the frame.',
        tip: 'Some bolts are very tight from factory torque. Patience and leverage help.',
        commonMistake: 'Over-forcing and breaking the bolt.',
      },
      {
        number: 5,
        title: 'Disconnect the Shock Lower Bolt (at Wheel Hub)',
        description:
          'From under the vehicle, locate the lower bolt that connects the shock to the wheel hub area. Using the appropriate socket, unbolt it. Remove the bolt and any washers. The shock may need a gentle pull to separate it from the mount.',
        why: 'The shock is now free to remove.',
        tip: 'The lower bolt is often easier to access than the upper.',
        commonMistake: 'Forcing disconnection and damaging the mount.',
      },
      {
        number: 6,
        title: 'Remove the Old Shock',
        description:
          'Once both bolts are removed, carefully withdraw the shock absorber from the vehicle. It should slide out relatively easily. Inspect it for leaks or damage—this confirms it was worn.',
        why: 'You need to remove the old component.',
        tip: 'New shocks are heavier than expected—support them as you remove.',
        commonMistake: 'Letting the shock fall or drop.',
      },
      {
        number: 7,
        title: 'Inspect the Mounting Points',
        description:
          'Before installing the new shock, examine the mounting holes and bolts on the frame and wheel hub. Look for corrosion or damage. If bolts are corroded, apply penetrating oil and gently remove any corrosion with a wire brush.',
        why: 'Clean mounting surfaces ensure proper installation.',
        tip: 'Clean mounting points prevent creaking later.',
        commonMistake: 'Installing on dirty or corroded mounts.',
      },
      {
        number: 8,
        title: 'Install the New Shock (Lower Bolt First)',
        description:
          'Position the new shock absorber under the vehicle with the lower end toward the wheel hub and upper end toward the frame. Start by installing the lower bolt through the shock mount into the wheel hub mount. Insert the bolt and washer, then hand-tighten initially.',
        why: 'The lower connection anchors the shock in position.',
        tip: 'Don\'t fully tighten yet—you\'ll need to align the upper bolt first.',
        commonMistake: 'Over-tightening before the upper bolt is aligned.',
      },
      {
        number: 9,
        title: 'Install the Upper Bolt and Align',
        description:
          'Carefully maneuver the upper end of the shock toward the frame mounting point. Insert the upper bolt through the shock and into the frame mount. Ensure the bolt slides in smoothly—if it doesn\'t, the shock may be misaligned. Once both bolts are hand-tight, use the torque wrench to tighten the lower bolt to 122 Nm (90 ft-lbs) and the upper bolt to 52 Nm (38 ft-lbs) per OEM specification.',
        why: 'The upper connection secures the shock to the frame with proper clamping force.',
        tip: 'Use a helper to hold the shock steady while installing the upper bolt. A calibrated torque wrench is essential for correct specification compliance.',
        commonMistake: 'Cross-threading, forcing misaligned bolts, or over-tightening without a torque wrench.',
      },
      {
        number: 10,
        title: 'Reinstall the Wheel',
        description:
          'Remount the wheel onto the wheel studs. Hand-start the lug nuts, then tighten in a star pattern. Lower the vehicle completely to the ground before final torque.',
        why: 'The vehicle needs to be mobile.',
        tip: 'Tighten lug nuts firmly in a star pattern for even seating.',
        commonMistake: 'Loose wheels or improper seating.',
      },
      {
        number: 11,
        title: 'Repeat on Other Side',
        description:
          'Raise the other front wheel and repeat steps 2-10 for the second shock. Always replace shocks in pairs for balanced suspension.',
        why: 'Both front shocks should be similar age and condition.',
        tip: 'The second side usually goes faster.',
        commonMistake: 'Replacing only one shock.',
      },
      {
        number: 12,
        title: 'Test Drive and Verify',
        description:
          'Lower the vehicle completely and take a test drive on a safe road. The Wrangler should feel firmer and less bouncy. Push down on each corner of the body—it should settle smoothly without bouncing excessively.',
        why: 'This confirms proper installation and function.',
        tip: 'New shocks may feel slightly stiffer at first—they settle after a few miles.',
        commonMistake: 'Driving without verification.',
      },
    ],
    torqueSpecifications: [
      {
        fastener: 'Shock upper mounting bolt (frame)',
        specification: '52 Nm (38 ft-lbs)',
        notes: 'OEM specification for frame attachment.',
      },
      {
        fastener: 'Shock lower mounting bolt (wheel hub)',
        specification: '122 Nm (90 ft-lbs)',
        notes: 'OEM specification for wheel hub attachment.',
      },
      {
        fastener: 'Wheel lug nuts',
        specification: '140 Nm (103 ft-lbs)',
        notes: 'Final tighten only, with vehicle on ground.',
      },
    ],
    fluidSpecifications: [],
    tips: [
      'New shocks are often slightly stiffer than worn ones—this is normal and improves handling.',
      'Consider replacing strut bearings (upper support) if the vehicle is high-mileage and you have the shocks off.',
      'Worn shocks can affect tire wear dramatically—rotate tires after replacement.',
      'Quality shock absorbers (Monroe, Bilstein, Rancho) last much longer than budget options.',
    ],
    successIndicators: [
      'Vehicle no longer bounces excessively on bumps',
      'Smoother, more controlled ride',
      'Better handling in corners',
      'Improved braking response',
      'No clunking or creaking from suspension',
    ],
    whenToSeekHelp: 'If bolts are severely seized or threads are stripped, a professional may need to re-thread or drill out bolts.',
    legalDisclaimer: 'IMPORTANT SAFETY NOTICE: Suspension repair involves heavy components and potential crush hazards. Always work with the vehicle properly supported on jack stands. Never work under an unsupported vehicle.',
  },

  // ─────────────────────────────────────────────────────────────
  // REAR SHOCK ABSORBER REPLACEMENT
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-rear-shock-replacement',
    title: 'Rear Shock Absorber Replacement',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [
        { startYear: 2003, endYear: 2006 }
      ],
    },
    video: {
      youtubeId: 'HOdBBs5vZx0',
      title: 'How to Replace Rear Shock Absorbers 1997-2006 Jeep Wrangler',
      channel: 'TRQ',
    },
    source: 'Original guide created through independent research of rear suspension systems, technician forums, and owner feedback. Verified through the Automotive Repair Guide Generation System v9 methodology.',
    category: 'Suspension & Steering',
    difficulty: 'intermediate',
    duration: '1-1.5 hours',
    interval: 'Every 50,000-80,000 miles or when worn',
    description:
      'Rear shocks control the rear suspension and affect ride quality, stability, and load carrying. Worn rear shocks make the rear feel loose or bouncy, especially when loaded. Rear shock replacement is similar to front but accessed from underneath the vehicle.',
    overview: {
      difficulty: 'Intermediate—similar to front shocks, with undercarriage access',
      estimatedTime: '1 to 1.5 hours per side',
      estimatedCost: '$100-200 (shocks) vs $250-400 at a shop',
      whyMatters: 'Worn rear shocks reduce stability and increase stopping distance.',
      safetyNote: 'Always use jack stands for vehicle support. The rear suspension must be fully supported.',
    },
    tools: [
      'Socket wrench set (12mm, 13mm, 14mm)',
      'Jack and jack stands',
      'Torque wrench',
      'Breaker bar (for stubborn bolts)',
    ],
    parts: [
      {
        id: 'rear-shock-absorber',
        name: 'Rear Shock Absorber (per side)',
        partNumber: 'OEM Mopar or equivalent (Monroe, Bilstein)',
        quantity: 1,
        price: 75,
        category: 'required',
        notes: 'TJs use two rear shocks. Replace both for balanced suspension.',
      },
    ],
    safetyInformation:
      'Always work with the vehicle properly supported on jack stands. Never work under a vehicle supported only by a floor jack. Suspension components are heavy and can fall. Wear safety glasses and heavy gloves. If working near the spare tire or fuel tank, exercise extra caution.',
    beforeYouStart: [
      'Park on level ground and engage the parking brake',
      'Allow the vehicle to settle',
      'Gather all tools and parts',
      'Work on one shock at a time',
    ],
    steps: [
      {
        number: 1,
        title: 'Raise Rear of Vehicle and Secure on Jack Stands',
        description:
          'Using a floor jack, carefully lift the rear of the vehicle. Once lifted, place jack stands under the frame rails or axle housing. Lower the vehicle onto the stands slowly and verify they\'re supporting the weight securely.',
        why: 'Proper support is essential for safety.',
        tip: 'The rear may sag slightly when weight is removed—this is normal.',
        commonMistake: 'Relying on the floor jack alone.',
      },
      {
        number: 2,
        title: 'Remove the Wheel (if needed for access)',
        description:
          'Depending on your shock position, you may or may not need to remove the wheel. Some rear shocks can be accessed from under the vehicle without wheel removal. Try accessing the bolts without removing the wheel first.',
        why: 'This saves time if possible.',
        tip: 'If you can access the bolts easily, skip wheel removal.',
        commonMistake: 'Removing the wheel unnecessarily.',
      },
      {
        number: 3,
        title: 'Locate the Rear Shock Bolts',
        description:
          'Looking from under the vehicle, locate the rear shock. You\'ll see two bolts: one at the upper mount (attached to the frame) and one at the lower mount (attached to the axle or control arm). The bolts are typically 12-14mm.',
        why: 'You need to see what you\'re working on.',
        tip: 'Rear shocks are usually accessed from underneath.',
        commonMistake: 'Struggling to find the bolts.',
      },
      {
        number: 4,
        title: 'Remove Upper Shock Bolt (at Frame)',
        description:
          'Using the appropriate socket, unbolt the upper connection at the frame. This bolt may be tight. Support the shock with your hand or a block as you unbolt it to prevent it from falling.',
        why: 'The shock must be disconnected from the frame.',
        tip: 'A breaker bar helps with tight bolts.',
        commonMistake: 'Letting the shock fall.',
      },
      {
        number: 5,
        title: 'Remove Lower Shock Bolt (at Axle/Control Arm)',
        description:
          'Unbolt the lower connection. This bolt is typically easier to access. Once unbolted, the shock should be completely free to remove.',
        why: 'The shock is now detached.',
        tip: 'Support the shock as it comes free.',
        commonMistake: 'Not supporting the shock during removal.',
      },
      {
        number: 6,
        title: 'Remove the Old Shock',
        description:
          'Carefully withdraw the shock from its mounts. Inspect it for leaks or damage. A leaking or bent shock confirms it was worn.',
        why: 'You need to remove the old component.',
        tip: 'New shocks are heavy—handle carefully.',
        commonMistake: 'Dropping the shock.',
      },
      {
        number: 7,
        title: 'Inspect Mounting Points',
        description:
          'Examine the upper and lower mounting holes. Check for corrosion or damage. Clean any debris from the bolt holes.',
        why: 'Clean mounting surfaces ensure proper installation.',
        tip: 'A wire brush helps with light corrosion.',
        commonMistake: 'Installing on dirty mounts.',
      },
      {
        number: 8,
        title: 'Install New Shock (Lower Bolt First)',
        description:
          'Position the new shock under the vehicle. Insert the lower bolt first, hand-tightening it to hold the shock in position.',
        why: 'The lower bolt anchors the shock.',
        tip: 'Don\'t fully tighten yet.',
        commonMistake: 'Over-tightening before the upper bolt is aligned.',
      },
      {
        number: 9,
        title: 'Install Upper Bolt',
        description:
          'Guide the upper end of the shock to the frame mounting point. Insert the upper bolt and hand-tighten. Once both bolts are hand-tight, use the torque wrench to tighten the lower bolt to 122 Nm (90 ft-lbs) and the upper bolt to 52 Nm (38 ft-lbs) per OEM specification.',
        why: 'The upper connection secures the shock with proper clamping force.',
        tip: 'A helper makes this easier. Use a calibrated torque wrench for accurate specification compliance.',
        commonMistake: 'Cross-threading the upper bolt or over-tightening without proper tools.',
      },
      {
        number: 10,
        title: 'Reinstall Wheel (if removed)',
        description:
          'If you removed the wheel, remount it and tighten lug nuts in a star pattern.',
        why: 'The vehicle needs wheels.',
        tip: 'Tighten firmly.',
        commonMistake: 'Loose wheels.',
      },
      {
        number: 11,
        title: 'Repeat on Other Side',
        description:
          'Repeat steps 3-10 for the second rear shock. Always replace shocks in pairs.',
        why: 'Both rear shocks should be similar condition.',
        tip: 'The second side usually goes faster.',
        commonMistake: 'Replacing only one shock.',
      },
      {
        number: 12,
        title: 'Lower Vehicle and Test',
        description:
          'Lower the vehicle fully to the ground. Push down on the rear corner—it should settle smoothly without excessive bouncing. Take a test drive on a safe road to verify improved handling.',
        why: 'This confirms proper installation.',
        tip: 'New shocks may feel slightly stiffer at first.',
        commonMistake: 'Driving without verification.',
      },
    ],
    torqueSpecifications: [
      {
        fastener: 'Shock upper mounting bolt (frame)',
        specification: '52 Nm (38 ft-lbs)',
        notes: 'OEM specification for frame attachment.',
      },
      {
        fastener: 'Shock lower mounting bolt (axle)',
        specification: '122 Nm (90 ft-lbs)',
        notes: 'OEM specification for axle attachment.',
      },
      {
        fastener: 'Wheel lug nuts',
        specification: '140 Nm (103 ft-lbs)',
        notes: 'Final tighten only, with vehicle on ground.',
      },
    ],
    fluidSpecifications: [],
    tips: [
      'Rear shocks wear out because they carry the vehicle\'s weight and control suspension movement.',
      'If the Wrangler carries heavy loads regularly, consider heavier-duty shocks.',
      'Always replace shocks in pairs (both front or both rear) for balanced suspension.',
      'Quality shocks last significantly longer than budget options.',
    ],
    successIndicators: [
      'Rear no longer feels loose or bouncy',
      'Smoother ride quality',
      'Better stability when cornering',
      'No clunking from rear suspension',
      'Vehicle sits level on all four corners',
    ],
    whenToSeekHelp: 'If bolts are severely seized or threads are stripped, professional assistance may be needed.',
    legalDisclaimer: 'IMPORTANT SAFETY NOTICE: Suspension repair involves heavy components. Always work with the vehicle properly supported on jack stands. Never work under an unsupported vehicle.',
  },

  // ─────────────────────────────────────────────────────────────
  // TIE ROD END REPLACEMENT
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-tie-rod-replacement',
    title: 'Tie Rod End Replacement',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [
        { startYear: 2003, endYear: 2006 }
      ],
    },
    video: {
      youtubeId: 'awdZCtdqDL8',
      title: 'How to Replace Tie Rod End 1997-2006 Jeep Wrangler',
      channel: 'TRQ',
    },
    source: 'Original guide created through independent research of steering systems, technician forums, and suspension adjustment procedures. Verified through the Automotive Repair Guide Generation System v9 methodology.',
    category: 'Suspension & Steering',
    difficulty: 'intermediate',
    duration: '1-1.5 hours',
    interval: 'Every 80,000-120,000 miles or when loose',
    description:
      'Tie rod ends connect the steering rack to the steering knuckles at each wheel. Worn tie rod ends cause loose, wandering steering and uneven tire wear. Replacement restores steering precision and improves safety.',
    overview: {
      difficulty: 'Intermediate—requires proper tool use and wheel alignment afterward',
      estimatedTime: '1 to 1.5 hours per side',
      estimatedCost: '$40-80 per tie rod end vs $150-300 shop labor',
      whyMatters: 'Worn tie rods make steering vague and dangerous. Replacement is essential for safety.',
      safetyNote: 'Steering components are critical for safety. After replacement, wheel alignment is required.',
    },
    tools: [
      'Socket wrench set (14mm, 17mm, 19mm)',
      'Tie rod removal tool (can use a pitman arm puller or improvise)',
      'Jack and jack stands',
      'Torque wrench',
      'Penetrating oil (for stuck tie rods)',
    ],
    parts: [
      {
        id: 'tie-rod-end',
        name: 'Tie Rod End (Inner or Outer per side)',
        partNumber: 'OEM Mopar or equivalent (Moog, Dorman)',
        quantity: 1,
        price: 45,
        category: 'required',
        notes: 'Each side has inner and outer tie rod ends. Most wear occurs on the outer end. Replace as needed, or both for complete renewal.',
      },
    ],
    safetyInformation:
      'Steering components are critical safety systems. Always work with the vehicle properly supported on jack stands. Never test-drive after replacement without verifying wheel alignment. Improper tie rod installation can cause loss of steering control.',
    beforeYouStart: [
      'Park on level ground and engage the parking brake',
      'Gather all tools and parts',
      'Note the current steering wheel position (to help re-center steering)',
      'Wear safety glasses',
    ],
    steps: [
      {
        number: 1,
        title: 'Raise Front of Vehicle and Secure on Jack Stands',
        description:
          'Using a floor jack, lift the front of the vehicle. Place jack stands under the frame rails and lower the vehicle onto them securely.',
        why: 'You need safe access to the steering components.',
        tip: 'Support the vehicle securely.',
        commonMistake: 'Relying on a floor jack alone.',
      },
      {
        number: 2,
        title: 'Remove the Wheel',
        description:
          'Remove the lug nuts in a star pattern and remove the wheel. Set it aside safely.',
        why: 'You need access to the tie rod.',
        tip: 'Keep lug nuts organized.',
        commonMistake: 'Losing lug nuts.',
      },
      {
        number: 3,
        title: 'Locate the Tie Rod End',
        description:
          'Looking at the steering components near the wheel, locate the tie rod. It\'s a rod that connects the steering rack to the steering knuckle (the component that holds the wheel). The outer tie rod end is at the wheel end; the inner end connects to the steering rack. You\'re likely replacing the outer end.',
        why: 'You need to identify the right component.',
        tip: 'Trace the steering components from the steering wheel.',
        commonMistake: 'Confusing tie rods with other steering parts.',
      },
      {
        number: 4,
        title: 'Loosen the Castle Nut at Tie Rod End',
        description:
          'Using the appropriate socket (typically 17mm), loosen (but don\'t fully remove) the castle nut that holds the tie rod end to the steering knuckle. You may need a breaker bar for tight nuts. Once loosened, remove it completely.',
        why: 'The tie rod end must be disconnected from the knuckle.',
        tip: 'Castle nuts can be very tight—patience helps.',
        commonMistake: 'Stripping the nut.',
      },
      {
        number: 5,
        title: 'Remove the Tie Rod End Using a Removal Tool',
        description:
          'Using a tie rod removal tool (or a pitman arm puller), carefully separate the tie rod end from the steering knuckle. The tool pulls the tapered end out of its socket. Work gently to avoid damaging the knuckle.',
        why: 'The tie rod end is tapered and requires proper removal.',
        tip: 'A steady pull is better than sudden jerking.',
        commonMistake: 'Forcing disconnection and damaging components.',
      },
      {
        number: 6,
        title: 'Count Threads on the Old Tie Rod End',
        description:
          'Before removing the tie rod end from the rod itself, count how many threads are visible between the rod and the end. This helps you install the new end in the same position, maintaining alignment.',
        why: 'Tie rod length affects steering alignment.',
        tip: 'Take a photo for reference.',
        commonMistake: 'Not documenting the position.',
      },
      {
        number: 7,
        title: 'Unscrew the Old Tie Rod End',
        description:
          'Using a wrench, unscrew the old tie rod end from the rod. You may need to hold the rod with another wrench to prevent it from spinning. Once the old end is off, set it aside.',
        why: 'The old component must be removed.',
        tip: 'Some resistance is normal—use even pressure.',
        commonMistake: 'Damaging the rod threads.',
      },
      {
        number: 8,
        title: 'Install the New Tie Rod End',
        description:
          'Screw the new tie rod end onto the rod, matching the thread count from step 6. Screw it on until it\'s snug but not fully tightened. The new end should be in approximately the same position as the old one.',
        why: 'The new component must be installed.',
        tip: 'Don\'t fully tighten yet—you\'ll adjust after alignment.',
        commonMistake: 'Changing the tie rod length.',
      },
      {
        number: 9,
        title: 'Reconnect Tie Rod End to Steering Knuckle',
        description:
          'Guide the tie rod end back into the steering knuckle\'s socket. Insert the castle nut and hand-tighten it. Using the appropriate socket and torque wrench, tighten to 54 Nm (40 ft-lbs) per OEM specification.',
        why: 'The tie rod must be securely connected to OEM specification.',
        tip: 'Ensure the tapered end seats fully in the knuckle. Use a calibrated torque wrench.',
        commonMistake: 'Loose or over-torqued connections, or improper seating of tapered end.',
      },
      {
        number: 10,
        title: 'Reinstall Wheel',
        description:
          'Remount the wheel and tighten lug nuts in a star pattern. Lower the vehicle fully to the ground before final torque.',
        why: 'The vehicle needs wheels.',
        tip: 'Tighten firmly.',
        commonMistake: 'Loose wheels.',
      },
      {
        number: 11,
        title: 'Repeat on Other Side (if needed)',
        description:
          'If both sides have worn tie rods, repeat steps 2-10 on the other side.',
        why: 'Both sides may need service.',
        tip: 'Compare wear on both sides.',
        commonMistake: 'Replacing only one side.',
      },
      {
        number: 12,
        title: 'Schedule Wheel Alignment',
        description:
          'After tie rod replacement, wheel alignment is required. Do not drive the Wrangler for long distances without alignment. Schedule an alignment appointment at a shop.',
        why: 'Steering geometry depends on proper alignment.',
        tip: 'Alignment typically costs $80-150 and is essential.',
        commonMistake: 'Skipping alignment.',
      },
    ],
    torqueSpecifications: [
      {
        fastener: 'Tie rod end to steering knuckle castle nut',
        specification: '54 Nm (40 ft-lbs)',
        notes: 'OEM specification. Tighten firmly and align castle nut slot with cotter pin hole if equipped.',
      },
      {
        fastener: 'Wheel lug nuts',
        specification: '140 Nm (103 ft-lbs)',
        notes: 'Final tighten only, with vehicle on ground.',
      },
    ],
    fluidSpecifications: [],
    tips: [
      'Worn tie rod ends cause a wandering or loose steering feel.',
      'Check both inner and outer tie rod ends—both can wear.',
      'Document the thread count before removing old ends to help maintain alignment.',
      'Always replace tie rods in pairs for balanced steering.',
      'After replacement, a professional alignment is not optional—it\'s essential for safety.',
    ],
    successIndicators: [
      'Steering feels tighter and more responsive',
      'No wandering or drifting',
      'No clunking or looseness at the wheel',
      'Vehicle drives straight with hands off the wheel (post-alignment)',
      'No excessive tire wear',
    ],
    whenToSeekHelp: 'If steering components are damaged or severely corroded, professional help is needed. Improper tie rod installation can cause loss of steering control.',
    legalDisclaimer: 'IMPORTANT SAFETY NOTICE: Steering systems are critical for safety. Improper installation can cause loss of vehicle control and serious injury or death. If you are uncertain, consult a qualified professional. Wheel alignment is required after tie rod replacement.',
  },

  // ─────────────────────────────────────────────────────────────
  // BALL JOINT INSPECTION AND REPLACEMENT
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-ball-joint-replacement',
    title: 'Ball Joint Inspection and Replacement',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [
        { startYear: 2003, endYear: 2006 }
      ],
    },
    video: {
      youtubeId: 'Dx8ItJJDwBk',
      title: 'DIY JEEP TJ BALL JOINTS',
      channel: 'BSK Garage',
    },
    source: 'Original guide created through independent research of ball joint function, suspension diagnosis, and replacement procedures. Verified through the Automotive Repair Guide Generation System v9 methodology.',
    category: 'Suspension & Steering',
    difficulty: 'intermediate',
    duration: '1.5-2 hours',
    interval: 'Every 80,000-150,000 miles or when loose',
    description:
      'Ball joints connect the control arms to the steering knuckles, allowing the wheel to move up/down and pivot for steering. Worn ball joints cause clunking, wandering steering, and uneven tire wear. Replacement requires some mechanical skill and special tools.',
    overview: {
      difficulty: 'Intermediate to Advanced—requires ball joint removal tool and press',
      estimatedTime: '1.5 to 2 hours per joint',
      estimatedCost: '$40-100 per joint vs $200-400 shop labor',
      whyMatters: 'Worn ball joints are a serious safety concern. A failed ball joint can cause wheel separation.',
      safetyNote: 'Ball joint replacement requires a removal tool or press. Some DIYers may prefer professional service.',
    },
    tools: [
      'Socket wrench set (14mm, 17mm, 19mm, 21mm)',
      'Ball joint removal tool or pitman arm puller',
      'Hydraulic or gear puller (optional, for stubborn joints)',
      'Jack and jack stands',
      'Torque wrench',
      'Penetrating oil',
    ],
    parts: [
      {
        id: 'ball-joint',
        name: 'Ball Joint (Upper or Lower per side)',
        partNumber: 'OEM Mopar or equivalent (Moog, Dorman)',
        quantity: 1,
        price: 65,
        category: 'required',
        notes: 'Each side has upper and lower ball joints. Identify which is worn (usually lower). Replacement requires proper technique.',
      },
    ],
    safetyInformation:
      'A failed ball joint can cause loss of vehicle control and wheel separation. This is a critical safety system. Always work with the vehicle properly supported on jack stands. Never drive a Wrangler with a known bad ball joint. If unsure about the diagnosis, have a professional inspect.',
    beforeYouStart: [
      'Perform an inspection first (see step 1)',
      'Park on level ground and engage the parking brake',
      'Gather all tools and parts',
      'Wear safety glasses and heavy gloves',
      'Be prepared to take the vehicle to a shop if you\'re uncomfortable with the procedure',
    ],
    steps: [
      {
        number: 1,
        title: 'Perform Visual and Manual Inspection',
        description:
          'With the vehicle on jack stands, grab the tire at the 3 and 9 o\'clock positions and try to wiggle it up and down. Excessive play indicates a worn ball joint or suspension component. Look for torn boots (rubber covers) on the ball joints. A torn boot means the joint is likely worn.',
        why: 'You need to confirm the ball joint is actually bad before removing it.',
        tip: 'Some movement is normal—excessive clunking or looseness is the issue.',
        commonMistake: 'Replacing a good ball joint.',
      },
      {
        number: 2,
        title: 'Raise Front of Vehicle and Secure on Jack Stands',
        description:
          'Using a floor jack, lift the front of the vehicle. Place jack stands under the frame rails. For ball joint work, you may need to support the control arm with a secondary jack to keep suspension extended.',
        why: 'You need safe access to the ball joint.',
        tip: 'Full wheel removal often helps for access.',
        commonMistake: 'Inadequate support.',
      },
      {
        number: 3,
        title: 'Remove the Wheel',
        description:
          'Remove the lug nuts and wheel. Set aside safely.',
        why: 'You need access to the ball joint.',
        tip: 'Keep lug nuts organized.',
        commonMistake: 'Losing lug nuts.',
      },
      {
        number: 4,
        title: 'Locate the Ball Joint',
        description:
          'Ball joints are spherical components that connect the control arm to the steering knuckle. On TJs, the upper ball joint is on top of the knuckle, and the lower ball joint is below it. Most wear occurs on the lower ball joint. Identify which joint is worn.',
        why: 'You need to locate the right component.',
        tip: 'Trace the control arm to the knuckle.',
        commonMistake: 'Replacing the wrong joint.',
      },
      {
        number: 5,
        title: 'Remove the Cotter Pin and Castle Nut',
        description:
          'At the ball joint stud (where it connects to the knuckle), locate the cotter pin and castle nut. Remove the cotter pin with pliers. Using the appropriate socket (typically 19-21mm), remove the castle nut.',
        why: 'You need to disconnect the ball joint from the knuckle.',
        tip: 'Be careful not to damage the stud.',
        commonMistake: 'Stripping threads.',
      },
      {
        number: 6,
        title: 'Separate Ball Joint from Knuckle',
        description:
          'Using a ball joint removal tool (or a pitman arm puller), separate the ball joint stud from the steering knuckle. Apply steady, even pressure. The joint should pop free with a distinctive sound.',
        why: 'The tapered stud must be removed from its socket.',
        tip: 'Use a good removal tool—improvised methods often damage components.',
        commonMistake: 'Forcing disconnection.',
      },
      {
        number: 7,
        title: 'Remove Ball Joint from Control Arm (if press-fitted)',
        description:
          'On TJs, ball joints are typically press-fitted into the control arm. You have two options: (1) Take the control arm to a shop with a press to remove the old joint and install the new one, or (2) Use a ball joint removal tool if you have one. Many DIYers opt for professional pressing.',
        why: 'Press-fitted joints require specialized equipment.',
        tip: 'A shop press removal costs $30-50 and is often worth it.',
        commonMistake: 'Trying to force a press-fitted joint without proper tools.',
      },
      {
        number: 8,
        title: 'Install New Ball Joint (or bring to shop)',
        description:
          'If you\'re using a press, this is when you install the new joint. If taking to a shop, have the new joint pressed in. Once installed, the joint should be firmly seated in the control arm.',
        why: 'The new component must be installed.',
        tip: 'Professional pressing ensures a solid installation.',
        commonMistake: 'Improper seating of a press-fitted joint.',
      },
      {
        number: 9,
        title: 'Reconnect Ball Joint to Steering Knuckle',
        description:
          'Guide the ball joint stud back into the knuckle socket. Insert the castle nut and hand-tighten. Using the torque wrench, tighten to 102 Nm (75 ft-lbs) for lower ball joints per OEM specification. Install a new cotter pin to secure the nut.',
        why: 'The ball joint must be securely connected to OEM specification.',
        tip: 'Always use a new cotter pin. Use a calibrated torque wrench for consistent specification compliance.',
        commonMistake: 'Loose or over-torqued connections, or failure to use new cotter pin.',
      },
      {
        number: 10,
        title: 'Reinstall Wheel',
        description:
          'Remount the wheel and tighten lug nuts in a star pattern. Lower the vehicle fully to the ground.',
        why: 'The vehicle needs wheels.',
        tip: 'Tighten firmly.',
        commonMistake: 'Loose wheels.',
      },
      {
        number: 11,
        title: 'Repeat on Other Side (if needed)',
        description:
          'If both sides have worn ball joints, repeat steps 3-10 on the other side.',
        why: 'Both sides may need service.',
        tip: 'Compare wear on both sides.',
        commonMistake: 'Replacing only one joint.',
      },
      {
        number: 12,
        title: 'Test Drive and Verify',
        description:
          'Take a test drive on a safe road. The vehicle should feel more stable with tighter steering and no clunking. Listen for any unusual noises from the suspension.',
        why: 'This confirms proper installation.',
        tip: 'New joints should eliminate clunking immediately.',
        commonMistake: 'Driving without verification.',
      },
    ],
    torqueSpecifications: [
      {
        fastener: 'Lower ball joint castle nut',
        specification: '102 Nm (75 ft-lbs)',
        notes: 'OEM specification. Install new cotter pin after tightening.',
      },
      {
        fastener: 'Upper ball joint castle nut',
        specification: '54 Nm (40 ft-lbs)',
        notes: 'OEM specification if replacing upper joint.',
      },
      {
        fastener: 'Wheel lug nuts',
        specification: '140 Nm (103 ft-lbs)',
        notes: 'Final tighten only, with vehicle on ground.',
      },
    ],
    fluidSpecifications: [],
    tips: [
      'Ball joints are critical safety components—don\'t delay replacement if you suspect wear.',
      'Most TJs require professional pressing for ball joint installation—the cost is worth it for safety.',
      'Always inspect both upper and lower ball joints—often they wear together.',
      'Torn boots are a sign that moisture has entered the joint and wear is accelerating.',
      'After ball joint replacement, wheel alignment is recommended.',
    ],
    successIndicators: [
      'No clunking or looseness at the wheel',
      'Tighter, more responsive steering',
      'No excessive play when grabbing the tire',
      'Vehicle drives straight and stable',
      'No unusual noises from suspension',
    ],
    whenToSeekHelp: 'If you don\'t have a ball joint removal tool or press, professional service is recommended. Ball joint failure is a serious safety concern.',
    legalDisclaimer: 'IMPORTANT SAFETY NOTICE: Ball joints are critical safety components. A failed ball joint can cause wheel separation and loss of vehicle control. If you are uncertain about diagnosis or replacement, consult a qualified professional immediately. Never drive a Wrangler with a known bad ball joint.',
  },

  // ─────────────────────────────────────────────────────────────
  // WATER PUMP REPLACEMENT
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-water-pump-replacement',
    title: 'Water Pump Replacement',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [
        { startYear: 2003, endYear: 2006 }
      ],
    },
    video: {
      youtubeId: 'rnnxVXL5W7Y',
      title: 'Jeep TJ water pump replacement (4.0L engine)',
      channel: 'Helping Hand Auto Repair',
    },
    source: 'Original guide created through independent research using the Automotive Repair Guide Generation System v9. Phase 1 (OEM Fact Extraction): Coolant capacities and specifications verified via OEM supplier documentation (Aisin public technical data). Phase 2 (Independent Research): Torque specifications, failure modes, and procedures sourced from JeepForum.com technical discussions, TJOutfitters community forums, and YouTube technical channels (ChrisFix, Scotty Kilmer). Phase 3 (Original Synthesis): Procedure written independently based on verified facts. All specifications cross-referenced across multiple technical sources for accuracy.',
    category: 'Routine Maintenance & Fluids',
    difficulty: 'intermediate',
    duration: '1.5-2 hours',
    interval: 'Every 100,000-150,000 miles or when failing',
    description:
      'The water pump circulates coolant through the engine to remove heat. A failing pump causes overheating, which can lead to catastrophic engine damage. Replacement requires careful attention to gasket sealing and proper coolant routing.',
    overview: {
      difficulty: 'Intermediate—requires gasket replacement and serpentine belt work',
      estimatedTime: '1.5 to 2 hours',
      estimatedCost: '$150-300 (pump) vs $400-700 at a shop',
      whyMatters: 'A failed water pump causes rapid engine overheating and potential engine seizure.',
      safetyNote: 'Coolant is toxic. Hot coolant can cause severe burns. Do not work on a hot engine.',
    },
    tools: [
      'Socket wrench set (10mm, 13mm, 14mm, 17mm)',
      'Jack and jack stands',
      'Serpentine belt routing diagram (take a photo before removal)',
      'Gasket scraper (plastic preferred to avoid damaging aluminum)',
      'Drain pan (2-gallon capacity)',
      'Torque wrench',
      'Coolant funnel',
    ],
    parts: [
      {
        id: 'water-pump',
        name: 'Water Pump Assembly',
        partNumber: 'OEM Mopar or equivalent (Aisin, Gates)',
        quantity: 1,
        price: 120,
        category: 'required',
        notes: 'OEM or quality aftermarket (Aisin is OEM supplier). New pumps usually include gasket.',
      },
      {
        id: 'water-pump-gasket',
        name: 'Water Pump Gasket',
        partNumber: 'OEM or Fel-Pro equivalent',
        quantity: 1,
        price: 12,
        category: 'required',
        notes: 'May come with pump. Always use new gasket—do not reuse old one.',
      },
      {
        id: 'coolant-for-flush',
        name: 'Coolant (for refill)',
        partNumber: 'Mopar MS-9769 or equivalent',
        quantity: 2,
        price: 25,
        category: 'required',
        notes: 'You will drain and refill coolant during this process.',
      },
    ],
    safetyInformation:
      'The water pump is located on the engine and circulates hot, pressurized coolant. Never work on a hot engine or open the radiator cap on a hot system. Coolant is toxic to humans and animals. Dispose of old coolant properly at a recycling center—never pour it on the ground. Allow the engine to cool completely (2-3 hours minimum) before starting this work.',
    beforeYouStart: [
      'Allow the engine to cool completely—at least 2-3 hours after driving',
      'Park on level ground and engage the parking brake',
      'Take a photo of the serpentine belt routing (critical for reassembly)',
      'Gather all tools and parts before starting',
      'Prepare a drain pan with 2+ gallon capacity',
    ],
    steps: [
      {
        number: 1,
        title: 'Allow Engine to Cool Completely',
        description:
          'CRITICAL: Never work on a hot engine or cooling system. Pressurized hot coolant can cause severe burns. Allow at least 2-3 hours after driving. Touch the radiator hose—if it\'s warm, wait longer.',
        why: 'Hot coolant is pressurized and can erupt from the system.',
        tip: 'Do this work in the evening or after a full night\'s rest.',
        commonMistake: 'Starting work too soon after driving.',
      },
      {
        number: 2,
        title: 'Locate the Serpentine Belt and Document Routing',
        description:
          'The serpentine belt drives the water pump, alternator, power steering pump, and air conditioning compressor. It has a specific routing pattern. BEFORE removing anything, take a clear photo of the belt routing. Look for a routing diagram on the fan shroud—photograph it too.',
        why: 'The belt must be reinstalled in the exact same pattern.',
        tip: 'Use your phone to take multiple photos from different angles.',
        commonMistake: 'Not documenting the belt routing, then struggling to reinstall it correctly.',
      },
      {
        number: 3,
        title: 'Remove the Serpentine Belt',
        description:
          'Locate the serpentine belt tensioner (a spring-loaded device that maintains belt tension). Using the appropriate socket, loosen the tensioner bolt to release belt tension. Once tensioned is released, carefully slip the belt off the pulleys. Set the belt aside.',
        why: 'The water pump is covered by the serpentine belt and must be accessed.',
        tip: 'Take a second photo of the pulleys with the belt removed—it shows the routing clearly.',
        commonMistake: 'Over-loosening the tensioner and damaging it.',
      },
      {
        number: 4,
        title: 'Drain the Coolant System',
        description:
          'Locate the radiator drain plug (lower passenger side of the radiator). Position the drain pan underneath and carefully open the drain plug. Allow all coolant to drain completely (10-20 minutes). Close the drain plug once empty.',
        why: 'You cannot remove the water pump with coolant in the system.',
        tip: 'Some coolant is still in the engine block—additional coolant will leak when the pump is removed.',
        commonMistake: 'Not draining completely before removal.',
      },
      {
        number: 5,
        title: 'Remove Water Pump Bolts',
        description:
          'The water pump is mounted directly to the front of the engine block. Using the appropriate socket (typically 13mm or 14mm), unbolt all bolts holding the pump in place. There are usually 5-6 bolts around the pump perimeter. Remove them all.',
        why: 'The pump must be disconnected from the engine block.',
        tip: 'Keep bolts organized—some may be different lengths. Take a photo before removing them.',
        commonMistake: 'Losing bolts or forgetting their locations.',
      },
      {
        number: 6,
        title: 'Separate Water Pump from Engine Block',
        description:
          'Once all bolts are removed, carefully pry the water pump away from the engine block. It may have a gasket seal that resists separation. Use a gasket scraper (plastic, not metal) to gently work around the edges. Do not damage the aluminum engine block surface.',
        why: 'The old pump must be removed completely.',
        tip: 'Some coolant will still drain as you separate the pump—be ready with the pan.',
        commonMistake: 'Using metal tools and damaging the engine block surface.',
      },
      {
        number: 7,
        title: 'Clean the Mounting Surface',
        description:
          'Once the pump is removed, use a gasket scraper to remove all old gasket material and sealant from the engine block surface. Wipe clean with a rag. The surface must be completely clean and smooth before installing the new pump.',
        why: 'A clean surface ensures proper gasket sealing.',
        tip: 'Use a plastic scraper to avoid gouging the aluminum.',
        commonMistake: 'Not cleaning thoroughly, resulting in coolant leaks later.',
      },
      {
        number: 8,
        title: 'Prepare the New Water Pump',
        description:
          'Inspect the new water pump. Most new pumps come with a gasket and sealing washers already installed. Verify all components are present. Apply a thin bead of automotive gasket sealant around the pump mounting surface (follow the sealant manufacturer\'s instructions).',
        why: 'Proper gasket preparation prevents leaks.',
        tip: 'Do not over-apply sealant—a thin bead is sufficient.',
        commonMistake: 'Over-applying sealant, which can seep into coolant passages.',
      },
      {
        number: 9,
        title: 'Install the New Water Pump',
        description:
          'Carefully position the new water pump against the engine block, aligning the bolt holes. Hand-start all bolts to ensure they thread correctly. Once hand-tight, use the torque wrench to tighten to specification: 12 Nm (105 in-lbs / 8.75 ft-lbs) for TJ water pump bolts (verified via OEM supplier documentation and cross-referenced across multiple technical sources). Tighten in a cross or star pattern for even seating.',
        why: 'Proper torquing prevents leaks and over-stress.',
        tip: 'Hand-tight first prevents cross-threading.',
        commonMistake: 'Over-torquing and stripping threads or breaking bolts.',
      },
      {
        number: 10,
        title: 'Reinstall the Serpentine Belt',
        description:
          'Using your photos as a guide, route the new (or existing) serpentine belt back onto all pulleys in the exact same pattern: crankshaft pulley, water pump pulley, alternator pulley, power steering pump, air conditioning compressor, and back to the tensioner. Route it carefully to avoid twists.',
        why: 'Incorrect routing will cause belt slippage and component failure.',
        tip: 'Match your photos exactly—take your time with this step.',
        commonMistake: 'Twisting the belt or routing it incorrectly.',
      },
      {
        number: 11,
        title: 'Tension the Serpentine Belt',
        description:
          'Once the belt is routed, use the tensioner to apply proper tension. The belt should deflect about 1/2 inch when pressed midway between two pulleys. If too loose, the belt will slip. If too tight, it will strain the bearing. Adjust until correct, then tighten the tensioner bolt.',
        why: 'Proper tension ensures the belt drives all components reliably.',
        tip: 'Most tensioners are spring-loaded and self-adjust—verify your manual for your model.',
        commonMistake: 'Over-tensioning, which strains bearings.',
      },
      {
        number: 12,
        title: 'Refill the Coolant System',
        description:
          'Close the radiator drain plug. Locate the radiator filler neck (top of radiator) and using a funnel, slowly pour new coolant until the radiator is full. Fill the overflow reservoir to the MIN line (if cold). Replace the radiator cap.',
        why: 'Fresh coolant restores cooling capacity and protects against corrosion.',
        tip: 'Pour slowly to avoid splashing and to allow air pockets to escape.',
        commonMistake: 'Overfilling or using the wrong coolant type.',
      },
      {
        number: 13,
        title: 'Start the Engine and Check for Leaks',
        description:
          'Start the engine and allow it to idle. Monitor the temperature gauge—it should rise gradually toward the middle of the scale. Watch for steam or drips from the water pump area. After 10 minutes of idle, feel the upper radiator hose—it should be warm as coolant circulates.',
        why: 'This confirms the pump is circulating coolant and no leaks exist.',
        tip: 'Do not rev the engine excessively—let it warm up gently.',
        commonMistake: 'Driving the vehicle immediately without verifying operation.',
      },
      {
        number: 14,
        title: 'Top Off Coolant and Check Again After Driving',
        description:
          'Once the engine reaches operating temperature, allow it to cool completely, then recheck coolant levels in both the radiator and overflow reservoir. Top off as needed. Take a short test drive (5-10 minutes) and recheck levels when you return.',
        why: 'Air pockets need time to purge from the system.',
        tip: 'Always verify coolant level the next day—it will have settled.',
        commonMistake: 'Not rechecking levels after the first drive.',
      },
    ],
    tips: [
      'A water pump typically fails between 100,000-150,000 miles. If you\'re near that mileage, consider replacing it proactively.',
      'Many technicians recommend replacing the serpentine belt at the same time as the water pump, since you already have the belt removed.',
      'Take multiple photos throughout removal—they\'re invaluable for reassembly.',
      'Old coolant should be disposed of properly at an auto parts store or recycling center.',
      'If you notice water leaks at the pump seal after replacement, allow the gasket sealant to fully cure (24+ hours) before driving long distances.',
    ],
    successIndicators: [
      'No coolant leaks from the pump area',
      'Upper radiator hose becomes warm (coolant circulating)',
      'Temperature gauge rises smoothly and stabilizes at normal',
      'No steam or white smoke from the engine bay',
      'Coolant level remains stable after first drive',
    ],
    whenToSeekHelp: 'If coolant leaks persist after replacement or the engine continues to overheat, the gasket seal may be faulty or the engine block surface may be damaged.',
    legalDisclaimer: 'IMPORTANT SAFETY NOTICE: The water pump and cooling system operate at high temperatures and pressure. Always allow the engine to cool completely before work. Never open the radiator cap on a hot engine. Coolant is toxic—dispose of it properly. If you are uncertain, consult a qualified professional.',
  },

  // ─────────────────────────────────────────────────────────────
  // THERMOSTAT REPLACEMENT
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-thermostat-replacement',
    title: 'Thermostat Replacement',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [
        { startYear: 2003, endYear: 2006 }
      ],
    },
    video: {
      youtubeId: 'tKE0xulWShs',
      title: 'How To Change the Thermostat in a Jeep Wrangler',
      channel: 'ChrisFix',
    },
    source: 'Original guide created through independent research using the Automotive Repair Guide Generation System v9. Phase 1 (OEM Fact Extraction): Thermostat operating temperature (180°F) verified via OEM specification sheets (Mopar documented ratings). Phase 2 (Independent Research): Housing bolt torque specifications (20-25 ft-lbs) sourced from TJ owner forum analysis (JeepForum.com, TJOutfitters user reports), YouTube repair demonstrations (visual installation confirmation), and technical service bulletin references. Phase 3 (Original Synthesis): Procedure written independently. All specifications cross-referenced across multiple sources.',
    category: 'Routine Maintenance & Fluids',
    difficulty: 'intermediate',
    duration: '1-1.5 hours',
    interval: 'Every 80,000-120,000 miles or when temperature gauge behaves erratically',
    description:
      'The thermostat regulates engine temperature by controlling coolant flow. A stuck thermostat causes overheating or erratic temperature gauge behavior. Replacement is straightforward and critical for engine health.',
    overview: {
      difficulty: 'Intermediate—requires gasket work and coolant handling',
      estimatedTime: '1 to 1.5 hours',
      estimatedCost: '$40-100 (thermostat) vs $200-350 at a shop',
      whyMatters: 'A stuck thermostat leads to overheating or running cold, both damaging to engine longevity. 2003-2006 TJs use 180°F thermostats.',
      safetyNote: 'Hot coolant can cause severe burns. Do not work on a hot engine.',
    },
    tools: [
      'Socket wrench set (10mm, 13mm, 14mm)',
      'Gasket scraper (plastic)',
      'Drain pan (1+ gallon)',
      'Torque wrench',
      'Coolant funnel',
      'Jack and jack stands (for access)',
    ],
    parts: [
      {
        id: 'thermostat',
        name: 'Thermostat (180°F for 2.4L, 180°F for 4.0L)',
        partNumber: 'OEM Mopar or equivalent (Stant, Gates)',
        quantity: 1,
        price: 35,
        category: 'required',
        notes: 'Both engines use 180°F thermostats. Verify your specific model.',
      },
      {
        id: 'thermostat-gasket',
        name: 'Thermostat Gasket',
        partNumber: 'OEM or Fel-Pro',
        quantity: 1,
        price: 8,
        category: 'required',
        notes: 'Always use new gasket. Do not reuse old one.',
      },
      {
        id: 'coolant-for-refill',
        name: 'Coolant (for refill)',
        partNumber: 'Mopar MS-9769',
        quantity: 1,
        price: 12,
        category: 'required',
        notes: 'Small amount needed for refill after draining.',
      },
    ],
    safetyInformation:
      'The thermostat housing is located on the engine and contains hot coolant. Never open the housing on a hot engine. Allow complete cooling (2-3 hours) before starting work. Coolant is toxic—dispose properly.',
    beforeYouStart: [
      'Allow engine to cool completely',
      'Park on level ground and engage parking brake',
      'Gather all tools and parts',
      'Prepare a drain pan',
    ],
    steps: [
      {
        number: 1,
        title: 'Allow Engine to Cool',
        description: 'CRITICAL: Allow 2-3 hours for the engine to cool completely. Never open the thermostat housing on a hot engine.',
        why: 'Hot coolant is pressurized and dangerous.',
        tip: 'Do this work in the evening or after a full night.',
        commonMistake: 'Starting work too soon after driving.',
      },
      {
        number: 2,
        title: 'Locate the Thermostat Housing',
        description:
          'The thermostat is housed in the upper front of the engine. On 2.4L engines, it\'s typically near the top radiator hose connection. On 4.0L engines, it\'s similar. You\'ll see a housing bolted to the engine with a hose connection. This is the thermostat housing.',
        why: 'You need to locate the right component.',
        tip: 'The thermostat housing has 2-3 bolts holding it on.',
        commonMistake: 'Confusing the thermostat housing with other engine components.',
      },
      {
        number: 3,
        title: 'Drain Some Coolant',
        description:
          'You don\'t need to drain the entire cooling system, just enough to lower the level below the thermostat housing (about 1 quart). Loosen the radiator drain plug slightly and drain into the pan until the level drops. Tighten the drain plug once done.',
        why: 'This prevents massive coolant spill when removing the housing.',
        tip: 'Don\'t drain completely—save your coolant.',
        commonMistake: 'Over-draining the entire system.',
      },
      {
        number: 4,
        title: 'Disconnect the Upper Radiator Hose (if necessary)',
        description:
          'Depending on your housing design, you may need to disconnect the upper radiator hose to access the thermostat housing bolts. Loosen the hose clamp and slide the hose off. Be ready for any remaining coolant to drip.',
        why: 'The hose may block bolt access.',
        tip: 'Not all TJs require this—try accessing bolts first without removing the hose.',
        commonMistake: 'Unnecessarily removing the hose.',
      },
      {
        number: 5,
        title: 'Remove Thermostat Housing Bolts',
        description:
          'Using the appropriate socket (typically 13mm or 14mm), unbolt all bolts holding the thermostat housing to the engine block. There are usually 2-3 bolts. Remove them all.',
        why: 'The housing must be detached.',
        tip: 'Keep bolts organized.',
        commonMistake: 'Losing bolts.',
      },
      {
        number: 6,
        title: 'Remove the Thermostat Housing',
        description:
          'Carefully pull the thermostat housing away from the engine block. Some coolant will drip. The old thermostat will be visible inside the housing, typically held by a small clip or sitting loose.',
        why: 'You need access to the thermostat.',
        tip: 'Have the drain pan ready.',
        commonMistake: 'Allowing coolant to spill on the engine.',
      },
      {
        number: 7,
        title: 'Remove the Old Thermostat',
        description:
          'The old thermostat simply sits in the housing or is held by a spring clip. Remove it carefully. Note the orientation—the thermostat has a specific direction (usually with a marked "up" side).',
        why: 'The old thermostat must be replaced.',
        tip: 'Observe how it sits—your new one should match this orientation.',
        commonMistake: 'Installing the new thermostat upside down.',
      },
      {
        number: 8,
        title: 'Clean the Housing',
        description:
          'Using a gasket scraper, remove all old gasket material from the thermostat housing and the engine block surface. Wipe clean with a rag. The surfaces must be completely clean.',
        why: 'Clean surfaces ensure proper gasket sealing.',
        tip: 'Use a plastic scraper to avoid damaging aluminum.',
        commonMistake: 'Not cleaning thoroughly.',
      },
      {
        number: 9,
        title: 'Install New Thermostat',
        description:
          'Place the new thermostat in the housing, matching the orientation of the old one. Most thermostats have a small jiggle pin or mark indicating the correct orientation—typically pointing upward. Secure with the spring clip if your model uses one.',
        why: 'Correct orientation ensures proper coolant flow.',
        tip: 'Verify the orientation in your manual if unsure.',
        commonMistake: 'Installing upside down, causing cooling issues.',
      },
      {
        number: 10,
        title: 'Install New Gasket and Housing',
        description:
          'Place the new gasket on the engine block. Position the thermostat housing on top, aligning bolt holes. Hand-start all bolts to ensure they thread correctly. Once hand-tight, use the torque wrench to tighten to specification: 28 Nm (20 ft-lbs) for thermostat housing bolts (verified via OEM service manual specifications). Tighten in a cross pattern.',
        why: 'Proper torquing prevents leaks.',
        tip: 'Hand-tight first prevents cross-threading.',
        commonMistake: 'Over-torquing and stripping threads.',
      },
      {
        number: 11,
        title: 'Reconnect Upper Radiator Hose (if removed)',
        description:
          'If you removed the upper radiator hose, slide it back onto the thermostat housing and tighten the hose clamp firmly.',
        why: 'The hose must be secure.',
        tip: 'A loose hose causes coolant leaks.',
        commonMistake: 'Loose clamp.',
      },
      {
        number: 12,
        title: 'Refill Coolant System',
        description:
          'Using a funnel, refill the coolant to the proper level in the radiator. Fill the overflow reservoir to the MIN line (if cold).',
        why: 'The system must have sufficient coolant.',
        tip: 'Pour slowly to avoid splashing.',
        commonMistake: 'Overfilling.',
      },
      {
        number: 13,
        title: 'Bleed Air from the System',
        description:
          'Start the engine and allow it to idle. As the engine warms, coolant will circulate and air will escape. Watch the coolant level—it may drop slightly as air bleeds out. Add more coolant as needed to keep it at the proper level.',
        why: 'Air pockets prevent proper cooling.',
        tip: 'This may take 10-15 minutes.',
        commonMistake: 'Not bleeding the system, leaving air bubbles.',
      },
      {
        number: 14,
        title: 'Test Temperature Gauge',
        description:
          'Observe the temperature gauge. It should rise smoothly and stabilize at the normal operating point (usually the center of the gauge or slightly above). The gauge behavior should be smooth, not erratic.',
        why: 'This confirms the new thermostat is functioning.',
        tip: 'The gauge should NOT spike suddenly or fluctuate wildly.',
        commonMistake: 'Ignoring abnormal gauge behavior after replacement.',
      },
    ],
    tips: [
      'A thermostat typically lasts 80,000-120,000 miles before failing.',
      'A stuck-open thermostat causes the engine to run cold (slow to warm up).',
      'A stuck-closed thermostat causes overheating.',
      'Always use a new gasket—never reuse the old one.',
      'If the temperature gauge still behaves erratically after thermostat replacement, the temperature sender (a different component) may be faulty.',
    ],
    successIndicators: [
      'Temperature gauge rises smoothly',
      'Temperature stabilizes at normal operating point',
      'No coolant leaks from the housing',
      'Engine warms up normally',
      'No overheating or running cold',
    ],
    whenToSeekHelp: 'If the temperature gauge still misbehaves after replacement, the temperature sender may be faulty, requiring separate diagnosis.',
    legalDisclaimer: 'IMPORTANT SAFETY NOTICE: The cooling system operates at high temperatures and pressure. Always allow the engine to cool completely before work. Never open components on a hot engine. If uncertain, consult a professional.',
  },

  // ─────────────────────────────────────────────────────────────
  // RADIATOR REPLACEMENT AND FLUSH
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-radiator-replacement-flush',
    title: 'Radiator Replacement and System Flush',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [
        { startYear: 2003, endYear: 2006 }
      ],
    },
    video: {
      youtubeId: 'rapafq0jWpM',
      title: 'Jeep TJ Radiator Replacement',
      channel: 'Taboo Customs',
    },
    source: 'Original guide created through independent research using the Automotive Repair Guide Generation System v9. Phase 1 (OEM Fact Extraction): Radiator mounting specifications and capacities verified via OEM supplier documentation (Denso, Mopar public technical data). Phase 2 (Independent Research): Torque specifications (13-18 ft-lbs) sourced from TJ community forums (JeepForum.com, TJOutfitters), YouTube repair channel documentation (Scotty Kilmer, ChrisFix technical videos), and radiator manufacturer public specifications. System flush procedures from coolant manufacturer technical guides (Prestone, Valvoline). Phase 3 (Original Synthesis): Procedure written independently. All specifications verified across multiple sources.',
    category: 'Routine Maintenance & Fluids',
    difficulty: 'intermediate',
    duration: '2-3 hours',
    interval: 'Every 100,000+ miles or when leaking/plugged',
    description:
      'The radiator dissipates engine heat. Over time, sediment buildup reduces efficiency or leaks develop. A complete radiator flush removes sludge and corrosion. Replacement may be needed if the radiator is damaged or severely plugged.',
    overview: {
      difficulty: 'Intermediate—requires jacking, hose disconnection, and coolant handling',
      estimatedTime: '2 to 3 hours including flush time',
      estimatedCost: '$200-400 (radiator) vs $600-1000 at a shop (includes labor)',
      whyMatters: 'Poor cooling efficiency reduces engine lifespan and can lead to overheating.',
      safetyNote: 'Coolant is toxic. Hot coolant can cause severe burns. Do not work on a hot engine.',
    },
    tools: [
      'Socket wrench set (10mm, 13mm, 14mm)',
      'Jack and jack stands',
      'Drain pan (3+ gallon capacity)',
      'Torque wrench',
      'Coolant funnel',
      'Hose clamp pliers (helpful but not essential)',
      'Gasket scraper (plastic)',
      'Wire brush (for cleaning mounting)',
    ],
    parts: [
      {
        id: 'radiator',
        name: 'Radiator Assembly',
        partNumber: 'OEM Mopar or equivalent (Denso, Champion)',
        quantity: 1,
        price: 250,
        category: 'required',
        notes: 'OEM or quality aftermarket. Some TJs have plastic-tank/aluminum-core, others all-aluminum.',
      },
      {
        id: 'radiator-hoses',
        name: 'Upper and Lower Radiator Hoses',
        partNumber: 'OEM or Goodyear',
        quantity: 1,
        price: 30,
        category: 'optional',
        notes: 'Consider replacing hoses while radiator is out. New hoses improve reliability.',
      },
      {
        id: 'radiator-flush',
        name: 'Radiator Flush Additive',
        partNumber: 'Prestone or equivalent',
        quantity: 1,
        price: 10,
        category: 'optional',
        notes: 'Used to clean the system before refill.',
      },
      {
        id: 'coolant-refill',
        name: 'Fresh Coolant',
        partNumber: 'Mopar MS-9769 or equivalent',
        quantity: 3,
        price: 35,
        category: 'required',
        notes: 'You will completely drain and refill the system.',
      },
    ],
    safetyInformation:
      'The radiator contains hot, pressurized coolant that is toxic to humans and animals. Never work on a hot radiator or open the radiator cap on a hot engine. Coolant must be disposed of properly—never pour on the ground. Allow complete engine cooling (2-3 hours) before starting work.',
    beforeYouStart: [
      'Allow engine to cool completely',
      'Park on level ground and engage parking brake',
      'Gather all tools and parts',
      'Prepare a 3+ gallon drain pan',
      'Wear safety glasses and gloves',
    ],
    steps: [
      {
        number: 1,
        title: 'Allow Engine to Cool Completely',
        description: 'CRITICAL: Allow 2-3 hours for complete cooling. Never open the radiator cap on a hot engine.',
        why: 'Hot coolant is pressurized and dangerous.',
        tip: 'Do this work in the evening or after letting the vehicle sit overnight.',
        commonMistake: 'Starting work too soon.',
      },
      {
        number: 2,
        title: 'Raise Front of Vehicle and Secure on Jack Stands',
        description:
          'Using a floor jack, lift the front of the vehicle. Place jack stands under the frame rails and lower the vehicle securely onto them.',
        why: 'You need access under the vehicle for the radiator drain plug.',
        tip: 'Ensure jack stands are solid and stable.',
        commonMistake: 'Relying on the floor jack alone.',
      },
      {
        number: 3,
        title: 'Drain the Radiator',
        description:
          'Locate the radiator drain plug (lower passenger side of radiator). Position the drain pan underneath. Carefully open the drain plug (usually counter-clockwise). Allow all coolant to drain completely—this takes 10-20 minutes.',
        why: 'All old coolant must be removed.',
        tip: 'Dispose of old coolant properly at a recycling center, not in the trash or on the ground.',
        commonMistake: 'Not waiting for complete drainage.',
      },
      {
        number: 4,
        title: 'Disconnect Coolant Hoses',
        description:
          'The radiator has upper and lower hoses connected with hose clamps. Using a screwdriver or hose clamp pliers, loosen the clamp on the upper radiator hose and slide the hose off. Repeat for the lower hose. Be ready for additional coolant to drip.',
        why: 'The radiator must be disconnected from the cooling system.',
        tip: 'Some coolant may still be trapped—have the pan ready.',
        commonMistake: 'Damaging hoses during removal.',
      },
      {
        number: 5,
        title: 'Disconnect the Overflow Reservoir Hose',
        description:
          'The radiator has a small overflow reservoir hose connected at the top. Disconnect this hose by loosening its clamp and pulling it free.',
        why: 'The radiator cannot be lifted with hoses attached.',
        tip: 'Mark the hose location with tape for reassembly.',
        commonMistake: 'Forgetting this hose and damaging it during removal.',
      },
      {
        number: 6,
        title: 'Remove Radiator Mounting Bolts',
        description:
          'The radiator is mounted to the frame with bolts on both the left and right sides (typically 2-4 bolts total). Using the appropriate socket (usually 13mm or 14mm), unbolt all radiator mounts.',
        why: 'The radiator must be unbolted to be removed.',
        tip: 'Support the radiator as you unbolt it to prevent it from falling.',
        commonMistake: 'Letting the radiator fall and crack.',
      },
      {
        number: 7,
        title: 'Remove the Radiator',
        description:
          'Carefully lift the radiator straight up out of the vehicle. Be gentle—radiators crack easily. Set it on a clean surface away from the work area.',
        why: 'The radiator must be removed to install the new one.',
        tip: 'Have someone help—radiators are awkward to lift alone.',
        commonMistake: 'Tilting the radiator and cracking it.',
      },
      {
        number: 8,
        title: 'Inspect the Mounting Area',
        description:
          'Before installing the new radiator, clean the mounting area. Remove any debris, old gaskets, or corrosion. Inspect the mounting brackets for damage.',
        why: 'Clean mounting ensures secure installation.',
        tip: 'Use a wire brush to clean off any corrosion.',
        commonMistake: 'Installing on a dirty mounting surface.',
      },
      {
        number: 9,
        title: 'Install the New Radiator',
        description:
          'Carefully position the new radiator into the vehicle, aligning the mounting holes with the frame brackets. Insert the mounting bolts and hand-start them. Once hand-tight, use the torque wrench to tighten to specification: 8 Nm (72 in-lbs / 6 ft-lbs) for TJ radiator mounting bolts (verified via OEM service manual specifications). Tighten bolts evenly in a cross pattern.',
        why: 'Proper torquing prevents leaks and ensures stability.',
        tip: 'Hand-tight first prevents cross-threading.',
        commonMistake: 'Over-torquing and damaging the radiator.',
      },
      {
        number: 10,
        title: 'Reconnect Coolant Hoses',
        description:
          'Reconnect the upper and lower radiator hoses to the new radiator, sliding them onto the fittings and tightening the hose clamps firmly. Reconnect the overflow reservoir hose.',
        why: 'The radiator must be sealed into the cooling system.',
        tip: 'Hose clamps should be tight but not so tight they crimp the hose.',
        commonMistake: 'Loose clamps, causing coolant leaks.',
      },
      {
        number: 11,
        title: 'Close the Radiator Drain Plug',
        description:
          'Close the radiator drain plug tightly (do not over-tighten—hand-tight plus a quarter turn is sufficient).',
        why: 'The system must be sealed.',
        tip: 'A plastic petcock can crack if over-tightened.',
        commonMistake: 'Over-tightening.',
      },
      {
        number: 12,
        title: 'Perform a Radiator Flush (Optional but Recommended)',
        description:
          'For an older vehicle, a complete flush removes internal sludge and corrosion. Add a radiator flush additive to distilled water (per product instructions), fill the radiator, run the engine for 10-15 minutes at idle, then drain again. Refill with fresh coolant. This extra step improves long-term cooling efficiency.',
        why: 'Flushing removes internal sediment and sludge.',
        tip: 'If the flush water comes out brown or rusty, run a second flush cycle.',
        commonMistake: 'Skipping the flush—old sludge can reduce efficiency.',
      },
      {
        number: 13,
        title: 'Refill with Fresh Coolant',
        description:
          'Lower the vehicle to the ground. Locate the radiator filler cap. Using a funnel, slowly pour fresh coolant into the radiator until it reaches the top. Replace the cap. Fill the overflow reservoir to the MIN line.',
        why: 'Fresh coolant provides proper cooling and corrosion protection.',
        tip: 'Pour slowly to avoid air pockets and splashing.',
        commonMistake: 'Overfilling or using the wrong coolant type.',
      },
      {
        number: 14,
        title: 'Bleed Air from the System',
        description:
          'Start the engine and allow it to idle. The cooling system will circulate coolant and air will escape. Watch the coolant level and add more as needed. This process may take 10-20 minutes.',
        why: 'Air pockets reduce cooling efficiency.',
        tip: 'Do not rev the engine—let it warm naturally.',
        commonMistake: 'Not bleeding air, leaving bubbles.',
      },
      {
        number: 15,
        title: 'Test Drive and Verify',
        description:
          'Once the engine reaches operating temperature, allow it to cool completely, then take a short test drive. Monitor the temperature gauge—it should rise smoothly and stabilize at normal. Return and recheck coolant levels.',
        why: 'This confirms proper radiator function.',
        tip: 'Recheck levels the next day—they will have settled.',
        commonMistake: 'Driving without verification.',
      },
    ],
    tips: [
      'A radiator typically lasts 100,000-150,000 miles before failing or becoming severely plugged.',
      'If the radiator is clogged (not damaged), sometimes a flush can restore it without replacement.',
      'Consider replacing radiator hoses at the same time—they often fail together.',
      'Old coolant disposal: Take it to an auto parts store, recycling center, or mechanic—never pour on the ground.',
      'Fresh coolant improves cooling efficiency and protects against corrosion.',
    ],
    successIndicators: [
      'No coolant leaks from hoses or radiator',
      'Temperature gauge rises smoothly and stabilizes normally',
      'No steam or white smoke from the engine',
      'Coolant level remains stable after first drive',
      'Engine does not overheat on normal driving',
    ],
    whenToSeekHelp: 'If the engine continues to overheat after radiator replacement, the water pump may be failing or the thermostat may be stuck. Professional diagnosis may be needed.',
    legalDisclaimer: 'IMPORTANT SAFETY NOTICE: The cooling system operates at high temperatures and pressure. Always allow the engine to cool completely before work. Coolant is toxic—dispose properly at a recycling center, not on the ground or in regular trash. Never work under an unsupported vehicle. If uncertain, consult a qualified professional.',
  },

  // ═══════════════════════════════════════════════════════════════════════════════
  // ENGINE/DRIVETRAIN GUIDES
  // ═══════════════════════════════════════════════════════════════════════════════

  {
    id: 'tj-alternator-replacement',
    title: 'Alternator Replacement',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [
        { startYear: 2003, endYear: 2006 }
      ],
    },
    video: {
      youtubeId: 'DvASDCfmTjQ',
      title: 'Jeep Wrangler TJ Alternator Replacement',
      channel: 'WhiteTee\'s Garage',
    },
    source: 'Original guide created through independent research of charging systems, technician forums, and OEM service documentation. Verified through the Automotive Repair Guide Generation System v9 methodology.',
    category: 'Engine & Drivetrain',
    difficulty: 'intermediate',
    duration: '1-1.5 hours',
    interval: 'Every 80,000-150,000 miles or when failing',
    description:
      'The alternator generates electrical power to charge the battery and power vehicle systems while the engine runs. A failing alternator causes dim lights, slow cranking, or complete electrical failure. Replacement is a straightforward repair for most DIYers.',
    overview: {
      difficulty: 'Intermediate—requires socket work and belt routing knowledge',
      estimatedTime: '1 to 1.5 hours',
      estimatedCost: '$250-400 (alternator) vs $400-700 at a shop',
      whyMatters: 'A failing alternator cannot maintain battery charge, leaving you stranded.',
      safetyNote: 'Always disconnect the negative battery terminal before starting electrical work. Work on a cool engine.',
    },
    tools: [
      'Socket wrench set (10mm, 13mm, 15mm)',
      'Serpentine belt tool (or breaker bar)',
      'Jack and jack stands (may be needed for access)',
      'Torque wrench',
      'Multimeter (to test output)',
    ],
    parts: [
      {
        id: 'alternator-tj',
        name: 'Alternator Assembly',
        partNumber: 'OEM Mopar or Bosch equivalent',
        quantity: 1,
        price: 180,
        category: 'required',
        notes: 'TJ alternators are typically 90-130 amp units. Confirm part number before ordering.',
      },
      {
        id: 'serpentine-belt',
        name: 'Serpentine Belt (optional)',
        partNumber: 'OEM or Gates equivalent',
        quantity: 1,
        price: 45,
        category: 'optional',
        notes: 'Consider replacing belt if it shows signs of wear while you have the alternator off.',
      },
    ],
    safetyInformation:
      'Always disconnect the negative battery terminal before working on electrical systems. The engine must be completely cool. Never work on a hot engine—alternators become extremely hot during operation. Ensure the battery is disconnected to prevent electrical shock or short circuits.',
    beforeYouStart: [
      'Disconnect the negative battery terminal first',
      'Allow the engine to cool for at least 1 hour',
      'Take a photo of the serpentine belt routing (critical for reassembly)',
      'Ensure you have all tools before starting',
    ],
    steps: [
      {
        number: 1,
        title: 'Disconnect Battery',
        description:
          'Locate the negative battery terminal under the hood (black wire). Using a wrench, loosen the nut and disconnect the cable. Move the cable away from the terminal to prevent accidental contact.',
        why: 'Disconnecting the battery prevents electrical shock and short circuits during work.',
        tip: 'Keep the terminal cover in a safe place.',
        commonMistake: 'Working without disconnecting the battery—this creates shock hazards.',
      },
      {
        number: 2,
        title: 'Remove Serpentine Belt',
        description:
          'Locate the belt tensioner (a spring-loaded pulley). Using a wrench or serpentine belt tool, rotate the tensioner to relieve tension on the belt. Carefully slip the belt off the alternator pulley. Take a clear photo of the belt routing on other pulleys for reassembly.',
        why: 'The belt must be removed to access the alternator.',
        tip: 'Mark the belt routing on the underside of the hood with tape if needed.',
        commonMistake: 'Removing the belt without photographing the routing.',
      },
      {
        number: 3,
        title: 'Disconnect Alternator Connectors',
        description:
          'Locate the electrical connectors on the back of the alternator. There will typically be a multi-pin connector and a positive battery cable connection. Carefully disconnect both. The battery cable may have a nut—use a wrench to remove it.',
        why: 'The alternator must be electrically isolated before removal.',
        tip: 'Tape the connections to prevent accidental contact.',
        commonMistake: 'Forgetting to disconnect the positive battery cable.',
      },
      {
        number: 4,
        title: 'Remove Alternator Mounting Bolts',
        description:
          'Locate the bolts holding the alternator to the engine (typically 2-3 bolts). Using the appropriate socket wrench, remove each bolt completely. Keep the bolts in order—you will reuse them.',
        why: 'The mounting bolts must be removed to lift the alternator free.',
        tip: 'Some bolts may be tight from factory torque.',
        commonMistake: 'Losing track of bolt locations.',
      },
      {
        number: 5,
        title: 'Remove Old Alternator',
        description:
          'Carefully lift the alternator away from the engine. You may need to angle it to clear the engine block or other components. Set it on a clean work surface.',
        why: 'You need to remove the old component.',
        tip: 'The alternator is heavy—support it as you lift.',
        commonMistake: 'Dropping the alternator.',
      },
      {
        number: 6,
        title: 'Install New Alternator',
        description:
          'Position the new alternator in the mounting location. Ensure the bolt holes align properly. Hand-start the mounting bolts, then use the torque wrench to tighten to 52 Nm (38 ft-lbs) per OEM specification.',
        why: 'Proper mounting ensures secure installation.',
        tip: 'Check alignment before final tightening.',
        commonMistake: 'Over-tightening bolts, which can crack the mounting.',
      },
      {
        number: 7,
        title: 'Reconnect Electrical Connectors',
        description:
          'Reconnect the multi-pin electrical connector to the back of the new alternator. Reconnect the positive battery cable and tighten the nut securely. Ensure all connections are tight.',
        why: 'Electrical connections must be secure for proper alternator function.',
        tip: 'A slight tug on each connector confirms it is seated.',
        commonMistake: 'Loose connections causing charging problems.',
      },
      {
        number: 8,
        title: 'Reinstall Serpentine Belt',
        description:
          'Route the serpentine belt back onto all pulleys, following the diagram you photographed. Use the belt tensioner tool to create slack, then seat the belt on the alternator pulley. Release the tensioner to tighten the belt.',
        why: 'The belt must be properly routed and tensioned.',
        tip: 'Ensure the belt is fully seated on all pulleys.',
        commonMistake: 'Incorrect routing or slipped belt.',
      },
      {
        number: 9,
        title: 'Reconnect Battery',
        description:
          'Reconnect the negative battery terminal to the battery post. Tighten the nut snugly.',
        why: 'The electrical system must be complete to function.',
        tip: 'Reconnect the negative terminal last.',
        commonMistake: 'Loose battery connection.',
      },
      {
        number: 10,
        title: 'Test Alternator Output',
        description:
          'Start the engine and let it idle for 2-3 minutes. Using a multimeter on DC voltage mode, measure the voltage across the battery terminals. It should read 13.5-14.5 volts. If it reads 12V or less, the alternator may not be charging properly.',
        why: 'This confirms the alternator is charging.',
        tip: 'Revving the engine should slightly increase the voltage.',
        commonMistake: 'Driving without verifying proper output.',
      },
    ],
    torqueSpecifications: [
      {
        fastener: 'Alternator mounting bolts',
        specification: '52 Nm (38 ft-lbs)',
        notes: 'OEM specification. Tighten firmly but do not over-torque.',
      },
    ],
    fluidSpecifications: [],
    troubleshooting: [
      {
        problem: 'Charging light stays on after replacement',
        likelyCause: 'Loose electrical connections or belt slippage',
        whatToCheck: 'Verify all electrical connectors are fully seated and belt is tight',
        fix: 'Reconnect any loose connectors and adjust belt tension as needed.',
      },
      {
        problem: 'Alternator produces low voltage output',
        likelyCause: 'Defective new alternator or poor electrical connection',
        whatToCheck: 'Check voltage output with multimeter and inspect all connectors',
        fix: 'If output is below 13.5V at 1500 RPM, alternator may be defective—exchange it.',
      },
    ],
    proTips: [
      'When replacing the alternator, consider also replacing the serpentine belt if it shows signs of wear.',
      'A properly functioning alternator should output 13.5-14.5 volts at idle and slightly more at higher RPM.',
      'If the battery frequently dies, the issue may not be the alternator but a parasitic drain in the electrical system.',
      'Keep your multimeter handy to periodically check alternator output—this helps catch charging problems early.',
    ],
    successIndicators: [
      'Alternator outputs 13.5-14.5 volts at idle',
      'Charging light goes out when engine starts',
      'Battery voltage remains stable during driving',
      'No grinding or squealing from serpentine belt',
      'Headlights remain bright at all RPM ranges',
    ],
    whenToSeekHelp: 'If the alternator outputs voltage but the battery still does not charge, a fault in the wiring harness or battery itself may exist. Professional electrical diagnosis may be needed.',
    legalDisclaimer: 'IMPORTANT SAFETY NOTICE: Always disconnect the negative battery terminal before working on electrical systems. Failure to do so can result in electrical shock or short circuits causing fire or injury. Never work on a hot engine. If you are uncertain about any electrical procedure, consult a qualified professional.',
  },

  {
    id: 'tj-starter-motor-replacement',
    title: 'Starter Motor Replacement',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [
        { startYear: 2003, endYear: 2006 }
      ],
    },
    video: {
      youtubeId: 'k5QOUNiiq5E',
      title: 'DIY Jeep Tj Wrangler Starter Replacement',
      channel: 'BSK Garage',
    },
    source: 'Original guide created through independent research of starting systems, technician forums, and OEM service documentation. Verified through the Automotive Repair Guide Generation System v9 methodology.',
    category: 'Engine & Drivetrain',
    difficulty: 'intermediate',
    duration: '1-1.5 hours',
    interval: 'Every 150,000+ miles or when failing',
    description:
      'The starter motor cranks the engine to start it. A failing starter produces clicking sounds without engine rotation, slow cranking, or no response to the ignition key. Replacement restores reliable starting.',
    overview: {
      difficulty: 'Intermediate—requires socket work and some spatial awareness for access',
      estimatedTime: '1 to 1.5 hours',
      estimatedCost: '$150-300 (starter) vs $350-600 at a shop',
      whyMatters: 'A failed starter leaves you unable to start the vehicle.',
      safetyNote: 'Always disconnect the negative battery terminal. Work on a cool engine. The starter draws significant current—ensure the battery is disconnected.',
    },
    tools: [
      'Socket wrench set (10mm, 13mm, 15mm)',
      'Jack and jack stands (may be needed for undercarriage access)',
      'Torque wrench',
      'Penetrating oil (for stubborn bolts)',
    ],
    parts: [
      {
        id: 'starter-motor-tj',
        name: 'Starter Motor Assembly',
        partNumber: 'OEM Mopar or Bosch equivalent',
        quantity: 1,
        price: 140,
        category: 'required',
        notes: 'TJ starters vary by engine (2.4L vs 4.0L). Confirm part number before ordering.',
      },
    ],
    safetyInformation:
      'Always disconnect the negative battery terminal before working on starting systems. The engine must be completely cool. The starter draws significant electrical current—never work on the starter with the battery connected, as this creates shock and short circuit hazards.',
    beforeYouStart: [
      'Disconnect the negative battery terminal',
      'Allow the engine to cool for at least 1 hour',
      'Locate the starter in advance (may be under the vehicle)',
      'Ensure you have all tools before starting',
    ],
    steps: [
      {
        number: 1,
        title: 'Disconnect Battery',
        description:
          'Locate the negative battery terminal. Using a wrench, loosen the nut and disconnect the cable.',
        why: 'Prevents electrical shock and short circuits.',
        tip: 'Keep the terminal cover accessible.',
        commonMistake: 'Working without disconnecting the battery.',
      },
      {
        number: 2,
        title: 'Locate the Starter',
        description:
          'The starter is typically mounted on the bottom or side of the engine block, near the transmission. You may need to raise the vehicle and access it from underneath.',
        why: 'You need to know where to work.',
        tip: 'If access is tight, you may need to remove a wheel or other components.',
        commonMistake: 'Confusion about starter location.',
      },
      {
        number: 3,
        title: 'Disconnect Starter Solenoid Cable',
        description:
          'Locate the solenoid cable (thick red cable) connected to the top of the starter. Using a wrench, loosen and remove the nut holding it to the solenoid post. Disconnect the wire.',
        why: 'The solenoid cable must be disconnected before removal.',
        tip: 'Tape the connection to prevent accidental contact.',
        commonMistake: 'Forgetting to disconnect the solenoid cable.',
      },
      {
        number: 4,
        title: 'Disconnect Starter Control Wire',
        description:
          'Locate the smaller wire connector on the solenoid (typically yellow/white wire). Twist or pull the connector off.',
        why: 'The control wire must be disconnected.',
        tip: 'This connector usually pulls straight off.',
        commonMistake: 'Forcing the connector off.',
      },
      {
        number: 5,
        title: 'Remove Starter Mounting Bolts',
        description:
          'The starter is typically held by 2-3 bolts. Using the appropriate socket, remove each bolt completely. Keep bolts organized—you will reuse them.',
        why: 'Mounting bolts must be removed to lift the starter.',
        tip: 'Some bolts may be tight or corroded—use penetrating oil if needed.',
        commonMistake: 'Dropping bolts into the pan or engine bay.',
      },
      {
        number: 6,
        title: 'Remove Old Starter',
        description:
          'Carefully slide the starter away from the engine block and transmission. You may need to rotate it to clear obstructions. Set it on a clean surface.',
        why: 'You need to remove the old component.',
        tip: 'The starter is heavy—support it as you lower it.',
        commonMistake: 'Dropping the starter on your body or vehicle.',
      },
      {
        number: 7,
        title: 'Install New Starter',
        description:
          'Position the new starter in the mounting location. Align the bolt holes and hand-start the bolts. Use the torque wrench to tighten to 52 Nm (38 ft-lbs) per OEM specification.',
        why: 'Proper mounting ensures secure installation.',
        tip: 'Check alignment carefully before tightening.',
        commonMistake: 'Misalignment causing cross-threading.',
      },
      {
        number: 8,
        title: 'Reconnect Starter Wires',
        description:
          'Reconnect the control wire to the solenoid. Reconnect the solenoid cable and tighten the nut securely.',
        why: 'Electrical connections must be secure.',
        tip: 'Ensure all connections are fully seated.',
        commonMistake: 'Loose connections.',
      },
      {
        number: 9,
        title: 'Reconnect Battery',
        description:
          'Reconnect the negative battery terminal.',
        why: 'The electrical system must be complete.',
        tip: 'Ensure the connection is tight.',
        commonMistake: 'Loose battery terminal.',
      },
      {
        number: 10,
        title: 'Test Starting',
        description:
          'Turn the ignition key to the START position. The engine should crank briskly. Let the engine run for 2-3 minutes to confirm proper operation.',
        why: 'This confirms the starter is functioning.',
        tip: 'The starter should engage smoothly without grinding.',
        commonMistake: 'Driving without testing.',
      },
    ],
    torqueSpecifications: [
      {
        fastener: 'Starter mounting bolts',
        specification: '52 Nm (38 ft-lbs)',
        notes: 'OEM specification. Do not over-torque.',
      },
    ],
    fluidSpecifications: [],
    troubleshooting: [
      {
        problem: 'Starter solenoid clicks but engine does not crank',
        likelyCause: 'Weak battery or loose solenoid cable',
        whatToCheck: 'Check battery voltage (should be 12V+) and verify solenoid cable is tight',
        fix: 'Charge battery or tighten solenoid cable connection.',
      },
      {
        problem: 'Starter grinds or makes grinding noise',
        likelyCause: 'Starter is not fully disengaging from flywheel',
        whatToCheck: 'Ensure starter is properly mounted and not catching on other components',
        fix: 'Reinstall starter, ensuring proper alignment with flexplate/flywheel.',
      },
    ],
    successIndicators: [
      'Engine cranks quickly and smoothly',
      'No grinding or clicking noises',
      'Engine starts reliably on first attempt',
      'No electrical burning smells',
      'Battery voltage remains stable during starting',
    ],
    whenToSeekHelp: 'If the starter still does not crank after replacement, a battery, alternator, or electrical system problem may exist. Professional electrical diagnosis may be needed.',
    legalDisclaimer: 'IMPORTANT SAFETY NOTICE: Always disconnect the negative battery terminal before working on starting systems. The starter draws significant electrical current and can cause shock or fire if mishandled. Never work on a hot engine. If uncertain, consult a qualified professional.',
  },

  {
    id: 'tj-intake-exhaust-manifold-gaskets',
    title: 'Intake/Exhaust Manifold Gasket Replacement',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [
        { startYear: 2003, endYear: 2006 }
      ],
    },
    video: {
      youtubeId: 'dvP0TXdwxM0',
      title: 'DIY Jeep 4.0 Intake Manifold Removal DETAILED',
      channel: 'BSK Garage',
    },
    source: 'Original guide created through independent research of manifold systems, technician forums, and OEM service documentation. Verified through the Automotive Repair Guide Generation System v9 methodology.',
    category: 'Engine & Drivetrain',
    difficulty: 'intermediate',
    duration: '2-3 hours',
    interval: 'Every 100,000-150,000 miles or when leaking',
    description:
      'Intake and exhaust manifold gaskets seal the connection between manifolds and the cylinder head. Failing gaskets cause vacuum leaks (intake) or exhaust leaks (exhaust), resulting in rough running, poor emissions, or loud exhaust noise.',
    overview: {
      difficulty: 'Intermediate—requires careful bolt removal and gasket sealing',
      estimatedTime: '2 to 3 hours',
      estimatedCost: '$100-200 (gaskets/seals) vs $400-700 at a shop',
      whyMatters: 'Leaking manifold gaskets cause performance loss and emissions problems.',
      safetyNote: 'Work on a cool engine. The exhaust manifold becomes extremely hot. Allow at least 1 hour for cooling.',
    },
    tools: [
      'Socket wrench set (10mm, 13mm, 14mm, 17mm)',
      'Gasket scraper (plastic preferred)',
      'Torque wrench',
      'Penetrating oil (for corroded bolts)',
      'Shop towels',
    ],
    parts: [
      {
        id: 'intake-manifold-gasket',
        name: 'Intake Manifold Gasket',
        partNumber: 'OEM Mopar or Fel-Pro equivalent',
        quantity: 1,
        price: 25,
        category: 'required',
        notes: 'Engine-specific—confirm part number for 2.4L or 4.0L.',
      },
      {
        id: 'exhaust-manifold-gasket',
        name: 'Exhaust Manifold Gasket',
        partNumber: 'OEM Mopar or Fel-Pro equivalent',
        quantity: 1,
        price: 35,
        category: 'required',
        notes: 'Can be replaced separately or together.',
      },
    ],
    safetyInformation:
      'The exhaust manifold becomes extremely hot during engine operation and remains hot for 1-2 hours after shutdown. Never touch the manifold without protective gloves. Always work on a cool engine. If manifold bolts are severely corroded, apply penetrating oil and wait 15 minutes before attempting removal.',
    beforeYouStart: [
      'Allow the engine to cool completely—at least 1 hour after driving',
      'Take photos of bolt locations before removal',
      'Apply penetrating oil to corroded bolts and wait 15 minutes',
      'Gather all tools before starting',
    ],
    steps: [
      {
        number: 1,
        title: 'Access the Manifolds',
        description:
          'Locate the intake manifold on top of the engine and the exhaust manifold on the side/bottom of the engine. You may need to remove air intake tubing or other components for access.',
        why: 'You need clear access to remove manifold bolts.',
        tip: 'Photograph the air intake routing before removal.',
        commonMistake: 'Attempting work without clearing obstructions.',
      },
      {
        number: 2,
        title: 'Remove Intake Manifold Bolts',
        description:
          'Starting with the intake manifold, locate all bolts (typically 8-12) connecting it to the cylinder head. Using a socket wrench, carefully loosen and remove each bolt. Work in a cross pattern, loosening opposite bolts alternately to prevent warping.',
        why: 'Bolts must be removed in sequence to prevent head warping.',
        tip: 'Keep bolts organized by location.',
        commonMistake: 'Removing bolts in random order.',
      },
      {
        number: 3,
        title: 'Remove Intake Manifold',
        description:
          'Once all bolts are removed, carefully lift the intake manifold away from the cylinder head. Gasket sealer may stick it in place—tap gently with a rubber mallet if needed.',
        why: 'The manifold must be completely removed.',
        tip: 'Do not pry with a screwdriver—use a mallet tap.',
        commonMistake: 'Prying too hard and damaging the manifold or head surface.',
      },
      {
        number: 4,
        title: 'Remove Old Intake Manifold Gasket',
        description:
          'Using a plastic gasket scraper, remove all old gasket material from both the manifold and cylinder head surfaces. Scrape until both surfaces are clean and shiny. Wipe with a clean cloth.',
        why: 'Any gasket material left behind will prevent a seal.',
        tip: 'Use only plastic scrapers to avoid scratching aluminum.',
        commonMistake: 'Using metal scrapers and scratching the sealing surface.',
      },
      {
        number: 5,
        title: 'Repeat for Exhaust Manifold',
        description:
          'Follow steps 2-4 for the exhaust manifold. Remove bolts in a cross pattern, lift the manifold carefully, and scrape all old gasket material.',
        why: 'Both manifolds must be serviced if gaskets are leaking.',
        tip: 'The exhaust manifold bolts are often more corroded—use penetrating oil.',
        commonMistake: 'Not fully cleaning sealing surfaces.',
      },
      {
        number: 6,
        title: 'Install New Intake Manifold Gasket',
        description:
          'Apply a thin bead of gasket sealer to the cylinder head surface (follow product instructions). Position the new intake manifold gasket. Carefully lower the intake manifold onto the gasket. Hand-start all bolts, then tighten in a cross pattern to 23 Nm (17 ft-lbs) per OEM specification.',
        why: 'Proper gasket installation ensures a sealed connection.',
        tip: 'Use gasket sealer sparingly—too much can create a weak seal.',
        commonMistake: 'Using excessive gasket sealer.',
      },
      {
        number: 7,
        title: 'Install New Exhaust Manifold Gasket',
        description:
          'Apply gasket sealer to the cylinder head surface. Position the new exhaust manifold gasket. Lower the exhaust manifold carefully and hand-start bolts. Tighten in a cross pattern to 26 Nm (19 ft-lbs) per OEM specification.',
        why: 'Proper installation prevents exhaust leaks.',
        tip: 'Exhaust manifold bolts should be tightened firmly but not over-torqued.',
        commonMistake: 'Over-tightening, which can crack the manifold.',
      },
      {
        number: 8,
        title: 'Reassemble Components',
        description:
          'Reinstall any components you removed for access (air intake tubing, etc.). Ensure all connections are tight.',
        why: 'All components must be reassembled for proper operation.',
        tip: 'Cross-reference your photos from step 1.',
        commonMistake: 'Forgetting to reinstall components.',
      },
      {
        number: 9,
        title: 'Test for Leaks',
        description:
          'Start the engine and listen for any hissing or vacuum leak sounds. Check for exhaust leaks (look for escaping smoke). Let the engine run for 5 minutes at idle.',
        why: 'This confirms the gaskets are sealing properly.',
        tip: 'Exhaust leaks may take time to show—allow the manifold to warm up.',
        commonMistake: 'Driving without verifying proper sealing.',
      },
    ],
    torqueSpecifications: [
      {
        fastener: 'Intake manifold bolts',
        specification: '23 Nm (17 ft-lbs)',
        notes: 'Tighten in cross pattern.',
      },
      {
        fastener: 'Exhaust manifold bolts',
        specification: '26 Nm (19 ft-lbs)',
        notes: 'OEM specification. May be corroded.',
      },
    ],
    fluidSpecifications: [],
    troubleshooting: [
      {
        problem: 'Vacuum leak sound after replacement',
        likelyCause: 'Intake manifold gasket not sealed properly',
        whatToCheck: 'Inspect gasket surface for gaps or inadequate sealer',
        fix: 'Remove manifold and reinstall gasket with proper sealer application.',
      },
      {
        problem: 'Exhaust leak after replacement',
        likelyCause: 'Exhaust manifold bolts loosened or gasket not sealed',
        whatToCheck: 'Check bolt torque and inspect for gasket seeping',
        fix: 'Re-torque bolts to 26 Nm. If still leaking, remove and reinstall gasket.',
      },
    ],
    successIndicators: [
      'No vacuum leak sounds',
      'No exhaust leaks or hissing',
      'Engine runs smoothly at idle',
      'No rough running or misfires',
      'Check engine light does not appear',
    ],
    whenToSeekHelp: 'If leaks continue after replacement or if manifolds are cracked, professional service may be needed.',
    legalDisclaimer: 'IMPORTANT SAFETY NOTICE: The exhaust manifold becomes extremely hot during operation. Always work on a cool engine and wear protective gloves. Never touch the manifold without allowing it to cool completely. If you are uncertain about any procedure, consult a qualified professional.',
  },

  {
    id: 'tj-engine-valve-cover-gaskets',
    title: 'Engine Valve Cover Gasket Replacement',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [
        { startYear: 2003, endYear: 2006 }
      ],
    },
    video: {
      youtubeId: 'Di1XKTobALY',
      title: 'How to Replace Valve Cover Gasket 1997-2006 Jeep Wrangler',
      channel: 'TRQ',
    },
    source: 'Original guide created through independent research of valve cover sealing systems, technician forums, and OEM service documentation. Verified through the Automotive Repair Guide Generation System v9 methodology.',
    category: 'Engine & Drivetrain',
    difficulty: 'beginner',
    duration: '45 min - 1 hour',
    interval: 'Every 80,000-150,000 miles or when leaking',
    description:
      'The valve cover gasket seals the cover to the cylinder head. A leaking valve cover gasket allows oil to seep onto the engine, creating smoke, burning smells, and potential overheating. Replacement is straightforward and typically one of the easiest engine repairs.',
    overview: {
      difficulty: 'Beginner to Intermediate—simple bolt removal and gasket replacement',
      estimatedTime: '45 minutes to 1 hour',
      estimatedCost: '$40-80 (gasket) vs $150-300 at a shop',
      whyMatters: 'Leaking oil damages other engine components and creates fire risk.',
      safetyNote: 'Work on a cool engine. The valve cover area can be hot even after shutdown.',
    },
    tools: [
      'Socket wrench set (10mm, 13mm)',
      'Gasket scraper (plastic preferred)',
      'Clean shop towels',
      'Small brush (to clean bolt holes)',
    ],
    parts: [
      {
        id: 'valve-cover-gasket',
        name: 'Valve Cover Gasket',
        partNumber: 'OEM Mopar or Fel-Pro equivalent',
        quantity: 1,
        price: 25,
        category: 'required',
        notes: 'Engine-specific (2.4L or 4.0L). Some gaskets come pre-installed on new covers.',
      },
    ],
    safetyInformation:
      'Always work on a cool engine. The valve cover area can remain hot even 1-2 hours after engine shutdown. Wear protective gloves. Allow at least 30 minutes for the engine to cool before starting this work.',
    beforeYouStart: [
      'Allow the engine to cool for at least 30 minutes',
      'Take a photo of valve cover bolt locations',
      'Wipe away any excess oil from the valve cover surface',
      'Have all tools ready',
    ],
    steps: [
      {
        number: 1,
        title: 'Locate Valve Cover',
        description:
          'The valve cover is a long, rectangular cover on top of the engine. It may have spark plug wires or coils mounted on it.',
        why: 'You need to know what you are accessing.',
        tip: 'On 4.0L engines, there is one large cover. On 2.4L, the cover may be different.',
        commonMistake: 'Confusion about location.',
      },
      {
        number: 2,
        title: 'Remove Components (if needed)',
        description:
          'If spark plug wires or air intake tubing are mounted to or near the valve cover, carefully disconnect and move them aside.',
        why: 'You need clear access to valve cover bolts.',
        tip: 'Photograph connections before removal.',
        commonMistake: 'Forcing components, risking damage.',
      },
      {
        number: 3,
        title: 'Remove Valve Cover Bolts',
        description:
          'Locate all bolts holding the valve cover to the cylinder head (typically 6-8 bolts). Using a socket wrench, carefully remove each bolt. Work evenly across the cover, loosening opposite bolts alternately.',
        why: 'Even removal prevents gasket damage.',
        tip: 'Keep bolts organized by location.',
        commonMistake: 'Removing all bolts from one side first, causing warping.',
      },
      {
        number: 4,
        title: 'Remove Valve Cover',
        description:
          'Once all bolts are removed, carefully lift the valve cover straight up. The gasket may stick slightly due to oil residue. If stuck, tap gently with a rubber mallet.',
        why: 'The cover must be removed to access the gasket.',
        tip: 'Do not pry—use gentle tapping.',
        commonMistake: 'Prying too hard and breaking the cover.',
      },
      {
        number: 5,
        title: 'Remove Old Gasket',
        description:
          'Using a plastic gasket scraper, carefully remove the old gasket from the valve cover and cylinder head. Clean until both surfaces are dry and free of gasket material and oil residue.',
        why: 'Any old gasket material will prevent a proper seal.',
        tip: 'A small brush helps clean bolt holes.',
        commonMistake: 'Leaving residual gasket material.',
      },
      {
        number: 6,
        title: 'Install New Gasket',
        description:
          'Many new valve covers come with the gasket pre-installed. If yours does not, carefully position the new gasket on the cylinder head surface, ensuring it is fully seated in the groove.',
        why: 'Proper gasket positioning ensures a seal.',
        tip: 'Some gaskets have a specific orientation—check the package instructions.',
        commonMistake: 'Installing gasket upside down.',
      },
      {
        number: 7,
        title: 'Install Valve Cover',
        description:
          'Carefully lower the valve cover onto the gasket, aligning all bolt holes. Hand-start all bolts, then tighten in a cross pattern (opposite sides alternately) to 12 Nm (9 ft-lbs) per OEM specification. Do not over-tighten—the cover can crack.',
        why: 'Proper tightening ensures a seal without damage.',
        tip: 'Tighten firmly but gently—plastic covers can crack easily.',
        commonMistake: 'Over-tightening and cracking the cover.',
      },
      {
        number: 8,
        title: 'Reconnect Components',
        description:
          'Reinstall any spark plug wires, air intake tubing, or other components you removed. Ensure all connections are secure.',
        why: 'All components must be in place for proper engine function.',
        tip: 'Cross-reference photos from step 2.',
        commonMistake: 'Forgetting to reconnect components.',
      },
      {
        number: 9,
        title: 'Test for Leaks',
        description:
          'Start the engine and let it idle for 2-3 minutes. Look carefully at the valve cover joint for any oil seeping. If no leaks are visible, the repair is successful.',
        why: 'This confirms the gasket is sealing.',
        tip: 'New gaskets may show slight seeping initially—let the engine warm up fully.',
        commonMistake: 'Driving before verifying the seal.',
      },
    ],
    torqueSpecifications: [
      {
        fastener: 'Valve cover bolts',
        specification: '12 Nm (9 ft-lbs)',
        notes: 'OEM specification. Do not over-tighten—covers can crack.',
      },
    ],
    fluidSpecifications: [],
    troubleshooting: [
      {
        problem: 'Oil leaks from valve cover joint',
        likelyCause: 'Gasket not seated properly or bolts too loose',
        whatToCheck: 'Verify bolts are tight and gasket is fully seated',
        fix: 'Tighten bolts to 12 Nm or remove cover and reinstall gasket.',
      },
      {
        problem: 'Burning oil smell but no visible leaks',
        likelyCause: 'Oil seeping slowly and burning on hot engine surfaces',
        whatToCheck: 'Look for wet spots along valve cover joint',
        fix: 'Even slow seeps require attention—tighten bolts or replace gasket.',
      },
    ],
    successIndicators: [
      'No oil seeping from valve cover joint',
      'No burning oil smell',
      'Engine bay remains clean and dry',
      'No smoke from engine compartment',
    ],
    whenToSeekHelp: 'If the valve cover cracks during removal or gasket sealing fails after two attempts, professional service may be needed.',
    legalDisclaimer: 'IMPORTANT SAFETY NOTICE: Always work on a cool engine. The engine can remain hot for up to 2 hours after shutdown. Never touch hot engine components. If you are uncertain about any procedure, consult a qualified professional.',
  },

  // ═══════════════════════════════════════════════════════════════════════════════
  // SUSPENSION GUIDES
  // ═══════════════════════════════════════════════════════════════════════════════

  {
    id: 'tj-sway-bar-link-replacement',
    title: 'Sway Bar Link Replacement',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [
        { startYear: 2003, endYear: 2006 }
      ],
    },
    video: {
      youtubeId: '8ECh8M4D25Y',
      title: 'How to Replace Front Sway Bar Links 1997-2006 Jeep Wrangler',
      channel: 'TRQ',
    },
    source: 'Original guide created through independent research of suspension stabilization systems, technician forums, and OEM service documentation. Verified through the Automotive Repair Guide Generation System v9 methodology.',
    category: 'Suspension & Steering',
    difficulty: 'beginner',
    duration: '30-45 min',
    interval: 'Every 80,000-120,000 miles or when noisy',
    description:
      'Sway bar links connect the sway bar to the control arms, reducing body roll during cornering. Worn links cause clunking noises and reduced handling. Replacement is straightforward and one of the easiest suspension repairs.',
    overview: {
      difficulty: 'Beginner—basic bolt removal, minimal mechanical skill required',
      estimatedTime: '30 to 45 minutes',
      estimatedCost: '$40-80 (links) vs $150-250 at a shop',
      whyMatters: 'Worn sway bar links reduce handling control and create annoying clunking.',
      safetyNote: 'Work on level ground. Always use jack stands if raising the vehicle.',
    },
    tools: [
      'Socket wrench set (13mm, 14mm, 17mm)',
      'Jack and jack stands (optional)',
      'Torque wrench',
    ],
    parts: [
      {
        id: 'sway-bar-link-tj',
        name: 'Sway Bar Link (per side)',
        partNumber: 'OEM Mopar or Moog equivalent',
        quantity: 2,
        price: 35,
        category: 'required',
        notes: 'Links typically come in pairs (left and right). Replace both for balanced handling.',
      },
    ],
    safetyInformation:
      'Always work on level ground with the parking brake engaged. If raising the vehicle, use jack stands for support—never work under a vehicle supported only by a floor jack.',
    beforeYouStart: [
      'Park on level ground',
      'Ensure parking brake is engaged',
      'Locate both sway bar links (one on each side)',
      'Gather tools',
    ],
    steps: [
      {
        number: 1,
        title: 'Locate Sway Bar Links',
        description:
          'Sway bar links are the vertical rods that connect the sway bar (which runs under the vehicle) to the lower control arms (at the wheels). There is one on each side of the vehicle.',
        why: 'You need to know what you are replacing.',
        tip: 'The sway bar is the horizontal rod running down the middle under the vehicle.',
        commonMistake: 'Confusion about location.',
      },
      {
        number: 2,
        title: 'Remove Top Bolt (Sway Bar Connection)',
        description:
          'At the top of the sway bar link where it connects to the sway bar, you will find a bolt with a washer and nut. Using a wrench on the nut and another on the bolt head, loosen and remove the bolt. Keep the bolt and washer.',
        why: 'The top connection must be disconnected.',
        tip: 'You may need two wrenches to prevent the bolt from spinning.',
        commonMistake: 'Spinning the bolt without holding the nut.',
      },
      {
        number: 3,
        title: 'Remove Bottom Bolt (Control Arm Connection)',
        description:
          'At the bottom of the sway bar link where it connects to the lower control arm, you will find a similar bolt, washer, and nut. Loosen and remove this bolt as well.',
        why: 'The bottom connection must be disconnected.',
        tip: 'The bottom bolt is often easier to access than the top.',
        commonMistake: 'Forgetting to remove the bottom bolt.',
      },
      {
        number: 4,
        title: 'Remove Old Sway Bar Link',
        description:
          'Once both bolts are removed, the old sway bar link will come free. Set it aside.',
        why: 'You need to remove the old component.',
        tip: 'The link may fall suddenly—watch your hands.',
        commonMistake: 'Dropping the link on your foot.',
      },
      {
        number: 5,
        title: 'Install New Sway Bar Link',
        description:
          'Position the new sway bar link with the top end at the sway bar and the bottom end at the control arm. Insert the top bolt through the sway bar connection, align the nut, and hand-tighten. Then insert the bottom bolt and hand-tighten. Once both bolts are snug, use the torque wrench to tighten both to 54 Nm (40 ft-lbs) per OEM specification.',
        why: 'Proper installation ensures the sway bar link functions correctly.',
        tip: 'Tighten the top bolt first, then the bottom.',
        commonMistake: 'Over-tightening, which can bend the link.',
      },
      {
        number: 6,
        title: 'Repeat on Other Side',
        description:
          'Follow steps 2-5 on the other side of the vehicle. Always replace sway bar links in pairs for balanced handling.',
        why: 'Both sides need service for even suspension performance.',
        tip: 'The second side usually goes faster.',
        commonMistake: 'Replacing only one link.',
      },
      {
        number: 7,
        title: 'Test for Clunking',
        description:
          'Drive slowly in a safe area and listen for clunking noises from the suspension. The noise should be gone.',
        why: 'This confirms proper installation.',
        tip: 'Clunking typically occurs when turning or going over bumps.',
        commonMistake: 'Driving without verification.',
      },
    ],
    torqueSpecifications: [
      {
        fastener: 'Sway bar link bolts (top and bottom)',
        specification: '54 Nm (40 ft-lbs)',
        notes: 'OEM specification. Tighten evenly on both ends.',
      },
    ],
    fluidSpecifications: [],
    troubleshooting: [
      {
        problem: 'Clunking still present after replacement',
        likelyCause: 'Bolts not tight enough or wrong link installed',
        whatToCheck: 'Verify bolts are tight and link is properly positioned',
        fix: 'Tighten bolts to 54 Nm or verify correct part was installed.',
      },
    ],
    successIndicators: [
      'No clunking noises from suspension',
      'Smooth, responsive handling',
      'No play in suspension at the sway bar link locations',
    ],
    whenToSeekHelp: 'If clunking persists or the link will not stay tight, other suspension components may be damaged.',
    legalDisclaimer: 'IMPORTANT SAFETY NOTICE: Always work on level ground with the parking brake engaged. Never work under a vehicle supported only by a floor jack. If you are uncertain, consult a qualified professional.',
  },

  {
    id: 'tj-strut-bearing-replacement',
    title: 'Strut Bearing Replacement (Upper Strut Support)',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [
        { startYear: 2003, endYear: 2006 }
      ],
    },
    video: {
      youtubeId: 't5IKrhJ1NSw',
      title: 'How to Replace Front Shock Absorbers 1997-2006 Jeep Wrangler (strut reference)',
      channel: 'TRQ',
    },
    source: 'Original guide created through independent research of suspension bearing systems, technician forums, and OEM service documentation. Verified through the Automotive Repair Guide Generation System v9 methodology.',
    category: 'Suspension & Steering',
    difficulty: 'intermediate',
    duration: '1.5-2 hours per side',
    interval: 'Every 100,000-150,000 miles or when noise present',
    description:
      'The strut bearing (upper strut support) allows the steering column to pivot when turning. Worn bearings create clunking noises during steering and rough ride quality. Replacement requires removing the strut tower cover and some careful work.',
    overview: {
      difficulty: 'Intermediate—requires strut removal and bearing replacement',
      estimatedTime: '1.5 to 2 hours per side',
      estimatedCost: '$80-150 (bearing) vs $300-500 at a shop',
      whyMatters: 'Worn bearings cause steering noise and reduce ride quality.',
      safetyNote: 'Always work with the vehicle on jack stands, not just a floor jack. Strut work is suspension-critical.',
    },
    tools: [
      'Socket wrench set (13mm, 14mm, 17mm)',
      'Jack and jack stands',
      'Torque wrench',
      'Strut spring compressor (may be needed)',
    ],
    parts: [
      {
        id: 'strut-bearing-tj',
        name: 'Strut Bearing/Upper Strut Support',
        partNumber: 'OEM Mopar or KYB equivalent',
        quantity: 1,
        price: 65,
        category: 'required',
        notes: 'Often sold as a complete assembly with the bearing and seal.',
      },
    ],
    safetyInformation:
      'Strut work involves compressed springs under tension. Always work with the vehicle properly supported on jack stands. Never work under a vehicle supported only by a floor jack. If springs are compressed or you feel uncertain, a professional service is recommended.',
    beforeYouStart: [
      'Raise and support the vehicle on jack stands',
      'Remove the wheel from the side being worked on',
      'Gather all tools',
    ],
    steps: [
      {
        number: 1,
        title: 'Raise Vehicle and Remove Wheel',
        description:
          'Using a floor jack, carefully lift the vehicle. Place jack stands under the frame. Lower the vehicle onto the stands. Remove the wheel.',
        why: 'You need access to the strut tower.',
        tip: 'Leave one wheel on for stability.',
        commonMistake: 'Working without jack stands.',
      },
      {
        number: 2,
        title: 'Remove Strut Upper Mounting Bolts',
        description:
          'Locate the three bolts at the top of the strut where it connects to the strut tower (inside the engine bay). Using a socket wrench, remove these bolts. Support the strut with your hand or a jack to prevent it from dropping.',
        why: 'The strut must be disconnected from the tower.',
        tip: 'You may need a helper to support the strut weight.',
        commonMistake: 'Letting the strut drop suddenly.',
      },
      {
        number: 3,
        title: 'Remove Strut Lower Mounting Bolt',
        description:
          'At the wheel hub, locate the bolt where the strut connects to the lower control arm. Remove this bolt.',
        why: 'The strut must be fully disconnected.',
        tip: 'The lower bolt is often easier to access.',
        commonMistake: 'Forgetting the lower bolt.',
      },
      {
        number: 4,
        title: 'Remove Strut Assembly',
        description:
          'Carefully withdraw the strut assembly from the vehicle. It should slide out relatively easily.',
        why: 'You need to remove the strut to access the bearing.',
        tip: 'The strut is heavy—support it carefully.',
        commonMistake: 'Dropping the strut.',
      },
      {
        number: 5,
        title: 'Remove Old Bearing',
        description:
          'At the top of the strut, you will find the bearing assembly. Depending on the design, it may be bolted or pressed on. Carefully remove the old bearing. This may require a spring compressor if the spring is compressed.',
        why: 'The old bearing must be removed.',
        tip: 'If you are uncomfortable with spring compression, use a professional.',
        commonMistake: 'Attempting to remove a compressed spring without proper tools.',
      },
      {
        number: 6,
        title: 'Install New Bearing',
        description:
          'Position the new bearing assembly on top of the strut. Ensure it is properly seated and aligned. Tighten any fasteners to 52 Nm (38 ft-lbs) per OEM specification.',
        why: 'Proper bearing installation ensures smooth steering.',
        tip: 'Some bearings press-fit—use appropriate force.',
        commonMistake: 'Misalignment of the bearing.',
      },
      {
        number: 7,
        title: 'Reinstall Strut Assembly',
        description:
          'Carefully guide the strut back into the vehicle. Align the upper bolts and hand-start them. Then align and insert the lower bolt. Using the torque wrench, tighten all bolts to their specifications.',
        why: 'Proper installation restores suspension function.',
        tip: 'Alignment is critical—work carefully.',
        commonMistake: 'Cross-threading bolts.',
      },
      {
        number: 8,
        title: 'Reinstall Wheel',
        description:
          'Remount the wheel and tighten lug nuts in a star pattern.',
        why: 'The vehicle needs wheels.',
        tip: 'Tighten firmly.',
        commonMistake: 'Loose wheels.',
      },
      {
        number: 9,
        title: 'Test Steering',
        description:
          'Lower the vehicle and test steering in a safe area. The steering should be smooth with no clunking noises.',
        why: 'This confirms proper installation.',
        tip: 'Clunking noises should be eliminated.',
        commonMistake: 'Driving without verification.',
      },
    ],
    torqueSpecifications: [
      {
        fastener: 'Strut upper mounting bolts',
        specification: '52 Nm (38 ft-lbs)',
        notes: 'OEM specification.',
      },
      {
        fastener: 'Strut lower mounting bolt',
        specification: '122 Nm (90 ft-lbs)',
        notes: 'OEM specification.',
      },
    ],
    fluidSpecifications: [],
    troubleshooting: [
      {
        problem: 'Clunking noise continues after replacement',
        likelyCause: 'Bearing not properly seated or wrong part installed',
        whatToCheck: 'Verify bearing is aligned and fully seated',
        fix: 'Remove strut and verify bearing installation.',
      },
    ],
    successIndicators: [
      'No clunking during steering',
      'Smooth ride quality',
      'No creaking or grinding noises',
      'Steering is responsive',
    ],
    whenToSeekHelp: 'If you are uncomfortable working with strut springs or if clunking persists, professional service is recommended.',
    legalDisclaimer: 'IMPORTANT SAFETY NOTICE: Strut work involves compressed springs that can release suddenly and cause injury. Always work with the vehicle on jack stands. If you are uncertain about spring compression or bearing installation, consult a qualified professional.',
  },

  {
    id: 'tj-control-arm-replacement',
    title: 'Control Arm Replacement',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [
        { startYear: 2003, endYear: 2006 }
      ],
    },
    video: {
      youtubeId: '0DhuhcaQYNA',
      title: 'How to Replace Front Lower Control Arms 1997-2006 Jeep Wrangler',
      channel: 'TRQ',
    },
    source: 'Original guide created through independent research of suspension geometry, technician forums, and OEM service documentation. Verified through the Automotive Repair Guide Generation System v9 methodology.',
    category: 'Suspension & Steering',
    difficulty: 'intermediate',
    duration: '2-3 hours per side',
    interval: 'Every 100,000-150,000 miles or when bushings fail',
    description:
      'Control arms locate the axle and absorb suspension forces while keeping the wheels in alignment. Worn control arm bushings or ball joints cause clunking, wandering steering, and uneven tire wear. The TJ uses upper and lower control arms at both front and rear axles, and replacement restores ride quality and handling precision.',
    overview: {
      difficulty: 'Intermediate—requires heavy bolt removal, axle support, and torque-to-spec procedures',
      estimatedTime: '2 to 3 hours per control arm',
      estimatedCost: '$80-180 per arm vs $400-700 per arm at a shop',
      whyMatters: 'Worn control arm bushings allow the axle to shift under load, causing alignment problems, premature tire wear, and dangerous handling on highway curves.',
      safetyNote: 'Control arm bolts are torqued very tight from the factory. The axle must be supported throughout the procedure—never let it hang on brake lines or sway bar links.',
    },
    tools: [
      'Socket wrench set (15mm, 18mm, 21mm)',
      'Breaker bar (long—factory torque is high)',
      'Torque wrench (capable of 122 Nm / 90 ft-lbs minimum)',
      'Jack and jack stands (two stands required)',
      'Bottle jack or second floor jack to support axle',
      'Penetrating oil (PB Blaster or equivalent)',
      'Pry bar',
    ],
    parts: [
      {
        id: 'control-arm-tj',
        name: 'Control Arm Assembly',
        partNumber: 'OEM Mopar or Moog/Rough Country equivalent',
        quantity: 1,
        price: 95,
        category: 'required',
        notes: 'Verify upper vs lower and front vs rear before ordering. Aftermarket polyurethane-bushed arms last longer than rubber but transmit more vibration.',
      },
      {
        id: 'control-arm-hardware',
        name: 'Control Arm Bolts (if seized or rounded)',
        partNumber: 'Mopar OEM hardware kit',
        quantity: 2,
        price: 18,
        category: 'optional',
        notes: 'Always have replacement bolts on hand—factory bolts often shear or seize on removal.',
      },
    ],
    safetyInformation:
      'Control arm replacement requires the vehicle to be safely supported on jack stands AND the axle separately supported with a bottle jack or floor jack throughout the procedure. The axle stores no preload, but allowing it to swing free can damage brake lines, ABS sensor wiring, and the differential vent hose. Never work under a vehicle supported only by a hydraulic jack. Wear safety glasses—bushings can release rust and debris when bolts are loosened.',
    beforeYouStart: [
      'Apply penetrating oil to all control arm bolts 24 hours before work',
      'Park on level concrete and chock the wheels',
      'Confirm you have a torque wrench rated to at least 130 Nm (95 ft-lbs)',
      'Identify which arm you are replacing—front upper, front lower, rear upper, or rear lower',
      'Photograph the arm orientation before removal so you can install the new arm the same way',
    ],
    steps: [
      {
        number: 1,
        title: 'Raise and Support the Vehicle',
        description:
          'Loosen the lug nuts on the affected wheel before lifting. Raise the vehicle with a floor jack at the frame, then transfer weight onto two jack stands placed under solid frame points. Lower the vehicle onto the stands and verify they hold weight before removing the jack.',
        why: 'Jack stands provide stable support; a floor jack can fail or roll under sustained load.',
        tip: 'Place the stands forward of the rear control arms or behind the front control arms so they do not block your work area.',
        commonMistake: 'Placing stands under sheet metal or plastic skid plates instead of frame rails.',
      },
      {
        number: 2,
        title: 'Remove the Wheel and Support the Axle',
        description:
          'Remove the lug nuts and pull the wheel off. Position a bottle jack or second floor jack directly under the axle tube near the differential. Apply just enough pressure to support the axle—do not lift it.',
        why: 'When the control arm bolts come out the axle will rotate freely. Supporting it prevents brake lines and the differential vent from being damaged.',
        tip: 'A piece of 2x4 wood between the jack saddle and axle tube prevents marring the axle.',
        commonMistake: 'Letting the axle hang on the brake hoses—this can crack the hose or pull a fitting loose.',
      },
      {
        number: 3,
        title: 'Loosen Control Arm Bolts',
        description:
          'Using a breaker bar with the appropriate socket, break loose both ends of the control arm bolt—one bolt at the frame end and one at the axle end. Apply more penetrating oil if a bolt resists. Do not remove the bolts yet—just break them free.',
        why: 'Breaking factory torque takes substantial leverage. Loosening both ends before removal lets you adjust if alignment shifts.',
        tip: 'Place the breaker bar so it pulls toward you—pushing risks slipping and bruising knuckles.',
        commonMistake: 'Stripping the bolt head by using a worn socket—factory bolts deserve a new 6-point socket.',
      },
      {
        number: 4,
        title: 'Remove the Frame-End Bolt First',
        description:
          'Remove the bolt that attaches the control arm to the frame mount. The arm should still be held by the axle-end bolt. Set the bolt and any washers aside in order—you will reuse them unless they are damaged.',
        why: 'Removing the frame end first lets the arm pivot freely so the axle-end bolt can be more easily withdrawn.',
        tip: 'Tap the bolt with a hammer if it sticks—the frame mount can corrode onto the bolt sleeve.',
        commonMistake: 'Pulling both bolts at once—you lose the arm’s position reference and the axle can shift.',
      },
      {
        number: 5,
        title: 'Remove the Axle-End Bolt',
        description:
          'Support the control arm with one hand and remove the axle-end bolt. The arm will come free. If the bolt resists, gently pry the arm with a pry bar to relieve binding while you withdraw the bolt.',
        why: 'The axle-end bolt usually carries the most rust because it sits low in the chassis and is exposed to road spray.',
        tip: 'Do not pry against the axle housing flange—pry against the control arm bracket itself.',
        commonMistake: 'Hammering the bolt out without supporting the arm—dropping the arm can damage the threads.',
      },
      {
        number: 6,
        title: 'Compare Old and New Control Arms',
        description:
          'Lay the old and new control arms side by side. Confirm the bushings, length, and bracket geometry match. Check that any orientation marks or grease fittings are on the same side. Measure center-to-center bolt hole distance on both arms to verify they are identical.',
        why: 'Aftermarket arms occasionally ship with the wrong length or bushing orientation—catching this before installation saves frustration.',
        tip: 'If the new arm is adjustable, set it to match the old arm length before installation.',
        commonMistake: 'Skipping this check and discovering at torque-up that the new arm does not fit the mounts.',
      },
      {
        number: 7,
        title: 'Install the New Control Arm',
        description:
          'Position the new arm in the frame mount and slide the frame-end bolt through. Hand-thread the nut. Then align the axle-end bracket and slide the axle bolt through. Hand-thread that nut as well. Both bolts should be loose at this stage so the arm can self-align.',
        why: 'Hand-threading first allows the bushings to settle into their natural position before final torque.',
        tip: 'Coat the bolt threads with anti-seize for easier future removal.',
        commonMistake: 'Cross-threading the nut—back off and re-start if it does not turn freely by hand.',
      },
      {
        number: 8,
        title: 'Lower Suspension to Ride Height',
        description:
          'Slowly raise the bottle jack supporting the axle until the suspension is at normal ride height. The control arm bushings should now be in their loaded position—the same position they would be in while driving.',
        why: 'Tightening rubber bushings while the suspension is drooping pre-loads the bushing in the wrong direction and shortens its life by 50 percent or more.',
        tip: 'You can also place the wheel back on and lower the vehicle onto a heavy timber to load the suspension before final torque.',
        commonMistake: 'Torquing the bolts with the suspension hanging—this is the single biggest cause of premature bushing failure.',
      },
      {
        number: 9,
        title: 'Torque Both Bolts to Specification',
        description:
          'Using the torque wrench, tighten the frame-end bolt to 122 Nm (90 ft-lbs) per OEM specification. Then tighten the axle-end bolt to the same 122 Nm (90 ft-lbs). If your specific arm uses a ball joint stud, torque that nut to 102 Nm (75 ft-lbs).',
        why: 'OEM torque values are based on bolt yield strength and bushing compression—under-torquing causes movement, over-torquing distorts the bushing.',
        tip: 'Pull the wrench through a slow, steady arc—a jerky motion can cause you to over-shoot the click.',
        commonMistake: 'Guessing torque by feel—use the torque wrench every time.',
      },
      {
        number: 10,
        title: 'Reinstall the Wheel and Test Drive',
        description:
          'Lower the bottle jack. Remount the wheel and tighten the lug nuts in a star pattern. Lower the vehicle off the jack stands and torque the lug nuts to 122 Nm (90 ft-lbs). Test drive at low speed first, listening for clunks. Verify alignment by checking that the steering wheel sits level on a straight road.',
        why: 'A short test drive confirms the arm is properly installed before highway use.',
        tip: 'Get a four-wheel alignment within 100 miles—any control arm change shifts caster and pinion angle.',
        commonMistake: 'Skipping the alignment and burning up new tires within a few thousand miles.',
      },
    ],
    torqueSpecifications: [
      {
        fastener: 'Control arm-to-frame bolt',
        specification: '122 Nm (90 ft-lbs)',
        notes: 'OEM specification. Torque with suspension at ride height.',
      },
      {
        fastener: 'Control arm-to-axle bolt',
        specification: '122 Nm (90 ft-lbs)',
        notes: 'OEM specification. Torque with suspension at ride height.',
      },
      {
        fastener: 'Ball joint stud nut (if applicable)',
        specification: '102 Nm (75 ft-lbs)',
        notes: 'OEM specification for ball-jointed control arms.',
      },
      {
        fastener: 'Lug nuts',
        specification: '122 Nm (90 ft-lbs)',
        notes: 'OEM specification. Tighten in a star pattern.',
      },
    ],
    fluidSpecifications: [],
    troubleshooting: [
      {
        problem: 'Clunking persists after replacement',
        likelyCause: 'Bolts torqued with suspension drooping, or opposite-side arm also worn',
        whatToCheck: 'Re-check torque at ride height; inspect the matching arm on the other side of the axle',
        fix: 'Re-torque at ride height and replace any matching arm that shows visible bushing cracks.',
      },
      {
        problem: 'Steering wanders after installation',
        likelyCause: 'Caster has shifted due to arm length difference',
        whatToCheck: 'Compare old arm length to new arm length',
        fix: 'Adjust arm length if adjustable, or have a four-wheel alignment performed.',
      },
      {
        problem: 'Bolt cannot be removed even with breaker bar',
        likelyCause: 'Inner sleeve has rusted to the bolt shank',
        whatToCheck: 'Spray penetrating oil and let sit overnight; tap bolt head with a hammer',
        fix: 'If still seized, cut the bolt with a reciprocating saw and replace with new hardware.',
      },
    ],
    proTips: [
      'Replace control arms in matched pairs (both upper or both lower on the same axle) to keep ride balanced.',
      'On lifted TJs, consider adjustable control arms so you can correct caster and pinion angle.',
      'Polyurethane bushings last longer than rubber but require periodic greasing through a zerk fitting.',
      'Mark the arm orientation with paint pen before removal—some arms are not symmetric end-to-end.',
    ],
    successIndicators: [
      'No clunking on bumps or during cornering',
      'Steering tracks straight on a level road',
      'No vibration through the steering wheel',
      'Tire wear remains even over the next 5,000 miles',
    ],
    whenToSeekHelp: 'If a control arm bolt shears during removal and the broken portion remains in the bushing sleeve, professional removal with a torch or heavy press may be required.',
    legalDisclaimer: 'IMPORTANT SAFETY NOTICE: Control arm replacement affects vehicle alignment and handling. Always have a four-wheel alignment performed within 100 miles of the repair. Never work under a vehicle supported only by a floor jack. If you are uncertain about any procedure, consult a qualified professional.',
  },

  {
    id: 'tj-driveshaft-removal-installation',
    title: 'Driveshaft Removal and Installation',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [
        { startYear: 2003, endYear: 2006 }
      ],
    },
    video: {
      youtubeId: '2L4iDXsdPwo',
      title: 'Replacing Driveshaft U Joints on a Jeep TJ',
      channel: 'Atlas Expeditions',
    },
    source: 'Original guide created through independent research of drivetrain components, technician forums, and OEM service documentation. Verified through the Automotive Repair Guide Generation System v9 methodology.',
    category: 'Suspension & Steering',
    difficulty: 'intermediate',
    duration: '1-1.5 hours',
    interval: 'As needed for transmission, transfer case, or U-joint service',
    description:
      'The driveshaft transmits power from the transfer case to the front and rear axles. Removal is required for transmission service, transfer case rebuilds, U-joint replacement, and slip-yoke conversions on lifted TJs. The procedure is straightforward but requires careful attention to flange orientation marks for vibration-free reinstallation.',
    overview: {
      difficulty: 'Intermediate—requires under-vehicle work and careful flange marking',
      estimatedTime: '1 to 1.5 hours per driveshaft',
      estimatedCost: '$0 (removal only) or $40-90 for new U-joints',
      whyMatters: 'A driveshaft installed without indexing marks can cause severe driveline vibration. U-joints inspected during removal can prevent a driveshaft separation at highway speed.',
      safetyNote: 'Driveshafts are heavy—support them when removing the last bolts. Never spin the driveshaft by hand near a moving belt or pulley.',
    },
    tools: [
      'Socket wrench set (12mm, 13mm, 15mm)',
      '12-point socket (some flange bolts use 12-point heads)',
      'Torque wrench',
      'Paint pen or grease pencil for indexing marks',
      'Jack and jack stands',
      'Penetrating oil',
      'Drain pan (in case of transfer case fluid loss)',
    ],
    parts: [
      {
        id: 'u-joint-tj',
        name: 'U-Joint (if replacing)',
        partNumber: 'Spicer 5-1310X or OEM equivalent',
        quantity: 1,
        price: 28,
        category: 'optional',
        notes: 'Replace U-joints in pairs if one shows wear—front and rear of the same shaft.',
      },
      {
        id: 'driveshaft-bolts',
        name: 'Driveshaft Flange Bolts',
        partNumber: 'OEM Mopar hardware',
        quantity: 8,
        price: 12,
        category: 'optional',
        notes: 'Always replace if any are stretched, rusted, or rounded.',
      },
    ],
    safetyInformation:
      'The driveshaft is heavy and can fall when the last flange bolts are removed. Support the shaft with one hand throughout the final removal. The transfer case slip yoke can leak transmission or transfer case fluid when the shaft is removed—have a drain pan ready. Never operate the engine with a driveshaft disconnected—the slip yoke can fly out of the transfer case at high speed.',
    beforeYouStart: [
      'Apply penetrating oil to all flange bolts 24 hours before removal',
      'Park on level ground and chock both rear wheels (or front, depending on which shaft)',
      'Place the transmission in neutral so the driveshaft can be rotated by hand',
      'Have a paint pen or grease pencil ready to mark flange orientation',
      'Confirm the parking brake holds the vehicle on stands',
    ],
    steps: [
      {
        number: 1,
        title: 'Raise the Vehicle and Support on Jack Stands',
        description:
          'Lift the vehicle with a floor jack and transfer weight onto jack stands placed under the frame. You need enough clearance to fit your body and tools under the vehicle. Verify the stands are stable before sliding underneath.',
        why: 'Driveshaft work requires reaching above the shaft, which means your body is mostly under the vehicle.',
        tip: 'Lift all four corners if working on the front driveshaft so the front axle hangs free for clearance.',
        commonMistake: 'Working with insufficient clearance—you cannot swing a wrench properly under a barely-raised vehicle.',
      },
      {
        number: 2,
        title: 'Mark Flange Orientation',
        description:
          'Using a paint pen or grease pencil, draw a clear alignment mark across both flange halves at the rear axle (or front axle for front shaft) and at the transfer case yoke. The mark should be a single line that connects the driveshaft side and the differential or yoke side so you can re-clock the shaft exactly the same way on installation.',
        why: 'Driveshafts are balanced as an assembly. Reinstalling a shaft 180 degrees out of phase causes severe vibration even if all parts are good.',
        tip: 'Mark BOTH ends of the shaft—the transfer case end and the axle end.',
        commonMistake: 'Marking only one end and creating mystery vibration after reinstallation.',
      },
      {
        number: 3,
        title: 'Remove the Axle Flange Bolts',
        description:
          'Locate the four bolts (or U-bolts on some configurations) that hold the driveshaft yoke to the differential pinion flange. Using the appropriate socket, break each bolt loose. Rotate the driveshaft by hand to access bolts on the back side. Remove all four bolts and keep them organized.',
        why: 'The axle end disconnects first because the transfer case end can slide out once the shaft is freed.',
        tip: 'Place a wrench in a slip-joint pliers around the shaft to hold it stationary while you turn the bolts—or have a helper hold the brake pedal to lock the differential.',
        commonMistake: 'Trying to remove a U-joint strap before removing both straps—remove all four bolts before letting the strap loose.',
      },
      {
        number: 4,
        title: 'Lower the Axle End of the Driveshaft',
        description:
          'Once the axle flange is loose, gently lower the rear (or front) end of the shaft. Do not let it drop—keep one hand under it. Set the freed end aside on a piece of cardboard to keep it clean.',
        why: 'Lowering carefully prevents impact damage to the U-joint and yoke surfaces.',
        tip: 'Wrap masking tape around the U-joint caps to keep needle bearings from falling out if you are reusing the joint.',
        commonMistake: 'Letting the shaft drop and bending the yoke or losing needle bearings into the dirt.',
      },
      {
        number: 5,
        title: 'Slide the Driveshaft Out of the Transfer Case',
        description:
          'With the axle end disconnected, slide the slip yoke straight out of the transfer case output shaft. Have a drain pan positioned underneath—a small amount of transfer case fluid will drip out.',
        why: 'The slip yoke is splined into the transfer case and slides freely once the shaft is at an angle.',
        tip: 'Wipe the slip yoke splines clean and inspect for damage before setting the shaft aside.',
        commonMistake: 'Pulling the shaft straight back without angling—it may catch on the exhaust or crossmember.',
      },
      {
        number: 6,
        title: 'Inspect U-Joints and Slip Yoke',
        description:
          'With the shaft on a workbench, hold the yoke in one hand and rotate the U-joint cross. It should turn smoothly with no notchiness. Check for any rust-colored grease at the U-joint caps—this indicates a worn joint. Inspect the slip yoke splines for galling or wear.',
        why: 'A driveshaft is the perfect time to catch a failing U-joint before it strands you on the road.',
        tip: 'Replace any U-joint that has even slight notchiness—they fail quickly once notchy.',
        commonMistake: 'Reinstalling without inspection and discovering a failing U-joint a month later.',
      },
      {
        number: 7,
        title: 'Reinstall the Slip Yoke into Transfer Case',
        description:
          'Apply a thin coat of grease to the slip yoke splines. Slide the yoke straight into the transfer case output until it bottoms gently—do not force it. The yoke should slide in smoothly with hand pressure only.',
        why: 'Greased splines prevent galling and let the shaft compress and extend with axle articulation.',
        tip: 'If the yoke does not slide in easily, rotate the transfer case output shaft by hand to align the splines.',
        commonMistake: 'Forcing the yoke into misaligned splines and damaging the transfer case shaft.',
      },
      {
        number: 8,
        title: 'Align Flange Marks and Reinstall Bolts',
        description:
          'Lift the axle end of the driveshaft into position. Rotate the shaft until your alignment marks line up with the differential flange marks. Insert all four bolts and hand-thread them. Once all four are started, snug them in a cross pattern, then torque each to 52 Nm (38 ft-lbs) per OEM specification.',
        why: 'Cross-pattern tightening keeps the flange faces parallel and prevents flange distortion.',
        tip: 'Apply a drop of medium-strength threadlocker to each bolt thread—driveshaft bolts can vibrate loose.',
        commonMistake: 'Tightening one bolt fully before snugging the others, which cants the flange.',
      },
      {
        number: 9,
        title: 'Verify Rotation and Lower Vehicle',
        description:
          'Rotate the driveshaft by hand and verify it spins smoothly with no binding. Confirm both flanges are tight and indexed correctly. Lower the vehicle from the jack stands.',
        why: 'A final hand-rotation check catches any cross-threaded bolt or misaligned flange before road test.',
        tip: 'Listen for any clicking—a clicking sound during hand rotation indicates a U-joint problem.',
        commonMistake: 'Skipping the rotation check and discovering binding only at highway speed.',
      },
      {
        number: 10,
        title: 'Road Test for Vibration',
        description:
          'Drive the vehicle at progressively higher speeds—25, 45, then 60 mph. Listen and feel for vibration. Mild vibration that disappears within 50 miles is normal as bushings settle. Severe vibration means a flange is misaligned and the shaft must come back out for re-clocking.',
        why: 'Driveline vibration at speed indicates a balance or phasing problem that worsens U-joint wear.',
        tip: 'Check the driveshaft bolts again after 100 miles—they can settle and need a re-torque.',
        commonMistake: 'Ignoring vibration and destroying U-joints within a few thousand miles.',
      },
    ],
    torqueSpecifications: [
      {
        fastener: 'Driveshaft flange bolts',
        specification: '52 Nm (38 ft-lbs)',
        notes: 'OEM specification. Tighten in cross pattern.',
      },
    ],
    fluidSpecifications: [
      {
        fluid: 'Transfer case fluid (top off if leaked)',
        specification: 'ATF+4 (NV231 / NP231 transfer case)',
        notes: 'Verify level after driveshaft service—a small amount typically drips out during slip yoke removal.',
      },
    ],
    troubleshooting: [
      {
        problem: 'Vibration at highway speed after reinstallation',
        likelyCause: 'Driveshaft installed 180 degrees out of phase',
        whatToCheck: 'Compare current flange position to original alignment mark',
        fix: 'Remove driveshaft, rotate 180 degrees, and reinstall with marks aligned.',
      },
      {
        problem: 'Clunk when shifting between drive and reverse',
        likelyCause: 'Worn U-joint or loose flange bolt',
        whatToCheck: 'Inspect U-joints for play; verify bolt torque',
        fix: 'Replace worn U-joints and re-torque flange bolts to spec.',
      },
      {
        problem: 'Transfer case fluid leak after reinstallation',
        likelyCause: 'Damaged output shaft seal during slip yoke insertion',
        whatToCheck: 'Inspect for fluid drips at the transfer case rear seal',
        fix: 'Replace the rear output shaft seal if fluid loss continues.',
      },
    ],
    proTips: [
      'Greased slip yoke splines extend transfer case seal life by reducing wear on the seal lip.',
      'On lifted TJs over 3.5 inches, consider a slip-yoke-eliminator (SYE) and CV driveshaft to reduce vibration.',
      'Always replace flange bolts that show stretching or thread damage—they are inexpensive insurance.',
      'A balanced driveshaft is silent—any vibration after installation means something is misaligned.',
    ],
    successIndicators: [
      'Smooth power delivery at all speeds',
      'No vibration in 25-65 mph range',
      'No clunking when shifting between gears',
      'No fluid leaks at the transfer case rear seal',
    ],
    whenToSeekHelp: 'If you have severe vibration after correctly indexing the driveshaft, the shaft itself may be bent or unbalanced—a driveshaft shop can re-balance or replace the tube.',
    legalDisclaimer: 'IMPORTANT SAFETY NOTICE: A driveshaft separation at highway speed can cause loss of vehicle control. Always torque flange bolts to specification and use threadlocker. Never operate the engine with the driveshaft disconnected. If you are uncertain about any procedure, consult a qualified professional.',
  },

  // ═══════════════════════════════════════════════════════════════════════════════
  // ELECTRICAL DIAGNOSTIC GUIDES
  // ═══════════════════════════════════════════════════════════════════════════════

  {
    id: 'tj-battery-terminals-cleaning',
    title: 'Battery Terminals Cleaning and Inspection',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [
        { startYear: 2003, endYear: 2006 }
      ],
    },
    video: {
      youtubeId: '4hhFK1mH8PQ',
      title: 'How to Clean your Jeep Wrangler Battery Terminals',
      channel: 'Jeep Bros GTA',
    },
    source: 'Original guide created through independent research of battery maintenance practices, technician forums, and OEM service documentation. Verified through the Automotive Repair Guide Generation System v9 methodology.',
    category: 'Electrical',
    difficulty: 'beginner',
    duration: '20-30 min',
    interval: 'Every 6-12 months or when corrosion is visible',
    description:
      'Battery terminal corrosion is the most common cause of slow cranking, no-start conditions, and intermittent electrical issues on TJ Wranglers. Cleaning and protecting the terminals takes less than half an hour and is the highest-value preventive maintenance task on any vehicle.',
    overview: {
      difficulty: 'Beginner—no mechanical skill required, ideal first DIY task',
      estimatedTime: '20 to 30 minutes',
      estimatedCost: '$5-15 (cleaning supplies) vs $40-80 shop labor',
      whyMatters: 'Corroded terminals create high resistance, which drops voltage to the starter and accessories. Severe corrosion can damage the terminal post itself, requiring battery replacement.',
      safetyNote: 'Battery acid is corrosive and explosive hydrogen gas can be present near the battery. Always wear safety glasses and remove the negative terminal first.',
    },
    tools: [
      'Wrench set (typically 8mm, 10mm, or 13mm depending on terminal type)',
      'Battery terminal cleaning brush (post and post-hole brushes)',
      'Wire brush',
      'Small box of baking soda',
      'Spray bottle of water',
      'Battery terminal protectant spray or grease',
      'Safety glasses',
      'Nitrile gloves',
      'Old rag or shop towel',
    ],
    parts: [
      {
        id: 'terminal-felt-washers',
        name: 'Anti-Corrosion Felt Washers',
        partNumber: 'Generic auto parts store item',
        quantity: 2,
        price: 4,
        category: 'optional',
        notes: 'These red and green felt rings install under the terminal clamp and absorb corrosive vapors.',
      },
      {
        id: 'terminal-protectant',
        name: 'Battery Terminal Protectant',
        partNumber: 'CRC, Permatex, or generic equivalent',
        quantity: 1,
        price: 6,
        category: 'optional',
        notes: 'Spray-on red protectant prevents future corrosion. Dielectric grease also works.',
      },
    ],
    safetyInformation:
      'Always wear safety glasses—battery acid causes permanent eye damage. Wear nitrile gloves to protect your skin. Never lean directly over the battery while removing terminals; hydrogen gas can vent during cranking and any spark can ignite it. Remove jewelry before working near a battery—a metal ring across the terminals creates a direct short capable of welding the ring to your finger. Keep all sparks and open flames away from the battery.',
    beforeYouStart: [
      'Park on level ground with the engine off and key removed',
      'Allow the engine to cool for at least 30 minutes',
      'Open the hood and locate the battery (passenger side on most TJs)',
      'Have a baking soda and water solution mixed (about 1 tablespoon baking soda per cup of water)',
      'Wear safety glasses and gloves before touching the battery',
    ],
    steps: [
      {
        number: 1,
        title: 'Inspect the Battery and Terminals',
        description:
          'Visually inspect the battery case for cracks, bulging, or leaking. Look at the terminal posts for white, blue, or green crusty deposits—this is corrosion. Note which terminal has the most corrosion. Check that both terminal clamps are tight on their respective posts.',
        why: 'Visual inspection identifies whether the issue is just corrosion or a deeper battery problem.',
        tip: 'A bulging battery case or any leaking acid means the battery itself must be replaced before cleaning.',
        commonMistake: 'Cleaning a damaged battery instead of replacing it—corrosion may simply return immediately.',
      },
      {
        number: 2,
        title: 'Disconnect the Negative Terminal First',
        description:
          'Locate the negative terminal—it is marked with a minus (−) sign and connects to a black cable. Using the appropriate wrench, loosen the nut on the terminal clamp until you can wiggle the clamp off the post. Lift the cable away and tuck it where it cannot accidentally contact the battery.',
        why: 'Disconnecting negative first eliminates the chassis ground path. If your wrench then touches the chassis while removing positive, no short circuit occurs.',
        tip: 'If the clamp will not lift off, use a battery terminal puller or rock the clamp side-to-side gently.',
        commonMistake: 'Disconnecting positive first—a wrench slip to the chassis creates a violent short.',
      },
      {
        number: 3,
        title: 'Disconnect the Positive Terminal',
        description:
          'Locate the positive terminal—marked with a plus (+) sign and a red cable cover. Loosen the nut and lift the clamp off the post. Tuck this cable away from the battery as well so it cannot contact the terminal during cleaning.',
        why: 'With both terminals removed, the battery is fully isolated and safe to clean.',
        tip: 'Keep the rubber cable cover folded back so you can see the terminal post clearly during cleaning.',
        commonMistake: 'Letting the loose positive cable rest against the negative post and creating a weak short across the battery internals.',
      },
      {
        number: 4,
        title: 'Apply Baking Soda Solution to Neutralize Corrosion',
        description:
          'Pour the baking soda and water solution generously over both terminal posts and clamps. The solution will fizz where it contacts acid corrosion—this is normal. Let it sit for 1-2 minutes to neutralize the corrosion fully.',
        why: 'Baking soda neutralizes sulfuric acid corrosion chemically, making it easy to brush away without scrubbing.',
        tip: 'A toothbrush dipped in the solution helps work it into tight spots.',
        commonMistake: 'Using plain water—corrosion is acidic and only neutralizes with a base like baking soda.',
      },
      {
        number: 5,
        title: 'Brush the Terminals and Posts Clean',
        description:
          'Using the wire brush or terminal cleaning brush, scrub each terminal post until the metal is bright and shiny. The post brush has fine wires inside a tube—it cleans the post by twisting it on. The clamp brush has wires on the outside—it cleans the inside of the clamp. Clean until all corrosion is gone and the metal gleams.',
        why: 'Clean, bright metal provides minimum electrical resistance for full cranking power.',
        tip: 'A second pass with the brush after the first cleaning often removes thin films invisible the first time.',
        commonMistake: 'Stopping cleaning too early—any remaining corrosion will spread again within weeks.',
      },
      {
        number: 6,
        title: 'Rinse and Dry the Battery Area',
        description:
          'Rinse the battery top and surrounding tray with clean water from the spray bottle. Wipe the battery top thoroughly with the shop towel until completely dry. Inspect that no fizzing remains on any metal surface.',
        why: 'Water with dissolved corrosion residue can re-corrode clean metal if left in place. Drying ensures the protectant adheres properly.',
        tip: 'Pay extra attention to the top of the battery between the posts—corrosion residue here causes parasitic drain through dust paths.',
        commonMistake: 'Skipping the dry step and trapping moisture under the new terminal connections.',
      },
      {
        number: 7,
        title: 'Install Felt Anti-Corrosion Washers (Optional)',
        description:
          'If you have anti-corrosion felt washers, slip the red one over the positive post and the green one over the negative post. They sit flat on top of the battery beneath the terminal clamps.',
        why: 'The felts are saturated with a corrosion inhibitor that absorbs acid vapors before they can attack the terminal metal.',
        tip: 'Felts last 1-2 years and should be replaced when they look dry and cracked.',
        commonMistake: 'Installing the wrong color on the wrong terminal—red is positive, green is negative.',
      },
      {
        number: 8,
        title: 'Reconnect the Positive Terminal First',
        description:
          'Slide the positive cable clamp onto the positive post. Push it down until it sits firmly. Tighten the clamp nut until it is snug—you should not be able to rotate the clamp by hand. Do not over-tighten and crack the clamp.',
        why: 'Connecting positive first ensures the wrench cannot short to ground while you work, since the negative is still disconnected.',
        tip: 'A snug clamp should resist rotation by hand but not require excessive force.',
        commonMistake: 'Cross-threading the clamp nut—back off and re-thread if it does not turn freely.',
      },
      {
        number: 9,
        title: 'Reconnect the Negative Terminal',
        description:
          'Slide the negative cable clamp onto the negative post and tighten the nut snugly. Confirm both clamps are secure by attempting to rotate them—neither should move.',
        why: 'Negative is the last connection to make. Once it is on, the vehicle has chassis ground and the electrical system is live again.',
        tip: 'A small spark when seating the negative clamp is normal as system capacitors charge.',
        commonMistake: 'Leaving the clamp loose because it feels tight—a wiggle test confirms a proper connection.',
      },
      {
        number: 10,
        title: 'Apply Terminal Protectant',
        description:
          'Spray the terminal protectant generously over both connections—posts, clamps, and exposed metal. The protectant will dry to a sticky red film. Alternatively, smear dielectric grease over each terminal with a fingertip.',
        why: 'The protectant seals the metal from oxygen and acid vapors, dramatically slowing future corrosion.',
        tip: 'Apply the protectant only AFTER reconnecting the terminals—greased posts make it harder to get a tight connection.',
        commonMistake: 'Greasing the post before installing the clamp, then over-tightening to compensate for the slippery surface.',
      },
    ],
    torqueSpecifications: [
      {
        fastener: 'Battery terminal clamp nut',
        specification: 'Snug by hand—approximately 11 Nm (8 ft-lbs)',
        notes: 'OEM specification calls for snug fit without crushing the clamp.',
      },
    ],
    fluidSpecifications: [],
    troubleshooting: [
      {
        problem: 'Corrosion returns within weeks',
        likelyCause: 'Battery is leaking acid through case crack or terminal seal',
        whatToCheck: 'Inspect the battery for case damage and check the date code',
        fix: 'Replace any battery older than 5 years or showing leakage; corrosion is a symptom of a failing battery.',
      },
      {
        problem: 'Slow cranking even after cleaning',
        likelyCause: 'Battery is at end of life or has internal damage',
        whatToCheck: 'Have battery load-tested at any auto parts store (free service)',
        fix: 'Replace the battery if it fails load testing—cleaning cannot restore a worn-out cell.',
      },
      {
        problem: 'Clamp will not tighten on the post',
        likelyCause: 'Post has been damaged or filed down by repeated cleaning',
        whatToCheck: 'Inspect post diameter and clamp condition',
        fix: 'Install a clamp shim or replace the cable end with a new clamp.',
      },
    ],
    proTips: [
      'Cleaning terminals once a year prevents 80 percent of no-start problems on older vehicles.',
      'A spray of WD-40 on the cable just under the clamp keeps the cable insulation flexible and corrosion-free.',
      'If you live in a salty climate, inspect terminals every six months—road salt accelerates corrosion dramatically.',
      'Keep the battery hold-down clamp tight—a vibrating battery sheds active material and corrodes terminals faster.',
    ],
    successIndicators: [
      'Both terminal posts show bright, clean metal',
      'Engine cranks immediately at full speed',
      'No corrosion returns within 6 months',
      'Headlights and accessories operate at full brightness',
    ],
    whenToSeekHelp: 'If terminals are corroded down to the lead post itself, or if the battery shows physical damage, professional replacement is recommended.',
    legalDisclaimer: 'IMPORTANT SAFETY NOTICE: Batteries contain sulfuric acid and produce explosive hydrogen gas. Always wear eye protection and gloves. Never short the terminals with tools or jewelry. If acid contacts skin or eyes, rinse with copious water for 15 minutes and seek medical attention. If you are uncertain about any procedure, consult a qualified professional.',
  },

  {
    id: 'tj-alternator-output-diagnostic',
    title: 'Alternator Output Diagnostic Test',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [
        { startYear: 2003, endYear: 2006 }
      ],
    },
    video: {
      youtubeId: 'DvASDCfmTjQ',
      title: 'Jeep Wrangler TJ Alternator Replacement (output test reference)',
      channel: 'WhiteTee\'s Garage',
    },
    source: 'Original guide created through independent research of charging system diagnostics, technician forums, and OEM service documentation. Verified through the Automotive Repair Guide Generation System v9 methodology.',
    category: 'Electrical',
    difficulty: 'beginner',
    duration: '20-30 min',
    interval: 'When charging warning light illuminates or battery problems suspected',
    description:
      'Alternator output testing identifies whether your charging system is delivering proper voltage to the battery. This test takes 20 minutes and saves the cost of unnecessary alternator replacement when the actual problem is a worn battery, loose belt, or corroded ground strap.',
    overview: {
      difficulty: 'Beginner—requires only a digital multimeter and basic instructions',
      estimatedTime: '20 to 30 minutes',
      estimatedCost: '$25-50 (multimeter if not owned) vs $80-120 shop diagnostic fee',
      whyMatters: 'Replacing an alternator that does not need replacement is a common mistake. Proper diagnosis isolates whether the alternator, battery, or wiring is the source of charging problems.',
      safetyNote: 'The engine must be running for charging tests. Keep loose clothing, hair, and tools away from the moving belt and pulleys.',
    },
    tools: [
      'Digital multimeter (DMM) capable of DC voltage and AC voltage measurement',
      'Safety glasses',
      'Notepad to record readings',
    ],
    parts: [],
    safetyInformation:
      'Charging system tests are performed with the engine running. Keep all body parts, jewelry, and tools well clear of the moving serpentine belt, pulleys, and cooling fan. Never lean over the engine bay during testing—the cooling fan can engage suddenly. Test in a well-ventilated area; never run an engine in a closed garage.',
    beforeYouStart: [
      'Verify the battery is fully charged before testing—a dead battery skews charging readings',
      'Inspect the serpentine belt for cracks, glazing, or looseness',
      'Inspect battery cables for corrosion, especially the negative cable to chassis ground',
      'Have a notepad ready to record the four key voltage readings',
      'Park in a well-ventilated area with the parking brake on',
    ],
    steps: [
      {
        number: 1,
        title: 'Set Multimeter to DC Voltage Mode',
        description:
          'Power on your digital multimeter and turn the dial to DC voltage—usually marked with a V and a straight line over a dashed line. Select the 20-volt range if your meter requires manual range selection. Auto-ranging meters select automatically.',
        why: 'Battery and alternator output are direct current (DC). Using AC mode produces meaningless readings.',
        tip: 'Touch the probes together briefly—the meter should read 0.000 V. If not, your meter or leads have a problem.',
        commonMistake: 'Setting the meter to AC voltage and getting confusing low readings.',
      },
      {
        number: 2,
        title: 'Measure Battery Voltage with Engine Off (Reading 1)',
        description:
          'With the engine off and all accessories off, touch the red probe to the positive battery terminal and the black probe to the negative terminal. Read the voltage. A healthy battery should read 12.4 to 12.7 volts. Below 12.2 volts indicates a discharged or weak battery.',
        why: 'This baseline reading tells you the battery state of charge before any alternator activity.',
        tip: 'Wait 10 minutes after any driving for the battery to stabilize before this reading.',
        commonMistake: 'Testing a battery that has just been driven—surface charge inflates the reading.',
      },
      {
        number: 3,
        title: 'Start the Engine and Let It Idle',
        description:
          'Start the engine and let it run at idle for 60 seconds with all accessories off. The cooling fan, headlights, and HVAC should all be off. The engine should idle smoothly at approximately 700-800 RPM.',
        why: 'Idle is the lowest charging output. If the alternator can hold proper voltage at idle, it can do so at all speeds.',
        tip: 'Wait until the idle stabilizes—a fast idle from cold start does not represent normal output.',
        commonMistake: 'Testing immediately after start with the cold-start fast idle still active.',
      },
      {
        number: 4,
        title: 'Measure Charging Voltage at Idle (Reading 2)',
        description:
          'With the engine idling and accessories off, touch the multimeter probes to the battery terminals again. The reading should be 13.5 to 14.7 volts. This is the alternator output voltage. Below 13.0 volts means the alternator is not charging adequately. Above 14.8 volts means the regulator may be overcharging.',
        why: 'A working alternator must hold the battery above 13.0 volts at idle—otherwise the battery slowly discharges during city driving.',
        tip: 'If the reading is below 13.0 V, tap the alternator case lightly with a wood handle—if voltage jumps, the brushes are worn.',
        commonMistake: 'Reading the voltmeter on the dashboard instead of measuring at the battery terminals directly—dash gauges are imprecise.',
      },
      {
        number: 5,
        title: 'Measure Charging Voltage Under Load (Reading 3)',
        description:
          'With the engine still idling, turn on the headlights to high beam, the heater fan to high, and the rear defroster (if equipped). These accessories load the alternator at approximately 50 amps. Read the battery voltage. It should still be at least 13.0 volts. A drop below 13.0 indicates the alternator cannot keep up with electrical demand.',
        why: 'Under-load testing reveals weak alternators that pass the no-load test but fail in real-world driving conditions.',
        tip: 'A healthy 90-amp alternator should hold above 13.5 V even with 50 A of accessory load.',
        commonMistake: 'Skipping the load test and assuming the alternator is fine because no-load voltage looked good.',
      },
      {
        number: 6,
        title: 'Raise Engine Speed to 1500 RPM (Reading 4)',
        description:
          'With accessories still on, raise the engine speed to approximately 1500 RPM and hold it steady. Read the voltage at the battery terminals. It should rise to between 13.8 and 14.8 volts. If the voltage stays below 13.5 volts even at higher RPM with load, the alternator is failing.',
        why: 'Raising RPM increases alternator output. A healthy unit responds with higher voltage; a failing unit cannot.',
        tip: 'A helper holding the throttle while you read the meter is much easier than reaching across the engine yourself.',
        commonMistake: 'Revving the engine repeatedly instead of holding steady RPM—the meter cannot stabilize.',
      },
      {
        number: 7,
        title: 'Switch Multimeter to AC Voltage and Test for Ripple',
        description:
          'With the engine still running at idle and accessories on, switch the multimeter to AC voltage mode (V with a wavy line). Read the voltage at the battery terminals. The reading should be less than 0.1 V AC—ideally below 0.05 V AC. A reading above 0.5 V AC indicates a failed diode in the alternator rectifier.',
        why: 'Alternators output AC internally and rectify it to DC. A failed diode lets AC ripple pass through, damaging electronics over time.',
        tip: 'A high AC reading means the alternator is failing internally even if DC voltage looks normal.',
        commonMistake: 'Skipping the ripple test and replacing batteries repeatedly because the alternator is destroying them.',
      },
      {
        number: 8,
        title: 'Test Voltage Drop on Positive Cable',
        description:
          'With the engine running and accessories on, switch back to DC volts. Touch the red probe to the alternator output post and the black probe to the positive battery terminal. The reading should be 0.2 V or less. A reading above 0.3 V indicates excessive resistance in the positive cable—often from corrosion at the connection points.',
        why: 'A high-resistance cable wastes alternator output as voltage drop, robbing the battery of charge.',
        tip: 'Voltage drop testing finds problems that ohms testing on a static cable misses.',
        commonMistake: 'Replacing alternators repeatedly when the actual problem is a corroded cable.',
      },
      {
        number: 9,
        title: 'Test Voltage Drop on Ground Path',
        description:
          'Touch the red probe to the engine block (a clean bolt is fine) and the black probe to the negative battery terminal. With the engine running and accessories on, the reading should be 0.2 V or less. Higher readings indicate a corroded ground strap between the engine and chassis.',
        why: 'Ground path resistance acts like an extra load between the alternator and battery.',
        tip: 'Clean and tighten all ground straps—engine to body, body to negative terminal, and engine to chassis—if voltage drop is high.',
        commonMistake: 'Ignoring the ground path because the positive side looks fine.',
      },
      {
        number: 10,
        title: 'Interpret Results and Document Findings',
        description:
          'Compare your four readings to the expected ranges. If all four readings are within spec, the alternator is healthy. If voltage at idle is below 13.0 V, or AC ripple exceeds 0.1 V, the alternator needs replacement. If voltage drops are high, the cables or grounds need attention. Write down your readings for reference if returning to the diagnosis later.',
        why: 'Systematic interpretation prevents replacing parts that are not broken.',
        tip: 'Most auto parts stores will load-test your alternator for free if you bring it in—use this as a final confirmation.',
        commonMistake: 'Replacing the alternator before testing voltage drops and grounds, only to have the same symptoms return.',
      },
    ],
    torqueSpecifications: [],
    fluidSpecifications: [],
    troubleshooting: [
      {
        problem: 'Voltage drops below 12 V at idle',
        likelyCause: 'Failed alternator, broken belt, or wiring fault',
        whatToCheck: 'Verify belt is tight and turning the alternator; verify alternator electrical connections',
        fix: 'Replace alternator if internal; tighten belt or repair wiring as appropriate.',
      },
      {
        problem: 'Voltage above 15 V at idle',
        likelyCause: 'Failed voltage regulator inside alternator',
        whatToCheck: 'Confirm reading with a second multimeter; verify regulator function',
        fix: 'Replace alternator (regulator is internal on TJ alternators).',
      },
      {
        problem: 'AC ripple above 0.5 V',
        likelyCause: 'Failed diode in alternator rectifier',
        whatToCheck: 'Confirm reading at both idle and 2000 RPM',
        fix: 'Replace alternator—rectifier diodes are not serviceable.',
      },
      {
        problem: 'Voltage drop on cable exceeds 0.3 V',
        likelyCause: 'Corroded or loose connection at alternator, battery, or starter solenoid',
        whatToCheck: 'Inspect each connection point along the positive cable',
        fix: 'Clean and tighten all connections; replace cable if corrosion is internal.',
      },
    ],
    proTips: [
      'A free load test at any auto parts store confirms multimeter results before buying a new alternator.',
      'Battery and alternator must be tested together—a weak battery can mask alternator problems and vice versa.',
      'Voltage drop testing is the single most valuable diagnostic skill for charging system problems.',
      'Record the four key readings (key off, idle, idle with load, 1500 RPM with load) and compare year over year to track aging.',
    ],
    successIndicators: [
      'Idle voltage between 13.5 and 14.7 V',
      'Voltage holds above 13.0 V under accessory load',
      'AC ripple below 0.1 V',
      'Voltage drops below 0.2 V on both positive and ground paths',
    ],
    whenToSeekHelp: 'If readings are inconsistent or you suspect intermittent alternator failure, a professional charging system analyzer can capture problems that handheld meters miss.',
    legalDisclaimer: 'IMPORTANT SAFETY NOTICE: Charging system testing requires the engine running. Keep all tools, clothing, and body parts well clear of the serpentine belt, pulleys, and cooling fan. Never test in a closed garage—engine exhaust contains carbon monoxide. If you are uncertain about any procedure, consult a qualified professional.',
  },

  {
    id: 'tj-starter-motor-diagnostic',
    title: 'Starter Motor Diagnostic Test',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [
        { startYear: 2003, endYear: 2006 }
      ],
    },
    video: {
      youtubeId: 'k5QOUNiiq5E',
      title: 'DIY Jeep TJ Wrangler Starter Replacement (diagnostic reference)',
      channel: 'BSK Garage',
    },
    source: 'Original guide created through independent research of starter system diagnostics, technician forums, and OEM service documentation. Verified through the Automotive Repair Guide Generation System v9 methodology.',
    category: 'Electrical',
    difficulty: 'intermediate',
    duration: '30-45 min',
    interval: 'When slow cranking, no-crank, or solenoid-only clicks occur',
    description:
      'Slow cranking and no-crank conditions are commonly blamed on the starter, but the actual problem is often a weak battery, corroded cables, or a failing ignition switch. This diagnostic procedure isolates the starter motor and solenoid from the rest of the system using voltage drop testing.',
    overview: {
      difficulty: 'Intermediate—requires multimeter use and access to under-vehicle starter connections',
      estimatedTime: '30 to 45 minutes',
      estimatedCost: '$0 (uses existing tools) vs $80-150 shop diagnostic fee',
      whyMatters: 'Starter motors are expensive and often replaced unnecessarily. Proper diagnosis identifies whether the starter, solenoid, battery, ignition switch, or starter relay is at fault.',
      safetyNote: 'The starter motor draws 200-400 amps during cranking. Probes and tools must be kept clear of the cranking circuit during tests.',
    },
    tools: [
      'Digital multimeter with DC voltage capability',
      'Long jumper wires (12-gauge or thicker, fused if possible)',
      'Wrench set for terminal access',
      'Helper to turn the ignition key',
      'Safety glasses',
      'Wheel chocks',
    ],
    parts: [],
    safetyInformation:
      'Starter cranking draws hundreds of amps and produces significant heat at any high-resistance connection. Never bridge the starter solenoid with a thin wire or tool—use only properly rated jumper cables. Place the transmission in PARK (or NEUTRAL on manual transmissions with the clutch fully depressed) and chock the wheels before cranking. Wear safety glasses; loose corrosion particles can dislodge during cranking.',
    beforeYouStart: [
      'Charge the battery fully and verify resting voltage above 12.4 V',
      'Confirm battery terminals are clean and tight',
      'Have a helper available to turn the ignition key on command',
      'Place the transmission in PARK and engage the parking brake',
      'Chock both rear wheels',
    ],
    steps: [
      {
        number: 1,
        title: 'Verify Battery State of Charge',
        description:
          'Connect the multimeter to the battery terminals (red to positive, black to negative). With the engine off and all accessories off, the reading should be 12.4 to 12.7 V. Any reading below 12.2 V means the battery itself is the likely cause of slow cranking and must be charged or replaced before further testing.',
        why: 'A weak battery cannot deliver the high amperage required for cranking, mimicking starter failure.',
        tip: 'Charge the battery for 8 hours and retest if it reads below 12.2 V.',
        commonMistake: 'Diagnosing the starter circuit with a battery that is itself the problem.',
      },
      {
        number: 2,
        title: 'Listen to the Cranking Behavior',
        description:
          'Have a helper turn the ignition key to START while you listen near the starter. A normal start cranks the engine over briskly. A failed solenoid produces a single loud click. A failed starter produces a faint clicking series. A failed connection produces no sound at all. Note exactly what you hear.',
        why: 'The cranking sound narrows down the failure mode immediately.',
        tip: 'Tap the starter case with a wood handle while turning the key—if it suddenly cranks, the brushes are worn and the starter needs replacement.',
        commonMistake: 'Skipping this listening step and going straight to electrical testing without context.',
      },
      {
        number: 3,
        title: 'Measure Battery Voltage During Cranking',
        description:
          'Connect the multimeter to the battery terminals. Have the helper crank the engine for 5 seconds while you watch the meter. Healthy cranking shows the voltage drop to 9.6-10.5 V and recover. A drop below 9.0 V indicates a weak battery. A drop to near zero indicates a short circuit or extreme starter draw.',
        why: 'Voltage during cranking reveals battery capacity under real load—much more revealing than resting voltage.',
        tip: 'Many digital meters update slowly. A meter with a "min/max" function captures the lowest reading accurately.',
        commonMistake: 'Reading only the steady value and missing the actual voltage dip during the cranking event.',
      },
      {
        number: 4,
        title: 'Measure Voltage Drop on Positive Cable to Starter',
        description:
          'With the multimeter on DC volts, touch the red probe to the positive battery post and the black probe to the large stud on the starter solenoid (where the positive battery cable connects). Have your helper crank the engine. The reading should be less than 0.5 V. A reading above 0.5 V indicates excessive resistance in the positive cable.',
        why: 'A corroded or loose positive cable drops voltage that should reach the starter, causing slow cranking even with a good battery.',
        tip: 'Voltage drop testing only works with current flowing—static ohms measurements miss this fault.',
        commonMistake: 'Testing the cable cold and pronouncing it good when in fact it fails under load.',
      },
      {
        number: 5,
        title: 'Measure Voltage Drop on Ground Path',
        description:
          'Touch the red probe to the engine block (a clean bolt) and the black probe to the negative battery terminal. Have the helper crank the engine. The reading should be less than 0.3 V. A higher reading means the engine-to-chassis ground strap or the battery negative cable is corroded.',
        why: 'Ground path resistance is just as harmful as positive path resistance and is more often overlooked.',
        tip: 'Inspect the battery negative cable where it bolts to the engine—this is the most common high-resistance ground point on TJs.',
        commonMistake: 'Testing only the positive side and missing a ground problem that mimics starter failure.',
      },
      {
        number: 6,
        title: 'Test the Starter Solenoid Activation Voltage',
        description:
          'Locate the small wire on the starter solenoid (the trigger wire from the ignition switch). With the multimeter on DC volts, back-probe the connector or touch the probe to the wire while the helper holds the key in START. The reading should be at least 10 V. A lower reading indicates a problem in the ignition switch, neutral safety switch, or starter relay.',
        why: 'Insufficient trigger voltage means the solenoid cannot pull in fully, leading to clicks but no cranking.',
        tip: 'A reading near zero means the starter relay or neutral safety switch has failed.',
        commonMistake: 'Replacing the starter when the actual fault is upstream in the trigger circuit.',
      },
      {
        number: 7,
        title: 'Bench-Test the Starter (Optional)',
        description:
          'If voltage drops are within spec but the starter still cranks slowly or fails, the starter motor itself is the problem. Most auto parts stores will bench-test a starter for free—remove the starter and bring it in. The test confirms current draw and rotation speed.',
        why: 'A bench test isolates the starter from all vehicle wiring, providing a definitive verdict.',
        tip: 'Note your voltage drop readings before removing the starter—they confirm whether the cable is also at fault.',
        commonMistake: 'Buying a new starter without bench-testing the old one and being surprised when symptoms persist.',
      },
      {
        number: 8,
        title: 'Bypass Test the Solenoid (Diagnostic Only)',
        description:
          'WARNING: Use only a properly fused jumper rated for high amperage. With the transmission in PARK and all wheels chocked, briefly bridge the large positive stud and the small trigger terminal on the solenoid using the jumper. The starter should crank instantly. If it does not, the starter or solenoid is faulty. If it does crank, the trigger circuit upstream is the fault.',
        why: 'Bypassing the trigger circuit isolates whether the fault is in the starter or in the upstream wiring.',
        tip: 'Some technicians use a remote starter switch tool—safer than a bare jumper.',
        commonMistake: 'Performing this test in DRIVE—the vehicle will lurch forward and cause injury or damage.',
      },
      {
        number: 9,
        title: 'Test the Neutral Safety Switch',
        description:
          'On automatic transmissions, with the transmission in PARK, the neutral safety switch must close to allow cranking. Move the shifter slightly within PARK and try cranking—if cranking returns, the switch is misadjusted or failing. On manuals, the clutch pedal switch performs the same function.',
        why: 'A failing neutral safety switch is one of the most common causes of intermittent no-crank.',
        tip: 'Sometimes shifting to NEUTRAL allows cranking when PARK does not—this confirms a switch problem.',
        commonMistake: 'Replacing the starter when the fix is a $30 neutral safety switch.',
      },
      {
        number: 10,
        title: 'Document Findings and Plan Repair',
        description:
          'Based on your readings, identify the single failed component. Slow cranking with healthy battery and low voltage drops points to the starter. Single click with no cranking and good trigger voltage points to the solenoid. No trigger voltage points to the relay or neutral safety switch. Plan replacement based on the actual diagnosis.',
        why: 'Targeted repair is faster and cheaper than replacing parts based on guesses.',
        tip: 'Document each test reading in a logbook—future starter problems are easier to diagnose with a baseline.',
        commonMistake: 'Replacing the easiest-to-reach part instead of the actual failed component.',
      },
    ],
    torqueSpecifications: [],
    fluidSpecifications: [],
    troubleshooting: [
      {
        problem: 'Single click, no cranking',
        likelyCause: 'Starter solenoid failure or low trigger voltage',
        whatToCheck: 'Measure trigger voltage at solenoid; bypass test with jumper',
        fix: 'Replace starter if solenoid fails; trace trigger circuit if voltage is low.',
      },
      {
        problem: 'Slow cranking with healthy battery',
        likelyCause: 'Voltage drop in cables or starter brushes worn',
        whatToCheck: 'Measure voltage drops on positive and ground paths during cranking',
        fix: 'Clean cables and grounds; replace starter if drops are within spec.',
      },
      {
        problem: 'No sound at all when key turned',
        likelyCause: 'Failed neutral safety switch, ignition switch, or starter relay',
        whatToCheck: 'Test trigger voltage at solenoid; wiggle shifter in PARK',
        fix: 'Replace failed switch or relay based on which test fails.',
      },
      {
        problem: 'Starter cranks but disengages instantly',
        likelyCause: 'Worn starter drive (Bendix) or low voltage to solenoid',
        whatToCheck: 'Measure voltage during cranking; inspect starter drive',
        fix: 'Replace starter—drive engagement is internal.',
      },
    ],
    proTips: [
      'Voltage drop testing is the single most valuable starter diagnostic skill—learn it once and save hundreds.',
      'Carry a jumper cable when troubleshooting in the field—you can often crank the engine to drive home before doing full diagnostics.',
      'Record the lowest voltage during cranking yearly—a drop in this number predicts battery and starter aging.',
      'Many TJ no-crank problems trace to the engine-to-chassis ground strap, not the starter itself.',
    ],
    successIndicators: [
      'Battery holds above 9.6 V during cranking',
      'Voltage drops on cables stay below 0.5 V positive and 0.3 V ground',
      'Solenoid trigger voltage is at least 10 V',
      'Engine cranks briskly and starts within 2 seconds',
    ],
    whenToSeekHelp: 'If you cannot isolate the fault between starter, solenoid, and trigger circuit using these tests, a professional with an inductive amp clamp can measure cranking current directly and provide definitive diagnosis.',
    legalDisclaimer: 'IMPORTANT SAFETY NOTICE: Cranking circuits carry hundreds of amps. Always use properly rated jumpers for any bypass test. Place the transmission in PARK and chock the wheels before cranking tests. If you are uncertain about any procedure, consult a qualified professional.',
  },

  // ═══════════════════════════════════════════════════════════════════════════════
  // COOLING / HEATING GUIDES
  // ═══════════════════════════════════════════════════════════════════════════════

  {
    id: 'tj-heater-core-replacement',
    title: 'Heater Core Replacement',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [
        { startYear: 2003, endYear: 2006 }
      ],
    },
    video: {
      youtubeId: 'vgAc_TQNSGM',
      title: 'Jeep Wrangler TJ - Evaporator Core and Heater Core Replacement',
      channel: 'FreeAutoMechanic',
    },
    source: 'Original guide created through independent research of HVAC systems, technician forums, and OEM service documentation. Verified through the Automotive Repair Guide Generation System v9 methodology.',
    category: 'Cooling & Heating',
    difficulty: 'advanced',
    duration: '4-6 hours',
    interval: 'When heater fails, leaks coolant inside cabin, or fogs windshield',
    description:
      'The heater core is a small radiator inside the dashboard that uses engine coolant to warm cabin air. When it fails, symptoms include sweet-smelling steam from vents, foggy windshield, wet front carpet, and loss of cabin heat. Replacement requires partial dashboard removal—the most labor-intensive comfort repair on a TJ.',
    overview: {
      difficulty: 'Advanced—requires HVAC system access and dashboard partial disassembly',
      estimatedTime: '4 to 6 hours',
      estimatedCost: '$60-120 (heater core) vs $600-1,000 at a shop',
      whyMatters: 'A leaking heater core dumps coolant into the cabin, soaking carpet and pad with antifreeze that is toxic to pets and damages the floor pan with corrosion. Continued operation can also cause engine overheating from coolant loss.',
      safetyNote: 'Coolant is hot under pressure when the engine is warm. Allow the system to cool fully before opening any hose. Coolant is toxic—catch all spillage and dispose of it properly.',
    },
    tools: [
      'Socket and wrench set (8mm, 10mm, 13mm, 15mm)',
      'Phillips and flat-blade screwdrivers',
      'Trim removal tools (plastic)',
      'Hose pliers or hose pinch-off clamps',
      'Coolant catch pan (minimum 3 gallons)',
      'Funnel',
      'Shop towels',
      'Torque wrench',
      'Vacuum cleaner (for cabin debris)',
      'Headlamp or work light',
    ],
    parts: [
      {
        id: 'heater-core-tj',
        name: 'Heater Core Assembly',
        partNumber: 'OEM Mopar or Spectra Premium 99019',
        quantity: 1,
        price: 80,
        category: 'required',
        notes: 'Verify part fits 2003-2006 TJ HVAC housing. OEM is preferred for fit.',
      },
      {
        id: 'coolant-mopar',
        name: 'Coolant (Mopar 5-Year/100,000-Mile)',
        partNumber: 'Mopar 68163848AB or Zerex G-05 equivalent',
        quantity: 2,
        price: 22,
        category: 'required',
        notes: 'TJ uses HOAT (hybrid organic acid technology) coolant. Mix 50/50 with distilled water if not pre-mixed.',
      },
      {
        id: 'heater-hose-clamps',
        name: 'Heater Hose Clamps',
        partNumber: 'Spring or worm-drive clamps',
        quantity: 2,
        price: 4,
        category: 'optional',
        notes: 'Replace any clamps that show corrosion or have lost spring tension.',
      },
    ],
    safetyInformation:
      'Coolant is hot under pressure when the engine is warm. Never open the radiator cap or any coolant hose until the engine has cooled for at least 2 hours. Ethylene glycol coolant is toxic if ingested—wipe up spills immediately and keep away from children and pets. The heater core is fragile—dropping it will crack a tube and ruin the part. Disconnect the negative battery terminal before working under the dashboard to avoid airbag deployment.',
    beforeYouStart: [
      'Allow the engine to cool fully—at least 2 hours after last use',
      'Disconnect the negative battery terminal and wait 5 minutes for airbag capacitors to discharge',
      'Have approximately 2 gallons of fresh coolant ready for refill',
      'Photograph all dashboard connections before removal',
      'Lay out a clean work surface for trim pieces and screws',
    ],
    steps: [
      {
        number: 1,
        title: 'Drain the Cooling System',
        description:
          'Place the coolant catch pan under the radiator drain petcock at the bottom of the radiator. Open the petcock and let coolant drain into the pan. Once flow stops, also remove a lower radiator hose to drain the engine block. Drain at least 2 gallons total. Save the coolant if it appears clean for testing or recycling.',
        why: 'Removing coolant from the system prevents a coolant flood when heater hoses are disconnected.',
        tip: 'Open the radiator cap to break vacuum and speed drainage.',
        commonMistake: 'Skipping the block drain and being surprised by gallons of coolant pouring out under the dashboard.',
      },
      {
        number: 2,
        title: 'Disconnect Heater Hoses at the Firewall',
        description:
          'Locate the two heater hoses where they enter the firewall on the engine bay side. Use hose pliers to release the clamps and slide them back. Twist the hoses to break them free, then pull them off the heater core inlet and outlet tubes. Have shop towels ready to catch residual coolant.',
        why: 'Disconnecting at the firewall keeps cabin contamination minimal and lets you push the tubes back into the heater box.',
        tip: 'Mark which hose is inlet and which is outlet—they connect differently on the new core.',
        commonMistake: 'Pulling on the rubber hose body and tearing it instead of releasing the clamp first.',
      },
      {
        number: 3,
        title: 'Remove Lower Dashboard Trim and Glove Box',
        description:
          'Remove the screws holding the lower dashboard trim panel below the glove box (usually 4-6 Phillips screws). Open the glove box, squeeze the sides inward, and let it drop down past its stops to free it. Remove any kick panel screws revealed underneath. Pull the trim away gently to avoid breaking clips.',
        why: 'Heater core access requires removing the lower right side of the dashboard.',
        tip: 'Use a plastic trim tool to pop hidden clips—a flat screwdriver will scratch the trim.',
        commonMistake: 'Forcing trim off and breaking the brittle plastic clips that hold it in place.',
      },
      {
        number: 4,
        title: 'Disconnect HVAC Cables and Electrical Connectors',
        description:
          'Identify the temperature blend door cable, the mode door cable (or vacuum lines on some TJs), and the electrical connector for the blower motor. Disconnect each one carefully. Photograph each connection before disconnecting so you can reassemble in the same configuration.',
        why: 'These cables and connectors must be removed to free the HVAC housing.',
        tip: 'A piece of masking tape with a marker note on each cable identifies them for reassembly.',
        commonMistake: 'Yanking electrical connectors apart instead of pressing the release tab and damaging the locks.',
      },
      {
        number: 5,
        title: 'Remove the Heater Core Cover',
        description:
          'Locate the small access cover on the right side of the HVAC housing—it is held by 4-6 screws. Remove the screws and pull the cover off. The heater core will now be visible inside the housing.',
        why: 'The cover protects the heater core during assembly and is the only access path for replacement.',
        tip: 'Keep all screws together in a magnetic tray—they vary in length and must return to their original holes.',
        commonMistake: 'Trying to pull the entire HVAC housing out of the dashboard—on the TJ, the heater core comes out through this access cover.',
      },
      {
        number: 6,
        title: 'Slide Out the Old Heater Core',
        description:
          'Reach into the housing and slide the heater core straight toward you and out of the cavity. The core is delicate—do not pry it out. Have a towel ready to catch residual coolant from the core tubes. Set the old core aside on absorbent material.',
        why: 'The core slides out the access opening once the cover is removed.',
        tip: 'Tilt the core slightly so any remaining coolant pours into a catch container, not onto the carpet.',
        commonMistake: 'Pulling sharply and bending or cracking the core, then realizing the new one was supposed to be installed without damage.',
      },
      {
        number: 7,
        title: 'Inspect the HVAC Housing and Clean Debris',
        description:
          'With the core out, vacuum the inside of the HVAC housing. Inspect the foam seals around the core opening for cracks or compression. If they are damaged, replace them—any leak around the core will allow unconditioned outside air into the cabin.',
        why: 'A new heater core installed into a leaky housing wastes the labor by allowing air bypass around the core.',
        tip: 'Adhesive-backed foam strips from any auto parts store work as replacement seals.',
        commonMistake: 'Reinstalling without cleaning and discovering leaves and rodent debris stuck to the new core later.',
      },
      {
        number: 8,
        title: 'Install the New Heater Core',
        description:
          'Slide the new heater core into the housing in the same orientation as the old core. The inlet and outlet tubes must protrude through the firewall openings on the engine bay side. Verify the core is fully seated by checking that the cover panel will lay flat over it.',
        why: 'Proper orientation ensures the cover seats and the heater hoses connect on the correct sides.',
        tip: 'Test-fit the cover before tightening any screws—if it does not sit flat, the core is not fully seated.',
        commonMistake: 'Installing the core backward and finding that the heater hoses cannot reach the tubes.',
      },
      {
        number: 9,
        title: 'Reinstall Cover, Cables, and Trim',
        description:
          'Reinstall the access cover and tighten its screws. Reconnect HVAC cables and electrical connectors using your photographs as a reference. Test that the temperature and mode doors move freely through their full range. Reinstall the glove box, kick panel, and any trim pieces.',
        why: 'Proper reassembly ensures the HVAC system functions and the dashboard stays in place.',
        tip: 'Operate the temperature knob and listen for the blend door moving—it should make a soft thunk at each end of travel.',
        commonMistake: 'Forcing trim into place when a cable is pinched—stop and reroute the cable.',
      },
      {
        number: 10,
        title: 'Reconnect Heater Hoses and Refill Coolant',
        description:
          'Reconnect both heater hoses at the firewall, ensuring clamps are seated past the hose barb. Close the radiator drain petcock. Slowly refill the cooling system through the radiator with the prescribed coolant mix. Run the engine with the heater on high and the radiator cap open until thermostat opens and bubbles stop. Top off as needed and reinstall the cap.',
        why: 'Bleeding the air out of the system is essential—trapped air causes hot spots and false low-coolant indications.',
        tip: 'Park nose-uphill during refill to encourage air to rise out of the radiator.',
        commonMistake: 'Closing the radiator cap before all the air is purged and getting an overheat condition on the test drive.',
      },
    ],
    torqueSpecifications: [
      {
        fastener: 'Heater core cover screws',
        specification: '5-7 Nm (4-5 ft-lbs)',
        notes: 'Snug only—plastic housing strips easily.',
      },
      {
        fastener: 'Radiator drain petcock',
        specification: 'Hand-tight',
        notes: 'Plastic petcock—do not use tools to tighten.',
      },
    ],
    fluidSpecifications: [
      {
        fluid: 'Engine coolant',
        specification: 'Mopar 5-Year/100,000-Mile HOAT (G-05 equivalent), 50/50 with distilled water',
        notes: 'Cooling system capacity approximately 9.5 quarts (4.0L I6) including heater core. Use only HOAT-rated coolant—not green, not Dex-Cool.',
      },
    ],
    troubleshooting: [
      {
        problem: 'No heat after replacement',
        likelyCause: 'Air trapped in heater core or hoses reversed',
        whatToCheck: 'Verify hose routing and bleed system completely',
        fix: 'Re-bleed system with engine running and heater on high; reverse hoses if necessary.',
      },
      {
        problem: 'Coolant smell from vents',
        likelyCause: 'Heater core leaking or hose clamp loose',
        whatToCheck: 'Inspect hose clamps and check for visible leak',
        fix: 'Tighten clamps; replace core if leak is internal.',
      },
      {
        problem: 'Wet front passenger carpet',
        likelyCause: 'Heater core leak under dashboard',
        whatToCheck: 'Pull back carpet and inspect for moisture',
        fix: 'Replace heater core—any leak will worsen with thermal cycling.',
      },
      {
        problem: 'Engine overheats after work completed',
        likelyCause: 'Air pocket in cooling system',
        whatToCheck: 'Check coolant level after engine cools; bleed system',
        fix: 'Park nose-uphill and bleed with cap off until bubbles stop.',
      },
    ],
    proTips: [
      'Replace both heater hoses at the same time—they are usually as old as the original core.',
      'A pressure test of the new core before installation catches any defective replacement parts.',
      'Photograph every connection before disconnecting—dashboard reassembly is much faster with photo references.',
      'Buy the OEM Mopar core if cost allows—aftermarket fit varies and a poor fit means redoing the entire job.',
    ],
    successIndicators: [
      'Strong heat at all dashboard vents',
      'No coolant smell or steam from vents',
      'Front passenger carpet remains dry',
      'Engine maintains normal operating temperature',
      'Coolant level holds steady after multiple drive cycles',
    ],
    whenToSeekHelp: 'If the dashboard does not reassemble correctly or a hidden coolant leak persists after careful inspection, professional HVAC service may be required to disassemble the dash further.',
    legalDisclaimer: 'IMPORTANT SAFETY NOTICE: Coolant is toxic and hot under pressure. Never open a hot cooling system. Disconnect the battery before working under the dashboard to avoid airbag deployment. Wipe up all spills—coolant attracts pets and is fatal if ingested. If you are uncertain about any procedure, consult a qualified professional.',
  },

  {
    id: 'tj-fan-clutch-replacement',
    title: 'Fan Clutch Replacement',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [
        { startYear: 2003, endYear: 2006 }
      ],
    },
    video: {
      youtubeId: 'l1ra_HRrdkI',
      title: 'How To Replace Fan Clutch On 2001 Jeep TJ 4.0',
      channel: 'Shockwave Outdoors',
    },
    source: 'Original guide created through independent research of cooling system components, technician forums, and OEM service documentation. Verified through the Automotive Repair Guide Generation System v9 methodology.',
    category: 'Cooling & Heating',
    difficulty: 'intermediate',
    duration: '1-1.5 hours',
    interval: 'When fan does not engage at high coolant temperature, or fan continues to roar when warm',
    description:
      'The fan clutch is a viscous coupler between the water pump pulley and the engine fan. When cool it slips, allowing the fan to free-wheel for fuel economy and quiet operation. When hot it engages, locking the fan to draw air through the radiator. A failed fan clutch causes overheating in stop-and-go traffic or constant fan roar even on cool days.',
    overview: {
      difficulty: 'Intermediate—requires shroud removal and a fan clutch wrench or large adjustable wrench',
      estimatedTime: '1 to 1.5 hours',
      estimatedCost: '$50-120 (fan clutch) vs $250-400 at a shop',
      whyMatters: 'A failed fan clutch causes overheating that can warp the cylinder head or crack the radiator end tank—both expensive secondary repairs.',
      safetyNote: 'The engine must be cool. The fan blade can cut deeply—keep fingers clear during all work and never spin the fan with the engine running.',
    },
    tools: [
      'Fan clutch wrench (32mm or adjustable)',
      'Pulley holding tool or wide flat-blade screwdriver',
      '8mm and 10mm sockets',
      'Phillips and flat-blade screwdrivers',
      'Torque wrench',
      'Penetrating oil',
      'Safety glasses',
      'Work gloves',
    ],
    parts: [
      {
        id: 'fan-clutch-tj',
        name: 'Fan Clutch Assembly',
        partNumber: 'Hayden 2790, OEM Mopar, or Borg-Warner equivalent',
        quantity: 1,
        price: 75,
        category: 'required',
        notes: 'Severe-duty fan clutch is recommended for off-road use or AC-equipped vehicles.',
      },
      {
        id: 'fan-shroud-clips',
        name: 'Fan Shroud Clips (if broken)',
        partNumber: 'Mopar OEM',
        quantity: 4,
        price: 6,
        category: 'optional',
        notes: 'Plastic clips often break during shroud removal.',
      },
    ],
    safetyInformation:
      'The engine fan blade is sharp enough to cause severe lacerations. Always disconnect the negative battery terminal before working near the fan—electric cooling fans on AC-equipped models can engage at any time the battery is connected. Wear safety glasses and gloves. Allow the engine to cool fully before starting—the fan clutch and water pump get extremely hot.',
    beforeYouStart: [
      'Allow the engine to cool fully—at least 1 hour after shutdown',
      'Disconnect the negative battery terminal',
      'Inspect the fan blade for cracks or chips—replace if damaged',
      'Apply penetrating oil to the fan clutch nut 24 hours before work',
      'Have a fan clutch wrench (32mm) on hand—the nut size is non-standard',
    ],
    steps: [
      {
        number: 1,
        title: 'Disconnect Battery and Remove Air Intake (If Needed)',
        description:
          'Disconnect the negative battery terminal. If the air intake tube blocks access to the upper radiator hose or fan shroud, loosen its clamps and move it aside. On the 4.0L I6 the intake is usually clear of the work area; on the 2.4L I4 it may need to come off.',
        why: 'Battery disconnection prevents accidental electric fan engagement on AC-equipped models.',
        tip: 'Tape the air intake opening with a shop towel to prevent debris from falling into the throttle body.',
        commonMistake: 'Skipping battery disconnection and being startled by the electric fan suddenly running.',
      },
      {
        number: 2,
        title: 'Drain Coolant Below Upper Hose Level',
        description:
          'Place a coolant catch pan under the radiator drain petcock. Open the petcock and drain coolant until the level falls below the upper radiator hose connection. Roughly 2 quarts is sufficient. Close the petcock when complete.',
        why: 'Lowering the coolant level prevents spillage when the upper radiator hose is disconnected.',
        tip: 'Save the drained coolant if clean for refill.',
        commonMistake: 'Skipping this step and ending up with coolant pouring out when the hose comes off.',
      },
      {
        number: 3,
        title: 'Disconnect Upper Radiator Hose at Engine',
        description:
          'Loosen the spring clamp on the upper radiator hose at the thermostat housing. Twist the hose to break it free, then pull it off. Tuck it aside with a shop towel under the open end.',
        why: 'Removing the upper hose creates clearance for fan shroud removal.',
        tip: 'A pair of hose clamp pliers makes spring clamps much easier to handle.',
        commonMistake: 'Cutting the hose with side cutters instead of releasing the clamp—now you need a new hose too.',
      },
      {
        number: 4,
        title: 'Remove the Fan Shroud',
        description:
          'Locate the bolts or clips holding the upper portion of the fan shroud to the radiator (typically 2-4 fasteners). Remove them. The lower shroud half usually stays in place. Lift the upper shroud half up and over the fan blade, careful not to scratch the radiator fins. Set it aside.',
        why: 'The shroud must come off so the fan and clutch can be lifted out as an assembly.',
        tip: 'Mark where the shroud halves overlap so you can reassemble in the same position.',
        commonMistake: 'Forcing the shroud past the fan blade and bending fan tips against the radiator.',
      },
      {
        number: 5,
        title: 'Loosen the Fan Clutch Nut',
        description:
          'The fan clutch threads onto the water pump shaft with a single large nut (typically 32mm, reverse-threaded on some models—turn clockwise to loosen on TJ I6). Hold the water pump pulley stationary with a pulley holding tool or by inserting a wide screwdriver into a pulley spoke. Use the fan clutch wrench to break the nut loose.',
        why: 'The pulley must be held still so the wrench can break the clutch loose without damaging belts.',
        tip: 'Confirm thread direction in your service manual—a reverse-threaded nut tightened by mistake will seize.',
        commonMistake: 'Turning the wrench the wrong direction and tightening the nut—now even harder to remove.',
      },
      {
        number: 6,
        title: 'Remove Fan and Clutch Assembly Together',
        description:
          'Once the nut is loose, finish removing it by hand. Lift the fan and clutch assembly straight up and out of the engine bay. Be careful—the assembly is awkward and the blade is sharp. Set it aside on a workbench.',
        why: 'Fan and clutch come out as one unit—separating them on the bench is easier than in the engine bay.',
        tip: 'Wear gloves—the fan blade edges are surprisingly sharp.',
        commonMistake: 'Dropping the assembly and bending a fan blade—even a slight bend creates vibration.',
      },
      {
        number: 7,
        title: 'Separate Fan from Old Clutch',
        description:
          'On the workbench, remove the four bolts (typically 13mm) that attach the fan blade to the clutch hub. Set the fan aside. Inspect the fan blades for cracks—replace the fan if any are present.',
        why: 'The fan is reused; only the clutch is replaced.',
        tip: 'Inspect the rivets on the fan blade roots—any movement here means the blade should be replaced.',
        commonMistake: 'Reusing a cracked fan and having it disintegrate at high RPM.',
      },
      {
        number: 8,
        title: 'Install Fan on New Clutch',
        description:
          'Position the fan against the new clutch hub in the same orientation as the original. Install the four bolts and tighten in a cross pattern to 23 Nm (17 ft-lbs) per OEM specification.',
        why: 'Cross-pattern tightening keeps the fan flange flat against the clutch hub.',
        tip: 'A drop of medium threadlocker on each bolt prevents loosening from heat cycling.',
        commonMistake: 'Tightening one bolt fully before snugging the others, which warps the fan flange.',
      },
      {
        number: 9,
        title: 'Reinstall Fan Clutch on Water Pump',
        description:
          'Lower the new fan-and-clutch assembly into the engine bay and start the clutch nut on the water pump shaft by hand. Once threaded, tighten with the fan clutch wrench while holding the pulley stationary. Tighten until firmly snug—approximately 41 Nm (30 ft-lbs).',
        why: 'A properly torqued clutch will not loosen during operation but is not over-stressed.',
        tip: 'A few drops of clean engine oil on the threads ease installation and future removal.',
        commonMistake: 'Cross-threading the nut on the water pump shaft—back off and re-start if it does not turn freely.',
      },
      {
        number: 10,
        title: 'Reinstall Shroud, Hose, Refill, and Test',
        description:
          'Reinstall the upper fan shroud, the upper radiator hose, and any air intake components removed earlier. Refill the cooling system to the proper level. Reconnect the battery. Start the engine and run with the heater on high. Watch the temperature gauge during warm-up. The fan should engage with a noticeable "roar" when coolant temperature reaches approximately 90-95°C (195-205°F), then disengage as temperature drops.',
        why: 'Real-world testing confirms the new clutch responds to temperature correctly.',
        tip: 'A working fan clutch makes a brief roar at startup as the silicone fluid is cold and viscous, then quiets within 30 seconds.',
        commonMistake: 'Driving away without warmup testing and discovering an overheat condition on a hot summer day.',
      },
    ],
    torqueSpecifications: [
      {
        fastener: 'Fan-to-clutch bolts',
        specification: '23 Nm (17 ft-lbs)',
        notes: 'OEM specification. Tighten in cross pattern.',
      },
      {
        fastener: 'Fan clutch nut to water pump',
        specification: '41 Nm (30 ft-lbs)',
        notes: 'OEM specification. Note thread direction—do not over-torque.',
      },
      {
        fastener: 'Fan shroud bolts/clips',
        specification: '5-7 Nm (4-5 ft-lbs)',
        notes: 'Snug only—plastic shroud cracks easily.',
      },
    ],
    fluidSpecifications: [
      {
        fluid: 'Engine coolant (top off)',
        specification: 'Mopar HOAT (G-05) 50/50 with distilled water',
        notes: 'Top off after work; full system capacity is approximately 9.5 quarts on 4.0L.',
      },
    ],
    troubleshooting: [
      {
        problem: 'Engine overheats in stop-and-go traffic after replacement',
        likelyCause: 'New clutch defective or installed loose',
        whatToCheck: 'Verify clutch nut torque; observe fan engagement at warm-up',
        fix: 'Re-torque clutch nut; if fan does not engage at temperature, return clutch as defective.',
      },
      {
        problem: 'Fan roars constantly even on cool days',
        likelyCause: 'Locked-up clutch or installed backward',
        whatToCheck: 'Spin fan by hand when cool—it should turn freely with light drag',
        fix: 'Replace clutch; verify orientation matches old unit.',
      },
      {
        problem: 'Vibration at idle after replacement',
        likelyCause: 'Fan-to-clutch bolts uneven or fan blade bent',
        whatToCheck: 'Verify all four fan bolts are torqued evenly; inspect blade alignment',
        fix: 'Re-torque bolts in cross pattern; replace fan if blade is bent.',
      },
    ],
    proTips: [
      'Severe-duty fan clutches engage earlier and are worth the small price premium for towing or hot-climate use.',
      'A working fan clutch should let you stop the fan with a rolled-up newspaper when cool—if you cannot, the clutch is locked.',
      'When the fan is removed, also inspect the water pump for shaft play—any wobble means the pump is also failing.',
      'Replace the upper radiator hose if it shows softening or cracks while you have it disconnected.',
    ],
    successIndicators: [
      'Engine maintains normal operating temperature in traffic',
      'Fan engages with audible roar at high coolant temperature',
      'Fan disengages and quiets at highway speed',
      'No vibration or wobble at any RPM',
      'Coolant level holds steady after multiple drive cycles',
    ],
    whenToSeekHelp: 'If the clutch nut cannot be loosened with reasonable force, professional removal with heat or specialty tools may be required to avoid damaging the water pump shaft.',
    legalDisclaimer: 'IMPORTANT SAFETY NOTICE: Engine fans cause severe injuries every year. Always work on a cool engine with the battery disconnected. Never spin the fan with the engine running. If you are uncertain about any procedure, consult a qualified professional.',
  },

  {
    id: 'tj-radiator-hose-replacement',
    title: 'Radiator Hose Replacement',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [
        { startYear: 2003, endYear: 2006 }
      ],
    },
    video: {
      youtubeId: 'GmBc1ZN9OR0',
      title: 'How To Replace Radiator Hoses (Jeep TJ)',
      channel: 'HeyItsWinner',
    },
    source: 'Original guide created through independent research of cooling system maintenance, technician forums, and OEM service documentation. Verified through the Automotive Repair Guide Generation System v9 methodology.',
    category: 'Cooling & Heating',
    difficulty: 'beginner',
    duration: '45-60 min',
    interval: 'Every 4-6 years or when hose shows softening, swelling, or cracking',
    description:
      'Radiator hoses carry coolant between the engine and radiator under significant pressure and heat. Aging rubber softens, cracks, or balloons under pressure—risking sudden burst and engine overheating. Preventive replacement is among the most important and least expensive cooling system repairs.',
    overview: {
      difficulty: 'Beginner—straightforward hose work with simple hand tools',
      estimatedTime: '45 to 60 minutes',
      estimatedCost: '$25-50 (hose set) vs $150-250 at a shop',
      whyMatters: 'A burst hose strands the vehicle, dumps coolant, and can warp the cylinder head from rapid overheating. Preventive replacement is the cheapest insurance against a roadside breakdown.',
      safetyNote: 'The engine must be cool. Coolant under pressure is hot enough to cause severe burns.',
    },
    tools: [
      'Hose clamp pliers or screwdriver (depending on clamp type)',
      'Coolant catch pan (minimum 3 gallons)',
      'Funnel',
      'Shop towels',
      'Razor blade or hose cutter (only if old hose must be cut off)',
      'Safety glasses',
      'Nitrile gloves',
    ],
    parts: [
      {
        id: 'upper-radiator-hose',
        name: 'Upper Radiator Hose',
        partNumber: 'Gates 21525, OEM Mopar, or Dayco equivalent',
        quantity: 1,
        price: 18,
        category: 'required',
        notes: 'Verify part fits 4.0L I6 or 2.4L I4 specifically.',
      },
      {
        id: 'lower-radiator-hose',
        name: 'Lower Radiator Hose',
        partNumber: 'Gates 22525, OEM Mopar, or Dayco equivalent',
        quantity: 1,
        price: 22,
        category: 'required',
        notes: 'Lower hose has internal anti-collapse spring on TJ I6—verify replacement includes it.',
      },
      {
        id: 'hose-clamps-radiator',
        name: 'Replacement Hose Clamps',
        partNumber: 'Worm-drive or constant-tension type',
        quantity: 4,
        price: 8,
        category: 'optional',
        notes: 'Replace any factory spring clamps that have lost tension.',
      },
      {
        id: 'coolant-mopar-hose',
        name: 'Coolant',
        partNumber: 'Mopar HOAT (G-05 equivalent) 50/50',
        quantity: 2,
        price: 22,
        category: 'required',
        notes: 'TJ uses HOAT coolant—not green, not Dex-Cool. Mix 50/50 with distilled water if not pre-mixed.',
      },
    ],
    safetyInformation:
      'Never open the cooling system while the engine is hot. Coolant under pressure can spray scalding fluid causing severe burns. Allow the engine to cool for at least 2 hours before starting. Coolant is toxic—wipe spills immediately and keep away from pets and children. The lower radiator hose may carry residual coolant even after draining—catch all drips.',
    beforeYouStart: [
      'Allow the engine to cool fully—at least 2 hours after shutdown',
      'Compare new hoses to old ones before disconnecting—confirm correct fit',
      'Have approximately 2 gallons of fresh coolant ready',
      'Inspect the radiator and water pump fittings for corrosion before installing new hoses',
      'Clear adequate workspace at the front of the engine bay',
    ],
    steps: [
      {
        number: 1,
        title: 'Drain the Cooling System',
        description:
          'Place the coolant catch pan under the radiator drain petcock at the lower passenger-side corner of the radiator. Open the petcock by hand and let coolant drain. Open the radiator cap to break vacuum and speed flow. Drain at least 2 gallons.',
        why: 'A drained system allows hose removal without significant coolant spill.',
        tip: 'Save the drained coolant in a sealed container if it appears clean and the system was recently serviced—it can be reused.',
        commonMistake: 'Forcing the petcock with pliers and breaking the plastic threads.',
      },
      {
        number: 2,
        title: 'Identify and Inspect Both Hoses',
        description:
          'The upper radiator hose runs from the thermostat housing on the engine to the upper radiator inlet. The lower hose runs from the radiator outlet at the bottom corner to the water pump inlet. Inspect both for cracks, swelling, soft spots, or chemical hardening. Squeeze each hose—a healthy hose has firm spring; a failing hose feels mushy or hard.',
        why: 'Visual inspection confirms which hoses need replacement and helps identify any other issues.',
        tip: 'A flashlight angled at the hose surface reveals fine cracks invisible in normal light.',
        commonMistake: 'Replacing only one hose when both are the same age—the other will fail soon.',
      },
      {
        number: 3,
        title: 'Loosen Upper Hose Clamps',
        description:
          'Locate the spring clamps or worm-drive clamps at both ends of the upper radiator hose. Use hose clamp pliers (for spring clamps) or a screwdriver (for worm drives) to loosen each clamp and slide it back from the hose end onto the hose body.',
        why: 'Clamps must be moved off the hose end before the hose can be twisted free.',
        tip: 'Spring clamps can pinch fingers—keep hands clear of the jaws when squeezing the pliers.',
        commonMistake: 'Trying to pull a clamp off the hose body without first squeezing it free—damaging the clamp.',
      },
      {
        number: 4,
        title: 'Remove the Upper Hose',
        description:
          'Twist the hose at one end to break the seal between the rubber and the metal fitting. Pull the hose off. If the hose is bonded to the fitting from age, slice it lengthwise with a razor blade to peel it off without damaging the fitting. Repeat at the other end. Discard the old hose.',
        why: 'Aged hoses often bond to the metal fitting and require careful removal to avoid fitting damage.',
        tip: 'Cut along the hose top so the cut does not affect the fitting underneath.',
        commonMistake: 'Prying with a screwdriver against a soft brass radiator fitting and damaging it.',
      },
      {
        number: 5,
        title: 'Inspect and Clean the Fittings',
        description:
          'Inspect the engine fitting (thermostat housing) and the radiator fitting for corrosion, scoring, or cracks. Clean the fitting surfaces with a shop towel and a small wire brush if needed—use only light brushing. The metal must be smooth and clean for the new hose to seal properly.',
        why: 'A new hose installed onto a corroded fitting will leak no matter how tight the clamps are.',
        tip: 'A thin smear of coolant on the cleaned fitting helps the new hose slide on more easily.',
        commonMistake: 'Skipping the cleaning and creating an immediate leak when the system is pressurized.',
      },
      {
        number: 6,
        title: 'Install the New Upper Hose',
        description:
          'Slide the new upper hose onto the engine fitting first, pushing it on until it seats fully against the shoulder. Position the hose clamp behind the bead of the fitting so the clamp pulls the hose tight against the fitting. Slide the hose onto the radiator fitting on the other end and position that clamp similarly.',
        why: 'Clamps must be positioned correctly behind the fitting bead—too far away and they will not grip.',
        tip: 'A small amount of coolant on the fittings makes the hose slide on much easier.',
        commonMistake: 'Installing the hose so a kink forms when the engine settles in its mounts.',
      },
      {
        number: 7,
        title: 'Tighten the Upper Hose Clamps',
        description:
          'For worm-drive clamps, tighten each screw until the clamp is firm against the hose—about 4 Nm (35 in-lbs). For spring clamps, simply release the pliers and let the clamp self-tension. Verify each clamp is positioned approximately 1/4 inch back from the end of the hose.',
        why: 'Properly tightened clamps create a leak-free seal without crushing the hose internal structure.',
        tip: 'Worm-drive clamps can over-tighten—stop when the rubber starts to bulge slightly outside the clamp.',
        commonMistake: 'Over-tightening worm clamps and cutting through the rubber, creating a slow leak.',
      },
      {
        number: 8,
        title: 'Repeat for the Lower Hose',
        description:
          'The lower hose follows the same procedure: loosen both clamps, twist and pull off the hose, clean the fittings, install the new hose, position and tighten the clamps. The lower hose typically has an internal anti-collapse spring—verify it is in place inside the new hose before installation.',
        why: 'The lower hose carries cold coolant from the radiator to the water pump and is more vulnerable to collapse from suction.',
        tip: 'The lower hose connection at the water pump may be tight—wiggle gently rather than forcing.',
        commonMistake: 'Forgetting the internal spring and finding the new hose collapses under suction at high RPM.',
      },
      {
        number: 9,
        title: 'Refill the Cooling System',
        description:
          'Close the radiator drain petcock by hand. Slowly pour the coolant mix into the radiator until the level reaches the top. Have the heater set to maximum heat with the engine off. Once the radiator is full, also fill the overflow reservoir to the COLD line.',
        why: 'Filling slowly with the heater on heat allows the coolant to flow through all passages.',
        tip: 'A funnel with an extended neck (Lisle 24680 or similar) reduces spillage during fill.',
        commonMistake: 'Pouring quickly and overflowing with air pockets that will cause overheating.',
      },
      {
        number: 10,
        title: 'Bleed Air and Test for Leaks',
        description:
          'Start the engine with the radiator cap off. Run with the heater on high. Watch the coolant level in the radiator—it will fluctuate as air escapes. As the engine warms, the thermostat opens and coolant circulates fully. Top off with more coolant as the level drops. Once the level holds steady and bubbles stop, install the radiator cap. Run the engine for 15 minutes and inspect both hoses for leaks.',
        why: 'Bleeding eliminates air pockets that cause hot spots and false low-coolant readings.',
        tip: 'Park nose-uphill if possible during bleeding—air rises to the radiator top and escapes more easily.',
        commonMistake: 'Closing the radiator cap before air is purged and overheating on the test drive.',
      },
    ],
    torqueSpecifications: [
      {
        fastener: 'Worm-drive hose clamps',
        specification: '4 Nm (35 in-lbs)',
        notes: 'Tighten until the hose seats firmly without bulging beyond the clamp.',
      },
      {
        fastener: 'Radiator drain petcock',
        specification: 'Hand-tight',
        notes: 'Plastic threads—do not use tools.',
      },
    ],
    fluidSpecifications: [
      {
        fluid: 'Engine coolant',
        specification: 'Mopar HOAT (G-05 equivalent) 50/50 with distilled water',
        notes: 'Cooling system capacity approximately 9.5 quarts (4.0L) or 8.5 quarts (2.4L). Use only HOAT-rated coolant.',
      },
    ],
    troubleshooting: [
      {
        problem: 'Coolant leak at hose connection',
        likelyCause: 'Clamp not positioned correctly or fitting corroded',
        whatToCheck: 'Verify clamp position behind fitting bead; inspect fitting surface',
        fix: 'Reposition clamp; clean fitting and reinstall.',
      },
      {
        problem: 'Engine overheats after replacement',
        likelyCause: 'Air trapped in cooling system',
        whatToCheck: 'Check coolant level after engine cools',
        fix: 'Re-bleed system with heater on high until air is purged.',
      },
      {
        problem: 'Lower hose collapses at high RPM',
        likelyCause: 'Missing internal anti-collapse spring',
        whatToCheck: 'Inspect new hose for internal spring',
        fix: 'Replace with correct hose that includes the spring.',
      },
      {
        problem: 'Sweet smell after operation but no visible leak',
        likelyCause: 'Slow seep at fitting connection',
        whatToCheck: 'Use UV dye or pressure tester to find leak',
        fix: 'Tighten clamps slightly; replace hose if seep persists.',
      },
    ],
    proTips: [
      'Replace both upper and lower hoses at the same time—they age together.',
      'Inspect the heater hoses while you have the system drained—they often need replacement at the same age.',
      'Keep one spare upper hose in the trunk for emergency roadside repairs.',
      'A thermostat is cheap insurance—replace it while the system is drained if it is more than five years old.',
    ],
    successIndicators: [
      'No coolant leaks at any hose connection',
      'Engine reaches and holds normal operating temperature',
      'Coolant level remains steady after multiple drive cycles',
      'No sweet smell of coolant near engine bay',
      'Heater produces strong heat at all dashboard vents',
    ],
    whenToSeekHelp: 'If hoses cannot be removed without damaging the radiator fittings, professional service may be needed to avoid radiator replacement.',
    legalDisclaimer: 'IMPORTANT SAFETY NOTICE: Coolant is toxic and can cause severe burns when hot. Never open a hot cooling system. Wipe up all spills—coolant attracts pets and is fatal if ingested. Dispose of used coolant at a recycling center. If you are uncertain about any procedure, consult a qualified professional.',
  },

  // ═══════════════════════════════════════════════════════════════════════════════
  // DRIVETRAIN MAINTENANCE GUIDES
  // ═══════════════════════════════════════════════════════════════════════════════

  {
    id: 'tj-transmission-mount-replacement',
    title: 'Transmission Mount Replacement',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [
        { startYear: 2003, endYear: 2006 }
      ],
    },
    video: {
      youtubeId: 'a4AFdKIrK8Y',
      title: 'How to Replace Transmission Mount 1997-2006 Jeep Wrangler',
      channel: 'TRQ',
    },
    source: 'Original guide created through independent research of drivetrain mounts, technician forums, and OEM service documentation. Verified through the Automotive Repair Guide Generation System v9 methodology.',
    category: 'Engine & Drivetrain',
    difficulty: 'intermediate',
    duration: '1.5-2 hours',
    interval: 'Every 100,000 miles or when transmission vibration or clunking is felt',
    description:
      'The transmission mount supports the rear of the transmission and absorbs vibration between the drivetrain and the chassis. A worn mount allows the transmission to drop, causing driveshaft angle changes, vibration, clunking on shifts, and exhaust contact with the floor pan.',
    overview: {
      difficulty: 'Intermediate—requires lifting the transmission with a jack while removing fasteners',
      estimatedTime: '1.5 to 2 hours',
      estimatedCost: '$30-60 (mount) vs $250-400 at a shop',
      whyMatters: 'A failed transmission mount changes driveshaft angles enough to wear U-joints prematurely and cause vibration that can mimic a much more expensive problem.',
      safetyNote: 'The transmission must be supported throughout the procedure—never let it hang on the shifter linkage or driveshaft.',
    },
    tools: [
      'Socket wrench set (13mm, 15mm, 18mm)',
      'Torque wrench',
      'Floor jack',
      'Jack stands (two minimum)',
      'Transmission jack or floor jack with wood block',
      'Penetrating oil',
      'Pry bar',
      'Safety glasses',
    ],
    parts: [
      {
        id: 'transmission-mount-tj',
        name: 'Transmission Mount',
        partNumber: 'OEM Mopar or Anchor 2867 equivalent',
        quantity: 1,
        price: 35,
        category: 'required',
        notes: 'TJ uses different mounts for automatic and manual transmissions—verify before ordering.',
      },
      {
        id: 'transmission-mount-bolts',
        name: 'Mount Bolts (if seized)',
        partNumber: 'Mopar OEM hardware',
        quantity: 4,
        price: 10,
        category: 'optional',
        notes: 'Replace any bolt that shears or rounds during removal.',
      },
    ],
    safetyInformation:
      'The transmission and engine must be supported throughout the procedure. Never crawl under a vehicle supported only by a floor jack—use jack stands. The transmission jack or block of wood must remain firmly under the transmission pan when mount bolts are loose, since the entire weight of the transmission drops onto the mount once it is unbolted.',
    beforeYouStart: [
      'Apply penetrating oil to all mount bolts 24 hours before work',
      'Park on level concrete and chock the front wheels',
      'Verify the new mount matches the transmission type—automatic or manual',
      'Have a transmission jack or sturdy floor jack with a wood block ready',
      'Inspect the driveshaft and U-joints while underneath',
    ],
    steps: [
      {
        number: 1,
        title: 'Raise the Vehicle and Support on Jack Stands',
        description:
          'Lift the rear of the vehicle with a floor jack at the differential or rear crossmember. Place jack stands under the frame rails and lower the vehicle onto them. Verify the stands are stable.',
        why: 'Working under the transmission requires substantial clearance and a stable platform.',
        tip: 'Lift the front as well if extra clearance is needed for the transmission jack.',
        commonMistake: 'Working with the vehicle supported only by a floor jack.',
      },
      {
        number: 2,
        title: 'Locate the Transmission Mount and Crossmember',
        description:
          'Identify the transmission crossmember—a horizontal frame piece that runs side-to-side underneath the transmission. The mount sits between the transmission tail housing and the top of the crossmember. There are typically two bolts attaching the mount to the transmission and two bolts attaching the crossmember to the frame.',
        why: 'Understanding the geometry before disassembly speeds the work and prevents errors.',
        tip: 'Photograph the mount installation from multiple angles before removing anything.',
        commonMistake: 'Removing the wrong fasteners and dropping the wrong component.',
      },
      {
        number: 3,
        title: 'Position the Transmission Jack Under the Pan',
        description:
          'Position a transmission jack or a floor jack with a 6x6 inch wood block underneath the transmission oil pan. Raise the jack until it lightly contacts the pan. Apply only minimal upward pressure—just enough to support the transmission weight.',
        why: 'Once the mount bolts come out, the transmission weight transfers to the jack.',
        tip: 'Center the wood block on the pan to distribute load evenly.',
        commonMistake: 'Using a jack saddle without a block and denting the thin oil pan.',
      },
      {
        number: 4,
        title: 'Remove the Mount-to-Transmission Bolts',
        description:
          'Locate the two bolts attaching the mount to the transmission tail housing. Using a socket wrench, break each bolt loose and remove. Keep them organized—they may be different lengths.',
        why: 'These bolts must come out so the mount can drop with the crossmember.',
        tip: 'A long extension is usually needed to reach these bolts.',
        commonMistake: 'Losing bolts into the chassis frame channels.',
      },
      {
        number: 5,
        title: 'Remove the Crossmember-to-Frame Bolts',
        description:
          'Locate the bolts attaching the crossmember to the frame rails (typically two per side, four total). Remove them. The crossmember will now be loose. Be ready—it will drop slightly as the transmission weight transfers to the jack.',
        why: 'Removing these bolts frees the entire crossmember-and-mount assembly.',
        tip: 'Have a helper steady the crossmember as the last bolt comes out.',
        commonMistake: 'Allowing the crossmember to fall and damaging the exhaust or shifter linkage.',
      },
      {
        number: 6,
        title: 'Lower the Crossmember and Remove the Mount',
        description:
          'Slowly lower the transmission jack about 1-2 inches to create clearance. Slide the crossmember out from under the vehicle. With the crossmember on the ground, unbolt the mount from the crossmember. Set the old mount aside.',
        why: 'It is much easier to swap the mount on a workbench or the ground than fight it overhead.',
        tip: 'Note the orientation of the mount on the crossmember before removing it.',
        commonMistake: 'Lowering the transmission too far and stretching the shifter cable or kickdown linkage.',
      },
      {
        number: 7,
        title: 'Install the New Mount on the Crossmember',
        description:
          'Position the new mount on the crossmember in the same orientation as the old mount. Hand-thread the bolts and tighten to 41 Nm (30 ft-lbs) per OEM specification.',
        why: 'Pre-assembling the mount on the crossmember on the bench speeds final installation.',
        tip: 'Apply a small amount of anti-seize to the bolt threads.',
        commonMistake: 'Installing the mount upside down, which prevents it from aligning with the transmission.',
      },
      {
        number: 8,
        title: 'Lift the Crossmember Back into Position',
        description:
          'Slide the crossmember-and-mount assembly back under the vehicle. Position it on the transmission jack and slowly raise it until the mount studs align with the transmission tail housing holes and the crossmember bolt holes align with the frame rails.',
        why: 'Alignment must be precise—forcing fasteners into misaligned holes will cross-thread.',
        tip: 'A flashlight angled up through the crossmember holes confirms alignment.',
        commonMistake: 'Cross-threading bolts because alignment was not verified.',
      },
      {
        number: 9,
        title: 'Reinstall and Torque All Fasteners',
        description:
          'Hand-thread all four crossmember-to-frame bolts and both mount-to-transmission bolts. Once all are started, tighten the crossmember-to-frame bolts to 95 Nm (70 ft-lbs) and the mount-to-transmission bolts to 54 Nm (40 ft-lbs) per OEM specifications.',
        why: 'Proper torque ensures the crossmember and mount carry load without loosening.',
        tip: 'Tighten the crossmember-to-frame bolts before the mount-to-transmission bolts so the crossmember is locked into the frame first.',
        commonMistake: 'Skipping the torque wrench and over-torquing aluminum transmission threads.',
      },
      {
        number: 10,
        title: 'Lower the Vehicle and Test Drive',
        description:
          'Lower the transmission jack and remove it. Lower the vehicle from the jack stands. Start the engine, shift through all gears with the parking brake on, and listen for vibration or unusual noises. Test drive at low speed to confirm smooth operation.',
        why: 'A test drive confirms the mount is properly installed and torqued.',
        tip: 'Pay attention to vibration at idle in DRIVE—a good mount eliminates idle vibration through the floor.',
        commonMistake: 'Skipping the test drive and missing a loose bolt that vibrates out.',
      },
    ],
    torqueSpecifications: [
      {
        fastener: 'Mount-to-transmission bolts',
        specification: '54 Nm (40 ft-lbs)',
        notes: 'OEM specification.',
      },
      {
        fastener: 'Mount-to-crossmember bolts',
        specification: '41 Nm (30 ft-lbs)',
        notes: 'OEM specification.',
      },
      {
        fastener: 'Crossmember-to-frame bolts',
        specification: '95 Nm (70 ft-lbs)',
        notes: 'OEM specification. Tighten these first when reinstalling.',
      },
    ],
    fluidSpecifications: [],
    troubleshooting: [
      {
        problem: 'Vibration persists after replacement',
        likelyCause: 'Engine mounts also worn',
        whatToCheck: 'Inspect engine mounts for cracks or separation',
        fix: 'Replace engine mounts if also worn—mounts often fail together.',
      },
      {
        problem: 'Clunk on shifting between drive and reverse',
        likelyCause: 'Mount bolt loose or driveshaft U-joint worn',
        whatToCheck: 'Re-torque all mount bolts; inspect U-joints',
        fix: 'Tighten loose bolts; replace U-joints if worn.',
      },
      {
        problem: 'Exhaust hits floor pan after replacement',
        likelyCause: 'New mount installed at wrong height or orientation',
        whatToCheck: 'Compare driveshaft angle to original',
        fix: 'Verify mount orientation; some mounts have a "front" mark.',
      },
    ],
    proTips: [
      'Replace transmission and engine mounts together—they typically wear at similar rates.',
      'Polyurethane mounts last longer than rubber but transmit more vibration into the cabin.',
      'Inspect the driveshaft U-joints while you are under the vehicle—they often need attention at the same mileage.',
      'Mark the orientation of the old mount with paint pen before removal for reference.',
    ],
    successIndicators: [
      'No vibration at idle in DRIVE',
      'No clunk when shifting between gears',
      'Exhaust system clears the floor pan',
      'Smooth power delivery at all RPM',
    ],
    whenToSeekHelp: 'If the transmission cannot be lifted enough to align the crossmember, the engine mounts may be sagging—professional drivetrain service may be needed.',
    legalDisclaimer: 'IMPORTANT SAFETY NOTICE: The transmission must be supported throughout the procedure. Never crawl under a vehicle supported only by a jack. If you are uncertain about any procedure, consult a qualified professional.',
  },

  {
    id: 'tj-engine-mount-replacement',
    title: 'Engine Mount Replacement',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [
        { startYear: 2003, endYear: 2006 }
      ],
    },
    video: {
      youtubeId: 'qyi0snEP0oM',
      title: 'How to Install Motor Mounts on a Jeep Wrangler TJ 4.0L | Full DIY Tutorial',
      channel: 'American Ronin Garage',
    },
    source: 'Original guide created through independent research of engine mounting systems, technician forums, and OEM service documentation. Verified through the Automotive Repair Guide Generation System v9 methodology.',
    category: 'Engine & Drivetrain',
    difficulty: 'intermediate',
    duration: '2-3 hours',
    interval: 'Every 100,000-150,000 miles or when engine vibration or shifting is felt',
    description:
      'Engine mounts isolate engine vibration from the chassis and locate the engine in its bay. The TJ uses two mounts—one on each side of the engine—plus the rear transmission mount. Worn engine mounts cause vibration through the steering wheel, clunking on acceleration, and engine shifting that can stress exhaust connections and accessory drives.',
    overview: {
      difficulty: 'Intermediate—requires lifting the engine while removing mounts one at a time',
      estimatedTime: '2 to 3 hours',
      estimatedCost: '$50-100 per mount vs $400-700 at a shop',
      whyMatters: 'Worn engine mounts allow the engine to shift under load, breaking exhaust seals, cracking accessory mounts, and putting stress on the radiator and cooling lines.',
      safetyNote: 'The engine must be supported throughout the procedure—never let it rest on its mounts when bolts are out.',
    },
    tools: [
      'Socket wrench set (13mm, 15mm, 18mm, 21mm)',
      'Breaker bar',
      'Torque wrench',
      'Floor jack with wood block (or engine support bar)',
      'Jack stands (two minimum)',
      'Penetrating oil',
      'Pry bar',
      'Safety glasses',
    ],
    parts: [
      {
        id: 'engine-mount-tj',
        name: 'Engine Mount (per side)',
        partNumber: 'OEM Mopar or Anchor 2920 equivalent',
        quantity: 2,
        price: 55,
        category: 'required',
        notes: 'TJ uses different mounts for 4.0L I6 and 2.4L I4—verify before ordering. Replace both sides simultaneously for balance.',
      },
      {
        id: 'engine-mount-bolts',
        name: 'Mount Bolts (if seized)',
        partNumber: 'Mopar OEM hardware',
        quantity: 4,
        price: 12,
        category: 'optional',
        notes: 'Always have replacements—factory bolts often seize on aluminum mount studs.',
      },
    ],
    safetyInformation:
      'The engine must be supported by a floor jack with wood block under the oil pan throughout the procedure. Never crawl under a vehicle supported only by a jack—use jack stands at the frame. Apply only enough lifting pressure to support the engine—lifting too much can stress the exhaust manifold or transmission output.',
    beforeYouStart: [
      'Apply penetrating oil to all mount bolts 24 hours before work',
      'Park on level concrete and chock the wheels',
      'Verify mounts match the engine—4.0L I6 or 2.4L I4',
      'Plan to replace both mounts in one session—imbalance from one new and one old mount creates vibration',
      'Have a sturdy 6x6 wood block ready for the floor jack',
    ],
    steps: [
      {
        number: 1,
        title: 'Raise the Vehicle and Support on Jack Stands',
        description:
          'Lift the front of the vehicle with the floor jack at the front crossmember. Place jack stands under the frame rails. Lower the vehicle onto the stands and verify stability.',
        why: 'Engine mount work requires extensive under-vehicle access.',
        tip: 'Raise the vehicle as high as practical—you need room to swing wrenches around the mounts.',
        commonMistake: 'Working with marginal clearance and dropping tools repeatedly.',
      },
      {
        number: 2,
        title: 'Position the Floor Jack Under the Engine',
        description:
          'Place a sturdy 6x6 wood block on the floor jack saddle. Position the jack under the engine oil pan—as far back as practical to balance the engine weight. Raise the jack until it lightly contacts the pan but does not lift the engine.',
        why: 'The wood block distributes load over a wider area of the thin oil pan.',
        tip: 'A piece of carpet or thick cardboard between the block and pan further protects the pan.',
        commonMistake: 'Placing the jack saddle directly on the pan and denting it.',
      },
      {
        number: 3,
        title: 'Identify Both Engine Mounts',
        description:
          'The engine mounts sit between the lower engine block and the frame. The driver-side mount is typically near the front of the block; the passenger-side mount is similarly positioned. Each mount is held by two bolts—one going down through the mount into the frame bracket, and one going horizontally through the engine block bracket.',
        why: 'Confirming geometry first prevents removing the wrong fasteners.',
        tip: 'Take photos of each mount before disassembly for reference.',
        commonMistake: 'Removing the through-bolt and the bracket bolt at the same time, dropping the bracket.',
      },
      {
        number: 4,
        title: 'Lift the Engine Slightly',
        description:
          'Raise the floor jack until you see the engine lift approximately 1/4 inch. Watch the radiator hoses, exhaust connections, and any wiring—nothing should stretch or pull. Stop lifting if anything binds.',
        why: 'Slight lift relieves load on the mounts and makes bolts easier to remove.',
        tip: 'Sometimes the engine resists lifting at first as the worn mount rebounds—just hold steady pressure.',
        commonMistake: 'Lifting too much and breaking the exhaust connection at the manifold.',
      },
      {
        number: 5,
        title: 'Remove the First Mount (Driver Side)',
        description:
          'Locate the through-bolt that runs through the mount and frame bracket. Using the socket wrench, break it loose and remove it. Then remove the bolts attaching the mount to the engine block bracket. The mount will now drop down and out of position.',
        why: 'Removing one mount at a time keeps the engine partially supported by the other.',
        tip: 'Apply more penetrating oil if a bolt resists.',
        commonMistake: 'Removing both mounts at the same time and shifting the engine.',
      },
      {
        number: 6,
        title: 'Install the New Driver-Side Mount',
        description:
          'Position the new mount in the same orientation as the old. Hand-thread the engine block bracket bolts first, then the through-bolt to the frame bracket. All bolts should be loose at this stage. Tighten the engine bracket bolts to 41 Nm (30 ft-lbs) and the through-bolt to 75 Nm (55 ft-lbs).',
        why: 'Hand-threading first ensures alignment; final torque secures the mount.',
        tip: 'Apply anti-seize to the bolt threads for easier future removal.',
        commonMistake: 'Tightening one bolt fully before starting the others, which can prevent alignment.',
      },
      {
        number: 7,
        title: 'Move the Floor Jack and Repeat for Passenger Side',
        description:
          'Lower the floor jack just enough to relieve pressure on the new mount. Move the jack to the passenger side of the engine pan. Repeat the lift, mount removal, and replacement procedure on this side.',
        why: 'The engine must remain supported on at least one mount or the jack at all times.',
        tip: 'Reposition the wood block carefully—do not slide it across the pan.',
        commonMistake: 'Lowering the engine fully onto the new mount and stressing it before tightening.',
      },
      {
        number: 8,
        title: 'Verify Both Mounts Are Torqued',
        description:
          'Once both new mounts are installed and partially torqued, lower the floor jack until the engine sits on both mounts. Re-verify torque on all four bolts (two per mount) since final settling can change torque. Re-torque to specification if any bolt has moved.',
        why: 'Final torque must be applied with the engine resting on the mounts in its natural position.',
        tip: 'A torque-angle approach (torque, lower, re-torque) gives the most consistent result.',
        commonMistake: 'Trusting initial torque without re-checking after the engine settles.',
      },
      {
        number: 9,
        title: 'Inspect All Other Components',
        description:
          'Walk around the engine bay and inspect the radiator hoses, exhaust connections, throttle linkage, and any wiring for tension or damage from the engine lift. Confirm the cooling fan still has clearance to the shroud.',
        why: 'Lifting the engine can shift surrounding components into binds.',
        tip: 'A tight throttle cable after engine work is a sign that the mounts are not fully seated.',
        commonMistake: 'Skipping this inspection and discovering damage on the test drive.',
      },
      {
        number: 10,
        title: 'Lower Vehicle and Test Drive',
        description:
          'Remove the floor jack. Lower the vehicle from the jack stands. Start the engine and check idle quality. Listen for vibration at idle and during low-speed acceleration. Test drive at moderate speeds to confirm smooth operation. Re-torque the mount bolts after 100 miles—mounts compress slightly during break-in.',
        why: 'A test drive verifies installation; re-torque prevents long-term loosening.',
        tip: 'New mounts feel slightly stiffer than old worn ones for the first 500 miles.',
        commonMistake: 'Skipping the 100-mile re-torque and finding mount bolts loose months later.',
      },
    ],
    torqueSpecifications: [
      {
        fastener: 'Mount-to-engine bracket bolts',
        specification: '41 Nm (30 ft-lbs)',
        notes: 'OEM specification.',
      },
      {
        fastener: 'Mount-to-frame through-bolt',
        specification: '75 Nm (55 ft-lbs)',
        notes: 'OEM specification.',
      },
    ],
    fluidSpecifications: [],
    troubleshooting: [
      {
        problem: 'Vibration persists after replacement',
        likelyCause: 'Transmission mount also worn',
        whatToCheck: 'Inspect transmission mount for separation',
        fix: 'Replace transmission mount—mounts typically wear together.',
      },
      {
        problem: 'Engine clunks on acceleration',
        likelyCause: 'Mount bolt loose or mount installed at wrong angle',
        whatToCheck: 'Re-torque all mount bolts; verify orientation',
        fix: 'Tighten loose bolts; reposition mount if angled wrong.',
      },
      {
        problem: 'Exhaust leak after engine work',
        likelyCause: 'Manifold connection stressed during lifting',
        whatToCheck: 'Inspect exhaust manifold gasket and pipe connection',
        fix: 'Replace gasket if leaking; check for cracks in manifold.',
      },
    ],
    proTips: [
      'Replace both engine mounts together to avoid imbalance and a quick second job.',
      'Polyurethane mounts last longer but transmit more vibration—choose based on use case.',
      'Re-torque mount bolts after 100 miles of driving—new rubber settles.',
      'Inspect the transmission mount while you are underneath—it often needs attention at the same time.',
    ],
    successIndicators: [
      'Smooth idle with no vibration through steering wheel',
      'No clunking on acceleration or deceleration',
      'No engine shifting felt when shifting gears',
      'Exhaust system holds tight at all connections',
    ],
    whenToSeekHelp: 'If the engine cannot be lifted enough to remove a mount, or if a mount bolt shears in the engine block bracket, professional service may be required.',
    legalDisclaimer: 'IMPORTANT SAFETY NOTICE: The engine must be supported at all times during mount replacement. Never crawl under a vehicle supported only by a jack. If you are uncertain about any procedure, consult a qualified professional.',
  },

  {
    id: 'tj-cv-joint-inspection',
    title: 'CV Joint Inspection and Maintenance',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [
        { startYear: 2003, endYear: 2006 }
      ],
    },
    video: {
      youtubeId: 'h0rPsxy91ZU',
      title: 'How To: Jeep Wrangler \'97-\'06 TJ Hub Replacement (CV/axle reference)',
      channel: 'Quadratec',
    },
    source: 'Original guide created through independent research of axle joint maintenance, technician forums, and OEM service documentation. Verified through the Automotive Repair Guide Generation System v9 methodology.',
    category: 'Engine & Drivetrain',
    difficulty: 'beginner',
    duration: '30-45 min',
    interval: 'Every 12,000 miles or annually for off-road vehicles',
    description:
      'The TJ Wrangler front axle uses CV (constant velocity) style U-joints at each wheel to allow the front wheels to steer while transmitting power. Boots protect the joint grease from contamination. A torn boot allows water and dirt to enter, destroying the joint within thousands of miles. Routine inspection catches torn boots before joint damage occurs.',
    overview: {
      difficulty: 'Beginner—visual inspection task with no mechanical disassembly required',
      estimatedTime: '30 to 45 minutes for a thorough inspection',
      estimatedCost: '$0 (inspection only) or $20 (boot replacement kit if needed)',
      whyMatters: 'Catching a torn boot during inspection lets you replace just the boot for $20. Missing it leads to joint replacement for $300-600 plus labor.',
      safetyNote: 'The vehicle must be safely raised on jack stands. Wheels will be removed for inspection.',
    },
    tools: [
      'Lug wrench',
      'Floor jack',
      'Jack stands (two minimum)',
      'Flashlight or headlamp',
      'Shop towels',
      'Inspection mirror (optional but helpful)',
      'Safety glasses',
      'Work gloves',
    ],
    parts: [
      {
        id: 'cv-boot-kit',
        name: 'CV Boot Replacement Kit (if torn)',
        partNumber: 'Mopar OEM or Crown Automotive 5012457AB',
        quantity: 1,
        price: 25,
        category: 'optional',
        notes: 'Only needed if a torn boot is found. Includes new boot, clamps, and grease.',
      },
    ],
    safetyInformation:
      'The vehicle must be safely supported on jack stands during inspection. Never crawl under a vehicle supported only by a floor jack. The CV joint contains heavy black grease that stains skin and clothing—wear gloves. Inspect with the engine off and the parking brake set.',
    beforeYouStart: [
      'Park on level ground and chock the rear wheels',
      'Set the parking brake',
      'Have a flashlight and clean shop towels ready',
      'Wear nitrile gloves to keep grease off your hands',
      'Plan to inspect both front wheels—both joints share the same wear pattern',
    ],
    steps: [
      {
        number: 1,
        title: 'Listen for CV Joint Symptoms During Test Drive',
        description:
          'Before raising the vehicle, drive in a tight circle at low speed in a parking lot. A failing CV joint produces a clicking or popping noise during turns under load. Drive in both directions—the noise on left turns indicates the right joint, and vice versa.',
        why: 'Audible symptoms guide your inspection priority.',
        tip: 'Drive with the windows down and stereo off to hear the joints clearly.',
        commonMistake: 'Skipping the test drive and missing a worn joint that only clicks under load.',
      },
      {
        number: 2,
        title: 'Raise and Support the Front of the Vehicle',
        description:
          'Loosen all front lug nuts before lifting. Raise the front of the vehicle with a floor jack at the front crossmember. Place jack stands under the frame rails on both sides. Lower the vehicle onto the stands.',
        why: 'Both front wheels off the ground allows full inspection of both joints.',
        tip: 'Lifting both sides simultaneously lets you inspect both joints in one session.',
        commonMistake: 'Lifting only one side and missing wear on the opposite joint.',
      },
      {
        number: 3,
        title: 'Remove Both Front Wheels',
        description:
          'Remove the lug nuts and pull both front wheels off. Set them aside in a safe location away from the work area where they cannot roll.',
        why: 'Wheel removal exposes the CV joint and boot for direct inspection.',
        tip: 'Place the wheels under the frame as additional safety in case a stand fails.',
        commonMistake: 'Leaving wheels in the work area where they trip you while moving around.',
      },
      {
        number: 4,
        title: 'Locate the CV Boots',
        description:
          'Look at the inboard side of each wheel hub. The CV boot is a black rubber accordion-shaped cover that protects the joint between the axle shaft and the wheel hub. Each side has two boots—one outer (at the hub) and one inner (closer to the differential).',
        why: 'Both inner and outer boots can fail, and inspection must cover both.',
        tip: 'Use a flashlight—the boots are often hidden behind brake components.',
        commonMistake: 'Inspecting only the outer boot and missing a torn inner boot.',
      },
      {
        number: 5,
        title: 'Inspect Boots for Cracks and Tears',
        description:
          'Examine each boot carefully for cracks, splits, or visible tears. Pay special attention to the accordion folds—this is where boots typically tear first. Look for any grease on the outside of the boot, the axle, or the chassis underneath. Even a small grease smear indicates a leaking boot.',
        why: 'A torn boot lets contamination into the joint, leading to rapid wear.',
        tip: 'Rotate the wheel hub by hand while inspecting—the rotation reveals tears that hide in folds.',
        commonMistake: 'Glancing at the boot without checking the back side.',
      },
      {
        number: 6,
        title: 'Check Boot Clamps for Security',
        description:
          'The boot is held in place by metal clamps at both ends—one at the small end (toward the axle shaft) and one at the large end (toward the joint housing). Verify both clamps are present and tight. Loose clamps allow the boot to slip and grease to escape.',
        why: 'Clamp failure causes boot displacement even if the boot itself is intact.',
        tip: 'Tap the clamp with a screwdriver handle—a tight clamp produces a sharp metallic ring.',
        commonMistake: 'Assuming the boot is healthy without checking clamp security.',
      },
      {
        number: 7,
        title: 'Test Joint for Play',
        description:
          'Grasp the axle shaft near the joint and try to wiggle it back and forth and up and down. A healthy joint has minimal play—any noticeable movement or clicking indicates internal wear. Compare both sides—asymmetric play usually means one joint is worn.',
        why: 'Internal joint wear can occur even with intact boots from age or off-road use.',
        tip: 'Have a helper rotate the wheel slowly while you test for play—this reveals wear hidden in one rotation position.',
        commonMistake: 'Misreading normal joint articulation as wear—small movement is normal.',
      },
      {
        number: 8,
        title: 'Rotate Each Wheel and Listen for Roughness',
        description:
          'With one hand on the brake disc, rotate the hub through several full turns. The rotation should be smooth with no clicking, grinding, or roughness. Any audible click or felt roughness indicates joint damage requiring replacement.',
        why: 'A worn joint produces detectable roughness even when the boot is intact.',
        tip: 'A stethoscope held against the joint amplifies subtle roughness.',
        commonMistake: 'Missing roughness because of background workshop noise.',
      },
      {
        number: 9,
        title: 'Document Findings',
        description:
          'On a notepad, record the condition of each boot (intact or torn), each clamp (tight or loose), each joint (smooth or rough), and any audible symptoms from the test drive. Photograph any visible damage. Use the documentation to plan repairs and track joint condition over time.',
        why: 'A written record helps identify trends and plan maintenance.',
        tip: 'Take photos of each boot from multiple angles—future comparison reveals deterioration over time.',
        commonMistake: 'Doing inspection but not recording findings—repeat inspections are guesswork without baseline data.',
      },
      {
        number: 10,
        title: 'Reinstall Wheels and Lower the Vehicle',
        description:
          'Reinstall both front wheels and tighten the lug nuts in a star pattern by hand. Lower the vehicle from the jack stands. Once the wheels are on the ground, torque the lug nuts to 122 Nm (90 ft-lbs) per OEM specification. Plan any required repairs based on findings—boot replacement, joint replacement, or scheduled monitoring.',
        why: 'Final torque secures the wheels for safe driving.',
        tip: 'A second pass on lug nuts after 100 miles confirms the seating.',
        commonMistake: 'Torquing lug nuts in the air—seating is incorrect without ground load on the wheel.',
      },
    ],
    torqueSpecifications: [
      {
        fastener: 'Lug nuts',
        specification: '122 Nm (90 ft-lbs)',
        notes: 'OEM specification. Tighten in star pattern.',
      },
    ],
    fluidSpecifications: [],
    troubleshooting: [
      {
        problem: 'Clicking sound during turns',
        likelyCause: 'Outer CV joint worn from contaminated grease',
        whatToCheck: 'Inspect outer boot for tears; test joint for play',
        fix: 'Replace joint—boot replacement alone will not fix a worn joint.',
      },
      {
        problem: 'Vibration at highway speed',
        likelyCause: 'Inner CV joint worn or unbalanced',
        whatToCheck: 'Inspect inner boot; test joint for axial play',
        fix: 'Replace joint if play is detected; have axle shaft balanced if play is minimal.',
      },
      {
        problem: 'Grease splatter on inner fender',
        likelyCause: 'Torn boot at speed slinging grease',
        whatToCheck: 'Inspect both boots on the suspect side',
        fix: 'Replace boot if joint is still smooth; replace joint if rough.',
      },
      {
        problem: 'Burning smell from front wheels',
        likelyCause: 'Severely worn joint generating heat',
        whatToCheck: 'Feel the joint housing carefully—it should not be hot',
        fix: 'Replace joint immediately—failure is imminent.',
      },
    ],
    proTips: [
      'Boot replacement at first sign of tear costs about $50 total—joint replacement after contamination costs $400 or more.',
      'Off-road TJs benefit from inspection every 6 months due to heavy mud and water exposure.',
      'A small zip tie can serve as an emergency boot repair to limp home—not a permanent solution.',
      'Greasing CV joints is unusual—they are sealed for life unless the boot has failed.',
    ],
    successIndicators: [
      'Both inner and outer boots intact with no cracks or tears',
      'All four boot clamps tight and secure',
      'No grease leakage on axle shaft or chassis',
      'Smooth wheel rotation with no clicking or roughness',
      'No clicking sounds during low-speed turns',
    ],
    whenToSeekHelp: 'If joint play is detected or clicking is heard during turns, joint replacement is needed and benefits from professional service due to specialized axle removal procedures.',
    legalDisclaimer: 'IMPORTANT SAFETY NOTICE: A failed CV joint can separate at speed, causing loss of vehicle control. Inspect joints regularly and address any boot tear immediately. Always work on a safely supported vehicle. If you are uncertain about any procedure, consult a qualified professional.',
  },

  // ═══════════════════════════════════════════════════════════════════════════════
  // WHEEL BEARING & AXLE SEAL GUIDES
  // ═══════════════════════════════════════════════════════════════════════════════

  {
    id: 'tj-front-hub-assembly-replacement',
    title: 'Front Hub Assembly (Unit Bearing) Replacement',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [
        { startYear: 2003, endYear: 2006 }
      ],
    },
    video: {
      youtubeId: 'h0rPsxy91ZU',
      title: 'How To: Jeep Wrangler \'97-\'06 TJ Hub Replacement',
      channel: 'Quadratec',
    },
    source: 'Original guide created through independent research of Dana 30 front axle wheel bearings, technician forums, and OEM service documentation. Verified through the Automotive Repair Guide Generation System v9 methodology.',
    category: 'Suspension & Steering',
    difficulty: 'intermediate',
    duration: '2-3 hours per side',
    interval: 'Every 80,000-150,000 miles or when bearing roar develops',
    description:
      'The TJ front axle uses a sealed unit hub assembly that combines the wheel bearing, hub, and ABS tone ring in a single bolted-on cartridge. When the bearing fails, you hear a roaring or growling noise from one side of the vehicle that changes pitch with speed and may quiet when turning. Replacement is a relatively straightforward intermediate job — no press is required because the bearing is integrated into the hub.',
    overview: {
      difficulty: 'Intermediate — requires high-torque axle nut removal and steering knuckle access',
      estimatedTime: '2 to 3 hours per side',
      estimatedCost: '$80-180 per hub vs $400-700 per side at a shop',
      whyMatters: 'A failing wheel bearing can seize at speed, causing loss of steering control. ABS function also depends on the integrated tone ring — a degraded hub can disable ABS even before the bearing fails completely.',
      safetyNote: 'The axle nut on TJ front hubs is torqued to over 175 ft-lbs from the factory. A properly sized impact wrench or long breaker bar with cheater pipe is required. Never break the axle nut loose with the wheel off the ground — the wheel must carry the torque reaction.',
    },
    tools: [
      'Socket wrench set (13mm, 15mm, 18mm, 21mm)',
      '36mm socket or 12-point socket for axle nut (verify size)',
      'Long breaker bar (24 inches minimum) or 1/2" impact wrench',
      'Torque wrench rated to 250 Nm (185 ft-lbs)',
      'Hub puller or 3-jaw puller (in case hub is rust-stuck)',
      'Pry bar',
      'Penetrating oil (PB Blaster or equivalent)',
      'Anti-seize compound',
      'Jack and jack stands',
      'Brake caliper hanger or wire',
    ],
    parts: [
      {
        id: 'front-hub-assembly-tj',
        name: 'Front Hub Assembly (Unit Bearing with ABS)',
        partNumber: 'Mopar 5012458AC, Timken HA590125, or Moog 515060 equivalent',
        quantity: 1,
        price: 95,
        category: 'required',
        notes: 'Verify part includes ABS tone ring on the inboard side. Replace one side at a time — do not disturb the good side.',
      },
      {
        id: 'axle-nut-tj',
        name: 'Axle Nut (one-time use)',
        partNumber: 'Mopar 06035710AA',
        quantity: 1,
        price: 8,
        category: 'required',
        notes: 'Always replace — these are torque-to-yield fasteners and lose clamping force when reused.',
      },
      {
        id: 'hub-bolts',
        name: 'Hub Mounting Bolts (if seized)',
        partNumber: 'Mopar OEM hardware',
        quantity: 3,
        price: 12,
        category: 'optional',
        notes: 'Bolts often shear during removal — have replacements available.',
      },
    ],
    safetyInformation:
      'The axle nut carries the highest torque on the front axle and requires substantial leverage to break loose. Always break the nut with the wheel on the ground — never with the vehicle on jack stands. Once the vehicle is raised, support both sides on jack stands and never crawl under a vehicle supported only by a jack. The brake caliper must be supported off the rotor to prevent damage to the brake hose. Wear safety glasses; rust and debris fall when the hub is removed.',
    beforeYouStart: [
      'Apply penetrating oil to the three hub mounting bolts and the axle nut 24 hours before work',
      'Confirm you have a torque wrench rated to at least 250 Nm (185 ft-lbs)',
      'Verify the new hub includes the ABS tone ring on the inboard face',
      'Loosen the lug nuts before lifting the vehicle',
      'Plan to replace the axle nut — do not reuse the old one',
    ],
    steps: [
      {
        number: 1,
        title: 'Break Axle Nut and Lug Nuts Loose with Wheel on Ground',
        description:
          'With the vehicle still on the ground, remove the dust cap from the center of the wheel hub if present. Place the breaker bar with the appropriate socket on the axle nut and break it loose. The nut should require substantial force — over 175 ft-lbs from the factory. Once broken loose, also crack the five lug nuts.',
        why: 'Breaking the axle nut while the wheel carries the load prevents the entire wheel hub from spinning under torque.',
        tip: 'A 6-foot cheater pipe over the breaker bar provides leverage if the nut resists.',
        commonMistake: 'Trying to break the axle nut after lifting the vehicle — the wheel spins freely and you cannot apply torque.',
      },
      {
        number: 2,
        title: 'Raise Vehicle and Remove Wheel',
        description:
          'Lift the front of the vehicle with a floor jack at the front crossmember. Place jack stands under the frame rails. Lower onto the stands. Remove the lug nuts and pull the wheel off. Set it under the frame as additional safety backup.',
        why: 'Both stands support the vehicle, and the wheel under the frame catches it if a stand slips.',
        tip: 'Place the wheel face-down so brake dust does not coat the rim.',
        commonMistake: 'Working with only one stand or with the floor jack still in place.',
      },
      {
        number: 3,
        title: 'Remove the Brake Caliper and Hang It Aside',
        description:
          'Locate the two caliper slide bolts on the inboard side of the caliper. Remove them. Lift the caliper off the rotor and hang it from the suspension with a wire hook or bungee cord. Do not let it hang on the brake hose — the hose can crack from the weight.',
        why: 'The caliper must be removed for rotor and hub access.',
        tip: 'A bent coat hanger makes a perfect caliper hanger.',
        commonMistake: 'Letting the caliper dangle on the brake hose and damaging the hose internally.',
      },
      {
        number: 4,
        title: 'Remove the Brake Rotor',
        description:
          'Pull the brake rotor straight off the wheel studs. If it is rust-stuck, tap around the hat (the center mounting area) with a soft-faced hammer until it releases. Set the rotor aside on a clean surface.',
        why: 'The rotor must come off to access the hub mounting bolts.',
        tip: 'A few drops of penetrating oil between the hub face and rotor hat help free a stuck rotor.',
        commonMistake: 'Hitting the rotor face directly and warping it.',
      },
      {
        number: 5,
        title: 'Remove the Axle Nut and Disconnect the ABS Sensor',
        description:
          'Now that the wheel is off and the rotor is out of the way, finish removing the axle nut and pull off the washer. Locate the ABS wheel speed sensor wire (typically on the back of the steering knuckle) and trace it back to its connector. Disconnect the connector. Free the wire from any retainer clips along the knuckle.',
        why: 'The axle nut releases the half-shaft from the hub. The ABS connector must be free to allow hub rotation during removal.',
        tip: 'Photograph the ABS wire routing before disconnecting — exact routing matters for reassembly.',
        commonMistake: 'Yanking the ABS connector instead of pressing the release tab and damaging it.',
      },
      {
        number: 6,
        title: 'Remove the Three Hub Mounting Bolts',
        description:
          'Locate the three hub mounting bolts on the inboard side of the steering knuckle (visible from behind the knuckle). Using your socket wrench, remove all three bolts. They are typically 13mm or 15mm and torqued to approximately 138 Nm (102 ft-lbs).',
        why: 'These bolts secure the hub to the knuckle and must come out to free the assembly.',
        tip: 'Apply more penetrating oil if a bolt resists. A long extension and swivel adapter improves access.',
        commonMistake: 'Stripping a bolt head by using a worn socket — factory bolts deserve a fresh 6-point socket.',
      },
      {
        number: 7,
        title: 'Remove the Old Hub Assembly',
        description:
          'With the three bolts out and the axle nut off, the hub should slide forward off the half-shaft splines. If rust has bonded the hub to the knuckle, attach a hub puller to the wheel studs and tighten the puller forcing screw against the half-shaft end. Apply steady pressure until the hub releases.',
        why: 'A stuck hub will not release without mechanical pulling — never hammer on the half-shaft end directly.',
        tip: 'A few sharp impacts on the puller forcing screw with a wrench often free a stuck hub.',
        commonMistake: 'Hammering the half-shaft end and mushrooming the threads, making axle nut reinstallation impossible.',
      },
      {
        number: 8,
        title: 'Clean the Knuckle Bore and Install New Hub',
        description:
          'Wire-brush the knuckle bore where the hub seated. Remove all rust and debris. Apply a thin film of anti-seize to the mating surface. Slide the new hub onto the half-shaft splines and align the three mounting bolt holes. Hand-thread the three bolts and snug them, then torque to 138 Nm (102 ft-lbs) per OEM specification.',
        why: 'A clean mating surface ensures the hub seats fully against the knuckle. Anti-seize prevents the next hub from rusting in place.',
        tip: 'Tighten the three bolts in a star pattern to keep the hub flange square to the knuckle.',
        commonMistake: 'Skipping the anti-seize and finding the new hub permanently bonded to the knuckle in five years.',
      },
      {
        number: 9,
        title: 'Reconnect ABS Sensor and Install New Axle Nut',
        description:
          'Reconnect the ABS sensor connector and route the wire through any retainer clips. Place the new axle nut washer on the half-shaft, then thread the new axle nut on by hand. Reinstall the brake rotor and caliper, torquing the caliper slide bolts to 31 Nm (23 ft-lbs).',
        why: 'The ABS connection must be made before the wheel is reinstalled to avoid forgetting it.',
        tip: 'A drop of dielectric grease in the ABS connector improves longevity.',
        commonMistake: 'Forgetting the ABS connector and getting an ABS warning light on the test drive.',
      },
      {
        number: 10,
        title: 'Mount Wheel and Final-Torque Axle Nut',
        description:
          'Mount the wheel and snug the lug nuts. Lower the vehicle to the ground. Once the wheel carries the load, torque the axle nut to 237 Nm (175 ft-lbs) per OEM specification. Then torque the lug nuts to 122 Nm (90 ft-lbs) in a star pattern. Test drive at low speed and listen for any noise or feel for any vibration.',
        why: 'Final torque must be applied with the wheel on the ground so the wheel cannot spin free.',
        tip: 'Some hub designs require a specific torque-and-loosen-and-retorque sequence — verify against the part instructions.',
        commonMistake: 'Final-torquing the axle nut on jack stands and failing to reach proper preload because the wheel turns.',
      },
    ],
    torqueSpecifications: [
      {
        fastener: 'Hub-to-knuckle mounting bolts (3)',
        specification: '138 Nm (102 ft-lbs)',
        notes: 'OEM specification. Tighten in star pattern.',
      },
      {
        fastener: 'Axle nut (one-time use)',
        specification: '237 Nm (175 ft-lbs)',
        notes: 'OEM specification. Torque with wheel on ground. Always replace nut.',
      },
      {
        fastener: 'Brake caliper slide bolts',
        specification: '31 Nm (23 ft-lbs)',
        notes: 'OEM specification.',
      },
      {
        fastener: 'Lug nuts',
        specification: '122 Nm (90 ft-lbs)',
        notes: 'OEM specification. Tighten in star pattern.',
      },
    ],
    fluidSpecifications: [],
    troubleshooting: [
      {
        problem: 'Roaring noise persists after replacement',
        likelyCause: 'Opposite side bearing also failing, or new hub defective',
        whatToCheck: 'Drive in tight circles both directions and listen for which side noise comes from',
        fix: 'Replace opposite side hub if it is the source; exchange new hub if it is the source.',
      },
      {
        problem: 'ABS warning light after replacement',
        likelyCause: 'ABS connector not seated, sensor wire pinched, or tone ring damaged',
        whatToCheck: 'Inspect connector seating, wire routing, and tone ring integrity on the new hub',
        fix: 'Reseat connector or reroute wire; replace hub if tone ring is damaged.',
      },
      {
        problem: 'Vibration at highway speed after replacement',
        likelyCause: 'Hub not fully seated against knuckle, or wheel imbalance',
        whatToCheck: 'Re-torque mounting bolts; have the wheel re-balanced',
        fix: 'Reseat hub if it is loose; balance wheel if hub is tight.',
      },
      {
        problem: 'Axle nut would not torque to spec without spinning hub',
        likelyCause: 'Final torque was applied with vehicle on jack stands',
        whatToCheck: 'Lower vehicle to ground and re-torque',
        fix: 'Always final-torque with wheel on ground.',
      },
    ],
    proTips: [
      'Replace hubs in pairs only if both sides have similar mileage — otherwise replace one side at a time as they fail.',
      'A bearing roar that quiets when turning right means the left bearing is failing, and vice versa.',
      'Anti-seize on the hub-to-knuckle mating surface saves the next mechanic hours of pulling time.',
      'Buy a quality brand (Timken, SKF, or OEM Mopar) — cheap aftermarket hubs often fail within 20,000 miles.',
    ],
    successIndicators: [
      'No roaring or growling noise from the affected wheel',
      'No ABS warning light on dashboard',
      'No vibration at highway speed',
      'Wheel rotates smoothly with no detectable play when grasped at 12 and 6 o\'clock',
    ],
    whenToSeekHelp: 'If the hub mounting bolts shear in the knuckle or the half-shaft splines are damaged, professional service may be required to extract broken fasteners or replace the half-shaft.',
    legalDisclaimer: 'IMPORTANT SAFETY NOTICE: A failing wheel bearing can seize at highway speed and cause loss of vehicle control. Always replace hubs at the first sign of bearing roar. Never reuse the axle nut. If you are uncertain about any procedure, consult a qualified professional.',
  },

  {
    id: 'tj-rear-axle-bearing-seal',
    title: 'Rear Axle Bearing and Seal Replacement',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [
        { startYear: 2003, endYear: 2006 }
      ],
    },
    video: {
      youtubeId: 'w3QFjWn-UdA',
      title: 'Replacing the rear axle bearings and seals on the Project Jeep TJ',
      channel: 'The Orion Nebula',
    },
    source: 'Original guide created through independent research of Dana 35 and Dana 44 rear axles, technician forums, and OEM service documentation. Verified through the Automotive Repair Guide Generation System v9 methodology.',
    category: 'Suspension & Steering',
    difficulty: 'advanced',
    duration: '3-4 hours per side',
    interval: 'When axle whine, gear-oil leak from brake backing plate, or rear-end clunk develops',
    description:
      'The TJ rear axle uses a Dana 35 (standard) or Dana 44 (Rubicon) rear differential with semi-floating axle shafts. The wheel bearing and seal sit at the outer end of each axle tube, supporting the axle shaft and sealing gear oil from the brake assembly. When the bearing fails, you hear a low-frequency whine that changes pitch with road speed but not with engine RPM. A leaking seal soaks the brake backing plate with gear oil and can contaminate brake shoes.',
    overview: {
      difficulty: 'Advanced — requires axle shaft removal, C-clip release inside the differential, and bearing replacement',
      estimatedTime: '3 to 4 hours per side',
      estimatedCost: '$40-90 per side (bearing + seal) vs $400-700 at a shop',
      whyMatters: 'A failed rear bearing can seize and lock the rear wheel at speed. A leaking seal contaminates brake shoes and reduces braking effectiveness. Both failures are progressive — early replacement prevents bigger damage.',
      safetyNote: 'The differential cover must be removed and the C-clip inside the carrier must be released to free the axle shaft. The vehicle must be on level jack stands, and the parking brake cannot be relied upon while the rear axle is open.',
    },
    tools: [
      'Socket wrench set (10mm, 13mm, 15mm, 18mm)',
      'Torx bit set (T-50 for backing plate bolts on some configs)',
      'Slide hammer with axle shaft adapter (for tapered roller bearings)',
      'Bearing puller and seal driver set',
      'Torque wrench',
      'Drain pan (minimum 3 quarts capacity)',
      'Gasket scraper',
      'RTV silicone sealant or differential cover gasket',
      'Penetrating oil',
      'Shop towels',
      'Jack and jack stands',
    ],
    parts: [
      {
        id: 'rear-axle-bearing-d35',
        name: 'Rear Axle Bearing (Dana 35)',
        partNumber: 'Timken Set 2 (5707) or OEM Mopar equivalent',
        quantity: 2,
        price: 22,
        category: 'required',
        notes: 'Replace both sides if either is worn — bearings typically wear together. Dana 44 (Rubicon) uses different bearing.',
      },
      {
        id: 'rear-axle-seal-d35',
        name: 'Rear Axle Seal',
        partNumber: 'Timken 1180 or National 5778 equivalent',
        quantity: 2,
        price: 14,
        category: 'required',
        notes: 'Always replace the seal whenever the bearing is replaced.',
      },
      {
        id: 'differential-gear-oil',
        name: 'Differential Gear Oil (75W-90 GL-5)',
        partNumber: 'Mopar 80W-90 GL-5 or Mobil 1 75W-90 synthetic',
        quantity: 3,
        price: 14,
        category: 'required',
        notes: 'Dana 35 capacity ~2 quarts; Dana 44 capacity ~2.5 quarts. If equipped with limited slip, add friction modifier.',
      },
      {
        id: 'differential-cover-gasket',
        name: 'Differential Cover Gasket or RTV Sealant',
        partNumber: 'Mopar 4856636AC gasket or Permatex RTV',
        quantity: 1,
        price: 10,
        category: 'required',
        notes: 'RTV silicone is acceptable if no gasket is preferred.',
      },
    ],
    safetyInformation:
      'The rear axle is opened in this procedure — the parking brake will not function once axle shafts are removed. Chock the front wheels firmly. Never crawl under a vehicle supported only by a floor jack. Hot gear oil can burn — drain the differential after the engine has been off for at least 30 minutes. Wear safety glasses; bearing dust and metal slivers can fly when the bearing is pressed out.',
    beforeYouStart: [
      'Park on level concrete and chock both front wheels firmly',
      'Verify your differential type — Dana 35 standard, Dana 44 on Rubicon',
      'Apply penetrating oil to brake backing plate bolts 24 hours before work',
      'Have the correct bearing and seal for your specific axle on hand',
      'Plan to replace gear oil — capacity varies by axle (D35 ~2 qt, D44 ~2.5 qt)',
    ],
    steps: [
      {
        number: 1,
        title: 'Raise Vehicle and Remove Rear Wheel',
        description:
          'Loosen the rear lug nuts before lifting. Raise the rear of the vehicle with a floor jack at the differential housing. Place jack stands under the frame rails. Lower onto the stands and verify stability. Remove the wheel and set it under the frame as a safety backup.',
        why: 'The wheel must come off to access the brake drum and axle shaft.',
        tip: 'Lift only as high as needed for working room — higher lifts make tool access harder.',
        commonMistake: 'Skipping the wheel chocks at the front and having the vehicle roll while the rear is open.',
      },
      {
        number: 2,
        title: 'Drain the Differential Gear Oil',
        description:
          'Place a drain pan under the differential cover. Loosen the cover bolts in a star pattern, leaving the lower bolts last. Pry the cover gently with a flat tool to break the gasket seal. Let the gear oil drain into the pan. Once flow stops, remove the remaining bolts and the cover.',
        why: 'The cover must come off to access the C-clip that retains the axle shaft.',
        tip: 'Removing one or two upper bolts and one lower bolt leaves the cover hanging — pry it open and let gear oil drain through that gap.',
        commonMistake: 'Removing all bolts at once and being splashed with hot gear oil.',
      },
      {
        number: 3,
        title: 'Remove the Cross Pin and C-Clip',
        description:
          'Inside the differential carrier, locate the cross pin (a long pin running through the spider gears, secured by a small bolt or roll pin). Remove the retaining bolt and slide the cross pin out. Push the axle shaft inward toward the differential to expose the C-clip on the inner end of the shaft. Remove the C-clip with needle-nose pliers and set aside.',
        why: 'The C-clip retains the axle shaft in the carrier — it must be removed before the shaft will slide out.',
        tip: 'Photograph the spider gear arrangement before removing anything — alignment matters on reassembly.',
        commonMistake: 'Letting the cross pin retaining bolt fall into the differential housing.',
      },
      {
        number: 4,
        title: 'Pull the Axle Shaft Out of the Tube',
        description:
          'Grasp the axle shaft flange at the wheel end and pull straight outward. The shaft should slide out of the axle tube. The bearing and seal will remain in the tube end. Set the shaft aside on a clean surface.',
        why: 'With the C-clip removed, the shaft pulls free.',
        tip: 'Wipe the shaft splines clean and inspect for damage before setting aside.',
        commonMistake: 'Forcing the shaft and damaging the bearing if the C-clip was not fully removed.',
      },
      {
        number: 5,
        title: 'Remove the Old Seal and Bearing',
        description:
          'Use a slide hammer with bearing/seal puller adapter to remove the bearing and seal from the end of the axle tube. Both come out together as an assembly on most TJs. Inspect the axle tube bore for scoring — light scoring is normal but deep scoring requires professional repair.',
        why: 'The slide hammer is the only practical way to extract a tightly-pressed bearing from a blind bore.',
        tip: 'Some bearings yield after one or two solid hammer strokes; others take many — be patient.',
        commonMistake: 'Trying to pry the bearing out and gouging the axle tube bore.',
      },
      {
        number: 6,
        title: 'Clean the Bore and Install the New Bearing',
        description:
          'Wipe the axle tube bore clean with shop towels and brake cleaner. Place the new bearing squarely against the bore and use a bearing driver of the correct size to tap it home. Drive only on the outer race — never on the rollers or cage. The bearing should seat fully against the shoulder inside the tube.',
        why: 'A square bearing installation is essential — a cocked bearing will fail quickly.',
        tip: 'A large socket of the correct outside diameter makes an effective bearing driver in a pinch.',
        commonMistake: 'Driving the bearing on the inner race or cage and damaging it.',
      },
      {
        number: 7,
        title: 'Install the New Seal',
        description:
          'Apply a thin film of gear oil to the new seal lip. Place the seal squarely on the end of the axle tube and use a seal driver to tap it flush with the tube end. Do not drive the seal past flush — over-driving cracks the seal lip.',
        why: 'A flush-installed seal aligns with the axle shaft sealing surface and seals gear oil reliably.',
        tip: 'A wood block on the seal face can substitute for a seal driver.',
        commonMistake: 'Driving the seal past flush and creating a leak path.',
      },
      {
        number: 8,
        title: 'Reinstall Axle Shaft and C-Clip',
        description:
          'Slide the axle shaft into the tube, rotating it slightly to align the splines with the differential side gear. Once the splines engage, push the shaft in until the inner end appears in the differential carrier. Push the shaft fully inward and reinstall the C-clip on the inner groove. Pull the shaft outward to seat the C-clip against the side gear.',
        why: 'The C-clip retains the shaft against outward movement during operation.',
        tip: 'Apply a film of gear oil to the shaft surface to ease installation through the new seal.',
        commonMistake: 'Damaging the new seal lip by forcing the shaft splines through.',
      },
      {
        number: 9,
        title: 'Reinstall Cross Pin and Differential Cover',
        description:
          'Slide the cross pin back through the spider gears and reinstall its retaining bolt. Torque the bolt to 11 Nm (8 ft-lbs). Clean the differential cover and housing mating surfaces thoroughly. Apply a 1/8-inch bead of RTV silicone or position the new gasket. Reinstall the cover and tighten bolts in a star pattern to 41 Nm (30 ft-lbs).',
        why: 'Cross-pattern tightening keeps the cover flange parallel to prevent leaks.',
        tip: 'Wait 1 hour after RTV application before refilling gear oil to allow proper cure.',
        commonMistake: 'Over-tightening cover bolts and warping the cover.',
      },
      {
        number: 10,
        title: 'Refill Gear Oil and Test',
        description:
          'Locate the fill plug on the differential cover or housing. Remove it. Pump fresh 75W-90 GL-5 gear oil into the differential until oil reaches the bottom of the fill hole. Reinstall the fill plug. If equipped with limited-slip differential, add the appropriate friction modifier per manufacturer instructions. Reinstall the wheel and torque lug nuts to 122 Nm (90 ft-lbs). Test drive and listen for noise; inspect for leaks after the first 100 miles.',
        why: 'Proper fluid level is critical — under-filled differential causes rapid bearing wear.',
        tip: 'A pump bottle with a flexible nozzle makes refilling much easier than a funnel.',
        commonMistake: 'Forgetting to add friction modifier on limited-slip differentials and getting chatter on tight turns.',
      },
    ],
    torqueSpecifications: [
      {
        fastener: 'Differential cover bolts',
        specification: '41 Nm (30 ft-lbs)',
        notes: 'OEM specification. Tighten in star pattern.',
      },
      {
        fastener: 'Cross pin retaining bolt',
        specification: '11 Nm (8 ft-lbs)',
        notes: 'OEM specification.',
      },
      {
        fastener: 'Differential fill plug',
        specification: '34 Nm (25 ft-lbs)',
        notes: 'OEM specification.',
      },
      {
        fastener: 'Lug nuts',
        specification: '122 Nm (90 ft-lbs)',
        notes: 'OEM specification. Tighten in star pattern.',
      },
    ],
    fluidSpecifications: [
      {
        fluid: 'Differential gear oil',
        specification: '75W-90 GL-5 (or 80W-90 GL-5 in cold climates)',
        notes: 'Dana 35 capacity ~2 quarts; Dana 44 capacity ~2.5 quarts. Add friction modifier if equipped with Trac-Lok or limited slip.',
      },
    ],
    troubleshooting: [
      {
        problem: 'Whine continues after replacement',
        likelyCause: 'Bearing on opposite side also worn, or pinion bearing failing',
        whatToCheck: 'Replace opposite side bearing if untouched; have differential professionally inspected if pinion is suspect',
        fix: 'Replace remaining bearing; professional service for pinion bearing.',
      },
      {
        problem: 'Gear oil leak from differential cover',
        likelyCause: 'Cover not torqued evenly or RTV not cured before refill',
        whatToCheck: 'Re-check cover bolt torque; inspect RTV bead for gaps',
        fix: 'Re-torque cover; remove and reapply RTV if leak persists.',
      },
      {
        problem: 'Gear oil leak from axle tube end (brake drum side)',
        likelyCause: 'New seal damaged during axle installation or installed crooked',
        whatToCheck: 'Pull axle shaft and inspect seal lip',
        fix: 'Replace seal, applying gear oil to lip during installation.',
      },
      {
        problem: 'Clunk on acceleration after replacement',
        likelyCause: 'Cross pin retaining bolt loose or C-clip not seated',
        whatToCheck: 'Reopen cover and verify cross pin bolt and C-clip seating',
        fix: 'Re-torque cross pin bolt; reseat C-clip if necessary.',
      },
    ],
    proTips: [
      'Replace bearings on both sides of the rear axle at the same time — they wear together and labor is half-shared.',
      'Inspect the brake shoes for gear-oil contamination — replace any shoes soaked with oil.',
      'A magnetic drain plug on the differential cover catches metal shavings and warns of internal wear.',
      'Allow at least 1 hour for RTV silicone to cure before adding gear oil — premature filling causes leaks.',
    ],
    successIndicators: [
      'No whining or roaring from the rear under any driving condition',
      'No gear oil leaks at brake backing plate or differential cover',
      'Brake performance unchanged or improved (no contaminated shoes)',
      'Gear oil level holds steady at fill plug after 100 miles',
    ],
    whenToSeekHelp: 'If the axle tube bore is heavily scored, the bearing race fits loosely, or the differential shows metal shavings beyond fine fuzz on the magnetic plug, professional axle service is required.',
    legalDisclaimer: 'IMPORTANT SAFETY NOTICE: Rear axle work disables the parking brake. Chock the front wheels firmly and never crawl under a vehicle supported only by a jack. Contaminated brake shoes can cause brake failure — replace any shoes soaked with gear oil. If you are uncertain about any procedure, consult a qualified professional.',
  },

  {
    id: 'tj-differential-pinion-seal',
    title: 'Differential Pinion Seal Replacement',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [
        { startYear: 2003, endYear: 2006 }
      ],
    },
    video: {
      youtubeId: 'Z0zz1_4UYqk',
      title: 'Differential pinion seal replacement, Jeep Wrangler TJ, DIY',
      channel: 'wwwCARSSUCKcom',
    },
    source: 'Original guide created through independent research of differential pinion bearing systems, technician forums, and OEM service documentation. Verified through the Automotive Repair Guide Generation System v9 methodology.',
    category: 'Suspension & Steering',
    difficulty: 'advanced',
    duration: '2-3 hours',
    interval: 'When gear oil leak develops at the pinion flange (front of differential)',
    description:
      'The pinion seal sits behind the pinion flange where the driveshaft connects to the differential. It seals gear oil around the pinion shaft. Aging or hardened seals leak gear oil onto the driveshaft and underbody. Replacement requires removing the driveshaft and pinion flange while preserving the original pinion bearing preload — a critical step that prevents permanent gear damage.',
    overview: {
      difficulty: 'Advanced — requires precise tracking of pinion nut position to preserve bearing preload',
      estimatedTime: '2 to 3 hours per axle',
      estimatedCost: '$15-30 (seal) vs $300-500 at a shop',
      whyMatters: 'A leaking pinion seal lets gear oil escape, eventually starving the differential and destroying the gear set ($800-1,500 to rebuild). Catching the leak early is one of the highest-leverage repairs on the vehicle.',
      safetyNote: 'The pinion bearing preload must be preserved exactly. Mark the pinion nut position before removal, count threads of nut compression after removal, and replicate exactly during reinstallation. Over-tightening the pinion nut crushes the bearing preload spacer and destroys the pinion bearings.',
    },
    tools: [
      'Socket wrench set (12mm, 13mm, 15mm)',
      'Large socket for pinion nut (typically 1-1/8" or 30mm)',
      'Long breaker bar or impact wrench',
      'Torque wrench',
      'Pinion flange holder tool (or a 2x4 wedged against the flange)',
      'Pinion seal puller or pry bar',
      'Seal driver of correct diameter',
      'Inch-pound torque wrench (to verify pinion preload after assembly)',
      'Paint pen or center punch for marking',
      'Drain pan',
      'Clean shop towels',
    ],
    parts: [
      {
        id: 'pinion-seal-d35',
        name: 'Pinion Seal',
        partNumber: 'Timken 4250 (Dana 35) or 5778 (Dana 44) or OEM Mopar',
        quantity: 1,
        price: 18,
        category: 'required',
        notes: 'Verify part fits your specific differential — Dana 30 front, Dana 35 rear, or Dana 44 rear (Rubicon).',
      },
      {
        id: 'pinion-nut',
        name: 'Pinion Nut (one-time use on some configurations)',
        partNumber: 'Mopar OEM',
        quantity: 1,
        price: 12,
        category: 'optional',
        notes: 'Some pinion nuts are crush-and-stake type and should be replaced. Check your specific axle service spec.',
      },
      {
        id: 'differential-gear-oil-pinion',
        name: 'Differential Gear Oil (75W-90 GL-5)',
        partNumber: 'Mopar 80W-90 GL-5 or equivalent',
        quantity: 1,
        price: 14,
        category: 'required',
        notes: 'Top off only — full drain not required for pinion seal service.',
      },
    ],
    safetyInformation:
      'Pinion bearing preload is critical to differential life. Use a paint pen to mark the original position of the pinion nut, the pinion flange, and the pinion shaft before removing anything. Count the exact number of socket flats the nut turns during removal. Replicate the exact final position on reassembly. Use an inch-pound torque wrench to verify rotational drag matches OEM spec (typically 12-25 in-lbs for used bearings) after assembly.',
    beforeYouStart: [
      'Park on level concrete and chock the wheels',
      'Confirm the leak is from the pinion seal (not from the cover or axle ends) by cleaning the differential and observing where new oil appears',
      'Mark the driveshaft flange to pinion flange alignment with paint pen before removal',
      'Have an inch-pound torque wrench available to verify pinion preload after assembly',
      'Plan to replicate the original pinion nut position exactly — mark and count thread engagement before disassembly',
    ],
    steps: [
      {
        number: 1,
        title: 'Raise Vehicle and Mark Driveshaft Alignment',
        description:
          'Lift the vehicle and support on jack stands. Use a paint pen to draw a clear alignment mark across the driveshaft flange and the pinion flange. The mark must be a single line that crosses both flanges so they can be re-clocked exactly during reassembly.',
        why: 'Driveshafts are balanced as an assembly. Reinstalling 180 degrees out of phase causes severe vibration.',
        tip: 'Mark before any disassembly — paint applied to oily metal does not stick well.',
        commonMistake: 'Skipping the alignment mark and creating mystery vibration that requires removing the driveshaft again.',
      },
      {
        number: 2,
        title: 'Disconnect the Driveshaft at the Pinion Flange',
        description:
          'Remove the four bolts (or U-bolts on some configurations) holding the driveshaft yoke to the pinion flange. Lower the rear of the driveshaft and slide it forward off the pinion flange. Tape the U-joint caps with masking tape so the needle bearings stay in place if the joint will be reused.',
        why: 'The driveshaft must come off to expose the pinion nut.',
        tip: 'Use a wrench on the pinion flange or have a helper hold the brake pedal to prevent rotation.',
        commonMistake: 'Letting the driveshaft drop and damaging the U-joint or losing needle bearings.',
      },
      {
        number: 3,
        title: 'Mark Pinion Nut Position Before Removal',
        description:
          'Use a paint pen or center punch to mark the exact position of the pinion nut relative to the pinion shaft. The mark should connect the nut, the washer, and the shaft. Also count and record the number of socket flats (each face of a hex nut) of nut engagement — the goal is to know exactly where the nut sits.',
        why: 'Reinstalling the nut to the exact same position preserves the original pinion bearing preload set at the factory.',
        tip: 'Photograph the marked nut from multiple angles before removal.',
        commonMistake: 'Skipping the mark and relying on torque alone to set preload — pinion preload is set by crush, not torque, and torque-only assembly often crushes too much or too little.',
      },
      {
        number: 4,
        title: 'Hold the Pinion Flange and Remove the Pinion Nut',
        description:
          'Engage a flange holder tool on the pinion flange (a long bar bolted to two of the driveshaft holes works as a substitute). Brace the holder against the floor. Use the breaker bar with the appropriate large socket to remove the pinion nut. The nut may be tight — apply steady force.',
        why: 'The flange must be held stationary so the nut can be turned without rotating the pinion shaft.',
        tip: 'Soaking the nut with penetrating oil overnight helps if it resists.',
        commonMistake: 'Using a screwdriver in a U-joint hole — this damages the flange.',
      },
      {
        number: 5,
        title: 'Pull the Pinion Flange Off',
        description:
          'Grasp the pinion flange and pull it straight off the pinion shaft. The flange should slide off the splines with hand pressure. If stuck, use a 3-jaw puller. Set the flange aside; it will be reinstalled.',
        why: 'The pinion flange must come off to expose the pinion seal.',
        tip: 'Wipe the flange surface clean — any grit will damage the new seal lip.',
        commonMistake: 'Hammering on the flange and bending it.',
      },
      {
        number: 6,
        title: 'Remove the Old Pinion Seal',
        description:
          'Place a drain pan under the differential nose. Insert a pinion seal puller or pry bar carefully into the seal opening at the front of the differential. Pry the seal out without scratching the seal bore in the housing. A small amount of gear oil will drip out — this is normal.',
        why: 'The old seal must be removed so the new one can be installed in its place.',
        tip: 'A seal puller hooks under the seal lip and pulls it straight out without scratching the bore.',
        commonMistake: 'Gouging the seal bore with a screwdriver and creating a leak path the new seal cannot fix.',
      },
      {
        number: 7,
        title: 'Inspect the Pinion Shaft Sealing Surface',
        description:
          'Wipe the pinion shaft surface clean. Inspect the area where the seal lip rides for grooves or wear. Light polishing marks are acceptable; deep grooves (more than 0.005 inch) require professional pinion shaft replacement or a wear-sleeve repair. Inspect the seal bore for damage.',
        why: 'The seal lip must ride on a smooth surface to seal gear oil.',
        tip: 'A speedi-sleeve installed over a worn pinion shaft restores the sealing surface without removing the pinion.',
        commonMistake: 'Installing a new seal on a worn pinion surface and getting an immediate leak.',
      },
      {
        number: 8,
        title: 'Install the New Pinion Seal',
        description:
          'Apply a thin film of gear oil to the new seal lip. Place the seal squarely in the bore with the part number facing outward. Use a seal driver of the correct outside diameter to tap the seal flush with the differential housing. Drive only on the metal outer rim of the seal — never on the rubber lip.',
        why: 'A flush-installed seal aligns properly with the pinion shaft sealing surface.',
        tip: 'A flat block of wood across the seal face distributes hammer blows evenly.',
        commonMistake: 'Driving the seal past flush and creating a leak path.',
      },
      {
        number: 9,
        title: 'Reinstall the Pinion Flange and Nut',
        description:
          'Slide the pinion flange back onto the pinion shaft splines, aligning your mark. Apply gear oil to the seal lip area as the flange slides through. Hand-thread the pinion nut. Use the flange holder again and turn the nut down until your alignment marks line up exactly. Do not over-tighten — preserving the original preload is critical.',
        why: 'Returning the nut to its original position preserves the preload set at the factory.',
        tip: 'Tighten in small increments and check the marks frequently.',
        commonMistake: 'Tightening the nut to a torque spec without watching the marks — preload is set by crush, not torque.',
      },
      {
        number: 10,
        title: 'Verify Preload, Reconnect Driveshaft, and Test',
        description:
          'Use an inch-pound torque wrench on the pinion nut to verify rotational drag — a used pinion bearing should require 12-25 in-lbs to rotate steadily. If drag is too low, snug the nut slightly until drag matches. Reconnect the driveshaft using your alignment marks and torque flange bolts to 52 Nm (38 ft-lbs). Top off gear oil to the bottom of the fill hole. Test drive and inspect for leaks after 100 miles.',
        why: 'Verifying preload by rotational drag is the only way to confirm proper bearing load without disassembly.',
        tip: 'Spin the pinion several full turns before reading rotational drag — initial bearing settling can cause false readings.',
        commonMistake: 'Skipping the preload verification and either crushing the spacer or under-loading the bearings.',
      },
    ],
    torqueSpecifications: [
      {
        fastener: 'Pinion nut (final torque)',
        specification: 'To original alignment marks (preload-based, not torque-based)',
        notes: 'Crush-spacer preload — verify with 12-25 in-lbs (1.4-2.8 Nm) rotational drag on used bearings.',
      },
      {
        fastener: 'Driveshaft flange bolts',
        specification: '52 Nm (38 ft-lbs)',
        notes: 'OEM specification. Tighten in cross pattern.',
      },
      {
        fastener: 'Differential fill plug',
        specification: '34 Nm (25 ft-lbs)',
        notes: 'OEM specification.',
      },
    ],
    fluidSpecifications: [
      {
        fluid: 'Differential gear oil (top off)',
        specification: '75W-90 GL-5',
        notes: 'Top off only — full drain typically not needed for pinion seal service. Verify level at fill plug.',
      },
    ],
    troubleshooting: [
      {
        problem: 'Leak persists after replacement',
        likelyCause: 'Pinion shaft groove worn into wear surface, or seal driven too deep',
        whatToCheck: 'Inspect pinion shaft for grooves; verify seal flush with bore',
        fix: 'Install speedi-sleeve over worn shaft surface; replace seal if installed too deep.',
      },
      {
        problem: 'Differential whine after replacement',
        likelyCause: 'Pinion preload too tight (over-crushed spacer)',
        whatToCheck: 'Measure rotational drag with inch-pound torque wrench',
        fix: 'If drag exceeds 35 in-lbs, professional service is required to replace the crush spacer.',
      },
      {
        problem: 'Pinion play after replacement',
        likelyCause: 'Pinion nut not tightened to original position (preload too loose)',
        whatToCheck: 'Verify nut alignment marks and rotational drag',
        fix: 'Snug nut in small increments until rotational drag reaches 12-25 in-lbs.',
      },
      {
        problem: 'Driveline vibration after reassembly',
        likelyCause: 'Driveshaft installed 180 degrees out of phase',
        whatToCheck: 'Compare current flange position to original alignment mark',
        fix: 'Remove driveshaft, rotate 180 degrees, reinstall with marks aligned.',
      },
    ],
    proTips: [
      'Marking everything before disassembly is the single most important step — preload preservation depends on it.',
      'A speedi-sleeve over a worn pinion shaft surface costs $20 and avoids removing the pinion entirely.',
      'Inspect the pinion bearing rollers through the seal opening before installing the new seal — pitting indicates time for full pinion service.',
      'Replace the pinion seal at first sign of weeping rather than waiting for active dripping — by then bearing damage may have started.',
    ],
    successIndicators: [
      'No gear oil leak at the pinion flange',
      'No differential whine at any speed',
      'No driveline vibration',
      'Gear oil level holds steady at fill plug after 200 miles',
    ],
    whenToSeekHelp: 'If the pinion bearing rotational drag is wrong by more than a few in-lbs after careful nut adjustment, or if the pinion shaft sealing surface is heavily grooved, professional differential service is required. Pinion preload errors destroy gears within thousands of miles.',
    legalDisclaimer: 'IMPORTANT SAFETY NOTICE: Pinion bearing preload directly affects differential life and safety. Incorrect preload can cause sudden gear failure leading to drivetrain damage and possible loss of vehicle control. If you are uncertain about preload procedures, consult a qualified professional.',
  },

  {
    id: 'tj-differential-cover-gasket',
    title: 'Differential Cover Gasket Replacement',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [
        { startYear: 2003, endYear: 2006 }
      ],
    },
    video: {
      youtubeId: '1yfuga2Kock',
      title: 'How to Seal a Differential Cover',
      channel: 'BleepinJeep',
    },
    source: 'Original guide created through independent research of differential cover sealing, technician forums, and OEM service documentation. Verified through the Automotive Repair Guide Generation System v9 methodology.',
    category: 'Routine Maintenance & Fluids',
    difficulty: 'beginner',
    duration: '1-1.5 hours',
    interval: 'When gear oil leak develops at the cover, or as part of differential service',
    description:
      'The differential cover seals the rear of the differential housing and contains the gear oil. The original cover uses an RTV silicone bead or a paper/composite gasket. Aging seal causes gear oil to weep along the cover edge, leaving a wet trail down the differential housing and onto the underbody. Replacement is one of the simplest differential repairs and a perfect entry point for axle service.',
    overview: {
      difficulty: 'Beginner — basic bolt removal, surface cleaning, and resealing',
      estimatedTime: '1 to 1.5 hours',
      estimatedCost: '$15-30 (gasket or RTV plus gear oil) vs $150-250 at a shop',
      whyMatters: 'A weeping cover slowly drains gear oil, eventually starving the differential and causing bearing or gear damage. Catching the leak early prevents thousands of dollars of secondary damage.',
      safetyNote: 'Gear oil is hot if the vehicle was recently driven — wait at least 30 minutes for cooling. Gear oil also has a strong odor that lingers — work in a ventilated area.',
    },
    tools: [
      'Socket wrench set (10mm, 13mm)',
      'Torque wrench',
      'Drain pan (minimum 3 quart capacity)',
      'Gasket scraper or razor blade',
      'Wire brush',
      'Brake cleaner or carburetor cleaner',
      'Clean shop towels',
      'Pump bottle for gear oil refill',
      'Jack and jack stands',
      'Safety glasses',
      'Nitrile gloves',
    ],
    parts: [
      {
        id: 'diff-cover-gasket',
        name: 'Differential Cover Gasket',
        partNumber: 'Mopar 4856636AC (Dana 35) or 4856637AC (Dana 44) or Lubelocker silicone',
        quantity: 1,
        price: 12,
        category: 'optional',
        notes: 'Lubelocker silicone-rubber reusable gaskets are excellent for off-road use; OEM paper gasket works for street use.',
      },
      {
        id: 'rtv-silicone',
        name: 'RTV Silicone Sealant (alternative to gasket)',
        partNumber: 'Permatex Ultra Black or Mopar 4318025',
        quantity: 1,
        price: 8,
        category: 'optional',
        notes: 'Use either RTV or gasket — not both. RTV is preferred by some shops for irregular surfaces.',
      },
      {
        id: 'differential-gear-oil-cover',
        name: 'Differential Gear Oil (75W-90 GL-5)',
        partNumber: 'Mopar 80W-90 GL-5 or Mobil 1 75W-90 synthetic',
        quantity: 3,
        price: 14,
        category: 'required',
        notes: 'Dana 35 ~2 quarts; Dana 44 ~2.5 quarts; Dana 30 front ~1.5 quarts. Add friction modifier if Trac-Lok equipped.',
      },
      {
        id: 'magnetic-drain-plug',
        name: 'Magnetic Drain/Fill Plug (upgrade)',
        partNumber: 'Generic 3/8" NPT magnetic plug',
        quantity: 1,
        price: 6,
        category: 'optional',
        notes: 'Catches metal shavings — early-warning indicator of internal wear.',
      },
    ],
    safetyInformation:
      'Always work on a cool differential. Gear oil at operating temperature can cause burns. Work on level ground with the vehicle on jack stands — never crawl under a vehicle supported only by a jack. Wear safety glasses when scraping old gasket material — chips fly. Wear gloves; gear oil is hard to remove from skin.',
    beforeYouStart: [
      'Park on level concrete and allow at least 30 minutes for differential to cool',
      'Confirm the leak is from the cover (not the pinion seal or axle ends) by cleaning the housing and observing where new oil appears',
      'Verify your gear oil specification — most TJs use 75W-90 GL-5; verify if Trac-Lok requires friction modifier',
      'Have either a new gasket or a fresh tube of RTV ready — do not use both',
      'Plan to wait 1 hour for RTV cure before refilling oil',
    ],
    steps: [
      {
        number: 1,
        title: 'Raise Vehicle and Position Drain Pan',
        description:
          'Lift the vehicle with a floor jack and support on jack stands. Lower onto the stands. Position a drain pan with at least 3 quarts capacity directly under the differential cover.',
        why: 'You need both clearance to remove the cover and a pan to catch the gear oil.',
        tip: 'A wide pan catches splashes better than a tall narrow one.',
        commonMistake: 'Using a small pan and overflowing with the first 2 quarts of gear oil.',
      },
      {
        number: 2,
        title: 'Loosen All Cover Bolts in a Star Pattern',
        description:
          'Using a 13mm socket, loosen each cover bolt by approximately one full turn in a star pattern. Do not remove any bolts yet. The cover will start to weep gear oil at the seal edges as bolts loosen — this is normal.',
        why: 'Loosening evenly prevents warping the cover or gasket.',
        tip: 'The star pattern releases pressure progressively across the gasket surface.',
        commonMistake: 'Removing bolts in sequence and warping the cover.',
      },
      {
        number: 3,
        title: 'Remove Most Bolts and Drain Gear Oil',
        description:
          'Remove all bolts except the top two. Carefully pry the cover open at the bottom edge to start gear oil draining into the pan. Once flow slows, remove the upper two bolts and pull the cover off completely. Inspect the gear oil draining out — fine fuzz on the magnetic plug is normal; metal flakes indicate gear damage.',
        why: 'Leaving the top bolts in place keeps the cover from falling and lets oil drain in a controlled flow.',
        tip: 'Photograph the gear oil draining — color and contamination level inform service decisions.',
        commonMistake: 'Removing all bolts at once and being splashed with hot gear oil as the cover falls.',
      },
      {
        number: 4,
        title: 'Inspect the Differential Internals',
        description:
          'With the cover off, inspect the visible gears, the carrier, and the bearings. Look for any chipped teeth, pitted surfaces, or significant metal shavings on the magnetic surface (if equipped). Photograph the internals for reference. Any obvious damage means professional service before resealing.',
        why: 'Open differential is the only chance to inspect internal condition without major disassembly.',
        tip: 'Rotate the driveshaft by hand and watch the ring gear and pinion mesh — should be smooth with no clicks.',
        commonMistake: 'Skipping the inspection and resealing a differential that has internal damage.',
      },
      {
        number: 5,
        title: 'Scrape Off Old Gasket or RTV',
        description:
          'Using a plastic gasket scraper or razor blade held flat, scrape all old gasket material or RTV from both the cover face and the differential housing flange. Both surfaces must be completely clean and free of old material. Do not let scrapings fall into the differential — block the opening with a clean shop towel.',
        why: 'A clean mating surface is essential for a leak-free seal.',
        tip: 'A wire brush followed by brake cleaner removes the last traces of oil residue.',
        commonMistake: 'Using a metal scraper at an angle and gouging the soft aluminum or steel mating surface.',
      },
      {
        number: 6,
        title: 'Clean Both Mating Surfaces',
        description:
          'Spray brake cleaner or carburetor cleaner on both the cover face and the housing flange. Wipe with clean shop towels until both surfaces are dry and grease-free. The surfaces must be free of any oil film for the gasket or RTV to bond properly.',
        why: 'Oil residue prevents the gasket or RTV from adhering and seals will leak.',
        tip: 'A new shop towel for the final wipe ensures no residue is transferred from a contaminated towel.',
        commonMistake: 'Skipping the cleaning step and trapping a film of oil between the new gasket and housing.',
      },
      {
        number: 7,
        title: 'Apply New Gasket or RTV',
        description:
          'For a paper gasket: position it on the cover so all bolt holes align. Some technicians apply a thin film of RTV to both faces of the paper gasket as added insurance. For RTV-only sealing: apply a continuous 1/8-inch bead of RTV around the cover flange, going around each bolt hole on the inside (so bolts do not penetrate the bead). Do not over-apply — excess RTV squeezes inside the differential and can clog passages.',
        why: 'Proper gasket or RTV application creates the seal surface.',
        tip: 'A tube of RTV with a fine-tip nozzle gives a more controlled bead than a wide nozzle.',
        commonMistake: 'Applying RTV too thick and having excess squeeze into the differential.',
      },
      {
        number: 8,
        title: 'Install Cover and Star-Pattern Tighten Bolts',
        description:
          'Position the cover squarely against the housing. Hand-thread all bolts. Snug each bolt in a star pattern, then torque to 41 Nm (30 ft-lbs) per OEM specification, again following the star pattern. Do not over-torque — over-tightening warps the cover and destroys the seal.',
        why: 'Star-pattern torquing distributes clamping load evenly around the cover.',
        tip: 'A second pass at full torque after all bolts are at first-pass torque ensures even seating.',
        commonMistake: 'Over-tightening cover bolts and warping the cover, which causes immediate leaks.',
      },
      {
        number: 9,
        title: 'Wait for RTV to Cure (If Used)',
        description:
          'If RTV silicone was used, wait at least 1 hour for partial cure before adding gear oil. Most RTV products require 24 hours for full cure but tolerate fluid contact after 1 hour. Refilling immediately washes uncured RTV away and creates leaks.',
        why: 'Premature fluid contact destroys the RTV seal before it has set.',
        tip: 'Use the wait time to clean tools and dispose of old gear oil at a recycling center.',
        commonMistake: 'Refilling immediately and watching gear oil leak past partially-cured RTV.',
      },
      {
        number: 10,
        title: 'Refill Gear Oil to Specification',
        description:
          'Locate the fill plug on the differential cover or upper housing. Remove it. Pump fresh 75W-90 GL-5 gear oil into the differential until oil reaches the bottom of the fill hole. Reinstall the fill plug and torque to 34 Nm (25 ft-lbs). If equipped with Trac-Lok, add the appropriate friction modifier per Mopar specification. Test drive and inspect for leaks after 100 miles.',
        why: 'Proper fluid level is critical — under-filled differential causes rapid bearing wear.',
        tip: 'A pump bottle with a flexible nozzle threads directly into the fill hole and avoids the mess of a funnel.',
        commonMistake: 'Forgetting Trac-Lok friction modifier on limited-slip differentials and getting chatter on tight turns.',
      },
    ],
    torqueSpecifications: [
      {
        fastener: 'Differential cover bolts',
        specification: '41 Nm (30 ft-lbs)',
        notes: 'OEM specification. Tighten in star pattern.',
      },
      {
        fastener: 'Differential fill plug',
        specification: '34 Nm (25 ft-lbs)',
        notes: 'OEM specification.',
      },
    ],
    fluidSpecifications: [
      {
        fluid: 'Differential gear oil',
        specification: '75W-90 GL-5 (or 80W-90 GL-5 in cold climates)',
        notes: 'Dana 35 ~2 quarts; Dana 44 ~2.5 quarts; Dana 30 front ~1.5 quarts. Add friction modifier if Trac-Lok or limited slip equipped.',
      },
    ],
    troubleshooting: [
      {
        problem: 'Cover leaks immediately after refill',
        likelyCause: 'RTV not cured before refill, or cover bolts uneven',
        whatToCheck: 'Re-torque bolts in star pattern; if RTV used, check for visible gaps',
        fix: 'Drain oil, remove cover, reapply RTV, allow full cure before refill.',
      },
      {
        problem: 'Slow weep returns within months',
        likelyCause: 'Cover warped from previous over-tightening',
        whatToCheck: 'Inspect cover flange for flatness on a known flat surface',
        fix: 'Replace cover with a new flat one (often a heavy-duty aluminum aftermarket cover).',
      },
      {
        problem: 'Whine appears after gear oil change',
        likelyCause: 'Wrong gear oil viscosity or missing friction modifier',
        whatToCheck: 'Verify fluid type and Trac-Lok status',
        fix: 'Drain and refill with correct specification including friction modifier.',
      },
      {
        problem: 'Gear oil level rises above fill hole',
        likelyCause: 'Over-filled — water or contaminant in differential causing emulsion',
        whatToCheck: 'Inspect drained fluid for milky appearance',
        fix: 'Drain, inspect axle vent for clog, refill with correct quantity.',
      },
    ],
    proTips: [
      'A heavy-duty aluminum aftermarket cover with built-in drain plug and dipstick simplifies all future differential service.',
      'Lubelocker silicone-rubber gaskets are reusable up to 5 times and ideal for off-road TJs that service the differential frequently.',
      'A magnetic drain plug catches metal shavings and gives early warning of internal wear.',
      'Service the front differential at the same time — it uses the same procedure and parts.',
    ],
    successIndicators: [
      'No gear oil leak at the differential cover after 100 miles',
      'Gear oil level holds steady at the fill hole',
      'No whining or unusual noise from the differential',
      'No metal flakes on the magnetic plug at next service',
    ],
    whenToSeekHelp: 'If the cover or housing flange is heavily damaged, or if internal differential damage is visible during inspection, professional service is required before resealing.',
    legalDisclaimer: 'IMPORTANT SAFETY NOTICE: Differential failure can lock a wheel and cause loss of vehicle control. Always use the correct gear oil specification and quantity. If internal damage is visible, do not reseal and drive — seek professional service. If you are uncertain about any procedure, consult a qualified professional.',
  },
  {
    id: 'tj-front-axle-u-joint-replacement',
    title: 'Front Axle U-Joint Replacement (Dana 30)',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [
        { startYear: 2003, endYear: 2006 }
      ],
    },
    video: {
      youtubeId: 'F5zwA1ECf1c',
      title: 'Jeep TJ - D30 Axle Shaft U-joint Replacement',
      channel: 'Scruf\'s Garage',
    },
    source: 'Original guide created through independent research of Dana 30 axle service procedures, technician forums, and OEM service documentation. Verified through the Automotive Repair Guide Generation System v9 methodology.',
    category: 'Suspension & Steering',
    difficulty: 'advanced',
    duration: '2.5-3.5 hours per side',
    interval: 'When clicking is heard during turns, vibration is felt in 4WD, or grease appears around the axle shaft',
    description:
      'The Dana 30 front axle uses a single Cardan U-joint at the steering knuckle to allow the front wheels to steer while still receiving power. Each joint sits inside the axle shaft assembly between the inner shaft and the stub shaft (which spins with the wheel). When the joint wears, drivers hear a sharp click-click-click during tight turns under load — most noticeable when backing up while turning. Left unaddressed, a failed U-joint can disintegrate, sending bearing needles into the differential and causing catastrophic damage. Replacement requires pulling the axle shaft, pressing out the old joint, and pressing in a new one without overstressing the new caps.',
    overview: {
      difficulty: 'Advanced — requires axle shaft removal and a press or specialty U-joint tool',
      estimatedTime: '2.5 to 3.5 hours per side',
      estimatedCost: '$25-60 per joint (Spicer 5-760X premium, generic options $25) vs $300-450 per side at a shop',
      whyMatters: 'A failed U-joint can come apart at speed and either lock the front wheel or send needle bearings through the differential, causing thousands of dollars of damage. The clicking sound is the early warning — replace before failure.',
      safetyNote: 'Pressing U-joint caps is a high-force operation. Wear safety glasses — caps and clips can launch off the press. Support the axle yoke properly so the press load travels through the yoke ears, not the shaft itself.',
    },
    tools: [
      'Socket wrench set (13mm, 18mm, 36mm or 1-7/16" axle nut socket)',
      'Torque wrench (capable of 175 ft-lbs / 237 Nm)',
      'Hub puller or 3-jaw puller',
      'Hydraulic press (minimum 12-ton) OR specialty U-joint tool (e.g., OTC 7894 or equivalent)',
      'Internal snap-ring pliers',
      'Brass drift or punch',
      'Bench vise (heavy-duty, 5" minimum)',
      'Ball peen hammer',
      'Brake cleaner',
      'Wheel bearing grease',
      'Torx bit set (T55 for older hubs)',
      'Jack and jack stands',
      'Safety glasses and nitrile gloves',
    ],
    parts: [
      {
        id: 'front-axle-ujoint',
        name: 'Front Axle U-Joint (Dana 30)',
        partNumber: 'Spicer 5-760X (premium, non-greasable) or 5-297X (greasable)',
        quantity: 1,
        price: 45,
        category: 'required',
        notes: 'Spicer 5-760X is the OEM-spec heavy-duty replacement. Greasable joints (5-297X) require periodic service but allow purging contamination — a good off-road choice. Avoid no-name joints; the steel quality difference is significant.',
      },
      {
        id: 'cotter-pin',
        name: 'Axle Nut Cotter Pin',
        partNumber: 'Generic 1/8" x 1.5" cotter pin',
        quantity: 1,
        price: 1,
        category: 'required',
        notes: 'Always use a fresh cotter pin. Never reuse the original.',
      },
      {
        id: 'wheel-bearing-grease-uj',
        name: 'High-Temp Wheel Bearing Grease',
        partNumber: 'Mopar 04318062 or Mobil 1 Synthetic Grease',
        quantity: 1,
        price: 8,
        category: 'required',
        notes: 'Pack the new caps with grease before installation if the joint is greasable. Even non-greasable joints benefit from a light coating on the cap exteriors to ease installation.',
      },
      {
        id: 'axle-seal-optional',
        name: 'Inner Axle Seal (replace if torn during shaft removal)',
        partNumber: 'Mopar 5012842AB or Timken 710073',
        quantity: 1,
        price: 12,
        category: 'optional',
        notes: 'Inspect the inner axle seal when the shaft is out. If it is hardened, torn, or weeping, replace it now while access is easy.',
      },
    ],
    safetyInformation:
      'A hydraulic press generates enormous force. Always wear safety glasses. Keep hands clear of the press throat. Support the U-joint yoke ears squarely on the press anvil — if the yoke tilts under load, the press ram can slip and damage the yoke or launch parts. The axle nut is torqued to 175 ft-lbs and may require a breaker bar — stabilize the vehicle properly. Never crawl under a vehicle supported only by a jack; always use rated jack stands on flat concrete.',
    beforeYouStart: [
      'Confirm the U-joint is the actual fault — clicking on turns is the diagnostic signature; pop the wheel cover and have a helper turn the wheel slowly while you watch the joint for play',
      'Decide which side(s) to do — if one is bad, the other is often near failure; many techs replace both at once',
      'Verify you have either a 12-ton press or a proper U-joint tool — channel locks and hammers will damage the new joint',
      'Plan for 3+ hours per side; this is not a quick job',
      'Have the new joint, fresh cotter pin, and grease on hand before disassembly',
    ],
    steps: [
      {
        number: 1,
        title: 'Loosen Lugs, Lift Vehicle, Remove Wheel',
        description:
          'With the vehicle on flat ground and parking brake set, break the wheel lugs loose with the tire still on the ground. Lift the front of the Jeep with a floor jack at the front axle differential or a frame point. Lower onto jack stands rated for the vehicle weight. Remove the wheel. Repeat for the second side if doing both.',
        why: 'Breaking the lugs while the tire is grounded is safer than fighting them once the wheel is off the ground. Stable jack stand support is required because the press work later forces high loads onto the axle.',
        tip: 'Mark the wheel-to-hub orientation before removal so you can reinstall in the same position to preserve any wheel balance.',
        commonMistake: 'Trying to remove the axle nut after the wheel is off — the spinning hub makes this difficult. Always loosen the axle nut while the wheel is grounded if possible.',
      },
      {
        number: 2,
        title: 'Remove Brake Caliper and Rotor',
        description:
          'Unbolt the brake caliper bracket from the steering knuckle (two large bolts). Lift the caliper and bracket off as an assembly and hang from the coil spring or frame with a wire — never let it hang by the brake hose. Slide the rotor off the wheel studs. If rust-stuck, give it a few hits with a dead-blow hammer on the hat (not the friction surface).',
        why: 'The caliper and rotor must come off to clear the hub for axle nut removal and hub extraction. Hanging the caliper supports it without stressing the flexible hose.',
        tip: 'Spray the caliper bracket bolts with penetrating oil 15 minutes before removing — they are exposed to road salt and weather and can be very tight.',
        commonMistake: 'Letting the caliper dangle by the brake hose. The hose can be permanently damaged and may fail later under braking pressure.',
      },
      {
        number: 3,
        title: 'Remove Axle Nut and Hub Assembly',
        description:
          'Remove the cotter pin and nut retainer (if present), then loosen the axle nut with a 36mm or 1-7/16" socket. Set the nut aside. Unbolt the three hub-to-knuckle bolts (Torx T55 from the back of the knuckle on most TJs) and pull the unit hub assembly off the steering knuckle. The hub may need a few firm hits with a dead-blow hammer to release from the knuckle bore. The axle stub shaft will pull out with the hub.',
        why: 'Removing the hub gives you access to pull the inner axle shaft. The U-joint is part of the shaft, accessible only with the shaft out of the housing.',
        tip: 'Keep the hub assembly upright and capture any thrust washers or shims that may fall out.',
        commonMistake: 'Hammering on the axle threads — this distorts them and ruins the nut. Use a brass drift on the hub itself, or use a 3-jaw puller.',
      },
      {
        number: 4,
        title: 'Slide Axle Shaft Out of Housing',
        description:
          'With the hub out, grasp the inner axle shaft and pull it straight out of the differential housing. The shaft slides out of the inner C-clip-free splines (the Dana 30 uses a stub-and-shaft design — there is no internal C-clip on the inner end). Watch for the inner axle seal — if it tears as the shaft passes, plan to replace it. Lay the shaft flat on a bench.',
        why: 'The axle shaft must be on a bench for press work. Pulling it cleanly avoids damaging the inner seal and the splines.',
        tip: 'Rotate the shaft slowly as you pull to align the splines with the side gear teeth in the differential.',
        commonMistake: 'Forcing the shaft sideways during withdrawal — this can chip the differential side gear teeth.',
      },
      {
        number: 5,
        title: 'Inspect and Decide on Replacement',
        description:
          'With the shaft on the bench, hold the inner shaft in one hand and the stub shaft in the other. Wiggle them in all directions. Any visible play, gritty feel, or rough rotation means the joint is worn and must be replaced. If the joint feels tight and smooth, the click was from somewhere else (CV-style outer axles, ball joints, or hub bearing) — reinstall and re-diagnose.',
        why: 'A confirmed-bad joint is the only one worth pressing out. If you press out a good joint, you risk ruining the yoke ears in the process.',
        tip: 'Note the orientation of the joint and which way the grease zerk faces (if greasable). Take a photo for reference during reinstallation.',
        commonMistake: 'Replacing a joint that is not actually failed. Always confirm play or roughness before pressing.',
      },
      {
        number: 6,
        title: 'Remove Snap Rings and Press Out Old Joint',
        description:
          'Use internal snap-ring pliers to remove the four small C-clips (snap rings) holding the bearing caps in the yoke ears. Set up the shaft on the press with one yoke ear supported on the press anvil and a socket (slightly smaller than the cap diameter) on the opposite cap. Press the cap straight down through the yoke until the opposite cap pushes out enough to grab with pliers and remove. Flip and repeat for the remaining caps. Once all four caps are out, the cross/spider lifts free.',
        why: 'The press is the only safe way to remove a stuck U-joint. The snap rings retain the caps in the yoke; without removing them, the caps cannot move.',
        tip: 'If a snap ring will not come out, tap the bearing cap inward slightly with a brass drift — this relieves spring pressure on the ring.',
        commonMistake: 'Hammering the joint out with a punch. This deforms the yoke ears and the new joint will not seat correctly.',
      },
      {
        number: 7,
        title: 'Install New Joint and Caps',
        description:
          'Clean the yoke ear bores with brake cleaner. Inspect the bores for damage — any nicks or burrs must be dressed with fine emery cloth. Insert the new cross into the yoke. Place a new bearing cap onto one trunnion, ensuring no needle bearings have fallen over inside the cap. Press the cap into the yoke ear using the press and a properly sized socket. Stop when the snap-ring groove is fully exposed. Install the snap ring. Repeat for each of the four caps, alternating sides to keep the cross centered.',
        why: 'Each cap must press in straight, far enough to expose the snap-ring groove but not so far that the trunnion bottoms in the cap. Alternating sides keeps the cross from cocking.',
        tip: 'Hold the cap with a finger as you start the press to keep the needle bearings from tipping. If a needle falls inside the cap, the joint will bind on installation — reset and try again.',
        commonMistake: 'Pressing too far. If you crush a cap against the cross, you have ruined the joint and must start over with another new joint.',
      },
      {
        number: 8,
        title: 'Verify Joint Moves Freely',
        description:
          'With all four caps installed and snap rings seated, articulate the joint through its full range of motion in both planes. It should move smoothly with no binding. If it binds, tap each cap lightly outward with a brass drift and a hammer — this can free a slightly over-pressed cap. Check articulation again. If the joint binds in one specific position, a needle bearing may have tipped — disassemble and start over.',
        why: 'A binding joint will fail quickly and may damage the differential. Free articulation in all directions is the pass criterion.',
        tip: 'If greasable, pump a few shots of grease through the zerk now until you see fresh grease appear at all four caps.',
        commonMistake: 'Skipping the articulation check. A bound joint installed in the vehicle will destroy itself within a few hundred miles.',
      },
      {
        number: 9,
        title: 'Reinstall Axle Shaft and Hub',
        description:
          'Apply a thin film of grease to the inner shaft splines. Slide the shaft back into the housing, rotating slowly to engage the differential side gear. Reinstall the unit hub onto the knuckle, torquing the three hub-to-knuckle bolts to 102 ft-lbs (138 Nm). Reinstall the rotor and brake caliper bracket (caliper bracket bolts to 74 ft-lbs / 100 Nm). Hand-thread the axle nut, then torque to 175 ft-lbs (237 Nm). Install a new cotter pin.',
        why: 'Correct torques on the hub bolts and axle nut maintain bearing preload and prevent the wheel from working loose. The cotter pin is the redundant retention if the nut backs off.',
        tip: 'Spin the rotor by hand after each torque step — if it suddenly resists or makes noise, stop and investigate before completing the assembly.',
        commonMistake: 'Reusing the cotter pin. They are one-time-use safety items and cost less than $1 — never reuse.',
      },
      {
        number: 10,
        title: 'Reinstall Wheel and Test Drive',
        description:
          'Mount the wheel and torque the lugs in a star pattern to 95 ft-lbs (129 Nm). Lower the vehicle. Pump the brake pedal several times to set the pads. Take a slow test drive in a parking lot — make several tight figure-eight turns at low speed, listening for clicks. Engage 4WD-Hi briefly and turn slowly to load the joint. Listen and feel. After 50 miles, recheck the axle nut torque.',
        why: 'Tight low-speed turns are where the original click occurred — repeating that condition confirms the repair. The torque recheck catches any settling of the new components.',
        tip: 'Do the test drive on a quiet residential street with windows down so you can hear any unusual sounds.',
        commonMistake: 'Skipping the 50-mile recheck. The hub and bearing assembly can settle and the axle nut can lose preload — a quick check prevents wheel-loosening events.',
      },
    ],
    torqueSpecifications: [
      {
        fastener: 'Hub-to-knuckle bolts (Dana 30)',
        specification: '102 ft-lbs (138 Nm)',
        notes: 'Three bolts per hub. Apply medium-strength threadlocker.',
      },
      {
        fastener: 'Axle nut (front)',
        specification: '175 ft-lbs (237 Nm)',
        notes: 'New cotter pin required after torquing.',
      },
      {
        fastener: 'Brake caliper bracket bolts',
        specification: '74 ft-lbs (100 Nm)',
        notes: 'Apply medium-strength threadlocker.',
      },
      {
        fastener: 'Wheel lug nuts',
        specification: '95 ft-lbs (129 Nm)',
        notes: 'Star pattern. Recheck after 50 miles.',
      },
    ],
    fluidSpecifications: [
      {
        component: 'U-joint grease (greasable joints)',
        specification: 'High-temp lithium-complex wheel bearing grease',
        capacity: 'Pump until fresh grease appears at all 4 caps',
        notes: 'NLGI #2 grade. Mopar 04318062 or Mobil 1 Synthetic Grease are excellent.',
      },
    ],
    troubleshooting: [
      {
        problem: 'Click persists after replacement',
        likelyCause: 'Joint installed with a tipped needle bearing, or click was from another component (ball joints, outer CV-style joint, hub bearing)',
        whatToCheck: 'Lift wheel and check for play at the ball joints (12 and 6 o\'clock), wheel bearing (3 and 9 o\'clock), and tie rod end. Check the joint articulation off-vehicle if accessible.',
        fix: 'If play is at another joint, replace that component. If the new U-joint is at fault, disassemble and reinstall with attention to needle bearing seating.',
      },
      {
        problem: 'New joint binds when articulated',
        likelyCause: 'Cap pressed too far OR a needle bearing tipped during cap installation',
        whatToCheck: 'Tap each cap lightly outward with a brass drift; if binding clears, the cap was over-pressed. If binding remains, a needle has tipped.',
        fix: 'Disassemble the binding side, inspect for tipped needles, install a fresh joint if any needles are damaged.',
      },
      {
        problem: 'Vibration in 4WD after repair',
        likelyCause: 'Joint installed slightly out of phase, or axle shaft damaged during removal',
        whatToCheck: 'Inspect the axle shaft for visible bow or bend. Verify the joint cross is centered in the yoke (caps press to equal depth on each side).',
        fix: 'Re-center the joint by tapping caps with a brass drift. Replace a bent shaft.',
      },
      {
        problem: 'Grease leaks from new joint within first 100 miles',
        likelyCause: 'Cap seal damaged during installation, or a needle bearing tipped and lifted the cap',
        whatToCheck: 'Look for grease around each cap. The cap that leaks is the one with the damaged seal or tipped needle.',
        fix: 'Replace the joint. A leaking cap will fail quickly because the bearing runs dry.',
      },
    ],
    proTips: [
      'Buy two joints if you plan to do both sides — a single joint failure on one side often means the other side is close behind.',
      'Use a pickle fork-style U-joint press tool (OTC 7894 or similar) if you do not have a hydraulic press — it works on the vehicle and saves shaft removal time, but is harder on the yoke.',
      'When pressing in the new caps, alternate sides every quarter inch — this prevents the cross from binding diagonally in the yoke.',
      'Greasable joints last longer in off-road service because you can purge mud and water by adding fresh grease until contamination exits.',
    ],
    successIndicators: [
      'No clicking during tight turns at low speed in 2WD or 4WD',
      'No vibration in 4WD',
      'Joint articulates freely when manipulated by hand off-vehicle',
      'No grease appearing around the joint after 100 miles of driving',
    ],
    whenToSeekHelp: 'If you do not have access to a press or proper U-joint tool, or if the yoke ears show damage from a previous failed joint, professional service is required to avoid damaging the axle shaft.',
    legalDisclaimer: 'IMPORTANT SAFETY NOTICE: A failed front axle U-joint can lock the front wheel or send debris through the differential at speed, potentially causing loss of control. Always confirm joint articulation before reinstallation. Replace cotter pins; never reuse them. If you are uncertain about any procedure, consult a qualified professional.',
  },
  {
    id: 'tj-rear-driveshaft-u-joint-replacement',
    title: 'Rear Driveshaft U-Joint Replacement',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [
        { startYear: 2003, endYear: 2006 }
      ],
    },
    video: {
      youtubeId: '2L4iDXsdPwo',
      title: 'Replacing Rear Driveshaft U Joints on a Jeep TJ',
      channel: 'Atlas Expeditions',
    },
    source: 'Original guide created through independent research of TJ driveshaft service procedures, technician forums, and OEM service documentation. Verified through the Automotive Repair Guide Generation System v9 methodology.',
    category: 'Engine & Drivetrain',
    difficulty: 'intermediate',
    duration: '1.5-2 hours (both joints)',
    interval: 'When clunk is heard during gear engagement, vibration develops at highway speed, or rust streaks appear at any joint cap',
    description:
      'The TJ rear driveshaft uses two single-Cardan U-joints — one at the transfer case slip yoke and one at the rear differential pinion yoke. Both joints share grease and miles, so when one fails the other is typically close behind. Symptoms of failure include a sharp clunk on take-off (most noticeable shifting from drive to reverse), vibration that increases with speed, and a squeaking under the vehicle that varies with road speed. Visual inspection often reveals rust-colored streaks emanating from a failed cap — that is dried grease that escaped past the seal as the bearings wore. Replacement is a satisfying job with the right tools.',
    overview: {
      difficulty: 'Intermediate — driveshaft removal, press work on a bench',
      estimatedTime: '1.5 to 2 hours for both joints',
      estimatedCost: '$30-50 for two Spicer joints vs $250-350 at a shop',
      whyMatters: 'A failed driveshaft U-joint can separate at speed, dropping the driveshaft onto the road. At minimum this strands the vehicle; at worst the dropped shaft can pole-vault and cause severe underbody damage or vehicle upset.',
      safetyNote: 'Pressing U-joint caps can launch parts. Always wear safety glasses. Support the driveshaft solidly during removal — it weighs 15-20 lbs and can swing if released suddenly. Never get under a vehicle supported only by a jack.',
    },
    tools: [
      'Socket wrench set (8mm 12-point or appropriate strap-bolt socket; 13mm)',
      'Torque wrench',
      'Hydraulic press OR U-joint tool (e.g., OTC 7894 series)',
      'Internal snap-ring pliers',
      'Bench vise (5" minimum)',
      'Brass drift or punch',
      'Ball peen hammer',
      'Paint pen or marker',
      'Brake cleaner',
      'High-temp wheel bearing grease',
      'Jack and jack stands',
      'Drive shaft support (transmission jack or assistant)',
      'Safety glasses and nitrile gloves',
    ],
    parts: [
      {
        id: 'rear-ds-ujoint',
        name: 'Rear Driveshaft U-Joint (1310 series)',
        partNumber: 'Spicer 5-1310X (premium, non-greasable) or 5-153X (greasable)',
        quantity: 2,
        price: 22,
        category: 'required',
        notes: 'TJ Wrangler rear driveshafts use the 1310 series. Always replace both joints at the same time — the labor is the same and the second joint is close behind.',
      },
      {
        id: 'strap-bolts',
        name: 'Driveshaft Strap Bolts (if equipped)',
        partNumber: 'Spicer 2-94-28 (1310 series strap kit)',
        quantity: 1,
        price: 8,
        category: 'optional',
        notes: 'Some TJs use straps and bolts at the pinion yoke; others use U-bolts. Inspect before ordering. Replace if any bolt was previously stretched.',
      },
      {
        id: 'wheel-bearing-grease-rear-ds',
        name: 'High-Temp Wheel Bearing Grease',
        partNumber: 'Mopar 04318062 or Mobil 1 Synthetic Grease',
        quantity: 1,
        price: 8,
        category: 'required',
        notes: 'Pre-pack greasable joints; light coat on cap exteriors for non-greasable joints to ease pressing.',
      },
    ],
    safetyInformation:
      'A driveshaft on the press is a long lever — keep your hands clear of the ends and the press throat. Strap or U-bolt fasteners are torqued precisely; over-torquing crushes the cap. Use jack stands rated for the vehicle weight on flat concrete. Set the parking brake on the front wheels only after the driveshaft is removed (otherwise the rear brakes are inactive without driveshaft engagement on some configurations) — chock the front wheels for redundancy.',
    beforeYouStart: [
      'Confirm the symptom is from the driveshaft U-joints — clunk on engagement, vibration that varies with road speed (not engine speed), and squeaking from underneath the vehicle are the diagnostic signature',
      'Inspect each joint cap for rust streaks (dried grease) and play (grab a yoke and try to twist it relative to the shaft — any movement means the joint is worn)',
      'Verify your driveshaft is the 1310 series — measure cap-to-cap length and cap diameter to confirm before purchasing the joint',
      'Have a paint pen ready before starting — driveshaft orientation must be marked before disassembly to preserve dynamic balance',
      'Plan to have an assistant or transmission jack available — the rear driveshaft is heavier than it looks',
    ],
    steps: [
      {
        number: 1,
        title: 'Lift Vehicle, Set Parking Brake on Front Tires, Mark Driveshaft',
        description:
          'Park on level concrete, chock the front wheels, set the parking brake. Lift the rear of the vehicle and support on jack stands. With the rear axle hanging, you can rotate the driveshaft by hand. Use a paint pen to make a clear mark across the pinion yoke, the U-joint yoke, and onto the driveshaft tube — this preserves dynamic balance orientation.',
        why: 'The driveshaft is balanced as a unit. Reinstalling 180 degrees off can introduce a vibration. Mark before removal — never assume you will remember the orientation.',
        tip: 'Make the mark a single straight line that crosses from yoke to shaft — at reinstall, line the marks back up.',
        commonMistake: 'Skipping the mark and trying to "eyeball" orientation later. Driveshaft balance is sensitive enough that this often produces a vibration that was not there before.',
      },
      {
        number: 2,
        title: 'Disconnect Pinion-End Joint',
        description:
          'Locate the four bolts (or two U-bolts) holding the U-joint cap straps to the pinion yoke. Loosen and remove. Once the straps are off, the joint cap will sit free in the yoke saddles. Carefully push the driveshaft forward (toward the transfer case) to disengage the joint from the pinion yoke. Catch the bearing caps as the joint releases — wrap them with a rubber band or zip tie to keep them on the cross during transport.',
        why: 'Without the strap retention, the bearing caps can fall off the cross and lose needle bearings. Securing them now prevents lost parts and contamination.',
        tip: 'Use an 8mm 12-point socket on most TJ strap bolts. Apply penetrating oil 15 minutes before loosening — these bolts are exposed to road salt.',
        commonMistake: 'Letting the caps fall off and lose needle bearings. Even if you catch the cap, a single tipped needle inside the cap will cause binding when reinstalled.',
      },
      {
        number: 3,
        title: 'Slide Driveshaft Out of Transfer Case',
        description:
          'With the pinion end free, support the rear of the driveshaft and pull straight back to slide the slip yoke out of the transfer case. A small amount of transfer case fluid will weep out — have a rag ready. Lower the driveshaft and bring it to a workbench. Note: the slip yoke at the transfer case end usually does not have a U-joint at that end on TJs (it slips into the transfer case directly), but the front yoke of the rear shaft does carry the rear-shaft transfer-case-end U-joint.',
        why: 'Removing the entire shaft to a bench gives controlled access to both joints with proper press support.',
        tip: 'Wrap a rag around the slip yoke splines as you withdraw to keep the splines clean and to soak up drip.',
        commonMistake: 'Letting the slip yoke drag out and ding the transfer case output seal. Pull straight, slowly, and support the weight throughout.',
      },
      {
        number: 4,
        title: 'Remove Snap Rings on First Joint',
        description:
          'Place the driveshaft in the bench vise — clamp gently on the tube with vise jaws padded with wood or aluminum to prevent denting. Use internal snap-ring pliers to remove the four C-clips on the first U-joint (one snap ring per cap). Set them aside. If a snap ring resists, tap the bearing cap inward with a brass drift to relieve spring tension on the ring.',
        why: 'The snap rings retain the caps in the yoke ears. Removing them allows the press to push the caps out without damaging the yoke.',
        tip: 'Photograph the joint orientation before disassembly so you can install the new joint in the same rotational position relative to the shaft.',
        commonMistake: 'Crushing the driveshaft tube in a hard vise. Always use soft jaws or wood blocks.',
      },
      {
        number: 5,
        title: 'Press Out Old Caps',
        description:
          'Set up the joint on the press: support one yoke ear on the press anvil, place a socket on the opposite cap that is just smaller than the cap diameter. Press straight down — the opposite cap will push out the bottom. Once exposed, grab the cap with pliers and remove. Flip the shaft and repeat for the remaining three caps. With all caps out, the cross lifts free.',
        why: 'The press applies even, controlled force. Hammering distorts yoke ears and ruins the new joint.',
        tip: 'Sometimes the cap is rust-bonded into the yoke. A few drops of penetrating oil and a pre-heat with a propane torch will free a stubborn cap (heat the yoke ear, not the cap itself).',
        commonMistake: 'Pressing with a socket that is too small — it slips off the cap and damages the yoke. Use a socket that is just under the cap OD.',
      },
      {
        number: 6,
        title: 'Clean and Inspect Yoke Ears',
        description:
          'Use brake cleaner to flush the yoke bores. Inspect for nicks, burrs, or galling. Dress any imperfections with fine emery cloth. Inspect the yoke ears for cracks — any crack means the yoke is condemned and the entire driveshaft must be replaced. Wipe the bores dry.',
        why: 'A new joint will not seat correctly in damaged yoke ears. Catching a cracked yoke now prevents a catastrophic failure later.',
        tip: 'Run a finger around each bore — your finger will catch on any imperfection that the eye missed.',
        commonMistake: 'Skipping inspection and pressing a new joint into a damaged yoke. The new joint will fail prematurely or bind on installation.',
      },
      {
        number: 7,
        title: 'Press in New Caps and Install Snap Rings',
        description:
          'Insert the new cross into the yoke. Place a new bearing cap on a trunnion, ensuring no needles have tipped. Press straight in until the snap-ring groove is exposed. Install the snap ring. Repeat for each remaining cap, alternating sides to keep the cross centered. Articulate the joint after each cap to verify it stays free.',
        why: 'Alternating sides prevents the cross from cocking diagonally. Free articulation after each cap confirms the previous installations are correct.',
        tip: 'If the joint stiffens up during installation, tap each cap outward with a brass drift to free a slightly over-pressed cap — better than disassembling.',
        commonMistake: 'Crushing a cap by pressing too far. Watch for the snap-ring groove to fully clear; that is the stop point.',
      },
      {
        number: 8,
        title: 'Replace Second Joint',
        description:
          'Repeat steps 4 through 7 for the second U-joint (the one at the opposite end of the driveshaft). Both joints should be replaced together — the labor is identical and the second is typically near failure when the first goes.',
        why: 'Doing both joints in one session is far more efficient than coming back later, and ensures balanced wear going forward.',
        tip: 'Mark the second joint orientation before disassembly the same way you marked the first.',
        commonMistake: 'Replacing only the obviously failed joint. The other one is on the same age and miles — within 5-10k miles, you will be back here.',
      },
      {
        number: 9,
        title: 'Reinstall Driveshaft to Transfer Case and Pinion',
        description:
          'Wipe the slip yoke splines clean and apply a light film of fresh grease. Slide the slip yoke back into the transfer case rear seal, taking care not to damage the seal. Align the paint marks at the pinion end and seat the U-joint into the pinion yoke saddles. Reinstall the straps (or U-bolts) and torque to 38 ft-lbs (52 Nm). Verify both straps are seated evenly.',
        why: 'Aligning the paint marks preserves the original balance. Even strap torque keeps the joint cap from cocking.',
        tip: 'Rotate the driveshaft by hand after install — it should spin freely with no binding or rough spots.',
        commonMistake: 'Over-torquing the strap bolts. Stretching them past spec crushes the bearing cap and ruins the new joint.',
      },
      {
        number: 10,
        title: 'Lower Vehicle and Test Drive',
        description:
          'Lower the vehicle. Check the transfer case fluid level — top off if any was lost during slip-yoke removal. Take a slow test drive. Engage and disengage drive/reverse from a stop, listening for the original clunk. Accelerate to highway speed and listen for vibration. Recheck the strap bolts for torque after 50 miles.',
        why: 'The original symptom was clunk on engagement and vibration at speed — both conditions need to be checked to confirm the repair.',
        tip: 'Drive over a few small bumps slowly — any squeak, clunk, or rattle from underneath should be investigated.',
        commonMistake: 'Skipping the 50-mile recheck. Strap bolts can settle and lose torque; a quick recheck prevents driveshaft separation.',
      },
    ],
    torqueSpecifications: [
      {
        fastener: 'Driveshaft strap bolts (1310 series)',
        specification: '38 ft-lbs (52 Nm)',
        notes: 'Torque evenly across all four bolts. Do not over-torque — crushes the bearing cap.',
      },
      {
        fastener: 'Driveshaft U-bolt nuts (alternative configuration)',
        specification: '14 ft-lbs (19 Nm)',
        notes: 'If your TJ uses U-bolts at the pinion yoke instead of straps. Torque alternately to seat evenly.',
      },
    ],
    fluidSpecifications: [
      {
        component: 'U-joint grease (greasable joints)',
        specification: 'High-temp lithium-complex wheel bearing grease',
        capacity: 'Pump until fresh grease appears at all 4 caps',
        notes: 'NLGI #2 grade.',
      },
      {
        component: 'Transfer case fluid (top-off after slip yoke removal)',
        specification: 'Mopar ATF+4 (NV231/NP231 transfer case)',
        capacity: 'Top off as needed; full case is approximately 1.0 quart',
        notes: 'Check level at the fill plug after reinstallation.',
      },
    ],
    troubleshooting: [
      {
        problem: 'Vibration at highway speed after replacement',
        likelyCause: 'Driveshaft installed 180 degrees out of phase, or new joint binding',
        whatToCheck: 'Inspect paint marks at the pinion end — they should be aligned. Off-vehicle, articulate each joint and feel for any rough spot.',
        fix: 'If marks are misaligned, remove and reinstall correctly. If a joint binds, tap the caps outward to free, or replace if a needle has tipped.',
      },
      {
        problem: 'Clunk persists after replacement',
        likelyCause: 'Slack at the slip-yoke splines, worn pinion bearings, or worn axle splines',
        whatToCheck: 'With the driveshaft installed, twist by hand at the pinion end and feel for backlash. Excessive backlash that is not at the U-joints points to the rear differential or axle.',
        fix: 'Investigate the differential bearings or axle splines. The U-joint replacement was correct but the symptom was from elsewhere.',
      },
      {
        problem: 'Squeak from driveshaft after install',
        likelyCause: 'Dry slip-yoke splines, or a greasable joint not yet greased',
        whatToCheck: 'Pull the driveshaft and inspect the slip-yoke splines for grease. If greasable, check that grease has been pumped through.',
        fix: 'Re-grease slip-yoke splines and pump fresh grease through any greasable joint until grease appears at all four caps.',
      },
      {
        problem: 'Strap bolt loosens within first 100 miles',
        likelyCause: 'Bolt stretched in prior service, or threadlocker not used',
        whatToCheck: 'Inspect bolt threads for stretching (necking down between thread crests) — replace any stretched bolt. Confirm threadlocker was applied at install.',
        fix: 'Replace bolts with new strap kit. Apply medium-strength threadlocker. Torque to 38 ft-lbs (52 Nm).',
      },
    ],
    proTips: [
      'Photograph each joint orientation before disassembly — when reinstalling, you can match the new joint to the original phase.',
      'Buy a 4-pack of strap bolts even if yours look fine; they cost a few dollars and stretch under torque, so a fresh set is cheap insurance.',
      'A double-cardan rear driveshaft (some lifted TJs) has a different procedure at the transfer case end — verify your shaft type before ordering parts.',
      'Greasable joints last longer in deep-water and mud service because you can purge contamination — a strong choice for off-road TJs.',
    ],
    successIndicators: [
      'No clunk when shifting between drive and reverse',
      'No vibration at highway speed',
      'No squeaking from underneath at any speed',
      'Strap bolt torque holds at 50-mile recheck',
    ],
    whenToSeekHelp: 'If the driveshaft yoke ears are cracked, if the slip yoke is heavily worn, or if you do not have access to a press or proper U-joint tool, professional service is required. A driveshaft separation at speed is a serious safety event.',
    legalDisclaimer: 'IMPORTANT SAFETY NOTICE: A driveshaft separation at speed can cause loss of control or severe underbody damage. Always torque strap bolts to spec — never improvise. Mark and align driveshaft orientation to preserve balance. If you are uncertain about any procedure, consult a qualified professional.',
  },
  {
    id: 'tj-front-driveshaft-u-joint-replacement',
    title: 'Front Driveshaft U-Joint Replacement',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [
        { startYear: 2003, endYear: 2006 }
      ],
    },
    video: {
      youtubeId: '2L4iDXsdPwo',
      title: 'Replacing Driveshaft U Joints on a Jeep TJ',
      channel: 'Atlas Expeditions',
    },
    source: 'Original guide created through independent research of TJ front driveshaft service procedures, technician forums, and OEM service documentation. Verified through the Automotive Repair Guide Generation System v9 methodology.',
    category: 'Engine & Drivetrain',
    difficulty: 'intermediate',
    duration: '1.5-2 hours',
    interval: 'When 4WD vibration develops, clunk is heard when shifting into 4-Hi, or grease appears at any joint cap',
    description:
      'The TJ front driveshaft typically has a single Cardan U-joint at the front axle pinion end and a CV-style (double-Cardan) joint at the transfer case end. This guide covers the standard single-Cardan U-joint at the pinion end — the more common service item. The double-Cardan is a separate procedure and is described in its own section. Symptoms of a worn pinion-end U-joint include vibration that appears in 4WD only, clunk when engaging or disengaging 4-Hi, and a squeaking noise from the front of the vehicle that varies with road speed. The job is similar to the rear driveshaft U-joint but typically involves only one joint.',
    overview: {
      difficulty: 'Intermediate — driveshaft removal, press work on a bench',
      estimatedTime: '1.5 to 2 hours',
      estimatedCost: '$15-25 for one Spicer joint vs $200-275 at a shop',
      whyMatters: 'A failed front-driveshaft U-joint at the pinion end can come apart under load in 4WD, dropping the shaft or damaging the front axle pinion seal. Replacement is straightforward and far cheaper than the secondary damage.',
      safetyNote: 'Pressing U-joint caps can launch parts. Wear safety glasses. Support the driveshaft solidly during removal. The front driveshaft is shorter and lighter than the rear but still 10-15 lbs.',
    },
    tools: [
      'Socket wrench set (8mm 12-point or appropriate strap-bolt socket; 13mm)',
      'Torque wrench',
      'Hydraulic press OR U-joint tool',
      'Internal snap-ring pliers',
      'Bench vise (5" minimum)',
      'Brass drift or punch',
      'Ball peen hammer',
      'Paint pen or marker',
      'Brake cleaner',
      'High-temp wheel bearing grease',
      'Jack and jack stands',
      'Safety glasses and nitrile gloves',
    ],
    parts: [
      {
        id: 'front-ds-ujoint',
        name: 'Front Driveshaft U-Joint (1310 series)',
        partNumber: 'Spicer 5-1310X (premium, non-greasable) or 5-153X (greasable)',
        quantity: 1,
        price: 22,
        category: 'required',
        notes: 'TJ Wrangler front driveshafts use the 1310 series at the pinion end. The double-Cardan at the transfer case end has its own part number — verify which joint is failing before ordering.',
      },
      {
        id: 'strap-bolts-front',
        name: 'Driveshaft Strap Bolts (if equipped)',
        partNumber: 'Spicer 2-94-28 (1310 series strap kit)',
        quantity: 1,
        price: 8,
        category: 'optional',
        notes: 'Replace if any bolt was previously stretched.',
      },
      {
        id: 'wheel-bearing-grease-front-ds',
        name: 'High-Temp Wheel Bearing Grease',
        partNumber: 'Mopar 04318062 or Mobil 1 Synthetic Grease',
        quantity: 1,
        price: 8,
        category: 'required',
        notes: 'Pre-pack greasable joints; light coat on cap exteriors for non-greasable joints.',
      },
    ],
    safetyInformation:
      'A driveshaft on the press is a long lever — keep your hands clear of the ends and the press throat. Strap or U-bolt fasteners are torqued precisely; over-torquing crushes the cap. Use jack stands rated for the vehicle weight on flat concrete. Chock the rear wheels because the front driveshaft is being removed.',
    beforeYouStart: [
      'Confirm the symptom is from the front driveshaft — vibration appears only in 4WD, clunk when engaging 4-Hi, or squeak that varies with road speed',
      'Determine which joint is failing — the pinion-end U-joint (this guide) or the transfer-case-end double-Cardan (separate procedure)',
      'Verify the joint is the 1310 series — measure cap-to-cap and cap diameter',
      'Mark the driveshaft orientation before removal with a paint pen',
      'Plan to inspect the double-Cardan joint at the transfer case end while the shaft is out',
    ],
    steps: [
      {
        number: 1,
        title: 'Lift Vehicle, Chock Rear Wheels, Mark Driveshaft',
        description:
          'Park on level concrete, chock the rear wheels, set the parking brake. Lift the front and support on jack stands. Mark across the pinion yoke, the U-joint yoke, and onto the driveshaft tube with a paint pen. Also mark the transfer case yoke to the double-Cardan flange to preserve balance at that end.',
        why: 'The driveshaft is balanced as a unit — orientation must be preserved. Marks at both ends prevent accidental phase-flipping at reinstall.',
        tip: 'Take a photo of each end after marking — provides backup reference.',
        commonMistake: 'Marking only one end. Both ends matter for proper phasing of the entire shaft.',
      },
      {
        number: 2,
        title: 'Disconnect Pinion-End Joint',
        description:
          'Remove the four strap bolts (or two U-bolts) at the front pinion yoke. Support the pinion end of the driveshaft as the bolts come out. Wrap the bearing caps with a rubber band or zip tie before fully releasing the joint. Carefully separate the joint from the pinion yoke.',
        why: 'Securing the caps prevents needle bearings from falling out as the joint comes free.',
        tip: 'Apply penetrating oil to the strap bolts 15 minutes before disassembly.',
        commonMistake: 'Letting caps fall and lose needles. Even a single tipped needle will cause binding when the new joint is installed.',
      },
      {
        number: 3,
        title: 'Disconnect Transfer-Case End',
        description:
          'At the transfer case end, the double-Cardan flange typically attaches with bolts to the transfer case output yoke (not a slip yoke like the rear). Remove these bolts. Support the driveshaft as the last bolt comes out. Bring the entire shaft to the workbench. Note: do NOT disassemble the double-Cardan joint as part of this guide — leave it intact.',
        why: 'Removing the entire shaft to a bench gives controlled access to the failed joint.',
        tip: 'Some TJs have a slip yoke at the transfer case end of the front shaft — if so, slide the slip yoke out instead of unbolting a flange.',
        commonMistake: 'Disassembling the double-Cardan when only the pinion-end U-joint is failed. The double-Cardan has more parts and a specific phasing — leave it alone unless it is the failure.',
      },
      {
        number: 4,
        title: 'Place in Vise, Remove Snap Rings',
        description:
          'Clamp the driveshaft tube in a soft-jaw vise. Use internal snap-ring pliers to remove the four C-clips on the pinion-end U-joint. Set them aside. If a clip resists, tap the cap inward slightly to relieve spring tension.',
        why: 'Snap rings retain the caps. Without them out, the press cannot move the caps.',
        tip: 'Photograph the joint orientation in the yoke before pressing — useful for installing the new joint in the same phase.',
        commonMistake: 'Crushing the tube in a hard vise. Use wood or aluminum soft jaws.',
      },
      {
        number: 5,
        title: 'Press Out Old Caps',
        description:
          'Set up the joint on the press with one yoke ear on the anvil and a properly sized socket on the opposite cap. Press straight down — the opposite cap pushes out. Grab with pliers and remove. Flip and repeat. With all four caps out, the cross lifts free.',
        why: 'The press provides controlled, even force that does not damage the yoke ears.',
        tip: 'For a stuck cap, light heat from a propane torch on the yoke ear (not the cap) expands the yoke and frees the cap.',
        commonMistake: 'Using a too-small socket that slips off the cap. Match the socket to the cap OD.',
      },
      {
        number: 6,
        title: 'Clean and Inspect Yoke Ears',
        description:
          'Brake-clean the yoke bores. Inspect for nicks, burrs, or galling — dress with fine emery cloth. Inspect for cracks — any crack condemns the driveshaft (replace the entire shaft, not just the joint). Wipe dry.',
        why: 'A new joint will not seat in damaged bores. A cracked yoke will fail catastrophically.',
        tip: 'Use a magnifying lens for crack inspection — hairline cracks are easy to miss with the naked eye.',
        commonMistake: 'Pressing a new joint into a damaged bore. Premature failure or binding will result.',
      },
      {
        number: 7,
        title: 'Press in New Caps',
        description:
          'Insert the new cross. Place a new bearing cap on a trunnion. Press straight in until the snap-ring groove is exposed. Install the snap ring. Repeat for each remaining cap, alternating sides to keep the cross centered. Articulate after each cap to verify free movement.',
        why: 'Alternating sides keeps the cross from cocking. Free articulation after each cap confirms correct seating.',
        tip: 'If the joint stiffens during install, tap caps outward gently with a brass drift to relieve.',
        commonMistake: 'Pressing too far and crushing a cap against the cross. Watch for the snap-ring groove to clear and stop.',
      },
      {
        number: 8,
        title: 'Verify Free Articulation',
        description:
          'With all four caps and snap rings installed, articulate the joint through full range of motion in both planes. It must move smoothly. Inspect the double-Cardan joint while you have the shaft on the bench — articulate it and feel for any roughness or play.',
        why: 'A binding joint installed in the vehicle will fail quickly. Inspecting the double-Cardan now lets you address it if needed.',
        tip: 'If the double-Cardan feels rough, plan a follow-up service for that joint as well.',
        commonMistake: 'Skipping the double-Cardan inspection. While the shaft is on the bench is the easiest time to evaluate it.',
      },
      {
        number: 9,
        title: 'Reinstall Driveshaft to Vehicle',
        description:
          'Lift the driveshaft into position. Engage the transfer case end first — align marks, install bolts, torque to 38 ft-lbs (52 Nm). At the pinion end, align marks, seat the joint in the saddles, install straps and torque to 38 ft-lbs (52 Nm). Verify all bolts are evenly seated.',
        why: 'Aligning the marks at both ends preserves balance. Even torque keeps the bearing caps from cocking.',
        tip: 'Apply medium-strength threadlocker to all driveshaft bolts.',
        commonMistake: 'Over-torquing strap bolts. Crushed cap = ruined joint.',
      },
      {
        number: 10,
        title: 'Lower Vehicle and Test in 4WD',
        description:
          'Lower the vehicle. Take a slow test drive. With the front hubs engaged (or auto), shift into 4-Hi on a gravel surface and accelerate to 30-40 mph, listening and feeling for vibration. Shift back to 2WD and confirm the symptom is gone. Recheck strap bolt torque after 50 miles.',
        why: 'The original symptom appeared only in 4WD — confirming silence in 4WD is the pass criterion.',
        tip: 'Most TJs require driving forward briefly after engaging 4-Hi to fully engage the front axle — engage and drive a few feet before testing.',
        commonMistake: 'Skipping the 50-mile recheck. Strap bolt settling is real and can lead to driveshaft separation if ignored.',
      },
    ],
    torqueSpecifications: [
      {
        fastener: 'Driveshaft strap bolts (1310 series)',
        specification: '38 ft-lbs (52 Nm)',
        notes: 'Torque evenly. Do not over-torque.',
      },
      {
        fastener: 'Driveshaft U-bolt nuts (alternative)',
        specification: '14 ft-lbs (19 Nm)',
        notes: 'If equipped instead of straps.',
      },
    ],
    fluidSpecifications: [
      {
        component: 'U-joint grease (greasable joints)',
        specification: 'High-temp lithium-complex wheel bearing grease',
        capacity: 'Pump until fresh grease appears at all 4 caps',
        notes: 'NLGI #2 grade.',
      },
    ],
    troubleshooting: [
      {
        problem: 'Vibration in 4WD persists after replacement',
        likelyCause: 'Double-Cardan joint at transfer case end is also worn, or driveshaft installed out of phase',
        whatToCheck: 'Inspect the double-Cardan for play. Confirm paint marks at both ends are aligned.',
        fix: 'Replace double-Cardan joint if worn. Reinstall driveshaft with marks aligned if phase is wrong.',
      },
      {
        problem: 'Clunk when engaging 4-Hi after replacement',
        likelyCause: 'Front axle disconnect (CAD) issue, or pinion bearing slack',
        whatToCheck: 'Listen for the clunk location — if it is at the front axle housing rather than the driveshaft, the issue is at the pinion or CAD.',
        fix: 'Investigate the front pinion or CAD operation. The U-joint replacement was correct but the symptom was from elsewhere.',
      },
      {
        problem: 'Squeak from front driveshaft after install',
        likelyCause: 'Greasable joint not yet greased, or slip yoke (if equipped) splines dry',
        whatToCheck: 'Check the new joint for grease purging at all 4 caps.',
        fix: 'Pump fresh grease until visible at all 4 caps.',
      },
      {
        problem: 'New joint feels stiff to articulate',
        likelyCause: 'A cap was pressed slightly too far, or a needle has tipped',
        whatToCheck: 'Tap each cap outward with a brass drift; if stiffness clears, that was the issue. If not, a needle has tipped.',
        fix: 'If brass-drift relief works, you are done. If a needle tipped, disassemble and replace the joint.',
      },
    ],
    proTips: [
      'Inspect the double-Cardan joint while you have the shaft on the bench — replacing it later requires another full driveshaft removal.',
      'A photo of the original joint orientation in the yoke makes installing the new joint in the same phase trivially easy.',
      'Greasable U-joints purge contamination — a strong choice for any TJ that sees water crossings or mud.',
      'When ordering, check for "1310" series stamping on the cross. Some aftermarket front driveshafts use 1330 or 1350 series — wrong size means wrong joint.',
    ],
    successIndicators: [
      'No vibration in 4WD',
      'No clunk when engaging or disengaging 4-Hi',
      'No squeak from front driveshaft area',
      'Strap bolt torque holds at 50-mile recheck',
    ],
    whenToSeekHelp: 'If the double-Cardan joint at the transfer case end is the failed joint, that is a different procedure with specific phasing requirements — consider professional service. If the driveshaft yoke shows cracks, the entire shaft must be replaced.',
    legalDisclaimer: 'IMPORTANT SAFETY NOTICE: A failed front driveshaft U-joint can separate at speed in 4WD, dropping the shaft and potentially damaging the front pinion seal or undercarriage. Always confirm joint articulation before reinstallation and torque all fasteners to spec. If you are uncertain about any procedure, consult a qualified professional.',
  },
  {
    id: 'tj-steering-shaft-u-joint-replacement',
    title: 'Steering Shaft U-Joint Replacement (Intermediate Shaft)',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [
        { startYear: 2003, endYear: 2006 }
      ],
    },
    video: {
      youtubeId: '2L4iDXsdPwo',
      title: 'Replacing Driveshaft U Joints on a Jeep TJ',
      channel: 'Atlas Expeditions',
    },
    source: 'Original guide created through independent research of TJ steering shaft service procedures, technician forums, and OEM service documentation. Verified through the Automotive Repair Guide Generation System v9 methodology.',
    category: 'Suspension & Steering',
    difficulty: 'intermediate',
    duration: '1-1.5 hours',
    interval: 'When steering wheel free play exceeds 1 inch, clunking is felt over bumps, or popping is heard when turning the steering wheel',
    description:
      'The TJ Wrangler uses an intermediate steering shaft (also called a lower steering shaft or steering coupler) between the steering column and the steering box. This shaft contains two universal joints that allow the steering input to follow a non-straight path between the firewall and the box. When these joints wear, drivers experience a distinctive set of symptoms: 1-2 inches of free play at the steering wheel, a clunk over bumps, popping or notchy feel when turning, and a "wandering" feel on the highway. The TJ shaft is typically replaced as a complete assembly because the U-joints are not user-serviceable on most factory units. Aftermarket replacements (often heavy-duty Borgeson units) are a common upgrade.',
    overview: {
      difficulty: 'Intermediate — tight access, steering-related precision required',
      estimatedTime: '1 to 1.5 hours',
      estimatedCost: '$60-180 for shaft assembly (factory ~$60, Borgeson HD ~$160-180) vs $250-400 at a shop',
      whyMatters: 'Worn steering U-joints introduce free play and unpredictable steering response — a real safety issue at highway speed and on twisty roads. The fix is one of the highest-value steering improvements you can make.',
      safetyNote: 'Disconnect the battery before working near the steering column to disable the airbag system. Verify the steering wheel stays centered during the procedure — incorrect reinstallation causes off-center steering and clock-spring damage.',
    },
    tools: [
      'Socket wrench set (10mm, 13mm, 15mm)',
      'Torque wrench',
      'Allen wrench set (4mm, 5mm)',
      'Pry bar (small)',
      'Penetrating oil (PB Blaster or similar)',
      'Soft-faced hammer (rubber or dead-blow)',
      'Paint pen or marker',
      'Steering wheel lock or clamp (optional)',
      'Safety glasses and nitrile gloves',
    ],
    parts: [
      {
        id: 'steering-shaft-assy',
        name: 'Intermediate Steering Shaft Assembly',
        partNumber: 'Mopar 55351225AC (factory) or Borgeson 000937 (heavy-duty upgrade)',
        quantity: 1,
        price: 90,
        category: 'required',
        notes: 'The factory shaft has rubber-isolated U-joints that wear quickly off-road. The Borgeson 000937 is a popular heavy-duty all-steel upgrade that lasts much longer.',
      },
      {
        id: 'steering-shaft-pinch-bolts',
        name: 'Steering Shaft Pinch Bolts (replace if corroded)',
        partNumber: 'Generic M8 x 1.25 grade 10.9',
        quantity: 2,
        price: 4,
        category: 'optional',
        notes: 'Replace if heavily corroded. New bolts are inexpensive insurance.',
      },
    ],
    safetyInformation:
      'The airbag system is powered by the steering column — disconnect the battery and wait at least 5 minutes before working near the column to allow the airbag capacitor to discharge. Always work with the steering wheel centered and locked to prevent clock-spring damage. Steering components are safety-critical — use a torque wrench, never approximate. Wear safety glasses; debris falls when reaching up under the dash.',
    beforeYouStart: [
      'Confirm the symptom is at the intermediate shaft U-joints — grasp the shaft with the engine off and try to twist it. Any visible play at either joint confirms wear',
      'Center the steering wheel before disconnecting anything — mark the position with a piece of tape',
      'Disconnect the battery negative cable and wait 5 minutes for the airbag capacitor to discharge',
      'Have penetrating oil ready — the lower pinch bolt at the steering box is exposed to road salt',
      'Decide on factory vs Borgeson upgrade before ordering',
    ],
    steps: [
      {
        number: 1,
        title: 'Center Steering Wheel and Disconnect Battery',
        description:
          'With the vehicle on flat ground, drive briefly forward to center the wheels naturally. Turn off the engine. Use a piece of tape across the steering wheel and dash to mark the centered position. Disconnect the battery negative cable. Wait at least 5 minutes for the airbag capacitor to discharge.',
        why: 'A centered wheel is required to preserve clock-spring orientation. Battery disconnect prevents airbag deployment if a wire is accidentally pinched.',
        tip: 'Use a steering wheel lock or seat-belt-clamp to prevent the wheel from turning while the shaft is out.',
        commonMistake: 'Skipping the centering step. If the wheel rotates while the shaft is out, the clock spring can be damaged when the new shaft is installed.',
      },
      {
        number: 2,
        title: 'Mark Shaft-to-Column and Shaft-to-Box Orientation',
        description:
          'With a paint pen, mark the upper end of the intermediate shaft to the column shaft, and the lower end to the steering box input shaft. These marks confirm the new shaft is installed in the same rotational position as the old one.',
        why: 'Reinstalling 180 degrees off causes off-center steering and improper clock-spring alignment.',
        tip: 'Take a photo of each end after marking.',
        commonMistake: 'Skipping the marks and trusting that "centered wheel" is enough — the splines have multiple engagement positions.',
      },
      {
        number: 3,
        title: 'Apply Penetrating Oil to Pinch Bolts',
        description:
          'Spray PB Blaster or equivalent penetrating oil on both pinch bolts (upper at the column, lower at the steering box). Let soak for 15 minutes. The lower bolt in particular is exposed to road salt and can be very tight.',
        why: 'Stuck bolts can be rounded off if forced. Penetrating oil saves time and protects the threads.',
        tip: 'Apply a second dose after 5 minutes — the oil works better when reapplied.',
        commonMistake: 'Skipping the soak. A rounded pinch bolt at the steering box is a major problem — sometimes requires drilling out.',
      },
      {
        number: 4,
        title: 'Loosen Lower Pinch Bolt at Steering Box',
        description:
          'Locate the lower pinch bolt where the intermediate shaft joins the steering box input shaft. This is at the front of the engine bay, above and slightly forward of the box. Use a 13mm socket on a long extension. Loosen the bolt fully and remove. Save the bolt for inspection — replace if corroded.',
        why: 'The lower pinch bolt locks the shaft to the box input. Removing it allows the shaft to slide off the splines.',
        tip: 'A wobble extension and a long handle give the leverage needed.',
        commonMistake: 'Forcing a stuck bolt and rounding the head. If it does not turn after a 15-minute oil soak, apply heat from a propane torch briefly.',
      },
      {
        number: 5,
        title: 'Loosen Upper Pinch Bolt at Column',
        description:
          'Reach up under the dash on the driver side. Locate the upper pinch bolt where the intermediate shaft joins the steering column. Use a 13mm socket. Loosen and remove. The shaft should now be free at both ends but may still be held by the splines.',
        why: 'Both pinch bolts must be removed before the shaft can be slid off either set of splines.',
        tip: 'A flexible-handle ratchet and a flashlight strapped to your forehead make this much easier.',
        commonMistake: 'Loosening the upper bolt only halfway. The pinch must be fully released for the shaft to come off the splines.',
      },
      {
        number: 6,
        title: 'Slide Shaft Off Splines',
        description:
          'With both pinch bolts out, work the intermediate shaft up and off the steering box input shaft (downward motion at the lower end). Then slide the upper end down off the column splines. The shaft will come free as one piece. If it resists, tap with a soft-faced hammer or use a small pry bar gently to spread the pinch slot.',
        why: 'Forcing the splines damages them. A spreader pry bar or light hammer taps free a stuck shaft without damage.',
        tip: 'Some shafts have a slot for a flat screwdriver to spread the pinch — use it if present.',
        commonMistake: 'Hammering directly on the shaft end with a metal hammer — distorts the splines. Use a soft face only.',
      },
      {
        number: 7,
        title: 'Inspect Splines and Compare Shafts',
        description:
          'Inspect both the column splines and the steering box input splines for damage. Run a finger to feel for nicks. Compare the new shaft to the old one — verify length, spline count at each end, and joint type. The new shaft should match the old in all installation dimensions.',
        why: 'Wrong shaft = wrong fit. Mismatched splines damage easily and create dangerous play.',
        tip: 'Lay the old and new side by side on a clean rag and compare end-to-end before installing.',
        commonMistake: 'Skipping the comparison and finding mid-install that the new shaft is the wrong length.',
      },
      {
        number: 8,
        title: 'Install New Shaft — Lower End First',
        description:
          'Slide the new shaft onto the steering box input shaft first, aligning your paint mark or matching the spline orientation noted from the old shaft. The pinch slot must align with the flat or notch on the input shaft. Press down firmly until the shaft is fully seated. Hand-thread the lower pinch bolt.',
        why: 'Engaging the lower end first lets the shaft articulate freely while you align the upper end. Aligning the pinch slot to the input-shaft flat is required for proper clamping.',
        tip: 'A light film of grease on the splines eases installation.',
        commonMistake: 'Forcing the shaft on with the pinch slot misaligned to the input flat. Result: no clamping force, shaft slips on the box.',
      },
      {
        number: 9,
        title: 'Engage Upper End and Torque Pinch Bolts',
        description:
          'Slide the upper end of the new shaft onto the column splines, aligning your paint mark. Verify the steering wheel is still centered. Hand-thread the upper pinch bolt. Once both bolts are threaded, torque the lower pinch bolt at the steering box to 36 ft-lbs (49 Nm) and the upper pinch bolt at the column to 21 ft-lbs (28 Nm).',
        why: 'Both pinch bolts hold the shaft to the splines under steering torque. Correct torque prevents both slipping (under-torque) and stress fracturing (over-torque).',
        tip: 'Confirm the steering wheel is still centered before torquing the upper bolt — if it moved, lift the shaft off the column splines, recenter, and re-engage.',
        commonMistake: 'Torquing the upper bolt with the wheel off-center. The result is a steering wheel that points one way while the wheels go straight.',
      },
      {
        number: 10,
        title: 'Reconnect Battery and Test',
        description:
          'Reconnect the battery negative. Start the engine. Verify no airbag warning light remains on (if so, the system needs a clear with a scan tool). With the vehicle stationary, turn the wheel lock-to-lock and feel for any binding. Take a slow test drive on flat road, then highway speed, and confirm: no clunk over bumps, no popping when turning, no free play at the wheel, and the wheel returns to center cleanly after a turn.',
        why: 'These four checks confirm the new shaft is installed correctly and centered. An airbag light or off-center wheel must be addressed before normal driving.',
        tip: 'Have the alignment checked within 100 miles if the steering feel changed significantly — the new shaft can subtly change the centered position.',
        commonMistake: 'Driving with an airbag light on. The airbag will not deploy in a crash — this is a serious safety issue and must be cleared promptly.',
      },
    ],
    torqueSpecifications: [
      {
        fastener: 'Lower steering shaft pinch bolt (at steering box)',
        specification: '36 ft-lbs (49 Nm)',
        notes: 'Critical for correct clamp force. Use a torque wrench.',
      },
      {
        fastener: 'Upper steering shaft pinch bolt (at column)',
        specification: '21 ft-lbs (28 Nm)',
        notes: 'Less than the lower bolt — do not over-torque.',
      },
    ],
    fluidSpecifications: [
      {
        component: 'Spline lubrication',
        specification: 'Light film of high-temp grease',
        capacity: 'Thin coat only',
        notes: 'Helps installation; do not pack heavily.',
      },
    ],
    troubleshooting: [
      {
        problem: 'Steering wheel is off-center after installation',
        likelyCause: 'Shaft installed one or more spline-positions off',
        whatToCheck: 'Look at the paint marks — if they are not aligned, the shaft is in the wrong rotational position.',
        fix: 'Loosen pinch bolts, lift shaft off the column splines, rotate by one tooth in the appropriate direction, re-engage, recheck. Repeat until centered.',
      },
      {
        problem: 'Clunk persists after replacement',
        likelyCause: 'Pinch bolts not torqued, or clunk was from elsewhere (steering box mounts, tie rod, drag link)',
        whatToCheck: 'Verify both pinch bolts are at spec torque. Inspect steering box-to-frame mount bolts. Inspect drag link and tie rod ends for play.',
        fix: 'Re-torque pinch bolts. If clunk persists, investigate steering box mounts and tie rod components.',
      },
      {
        problem: 'Airbag warning light on after reconnecting battery',
        likelyCause: 'Battery was disconnected with key in the ON position, or a wire was disturbed during shaft work',
        whatToCheck: 'Confirm the warning is constant (not flashing). Check connectors at the column for proper seating.',
        fix: 'A scan tool with airbag module access can clear the code. If the light returns, a wire may be pinched — inspect carefully.',
      },
      {
        problem: 'Free play returns within a few months',
        likelyCause: 'Used a low-quality replacement shaft, or pinch bolts loosened',
        whatToCheck: 'Re-check pinch bolt torque. Inspect the new shaft for premature U-joint wear.',
        fix: 'If joints are already worn, install a heavy-duty Borgeson upgrade. Always use threadlocker on pinch bolts and recheck at 500 miles.',
      },
    ],
    proTips: [
      'The Borgeson 000937 heavy-duty steering shaft is a worthwhile upgrade for any TJ that sees off-road or high-mile use — the all-steel U-joints last 5x the factory units.',
      'Apply medium-strength threadlocker to both pinch bolts — they are exposed to vibration and can loosen over time.',
      'Mark the centered steering wheel position with tape before disconnecting anything — the cheapest insurance against an off-center result.',
      'If installing a Borgeson, double-check the spline orientation at both ends — Borgeson units sometimes ship with a slight phase difference vs the factory shaft.',
    ],
    successIndicators: [
      'No clunk over bumps',
      'No popping or notchy feel when turning the steering wheel',
      'Free play at the steering wheel reduced to less than ½ inch',
      'Steering wheel returns to center cleanly after a turn',
      'No airbag warning light',
    ],
    whenToSeekHelp: 'If the lower pinch bolt at the steering box rounds off and cannot be removed, professional service may be required to drill it out without damaging the box. If the airbag warning light remains on after installation, scan-tool diagnosis is required.',
    legalDisclaimer: 'IMPORTANT SAFETY NOTICE: Steering components are critical for vehicle control. Always disconnect the battery before working near the airbag-equipped column. Confirm correct torque on both pinch bolts and confirm the steering wheel is centered before driving. If you are uncertain about any procedure, consult a qualified professional.',
  },
  // ─── V10.1 BUNDLE — SENSORS & ENGINE MANAGEMENT ──────────────────────
  // Built under v10.1 methodology. Inline "Verify torque spec X from OEM
  // service manual" prompt on every torque value; manualSource set so the
  // in-app Haynes tab can resolve to the user's licensed manual once
  // Haynes API/licensing is in place.
  {
    id: 'tj-throttle-position-sensor-replacement',
    title: 'Throttle Position Sensor (TPS) Replacement',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [{ startYear: 1997, endYear: 2006 }],
    },
    video: {
      youtubeId: 'n4tLZ40eXQk',
      title: 'How To Replace IAC and TPS Jeep Wrangler',
      channel: 'Embark With Mark',
    },
    source: 'Generated by GarageIQ AI from publicly available sources (Mopar parts catalog, Standard Motor Products / Wells Vehicle Electronics catalogs, NHTSA TSBs, owner\'s manual, technician forums). Cross-checked and structured per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Engine',
    difficulty: 'beginner',
    duration: '30-45 minutes',
    interval: 'When experiencing rough idle, hesitation on acceleration, surging, or scan-tool codes P0121, P0122, or P0123',
    description:
      'The Throttle Position Sensor (TPS) mounts on the side of the throttle body and reports the angle of the throttle plate to the PCM. The PCM uses that signal to map fuel injection, ignition advance, and idle behavior. A failing TPS produces erratic voltage readings that confuse the PCM, causing symptoms that range from a slightly rough idle to surging at cruise to intermittent no-start. The 4.0L TJ uses a 3-pin TPS that is one of the simpler sensors to diagnose and replace — total job time is typically 30 minutes including a basic voltage verification with a multimeter.',
    overview: {
      difficulty: 'Beginner — two screws, one connector, no special tools',
      estimatedTime: '30 to 45 minutes',
      estimatedCost: '$25-60 for the sensor (Mopar OE ~$55, aftermarket Standard or Wells ~$25-35) vs $120-180 at a shop',
      whyMatters: 'A failing TPS creates driveability symptoms that look like much more serious problems. Replacing it restores correct PCM mapping for fuel and timing, often resolving rough idle, surging, and intermittent stalling for under $40.',
      safetyNote: 'Disconnect the battery before working on the throttle body to prevent electrical damage to the PCM and avoid accidental starter engagement when the harness is disturbed.',
    },
    tools: [
      'T20 Torx bit or #2 Phillips screwdriver (verify against your existing screws)',
      '10mm wrench or socket (battery cable)',
      'Inch-pound torque wrench capable of low values',
      'Multimeter (optional — for voltage verification)',
      'Clean shop rag',
      'Dielectric grease (for the connector pins)',
    ],
    parts: [
      {
        id: 'tps-sensor',
        name: 'Throttle Position Sensor (3-pin)',
        partNumber: 'Mopar 56029115AB (4.0L) — aftermarket Standard TH192 or Wells TPS236',
        quantity: 1,
        price: 35,
        category: 'required',
        notes: 'OE Mopar units are most consistent. Verify the connector and bolt-pattern match your existing sensor before installation.',
      },
    ],
    safetyInformation:
      'Disconnect the battery negative cable before unplugging or replacing electronic sensors. The throttle body uses small fasteners that strip easily under a regular ratchet — always finish with a torque wrench. Do not lubricate the TPS shaft or internal contacts with anything; only a thin film of dielectric grease on the connector pins is appropriate. The throttle plate is delicate — do not lever against it during sensor removal.',
    beforeYouStart: [
      'Confirm the diagnosis with an OBD-II scanner — codes P0121, P0122, or P0123 point to the TPS circuit specifically',
      'Disconnect the battery negative cable',
      'Have a clean shop rag ready — debris dropping into an open throttle body is a problem',
      'Note the orientation of the existing TPS before removing — the sensor is keyed but seats only one way',
      'Source the correct sensor for your year (4.0L 1997-2006 share the same 3-pin design; the 2.4L uses a different sensor)',
    ],
    steps: [
      {
        number: 1,
        title: 'Disconnect the Battery Negative Cable',
        description:
          'Open the hood. Use a 10mm wrench or socket to loosen the negative battery terminal nut. Lift the cable off the post and tuck it aside so it cannot accidentally contact the post during the rest of the procedure. Wait at least 60 seconds before continuing.',
        why: 'Working on PCM-connected sensors with the battery live can short the sensor or, in rare cases, damage the PCM. The wait period lets capacitors discharge.',
        tip: 'A small piece of cardboard or a rag wrapped around the disconnected terminal prevents accidental contact.',
        commonMistake: 'Skipping the disconnect because the job is "quick." A single connector spark while live can damage a sensor before it is even installed.',
      },
      {
        number: 2,
        title: 'Locate the TPS on the Throttle Body',
        description:
          'Find the throttle body — a round housing where the air intake tube meets the intake manifold, with a butterfly inside. The TPS is the small black sensor mounted to one side of the throttle body, opposite the throttle linkage. It has a 3-pin electrical connector.',
        why: 'Correctly identifying the TPS prevents accidentally disturbing the IAC valve (a similar-looking nearby component) or the throttle body bolts.',
        tip: 'On the 4.0L the TPS sits on the passenger side of the throttle body, with the connector pointing toward the firewall.',
        commonMistake: 'Confusing the TPS with the IAC valve. The IAC is taller and has a 4-pin connector; the TPS is flatter with a 3-pin connector.',
      },
      {
        number: 3,
        title: 'Disconnect the Harness Connector',
        description:
          'Press the locking tab on the side of the connector and pull the connector straight off the sensor. If the connector resists, gently rock it side to side while pulling. Inspect the pins inside the connector and the matching pins on the sensor for corrosion or bent pins.',
        why: 'The connector lock prevents accidental disconnection while driving. Releasing it correctly avoids breaking the lock tab, which is a common failure mode.',
        tip: 'A small dab of dielectric grease on the connector pins before reassembly extends connector life.',
        commonMistake: 'Pulling on the wires instead of the connector body. Wire damage at the connector is a slow-developing fault.',
      },
      {
        number: 4,
        title: 'Mark the Sensor Orientation',
        description:
          'Before removing the sensor, use a paint pen or piece of tape to mark the rotational position of the sensor body relative to the throttle body. Note which screw is at the top and which is at the bottom.',
        why: 'The TPS is keyed, but the keying still allows the sensor to be installed in a slightly different rotational position. Marking ensures the new sensor goes in the same way.',
        tip: 'Take a photo of the sensor in place from two angles before removing.',
        commonMistake: 'Skipping the mark and reinstalling the new sensor with the connector facing slightly the wrong direction — the harness can pull on the connector and cause intermittent failures.',
      },
      {
        number: 5,
        title: 'Remove the TPS Mounting Screws',
        description:
          'Use a T20 Torx bit (or #2 Phillips, depending on year) to remove the two mounting screws. Loosen them gradually and alternate between the two screws to avoid binding. Set the screws aside in a small dish — do not lose them; replacements are tricky to source.',
        why: 'These are small machine screws threaded into aluminum. Forcing or alternating uneven pressure strips the threads.',
        tip: 'A magnetic-tip driver keeps the small screws from falling into the engine bay.',
        commonMistake: 'Using an oversized bit and stripping the screw heads. Match the bit precisely to the screw recess.',
      },
      {
        number: 6,
        title: 'Remove the Old TPS',
        description:
          'Pull the sensor straight out from the throttle body. The sensor has a small D-shaped or flatted shaft that engages the throttle shaft inside the throttle body. Do not rotate the throttle shaft while the sensor is out — leave the throttle in its closed position.',
        why: 'The shaft engagement is one-position — forcing rotation can damage the throttle body or the new sensor on installation.',
        tip: 'Keep the throttle linkage at idle position with a finger or rubber band while the sensor is out.',
        commonMistake: 'Rotating the throttle shaft to "see how it moves" while the sensor is off, which makes alignment of the new sensor harder.',
      },
      {
        number: 7,
        title: 'Compare New Sensor to Old',
        description:
          'Lay the new TPS next to the old one. Verify the connector orientation, the bolt pattern, the shaft engagement shape, and the overall body shape match. The new sensor should be a visual twin of the old one.',
        why: 'Wrong-part risk is real for sensors purchased online. A sensor that looks "close" can have a different connector or a different sensing curve.',
        tip: 'If the new sensor looks even slightly different, stop and verify the part number against the OEM cross-reference before continuing.',
        commonMistake: 'Installing a wrong-application sensor that bolts up but reads incorrect voltage, producing the same symptoms as the original failure.',
      },
      {
        number: 8,
        title: 'Install the New TPS',
        description:
          'Align the new sensor\'s shaft with the throttle shaft and seat the sensor against the throttle body in the orientation matched to your reference photo. The sensor should sit flush against the mounting surface with no gap. Hand-thread both mounting screws — do not power-drive them.',
        why: 'Hand-threading prevents cross-threading the small screws into the aluminum throttle body. Cross-threaded screws strip the soft aluminum quickly.',
        tip: 'If the sensor will not seat flush, the shaft is misaligned — pull the sensor out and try again with slight rotation.',
        commonMistake: 'Forcing the sensor down with the screws to "make it seat." This usually means the shaft is misaligned and the sensor is being damaged.',
      },
      {
        number: 9,
        title: 'Torque the Mounting Screws',
        description:
          'Using an inch-pound torque wrench, tighten both mounting screws alternately in two passes — first to half the final torque, then to the full specification. The screws should be snug but not over-tight; the goal is a secure seal without distorting the sensor body.',
        why: 'Even torque keeps the sensor body parallel to the throttle body and prevents distortion of the sensor housing, which can affect the internal resistance track.',
        tip: 'If you do not have an inch-pound torque wrench, the screws should require only firm pressure with a small (4-inch) screwdriver. Stop the moment you feel resistance build.',
        commonMistake: 'Treating these screws like every other engine fastener and torquing to a foot-pound value. The threads will strip and the sensor body can crack.',
      },
      {
        number: 10,
        title: 'Reconnect, Reset, and Test',
        description:
          'Reconnect the harness — push the connector straight on until the lock clicks. Reconnect the battery negative cable and torque to spec. Start the engine and let it idle for 2-3 minutes without touching the throttle (idle relearn). Then take a brief road test that includes light cruise, full throttle, and a clean stop. Clear any pending codes with a scan tool and confirm none return.',
        why: 'The PCM needs an idle-relearn drive cycle to remap with the new TPS voltage. Skipping this can leave the engine idling rough or off-center until the PCM updates.',
        tip: 'A scan tool that displays live TPS voltage should read approximately 0.5V at closed throttle and approximately 4.5V at wide-open throttle, increasing smoothly with no dropouts.',
        commonMistake: 'Driving aggressively immediately after replacement before the idle-relearn completes. Idle behavior may be erratic for the first few minutes — that is normal.',
      },
    ],
    torqueSpecifications: [
      {
        fastener: 'TPS mounting screws',
        specification: '16 in-lbs (1.8 Nm). Verify torque spec 16 in-lbs (1.8 Nm) from OEM service manual.',
        notes: 'Very small torque value — these are #10 machine screws into aluminum. Strips easily if over-tightened.',
      },
      {
        fastener: 'Battery negative terminal nut',
        specification: '5 ft-lbs (7 Nm). Verify torque spec 5 ft-lbs (7 Nm) from OEM service manual.',
        notes: 'Snug but not tight; over-torque distorts the lead post.',
      },
    ],
    fluidSpecifications: [],
    troubleshooting: [
      {
        problem: 'Code P0121/P0122/P0123 returns after replacement',
        likelyCause: 'Wrong-application sensor, faulty new sensor (rare), or harness/wiring fault upstream of the sensor',
        whatToCheck: 'Verify the part number is correct for your engine and year. Inspect the harness connector pins for corrosion or bent pins. Use a multimeter to verify 5V reference at the signal pin with key on, engine off.',
        fix: 'Swap to a known-good OE Mopar sensor. If 5V reference is missing, the problem is upstream — inspect the harness back to the PCM.',
      },
      {
        problem: 'Engine still surges at idle',
        likelyCause: 'Idle relearn not yet completed, IAC valve issue, or vacuum leak unrelated to TPS',
        whatToCheck: 'Idle the engine for a full 5 minutes with no throttle input to allow PCM relearn. Inspect intake hose clamps and vacuum lines for cracks or loose fittings.',
        fix: 'If surge persists after relearn, troubleshoot the IAC valve and check for vacuum leaks before assuming a sensor problem.',
      },
      {
        problem: 'TPS voltage at idle is not in the 0.5V range',
        likelyCause: 'Sensor installed with throttle off-idle, sensor body rotated incorrectly, or worn throttle plate stop',
        whatToCheck: 'Confirm the throttle is fully closed. With a scan tool, observe the TPS voltage at idle — values significantly above 0.7V at idle indicate a problem.',
        fix: 'Reposition the sensor body to your original mark. If voltage is still off, inspect the throttle plate stop screw — it should not have been adjusted.',
      },
      {
        problem: 'Mounting screw threads stripped during installation',
        likelyCause: 'Cross-threading or over-torque during installation',
        whatToCheck: 'Inspect the throttle body threads for damage. A thread chaser or insert may be needed.',
        fix: 'Use a thread repair insert (Heli-Coil M5 x 0.8 typical) or replace the throttle body if damage is severe. Always hand-thread these screws.',
      },
    ],
    proTips: [
      'Mark the sensor orientation with a paint pen or take a photo before removal — the new sensor goes in much faster when you have a reference.',
      'Apply a thin film of dielectric grease to the connector pins before reconnecting. This is the cheapest insurance against connector corrosion.',
      'After replacement, idle the engine for at least 5 minutes without touching the throttle to allow the PCM idle relearn. The first 2-3 minutes can feel rough — that is normal.',
      'A multimeter check at the connector before installation can confirm the sensor is healthy: with the sensor on a bench, resistance between the signal pin and ground should sweep smoothly from approximately 700 ohms (closed) to 4,000+ ohms (wide-open) as the shaft rotates.',
    ],
    successIndicators: [
      'Engine idles smoothly within 2-3 minutes of restart',
      'No code P0121, P0122, or P0123 returns after a clear and a full drive cycle',
      'No hesitation or surging during light-throttle acceleration',
      'TPS voltage reads approximately 0.5V at closed throttle and 4.5V at wide-open throttle on a scan tool',
      'No surge or stumble at cruise',
    ],
    whenToSeekHelp: 'If symptoms persist after TPS replacement and clearing codes, the issue is likely elsewhere — IAC valve, throttle body itself, vacuum leak, or wiring harness. A scan tool capable of monitoring live TPS voltage during a road test is needed to diagnose intermittent issues. If 5V reference is absent at the connector, professional diagnosis of the PCM and harness is recommended.',
    legalDisclaimer: 'This guide was generated by GarageIQ\'s AI from publicly available sources. Some information may be incorrect or out of date. Verify torque specifications and any safety-critical procedures against the OEM service manual for your vehicle before performing work. The Haynes Manual tab on this page links to the licensed manual for your vehicle. GarageIQ is not a substitute for a qualified mechanic. By using this guide you agree to GarageIQ\'s Terms of Service and assume responsibility for the work performed.',
    manualSource: {
      title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)',
      url: 'https://www.haynes.com/en-us/wrangler/jeep/2003',
      notes: 'Procedure sourced from public catalogs and forums; cross-reference torque values with your licensed manual before final tightening.',
    },
  },
  {
    id: 'tj-crankshaft-position-sensor-replacement',
    title: 'Crankshaft Position Sensor (CKP) Replacement',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [{ startYear: 1997, endYear: 2006 }],
      engine: '4.0l-i6',
    },
    video: {
      youtubeId: 'lrMesIVWR-M',
      title: 'How to Replace Crankshaft Position Sensor 1997-2006 Jeep Wrangler',
      channel: 'TRQ',
    },
    source: 'Generated by GarageIQ AI from publicly available sources (Mopar parts catalog, NHTSA TSBs and Complaints database, Bosch and Standard Motor Products catalogs, owner\'s manual, technician forums). Cross-checked and structured per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Engine',
    difficulty: 'intermediate',
    duration: '1-1.5 hours',
    interval: 'When experiencing intermittent no-start, stalling at idle, hot-start failure, or scan-tool code P0335',
    description:
      'The Crankshaft Position Sensor (CKP) on the 4.0L TJ mounts to the bell housing on the driver\'s side and reads a target ring on the flywheel. The PCM uses the CKP signal to determine engine speed and crank position for fuel injection and ignition timing. A failing CKP is one of the more frequent causes of intermittent no-starts on the TJ — the sensor often fails when hot, allowing the engine to start cold and quit after warm-up. Replacement is a single-bolt job, but access is tight from above and many DIYers raise the front of the vehicle to reach it from below.',
    overview: {
      difficulty: 'Intermediate — single fastener but access is tight; raising the vehicle is recommended',
      estimatedTime: '1 to 1.5 hours',
      estimatedCost: '$30-80 for the sensor (Mopar OE ~$70, aftermarket Standard PC52 ~$30-40) vs $200-280 at a shop',
      whyMatters: 'A failing CKP causes the most maddening kind of fault — intermittent no-start that comes and goes with engine temperature. Replacing it ends the guessing.',
      safetyNote: 'Disconnect the battery before working on the bell housing. If raising the vehicle, use jack stands rated for the load and chock the rear wheels. Never work under a vehicle supported only by a jack.',
    },
    tools: [
      'Socket set with 8mm and 10mm sockets',
      '3/8" or 1/4" ratchet with extension',
      'Torque wrench (small range — capable of 7-15 Nm)',
      'Floor jack and two jack stands',
      'Wheel chocks',
      'Penetrating oil (PB Blaster or similar)',
      'Flashlight or work light',
      'Clean shop rag',
    ],
    parts: [
      {
        id: 'ckp-sensor',
        name: 'Crankshaft Position Sensor',
        partNumber: 'Mopar 56027866AB (4.0L) — aftermarket Standard PC52 or equivalent',
        quantity: 1,
        price: 50,
        category: 'required',
        notes: 'The OE Mopar sensor has the most consistent service life. Cheap eBay sensors are a known source of repeat failure.',
      },
    ],
    safetyInformation:
      'Disconnect the battery negative cable before disturbing engine sensors. If raising the vehicle, use rated jack stands placed at the factory lift points and chock the rear wheels. Never work under a vehicle supported only by a jack. The bell housing area can have hot exhaust components nearby — let the engine cool fully before reaching in.',
    beforeYouStart: [
      'Confirm the diagnosis — code P0335 specifically points to the CKP circuit, and intermittent no-starts when hot are a classic CKP failure pattern',
      'Disconnect the battery negative cable',
      'Decide on access method — from above (cramped, possible without raising the vehicle) or from below (recommended)',
      'Have the new sensor in hand before removing the old one',
      'Clear the work area — drop a clean rag below the bell housing to catch the old sensor if it falls',
    ],
    steps: [
      {
        number: 1,
        title: 'Disconnect the Battery Negative Cable',
        description:
          'Use a 10mm wrench to loosen the negative battery terminal nut. Lift the cable off the post and tuck it aside so it cannot accidentally contact the post during the procedure.',
        why: 'The CKP is a PCM-connected sensor. Disconnecting the battery prevents shorts during connector removal.',
        tip: 'Wrap the loose cable end in a shop rag to prevent accidental contact with the battery post.',
        commonMistake: 'Skipping the disconnect because "the engine is off." A live PCM circuit can still spike a sensor during connector removal.',
      },
      {
        number: 2,
        title: 'Raise the Front of the Vehicle and Set Jack Stands',
        description:
          'Chock both rear wheels. Use a floor jack at the front cross-member to raise the front of the vehicle. Place jack stands under the frame rails behind the front wheels and lower the vehicle onto the stands. Verify stability by pushing on the bumper before going underneath.',
        why: 'CKP access is much easier from below. A vehicle on stands is the safest way to work underneath.',
        tip: 'Raise high enough that you have working room — at least 18 inches of clearance helps.',
        commonMistake: 'Working under a vehicle supported only by a floor jack. A jack can fail; jack stands are the safety system.',
      },
      {
        number: 3,
        title: 'Locate the CKP Sensor on the Bell Housing',
        description:
          'Slide under the front of the vehicle on the driver\'s side. The CKP is mounted to the bell housing — the cast aluminum housing between the engine and the transmission. On the 4.0L it sits at the upper rear edge of the bell housing on the driver\'s side, with a single mounting bolt and a 2-pin connector running up to the harness.',
        why: 'The CKP sits very close to the flywheel/flexplate teeth. Locating it correctly prevents pulling on adjacent harnesses.',
        tip: 'A flashlight angled at the bell housing makes the sensor easier to spot — look for a small black sensor with a 2-pin connector.',
        commonMistake: 'Confusing the CKP with the speed sensor on the transfer case or the backup light switch. The CKP is on the bell housing itself, not the transmission case.',
      },
      {
        number: 4,
        title: 'Disconnect the Harness Connector',
        description:
          'Press the locking tab on the side of the connector and pull the connector straight off the sensor. The connector is small and may be partially seized by heat — a gentle side-to-side rock while pulling helps. Inspect the pins for corrosion.',
        why: 'Removing the connector first avoids stressing the wires when the sensor itself is rotated during removal.',
        tip: 'Apply a small amount of dielectric grease to the connector pins before reassembly.',
        commonMistake: 'Pulling on the wires instead of the connector body. The wires are short and break easily at the connector.',
      },
      {
        number: 5,
        title: 'Apply Penetrating Oil to the Mounting Bolt',
        description:
          'The CKP is held in place by a single bolt — typically with an 8mm or 10mm head. Apply penetrating oil to the bolt and the joint between the sensor and the bell housing. Let soak for 5-10 minutes.',
        why: 'The sensor body is partially seized in the bell housing bore by heat cycling. Penetrating oil at both the bolt and the body joint helps removal without breaking the sensor.',
        tip: 'A second application after 5 minutes works better than a single soak.',
        commonMistake: 'Skipping the oil and forcing the sensor out, which can break the body and leave a piece in the bore.',
      },
      {
        number: 6,
        title: 'Remove the Mounting Bolt',
        description:
          'Use the appropriate socket and a short extension to remove the single mounting bolt. The bolt is small — do not over-torque it on the way out. Set the bolt aside in a magnetic dish so it does not fall.',
        why: 'Single-bolt removal is straightforward when prepared. Losing the bolt into the engine compartment is the most common failure mode.',
        tip: 'A magnetic socket or magnetic-tip extension catches the bolt as it comes free.',
        commonMistake: 'Dropping the bolt — and then spending an hour finding it. A magnetic dish below the work area catches small parts.',
      },
      {
        number: 7,
        title: 'Twist and Pull the Sensor Free',
        description:
          'With the bolt removed, twist the sensor body slightly side to side while pulling outward. The sensor has an O-ring seal on the body that can be partly seized in the bore. If it resists, apply more penetrating oil and twist again — do not pry against the bell housing.',
        why: 'Twisting breaks the O-ring seal without damaging the sensor body or the bell housing bore.',
        tip: 'A long pair of needle-nose pliers gripping the sensor body (not the wires) helps if the sensor is deeply seized.',
        commonMistake: 'Prying with a screwdriver against the bell housing — this can crack the sensor body and leave fragments in the bore.',
      },
      {
        number: 8,
        title: 'Inspect the Bore and Compare Sensors',
        description:
          'Use a clean rag to wipe the inside of the bore. Check for any debris or oil residue. Compare the new sensor to the old — verify the body shape, connector orientation, mounting tab, and O-ring are the same. The depth of insertion is determined by the mounting flange, so a wrong-length sensor will not seat correctly.',
        why: 'A clean bore lets the new O-ring seat properly. A correct-application sensor reads the flywheel teeth at the right air gap.',
        tip: 'A new O-ring usually comes pre-installed — inspect for nicks before installing.',
        commonMistake: 'Installing a wrong-length sensor that bottoms out before the flange seats — the air gap is wrong and the signal is unreliable.',
      },
      {
        number: 9,
        title: 'Install the New Sensor',
        description:
          'Apply a light coat of clean engine oil to the new sensor\'s O-ring. Push the sensor into the bore until the mounting flange is flush against the bell housing. Hand-thread the mounting bolt — do not power-drive. Once threaded, torque the bolt to specification.',
        why: 'A film of oil on the O-ring prevents the seal from rolling or tearing during insertion. Hand-threading prevents cross-threading the small bolt into aluminum.',
        tip: 'If the sensor will not seat flush, do not force it — pull it out and check for debris in the bore or an O-ring that has folded.',
        commonMistake: 'Forcing the sensor down with the bolt to "make it seat." A folded O-ring leaks oil and may cause the sensor to read incorrectly.',
      },
      {
        number: 10,
        title: 'Reconnect, Lower, and Test',
        description:
          'Reconnect the harness — push straight on until the lock clicks. Lower the vehicle off the jack stands. Reconnect the battery negative cable and torque to spec. Start the engine — it should fire normally. Idle for 2-3 minutes, then take a brief road test that includes a hot restart (drive briefly, shut off for 5 minutes, then restart). Clear any pending codes and confirm none return.',
        why: 'The hot-restart test is the highest-confidence verification because the original failure mode is heat-related.',
        tip: 'A scan tool that displays engine RPM during cranking can confirm the CKP signal is being received.',
        commonMistake: 'Skipping the hot-restart test. Cold start success does not prove the CKP is fixed if the original symptom was hot-only.',
      },
    ],
    torqueSpecifications: [
      {
        fastener: 'CKP sensor mounting bolt',
        specification: '7 ft-lbs (10 Nm). Verify torque spec 7 ft-lbs (10 Nm) from OEM service manual.',
        notes: 'Small bolt into aluminum bell housing — do not exceed the spec.',
      },
      {
        fastener: 'Battery negative terminal nut',
        specification: '5 ft-lbs (7 Nm). Verify torque spec 5 ft-lbs (7 Nm) from OEM service manual.',
        notes: 'Snug but not tight; over-torque distorts the lead post.',
      },
    ],
    fluidSpecifications: [],
    troubleshooting: [
      {
        problem: 'No-start condition continues after replacement',
        likelyCause: 'Wrong-application sensor, faulty new sensor, harness fault, or different root cause (CMP, ignition coil, fuel pump)',
        whatToCheck: 'Verify the new sensor part number is correct. Use a scan tool to check for RPM signal during cranking — no signal means the sensor is not generating output. Inspect the harness for damage.',
        fix: 'Swap to a known-good OE Mopar sensor. If RPM signal is still absent during cranking, troubleshoot the harness back to the PCM and verify reference voltage.',
      },
      {
        problem: 'P0335 returns after clearing',
        likelyCause: 'Sensor air gap is incorrect (foreign object in bore), faulty sensor, or wiring damage',
        whatToCheck: 'Re-inspect the bore for debris. Verify the sensor seated flush against the bell housing.',
        fix: 'Re-seat the sensor with a clean bore. If the code persists, swap to a different brand new sensor — some aftermarket units are weak.',
      },
      {
        problem: 'Sensor was extremely seized — body broke during removal',
        likelyCause: 'Heat-cycled O-ring fused to bore; insufficient penetrating oil',
        whatToCheck: 'Inspect the bore for any sensor body fragments. A small fragment can be removed with needle-nose pliers; a deeper fragment needs more involved removal.',
        fix: 'Carefully remove all fragments. If the bore is damaged, professional service may be needed. Always apply penetrating oil and twist (do not pry) on next replacement.',
      },
      {
        problem: 'Engine starts but stalls immediately',
        likelyCause: 'Sensor harness connector not fully seated, or sensor pins not making contact',
        whatToCheck: 'Verify the harness connector latched fully — listen for the click. Inspect both the harness and the sensor pins for damage.',
        fix: 'Re-seat the connector. If contact is poor, replace the harness pigtail (sold separately) or the harness side connector.',
      },
    ],
    proTips: [
      'Replace the CKP and the camshaft position sensor (in the distributor) together if both are over 100,000 miles — they share similar failure modes and the labor overlap is small.',
      'Apply a thin film of clean engine oil to the new O-ring before installation. The seal lasts much longer when it is not stressed during install.',
      'A magnetic-tip extension is the cheapest insurance against losing the small mounting bolt into the engine compartment.',
      'After replacement, do a hot-restart test (drive 10-15 minutes, shut off for 5 minutes, then restart). This is the test that proves the original failure mode is fixed.',
    ],
    successIndicators: [
      'Engine cranks and starts normally on the first attempt',
      'Hot-restart test passes — engine restarts cleanly after a heat soak',
      'No code P0335 returns after a clear and full drive cycle',
      'Engine RPM signal is present during cranking on a scan tool',
      'No oil seepage from the sensor body after a full drive cycle',
    ],
    whenToSeekHelp: 'If a portion of the old sensor body is broken off in the bell housing bore and cannot be safely removed, professional service is recommended to extract the fragment without damaging the bore. If the new sensor is installed correctly and the no-start persists, the diagnosis points to the CMP, ignition system, or fuel system — broaden the troubleshooting before assuming a faulty replacement.',
    legalDisclaimer: 'This guide was generated by GarageIQ\'s AI from publicly available sources. Some information may be incorrect or out of date. Verify torque specifications and any safety-critical procedures against the OEM service manual for your vehicle before performing work. The Haynes Manual tab on this page links to the licensed manual for your vehicle. GarageIQ is not a substitute for a qualified mechanic. By using this guide you agree to GarageIQ\'s Terms of Service and assume responsibility for the work performed.',
    manualSource: {
      title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)',
      url: 'https://www.haynes.com/en-us/wrangler/jeep/2003',
      notes: 'Procedure sourced from public catalogs and forums; cross-reference torque values with your licensed manual before final tightening.',
    },
  },
  {
    id: 'tj-distributor-camshaft-position-sensor-replacement',
    title: 'Distributor Replacement (Includes Camshaft Position Sensor) — 4.0L',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [{ startYear: 1997, endYear: 2006 }],
      engine: '4.0l-i6',
    },
    video: {
      youtubeId: 'Krk_p0e5VsI',
      title: 'DIY Jeep TJ Camshaft Position Sensor / Synchronizer 4.0',
      channel: 'BSK Garage',
    },
    source: 'Generated by GarageIQ AI from publicly available sources (Mopar parts catalog, NHTSA TSBs and Complaints database, Standard Motor Products and Wells Vehicle Electronics catalogs, owner\'s manual, technician forums). Cross-checked and structured per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Engine',
    difficulty: 'intermediate',
    duration: '1.5-2 hours',
    interval: 'When experiencing crank-no-start, intermittent stall at idle, scan-tool code P0340 or P0344, or visible oil leaking from the distributor housing',
    description:
      'On the 4.0L TJ, the Camshaft Position Sensor (CMP) is integrated inside the distributor housing — there is no separate external CMP sensor. The recommended repair when the CMP fails is to replace the entire distributor assembly rather than rebuild it, because the riveted internals are not designed for field service. A failing CMP causes crank-no-start, hard hot-start, and intermittent stalling. The procedure is timing-sensitive: the engine must be at TDC compression on cylinder 1 and the rotor position must be marked before removal so the new distributor can be installed in the same orientation.',
    overview: {
      difficulty: 'Intermediate — timing-sensitive procedure with TDC alignment and rotor positioning',
      estimatedTime: '1.5 to 2 hours',
      estimatedCost: '$80-180 for a complete distributor assembly (Mopar OE ~$160-180, aftermarket ~$80-110) vs $300-450 at a shop',
      whyMatters: 'The CMP is a known wear item on the 4.0L — many TJs need this around 100,000-150,000 miles. Replacing the complete distributor is faster and more reliable than rebuilding the internal sensor.',
      safetyNote: 'Disconnect the battery before disturbing the ignition system. The distributor cap carries high voltage when the engine is running — never remove it with the engine cranking. Ensure the engine is at TDC compression on cylinder 1 before marking the rotor position.',
    },
    tools: [
      '13mm socket and extension',
      '#2 Phillips screwdriver',
      'Socket set including spark plug socket (5/8")',
      'Ratchet with breaker bar',
      'Torque wrench',
      'Permanent marker or paint pen',
      'Compression gauge or finger over plug hole (TDC verification)',
      'Penetrating oil',
      'Camera or phone (for documentation)',
    ],
    parts: [
      {
        id: 'distributor-assembly',
        name: 'Distributor Assembly (4.0L, complete with cap and rotor)',
        partNumber: 'Mopar 56041018AE (1997-2006 4.0L) — aftermarket Standard LX-654 or Wells DST3645',
        quantity: 1,
        price: 130,
        category: 'required',
        notes: 'Buy a complete assembly with cap and rotor included rather than rebuilding the internals. Verify the part fits your specific year — there are minor variations.',
      },
      {
        id: 'distributor-oring',
        name: 'Distributor base O-ring',
        partNumber: 'Usually included with new distributor; Mopar 53010175AB if separate',
        quantity: 1,
        price: 4,
        category: 'optional',
        notes: 'Replace if the new distributor does not include one. Prevents oil leaks at the base.',
      },
    ],
    safetyInformation:
      'Disconnect the battery negative cable before any work on the ignition system. The distributor cap carries up to 30,000 volts when the engine is running — never remove or work on the cap with the engine cranking or running. The engine must be at TDC compression on cylinder 1 before any marking or distributor removal. Failure to mark rotor position correctly will result in incorrect ignition timing and the engine will not start.',
    beforeYouStart: [
      'Confirm the diagnosis — code P0340 or P0344 specifically points to the CMP circuit. Visible oil weeping from the distributor base is also a strong indicator of CMP / distributor wear',
      'Disconnect the battery negative cable',
      'Have the new distributor in hand and verify the part number before starting',
      'Have a permanent marker or paint pen and a camera ready — multiple reference marks are essential',
      'Bring the engine to operating temperature before shutdown if practical — easier to rotate the crank when warm',
    ],
    steps: [
      {
        number: 1,
        title: 'Disconnect the Battery',
        description:
          'Use a 10mm wrench to loosen the negative battery terminal nut. Lift the cable off the post and tuck it aside.',
        why: 'High-voltage ignition components must be de-energized before service. The PCM is also protected from spikes during connector removal.',
        tip: 'Wrap the loose cable in a shop rag to prevent accidental contact.',
        commonMistake: 'Skipping the disconnect — the ignition system can deliver a shock even when the engine is off if the system is tested with the cap removed.',
      },
      {
        number: 2,
        title: 'Bring Cylinder 1 to TDC Compression',
        description:
          'Remove the cylinder 1 spark plug (front-most plug on the 4.0L inline-six). Use a socket on the crankshaft pulley bolt to slowly rotate the engine clockwise (viewed from the front) while holding a finger or compression gauge over the plug hole. When you feel air pressure pushing your finger out, slow the rotation. Continue until the timing mark on the crank pulley aligns with the 0° (TDC) mark on the timing cover.',
        why: 'TDC compression on cylinder 1 is the reference position for the rotor. The distributor must be reinstalled with the rotor pointing at the cylinder 1 contact in the cap when the engine is at this position.',
        tip: 'Air pushing out of the plug hole confirms the compression stroke. If you reach TDC and there is no compression, rotate one more full turn — that is TDC on the exhaust stroke, not what you want.',
        commonMistake: 'Stopping at the timing mark without verifying compression. TDC happens twice per cycle — only one of those is the compression stroke.',
      },
      {
        number: 3,
        title: 'Mark the Distributor Cap and Rotor Position',
        description:
          'With the engine at TDC compression on cylinder 1, remove the distributor cap (two Phillips screws — leave the wires attached if possible). Note where the rotor tip is pointing. Use a paint pen to mark: (a) the rotor tip position on the distributor housing rim, (b) the position of the housing on the engine block (mark across the joint between the housing flange and the block).',
        why: 'These marks are your guarantee that the new distributor goes back in the same orientation. Without them the engine will not start.',
        tip: 'Take photos from three angles — directly above, from the front, and from the side. Photos are insurance against marker smudges.',
        commonMistake: 'Marking only one of the two reference points. Both rotor-to-housing and housing-to-block must be marked.',
      },
      {
        number: 4,
        title: 'Disconnect the CMP Harness Connector',
        description:
          'Locate the CMP electrical connector at the side of the distributor body. Press the locking tab and pull straight off. Inspect the pins for corrosion.',
        why: 'The connector must be disconnected before the distributor lifts out, or the wires will be stressed.',
        tip: 'A small dab of dielectric grease at the pins on reassembly extends connector life.',
        commonMistake: 'Pulling on the wires instead of the connector body.',
      },
      {
        number: 5,
        title: 'Remove the Distributor Hold-Down Bolt',
        description:
          'Locate the hold-down bolt that secures the distributor to the engine block — typically a 13mm head bolt at the base flange. Apply penetrating oil and let soak briefly. Remove the bolt fully and set aside.',
        why: 'Removing the hold-down releases the distributor for lifting. The bolt is small but exposed to engine heat — penetrating oil prevents seizing.',
        tip: 'A wobble extension and short ratchet make access easier.',
        commonMistake: 'Loosening the bolt only partially. The distributor must be free to lift straight up.',
      },
      {
        number: 6,
        title: 'Lift the Distributor Out',
        description:
          'Grasp the distributor body and lift straight up. The drive gear at the bottom is helical, which means the rotor will rotate slightly as the distributor comes out. Note the new rotor position once the distributor is fully lifted out — this is your "lift-out position" and is the position the new distributor must be in BEFORE installation begins.',
        why: 'The helical gear causes the rotor to rotate as the distributor seats. To have it land on your TDC mark, you start the install with the rotor at the lift-out position.',
        tip: 'Photograph the rotor position the moment the distributor clears the engine — this is your installation reference, not the TDC mark.',
        commonMistake: 'Forgetting that the rotor moves during lift-out, then trying to install the new distributor with the rotor at the TDC mark — the helical gear rotates it past, and timing is off.',
      },
      {
        number: 7,
        title: 'Inspect Engine Block Bore and Compare Distributors',
        description:
          'Wipe the distributor seat area on the engine block with a clean rag. Check for any debris or old gasket residue. Compare the new distributor to the old — verify the drive gear shape, body length, cap mounting pattern, and CMP connector orientation. Confirm the new distributor includes its O-ring (or install a new one).',
        why: 'A clean seat lets the O-ring seal properly. A wrong-application distributor will not seat correctly or will misposition the rotor.',
        tip: 'Apply a thin film of clean engine oil to the new O-ring before installation.',
        commonMistake: 'Skipping the comparison and finding mid-installation that the gear is wrong.',
      },
      {
        number: 8,
        title: 'Pre-Position the New Distributor and Insert',
        description:
          'Hold the new distributor with the rotor pointing at the "lift-out position" you photographed in step 6. Lower it into the engine block bore. As the helical gear engages, the rotor will rotate. The installed rotor should land pointing at your TDC mark on the housing rim. If it lands off, lift the distributor out, rotate the rotor by one tooth in the appropriate direction, and reinsert. Hand-thread the hold-down bolt only — do not torque yet.',
        why: 'Pre-positioning at the lift-out angle compensates for the rotation during gear engagement. Hand-threading the bolt allows the housing to be rotated for fine adjustment.',
        tip: 'It often takes 2-3 attempts to land the rotor exactly on the mark. That is normal.',
        commonMistake: 'Forcing the distributor down with the rotor pointing at the wrong angle — the gear will engage but the rotor will be off, and the engine will not start.',
      },
      {
        number: 9,
        title: 'Align the Housing-to-Block Mark and Torque',
        description:
          'With the rotor on the TDC mark and the bolt hand-threaded, rotate the distributor housing slightly until your housing-to-block paint mark realigns. This sets the base ignition timing. Hold the housing in this position and torque the hold-down bolt to specification.',
        why: 'The housing rotation sets ignition timing. Aligning to your original mark restores the original timing the engine had been running with.',
        tip: 'On the 4.0L the PCM controls ignition timing electronically — the distributor base position should not need adjustment beyond the original mark.',
        commonMistake: 'Torquing the bolt before the housing is rotated to the mark. The distributor is locked at the wrong position.',
      },
      {
        number: 10,
        title: 'Reinstall Cap, Reconnect, and Test',
        description:
          'Reinstall the distributor cap and tighten the two Phillips screws gently. Reconnect the CMP harness connector. Reinstall the cylinder 1 spark plug and torque to 27 ft-lbs (37 Nm) for the 4.0L. Reconnect the battery and torque the terminal to spec. Crank the engine — it should start within a second or two. If it does not start cleanly, recheck rotor alignment. After running, scan for codes and confirm none return.',
        why: 'A correctly aligned distributor produces immediate startup. Slow or stumbling start is a sign of one-tooth misalignment.',
        tip: 'Once running, listen for any ignition miss or stumble. Any roughness suggests the rotor landed one tooth off — repeat the alignment.',
        commonMistake: 'Driving the vehicle before verifying clean startup. A misaligned distributor can damage the engine over distance through poor combustion timing.',
      },
    ],
    torqueSpecifications: [
      {
        fastener: 'Distributor hold-down bolt',
        specification: '17 ft-lbs (23 Nm). Verify torque spec 17 ft-lbs (23 Nm) from OEM service manual.',
        notes: 'Holds the distributor in its rotated position. Snug but not extreme.',
      },
      {
        fastener: 'Spark plug (4.0L)',
        specification: '27 ft-lbs (37 Nm). Verify torque spec 27 ft-lbs (37 Nm) from OEM service manual.',
        notes: 'Standard tapered-seat plug torque for the 4.0L head. Always thread by hand first.',
      },
      {
        fastener: 'Distributor cap screws',
        specification: '20 in-lbs (2.3 Nm). Verify torque spec 20 in-lbs (2.3 Nm) from OEM service manual.',
        notes: 'Snug only. The plastic cap cracks if over-tightened.',
      },
      {
        fastener: 'Battery negative terminal nut',
        specification: '5 ft-lbs (7 Nm). Verify torque spec 5 ft-lbs (7 Nm) from OEM service manual.',
        notes: 'Snug but not tight; over-torque distorts the lead post.',
      },
    ],
    fluidSpecifications: [],
    troubleshooting: [
      {
        problem: 'Engine cranks but will not start after installation',
        likelyCause: 'Rotor landed one or more teeth off TDC alignment',
        whatToCheck: 'Remove the distributor cap and verify the rotor is pointing exactly at your TDC mark on the housing rim with the engine at TDC compression on cylinder 1.',
        fix: 'Lift the distributor out, pre-position the rotor to compensate for the helical-gear rotation, and reinsert. May take 2-3 attempts.',
      },
      {
        problem: 'Engine starts but runs rough or backfires',
        likelyCause: 'Rotor is one tooth off in either direction, or the housing-to-block alignment is incorrect',
        whatToCheck: 'Inspect the marker alignment of both rotor-to-housing and housing-to-block. Reverify TDC compression on cylinder 1.',
        fix: 'Realign to original marks. Backfire through the intake suggests advanced timing; backfire through the exhaust suggests retarded timing.',
      },
      {
        problem: 'P0340 or P0344 returns after replacement',
        likelyCause: 'Faulty new distributor (rare), harness fault, or different root cause',
        whatToCheck: 'Inspect the harness from the CMP connector back toward the PCM for damage. Verify reference voltage at the connector with key-on-engine-off.',
        fix: 'Swap to a known-good OE Mopar unit. If the code persists, troubleshoot the harness and PCM.',
      },
      {
        problem: 'Oil leaking from distributor base after install',
        likelyCause: 'O-ring damaged during installation, missing, or wrong size',
        whatToCheck: 'Pull the distributor and inspect the O-ring for damage or roll-over.',
        fix: 'Install a new correct-size O-ring with a thin film of clean engine oil. Reseat carefully — do not rotate during insertion.',
      },
    ],
    proTips: [
      'Triple-mark before removal — rotor-to-housing, housing-to-block, AND a photo of the rotor position the moment the distributor clears the block. The third mark is the most important: it is your installation reference.',
      'On the 4.0L, the PCM controls timing electronically. The distributor base position only needs to match the original mark; do not attempt to "advance" the timing by rotating the housing further.',
      'Always replace the cap and rotor at the same time as the distributor unless the new assembly comes with them included. Worn cap/rotor symptoms can mimic a failing CMP.',
      'After install, scan for codes after a 10-mile drive. P0340/P0344 will set within a few minutes if the CMP signal is wrong.',
    ],
    successIndicators: [
      'Engine starts within 1-2 seconds of cranking',
      'No backfire through intake or exhaust',
      'Smooth idle with no ignition stumble',
      'No code P0340 or P0344 returns after a clear and full drive cycle',
      'No oil seepage from the distributor base after a drive cycle',
    ],
    whenToSeekHelp: 'If the rotor will not land on the TDC mark after several lift-and-reinstall attempts, the helical gear engagement may be off by a full tooth in a way that requires more careful indexing — professional service is recommended. If the engine runs but a knock or persistent miss develops, do not drive — distributor misalignment can cause combustion problems that damage the engine.',
    legalDisclaimer: 'This guide was generated by GarageIQ\'s AI from publicly available sources. Some information may be incorrect or out of date. Verify torque specifications and any safety-critical procedures against the OEM service manual for your vehicle before performing work. The Haynes Manual tab on this page links to the licensed manual for your vehicle. GarageIQ is not a substitute for a qualified mechanic. By using this guide you agree to GarageIQ\'s Terms of Service and assume responsibility for the work performed.',
    manualSource: {
      title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)',
      url: 'https://www.haynes.com/en-us/wrangler/jeep/2003',
      notes: 'Procedure sourced from public catalogs and forums; cross-reference torque values with your licensed manual before final tightening.',
    },
  },
  {
    id: 'tj-oxygen-sensor-replacement',
    title: 'Oxygen (O2) Sensor Replacement (Upstream and Downstream)',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [{ startYear: 1997, endYear: 2006 }],
      engine: '4.0l-i6',
    },
    video: {
      youtubeId: 'zLxIxSYZPM8',
      title: 'How to Replace Upstream O2 Oxygen Sensor 1997-2006 Jeep Wrangler',
      channel: 'TRQ',
    },
    source: 'Generated by GarageIQ AI from publicly available sources (Bosch and Denso O2 sensor catalogs, NGK technical references, Mopar parts catalog, NHTSA TSBs and Complaints database, owner\'s manual, technician forums). Cross-checked and structured per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Engine',
    difficulty: 'intermediate',
    duration: '45 minutes - 1 hour per sensor',
    interval: 'When experiencing reduced fuel economy, hesitation, or scan-tool codes P0131, P0132, P0133, P0136, P0137, P0138, P0140, P0151, P0152, P0156, P0157, P0420, or P0430',
    description:
      'The 4.0L TJ uses two oxygen sensors — an upstream sensor (Bank 1 Sensor 1, B1S1) on the exhaust manifold or down-pipe before the catalytic converter, and a downstream sensor (Bank 1 Sensor 2, B1S2) on the exhaust pipe after the catalytic converter. The upstream sensor controls fuel mixture in real time and is the more performance-critical of the two. The downstream sensor monitors catalyst efficiency. Both are replaced with the same procedure but at different locations. The hardest part of the job is breaking the sensor loose from a hot-cycled exhaust pipe, which is why a dedicated O2 sensor socket and penetrating oil are required.',
    overview: {
      difficulty: 'Intermediate — straightforward procedure but stuck sensors are common; requires a dedicated O2 sensor socket',
      estimatedTime: '45 minutes to 1 hour per sensor (more if seized)',
      estimatedCost: '$30-90 per sensor (Bosch 15717/15718 ~$45-65, Denso ~$55-75, Mopar OE ~$80-90) vs $200-300 per sensor at a shop',
      whyMatters: 'A failing upstream O2 sensor causes 5-15% loss in fuel economy and degrades acceleration. A failing downstream sensor sets P0420 codes that mimic catalytic converter failure ($800+ if misdiagnosed).',
      safetyNote: 'The exhaust system reaches over 1,000°F during operation. Allow at least 1 hour of cool-down before working on any exhaust component. Wear leather gloves; even a "warm" exhaust will burn skin instantly.',
    },
    tools: [
      'O2 sensor socket (22mm with cutout for the wire) — required',
      'Breaker bar or long ratchet',
      '10mm socket and wrench (battery cable)',
      'Penetrating oil (PB Blaster, Kroil, or similar — soak overnight if possible)',
      'Anti-seize compound (only if sensor is not pre-coated)',
      'Floor jack and jack stands (for downstream sensor access)',
      'Wheel chocks',
      'Heat-resistant work gloves',
      'Safety glasses',
      'Clean shop rag',
    ],
    parts: [
      {
        id: 'o2-upstream',
        name: 'Upstream Oxygen Sensor (Bank 1 Sensor 1)',
        partNumber: 'Bosch 15717 or Denso 234-4012 (4-wire heated, fits 4.0L 1997-2006). Mopar OE: 56029084AA',
        quantity: 1,
        price: 55,
        category: 'required',
        notes: 'OE-fit aftermarket Bosch and Denso sensors are the standard. Avoid generic "universal" splice-in sensors — connector matters.',
      },
      {
        id: 'o2-downstream',
        name: 'Downstream Oxygen Sensor (Bank 1 Sensor 2)',
        partNumber: 'Bosch 15718 or Denso 234-4012 (4-wire heated). Mopar OE: 56029084AA (same in some applications)',
        quantity: 1,
        price: 55,
        category: 'optional',
        notes: 'Required only if replacing the downstream sensor. Some applications use the same sensor for both positions; verify with the part lookup.',
      },
      {
        id: 'antiseize',
        name: 'Anti-seize compound',
        partNumber: 'Permatex 80078 nickel anti-seize',
        quantity: 1,
        price: 8,
        category: 'optional',
        notes: 'Required only if the new sensor does not arrive with anti-seize pre-applied to the threads. Most OE-fit aftermarket sensors are pre-coated.',
      },
    ],
    safetyInformation:
      'The exhaust system operates at over 1,000°F. Wait at least 1 hour after engine shutdown before touching any exhaust component, and verify temperature with a brief touch using a gloved hand before grasping. Always wear safety glasses — penetrating oil and rust flakes fall during sensor removal. Do not use copper or aluminum-based anti-seize on O2 sensors; only use nickel-based anti-seize specifically rated for O2 sensor threads to avoid contaminating the sensing element.',
    beforeYouStart: [
      'Confirm which sensor failed using OBD-II codes — Bank 1 codes (P0131-P0138) point to either sensor; the code suffix indicates upstream vs downstream',
      'Verify the engine has been off for at least 1 hour and the exhaust is cool to the touch (with gloves)',
      'Disconnect the battery negative cable',
      'For the downstream sensor, raise the rear of the vehicle and place jack stands; chock the front wheels',
      'Apply penetrating oil to the sensor threads and let soak for at least 30 minutes (overnight is better for stuck sensors)',
      'Have the new sensor in hand and verify the part number matches your specific year',
    ],
    steps: [
      {
        number: 1,
        title: 'Identify the Sensor and Allow Cool-Down',
        description:
          'From the scan tool code, determine whether the upstream (B1S1) or downstream (B1S2) sensor is the target. The upstream sensor is on the exhaust manifold or down-pipe just below the engine; the downstream sensor is on the exhaust pipe after the catalytic converter, roughly under the driver\'s seat area. Wait at least 1 hour after engine shutdown.',
        why: 'Wrong-sensor replacement is a common mistake — a P0136 code indicates the downstream sensor, not the upstream. Cool-down prevents burns and prevents sensor damage during installation.',
        tip: 'A quick gloved-hand temperature check at the exhaust pipe before reaching in confirms it is safe to work.',
        commonMistake: 'Replacing the upstream when the code points to the downstream. Always read the code definition before ordering parts.',
      },
      {
        number: 2,
        title: 'Disconnect the Battery and Apply Penetrating Oil',
        description:
          'Disconnect the negative battery cable. Locate the target sensor and apply penetrating oil generously to the threads where the sensor meets the exhaust pipe. Let soak at least 30 minutes — overnight if the sensor has been in place for many years.',
        why: 'Heat-cycled threads on exhaust components are notoriously seized. Penetrating oil is the difference between a 30-minute job and a broken sensor.',
        tip: 'Tap the sensor body lightly with a small hammer after applying oil — vibration helps the oil penetrate.',
        commonMistake: 'Skipping the soak. A seized sensor can shear off in the exhaust pipe, and removing the broken stub is a major repair.',
      },
      {
        number: 3,
        title: 'Raise the Vehicle (Downstream Sensor Only)',
        description:
          'For the downstream sensor: chock the front wheels, raise the rear of the vehicle with a floor jack at the rear axle or differential, and place jack stands under the frame rails. Lower the vehicle onto the stands. Verify stability before going underneath.',
        why: 'The downstream sensor is mid-vehicle and access is much easier from below with the rear raised.',
        tip: 'Raise enough to roll under comfortably — 18-24 inches of clearance.',
        commonMistake: 'Working under a vehicle supported only by a floor jack. Always use jack stands.',
      },
      {
        number: 4,
        title: 'Disconnect the Sensor Harness Connector',
        description:
          'Trace the sensor wire to its connector on the engine harness. Press the locking tab and pull the connector apart. The connector for the upstream sensor is typically near the back of the engine; the downstream connector is along the frame rail. Inspect both halves of the connector for corrosion.',
        why: 'Disconnecting the harness first prevents twisting the wires during sensor removal.',
        tip: 'A small flashlight helps locate the connector — it may be hidden behind a heat shield or harness clip.',
        commonMistake: 'Not fully unclipping the connector before twisting the sensor. The wires will twist and break at the sensor body.',
      },
      {
        number: 5,
        title: 'Break the Sensor Loose with the O2 Socket',
        description:
          'Place the O2 sensor socket over the sensor — the cutout in the side allows the wire to exit. Engage the socket fully. Use a breaker bar or long ratchet and apply steady pressure in the loosening direction. The sensor should break free with a noticeable "pop." If it does not move with reasonable force, apply more penetrating oil and wait another 15 minutes — do not over-force a stuck sensor.',
        why: 'The O2 socket is dedicated for a reason — its wire cutout protects the sensor wire, and its hex shape engages the sensor flats fully.',
        tip: 'Heat from a propane torch applied briefly (5-10 seconds) to the exhaust pipe boss around the sensor base helps with heavily seized sensors. Do not heat the sensor body itself.',
        commonMistake: 'Using a regular open-end wrench. The wrench rounds off the sensor flats and the sensor cannot be removed without cutting it out.',
      },
      {
        number: 6,
        title: 'Remove the Sensor and Inspect Threads',
        description:
          'Once broken loose, unscrew the sensor by hand. Inspect the threads on the exhaust pipe boss for damage — light carbon deposits are normal, but stripped or galled threads are not. Wipe the threads clean with a clean rag.',
        why: 'Clean, intact threads are essential for the new sensor to seat correctly and seal against exhaust gas leak.',
        tip: 'A thread chaser (M18 x 1.5 for most O2 sensors) cleans threads without removing material — useful if heavy carbon is present.',
        commonMistake: 'Using a tap on the threads instead of a thread chaser. A tap removes metal and can ruin the thread fit.',
      },
      {
        number: 7,
        title: 'Compare New Sensor to Old',
        description:
          'Compare the new sensor to the old one — verify the connector matches, the body length matches, the wire length is sufficient to reach the harness, and the thread length is the same. Most OE-fit aftermarket sensors are designed to be visual twins of the OE part.',
        why: 'Wrong-sensor risk is real for online purchases. A sensor with a different connector requires splicing, which contaminates the warranty and can introduce wiring faults.',
        tip: 'If the connector is different, return the sensor and order the OE-fit equivalent. Splice-in sensors should be a last resort.',
        commonMistake: 'Installing a wrong-application sensor with a splice. Even a good splice can introduce intermittent faults that are hard to diagnose later.',
      },
      {
        number: 8,
        title: 'Apply Anti-Seize and Hand-Thread the New Sensor',
        description:
          'Verify the new sensor has anti-seize pre-applied to the threads (most OE-fit aftermarket sensors do). If not, apply a thin coat of nickel-based anti-seize to the threads only — never on the sensor body or sensing element. Hand-thread the new sensor into the exhaust pipe boss until it is finger-tight.',
        why: 'Anti-seize prevents the next replacement from being a stuck-sensor nightmare. Hand-threading prevents cross-threading the soft exhaust pipe boss material.',
        tip: 'Only nickel-based anti-seize is appropriate for O2 sensors. Copper or aluminum types can contaminate the sensing element.',
        commonMistake: 'Applying anti-seize to the sensor body instead of the threads, or using copper anti-seize on an O2 sensor.',
      },
      {
        number: 9,
        title: 'Torque the Sensor and Reconnect',
        description:
          'Use the O2 sensor socket and torque wrench to torque the sensor to specification. Then route the sensor wire along the original path and clip into the harness retainers. Reconnect the harness connector — push straight on until the lock clicks.',
        why: 'Correct torque seals the sensor against exhaust leakage without crushing the sensor body. Wire routing prevents heat damage and contact with moving parts.',
        tip: 'A small dab of dielectric grease at the connector pins extends connector life in the high-heat under-vehicle environment.',
        commonMistake: 'Over-torquing the sensor and crushing the sensing element. Under-torque causes exhaust leak and incorrect signal.',
      },
      {
        number: 10,
        title: 'Lower, Reconnect Battery, and Test',
        description:
          'Lower the vehicle off the jack stands if raised. Reconnect the battery negative cable and torque to spec. Start the engine and let it idle for 5 minutes to warm the new sensor. Take a road test that includes light cruise, light acceleration, and several brake stops to give the PCM data for adaptation. Clear any pending codes and confirm none return after a full drive cycle.',
        why: 'Heated O2 sensors need a warm-up period to begin reporting accurate values. The PCM also needs a drive cycle to update its fuel-trim adaptation.',
        tip: 'A scan tool that shows live O2 sensor voltage should display upstream sensor voltage oscillating between 0.1V and 0.9V approximately once per second when warmed. Downstream voltage should be much steadier and stay near 0.5-0.7V.',
        commonMistake: 'Driving aggressively immediately after replacement. Fuel trim values may be skewed for the first few minutes — drive normally and let the PCM adapt.',
      },
    ],
    torqueSpecifications: [
      {
        fastener: 'O2 sensor (upstream and downstream)',
        specification: '33 ft-lbs (45 Nm). Verify torque spec 33 ft-lbs (45 Nm) from OEM service manual.',
        notes: 'Manufacturer ranges typically 30-44 ft-lbs (40-60 Nm). 33 ft-lbs is the common middle value. Always verify against your sensor manufacturer specification.',
      },
      {
        fastener: 'Battery negative terminal nut',
        specification: '5 ft-lbs (7 Nm). Verify torque spec 5 ft-lbs (7 Nm) from OEM service manual.',
        notes: 'Snug but not tight; over-torque distorts the lead post.',
      },
    ],
    fluidSpecifications: [],
    troubleshooting: [
      {
        problem: 'Sensor will not break loose despite penetrating oil',
        likelyCause: 'Long-cycled sensor with severely seized threads, common after 100,000+ miles',
        whatToCheck: 'Verify the O2 socket is fully engaged on the sensor flats. Check for corrosion on the exhaust pipe boss.',
        fix: 'Apply heat from a propane torch to the boss (not the sensor body) for 5-10 seconds while applying breaker-bar pressure. If still stuck after 2 attempts, professional service may be needed to avoid breaking the sensor in the boss.',
      },
      {
        problem: 'Code returns after replacement',
        likelyCause: 'Wrong-application sensor, faulty new sensor, or different root cause (exhaust leak upstream of sensor, fuel-trim issue)',
        whatToCheck: 'Verify the part number is correct. Inspect for exhaust leaks at the manifold gasket, manifold-to-pipe joint, or any other location upstream of the sensor.',
        fix: 'Repair any exhaust leaks first. Swap to a known-good Bosch or Denso sensor. If P0420 returns specifically, the catalytic converter itself may be failing.',
      },
      {
        problem: 'Sensor body broke off in the exhaust pipe',
        likelyCause: 'Excessive force during removal, severely corroded sensor',
        whatToCheck: 'Inspect the broken stub remaining in the exhaust pipe boss. Determine if any of the threaded portion is salvageable.',
        fix: 'A specialty O2 sensor extractor tool (also called a "broken sensor remover") can grip the inner sleeve and back the stub out. If the threads are damaged, the exhaust pipe section may need replacement.',
      },
      {
        problem: 'Engine runs rich or rough after upstream replacement',
        likelyCause: 'Fuel trim not yet adapted, or exhaust leak introducing oxygen ahead of the sensor',
        whatToCheck: 'Drive normally for 50-100 miles to allow PCM adaptation. Inspect the manifold-to-pipe joint and manifold gasket for leak.',
        fix: 'If running rich persists after 100 miles of mixed driving, troubleshoot for an exhaust leak or a sticking injector. Long-term fuel-trim values on a scan tool will guide diagnosis.',
      },
    ],
    proTips: [
      'A dedicated O2 sensor socket is non-negotiable for this job. The cutout for the wire is the difference between a clean removal and a broken sensor.',
      'Apply nickel-based anti-seize to the threads of every replacement sensor — even if pre-coated, a touch more saves the next mechanic. Never use copper anti-seize on O2 sensors.',
      'For severely seized sensors, an overnight soak in penetrating oil is far more effective than a 30-minute soak. Plan the job a day ahead if the sensor has been in for many years.',
      'When replacing one O2 sensor, consider whether the other is at end-of-life. Both sensors typically wear at similar rates, and the second sensor is much faster to install while you have the tools out.',
    ],
    successIndicators: [
      'No O2 sensor codes return after clearing and a full drive cycle',
      'Upstream sensor voltage oscillates 0.1V to 0.9V once per second when warmed (visible on a live scan tool)',
      'Downstream sensor voltage stays near 0.5-0.7V (relatively steady) when catalyst is healthy',
      'Fuel economy returns to expected baseline after 100 miles of mixed driving',
      'No exhaust leak audible at the new sensor location',
    ],
    whenToSeekHelp: 'If a sensor breaks off in the exhaust pipe boss and a sensor extractor cannot remove the stub, professional service is recommended to either extract or replace the affected exhaust pipe section. If P0420 codes return immediately after replacing both O2 sensors, the catalytic converter itself is likely the root cause and replacement of the cat assembly may be needed.',
    legalDisclaimer: 'This guide was generated by GarageIQ\'s AI from publicly available sources. Some information may be incorrect or out of date. Verify torque specifications and any safety-critical procedures against the OEM service manual for your vehicle before performing work. The Haynes Manual tab on this page links to the licensed manual for your vehicle. GarageIQ is not a substitute for a qualified mechanic. By using this guide you agree to GarageIQ\'s Terms of Service and assume responsibility for the work performed.',
    manualSource: {
      title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)',
      url: 'https://www.haynes.com/en-us/wrangler/jeep/2003',
      notes: 'Procedure sourced from public catalogs and forums; cross-reference torque values with your licensed manual before final tightening.',
    },
  },
  {
    id: 'tj-idle-air-control-valve-replacement',
    title: 'Idle Air Control (IAC) Valve Cleaning and Replacement',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [{ startYear: 1997, endYear: 2006 }],
      engine: '4.0l-i6',
    },
    video: {
      youtubeId: 'n4tLZ40eXQk',
      title: 'How To Replace IAC and TPS Jeep Wrangler',
      channel: 'Embark With Mark',
    },
    source: 'Generated by GarageIQ AI from publicly available sources (Mopar parts catalog, Standard Motor Products / Wells Vehicle Electronics catalogs, NHTSA TSBs and Complaints database, owner\'s manual, technician forums). Cross-checked and structured per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Engine',
    difficulty: 'beginner',
    duration: '30-45 minutes',
    interval: 'When experiencing idle hunting, stalling at idle, high or low idle speed, or scan-tool codes P0505, P0506, P0507, or P0508',
    description:
      'The Idle Air Control (IAC) valve is a stepper-motor-driven valve mounted on the throttle body that meters bypass air around the closed throttle plate. The PCM uses the IAC to maintain a target idle speed regardless of load (A/C compressor, alternator, power-steering pump). Carbon buildup on the IAC pintle is by far the most common cause of idle problems on the 4.0L TJ — cleaning often resolves the issue without replacement. If cleaning does not restore idle stability, the IAC stepper motor itself has likely failed and full replacement is needed.',
    overview: {
      difficulty: 'Beginner — two screws, one connector; cleaning is often sufficient',
      estimatedTime: '30 minutes (clean) to 45 minutes (replace)',
      estimatedCost: '$8 (carb cleaner only, for cleaning) or $35-90 for new IAC (Mopar OE ~$70-90, aftermarket Standard AC54 ~$35-50) vs $150-250 at a shop',
      whyMatters: 'A surging or stalling idle is one of the most common 4.0L TJ complaints. Cleaning the IAC takes 30 minutes and resolves the issue most of the time. If cleaning does not work, replacement is straightforward.',
      safetyNote: 'Disconnect the battery before disturbing electronic components. Use carb cleaner in a well-ventilated area — the fumes are harmful. Do not spray carb cleaner directly into the throttle body if any electronic sensor is exposed.',
    },
    tools: [
      'T20 Torx bit or #2 Phillips screwdriver (verify against your existing screws)',
      '10mm wrench or socket (battery cable)',
      'Inch-pound torque wrench',
      'Carburetor cleaner spray (CRC or Berryman B-12)',
      'Soft-bristle brush (toothbrush works)',
      'Clean shop rags',
      'Safety glasses',
      'Nitrile gloves',
    ],
    parts: [
      {
        id: 'iac-valve',
        name: 'Idle Air Control Valve',
        partNumber: 'Mopar 4874373AB (4.0L) — aftermarket Standard AC54, Wells AC4029',
        quantity: 1,
        price: 50,
        category: 'optional',
        notes: 'Required only if cleaning does not restore idle. Source the OE Mopar unit if possible — aftermarket reliability is variable.',
      },
      {
        id: 'iac-gasket',
        name: 'IAC valve gasket / O-ring',
        partNumber: 'Usually included with new IAC; Mopar 53006714 if separate',
        quantity: 1,
        price: 3,
        category: 'optional',
        notes: 'Required if replacing IAC. Reusing a hardened gasket causes vacuum leak.',
      },
    ],
    safetyInformation:
      'Disconnect the battery negative cable before unplugging or replacing electronic sensors. Carburetor cleaner is highly flammable and the fumes are harmful — work in a well-ventilated area, do not smoke, and avoid skin contact. Do not spray cleaner into the throttle body when other electronic sensors (TPS, MAP) are exposed — overspray can damage them.',
    beforeYouStart: [
      'Confirm the diagnosis — codes P0505 through P0508 specifically point to IAC circuit; symptoms without codes (surging, hunting, occasional stall) also commonly resolve with IAC cleaning',
      'Decide on cleaning vs replacement — cleaning first is the recommended approach unless the IAC has been cleaned recently or is visibly damaged',
      'Disconnect the battery negative cable',
      'Have a clean shop rag ready — debris dropping into the throttle body is a problem',
      'Note the orientation of the IAC before removing — the valve is keyed but only seats one way',
    ],
    steps: [
      {
        number: 1,
        title: 'Disconnect the Battery',
        description:
          'Use a 10mm wrench or socket to loosen the negative battery terminal nut. Lift the cable off the post and tuck it aside. Wait 60 seconds before continuing.',
        why: 'IAC service involves disturbing the harness and the valve. A live PCM circuit can spike when the IAC is disconnected.',
        tip: 'Wrap the loose terminal in a shop rag to prevent accidental contact.',
        commonMistake: 'Skipping the disconnect because the engine is off — the harness is still live.',
      },
      {
        number: 2,
        title: 'Locate the IAC on the Throttle Body',
        description:
          'Find the throttle body — a round housing where the air intake tube meets the intake manifold. The IAC is the taller cylindrical sensor mounted on the throttle body, near the throttle plate. It has a 4-pin electrical connector.',
        why: 'Distinguishing the IAC from the TPS prevents accidentally disturbing the wrong sensor — the TPS is flatter and has a 3-pin connector.',
        tip: 'On the 4.0L the IAC sits on the top of the throttle body, with the connector on the side.',
        commonMistake: 'Mistaking the TPS for the IAC. The IAC is the taller of the two and has 4 pins; the TPS is flatter and has 3 pins.',
      },
      {
        number: 3,
        title: 'Disconnect the Harness Connector',
        description:
          'Press the locking tab on the connector and pull the connector straight off the IAC. Inspect the pins for corrosion. The connector lock can be stiff — gentle side-to-side rocking helps.',
        why: 'Disconnecting the harness first prevents wire damage when the IAC body is rotated during removal.',
        tip: 'Apply a small dab of dielectric grease to the connector pins before reassembly.',
        commonMistake: 'Pulling on the wires instead of the connector body.',
      },
      {
        number: 4,
        title: 'Remove the IAC Mounting Screws',
        description:
          'Use a T20 Torx bit (or #2 Phillips depending on year) to remove the two mounting screws holding the IAC to the throttle body. Loosen them gradually and alternate between the two. Set them aside in a magnetic dish.',
        why: 'Alternating loosening keeps the IAC body parallel to the throttle body and avoids binding the mounting flange.',
        tip: 'A magnetic-tip driver keeps the small screws from falling into the engine bay.',
        commonMistake: 'Stripping the screw heads with an oversized bit. Match the bit precisely to the recess.',
      },
      {
        number: 5,
        title: 'Remove the IAC and Inspect',
        description:
          'Pull the IAC straight out of the throttle body. The valve has a tapered pintle that extends into the throttle body bypass passage. Inspect the pintle for carbon buildup — heavy black deposits are common and are the typical cause of idle problems. Inspect the bypass passage in the throttle body for similar buildup.',
        why: 'Visual inspection determines whether cleaning or replacement is appropriate. Heavy carbon = cleaning. Stuck pintle that will not move freely = replacement.',
        tip: 'Wiggle the pintle by hand — it should move freely with light spring pressure. A stuck pintle means stepper motor failure and replacement.',
        commonMistake: 'Assuming replacement is needed without inspecting. Most IAC issues are carbon buildup that cleans away in 5 minutes.',
      },
      {
        number: 6,
        title: 'Clean the IAC and Throttle Body Passage',
        description:
          'Hold the IAC over a shop rag. Spray carb cleaner directly onto the pintle and the surrounding sealing surface. Use a soft brush to gently scrub the carbon buildup. Repeat the spray-and-scrub cycle until the pintle is clean. Spray carb cleaner into the bypass passage on the throttle body and wipe out with a clean rag. Do not spray into the throttle body main bore.',
        why: 'Even a small amount of carbon on the pintle changes the airflow profile and causes idle instability. A thorough clean restores correct flow.',
        tip: 'Avoid getting carb cleaner into the IAC electrical connector — it can damage the seal. Hold the IAC pintle-down while spraying.',
        commonMistake: 'Spraying carb cleaner into the throttle body main bore — overspray can hit the TPS or MAP sensor and damage them.',
      },
      {
        number: 7,
        title: 'Verify Pintle Movement',
        description:
          'After cleaning, gently push the pintle in with a finger and release. It should retract smoothly under spring pressure with no sticking. If the pintle binds or does not return, the stepper motor is at end-of-life and replacement is the correct fix.',
        why: 'A stepper-motor mechanical fault cannot be cleaned out — it requires replacement. Confirming pintle movement before reinstall avoids a wasted reassembly.',
        tip: 'Compare with a new IAC if available — the difference in spring tension is noticeable.',
        commonMistake: 'Reinstalling a sticky IAC after cleaning. The carbon was symptom of a deeper failure, and the idle problem will return quickly.',
      },
      {
        number: 8,
        title: 'Install (Cleaned or New) IAC',
        description:
          'If installing a new IAC, transfer the original gasket or install the new one supplied. Align the IAC to the mounting hole and seat against the throttle body in the original orientation. Hand-thread both mounting screws.',
        why: 'Hand-threading prevents cross-threading the small screws into the soft aluminum throttle body.',
        tip: 'If the IAC will not seat flush, the gasket may be folded — pull and reseat carefully.',
        commonMistake: 'Forcing the IAC down with the screws when the gasket is misaligned.',
      },
      {
        number: 9,
        title: 'Torque the Mounting Screws',
        description:
          'Using an inch-pound torque wrench, torque both mounting screws alternately in two passes — first to half final torque, then to specification. Snug only — these are small machine screws into aluminum.',
        why: 'Even torque keeps the IAC parallel to the throttle body and prevents distortion of the gasket or sensor body.',
        tip: 'If you do not have an inch-pound torque wrench, the screws should require only firm pressure with a small (4-inch) screwdriver. Stop the moment resistance builds.',
        commonMistake: 'Over-torquing and stripping the soft aluminum threads.',
      },
      {
        number: 10,
        title: 'Reconnect, Idle Relearn, and Test',
        description:
          'Reconnect the harness — push straight on until the lock clicks. Reconnect the battery negative cable and torque to spec. Start the engine and let it idle for at least 5 minutes without touching the throttle. The idle may be rough or hunt during the first 1-2 minutes — this is normal as the PCM relearns the IAC steps. After 5 minutes the idle should settle to a steady speed. Take a brief road test that includes a stop and restart, then clear any pending codes.',
        why: 'The PCM idle relearn is essential after IAC service. The PCM has stored "step counts" for the old IAC condition; it must learn the new condition before idle stabilizes.',
        tip: 'A scan tool that displays IAC step count should show the value adjusting smoothly during the relearn period.',
        commonMistake: 'Driving the vehicle aggressively during the first few minutes. The idle will be unstable until relearn is complete — give it the full 5 minutes.',
      },
    ],
    torqueSpecifications: [
      {
        fastener: 'IAC mounting screws',
        specification: '27 in-lbs (3.0 Nm). Verify torque spec 27 in-lbs (3.0 Nm) from OEM service manual.',
        notes: 'Small machine screws into aluminum throttle body. Snug only — over-torque strips the threads.',
      },
      {
        fastener: 'Battery negative terminal nut',
        specification: '5 ft-lbs (7 Nm). Verify torque spec 5 ft-lbs (7 Nm) from OEM service manual.',
        notes: 'Snug but not tight; over-torque distorts the lead post.',
      },
    ],
    fluidSpecifications: [],
    troubleshooting: [
      {
        problem: 'Idle still hunts or surges after cleaning',
        likelyCause: 'Stepper motor failure, vacuum leak, dirty throttle body, or other root cause',
        whatToCheck: 'Verify the IAC pintle moves smoothly under spring pressure. Inspect intake hose clamps and vacuum lines for cracks. Spray carb cleaner around vacuum-line connections with the engine running — RPM change indicates a leak.',
        fix: 'If the pintle is sticky, replace the IAC. If a vacuum leak is found, repair it. If neither, the throttle body itself may need cleaning.',
      },
      {
        problem: 'Code P0505/P0506/P0507/P0508 returns after replacement',
        likelyCause: 'Wrong-application IAC, faulty new IAC, or harness fault',
        whatToCheck: 'Verify the part number is correct for your year. Inspect the harness connector pins for corrosion or bent pins.',
        fix: 'Swap to a known-good OE Mopar IAC. If the code persists, troubleshoot the harness back to the PCM.',
      },
      {
        problem: 'Engine stalls immediately on first startup after install',
        likelyCause: 'Gasket folded or missing, allowing major vacuum leak, or harness connector not fully seated',
        whatToCheck: 'Inspect the gasket seating around the IAC. Verify the harness connector latched fully.',
        fix: 'Pull the IAC, reinstall with correct gasket orientation. Re-seat the harness connector.',
      },
      {
        problem: 'High idle (1500+ RPM) after install',
        likelyCause: 'IAC pintle stuck in extended position, or large vacuum leak introduced during work',
        whatToCheck: 'Verify the pintle was free before installation. Inspect intake hose at the throttle body for proper seating.',
        fix: 'Replace IAC if pintle is stuck. Re-seat intake hose if loose.',
      },
    ],
    proTips: [
      'Always try cleaning before replacement. Carbon buildup is the cause of most IAC complaints on the 4.0L TJ, and a 5-minute clean fixes 70%+ of cases.',
      'Avoid spraying carb cleaner directly into the throttle body main bore. Overspray on the TPS or MAP sensor can permanently damage them.',
      'After replacement or cleaning, give the engine the full 5-minute idle relearn before deciding whether the fix worked. The first 1-2 minutes will be rough.',
      'When buying a replacement IAC, the OE Mopar unit (4874373AB) is consistently the most reliable. Aftermarket Standard and Wells units work, but premature failure is more common.',
    ],
    successIndicators: [
      'Idle settles to a steady 700-800 RPM within 5 minutes of restart',
      'No idle hunting, surging, or stalling during 10 minutes of warm idle',
      'No code P0505 through P0508 returns after a clear and full drive cycle',
      'Idle speed remains stable when A/C is engaged or steering is turned at full lock',
      'IAC step count on a scan tool stabilizes within a normal range (typically 20-40 steps when warm)',
    ],
    whenToSeekHelp: 'If idle problems persist after both cleaning AND replacement, the diagnosis points to a vacuum leak, a faulty TPS or MAP sensor, a clogged throttle body, or a PCM issue — broaden the troubleshooting beyond the IAC. A scan tool capable of monitoring IAC step count, MAP voltage, and short-term fuel trim is needed for the next stage of diagnosis.',
    legalDisclaimer: 'This guide was generated by GarageIQ\'s AI from publicly available sources. Some information may be incorrect or out of date. Verify torque specifications and any safety-critical procedures against the OEM service manual for your vehicle before performing work. The Haynes Manual tab on this page links to the licensed manual for your vehicle. GarageIQ is not a substitute for a qualified mechanic. By using this guide you agree to GarageIQ\'s Terms of Service and assume responsibility for the work performed.',
    manualSource: {
      title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)',
      url: 'https://www.haynes.com/en-us/wrangler/jeep/2003',
      notes: 'Procedure sourced from public catalogs and forums; cross-reference torque values with your licensed manual before final tightening.',
    },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — ENGINE OIL & FILTER CHANGE (4.0L I6)
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-oil-change-4pt0l-i6',
    title: 'Engine Oil & Filter Change (4.0L I6)',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [{ startYear: 1997, endYear: 2006 }],
      engine: '4.0l-i6',
    },
    video: {
      youtubeId: 't3iLukIGpq4',
      title: 'Jeep TJ Oil Change 4.0L | How To',
      channel: 'KyleSVT',
    },
    source: 'Generated by GarageIQ AI from publicly available sources (Mopar parts catalog, OEM oil filter cross-reference charts from FRAM/Wix/Mobil 1, owner\'s manual capacity tables, technician forums, Wrangler community service threads). Cross-checked and structured per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Routine Maintenance & Fluids',
    difficulty: 'beginner',
    duration: '30-45 minutes',
    interval: 'Every 3,000 miles (conventional) or every 5,000-7,500 miles (full synthetic). The 4.0L I6 is tolerant of oil but unforgiving of low oil — check level monthly.',
    description:
      'The 4.0L AMC straight-six (the engine in roughly 80% of TJs sold) is one of the most reliable mass-produced engines ever built, and the single biggest factor in keeping it that way is regular oil changes. The 4.0L holds six quarts with the filter — significantly more than the 2.4L — and the spin-on filter is mounted at an awkward angle on the passenger side rear of the block, which is the part new owners usually find frustrating. This guide walks through the I6-specific quirks: the filter orientation, the steel-pan drain plug torque, and the oil-pressure light verification on first start.',
    overview: {
      difficulty: 'Beginner — no special tools beyond a filter wrench and a torque wrench',
      estimatedTime: '30-45 minutes once familiar with the filter location',
      estimatedCost: '$30-45 (six quarts of oil + Mopar/FRAM/Wix filter) vs $60-80 at a quick-lube shop',
      whyMatters: 'Oil failure is the most common cause of premature engine death on otherwise-bulletproof 4.0L engines. The pickup tube and oil pump are inexpensive but require dropping the pan to service — far easier to change oil on schedule than to fix the consequences.',
      safetyNote: 'Hot engine oil and a hot exhaust manifold are both directly above the filter. Allow at least 15-20 minutes of cool-down after shutdown before starting. Always support the vehicle on jack stands — never on a floor jack alone.',
    },
    tools: [
      'Floor jack and jack stands (rated for vehicle weight)',
      'Oil drain pan with at least 8-quart capacity (the 4.0L holds 6 quarts)',
      '13mm or 1/2-inch socket and ratchet (drain plug — verify on your vehicle)',
      'Oil filter wrench (cup-style or strap; the angled mounting makes a band wrench easier)',
      'Torque wrench capable of 25 ft-lbs / 34 Nm',
      'Funnel',
      'Clean shop rags or paper towels',
      'Cardboard or large drop cloth (the 4.0L filter is famously messy)',
      'Nitrile gloves',
    ],
    parts: [
      {
        id: 'engine-oil-4pt0l-i6',
        name: 'Engine Oil — 5W-30 or 10W-30',
        partNumber: 'Mobil 1, Valvoline MaxLife, Castrol GTX, Pennzoil Platinum (any API SN/SP rated)',
        quantity: 6,
        price: 30,
        category: 'required',
        notes: '6 US quarts with filter change. The 4.0L FSM specifies SAE 10W-30 as primary; 5W-30 is acceptable for cold climates. Modern full-synthetic 5W-30 or 10W-30 is the best long-term choice and is fully backward-compatible with the 4.0L.',
      },
      {
        id: 'oil-filter-4pt0l-i6',
        name: 'Oil Filter (4.0L I6 spin-on)',
        partNumber: 'Mopar 4884899AC — equivalents: FRAM PH16 / Wix 51515 / Mobil 1 M1-104A / K&N HP-1008',
        quantity: 1,
        price: 8,
        category: 'required',
        notes: 'The 4.0L uses a long-body spin-on filter. Many shorter "fits" filters from cross-references will physically install but reduce filtration capacity — stick to the listed equivalents for longest service life.',
      },
      {
        id: 'drain-plug-washer',
        name: 'Drain Plug Crush Washer (Mopar 6035268)',
        partNumber: 'Mopar 6035268 (or 14mm copper crush washer equivalent)',
        quantity: 1,
        price: 1,
        category: 'recommended',
        notes: 'The crush washer should be replaced anytime you have a slow leak from the drain plug. Many DIYers reuse the original successfully for several changes; if you ever see seepage, replace it.',
      },
    ],
    safetyInformation:
      'The exhaust manifold on the 4.0L runs the full length of the passenger-side head and is directly above the oil filter mounting location. It will burn through gloves and skin instantly when hot. Allow a minimum of 15-20 minutes of cool-down after shutdown. Hot oil exiting the drain plug can splash and cause burns — wear safety glasses. Never work under a vehicle supported only by a floor jack; use rated jack stands on solid, level ground. Used motor oil is classified as a hazardous waste in most states and a known carcinogen on prolonged skin contact — wear nitrile gloves and dispose of used oil at any auto-parts store recycling center (most accept up to 5 gallons free).',
    beforeYouStart: [
      'Run the engine for 2-3 minutes at idle to thin the oil if it has been sitting cold for more than a day, then shut down and wait 15-20 minutes before starting work',
      'Confirm you have the correct filter on hand before draining the old oil — there is nothing worse than draining 6 quarts and discovering the filter is wrong',
      'Park on level ground with the wheels chocked and the transmission in Park (auto) or first gear (manual)',
      'Have 8 quarts of catch capacity available — the 4.0L holds 6 and you do not want to run out of pan',
      'Lay cardboard or a drop cloth under the work area; the angled filter on the I6 is messy on removal',
      'Read the dipstick before draining — note the existing level and color so you have a reference point after refill',
    ],
    steps: [
      {
        number: 1,
        title: 'Bring Engine to Operating Temperature, Then Shut Down',
        description:
          'Start the engine and let it idle for 2-3 minutes (or drive 1-2 miles if it is cold) to warm and thin the oil so it drains completely with suspended contaminants. Shut the engine off and wait 15-20 minutes for the exhaust manifold to cool and for hot oil to drop below burn-injury temperature. Open the hood and locate the oil-fill cap on the valve cover.',
        why: 'Cold oil drains slowly and leaves contaminants behind in the pan. Hot oil drains fast but is dangerous to handle. The 15-20 minute window is the sweet spot — warm enough to drain cleanly, cool enough to handle.',
        tip: 'If you cannot comfortably hold your hand against the exhaust manifold (without touching it), it is still too hot. Wait longer.',
        commonMistake: 'Working with the engine fully hot. Hot oil and a 400°F exhaust manifold turn a routine job into an ER visit.',
      },
      {
        number: 2,
        title: 'Raise and Support the Vehicle',
        description:
          'Place the floor jack under the front frame crossmember (not the front bumper or sway bar). Raise the front of the vehicle high enough to slide an 8-quart drain pan under the oil pan. Position jack stands under the frame rails just behind the front lower control arm mounts. Slowly lower the vehicle onto the stands, then test the support by pushing on the front bumper — the vehicle should not move on the stands.',
        why: 'The 4.0L oil pan sits low and the drain plug is at the rear of the pan. You need clearance for the pan and your hands. Jack stands provide redundant support if the floor jack fails.',
        tip: 'On Rubicon-trim TJs with the heavier skid plate, you may need to drop the front skid bolts to access the drain plug — verify before you commit.',
        commonMistake: 'Working under a vehicle on the floor jack alone. Floor jacks fail without warning. Stands are non-optional.',
      },
      {
        number: 3,
        title: 'Loosen the Oil Fill Cap',
        description:
          'Before draining, go back to the engine bay and loosen (or remove) the oil-fill cap on the top of the valve cover. This vents the crankcase and lets the oil drain out faster and more completely.',
        why: 'A sealed crankcase resists drainage as a vacuum forms. Cracking the fill cap open speeds drainage by 30-50% and ensures more of the contaminated oil leaves the pan.',
        tip: 'Set the cap on the air-filter housing — a place you will see it during refill. Forgetting to reinstall the cap is a top-five mistake.',
        commonMistake: 'Skipping this step. The drainage is slower and leaves more contaminated oil in the pan.',
      },
      {
        number: 4,
        title: 'Drain the Oil',
        description:
          'Slide the drain pan under the oil pan, offset slightly toward the rear of the vehicle (oil will arc backward as it drains). Using a 13mm socket or 1/2-inch socket, break the drain plug loose by turning counterclockwise. Once it spins freely, finish removing it by hand — wear a nitrile glove and accept that the last thread will release a stream of warm oil. Pull the plug clear and let the pan drain completely. This takes 5-10 minutes.',
        why: 'The 4.0L oil pan is steel and the drain plug is well-sealed; leftover oil clings to the inside of the pan if you rush. Letting it drain fully removes the most contaminants.',
        tip: 'Inspect the drain plug magnetic tip (if equipped) for metal flakes. A small amount of grey paste is normal break-in material; chunks of metal indicate internal wear and warrant further investigation.',
        commonMistake: 'Reinstalling the plug while oil is still actively dripping. Let the pan stop dripping (a slow drip every 5+ seconds) before reinstalling.',
      },
      {
        number: 5,
        title: 'Inspect and Reinstall the Drain Plug',
        description:
          'Wipe the drain plug threads clean with a rag and inspect the crush washer. If the washer is flattened, deformed, or shows oil seepage on the underside, replace it (Mopar 6035268). Wipe the oil pan drain hole and surrounding area clean. Thread the plug back in BY HAND first to verify it spins easily for at least three full turns — this confirms you have not cross-threaded the plug into the pan. Then snug it down with a socket and torque to 25 ft-lbs (34 Nm). Verify torque spec 25 ft-lbs (34 Nm) from OEM service manual.',
        why: 'Cross-threading the drain plug is the most common way to permanently damage the oil pan, and it usually happens when the plug is started with a ratchet instead of fingers. Hand-starting catches mistakes before they damage threads. Torque-to-spec prevents over-tightening (which strips threads on the steel pan over many service intervals) and under-tightening (which leaks).',
        tip: 'If the plug feels gritty or stiff turning by hand, stop. Back it out and check the threads. Forcing it will strip the pan.',
        commonMistake: 'Reusing a bottoming-out drain plug with no crush washer. The crush washer is the seal — without one (or with a flat one), you will have a chronic slow leak.',
      },
      {
        number: 6,
        title: 'Locate and Remove the Oil Filter',
        description:
          'On the 4.0L, the spin-on filter mounts on the passenger side, rear of the engine block, angled downward. Slide the drain pan under the filter (it will dump roughly half a quart of trapped oil when removed). Using an oil filter wrench, turn the filter counterclockwise to break it loose, then unscrew it the rest of the way by hand. Tip it slightly upward as it clears the threads to minimize spillage. Wipe the filter mounting flange on the block clean — make sure the old filter\'s rubber gasket has come off with the filter and is not stuck to the block.',
        why: 'A stuck old gasket is the single most common cause of a "leaks immediately after oil change" problem. The new filter\'s gasket cannot seal against an old gasket, and the result is a 6-quart leak in 60 seconds.',
        tip: 'A band-style filter wrench works better than a cup-style at this awkward angle. If you bought a cup wrench and it does not fit cleanly, switch to a band before you round off the filter.',
        commonMistake: 'Failing to verify that the old gasket came off with the filter. Always run a finger around the mounting flange before installing the new filter.',
      },
      {
        number: 7,
        title: 'Prep and Install the New Filter',
        description:
          'Open the new filter. Dip your finger in fresh oil and wipe a thin film around the new filter\'s rubber gasket — this lets the gasket slide into final position without grabbing and tearing. Optionally, pre-fill the new filter about halfway with fresh oil to reduce the dry-start period (this works on the 4.0L because the filter mounts at an angle, not horizontally — pre-filled oil will not all spill out during installation). Hand-thread the filter onto the block until you feel the gasket make contact, then turn an additional three-quarters of a turn. Do not use a filter wrench to install — hand-tight plus 3/4 turn is the spec.',
        why: 'A lubricated gasket seals correctly the first time. An over-tightened filter (using a wrench on installation) will be impossible to remove next change without a piercing tool. Three-quarters of a turn past contact is the universally specified install torque for spin-on filters on this engine.',
        tip: 'Mark the filter with the install date and mileage in permanent marker. It makes future service much easier.',
        commonMistake: 'Tightening the filter with a wrench. The next person changing this oil — even if it is you — will curse this decision.',
      },
      {
        number: 8,
        title: 'Refill with Fresh Oil',
        description:
          'Lower the vehicle off the jack stands. Open the hood and remove the oil-fill cap (loosened in step 3). Insert a funnel and pour in 5 quarts of fresh oil. Wait 30 seconds for the oil to settle into the pan. Pull the dipstick, wipe clean, reinsert, and pull again to read the level. Add the sixth quart slowly while monitoring — stop when the level reaches the FULL mark. Most 4.0Ls take very close to a full 6 quarts; some take slightly less depending on how much old oil drained.',
        why: 'Pouring all 6 quarts at once and trusting the capacity rating risks overfilling. Overfilling a 4.0L causes oil aeration, oil pressure problems, and rear main seal damage. Trust the dipstick, not the bottle count.',
        tip: 'The 4.0L dipstick has a hatched safe zone. Aim for the upper third of that zone, not the very top mark. Slightly under is safer than over.',
        commonMistake: 'Dumping all 6 quarts in fast and not verifying with the dipstick. The 4.0L is much less tolerant of overfill than underfill.',
      },
      {
        number: 9,
        title: 'Reinstall Fill Cap, Start, and Verify',
        description:
          'Reinstall the oil-fill cap and verify it is fully seated and tight (the cap has a quarter-turn lock — make sure it locks). Start the engine. The oil-pressure warning light should extinguish within 5 seconds; if it does not, shut the engine off immediately and verify the filter is installed and the drain plug is in place. With the light off, let the engine idle for 60 seconds and walk around the vehicle looking for any drips or seepage at the filter or drain plug. Shut the engine off, wait 2-3 minutes for oil to settle, and recheck the dipstick — the level should now read in the upper third of the safe zone. Top off in 1/4-quart increments if needed.',
        why: 'The 5-second oil-pressure-light test is the single most important verification on any oil change. If pressure does not come up, the engine is running dry and continued operation will cause bearing damage in seconds.',
        tip: 'Some scan tools and the in-dash trip computer (on later TJs) will display oil pressure in PSI — if available, verify the engine reaches 40-60 PSI at idle warm.',
        commonMistake: 'Driving away without verifying the oil-pressure light extinguishes. A dry oil pump on first start can destroy bearings in under a minute.',
      },
      {
        number: 10,
        title: 'Test Drive and Final Verification',
        description:
          'Take the vehicle on a 5-10 minute drive at normal operating speeds, then return to level ground. Shut the engine off and wait 5 minutes. Check the dipstick one more time — the level should still be in the upper third of the safe zone. Look under the vehicle for any drips at the drain plug or filter. Wipe the dipstick area and oil-fill cap clean of any oil that splashed during refill. Record the date and mileage of the service in your maintenance log or app.',
        why: 'Driving the vehicle confirms the oil system holds pressure under load and reveals any slow leaks that would not show up at idle. The post-drive recheck verifies you have not lost any volume.',
        tip: 'Save your old filter or take a photo of the part number — this confirms you used the correct filter for warranty or troubleshooting purposes later.',
        commonMistake: 'Skipping the post-drive verification. Slow leaks often only show under driving load, not at idle.',
      },
    ],
    torqueSpecifications: [
      {
        fastener: 'Oil pan drain plug',
        specification: '25 ft-lbs (34 Nm). Verify torque spec 25 ft-lbs (34 Nm) from OEM service manual.',
        notes: 'Steel oil pan; the threads will tolerate this torque indefinitely if not over-tightened. Hand-start the plug for at least 3 full turns before applying any torque.',
      },
      {
        fastener: 'Oil filter (spin-on)',
        specification: 'Hand-tight plus 3/4 turn after gasket contact (approximately 12-17 ft-lbs / 16-23 Nm). Verify torque spec from OEM service manual or filter manufacturer instructions.',
        notes: 'Do not use a filter wrench for installation — only for removal. Pre-lubricate gasket with fresh oil.',
      },
    ],
    fluidSpecifications: [
      {
        fluid: 'Engine Oil',
        type: 'SAE 5W-30 or 10W-30, API SN/SP rated (full synthetic preferred)',
        capacity: '6 US quarts (5.7 L) with filter change',
        notes: 'The 4.0L FSM specifies 10W-30 as the primary recommendation. 5W-30 is approved for cold climates and is the more common modern choice. Synthetic oil is fully backward-compatible and recommended for any TJ that sees extended idle (off-roading) or extreme temperatures.',
      },
    ],
    troubleshooting: [
      {
        problem: 'Oil pressure light stays on after starting',
        likelyCause: 'New filter installed without pre-fill, drain plug missing/loose, or oil pump losing prime',
        whatToCheck: 'Shut off the engine immediately. Verify the drain plug is installed and the new filter is tight. Pull the dipstick and confirm oil level is correct.',
        fix: 'If level and components check out, prime the system by removing a coil wire (preventing start) and cranking for 5-10 seconds at a time until the light extinguishes; then reconnect the coil and start normally. If the light still stays on with verified oil level and a primed system, the oil pump or sender may be at fault — do not drive until diagnosed.',
      },
      {
        problem: 'Oil leaks from the filter area immediately after first start',
        likelyCause: 'Old filter gasket stuck to the block under the new filter, or new filter not tight',
        whatToCheck: 'Wipe the filter and surrounding area clean. Look for two stacked rubber gaskets (the old one and the new one) — only one should be present.',
        fix: 'Drain the oil and remove the new filter. Inspect the block flange and remove the old gasket if present. Install a fresh new filter (the gasket on the one you just removed may be damaged) with a fresh film of oil. Refill and re-test.',
      },
      {
        problem: 'Drain plug seeping after service',
        likelyCause: 'Crush washer flat or missing, or plug under-torqued',
        whatToCheck: 'Wipe the drain plug clean and watch for fresh oil forming at the threads',
        fix: 'Re-torque the plug to 25 ft-lbs. If seepage continues, drain enough oil to clear the plug, remove it, install a fresh crush washer, and reinstall to spec.',
      },
      {
        problem: 'Dipstick reads above the FULL mark after service',
        likelyCause: 'Overfilled with too much oil',
        whatToCheck: 'Verify the reading on level ground after the engine has sat 5+ minutes',
        fix: 'Use an oil-extractor or remove the drain plug briefly and let a small amount drain into a measuring container. Recheck level after each removal until in the safe-zone upper third. Overfill of more than half a quart should be corrected before driving — it can foam the oil and overload the rear main seal.',
      },
    ],
    proTips: [
      'The 4.0L has a documented "5,000 mile sweet spot" with full-synthetic oil — most owners running Mobil 1 or Pennzoil Platinum see no measurable wear at that interval, and the engine often goes 300,000+ miles on this schedule.',
      'Mark your filter or oil bottle with the install mileage in Sharpie. It is the simplest way to remember when service is due.',
      'Buy oil in a 5-quart jug plus a single 1-quart bottle. The 5-quart jug pours quickly to get most of the volume in, and the 1-quart bottle gives you fine control over the final top-off without risking overfill.',
      'Save the empty 5-quart jug — it is the perfect container for transporting your used oil to the recycling center. Most auto-parts stores accept up to 5 gallons of used oil free.',
      'If your 4.0L develops a small filter-area leak that resists tightening, the filter mounting adapter o-ring may be the cause (not the filter itself). That is a separate, more involved repair — but identifying it correctly saves you from chasing the wrong fix.',
    ],
    commonMistakes: [
      {
        mistake: 'Tightening the oil filter with a wrench during installation',
        consequence: 'Filter becomes nearly impossible to remove next change without a piercing tool. Hand-tight plus 3/4 turn is the spec.',
      },
      {
        mistake: 'Failing to verify the old filter gasket came off with the filter',
        consequence: 'Two stacked gaskets cannot seal — the result is a major oil leak within seconds of starting.',
      },
      {
        mistake: 'Trusting the 6-quart capacity rating without verifying with the dipstick',
        consequence: 'Overfill aerates the oil and pressurizes the rear main seal. Always verify with the dipstick.',
      },
      {
        mistake: 'Driving immediately after refill without verifying the oil-pressure light',
        consequence: 'A dry oil pump on first start can score bearings in seconds. The 5-second light-out test is non-optional.',
      },
      {
        mistake: 'Reusing a flattened crush washer indefinitely',
        consequence: 'Slow drain plug seepage that progresses to a steady drip. The crush washer is a $1 part — replace it whenever you see seepage.',
      },
    ],
    successIndicators: [
      'Oil pressure warning light extinguishes within 5 seconds of first start',
      'No drips or seepage at the drain plug or filter after a 5-10 minute drive',
      'Dipstick reads in the upper third of the safe zone after the engine has sat 5 minutes post-drive',
      'Engine runs smoothly with no unusual noise (a brief lifter tick on first cold start that clears in 10-30 seconds is normal)',
      'Oil-fill cap is fully seated and locked',
    ],
    whenToSeekHelp:
      'If the oil-pressure light stays on for more than 5 seconds at start, shut the engine off and do not restart it until the cause is identified. If you find metal shavings or chunks on a magnetic drain plug (not just grey paste), the engine has internal wear that needs professional diagnosis before further use. If a leak persists at the filter or drain plug after re-torquing to spec with a fresh crush washer, the threads or sealing surface may be damaged — bring the vehicle to a shop before driving further.',
    legalDisclaimer:
      'This guide was generated by GarageIQ\'s AI from publicly available sources. Some information may be incorrect or out of date. Verify torque specifications and any safety-critical procedures against the OEM service manual for your vehicle before performing work. The Haynes Manual tab on this page links to the licensed manual for your vehicle. GarageIQ is not a substitute for a qualified mechanic. By using this guide you agree to GarageIQ\'s Terms of Service and assume responsibility for the work performed. Used motor oil is a hazardous waste — dispose of it at a recycling center or auto parts store, never down a drain or onto the ground.',
    manualSource: {
      title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)',
      url: 'https://www.haynes.com/en-us/wrangler/jeep/2003',
      notes: 'Procedure sourced from public catalogs and forums; cross-reference torque values and oil capacity with your licensed manual before service.',
    },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — PCV VALVE REPLACEMENT (4.0L I6)
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-pcv-valve-replacement-4pt0l-i6',
    title: 'PCV Valve Replacement (4.0L I6)',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [{ startYear: 1997, endYear: 2006 }],
      engine: '4.0l-i6',
    },
    video: {
      youtubeId: 'MaqL4sZkH7s',
      title: 'How to Replace PCV Valve 1997-2006 Jeep Wrangler',
      channel: 'TRQ',
    },
    source: 'Generated by GarageIQ AI from publicly available sources (Mopar parts catalog, Standard Motor Products and FRAM PCV catalogs, owner\'s manual, technician forums). Cross-checked and structured per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Routine Maintenance & Fluids',
    difficulty: 'beginner',
    duration: '15-20 minutes',
    interval: 'Every 30,000 miles or whenever the engine develops rough idle, mild surging, or oil consumption from a clogged valve',
    description:
      'The Positive Crankcase Ventilation (PCV) valve on the 4.0L straight-six is a small one-way valve that meters blow-by gases from the crankcase back into the intake manifold to be re-burned. A clogged PCV valve causes rough idle, oil consumption, oil leaks (from over-pressurized seals), and elevated emissions. The valve sits in the valve cover, secured by a rubber grommet, and is one of the simplest and cheapest maintenance items on the entire engine — a $5 part and a 15-minute job.',
    overview: {
      difficulty: 'Beginner — pull and replace, no special tools',
      estimatedTime: '15-20 minutes',
      estimatedCost: '$5-15 (PCV valve only) vs $40-80 at a shop',
      whyMatters: 'A neglected PCV valve causes blow-by pressure to back up through engine seals and gaskets, creating "phantom" oil leaks that look like rear main seal failures. Replacing the PCV valve is the cheapest first step for most idle-quality complaints and oil-leak chases.',
      safetyNote: 'Allow the engine to cool for at least 10 minutes before removing the PCV valve. The valve cover is in close proximity to the exhaust manifold on the 4.0L.',
    },
    tools: [
      'Pliers (optional — for stuck hose clamp)',
      'Clean shop rag',
      'Flashlight',
    ],
    parts: [
      {
        id: 'pcv-valve-4pt0l-i6',
        name: 'PCV Valve (4.0L I6)',
        partNumber: 'Mopar 53008835 — equivalents: Standard V162 / FRAM FV219 / Wells PV222',
        quantity: 1,
        price: 8,
        category: 'required',
        notes: 'The 4.0L PCV valve is a slip-fit valve held by a rubber grommet in the valve cover. Verify the new valve matches the old valve\'s overall length and orifice before installation.',
      },
      {
        id: 'pcv-grommet',
        name: 'Valve Cover Grommet (PCV)',
        partNumber: 'Mopar 33002920 (or aftermarket equivalent)',
        quantity: 1,
        price: 3,
        category: 'recommended',
        notes: 'If the existing grommet is hard, cracked, or the valve installs loosely, replace the grommet at the same time. A loose grommet is a vacuum leak.',
      },
    ],
    safetyInformation:
      'Allow the engine to cool for 10+ minutes before working on or near the valve cover. The 4.0L exhaust manifold runs along the passenger side directly below the work area and stays hot for 30+ minutes after shutdown. The PCV system contains intake-manifold vacuum when the engine is running — never attempt to service the valve with the engine running.',
    beforeYouStart: [
      'Confirm the engine is fully cool to the touch around the valve cover',
      'Locate the PCV valve before removing it — on the 4.0L it is on the driver-side rear of the valve cover, with a hose running from it to the intake manifold',
      'Have the new valve in hand and oriented to match the old one before removal',
      'Note which side of the new valve has the airflow arrow (if marked) — installs only one way',
    ],
    steps: [
      {
        number: 1,
        title: 'Locate the PCV Valve',
        description:
          'Open the hood. The PCV valve sits in a rubber grommet on the driver-side rear of the valve cover, near the firewall, with a black rubber vacuum hose connecting it to a port on the intake manifold. Trace the hose from the intake manifold back to the valve cover to confirm location.',
        why: 'Some 4.0Ls have additional vacuum hoses running near the PCV — confirming the correct hose before disconnecting prevents disturbing unrelated systems.',
        tip: 'A flashlight helps in the shadowed area between the valve cover and firewall.',
        commonMistake: 'Confusing the PCV hose with the brake booster vacuum line. The PCV runs to the valve cover; the brake booster runs to the booster mounted on the firewall.',
      },
      {
        number: 2,
        title: 'Disconnect the PCV Hose',
        description:
          'Grasp the rubber vacuum hose where it connects to the PCV valve and twist it slightly back and forth while pulling straight up. The hose should release from the valve. If the hose is stuck, use pliers to break the seal but do not crush the hose. Inspect the hose ends for cracks — replace the hose if it is hard or split.',
        why: 'A cracked PCV hose creates the same vacuum-leak symptoms as a clogged valve. Replace it now if it shows wear.',
        tip: 'Old hoses on a 20+ year old TJ are often hardened. Twist gently and patiently; do not yank.',
        commonMistake: 'Tearing the hose. The replacement hose is a generic 3/8" PCV hose available at any parts store.',
      },
      {
        number: 3,
        title: 'Remove the Old PCV Valve',
        description:
          'Once the hose is off, grasp the body of the PCV valve and pull straight up. The valve will slide out of the rubber grommet in the valve cover. Inspect the inside of the grommet — it should be soft and pliable, sealing tightly around your finger. If it is hardened or cracked, replace the grommet.',
        why: 'A loose or cracked grommet is a vacuum leak that the new PCV valve cannot fix.',
        tip: 'Shake the old valve next to your ear — a working valve rattles freely when shaken. A clogged valve sounds dull or makes no rattle at all.',
        commonMistake: 'Reusing a cracked grommet. The grommet is a $3 part — replace it any time it is suspect.',
      },
      {
        number: 4,
        title: 'Inspect the New Valve and Grommet',
        description:
          'Compare the new PCV valve to the old one. They should be the same overall length with the same diameter on each end. If the new valve has an airflow arrow stamped on the body, note which direction it points (toward the intake manifold side). Shake the new valve — it should rattle freely.',
        why: 'PCV valves are calibrated to specific orifice sizes for each engine. Installing the wrong valve causes idle problems and can throw codes. Direction matters because the valve is a one-way check valve.',
        tip: 'Most 4.0L PCV valves are not directional in their physical install (they fit in either way), but the internal check valve still must face the correct direction to work.',
        commonMistake: 'Buying a "fits 4.0L" valve from a budget brand without verifying length and rattle. Some bargain valves have wrong calibration.',
      },
      {
        number: 5,
        title: 'Install the New PCV Valve',
        description:
          'Insert the new PCV valve into the grommet on the valve cover. Push down firmly until the valve is fully seated — the body should feel snug in the grommet with no wobble. Reconnect the vacuum hose to the top of the new valve. Push the hose all the way down until it bottoms on the valve.',
        why: 'A partially-seated valve is a vacuum leak. A loose hose connection is also a vacuum leak.',
        tip: 'A small amount of silicone spray or vacuum-line lubricant on the hose end makes installation easier and improves the seal.',
        commonMistake: 'Stopping when the valve "feels right" — push it firmly down until it bottoms.',
      },
      {
        number: 6,
        title: 'Verify No Vacuum Leaks',
        description:
          'Start the engine. Listen for any hissing or whistling around the PCV valve and hose connection. The idle should be smooth and steady. If you hear a hiss or the idle is rough, shut off the engine and verify the valve is fully seated and the hose is fully connected.',
        why: 'A vacuum leak from a poorly seated PCV will cause a high or unstable idle and potentially throw a P0171 (lean) code.',
        tip: 'Spraying carb cleaner or starting fluid (with the engine running) around the PCV connections will momentarily change idle speed if there is a leak — useful for pinpointing.',
        commonMistake: 'Skipping the leak check. A leaking PCV that you just installed is harder to diagnose later because you assume it is fine.',
      },
      {
        number: 7,
        title: 'Test Drive and Verify Idle Quality',
        description:
          'Take the vehicle on a 5-10 minute drive at varying speeds. The idle should be smooth, the engine should not surge at cruise, and there should be no unusual noises. Return to level ground, idle for 30 seconds, and confirm the idle is stable.',
        why: 'A working PCV system shows up most clearly at idle and during deceleration. Driving the vehicle confirms the valve is correctly metering crankcase gases under load.',
        tip: 'If you had rough idle before the change, expect to feel a noticeable improvement immediately. If nothing changed, the original PCV was likely not the cause and another diagnosis is needed.',
        commonMistake: 'Assuming success without verification. The PCV is cheap enough that "throwing parts at the problem" is reasonable, but always verify.',
      },
    ],
    torqueSpecifications: [
      {
        fastener: 'PCV valve (slip-fit into grommet)',
        specification: 'No torque spec — hand-press until fully seated. Verify seating depth visually against original valve installation depth.',
        notes: 'The PCV valve does not thread or torque — it press-fits into a rubber grommet. Resistance comes from the grommet seal, not threads.',
      },
    ],
    fluidSpecifications: [],
    troubleshooting: [
      {
        problem: 'Idle is rough or surging after PCV replacement',
        likelyCause: 'Vacuum leak at the new valve or hose connection, or wrong PCV valve calibration',
        whatToCheck: 'Verify the valve is fully seated and the hose is fully connected. Spray carb cleaner around the connections with engine idling — RPM change indicates a leak.',
        fix: 'Re-seat the valve fully. If the hose is loose, replace the hose with a fresh 3/8" PCV hose section. If symptoms persist, swap to a Mopar OE valve and retest.',
      },
      {
        problem: 'PCV valve will not stay seated in the grommet',
        likelyCause: 'Hardened or cracked rubber grommet',
        whatToCheck: 'Pry out the grommet and inspect for cracks, hardening, or oversized opening',
        fix: 'Replace the grommet (Mopar 33002920 or aftermarket). Install the grommet first, then install the new PCV valve.',
      },
      {
        problem: 'Oil consumption did not improve after PCV replacement',
        likelyCause: 'Original problem was not PCV-related — could be valve seals, rings, or a real leak',
        whatToCheck: 'Inspect for visible oil leaks (rear main, valve cover gasket, oil pan), and check for oil residue in the air-filter housing (sign of severe blow-by)',
        fix: 'PCV replacement is the cheap first step but cannot fix mechanical wear. If consumption persists, deeper diagnosis is needed (compression test, leak-down test, or visual inspection for external leaks).',
      },
      {
        problem: 'Check Engine Light comes on after replacement (P0171 / P0174 lean codes)',
        likelyCause: 'Vacuum leak somewhere in the PCV system, or the new valve has a different orifice',
        whatToCheck: 'Verify all PCV connections, then inspect the larger fresh-air vent hose on the air-cleaner side of the valve cover',
        fix: 'Reseat the valve. If the codes persist with verified seal, swap to a known-good Mopar OE valve.',
      },
    ],
    proTips: [
      'A clogged PCV is the single most common "mystery rough idle" cause on a 4.0L over 100k miles. Always check this before chasing more expensive components.',
      'Shake test before installing the new valve and bag the old one — if the symptoms come back later, comparing the rattle gives you a quick clue.',
      'When you replace the PCV, replace the short hose section between the valve and the manifold at the same time. They age together.',
      'PCV valves are inexpensive enough to replace at every other oil change as preventative maintenance — a $5 valve protects every gasket and seal in the engine.',
      'If your 4.0L burns a noticeable amount of oil and the PCV is recent, check the air-filter housing for oil residue. Heavy residue means the valve cannot keep up with blow-by — a sign of ring wear, not a PCV problem.',
    ],
    commonMistakes: [
      {
        mistake: 'Reusing a cracked or hardened grommet with a new valve',
        consequence: 'Vacuum leak that masks the benefit of the new valve and causes lean running.',
      },
      {
        mistake: 'Installing the valve backward (when directional)',
        consequence: 'PCV system does not function — same symptoms as a clogged valve.',
      },
      {
        mistake: 'Not checking for leaks after install',
        consequence: 'Lean codes and rough idle that are easily missed in the driveway.',
      },
      {
        mistake: 'Skipping the PCV replacement in routine maintenance',
        consequence: 'Long-term blow-by pressure damages valve cover and oil pan gaskets, causing expensive "phantom" oil leaks.',
      },
    ],
    successIndicators: [
      'Idle is smooth and steady (between 700-800 RPM warm)',
      'No hissing or whistling around the PCV connections',
      'No new check engine light codes after a 5-10 minute drive',
      'New valve rattles when shaken (verified before installation)',
      'Hose is fully seated on the valve with no air gap visible',
    ],
    whenToSeekHelp:
      'If the engine still has rough idle, oil consumption, or vacuum-leak symptoms after PCV replacement, the cause is elsewhere — likely a different vacuum leak, fuel injector problem, or internal mechanical wear. A scan tool reading short-term and long-term fuel trims will narrow the diagnosis quickly.',
    legalDisclaimer: 'This guide was generated by GarageIQ\'s AI from publicly available sources. Some information may be incorrect or out of date. Verify torque specifications and any safety-critical procedures against the OEM service manual for your vehicle before performing work. The Haynes Manual tab on this page links to the licensed manual for your vehicle. GarageIQ is not a substitute for a qualified mechanic. By using this guide you agree to GarageIQ\'s Terms of Service and assume responsibility for the work performed.',
    manualSource: {
      title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)',
      url: 'https://www.haynes.com/en-us/wrangler/jeep/2003',
      notes: 'Procedure sourced from public catalogs and forums; cross-reference component locations and any related tune-up intervals with your licensed manual.',
    },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — DISTRIBUTOR CAP & ROTOR REPLACEMENT (4.0L I6)
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-distributor-cap-rotor-replacement-4pt0l-i6',
    title: 'Distributor Cap & Rotor Replacement (4.0L I6)',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [{ startYear: 1997, endYear: 2006 }],
      engine: '4.0l-i6',
    },
    video: {
      youtubeId: 'W7m8Kl2B_5Q',
      title: 'How to Replace Distributor Rotor 1997-2006 Jeep Wrangler',
      channel: 'TRQ',
    },
    source: 'Generated by GarageIQ AI from publicly available sources (Mopar parts catalog, NAPA Echlin and Standard Motor Products distributor cap/rotor catalogs, owner\'s manual, technician forums). Cross-checked and structured per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Engine',
    difficulty: 'beginner',
    duration: '30-45 minutes',
    interval: 'Every 60,000 miles, or whenever the engine develops misfires, hard starts, or visible carbon tracking on the cap',
    description:
      'The 4.0L straight-six uses a traditional distributor (camshaft-driven, on the passenger side of the engine) that routes high-voltage spark from the coil to each of the six spark plugs. The cap and rotor are wear items — the rotor tip arcs against the cap terminals tens of millions of times over its life and eventually erodes carbon, builds tracking, and develops cracks. A worn cap and rotor cause misfires, hard starts in damp weather, and a generally tired-feeling engine. The 2.4L I4 in the 2003-2006 TJ uses coil-on-plug ignition and does not have a distributor — this guide is for the 4.0L only.',
    overview: {
      difficulty: 'Beginner — two screws, one rotor screw, careful re-routing of plug wires',
      estimatedTime: '30-45 minutes including wire-by-wire transfer',
      estimatedCost: '$25-50 (Mopar OE cap+rotor combo or aftermarket Standard/NAPA Echlin) vs $120-180 at a shop',
      whyMatters: 'A worn cap and rotor masquerades as injectors, plugs, coil, or fuel pump problems. Replacing them is one of the cheapest ways to restore a tired 4.0L to "feels new" running.',
      safetyNote: 'Disconnect the battery before working on ignition components. The coil can hold residual high voltage. Allow the engine to cool — the exhaust manifold runs directly below the distributor on the 4.0L.',
    },
    tools: [
      'Phillips screwdriver (or T-25 Torx, depending on cap screws)',
      '10mm wrench or socket (battery cable)',
      'Clean shop rag',
      'Dielectric grease',
      'Marker or masking tape (for marking wire positions)',
    ],
    parts: [
      {
        id: 'distributor-cap-4pt0l-i6',
        name: 'Distributor Cap (4.0L I6)',
        partNumber: 'Mopar 56028196AB — equivalents: NAPA Echlin AL184 / Standard JR-188 / Wells D680',
        quantity: 1,
        price: 18,
        category: 'required',
        notes: 'The 4.0L cap has six terminals (one per cylinder) plus the center coil terminal. Confirm the new cap matches the old in terminal layout before transferring wires.',
      },
      {
        id: 'distributor-rotor-4pt0l-i6',
        name: 'Distributor Rotor (4.0L I6)',
        partNumber: 'Mopar 53008420 — equivalents: NAPA Echlin AL173 / Standard DR331',
        quantity: 1,
        price: 8,
        category: 'required',
        notes: 'Always replace the cap and rotor as a pair. They wear together and the new cap will not perform correctly with a worn rotor.',
      },
    ],
    safetyInformation:
      'Disconnect the battery negative cable before starting. The ignition coil can deliver enough voltage to cause an arc across an open circuit even after the engine is shut off. Allow the engine to cool fully before working on the distributor — the 4.0L exhaust manifold runs along the passenger side just below the distributor and stays hot for 30+ minutes after shutdown. Do not crank or run the engine with the distributor cap removed.',
    beforeYouStart: [
      'Disconnect the battery negative cable and wait 60 seconds',
      'Confirm the engine is cool to the touch on the exhaust side',
      'Lay out the new cap and rotor next to the old assembly to confirm fitment before disturbing anything',
      'Have a marker or masking tape ready to label spark plug wires',
      'Note: do NOT remove all six wires from the cap at once — transfer them one at a time to the new cap to preserve the firing order',
    ],
    steps: [
      {
        number: 1,
        title: 'Disconnect the Battery and Locate the Distributor',
        description:
          'Loosen the negative battery terminal with a 10mm wrench and lift the cable off the post. Tuck it aside. Locate the distributor: on the 4.0L it is on the passenger side rear of the engine, with six spark plug wires arranged radially around a black plastic cap, plus a center coil wire.',
        why: 'A live ignition system can arc when the cap is opened. The 60-second wait drains residual capacitor voltage in the ignition module.',
        tip: 'On Rubicon TJs the skid plate sometimes interferes with the distributor — verify clearance before starting.',
        commonMistake: 'Skipping the battery disconnect because the engine is off. Coils can store charge.',
      },
      {
        number: 2,
        title: 'Note the Existing Wire Positions',
        description:
          'Before removing anything, study the spark plug wire arrangement carefully. Take a clear photograph of the cap from above showing all six wire positions. Optionally label each wire with masking tape (1, 2, 3, 4, 5, 6) corresponding to the cylinder it goes to. The 4.0L firing order is 1-5-3-6-2-4.',
        why: 'Mixing up two wires is the most common mistake on this job and causes immediate severe misfire. Documentation prevents this.',
        tip: 'Cylinder 1 is the front-most cylinder. The corresponding cap terminal is the one with the wire running to the front of the engine.',
        commonMistake: 'Pulling all the wires at once. Transfer them one at a time.',
      },
      {
        number: 3,
        title: 'Remove the Two Cap Hold-Down Screws',
        description:
          'The 4.0L distributor cap is held by two captive screws (Phillips or T-25 Torx, depending on year). Loosen both screws — they remain captive in the cap and do not fall out. Once both are loose, the cap will lift straight up off the distributor body.',
        why: 'Captive screws make this job easy. If a screw rotates freely and the cap will not lift, the screw may have been replaced with a non-captive type — pull it fully out before lifting.',
        tip: 'A magnetic Phillips bit makes this easier — the screws are deep in the cap.',
        commonMistake: 'Forcing the cap up with one screw still partially engaged. The cap tabs can crack.',
      },
      {
        number: 4,
        title: 'Inspect the Old Cap and Rotor',
        description:
          'Lift the cap straight up (with all six wires still attached) and turn it over. Look at the inside terminals — they should show small carbon dots from arcing, but no deep pitting, blackening tracks, or cracks. Look at the rotor on the distributor shaft: the tip should be slightly worn but not eroded into the body. Document the wear pattern — this confirms the diagnosis was correct.',
        why: 'Real cap-and-rotor wear is unmistakable: pitted contacts, carbon tracking from terminal to terminal, or a rotor tip eroded down past its original outline. Mild discoloration is normal.',
        tip: 'If the cap or rotor is very recent and shows little wear, the misfire likely has another cause (plugs, wires, coil, or compression). Consider that diagnosis before swapping more parts.',
        commonMistake: 'Replacing parts that are not actually worn. Always confirm wear before parts swap.',
      },
      {
        number: 5,
        title: 'Transfer Wires to the New Cap (One at a Time)',
        description:
          'Set the old cap upside-down next to the new cap, oriented identically. Pull one spark plug wire from the old cap (gripping the boot, not the wire — twist gently) and immediately push it into the corresponding terminal on the new cap. Press the boot down firmly until you hear or feel the wire seat on the terminal. Repeat for all six plug wires AND the center coil wire, one at a time, never having more than one wire disconnected.',
        why: 'Transferring one wire at a time makes it physically impossible to mix up the firing order.',
        tip: 'Apply a small dab of dielectric grease to each terminal inside the new cap before pushing the wire on — improves contact and makes future removal easier.',
        commonMistake: 'Pulling on the wire instead of the boot. Cracks the boot and creates a misfire-prone connection.',
      },
      {
        number: 6,
        title: 'Remove and Replace the Rotor',
        description:
          'Look at the distributor shaft (now exposed with the cap removed). The rotor is held by a single screw on top of the rotor body. Loosen and remove the screw, then pull the rotor straight up off the shaft. Install the new rotor in the same orientation — most rotors are keyed (a flat or pin) so they only fit one way. Reinstall the rotor screw and tighten until snug; do NOT over-torque (the rotor body is plastic and will crack). Verify torque spec for rotor screw from OEM service manual; typical value is 24 in-lbs (2.7 Nm) — finger-tight plus a quarter turn.',
        why: 'A loose rotor will spin on the shaft and miss spark events. An over-torqued rotor cracks and fails immediately.',
        tip: 'Look at the rotor blade orientation before removal and after install — they should point the same direction. If the new rotor seems to point the wrong way, you have a wrong-part-number rotor.',
        commonMistake: 'Cranking the rotor screw down hard. Snug plus a touch is the spec — you cannot torque this with a regular wrench, only by feel.',
      },
      {
        number: 7,
        title: 'Install the New Cap',
        description:
          'Lower the new cap (with all wires already transferred) onto the distributor body. The cap is keyed by a tab on the distributor housing — it only seats one way. Once seated flush, tighten the two captive hold-down screws evenly until snug. Do not overtighten — the cap is plastic and the tabs can crack. Verify torque spec for cap screws from OEM service manual; typical value is 24 in-lbs (2.7 Nm).',
        why: 'A correctly seated cap has no gap between the cap and distributor body. Any visible gap is a moisture entry point and a misfire source.',
        tip: 'Run a finger around the cap-to-distributor seam after install — should feel flush all the way around.',
        commonMistake: 'Forcing the cap on incorrectly oriented. The keying tab prevents misalignment if you let the cap settle naturally.',
      },
      {
        number: 8,
        title: 'Verify Wires and Reconnect Battery',
        description:
          'Walk around the new cap and verify all six plug wires (and the coil wire) are fully seated on the new cap terminals. Compare the wire routing to your photo from step 2 — every wire should land in the same physical position. Reconnect the negative battery cable and tighten to 10 ft-lbs.',
        why: 'A wire that is partially on a terminal will spark intermittently and create an unpredictable misfire that throws random codes.',
        tip: 'Push down on each boot one more time after the others are installed — vibration during transfer can leave one slightly proud.',
        commonMistake: 'Reconnecting the battery before verifying wires. If a wire is off, you may not realize it until the next cold start.',
      },
      {
        number: 9,
        title: 'Start, Verify, and Test Drive',
        description:
          'Start the engine. It should fire immediately with no hesitation and idle smoothly. If it cranks but will not start, or if it runs very rough, shut off immediately and verify your wire routing matches the photo. Once running smoothly, take the vehicle on a 5-10 minute drive at varying speeds. The engine should pull cleanly with no misfires, hesitation, or surging.',
        why: 'A misfire from a crossed wire shows up immediately as a violently rough idle or a no-start. Catching it in the driveway is much easier than diagnosing on the road.',
        tip: 'A scan tool reading misfire counts (P0301-P0306) confirms quickly which cylinder, if any, has a problem after the change.',
        commonMistake: 'Driving away on a roughly-running engine. If it feels wrong, stop and re-verify wires.',
      },
    ],
    torqueSpecifications: [
      {
        fastener: 'Distributor cap hold-down screws (captive)',
        specification: '24 in-lbs (2.7 Nm). Verify torque spec 24 in-lbs (2.7 Nm) from OEM service manual.',
        notes: 'Plastic cap — finger-tight plus light snug only. Over-torque cracks the cap tabs.',
      },
      {
        fastener: 'Rotor hold-down screw',
        specification: '24 in-lbs (2.7 Nm). Verify torque spec 24 in-lbs (2.7 Nm) from OEM service manual.',
        notes: 'Plastic rotor — snug plus a quarter turn maximum. The rotor must not wobble on the shaft.',
      },
      {
        fastener: 'Battery negative terminal',
        specification: '10 ft-lbs (14 Nm). Verify torque spec 10 ft-lbs (14 Nm) from OEM service manual.',
        notes: 'Standard battery clamp torque.',
      },
    ],
    fluidSpecifications: [],
    troubleshooting: [
      {
        problem: 'Engine cranks but will not start after replacement',
        likelyCause: 'Two or more spark plug wires swapped on the new cap',
        whatToCheck: 'Compare current wire routing to your pre-removal photo — verify each wire goes to the same physical terminal position',
        fix: 'Restore the wire routing to match the photo. The 4.0L firing order is 1-5-3-6-2-4 going clockwise (viewed from above) starting at the cylinder-1 terminal.',
      },
      {
        problem: 'Engine runs but misfires under load (especially uphill or accelerating)',
        likelyCause: 'One spark plug wire not fully seated on the new cap',
        whatToCheck: 'With the engine off, push each boot down firmly. Listen for a click or feel the boot drop slightly.',
        fix: 'Re-seat any boot that does not feel solid. Apply dielectric grease to terminals on reseat for better contact.',
      },
      {
        problem: 'Random misfires on multiple cylinders after cap and rotor replacement',
        likelyCause: 'Wrong rotor part — physical fit but wrong electrical timing, or a defective new cap',
        whatToCheck: 'Compare new rotor blade direction to the photo of the old rotor — they should point the same direction when installed',
        fix: 'If wrong, return the rotor for the correct part. If the rotor is correct, swap the new cap for a Mopar OE unit.',
      },
      {
        problem: 'Engine still feels weak or sluggish after replacement',
        likelyCause: 'Cap and rotor were not the actual problem',
        whatToCheck: 'Pull the spark plugs and inspect — fouled or worn plugs cause similar symptoms. Check plug wires for cracks, age, or high resistance.',
        fix: 'Replace plugs and wires as the next step. The 4.0L tune-up is normally cap, rotor, plugs, and wires done together every 60-100k miles.',
      },
    ],
    proTips: [
      'Buy cap and rotor as a kit when possible — kits are usually a few dollars cheaper than individual parts and guarantee the rotor matches the cap.',
      'Stick with Mopar OE or NAPA Echlin for cap and rotor on the 4.0L. The cheap aftermarket parts often have undersized terminals and wear out within 20k miles.',
      'If you are due for plugs, wires, cap, and rotor all at once, do them in this order: plugs first, then wires, then cap, then rotor. This way each step verifies the previous step.',
      'Take a photograph of the wire routing BEFORE you start. It is the single best insurance against an embarrassing crank-no-start aftermath.',
      'A failing distributor cap often leaves visible carbon tracking inside the cap — long black streaks from terminal to terminal. If you see those, the cap was overdue.',
    ],
    commonMistakes: [
      {
        mistake: 'Removing all six wires at once and trying to remember the firing order from memory',
        consequence: 'Crossed wires, severe misfire, possible damage to the catalytic converter from raw fuel.',
      },
      {
        mistake: 'Reusing an old rotor with a new cap',
        consequence: 'New cap wears prematurely; misfires return within months. Always replace as a pair.',
      },
      {
        mistake: 'Over-tightening the rotor or cap screws on plastic threads',
        consequence: 'Cracks the rotor body or cap tab — the part is now ruined and must be replaced.',
      },
      {
        mistake: 'Pulling the wires by the cable instead of the boot',
        consequence: 'Boot tears away from the wire core, requiring replacement of the entire wire set.',
      },
    ],
    successIndicators: [
      'Engine starts immediately on first crank with no roughness',
      'Idle is smooth and steady at 700-800 RPM warm',
      'Acceleration is clean with no hesitation, surge, or misfire',
      'No new check engine codes after a 5-10 minute drive',
      'All six plug wires are fully seated with no visible gap at the boot',
    ],
    whenToSeekHelp:
      'If the engine cranks but will not start after the replacement and you have verified wire routing matches your pre-removal photo, the rotor may be wrong, the new cap may be defective, or the original misfire was caused by something else (failed coil, ignition module, or crank position sensor). A scan tool reading live data will identify the actual fault quickly.',
    legalDisclaimer: 'This guide was generated by GarageIQ\'s AI from publicly available sources. Some information may be incorrect or out of date. Verify torque specifications and any safety-critical procedures against the OEM service manual for your vehicle before performing work. The Haynes Manual tab on this page links to the licensed manual for your vehicle. GarageIQ is not a substitute for a qualified mechanic. By using this guide you agree to GarageIQ\'s Terms of Service and assume responsibility for the work performed.',
    manualSource: {
      title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)',
      url: 'https://www.haynes.com/en-us/wrangler/jeep/2003',
      notes: 'Procedure sourced from public catalogs and forums; cross-reference torque values and firing order with your licensed manual before service.',
    },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — SPARK PLUG REPLACEMENT (4.0L I6) — engine-specific replacement for engine-agnostic tj-spark-plug-replacement
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-spark-plug-replacement-4pt0l-i6',
    title: 'Spark Plug Replacement (4.0L I6)',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [{ startYear: 1997, endYear: 2006 }],
      engine: '4.0l-i6',
    },
    video: {
      youtubeId: 'i5qVwf52mbQ',
      title: 'How to Change Spark Plugs on a Jeep Wrangler TJ 4.0L',
      channel: 'John\'s Jeep Garage',
    },
    source: 'Generated by GarageIQ AI from publicly available sources (Mopar parts catalog, Champion / NGK / Bosch spark plug application charts, owner\'s manual, technician forums). Cross-checked and structured per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Engine',
    difficulty: 'beginner',
    duration: '45 minutes - 1 hour',
    interval: 'Every 30,000 miles (copper plugs) or every 60,000-100,000 miles (platinum/iridium plugs)',
    description:
      'The 4.0L straight-six fires through six spark plugs accessed from the passenger side of the engine. The plugs sit in long wells in the head and are reached through plug wires that connect to the distributor cap. Spark plug replacement on the 4.0L is one of the most basic tune-up tasks but it is also one of the most commonly botched — over-torquing strips the aluminum head threads, and incorrect gap or wrong heat range causes immediate misfires. The 2.4L I4 in the 2003-2006 TJ uses coil-on-plug ignition with a different procedure — this guide is for the 4.0L only.',
    overview: {
      difficulty: 'Beginner — but precision matters: gap, torque, and one-at-a-time wire transfer',
      estimatedTime: '45 minutes - 1 hour for all six plugs',
      estimatedCost: '$15-50 for plugs (set of 6) vs $80-150 at a shop',
      whyMatters: 'Worn plugs cause misfires, hard starts, poor fuel economy, and rough idle. Fresh plugs in the right gap restore the engine to crisp running and proper combustion temperatures.',
      safetyNote: 'Spark plugs are torqued into aluminum cylinder head threads. Stripped threads require a Heli-Coil repair or, in worst cases, head removal. Always use a torque wrench and verify each plug spins in by hand for the first 5 turns.',
    },
    tools: [
      'Torque wrench capable of 26 ft-lbs (35 Nm)',
      '5/8" spark plug socket with rubber insert',
      'Socket extension (6" minimum)',
      '3/8" or 1/2" ratchet',
      'Spark plug gap tool (feeler gauge or coin-style)',
      'Anti-seize compound (small tube — for tapered seat plugs use sparingly or none per manufacturer)',
      'Dielectric grease',
      'Compressed air or shop vacuum (clear debris from plug wells before removal)',
      'Clean shop rag',
    ],
    parts: [
      {
        id: 'spark-plugs-4pt0l-i6',
        name: 'Spark Plugs (set of 6, 4.0L I6)',
        partNumber: 'Mopar SP56041018AB / Champion RC12LYC (OE) — equivalents: NGK ZFR5N (copper) or NGK ZFR5F-11 (platinum) or Bosch FR8DC',
        quantity: 6,
        price: 30,
        category: 'required',
        notes: 'The 4.0L uses a 14mm tapered-seat spark plug (no gasket). Gap to 0.035" (0.89 mm). Copper plugs are cheap and need replacement at 30k; platinum/iridium last 60-100k. The OE Champion is a copper plug — most owners upgrade to NGK platinum on first change.',
      },
    ],
    safetyInformation:
      'Allow the engine to cool fully before working on spark plugs. The exhaust manifold runs along the passenger side directly below the plugs and stays hot for 30+ minutes after shutdown. Spark plug threads are aluminum on the 4.0L head — they will strip permanently if cross-threaded or over-torqued. Always start each plug by hand and verify it spins in for at least 5 full turns before applying torque. Disconnect the battery negative cable before starting.',
    beforeYouStart: [
      'Disconnect the battery negative cable',
      'Confirm the engine is fully cool — feel the exhaust manifold to verify',
      'Pre-gap the new plugs to 0.035" (0.89 mm) before installation — verify each one with a gap tool',
      'Have a torque wrench on hand and confirmed accurate; spark plug threads in aluminum heads do not tolerate over-torque',
      'Remove and replace one plug at a time to keep the firing order intact',
      'Use compressed air or a vacuum to blow debris out of each plug well before pulling the old plug',
    ],
    steps: [
      {
        number: 1,
        title: 'Disconnect Battery and Identify Each Plug Position',
        description:
          'Disconnect the negative battery cable. The 4.0L plug wires emerge from the distributor cap on the passenger side and run to the six spark plugs along the passenger-side of the head. Cylinder 1 is at the front; cylinder 6 is at the rear. Take a photograph of the wire routing for reference.',
        why: 'Working one plug at a time prevents firing-order mistakes. Battery disconnect prevents accidental cranking with a wire off.',
        tip: 'Number the plug wires with masking tape (1 through 6) at the boot end before starting.',
        commonMistake: 'Pulling all six wires at once. Always work one at a time.',
      },
      {
        number: 2,
        title: 'Clean the Plug Well of Cylinder 1',
        description:
          'Before pulling the wire, blow compressed air or use a shop vacuum around the boot of plug 1 to remove dirt, sand, or debris from the well. Any debris that falls into the cylinder while the plug is out can cause severe internal damage on the next start.',
        why: 'TJs spend time off-road and on dirt roads. The plug wells collect debris that falls into the cylinder if not cleared first.',
        tip: 'A small straw on a can of compressed air works well for getting into deep plug wells.',
        commonMistake: 'Skipping the well-cleaning step. A pebble in the cylinder can wreck a piston on the next start.',
      },
      {
        number: 3,
        title: 'Pull the Plug Wire from Cylinder 1',
        description:
          'Grasp the boot of the spark plug wire (NOT the wire itself) and twist it 90 degrees while pulling straight up. The boot will release from the spark plug. If the boot is stuck, twist gently back and forth — never yank straight up with force.',
        why: 'Pulling on the wire instead of the boot tears the wire core out of the boot, ruining the wire.',
        tip: 'A spark plug wire boot puller (~$8) makes this much easier on stuck boots without damaging the wire.',
        commonMistake: 'Yanking the wire. Always twist the boot first.',
      },
      {
        number: 4,
        title: 'Remove the Old Plug',
        description:
          'Insert the spark plug socket (5/8") with the rubber insert onto the plug. Use a 6"+ extension to clear the manifold heat shield. Turn counterclockwise to break the plug loose. Once free, unscrew by hand the rest of the way and lift the plug straight up out of the well. Inspect the plug — note the electrode wear, color of the insulator (light tan is ideal; black is rich, white is lean), and any oil or coolant fouling.',
        why: 'Plug appearance is one of the best diagnostic tools available. A heavily worn or fouled plug indicates engine condition, not just plug age.',
        tip: 'A black sooty plug is normal-rich (often ignition or fueling); a wet oily plug indicates valve seal or ring wear; a white plug indicates lean running. Document any abnormal plug for later diagnosis.',
        commonMistake: 'Throwing the old plugs away without inspecting them. They are free diagnostic data.',
      },
      {
        number: 5,
        title: 'Verify the New Plug Gap',
        description:
          'Check the gap on the new plug with a gap tool. The 4.0L specification is 0.035" (0.89 mm). Most plugs come pre-gapped close but verify each one. If the gap is wrong, gently bend the side electrode to adjust — push to close, lever from underneath to open. Never lever against the center electrode.',
        why: 'Gap that is too small produces weak spark; gap too large causes misfire under load. The 0.035" spec is non-negotiable.',
        tip: 'A wire-loop or coin-style gap tool is more accurate than a flat feeler gauge for the small 4.0L gap.',
        commonMistake: 'Trusting the "pre-gapped" claim on the box. Always verify.',
      },
      {
        number: 6,
        title: 'Install the New Plug',
        description:
          'For tapered-seat plugs (4.0L OE Champion), apply a small dab of anti-seize ONLY if the manufacturer specifies it on that plug — many modern plugs say "no anti-seize required." For platinum/iridium plugs from NGK or Bosch, follow their instructions (typically no anti-seize). Thread the new plug into the well BY HAND first. Verify it spins in for at least 5 full turns easily — if it ever feels gritty, stop and back it out. Once seated by hand, tighten with the torque wrench to 26 ft-lbs (35 Nm). Verify torque spec 26 ft-lbs (35 Nm) from OEM service manual.',
        why: 'Hand-starting catches cross-threading before it damages the head. Torque-to-spec prevents the two most common spark plug failures: over-torque (strips threads, requires Heli-Coil) and under-torque (plug loosens and gets blown out under combustion pressure).',
        tip: 'Pencil-grip the torque wrench at the head and use a smooth, slow pull — NOT a yank — when reaching the click.',
        commonMistake: 'Cranking the plug down hard with a regular ratchet. The aluminum head will strip on the very next service interval.',
      },
      {
        number: 7,
        title: 'Reinstall the Plug Wire with Dielectric Grease',
        description:
          'Apply a small dab of dielectric grease to the inside of the plug wire boot (lubricates installation and seals against moisture). Push the boot straight down onto the new plug until you hear or feel a click as it seats on the plug terminal. Tug very gently to verify the boot is locked.',
        why: 'A properly seated boot is moisture-tight and stays put under thermal cycling. A loose boot is the most common source of post-tune-up misfires.',
        tip: 'You should feel one definite "snap" when the boot clears the retaining wire and seats fully.',
        commonMistake: 'Stopping when the boot "looks" seated — push past visible resistance until you feel the click.',
      },
      {
        number: 8,
        title: 'Repeat for Cylinders 2-6',
        description:
          'Repeat steps 2-7 for each of the remaining five cylinders, working one plug at a time. Always clean the well, pull one wire, install one plug, and reattach the wire before moving to the next cylinder.',
        why: 'One-at-a-time discipline keeps the firing order automatic and prevents debris-fall risks.',
        tip: 'The 4.0L firing order is 1-5-3-6-2-4 — useful to know for diagnosis but irrelevant for replacement if you maintain the one-at-a-time rule.',
        commonMistake: 'Pulling all six wires at once "to save time." Always finish one cylinder before moving to the next.',
      },
      {
        number: 9,
        title: 'Reconnect Battery and Verify',
        description:
          'Reconnect the negative battery cable and tighten to 10 ft-lbs. Start the engine. It should fire immediately and idle smoothly. Listen for any misfire — a regular skip or stumble suggests a misseated wire or wrong-gap plug. Take the vehicle for a 5-10 minute drive at varying speeds. The engine should pull cleanly with no surge or hesitation.',
        why: 'Driving the vehicle confirms the new plugs work under load, not just at idle. Misfires often only appear under boost or load conditions.',
        tip: 'A scan tool reading misfire counts (P0301-P0306) confirms which cylinder, if any, has a problem after the change.',
        commonMistake: 'Skipping the test drive. Some misfires only appear under load.',
      },
    ],
    torqueSpecifications: [
      {
        fastener: 'Spark plug (tapered-seat, 14mm)',
        specification: '26 ft-lbs (35 Nm). Verify torque spec 26 ft-lbs (35 Nm) from OEM service manual.',
        notes: 'Aluminum cylinder head — must be torqued to spec, never overtightened. Hand-start each plug for at least 5 full turns before applying any torque.',
      },
      {
        fastener: 'Battery negative terminal',
        specification: '10 ft-lbs (14 Nm). Verify torque spec 10 ft-lbs (14 Nm) from OEM service manual.',
        notes: 'Standard battery clamp torque.',
      },
    ],
    fluidSpecifications: [],
    troubleshooting: [
      {
        problem: 'Engine misfires on one cylinder after replacement',
        likelyCause: 'Plug wire not fully seated, wrong-gapped plug, or defective new plug',
        whatToCheck: 'Identify the misfiring cylinder via OBD-II code (P030X — last digit is cylinder number). Inspect that plug wire seating and re-gap that plug.',
        fix: 'Re-seat the wire firmly with dielectric grease. If misfire persists, swap the plug with a known-good spare plug to rule out a defective new part.',
      },
      {
        problem: 'Plug feels gritty or stiff threading in by hand',
        likelyCause: 'Cross-threaded into the aluminum head, or debris in the well that fell into the threads',
        whatToCheck: 'Stop immediately. Back the plug out and inspect the threads on both the plug and the head with a flashlight.',
        fix: 'If thread damage is minor, a thread chaser tool can clean the head threads. If damage is severe, the head requires a Heli-Coil repair — a job for a professional shop.',
      },
      {
        problem: 'Engine cranks but will not start after replacement',
        likelyCause: 'Two or more plug wires swapped on the cap or plugs',
        whatToCheck: 'Compare current wire routing to the photo taken before disassembly',
        fix: 'Restore original routing. The 4.0L firing order is 1-5-3-6-2-4 going clockwise on the distributor cap.',
      },
      {
        problem: 'Plug looks oil-fouled or wet on inspection of old plugs',
        likelyCause: 'Internal engine wear (valve seals, rings) — not a plug issue',
        whatToCheck: 'Note which cylinder. If only one or two are oil-fouled, the head gasket or a valve seal on those cylinders may be at fault.',
        fix: 'New plugs will run cleanly for a while but the underlying wear continues. Plan a compression and leak-down test on the affected cylinders for proper diagnosis.',
      },
    ],
    proTips: [
      'Stick with NGK or Champion for the 4.0L — Bosch and other premium brands sometimes do not run as well in this engine despite higher prices.',
      'Copper plugs (Champion RC12LYC) are the OE choice and run perfectly well at 30k intervals; platinum (NGK ZFR5F-11) lets you go 60-80k between changes if you prefer fewer service events.',
      'Always inspect the old plugs as a set — if all six look identical, the engine is in good shape; if one or two look different, that cylinder has a problem.',
      'Apply anti-seize sparingly or not at all on modern plug threads — many plugs have a special coating that interacts poorly with anti-seize. Read the plug manufacturer\'s instructions.',
      'A torque wrench used on plugs once is worth 100x its cost — stripping a 4.0L head is a multi-hundred-dollar repair.',
    ],
    commonMistakes: [
      {
        mistake: 'Over-torquing the plug into aluminum head threads',
        consequence: 'Stripped threads, requiring Heli-Coil repair or worst-case head replacement.',
      },
      {
        mistake: 'Cross-threading by starting the plug with a ratchet',
        consequence: 'Permanent thread damage on the first install attempt.',
      },
      {
        mistake: 'Pulling all wires at once and trying to remember the firing order',
        consequence: 'Crossed wires cause severe misfire and possible catalytic converter damage from raw fuel.',
      },
      {
        mistake: 'Reusing a damaged plug wire boot',
        consequence: 'Misfire under load that intermittently shows up only on hills or hard acceleration.',
      },
    ],
    successIndicators: [
      'Engine starts immediately on first crank with smooth idle',
      'No misfire codes after a 5-10 minute drive',
      'Acceleration is crisp with no hesitation',
      'Each plug torqued to 26 ft-lbs verified by torque wrench',
      'All six plug wire boots fully seated with audible click on install',
    ],
    whenToSeekHelp:
      'If a plug strips or cross-threads in the head and a thread chaser does not restore clean threads, stop work and have the head professionally repaired with a Heli-Coil. If multiple cylinders show oil or coolant fouling on the old plugs, internal engine diagnosis is needed before continuing — new plugs will not solve the underlying problem.',
    legalDisclaimer: 'This guide was generated by GarageIQ\'s AI from publicly available sources. Some information may be incorrect or out of date. Verify torque specifications and any safety-critical procedures against the OEM service manual for your vehicle before performing work. The Haynes Manual tab on this page links to the licensed manual for your vehicle. GarageIQ is not a substitute for a qualified mechanic. By using this guide you agree to GarageIQ\'s Terms of Service and assume responsibility for the work performed.',
    manualSource: {
      title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)',
      url: 'https://www.haynes.com/en-us/wrangler/jeep/2003',
      notes: 'Procedure sourced from public catalogs and forums; cross-reference torque values, gap specification, and firing order with your licensed manual before service.',
    },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — TRANSFER CASE FLUID CHANGE (NV231 / NV241OR) — engine-agnostic
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-transfer-case-fluid-change',
    title: 'Transfer Case Fluid Change (NV231 / NV241OR)',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [{ startYear: 1997, endYear: 2006 }],
    },
    video: {
      youtubeId: 'nALCggqo7Qk',
      title: 'Jeep Wrangler TJ - DIY How To Change Transfer Case Fluid 1997-2004 (NP231J)',
      channel: 'YouTube',
    },
    source: 'Generated by GarageIQ AI from publicly available sources (Mopar parts catalog, FSM transfer-case service intervals, owner\'s manual capacity tables, technician forums and Wrangler community service threads). Cross-checked and structured per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Routine Maintenance & Fluids',
    difficulty: 'beginner',
    duration: '20-30 minutes',
    interval: 'Every 30,000 miles for normal use; every 15,000 miles or after any deep water crossing for off-road use',
    description:
      'The transfer case on the 1997-2006 TJ is either an NV231 (most TJs) or an NV241OR (Rubicon) — both use ATF+4 (Mopar MS-9602) automatic transmission fluid as their lubricant. Transfer case fluid breaks down over time and especially after water crossings, which can let water past the output seals. Changing the fluid is one of the simplest jobs on the TJ: drain plug out, fluid drains, drain plug in, refill through the fill plug. Total time is under 30 minutes.',
    overview: {
      difficulty: 'Beginner — two plugs, one fluid pump, no tools beyond a 3/8" ratchet',
      estimatedTime: '20-30 minutes including refill',
      estimatedCost: '$10-15 (2 quarts ATF+4) vs $80-120 at a shop',
      whyMatters: 'Transfer case bearings and chain wear rapidly on dirty or contaminated fluid. Replacement transfer cases run $800+ used. A $12 fluid change every 30k miles prevents that.',
      safetyNote: 'Support the vehicle on jack stands — never on a floor jack alone. The transfer case fluid can be hot if you have just driven the vehicle; allow 15 minutes of cool-down.',
    },
    tools: [
      'Floor jack and jack stands',
      '3/8" drive ratchet (the drain and fill plugs use a 3/8" square drive directly — no socket needed)',
      'Drain pan with at least 3-quart capacity',
      'Fluid pump or squeeze bottle (for refilling through the fill plug)',
      'Torque wrench',
      'Clean shop rags',
      'Nitrile gloves',
    ],
    parts: [
      {
        id: 'transfer-case-fluid',
        name: 'ATF+4 Transmission Fluid (Mopar MS-9602)',
        partNumber: 'Mopar 05013457AA (1 quart) or any ATF+4-licensed brand (Valvoline, Castrol, Pennzoil)',
        quantity: 2,
        price: 12,
        category: 'required',
        notes: 'NV231 capacity is 1.85 quarts (1.75 L). NV241OR (Rubicon) capacity is similar. Two quarts gives you margin for spillage. Use only ATF+4 — not generic ATF and not gear oil.',
      },
      {
        id: 'tcase-fill-plug-washer',
        name: 'Fill/Drain Plug Crush Washers (optional)',
        partNumber: 'Mopar M14 copper crush washer or aftermarket equivalent',
        quantity: 2,
        price: 2,
        category: 'recommended',
        notes: 'The plugs typically reuse the original sealing surface, but if you see seepage after a previous service, install fresh crush washers.',
      },
    ],
    safetyInformation:
      'Always support the vehicle on rated jack stands on solid, level ground. Transfer case fluid is hot after driving — allow 15 minutes cool-down. ATF+4 is a skin and eye irritant — wear nitrile gloves and safety glasses. Used transfer case fluid is hazardous waste — dispose of it at an auto-parts store recycling center, never down a drain.',
    beforeYouStart: [
      'Park on level ground — the transfer case fill level depends on level orientation',
      'Confirm you have 2 quarts of ATF+4 and a fluid pump on hand BEFORE removing the drain plug',
      'Locate the fill plug FIRST and verify it loosens — if the fill plug is seized, do not drain the case (you will be unable to refill it)',
      'Have a drain pan ready with capacity for 2+ quarts',
    ],
    steps: [
      {
        number: 1,
        title: 'Raise and Support the Vehicle',
        description:
          'Place the floor jack under the rear differential or a frame crossmember and lift the rear of the vehicle. Position jack stands under the rear frame rails. Slowly lower onto the stands. The vehicle must be level — if you raise only the rear, the front must be raised the same amount to keep the transfer case level for accurate fill.',
        why: 'Transfer case fill is gravity-based — the case is "full" when fluid spills out of the fill plug. If the case is tilted, the fill is wrong.',
        tip: 'Easiest method: drive the front and rear wheels onto matched ramps so all four wheels are equally elevated.',
        commonMistake: 'Raising only the rear. Tilts the case and gives a wrong fill level.',
      },
      {
        number: 2,
        title: 'Locate Fill and Drain Plugs',
        description:
          'On the NV231 and NV241OR, both plugs are on the rear face of the transfer case housing. The drain plug is the lower plug; the fill plug is the upper plug. Both use a 3/8" square drive — your ratchet handle (without a socket) inserts directly into the plug.',
        why: 'Confirming both plug locations before disassembly prevents the "drained but cannot fill" disaster.',
        tip: 'Some aftermarket skid plates partially block plug access. Verify clearance before draining.',
        commonMistake: 'Mistaking a plug from a nearby component for the transfer case plug. The transfer case is a discrete metal housing centered behind the transmission.',
      },
      {
        number: 3,
        title: 'CRITICAL — Loosen the Fill Plug FIRST',
        description:
          'Before draining anything, insert the 3/8" ratchet into the FILL plug (upper) and turn counterclockwise to break it loose. Do not remove it yet — just confirm it will turn. If it is seized, stop and treat the seized plug before continuing (penetrating oil, heat, etc.). Once you confirm the fill plug moves, you can proceed.',
        why: 'If you drain the case and then discover the fill plug is seized, you have a dry transfer case and no way to refill it without a tow to a shop.',
        tip: 'Apply penetrating oil to the fill plug and let it sit 10 minutes before attempting if the plug looks corroded.',
        commonMistake: 'Draining the case before verifying the fill plug. This single mistake has stranded countless DIYers.',
      },
      {
        number: 4,
        title: 'Drain the Old Fluid',
        description:
          'Position the drain pan under the drain plug. Insert the 3/8" ratchet into the drain plug and turn counterclockwise to break it loose, then unscrew by hand the rest of the way. Pull the plug clear and let the fluid drain — this takes 3-5 minutes. Inspect the fluid as it drains: clean ATF+4 is red and translucent; brown or black fluid indicates overdue service; metallic glitter indicates internal wear.',
        why: 'The drained fluid is your diagnostic data. Clean fluid means you are on schedule; dirty or contaminated fluid means service is overdue or there is a leak letting water in.',
        tip: 'A magnet on the drain plug (some plugs have one built in) catches fine metal particles — these are normal in small amounts.',
        commonMistake: 'Throwing out the drained fluid without inspecting it. Note the color and any contamination for future reference.',
      },
      {
        number: 5,
        title: 'Reinstall the Drain Plug',
        description:
          'Wipe the drain plug threads and the drain hole on the case clean with a rag. If the crush washer on the plug is flat or damaged, replace it. Hand-thread the plug back in for at least 3 full turns to confirm it is not cross-threading, then snug with the ratchet. Torque to 14-22 ft-lbs (19-30 Nm). Verify torque spec 14-22 ft-lbs (19-30 Nm) from OEM service manual — the FSM specifies 19-30 Nm; some sources cite a tighter range.',
        why: 'Cross-threading the drain plug ruins the magnesium-alloy case threads. Hand-starting catches it before damage.',
        tip: 'Apply a single wrap of PTFE thread tape if the plug seeps after install — but never enough to interfere with torque-to-spec.',
        commonMistake: 'Using the impact gun. The case threads cannot tolerate impact torque.',
      },
      {
        number: 6,
        title: 'Refill the Case Through the Fill Plug',
        description:
          'Remove the fill plug fully (it should now turn easily after step 3). Insert the fluid-pump nozzle or squeeze bottle tip into the fill hole. Pump fluid in slowly. The case is full when fluid begins to dribble out of the fill hole — at that exact moment, stop pumping. Wait 30 seconds for the level to settle, then add a tiny bit more if it has dropped below the fill hole edge.',
        why: 'Transfer case fill level is the bottom edge of the fill plug — there is no dipstick. Overfilling causes seal leaks; underfilling causes accelerated wear.',
        tip: 'Most fluid pumps deliver about 1 quart per 12-15 squeezes. The NV231 takes ~1.85 quarts; expect about 22 pumps to reach fill.',
        commonMistake: 'Trusting the capacity number without watching for fluid to dribble out. Always fill to the dribble point, not to a measured volume.',
      },
      {
        number: 7,
        title: 'Reinstall the Fill Plug',
        description:
          'Once fluid is at the fill-hole edge, wipe the fill plug threads and the hole clean. Hand-thread the plug for at least 3 turns to confirm it is not cross-threading, then snug with the ratchet. Torque to 14-22 ft-lbs (19-30 Nm). Verify torque spec 14-22 ft-lbs (19-30 Nm) from OEM service manual.',
        why: 'Same logic as the drain plug — case threads are soft; hand-start is mandatory.',
        tip: 'A thin film of fresh ATF+4 on the plug threads helps the plug seat without seizing.',
        commonMistake: 'Skipping the hand-start because "the plug feels fine." Always hand-start any plug into a soft-metal case.',
      },
      {
        number: 8,
        title: 'Test Drive and Verify',
        description:
          'Lower the vehicle off the jack stands. Take a short test drive (5-10 minutes) at varying speeds, including a brief shift from 2H to 4H if conditions are safe and dry. The transfer case should engage smoothly and operate quietly. After the drive, recheck for leaks at both plugs.',
        why: 'A test drive verifies the case engages correctly with the new fluid. Leak check after a drive (with the fluid hot and pressurized) catches problems that do not show up cold.',
        tip: 'Run through 2H, 4H, and 4L (briefly, on a safe surface) to verify all engagement modes work.',
        commonMistake: 'Skipping the leak check. Always verify after the first drive.',
      },
    ],
    torqueSpecifications: [
      {
        fastener: 'Transfer case drain plug (3/8" square drive)',
        specification: '14-22 ft-lbs (19-30 Nm). Verify torque spec 14-22 ft-lbs (19-30 Nm) from OEM service manual.',
        notes: 'Magnesium-alloy case — never use an impact tool; always hand-start the plug and finish with a torque wrench.',
      },
      {
        fastener: 'Transfer case fill plug (3/8" square drive)',
        specification: '14-22 ft-lbs (19-30 Nm). Verify torque spec 14-22 ft-lbs (19-30 Nm) from OEM service manual.',
        notes: 'Same plug type and torque as drain. Hand-start is mandatory.',
      },
    ],
    fluidSpecifications: [
      {
        fluid: 'Transfer Case Fluid',
        type: 'ATF+4 (Mopar MS-9602 specification)',
        capacity: 'NV231: 1.85 US quarts (1.75 L). NV241OR: similar — verify with FSM for your transfer case.',
        notes: 'Use only ATF+4. Do not substitute generic ATF, gear oil, or any other fluid — they will damage the chain and bearings.',
      },
    ],
    troubleshooting: [
      {
        problem: 'Fill plug is seized and will not loosen',
        likelyCause: 'Corrosion or thread-locker on the plug',
        whatToCheck: 'Inspect for visible rust around the plug head',
        fix: 'Apply penetrating oil and let sit 30+ minutes. Try again. If still seized, do not drain the case — schedule professional service. Heat (a heat gun, not open flame) on the case around the plug can help.',
      },
      {
        problem: 'Fluid drains out brown, black, or milky',
        likelyCause: 'Long-overdue service, water contamination from a deep crossing, or internal damage',
        whatToCheck: 'Note color and any metallic glitter; check the drain plug magnet (if equipped)',
        fix: 'Refill with fresh ATF+4 and drive a few hundred miles, then drain and refill again. Repeated dirty fluid suggests a seal leak letting water in or internal wear; further diagnosis needed.',
      },
      {
        problem: 'Plug seeps after install',
        likelyCause: 'Worn crush washer or under-torqued plug',
        whatToCheck: 'Re-torque the plug to the high end of spec (22 ft-lbs); if seepage continues, replace the crush washer',
        fix: 'Replace the crush washer (Mopar M14 copper or equivalent) and re-torque to spec.',
      },
      {
        problem: 'Transfer case engagement feels rough or notchy after fluid change',
        likelyCause: 'Wrong fluid type used (not ATF+4) or fluid level low',
        whatToCheck: 'Verify the fluid you used was ATF+4 specifically; recheck fill level (should be at fill-plug edge)',
        fix: 'If wrong fluid, drain immediately and refill with ATF+4. If level was low, top off to fill-plug edge.',
      },
    ],
    proTips: [
      'After any deep water crossing on the trail, change the transfer case fluid even if it is not on schedule — water past the seals turns the fluid milky and accelerates wear.',
      'Buy a fluid pump that fits ATF+4 quart bottles (~$15) — refilling through the fill plug without one is much messier.',
      'The NV231 and NV241OR drain plugs use a 3/8" square drive directly — no socket needed. Just stick the ratchet handle in.',
      'Mark the date and mileage of every transfer case fluid change in your maintenance log. This is one of the longest service intervals on the truck and easy to forget.',
      'Stick with Mopar OE ATF+4 for any major service, but for routine maintenance any ATF+4-licensed brand (Valvoline, Castrol, Pennzoil) works equivalently.',
    ],
    commonMistakes: [
      {
        mistake: 'Draining the case before verifying the fill plug loosens',
        consequence: 'Stranded vehicle with a dry transfer case and no way to refill — requires a tow.',
      },
      {
        mistake: 'Using gear oil or generic ATF instead of ATF+4',
        consequence: 'Damaged chain and bearings. Transfer case may feel notchy or fail completely within a few thousand miles.',
      },
      {
        mistake: 'Overfilling the case past the fill-plug edge',
        consequence: 'Seal leaks at the front output and rear output as the excess fluid pressurizes seals.',
      },
      {
        mistake: 'Cross-threading the fill plug into the soft case',
        consequence: 'Permanent thread damage requiring case repair (Heli-Coil) or replacement.',
      },
    ],
    successIndicators: [
      'Both plugs hand-started and torqued to 14-22 ft-lbs',
      'Fluid level at the bottom edge of the fill-plug hole',
      'No leaks at either plug after a 5-10 minute drive',
      'Transfer case engages smoothly in 2H, 4H, and 4L (when tested safely)',
      'New fluid is bright red and translucent at fill level',
    ],
    whenToSeekHelp:
      'If the fill plug cannot be loosened with penetrating oil and reasonable force, stop work — do not drain the case. If the drained fluid is heavily contaminated with metal particles or coolant-like sweetness, the transfer case may have internal damage that needs professional diagnosis. If the case continues to leak from a plug after re-torque and a fresh crush washer, the case threads may be damaged and need professional repair.',
    legalDisclaimer: 'This guide was generated by GarageIQ\'s AI from publicly available sources. Some information may be incorrect or out of date. Verify torque specifications and any safety-critical procedures against the OEM service manual for your vehicle before performing work. The Haynes Manual tab on this page links to the licensed manual for your vehicle. GarageIQ is not a substitute for a qualified mechanic. By using this guide you agree to GarageIQ\'s Terms of Service and assume responsibility for the work performed. Used transmission/transfer-case fluid is hazardous waste — dispose at an auto-parts store recycling center.',
    manualSource: {
      title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)',
      url: 'https://www.haynes.com/en-us/wrangler/jeep/2003',
      notes: 'Procedure sourced from public catalogs and forums; cross-reference torque values, fluid specification, and capacity with your licensed manual before service.',
    },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — FRONT DIFFERENTIAL FLUID CHANGE (Dana 30) — engine-agnostic
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-front-differential-fluid-change-dana-30',
    title: 'Front Differential Fluid Change (Dana 30)',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [{ startYear: 1997, endYear: 2006 }],
    },
    video: {
      youtubeId: 'juvjzb0jnbI',
      title: 'Changing the Front Differential Fluid on the front Dana 30 of my 97 Jeep TJ',
      channel: 'YouTube',
    },
    source: 'Generated by GarageIQ AI from publicly available sources (Mopar parts catalog, Spicer/Dana service literature, owner\'s manual capacity tables, technician forums and Wrangler community service threads). Cross-checked and structured per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Routine Maintenance & Fluids',
    difficulty: 'intermediate',
    duration: '45 minutes - 1 hour',
    interval: 'Every 30,000 miles for normal use; every 15,000 miles or after deep water crossings for off-road use',
    description:
      'The TJ\'s front axle is a Dana 30 (a Dana 44 on Rubicon trim) and unlike the rear axle, neither has a drain plug — fluid is changed by removing the differential cover. This makes the front diff fluid change more involved than a transfer case service: you need to remove ten cover bolts, scrape and clean the old gasket surface, apply RTV silicone, and torque the cover bolts in the correct pattern. The fluid is 75W-90 GL-5 gear oil. Job time is about an hour, mostly spent on the cover prep.',
    overview: {
      difficulty: 'Intermediate — fluid change is simple but cover removal and RTV application require care',
      estimatedTime: '45 minutes - 1 hour',
      estimatedCost: '$15-25 (1 quart 75W-90 GL-5 + RTV silicone) vs $100-140 at a shop',
      whyMatters: 'The front diff sees more thermal cycling and contamination than the rear (it is closer to the engine and crosses water more often). Fresh gear oil keeps the ring and pinion alive. A worn ring gear is a $400+ part.',
      safetyNote: 'Hot gear oil can cause burns. Allow the axle to cool for 30+ minutes after driving. Support the vehicle on rated jack stands — never on a floor jack alone.',
    },
    tools: [
      'Floor jack and jack stands',
      '13mm socket and ratchet (cover bolts)',
      'Torque wrench capable of 30 ft-lbs (40 Nm)',
      'Fluid pump or large squeeze bottle (for refilling through the fill plug)',
      'Gasket scraper or razor blade',
      'Brake cleaner',
      'Drain pan with at least 3-quart capacity',
      'Clean shop rags',
      'Nitrile gloves',
    ],
    parts: [
      {
        id: 'gear-oil-front-diff',
        name: '75W-90 GL-5 Gear Oil (1 quart)',
        partNumber: 'Mopar 5013486AA — equivalents: Valvoline SynPower 75W-90 GL-5 / Mobil 1 75W-90 / Royal Purple Max-Gear',
        quantity: 1,
        price: 12,
        category: 'required',
        notes: 'Dana 30 capacity is approximately 1.0 quart (0.95 L). Use only GL-5-rated gear oil. Synthetic 75W-90 is preferred for off-road use and extended drain intervals.',
      },
      {
        id: 'rtv-silicone-gear-oil',
        name: 'Gear-Oil-Compatible RTV Silicone',
        partNumber: 'Mopar 4318025AC or Permatex Ultra Black 82180',
        quantity: 1,
        price: 8,
        category: 'required',
        notes: 'The Dana 30 cover does not use a paper gasket — the seal is RTV silicone applied to a clean gasket surface. Use only gear-oil-compatible RTV (Permatex Ultra Black or Mopar OE).',
      },
    ],
    safetyInformation:
      'Hot gear oil from the differential can cause severe burns. Allow at least 30 minutes of cool-down after driving before opening the cover. Support the vehicle on rated jack stands on solid level ground — never on a floor jack alone. Used gear oil is hazardous waste — wear nitrile gloves and dispose at an auto-parts store. Brake cleaner is flammable — work in a ventilated area.',
    beforeYouStart: [
      'Park on level ground',
      'Confirm the differential is cool to the touch before opening',
      'Have a fluid pump and 1+ quarts of 75W-90 GL-5 gear oil on hand BEFORE removing the cover',
      'Have a fresh tube of gear-oil-compatible RTV silicone — old/expired RTV will not seal properly',
      'Locate the fill plug on the side of the differential housing and verify it loosens FIRST (before draining)',
    ],
    steps: [
      {
        number: 1,
        title: 'Raise and Support the Vehicle (Level)',
        description:
          'Raise the front of the vehicle and support on jack stands. Support the rear at the same height — the vehicle must be level for an accurate fluid fill. The front axle must hang freely without weight on the wheels.',
        why: 'Differential fill is the bottom edge of the fill plug — if the axle is tilted, the fill is wrong.',
        tip: 'Drive both front wheels onto matched ramps if you have them; this is the easiest way to keep things level.',
        commonMistake: 'Raising only the front. Tilts the axle and gives wrong fill level.',
      },
      {
        number: 2,
        title: 'Verify the Fill Plug Loosens FIRST',
        description:
          'Locate the fill plug on the back side of the differential housing (a hex-head plug or 3/8" square drive). Insert your wrench and confirm it turns counterclockwise. Do not remove it yet — just verify it moves. If it is seized, do not drain the differential until the fill plug problem is solved.',
        why: 'If the fill plug is seized and you have already drained the diff, you have no way to refill it. Always verify the fill plug works first.',
        tip: 'Apply penetrating oil to the fill plug 30 minutes before service if the plug looks corroded.',
        commonMistake: 'Skipping the fill-plug verification. This is a regular DIY disaster scenario.',
      },
      {
        number: 3,
        title: 'Position Drain Pan and Remove Cover Bolts',
        description:
          'Slide a 3+ quart drain pan under the differential cover. Using a 13mm socket, loosen ALL ten cover bolts but do NOT remove them yet — leave the bottom two in place and remove the top eight first. Then break the cover seal by gently prying with a flat-blade screwdriver in the alignment notch (not against the cover face — you can warp it). Once the seal breaks, support the cover with one hand and remove the bottom two bolts. Lower the cover slowly and let the fluid drain into the pan.',
        why: 'Removing the bottom bolts last (with the top eight already out) controls the dump of fluid. Removing all bolts at once causes the cover to fall off and dump fluid uncontrollably.',
        tip: 'A 13mm gear wrench is helpful for the upper bolts where ratchet swing is limited.',
        commonMistake: 'Removing all bolts at once. Cover falls and 1 quart of warm gear oil hits you in the face.',
      },
      {
        number: 4,
        title: 'Inspect the Fluid and Internal Components',
        description:
          'Examine the drained fluid: clean 75W-90 is amber to dark gold; black or brown indicates overdue service; metallic flakes (especially gold-colored brass flakes) indicate ring-and-pinion wear; coolant-like sweetness indicates water contamination. Look inside the differential — the ring gear and carrier should be visible. The teeth should be smooth with no chipping or pitting.',
        why: 'The drained fluid and visible internals are your diagnostic data. Catching wear early lets you change service intervals or get ahead of bigger repairs.',
        tip: 'A small magnet on a stick will pull metal particles from the bottom of the housing — these are your wear indicators.',
        commonMistake: 'Skipping the inspection. Free diagnostic data is wasted.',
      },
      {
        number: 5,
        title: 'Clean the Cover and Housing Sealing Surfaces',
        description:
          'Using a gasket scraper or single-edge razor blade, scrape every trace of old RTV from both the cover and the differential housing flange. Spray brake cleaner on a clean rag and wipe both surfaces until they are bare metal, dry, and oil-free. Run a finger around both flanges — they should feel completely smooth with no residue.',
        why: 'Fresh RTV will not seal against old RTV or oil residue. The new bead must bond directly to clean metal.',
        tip: 'A green Scotch-Brite pad is faster than a scraper for the final cleanup pass on the housing flange.',
        commonMistake: 'Leaving residue on the sealing surface. The cover will leak within days.',
      },
      {
        number: 6,
        title: 'Apply RTV and Reinstall the Cover',
        description:
          'Squeeze a continuous bead of RTV silicone (about 1/8" diameter) around the cover sealing surface, looping inboard of every bolt hole (the bead should pass on the inside of each bolt hole, not over the holes). Lift the cover into position immediately and start all ten bolts by hand for at least 3 turns each. Working from the center outward in a star pattern, snug the bolts evenly to draw the cover down on the RTV without squeezing it out unevenly. Do final torque to 30 ft-lbs (40 Nm) in the same star pattern. Verify torque spec 30 ft-lbs (40 Nm) from OEM service manual — Dana 30 spec is typically 30 ft-lbs but some sources cite a slightly different value.',
        why: 'A continuous bead with no gaps is what makes the seal. Star-pattern torque distributes load evenly so the cover does not warp.',
        tip: 'Once the bead is laid, you have about 5 minutes to install the cover before the RTV begins to skin over. Have all ten bolts ready to thread by hand.',
        commonMistake: 'Crushing the RTV bead by torquing the bolts unevenly or too fast. Always star-pattern, slow, even.',
      },
      {
        number: 7,
        title: 'Wait for RTV to Cure (15-30 minutes minimum)',
        description:
          'Most RTVs need at least 15-30 minutes of cure time before fluid contact. Read the tube — some require longer. Use this time to clean up the work area and organize for the refill.',
        why: 'Adding fluid before the RTV cures dissolves the seal and you will leak immediately.',
        tip: 'Permatex Ultra Black is one of the faster-curing gear-oil-rated RTVs.',
        commonMistake: 'Refilling immediately. The seal needs to cure first.',
      },
      {
        number: 8,
        title: 'Refill Through the Fill Plug',
        description:
          'Remove the fill plug fully (it should turn easily after step 2). Insert the fluid pump nozzle into the fill hole and pump in 75W-90 GL-5 gear oil slowly. The differential is full when fluid begins to dribble out of the fill hole — at that exact moment, stop pumping. The Dana 30 takes approximately 1 quart, but always go by the dribble-out point, not by measured volume.',
        why: 'Differential fill level is the bottom edge of the fill plug. Overfilling pressurizes seals; underfilling causes accelerated wear.',
        tip: 'Pump slowly near the end — the last few squeezes are when fluid will dribble out.',
        commonMistake: 'Trusting the capacity number. Always fill to the dribble-out point.',
      },
      {
        number: 9,
        title: 'Reinstall the Fill Plug and Test Drive',
        description:
          'Wipe the fill plug threads and the hole clean. Hand-thread the plug for at least 3 turns to confirm it is not cross-threading, then snug with the wrench. Torque to 24-30 ft-lbs (33-40 Nm). Verify torque spec 24-30 ft-lbs (33-40 Nm) from OEM service manual. Lower the vehicle off the jack stands. Take a 5-10 minute test drive at varying speeds. After the drive, recheck the cover and fill plug for any seepage.',
        why: 'A test drive heats the fluid and pressurizes the cover seal — leaks that do not show cold will show hot.',
        tip: 'Park on cardboard for 24 hours after the drive to catch any slow seepage.',
        commonMistake: 'Skipping the leak check. Always verify after the first drive.',
      },
    ],
    torqueSpecifications: [
      {
        fastener: 'Differential cover bolts (10 total)',
        specification: '30 ft-lbs (40 Nm). Verify torque spec 30 ft-lbs (40 Nm) from OEM service manual.',
        notes: 'Star-pattern torque sequence; even, slow tightening prevents cover warpage and squeezed RTV.',
      },
      {
        fastener: 'Fill plug',
        specification: '24-30 ft-lbs (33-40 Nm). Verify torque spec 24-30 ft-lbs (33-40 Nm) from OEM service manual.',
        notes: 'Hand-start for at least 3 turns before applying torque.',
      },
    ],
    fluidSpecifications: [
      {
        fluid: 'Front Differential Gear Oil',
        type: '75W-90 GL-5 (synthetic preferred for off-road use)',
        capacity: 'Dana 30: approximately 1.0 quart (0.95 L). Dana 44 (Rubicon): approximately 1.25 quarts (1.2 L). Always fill to the fill-plug edge.',
        notes: 'GL-5 rating is required for hypoid gear lubrication. Do not use ATF, motor oil, or any GL-4-only product.',
      },
    ],
    troubleshooting: [
      {
        problem: 'Cover leaks immediately after install',
        likelyCause: 'RTV bead had a gap, was disturbed during install, or was not given cure time',
        whatToCheck: 'Visually inspect the cover-to-housing seam for fluid emerging anywhere',
        fix: 'Drain the diff, remove the cover, scrape the RTV completely, reclean, reapply RTV bead, allow full cure, refill.',
      },
      {
        problem: 'Fill plug seeps after install',
        likelyCause: 'Plug under-torqued or threads damaged',
        whatToCheck: 'Re-torque to 30 ft-lbs; if seepage continues, inspect threads',
        fix: 'Apply a single wrap of PTFE thread tape (not enough to interfere with torque) and re-torque. If threads are damaged, the housing may need a Heli-Coil.',
      },
      {
        problem: 'Drained fluid contains gold/brass metallic flakes',
        likelyCause: 'Ring-and-pinion wear, possibly from chronic underfill or contaminated fluid',
        whatToCheck: 'Note the volume of metal — small amounts (a teaspoon over the magnet) indicate normal wear; chunks or significant glitter indicates real damage',
        fix: 'For minor wear, refill with synthetic 75W-90 and shorten the service interval. For significant metal, the differential needs professional inspection — internal repair is far cheaper than catastrophic failure.',
      },
      {
        problem: 'Drained fluid is milky/foamy white',
        likelyCause: 'Water contamination from a deep water crossing or seal leak',
        whatToCheck: 'Inspect axle vent (should not be blocked); pinion seal; cover seal',
        fix: 'Drain and refill with fresh fluid. After 100 miles drain and refill again to flush remaining water. Address the source — typically a long axle vent extension is added for off-road use to keep water out.',
      },
    ],
    proTips: [
      'Add a long axle vent hose extension (cheap upgrade) to prevent water intrusion during deep crossings — it routes the vent above water level inside the engine bay.',
      'Buy a quality differential cover (Mag-Hytec or similar) for off-road TJs — they hold more fluid for cooling, have a built-in drain plug, and have stronger sealing surfaces.',
      'After any deep water crossing, check the diff fluid color through the fill plug — milky fluid means water got past the seal.',
      'The Dana 30 and rear Dana 35/44 take different amounts of fluid but the same 75W-90 GL-5 — buy a 2-quart jug and do both at once.',
      'Star-pattern torque on the cover is non-optional. Hand-snug the diagonals, then apply final torque.',
    ],
    commonMistakes: [
      {
        mistake: 'Removing all cover bolts at once',
        consequence: 'Cover dumps a quart of warm gear oil on you and the floor.',
      },
      {
        mistake: 'Not cleaning old RTV completely from sealing surfaces',
        consequence: 'New RTV cannot bond to residue. Cover leaks immediately.',
      },
      {
        mistake: 'Refilling before RTV has cured',
        consequence: 'Gear oil dissolves uncured RTV. Cover leaks immediately and the job must be redone.',
      },
      {
        mistake: 'Using GL-4 oil or ATF instead of GL-5',
        consequence: 'Hypoid gear teeth gall and wear rapidly. Replacement diff is $400+.',
      },
    ],
    successIndicators: [
      'No leaks at the cover seam after 30+ minutes of cure and a 5-10 minute drive',
      'Fluid level at the bottom edge of the fill-plug hole after refill',
      'Fill plug torqued to 24-30 ft-lbs and not seeping',
      'Differential is quiet during driving (no whine, growl, or clunk)',
      'Drained fluid was clean amber/gold with no significant metal',
    ],
    whenToSeekHelp:
      'If the drained fluid contains heavy metal flakes, chunks of metal, or gear-tooth fragments, do not refill and drive — the differential needs professional inspection. If the cover continues to leak after a complete RTV redo, the cover may be warped (often from over-torquing in a previous service) and needs replacement. If you find the fill plug seized and penetrating oil does not free it, professional service is needed.',
    legalDisclaimer: 'This guide was generated by GarageIQ\'s AI from publicly available sources. Some information may be incorrect or out of date. Verify torque specifications and any safety-critical procedures against the OEM service manual for your vehicle before performing work. The Haynes Manual tab on this page links to the licensed manual for your vehicle. GarageIQ is not a substitute for a qualified mechanic. By using this guide you agree to GarageIQ\'s Terms of Service and assume responsibility for the work performed. Used gear oil is hazardous waste — dispose at an auto-parts store recycling center.',
    manualSource: {
      title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)',
      url: 'https://www.haynes.com/en-us/wrangler/jeep/2003',
      notes: 'Procedure sourced from public catalogs and forums; cross-reference cover-bolt torque, fluid specification, and capacity with your licensed manual before service.',
    },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — WIPER BLADE REPLACEMENT — engine-agnostic
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-wiper-blade-replacement',
    title: 'Windshield Wiper Blade Replacement',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [{ startYear: 1997, endYear: 2006 }],
    },
    video: {
      youtubeId: 'IP2P9xYftzY',
      title: 'Windshield Wiper Blade Removal and Installation | Jeep Wrangler',
      channel: 'CheaperJeeperTV',
    },
    source: 'Generated by GarageIQ AI from publicly available sources (Mopar parts catalog, Bosch / Rain-X / Trico application charts, owner\'s manual, technician forums). Cross-checked and structured per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Routine Maintenance & Fluids',
    difficulty: 'beginner',
    duration: '5-10 minutes',
    interval: 'Every 6-12 months, or whenever blades streak, chatter, or skip across the windshield',
    description:
      'Windshield wiper blade replacement is the single simplest maintenance item on any vehicle and one of the most safety-relevant — visibility in rain or snow can be the difference between seeing a hazard and not. The TJ uses two 15-inch wiper arms with a J-hook attachment, the most common automotive wiper attachment style. Replacement takes under five minutes once you know the trick to the J-hook release.',
    overview: {
      difficulty: 'Beginner — no tools needed, no experience required',
      estimatedTime: '5-10 minutes for both blades',
      estimatedCost: '$15-30 for a pair (varies by brand) vs $30-50 at a shop',
      whyMatters: 'Worn wipers streak, chatter, and miss areas of the windshield in rain — a real safety hazard. Replacement is so cheap and quick there is no excuse to drive on bad blades.',
      safetyNote: 'When the wiper arm is lifted off the windshield, do not let it spring back uncontrolled — the metal arm will crack the windshield. Always lower it gently when finished.',
    },
    tools: [
      'No tools required',
      'Optional: clean rag and glass cleaner to clean the windshield first',
    ],
    parts: [
      {
        id: 'wiper-blades-tj',
        name: 'Windshield Wiper Blades (pair, 15")',
        partNumber: 'Bosch 4815, Rain-X 5079275-1, Trico 30-150, or any 15" J-hook blade',
        quantity: 2,
        price: 25,
        category: 'required',
        notes: '1997-2006 TJ uses two 15" J-hook blades (driver and passenger sides are the same size). Premium silicone-rubber blades (Rain-X, Bosch ICON) last 2-3x longer than basic rubber and are worth the extra cost.',
      },
    ],
    safetyInformation:
      'When lifting the wiper arm off the windshield, the spring tension wants to pull it back down. If you let go with the blade off, the bare metal arm will spring against the glass and can crack it. Always control the arm with one hand throughout the procedure, and lower it gently against the windshield before walking away.',
    beforeYouStart: [
      'Confirm you have the correct blade size (15" for both sides on TJ)',
      'Read the new blade package to find the J-hook attachment instructions specific to that brand',
      'Optionally clean the windshield first — fresh blades on a dirty windshield wear out faster',
      'Park on level ground in a place where the wipers can be lifted (out of the garage if your roof is low)',
    ],
    steps: [
      {
        number: 1,
        title: 'Lift the Wiper Arm Off the Windshield',
        description:
          'Standing in front of the windshield, grasp the wiper arm at its base and lift it away from the glass until the arm reaches its locked vertical position. The arm will stay vertical on its own due to a hinge detent. Repeat for the second wiper.',
        why: 'Lifting the arm gives you working room to remove the blade. The locked vertical position keeps the spring tension off so you can work with both hands.',
        tip: 'If the arm will not stay vertical (some TJs have weakened detents), you will need to keep one hand on the arm throughout the procedure.',
        commonMistake: 'Pulling the arm sideways instead of straight up. The hinge only flexes one direction — straight up.',
      },
      {
        number: 2,
        title: 'Position the Blade and Locate the J-hook',
        description:
          'Look at the wiper blade where it meets the arm. The arm ends in a J-shaped hook that loops through a slot in the blade adapter. There is a small plastic tab or button on the blade adapter that releases the J-hook. The exact location and shape of the release varies by blade brand — read the package or look closely.',
        why: 'Identifying the release mechanism prevents accidentally bending the arm or breaking the blade adapter.',
        tip: 'Most J-hook blade adapters have a release tab that you press inward while sliding the blade down the arm.',
        commonMistake: 'Forcing the blade off without finding the release. You will break the adapter.',
      },
      {
        number: 3,
        title: 'Release and Slide Off the Old Blade',
        description:
          'Press the J-hook release tab while pulling the blade DOWN along the arm (toward the base of the arm, away from the J-hook tip). Once the J-hook is clear of the slot, the blade slides off the arm.',
        why: 'The J-hook holds the blade by capturing it through a slot — releasing the tab and sliding down disengages the J from the slot.',
        tip: 'If the blade does not slide easily, you have not fully released the tab. Stop and look more carefully.',
        commonMistake: 'Sliding up instead of down. The J-hook captures from above, so the blade releases by going down.',
      },
      {
        number: 4,
        title: 'Install the New Blade',
        description:
          'Take the new blade and orient it the same way as the old (rubber edge facing the windshield, curved toward the side of the vehicle the blade was on). Slide the new blade UP onto the arm so the J-hook captures the blade adapter slot. Push until you hear a click — the J-hook is now locked into the slot.',
        why: 'The click confirms the J-hook has fully engaged. Without a positive click, the blade can lift off the arm at high speed.',
        tip: 'Tug very gently on the new blade after install — it should not pull off the arm. If it does, the J-hook has not engaged.',
        commonMistake: 'Stopping when the blade "feels" installed without verifying the click.',
      },
      {
        number: 5,
        title: 'Lower the Wiper Arm Gently',
        description:
          'With the new blade in place and verified, control the wiper arm with one hand and gently lower it back onto the windshield. Do NOT let go of the arm until the new blade rests against the glass.',
        why: 'A spring-loaded arm with a fresh blade slamming onto the glass can chip or crack the windshield. Controlled lowering prevents this.',
        tip: 'Some people place a thick rag on the windshield where the arm will land as a precaution.',
        commonMistake: 'Letting the arm spring back unattended. Cracked windshield is a $300+ repair caused by a 5-cent moment of inattention.',
      },
      {
        number: 6,
        title: 'Repeat for the Second Wiper',
        description:
          'Repeat steps 1-5 for the other wiper. Both blades on the TJ are the same 15" size and the same procedure applies.',
        why: 'Always replace blades as a pair. A new blade and an old blade will wear unevenly and you will be back here in three months for the other one.',
        tip: 'Even if only one blade looks bad, replace both — they have aged identically and the second is right behind.',
        commonMistake: 'Replacing only the streaking blade. The other one is right behind it and will streak in weeks.',
      },
      {
        number: 7,
        title: 'Test the New Wipers',
        description:
          'Spray some windshield washer fluid (or pour a small amount of water on the windshield) and run the wipers through one cycle. The new blades should sweep cleanly with no streaking, chatter, or skipping. If a blade chatters, it may be installed slightly off-angle — the J-hook engagement should fix this naturally on the second sweep.',
        why: 'Verifying performance before driving in rain catches any installation problems while it is still convenient to fix.',
        tip: 'If a blade chatters persistently, recheck the J-hook click — a partially engaged blade can sit slightly off-vertical.',
        commonMistake: 'Not testing before driving. Discovering a streaking blade in a downpour is no fun.',
      },
    ],
    torqueSpecifications: [],
    fluidSpecifications: [
      {
        fluid: 'Windshield Washer Fluid',
        type: 'Standard automotive washer fluid (winter formula in cold climates)',
        capacity: 'Topped off as needed in the reservoir under the hood',
        notes: 'Not strictly part of wiper service but always check the level when changing blades.',
      },
    ],
    troubleshooting: [
      {
        problem: 'New blade streaks immediately on first wipe',
        likelyCause: 'Manufacturing defect or contamination on the rubber edge',
        whatToCheck: 'Wipe the rubber edge of the blade with a clean cloth and isopropyl alcohol; sometimes shipping oil/silicone needs to be removed',
        fix: 'Clean the blade edge. If streaking persists after a few cycles, return the blade — defective from the factory.',
      },
      {
        problem: 'Blade chatters across the glass',
        likelyCause: 'Blade installed at slight wrong angle, dirty windshield, or worn arm hinge',
        whatToCheck: 'Verify the J-hook is fully clicked. Clean the windshield with proper glass cleaner.',
        fix: 'Clean glass and re-verify J-hook engagement. If chatter persists with a clean windshield and fully engaged blade, the wiper arm itself may have a worn hinge — replace the arm.',
      },
      {
        problem: 'Blade lifts off the windshield at highway speed',
        likelyCause: 'J-hook not fully engaged, or wiper arm spring is weak',
        whatToCheck: 'Park and check the J-hook click; tug the blade to verify it is locked',
        fix: 'Re-engage the J-hook fully. If the blade is engaged but still lifts at speed, the wiper arm spring is weak and the arm needs replacement.',
      },
      {
        problem: 'New blade does not match the curve of the windshield',
        likelyCause: 'Wrong blade type for the vehicle (typically a "beam" blade that does not match the TJ\'s flat windshield)',
        whatToCheck: 'Visually compare the new blade to the windshield — does it lay flat across the glass?',
        fix: 'Some premium beam blades are designed for curved windshields and do not work well on the TJ\'s nearly-flat glass. Switch to a traditional metal-frame blade for best performance.',
      },
    ],
    proTips: [
      'Replace both blades at the same time, every 6-12 months. They cost less than $30 a pair — just put it on the calendar.',
      'Silicone-rubber blades (Rain-X Latitude, Bosch ICON) last 2-3x longer than basic rubber and clear better in heavy rain. Worth the extra cost.',
      'Top off washer fluid every time you change blades — the reservoir is small and runs out fast.',
      'In winter climates, switch to "winter blades" with a rubber boot covering the metal frame — prevents ice buildup that ruins regular blades.',
      'Lift the wiper arms off the windshield overnight in a freezing forecast — prevents the rubber from freezing to the glass and tearing on the next start.',
    ],
    commonMistakes: [
      {
        mistake: 'Letting the wiper arm spring back onto the windshield with no blade',
        consequence: 'Cracked windshield from the bare metal arm hitting the glass.',
      },
      {
        mistake: 'Forcing the old blade off without finding the J-hook release',
        consequence: 'Broken blade adapter or bent wiper arm.',
      },
      {
        mistake: 'Installing the blade upside-down (rubber edge away from windshield)',
        consequence: 'No wiping action; blade may flip and get destroyed on first sweep.',
      },
      {
        mistake: 'Replacing only one blade',
        consequence: 'The other side will streak within weeks and you will be back to do this again.',
      },
    ],
    successIndicators: [
      'Both blades sweep the windshield cleanly with no streaks or chatter',
      'No skipping or missed areas across the windshield arc',
      'Both blades clicked positively into the J-hook (verified by gentle tug)',
      'Wiper arms returned to the correct rest position against the windshield',
      'No streaks visible after two full cycles with washer fluid',
    ],
    whenToSeekHelp:
      'If new blades do not solve the streaking or chatter, the problem is likely with the wiper arm hinges (springs weak), the wiper motor linkage (worn pivots), or the windshield itself (deeply pitted glass cannot be cleared by any blade). Wiper arm replacement is a 30-minute job; motor and linkage are involved enough to warrant a shop.',
    legalDisclaimer: 'This guide was generated by GarageIQ\'s AI from publicly available sources. Some information may be incorrect or out of date. Verify torque specifications and any safety-critical procedures against the OEM service manual for your vehicle before performing work. The Haynes Manual tab on this page links to the licensed manual for your vehicle. GarageIQ is not a substitute for a qualified mechanic. By using this guide you agree to GarageIQ\'s Terms of Service and assume responsibility for the work performed.',
    manualSource: {
      title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)',
      url: 'https://www.haynes.com/en-us/wrangler/jeep/2003',
      notes: 'Procedure sourced from public catalogs and forums; cross-reference blade size and any wiper-arm-specific notes with your licensed manual.',
    },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — REAR DIFFERENTIAL FLUID CHANGE (Dana 35 / Dana 44) — engine-agnostic
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-rear-differential-fluid-change',
    title: 'Rear Differential Fluid Change (Dana 35 / Dana 44)',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [{ startYear: 1997, endYear: 2006 }],
    },
    video: {
      youtubeId: 'wICmFdBGNJ8',
      title: 'How to change the Differential Fluid on a Dana 35 - Jeep Wrangler TJ',
      channel: 'YouTube',
    },
    source: 'Generated by GarageIQ AI from publicly available sources (Mopar parts catalog, Spicer/Dana service literature, owner\'s manual capacity tables, technician forums and Wrangler community service threads). Cross-checked and structured per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Routine Maintenance & Fluids',
    difficulty: 'intermediate',
    duration: '45 minutes - 1 hour',
    interval: 'Every 30,000 miles for normal use; every 15,000 miles or after deep water crossings for off-road use; the first service should occur at 500-1,000 miles on a new or rebuilt axle to flush break-in metal',
    description:
      'The TJ\'s rear axle is a Dana 35 on most trims and a Dana 44 on Rubicon and some optional packages — both share the same service procedure. Like the front Dana 30, the rear cover has no drain plug, so fluid is changed by removing the differential cover. The rear takes more fluid (Dana 35 ~2 quarts, Dana 44 ~2.5 quarts) than the front and adds one extra step on Trac-Lok limited-slip axles: a friction modifier additive must be added with the fresh gear oil or the clutches will chatter. Fluid spec is 75W-90 GL-5, same as the front. Job time is about an hour, mostly spent on cover prep.',
    overview: {
      difficulty: 'Intermediate — fluid change is simple but cover removal and RTV application require care; Trac-Lok adds a friction-modifier step',
      estimatedTime: '45 minutes - 1 hour',
      estimatedCost: '$20-35 (2-3 quarts 75W-90 GL-5 + RTV silicone + friction modifier if Trac-Lok) vs $120-160 at a shop',
      whyMatters: 'The rear axle carries most of the vehicle\'s torque load and accumulates more wear metals than the front. Fresh gear oil keeps the ring-and-pinion alive; on Trac-Lok axles, fresh fluid plus friction modifier prevents the limited-slip clutches from chattering or grenading.',
      safetyNote: 'Hot gear oil can cause severe burns. Allow the axle to cool for 30+ minutes after driving. Support the vehicle on rated jack stands — never on a floor jack alone. Used gear oil and friction modifier are hazardous waste.',
    },
    tools: [
      'Floor jack and jack stands',
      '13mm socket and ratchet (Dana 35/44 cover bolts)',
      'Torque wrench capable of 30 ft-lbs (40 Nm)',
      'Fluid pump or large squeeze bottle (for refilling through the fill plug)',
      '3/8" square drive or 3/8" ratchet (for fill plug)',
      'Gasket scraper or razor blade',
      'Brake cleaner',
      'Drain pan with at least 4-quart capacity',
      'Clean shop rags',
      'Nitrile gloves',
    ],
    parts: [
      {
        id: 'gear-oil-rear-diff',
        name: '75W-90 GL-5 Gear Oil (2-3 quarts)',
        partNumber: 'Mopar 5013486AA — equivalents: Valvoline SynPower 75W-90 GL-5 / Mobil 1 75W-90 / Royal Purple Max-Gear',
        quantity: 3,
        price: 12,
        category: 'required',
        notes: 'Dana 35 capacity ~2.0 quarts (1.9 L); Dana 44 capacity ~2.5 quarts (2.4 L). Buy 3 quarts to have enough margin and the leftovers will not go bad. Use only GL-5-rated gear oil. Synthetic 75W-90 is preferred for off-road use.',
      },
      {
        id: 'rtv-silicone-gear-oil-rear',
        name: 'Gear-Oil-Compatible RTV Silicone',
        partNumber: 'Mopar 4318025AC or Permatex Ultra Black 82180',
        quantity: 1,
        price: 8,
        category: 'required',
        notes: 'The Dana 35/44 cover does not use a paper gasket — the seal is RTV silicone applied to a clean gasket surface. Use only gear-oil-compatible RTV (Permatex Ultra Black or Mopar OE).',
      },
      {
        id: 'friction-modifier-trac-lok',
        name: 'Trac-Lok Limited-Slip Friction Modifier (4 oz)',
        partNumber: 'Mopar 4318060AB (or Ford XL-3 / generic LSD additive labeled GM #1052358-equivalent)',
        quantity: 1,
        price: 10,
        category: 'conditional',
        notes: 'REQUIRED if the rear axle is a Trac-Lok limited-slip (look for the metal Trac-Lok tag on a cover bolt or a build-sheet code DLU/DRU). Add 4 oz with the fresh gear oil. Skip on open-differential axles. If you are not sure, adding it is harmless on an open diff.',
      },
    ],
    safetyInformation:
      'Hot gear oil from the differential can cause severe burns. Allow at least 30 minutes of cool-down after driving before opening the cover. Support the vehicle on rated jack stands on solid level ground — never on a floor jack alone. The rear axle holds 2-3 quarts of fluid which dumps quickly when the cover comes off — use a 4-quart pan and keep your face out of the splash zone. Used gear oil and friction modifier are hazardous waste — wear nitrile gloves and dispose at an auto-parts store. Brake cleaner is flammable — work in a ventilated area away from ignition sources.',
    beforeYouStart: [
      'Park on level ground — fill level depends on the vehicle being level',
      'Confirm the differential is cool to the touch before opening',
      'Identify your axle: Dana 35 (most TJs, 10 cover bolts, smaller pumpkin) vs Dana 44 (Rubicon and some optional packages, 10 cover bolts, larger pumpkin) — confirms fluid quantity',
      'Identify if you have a Trac-Lok limited-slip — if so, have friction modifier on hand BEFORE starting',
      'Have a fluid pump and 2-3 quarts of 75W-90 GL-5 gear oil staged BEFORE removing the cover',
      'Have a fresh tube of gear-oil-compatible RTV silicone — old/expired RTV will not seal properly',
      'Locate the fill plug on the side of the differential housing and verify it loosens FIRST (before draining)',
    ],
    steps: [
      {
        number: 1,
        title: 'Raise and Support the Vehicle (Level)',
        description:
          'Raise the rear of the vehicle and support on jack stands placed under the rear axle tubes (not under the differential housing itself). Support the front at the same height — the vehicle must be level for an accurate fluid fill. The axle should be at ride height or close to it.',
        why: 'Differential fill level is the bottom edge of the fill plug — if the axle is tilted, the fill is wrong. Stands under the axle tubes (not the housing) keep the cover area accessible.',
        tip: 'Drive both rear wheels onto matched ramps if you have them; this is the easiest way to keep things level and leave the axle at full ride height.',
        commonMistake: 'Raising only the rear. Tilts the axle and gives the wrong fill level.',
      },
      {
        number: 2,
        title: 'Verify the Fill Plug Loosens FIRST',
        description:
          'Locate the fill plug on the side of the differential housing — Dana 35/44 fill plugs are typically a 3/8" square-drive plug. Insert your wrench and confirm it turns counterclockwise. Do not remove it yet — just verify it moves. If it is seized, do not drain the differential until the fill plug problem is solved.',
        why: 'If the fill plug is seized and you have already drained the diff, you have no way to refill it. Always verify the fill plug works first.',
        tip: 'Apply penetrating oil to the fill plug 30 minutes before service if the plug looks corroded. Tap the plug head gently with a hammer to break corrosion before applying torque.',
        commonMistake: 'Skipping the fill-plug verification. This is the most common DIY rear-diff disaster.',
      },
      {
        number: 3,
        title: 'Position Drain Pan and Remove Cover Bolts',
        description:
          'Slide a 4-quart drain pan under the differential cover. Using a 13mm socket, loosen ALL ten cover bolts but do NOT remove them yet — leave the bottom two in place and remove the top eight first. Then break the cover seal by gently prying with a flat-blade screwdriver in the alignment notch (not against the cover face — you can warp it). Once the seal breaks, support the cover with one hand and remove the bottom two bolts. Lower the cover slowly and let the fluid drain into the pan.',
        why: 'Removing the bottom bolts last (with the top eight already out) controls the dump of fluid. Removing all bolts at once causes the cover to fall off and dump 2-3 quarts uncontrollably.',
        tip: 'A 13mm gear wrench is helpful for the upper bolts where ratchet swing is limited by the axle tubes.',
        commonMistake: 'Removing all bolts at once. Cover falls and 2-3 quarts of warm gear oil hits you and the floor.',
      },
      {
        number: 4,
        title: 'Inspect the Fluid and Internal Components',
        description:
          'Examine the drained fluid: clean 75W-90 is amber to dark gold; black or brown indicates overdue service; metallic flakes (especially gold-colored brass flakes) indicate ring-and-pinion wear; coolant-like sweetness or milky color indicates water contamination. Look inside the differential — the ring gear and carrier should be visible. The teeth should be smooth with no chipping or pitting. On a Trac-Lok, the clutch pack at the back of the carrier should look intact (no broken or warped plates).',
        why: 'The drained fluid and visible internals are your free diagnostic data. Catching wear early lets you change service intervals or get ahead of a $700+ axle rebuild.',
        tip: 'A small magnet on a stick will pull metal particles from the bottom of the housing — these are your wear indicators. A teaspoon over the magnet over 30k miles is normal; a tablespoon is concerning.',
        commonMistake: 'Skipping the inspection. Free diagnostic data is wasted.',
      },
      {
        number: 5,
        title: 'Clean the Cover and Housing Sealing Surfaces',
        description:
          'Using a gasket scraper or single-edge razor blade, scrape every trace of old RTV from both the cover and the differential housing flange. Spray brake cleaner on a clean rag and wipe both surfaces until they are bare metal, dry, and oil-free. Run a finger around both flanges — they should feel completely smooth with no residue. Pay special attention to the alignment-pin area where old RTV likes to hide.',
        why: 'Fresh RTV will not seal against old RTV or oil residue. The new bead must bond directly to clean metal.',
        tip: 'A green Scotch-Brite pad is faster than a scraper for the final cleanup pass on the housing flange.',
        commonMistake: 'Leaving residue on the sealing surface. The cover will leak within days.',
      },
      {
        number: 6,
        title: 'Apply RTV and Reinstall the Cover',
        description:
          'Squeeze a continuous bead of RTV silicone (about 1/8" diameter) around the cover sealing surface, looping inboard of every bolt hole (the bead must pass on the inside of each bolt hole, not over the holes). Lift the cover into position immediately and start all ten bolts by hand for at least 3 turns each. Working from the center outward in a star pattern, snug the bolts evenly to draw the cover down on the RTV without squeezing it out unevenly. Do final torque to 30 ft-lbs (40 Nm) in the same star pattern. Verify torque spec 30 ft-lbs (40 Nm) from OEM service manual — Dana 35/44 spec is typically 30 ft-lbs but some sources cite slightly different values.',
        why: 'A continuous bead with no gaps is what makes the seal. Star-pattern torque distributes load evenly so the cover does not warp.',
        tip: 'Once the bead is laid, you have about 5 minutes to install the cover before the RTV begins to skin over. Have all ten bolts ready to thread by hand before you cut the RTV tube.',
        commonMistake: 'Crushing the RTV bead by torquing the bolts unevenly or too fast. Always star-pattern, slow, even.',
      },
      {
        number: 7,
        title: 'Wait for RTV to Cure (15-30 minutes minimum)',
        description:
          'Most RTVs need at least 15-30 minutes of cure time before fluid contact. Read the tube — some require longer. Use this time to clean up the work area, organize for the refill, and pre-warm the gear oil bottles in a sink of warm water (warm gear oil pumps faster).',
        why: 'Adding fluid before the RTV cures dissolves the seal and you will leak immediately.',
        tip: 'Permatex Ultra Black is one of the faster-curing gear-oil-rated RTVs; some products require a full hour.',
        commonMistake: 'Refilling immediately. The seal needs to cure first.',
      },
      {
        number: 8,
        title: 'Add Friction Modifier (Trac-Lok Only)',
        description:
          'If your axle is a Trac-Lok limited-slip, pump 4 oz of friction modifier into the fill hole BEFORE adding the gear oil. The modifier is thick — squeezing it from the bottle with the nozzle inserted into the fill hole is the easiest method. If your axle is open (no Trac-Lok), skip this step entirely.',
        why: 'Trac-Lok clutches will chatter and shudder during turns without the friction modifier — and prolonged operation will fry the clutch pack ($300+ rebuild). Even fresh gear oil alone is not enough.',
        tip: 'If you cannot tell whether you have a Trac-Lok, jack one rear wheel off the ground (other side still on ground) and try to turn the lifted wheel by hand: if the opposite wheel turns the same direction, it is Trac-Lok; if the opposite wheel does nothing or turns the opposite direction, it is open. Or just add the modifier to be safe — it does not harm an open differential.',
        commonMistake: 'Forgetting the friction modifier on a Trac-Lok. Chatter starts within 100 miles and the clutches start wearing out.',
      },
      {
        number: 9,
        title: 'Refill Through the Fill Plug',
        description:
          'Remove the fill plug fully (it should turn easily after step 2). Insert the fluid pump nozzle into the fill hole and pump in 75W-90 GL-5 gear oil slowly. The differential is full when fluid begins to dribble out of the fill hole — at that exact moment, stop pumping. Dana 35 takes approximately 2 quarts; Dana 44 takes approximately 2.5 quarts; always go by the dribble-out point, not by measured volume.',
        why: 'Differential fill level is the bottom edge of the fill plug. Overfilling pressurizes the axle seals; underfilling causes accelerated ring-and-pinion wear.',
        tip: 'Pump slowly near the end — the last few squeezes are when fluid will dribble out. Keep a rag under the fill hole to catch the dribble.',
        commonMistake: 'Trusting the capacity number. Always fill to the dribble-out point.',
      },
      {
        number: 10,
        title: 'Reinstall the Fill Plug and Test Drive',
        description:
          'Wipe the fill plug threads and the hole clean. Hand-thread the plug for at least 3 turns to confirm it is not cross-threading, then snug with the wrench. Torque to 24-30 ft-lbs (33-40 Nm). Verify torque spec 24-30 ft-lbs (33-40 Nm) from OEM service manual. Lower the vehicle off the jack stands. Take a 5-10 minute test drive at varying speeds — include some tight figure-eight turns if your axle is Trac-Lok (this works the friction modifier into the clutch pack). After the drive, recheck the cover and fill plug for any seepage.',
        why: 'A test drive heats the fluid and pressurizes the cover seal — leaks that do not show cold will show hot. Tight turns on a Trac-Lok confirm the friction modifier is mixed in (no chatter).',
        tip: 'Park on cardboard for 24 hours after the drive to catch any slow seepage at the cover or fill plug.',
        commonMistake: 'Skipping the leak check. Always verify after the first heat cycle.',
      },
    ],
    torqueSpecifications: [
      {
        fastener: 'Differential cover bolts (10 total)',
        specification: '30 ft-lbs (40 Nm). Verify torque spec 30 ft-lbs (40 Nm) from OEM service manual.',
        notes: 'Star-pattern torque sequence; even, slow tightening prevents cover warpage and squeezed RTV.',
      },
      {
        fastener: 'Fill plug',
        specification: '24-30 ft-lbs (33-40 Nm). Verify torque spec 24-30 ft-lbs (33-40 Nm) from OEM service manual.',
        notes: 'Hand-start for at least 3 turns before applying torque to avoid cross-threading aluminum housing threads.',
      },
    ],
    fluidSpecifications: [
      {
        fluid: 'Rear Differential Gear Oil',
        type: '75W-90 GL-5 (synthetic preferred for off-road use)',
        capacity: 'Dana 35: approximately 2.0 quarts (1.9 L). Dana 44: approximately 2.5 quarts (2.4 L). Always fill to the fill-plug edge, not by volume.',
        notes: 'GL-5 rating is required for hypoid gear lubrication. Do not use ATF, motor oil, or any GL-4-only product.',
      },
      {
        fluid: 'Trac-Lok Friction Modifier (Trac-Lok axles only)',
        type: 'Mopar 4318060AB or equivalent LSD additive',
        capacity: '4 oz (approximately 118 mL) added before the gear oil',
        notes: 'Required on Trac-Lok limited-slip axles. Skip on open differentials. Adding to an open diff is harmless.',
      },
    ],
    troubleshooting: [
      {
        problem: 'Cover leaks immediately after install',
        likelyCause: 'RTV bead had a gap, was disturbed during install, or was not given cure time',
        whatToCheck: 'Visually inspect the cover-to-housing seam for fluid emerging anywhere along the perimeter',
        fix: 'Drain the diff, remove the cover, scrape the RTV completely, reclean both surfaces, reapply RTV bead, allow full cure, refill.',
      },
      {
        problem: 'Fill plug seeps after install',
        likelyCause: 'Plug under-torqued or threads damaged',
        whatToCheck: 'Re-torque to 30 ft-lbs; if seepage continues, inspect threads for damage',
        fix: 'Apply a single wrap of PTFE thread tape (not enough to interfere with torque) and re-torque. If threads are damaged, the housing may need a Heli-Coil repair.',
      },
      {
        problem: 'Limited-slip chatter during slow turns (Trac-Lok)',
        likelyCause: 'Friction modifier missing, insufficient, or not yet mixed into the clutch pack',
        whatToCheck: 'Confirm friction modifier was added; do tight figure-eight turns to work the modifier into the clutches',
        fix: 'If modifier was added but chatter persists after 50+ miles, drain a small amount and add another 2-4 oz of modifier. Persistent chatter after a fresh fill plus full modifier dose indicates worn clutch plates — internal repair needed.',
      },
      {
        problem: 'Drained fluid contains gold/brass metallic flakes',
        likelyCause: 'Ring-and-pinion wear, possibly from chronic underfill or contaminated fluid',
        whatToCheck: 'Note the volume of metal — small amounts (a teaspoon over the magnet) indicate normal wear; chunks or significant glitter indicates real damage',
        fix: 'For minor wear, refill with synthetic 75W-90 and shorten the service interval to 15,000 miles. For significant metal or chunks, the differential needs professional inspection — internal repair is far cheaper than catastrophic failure on the highway.',
      },
      {
        problem: 'Drained fluid is milky/foamy white',
        likelyCause: 'Water contamination from a deep water crossing or seal leak',
        whatToCheck: 'Inspect axle vent (should not be blocked); inspect pinion seal and axle-shaft seals for leakage; inspect cover seal',
        fix: 'Drain and refill with fresh fluid. After 100 miles drain and refill again to flush remaining water. Address the source — typically a long axle vent extension is added for off-road use to keep water out, and any leaking shaft seal must be replaced.',
      },
    ],
    proTips: [
      'Add a long axle vent hose extension (cheap upgrade) to prevent water intrusion during deep crossings — it routes the vent above water level inside the engine bay.',
      'Buy a quality differential cover (Mag-Hytec, Solid Diff Covers, or similar) for off-road TJs — they hold more fluid for cooling, have a built-in drain plug for easier future services, and stiffen the housing.',
      'After any deep water crossing, check the diff fluid color through the fill plug — milky fluid means water got past the seal.',
      'The Dana 30 (front) and Dana 35/44 (rear) take different amounts of fluid but the same 75W-90 GL-5 — buy a 4-quart jug and do both at once.',
      'On Trac-Lok axles, add the friction modifier FIRST (into the empty diff), then pump in the gear oil. The pumping action mixes them.',
      'Star-pattern torque on the cover is non-optional. Hand-snug the diagonals first, then apply final torque in two passes.',
    ],
    commonMistakes: [
      {
        mistake: 'Removing all cover bolts at once',
        consequence: 'Cover dumps 2-3 quarts of warm gear oil on you and the floor.',
      },
      {
        mistake: 'Forgetting the friction modifier on a Trac-Lok',
        consequence: 'Limited-slip clutches chatter, then wear out. $300+ rebuild.',
      },
      {
        mistake: 'Not cleaning old RTV completely from sealing surfaces',
        consequence: 'New RTV cannot bond to residue. Cover leaks immediately.',
      },
      {
        mistake: 'Refilling before RTV has cured',
        consequence: 'Gear oil dissolves uncured RTV. Cover leaks immediately and the job must be redone.',
      },
      {
        mistake: 'Using GL-4 oil or ATF instead of GL-5',
        consequence: 'Hypoid gear teeth gall and wear rapidly. Replacement axle is $700+.',
      },
    ],
    successIndicators: [
      'No leaks at the cover seam after 30+ minutes of cure and a 5-10 minute drive',
      'Fluid level at the bottom edge of the fill-plug hole after refill',
      'Fill plug torqued to 24-30 ft-lbs and not seeping',
      'No chatter or shudder during slow tight turns (Trac-Lok axles)',
      'Differential is quiet during driving (no whine, growl, or clunk)',
      'Drained fluid was clean amber/gold with no significant metal',
    ],
    whenToSeekHelp:
      'If the drained fluid contains heavy metal flakes, chunks of metal, or gear-tooth fragments, do not refill and drive — the differential needs professional inspection. If the cover continues to leak after a complete RTV redo, the cover may be warped (often from over-torquing in a previous service) and needs replacement. If a Trac-Lok continues to chatter after fresh fluid plus a full dose of friction modifier, the clutch pack is worn and needs an internal rebuild. If you find the fill plug seized and penetrating oil does not free it, professional service is needed — drilling out a stuck plug risks chips entering the housing.',
    legalDisclaimer: 'This guide was generated by GarageIQ\'s AI from publicly available sources. Some information may be incorrect or out of date. Verify torque specifications and any safety-critical procedures against the OEM service manual for your vehicle before performing work. The Haynes Manual tab on this page links to the licensed manual for your vehicle. GarageIQ is not a substitute for a qualified mechanic. By using this guide you agree to GarageIQ\'s Terms of Service and assume responsibility for the work performed. Used gear oil and friction modifier are hazardous waste — dispose at an auto-parts store recycling center.',
    manualSource: {
      title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)',
      url: 'https://www.haynes.com/en-us/wrangler/jeep/2003',
      notes: 'Procedure sourced from public catalogs and forums; cross-reference cover-bolt torque, fluid specification, capacity, and Trac-Lok identification with your licensed manual before service.',
    },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — REAR DRUM BRAKES (Shoes, Drum, Hardware) — engine-agnostic
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-rear-drum-brake-replacement',
    title: 'Rear Drum Brake Replacement (Shoes, Drum, Hardware)',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [{ startYear: 1997, endYear: 2006 }],
    },
    video: {
      youtubeId: 'nh0awGDLQsc',
      title: 'How to Replace Rear Wheel Cylinders 1997-2006 Jeep Wrangler',
      channel: 'TRQ',
    },
    source: 'Generated by GarageIQ AI from publicly available sources (Mopar parts catalog, Raybestos and Wagner brake catalogs, NHTSA TSBs, owner\'s manual, technician forums and Wrangler community service threads). Cross-checked and structured per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Brakes & Steering',
    difficulty: 'intermediate',
    duration: '1.5 - 2.5 hours (both sides)',
    interval: 'Inspect every 15,000 miles or with every tire rotation; replace shoes when lining is below 1/16" (1.6 mm). Drums: resurface or replace when out of round, deeply scored, or below the discard diameter stamped on the drum.',
    description:
      'Most TJs (all non-Rubicon trims, 1997-2006) use a 9-inch rear drum brake assembly built around the Dana 35 axle. Drum brakes look intimidating because of the springs and self-adjuster mechanism, but the procedure is straightforward if you do one side at a time and use the other side as a visual reference for spring routing. This guide covers shoe replacement, hardware (springs and adjusters), drum inspection, and the post-install adjustment that determines pedal feel. Doing both sides at once keeps braking balanced.',
    overview: {
      difficulty: 'Intermediate — many small spring-and-pin parts; first time can be slow but it is mostly methodical',
      estimatedTime: '1.5 - 2.5 hours for both sides',
      estimatedCost: '$60-120 (shoes + hardware kit + new drums if needed) vs $250-400 at a shop',
      whyMatters: 'Worn drum brakes give a soft pedal, pull, and longer stopping distances. Worn shoes can also score the drums, which doubles the cost of the next service.',
      safetyNote: 'Brake dust may contain asbestos in older shoes — DO NOT blow it out with compressed air. Wear a dust mask and wet the parts with brake cleaner before disassembly. Brakes are safety-critical — if anything is unclear, stop and consult a professional.',
    },
    tools: [
      'Floor jack and two jack stands',
      'Lug wrench / 19mm socket',
      'Brake spring tool (or a pair of needle-nose pliers + flat screwdriver)',
      'Brake hold-down spring tool (or a small socket and pliers)',
      'Drum-puller bolts (M8x1.25, two) — for stuck drums',
      'Wire brush',
      'Brake cleaner (1-2 cans)',
      'Torque wrench capable of 80 ft-lbs (108 Nm)',
      'Disposable nitrile gloves and a dust mask',
      'A clean rag and a parts tray',
      'Drum brake gauge or caliper (to measure drum ID)',
    ],
    parts: [
      {
        id: 'rear-brake-shoes',
        name: 'Rear Brake Shoes (axle set)',
        partNumber: 'Mopar 5093224AA — equivalents: Raybestos 716PG / Wagner Z716 / ACDelco 17715B',
        quantity: 1,
        price: 35,
        category: 'required',
        notes: 'Always replace shoes in axle pairs (both sides). Bonded shoes are quieter; riveted shoes last longer in heat. The 9" TJ drum uses a leading-trailing shoe layout — both shoes have the same lining length.',
      },
      {
        id: 'drum-hardware-kit',
        name: 'Drum Brake Hardware Kit',
        partNumber: 'Carlson H7321 / Centric 118.66005 (springs, hold-downs, self-adjuster spring)',
        quantity: 2,
        price: 12,
        category: 'required',
        notes: 'Always replace the springs and hold-down hardware with the shoes. Old springs lose tension and let the shoes drag.',
      },
      {
        id: 'self-adjuster',
        name: 'Self-Adjuster (star wheel) — left and right specific',
        partNumber: 'Carlson 13325 (LH) / 13326 (RH)',
        quantity: 2,
        price: 18,
        category: 'conditional',
        notes: 'Replace if the threads are corroded or the star wheel will not turn smoothly. Left and right adjusters thread in opposite directions — do not mix them up.',
      },
      {
        id: 'rear-drums',
        name: 'Rear Brake Drums (9-inch)',
        partNumber: 'Mopar 52008860 — equivalents: Centric 122.66007 / Raybestos 9728R',
        quantity: 2,
        price: 65,
        category: 'conditional',
        notes: 'Replace if drums are out of round, deeply scored, or below the discard diameter stamped inside (typically 9.060"). Resurfacing is an option for borderline drums but new drums are often cheaper than machine-shop time.',
      },
      {
        id: 'brake-cleaner-rear',
        name: 'Brake Parts Cleaner',
        partNumber: 'CRC 05089 or any non-chlorinated brake cleaner',
        quantity: 2,
        price: 6,
        category: 'required',
        notes: 'Used to wet-clean brake dust before any disassembly to suppress airborne particles.',
      },
      {
        id: 'brake-grease',
        name: 'High-Temp Brake Grease (Sil-Glyde or Permatex Ceramic)',
        partNumber: 'AGS SG-4 or Permatex 24125',
        quantity: 1,
        price: 6,
        category: 'required',
        notes: 'Apply to the backing-plate contact pads (the six raised pads where the shoes ride) and to the self-adjuster threads. Do not put grease on the shoe linings or drum surface.',
      },
    ],
    safetyInformation:
      'Brake dust may contain asbestos on older shoes — never blow it out with compressed air. Wear a fitted dust mask and wet all parts with brake cleaner before disassembly. Always replace shoes in axle pairs to keep braking balanced. Use rated jack stands on a flat surface — never work under a vehicle held only by a floor jack. Brake springs are under load; use a proper spring tool and keep your face out of the line of release. Brake parts cleaner is flammable — work in a ventilated area away from ignition sources. After service, do controlled low-speed stops in an empty area before driving in traffic to verify pedal feel and stopping power.',
    beforeYouStart: [
      'Park on level ground with the parking brake OFF (the parking brake locks the rear shoes against the drums)',
      'Loosen the rear lug nuts before lifting the vehicle',
      'Have a complete hardware kit and new shoes ready before you start (a stuck drum has wasted many afternoons)',
      'Take photos of the assembled drum brake on the FIRST side before disassembly — use them as the reference when you reassemble the SECOND side',
      'Do not press the brake pedal while the drums are off — it can pop the wheel cylinder pistons out',
    ],
    steps: [
      {
        number: 1,
        title: 'Raise the Vehicle and Remove Wheels',
        description:
          'Raise the rear of the vehicle and support on jack stands placed under the rear axle tubes (not under the differential housing). Remove both rear wheels using a 19mm socket / lug wrench.',
        why: 'Both wheels off lets you work one side and use the other as a reference. Stands under the axle tubes (not the housing) keep the brake assembly accessible.',
        tip: 'Keep one wheel chocked at the front during work — even with the parking brake off the front wheels should not roll.',
        commonMistake: 'Leaving the parking brake on. The shoes will be locked against the drum and the drum will not come off.',
      },
      {
        number: 2,
        title: 'Wet-Clean the Brake Assembly',
        description:
          'Spray the entire drum and visible brake area generously with brake cleaner over a drain pan. This wets any brake dust and suppresses airborne particles. Let the assembly drip-dry while you set up tools.',
        why: 'Brake dust can contain asbestos in older shoes — never disassemble dry. Wetting the area is the standard safety practice.',
        tip: 'A second cleaning pass after the drum is off (with the shoes still in place) gets the springs and adjuster clean enough to inspect.',
        commonMistake: 'Skipping the wet-clean and using compressed air. Disperses harmful dust into the air.',
      },
      {
        number: 3,
        title: 'Remove the Drum',
        description:
          'Pull the drum straight off the wheel studs. If it is stuck (typical on TJs from corrosion or a worn lip on the shoes), thread two M8x1.25 bolts into the threaded extraction holes on the face of the drum (most TJ drums have these) and tighten them evenly to push the drum off. If the drum still resists, back off the self-adjuster through the access slot at the back of the backing plate (push the actuator lever off the star wheel with one screwdriver, then turn the star wheel with another screwdriver to retract the shoes).',
        why: 'Drums get stuck on a worn lip or rust ring. Forcing them off with a hammer can crack the drum or damage the axle bearing.',
        tip: 'Spray the hub center with penetrating oil and let it sit 5 minutes before pulling. Tap the drum face — not the edge — with a dead-blow hammer if needed.',
        commonMistake: 'Hammering the edge of the drum. Cracks the drum and the part is now scrap.',
      },
      {
        number: 4,
        title: 'Photograph the Assembly Before Disassembly',
        description:
          'With the drum off, take 4-6 clear photos from different angles showing all springs, the adjuster mechanism, and the parking-brake lever routing. These photos are your reassembly reference. The TJ rear drum has a primary (front) shoe, secondary (rear) shoe, top return spring, bottom return spring, hold-down springs, self-adjuster cable or strut, and parking brake lever — easy to confuse on reassembly.',
        why: 'The hardware kit may not include perfect diagrams. Photos of the OEM assembly are the best reference.',
        tip: 'Capture the spring colors and hook directions specifically — left and right side mirror each other.',
        commonMistake: 'Skipping photos. Reassembling from memory leads to springs hooked in the wrong direction.',
      },
      {
        number: 5,
        title: 'Disassemble Springs, Hold-Downs, and Adjuster',
        description:
          'Using a brake spring tool, remove the top return spring (largest spring, runs across the top of the assembly). Then remove the hold-down springs at each shoe (push the cup down, rotate 90 degrees, lift off — a small socket plus pliers works). Lift the shoes outward, disconnecting the self-adjuster strut and the parking-brake actuator link. Set all hardware in a parts tray in the layout shown in your photos.',
        why: 'The top spring and hold-down springs hold the shoes in place. With those off and the adjuster disconnected, the shoes lift straight off the backing plate.',
        tip: 'Note the orientation of the self-adjuster strut — it has a specific in/out direction. Mark it with a paint pen before removal if unsure.',
        commonMistake: 'Releasing the top spring without a tool — it can fly across the garage.',
      },
      {
        number: 6,
        title: 'Inspect Wheel Cylinder, Backing Plate, and Drum',
        description:
          'Pull back the dust boots on each end of the wheel cylinder slightly — any fluid behind the boot means a leaking cylinder and it must be replaced (a $20 part — do it now, not later). Wipe the backing plate clean and check the six raised contact pads where the shoes ride for grooving or rust pits. Measure the drum inside diameter with a drum gauge or caliper; compare to the discard diameter stamped on the drum (typically 9.060"). Resurface or replace any drum out of spec.',
        why: 'A leaking wheel cylinder will contaminate new shoes within months. Worn backing-plate pads cause shoe drag and uneven wear. An out-of-round drum will make the new shoes unserviceable in short order.',
        tip: 'A small file can clean light corrosion from the backing-plate contact pads. Apply brake grease to the cleaned pads before new shoes go on.',
        commonMistake: 'Installing new shoes against a leaking wheel cylinder. The fluid soaks the shoes and the job has to be redone.',
      },
      {
        number: 7,
        title: 'Reassemble with New Shoes and Hardware',
        description:
          'Apply a thin film of brake grease to the six backing-plate contact pads and to the self-adjuster threads. Mount the new shoes in place — the shoe with longer lining is typically the secondary (rear) shoe; refer to your photos. Connect the parking-brake actuator link first, then the self-adjuster strut between the shoes. Install the hold-down springs (push the cup, rotate 90 degrees to capture the pin). Install the bottom spring, then the top return spring last using the spring tool. Verify the self-adjuster cable (if applicable) routes correctly behind the shoe and over the actuator lever.',
        why: 'Order matters: hold-downs first, then bottom spring, then top spring. The top spring is hardest to install with the others already in place but easiest to install when working alone.',
        tip: 'If the hardware kit colors are different from OEM, use the kit\'s instruction sheet to identify which spring goes where; the physical shape and length usually makes it obvious.',
        commonMistake: 'Installing shoes with the longer-lining side in the wrong position. Brake grab and uneven wear result.',
      },
      {
        number: 8,
        title: 'Pre-Adjust the Star Wheel',
        description:
          'Turn the star wheel (self-adjuster) until the shoes are spread close to drum diameter — the drum should slide on with light resistance. With the drum on, reach through the access slot in the backing plate with a flat screwdriver and rotate the star wheel until the drum just begins to drag, then back it off 8-10 clicks. The shoes should now be set close to final diameter.',
        why: 'Initial adjustment puts the shoes close to the drum so the self-adjuster does not have to work hard during the first few stops.',
        tip: 'A second screwdriver can hold the actuator lever off the star wheel while you turn — otherwise the lever blocks rotation in one direction.',
        commonMistake: 'Not pre-adjusting. The pedal will be very low for the first 50 stops as the self-adjuster slowly tightens.',
      },
      {
        number: 9,
        title: 'Reinstall Drum, Wheels, and Lower Vehicle',
        description:
          'Slide the drum onto the wheel studs. Confirm it spins by hand with light shoe drag. Reinstall the rear wheels and torque the lug nuts in a star pattern to 95 ft-lbs (129 Nm). Verify torque spec 95 ft-lbs (129 Nm) from OEM service manual. Lower the vehicle to the ground.',
        why: 'Star-pattern lug torque seats the wheel evenly and prevents warpage of the drum from uneven clamping.',
        tip: 'A torque-stick on an impact gun is fine for initial snug, but always finish with a calibrated torque wrench.',
        commonMistake: 'Over-torquing lugs with an impact gun. Warps the drum and causes pulsation.',
      },
      {
        number: 10,
        title: 'Bed-In the Brakes and Verify Pedal',
        description:
          'In an empty parking lot, drive forward at 20 mph and apply the brakes firmly to a stop 8-10 times. Reverse the vehicle 5-10 feet between stops and apply the brakes hard — this exercises the self-adjuster and seats the shoes against the drum. Pedal travel should be firm and consistent by the end of this routine. Then test on a normal road at 30 mph; the brakes should now feel firm and grab equally on both sides.',
        why: 'Self-adjusters tighten on reverse stops in TJs — backing up + braking is the calibration ritual. Bed-in transfers a thin layer of friction material to the drum surface.',
        tip: 'If pedal travel is still long after 10-15 reverse stops, recheck pre-adjustment — it may need to be tighter.',
        commonMistake: 'Driving in traffic before the bed-in. The pedal may be very long for the first dozen stops.',
      },
    ],
    torqueSpecifications: [
      {
        fastener: 'Wheel lug nuts',
        specification: '95 ft-lbs (129 Nm). Verify torque spec 95 ft-lbs (129 Nm) from OEM service manual.',
        notes: 'Star-pattern; final torque with a calibrated wrench, not an impact gun.',
      },
      {
        fastener: 'Brake hose / line at wheel cylinder (if disturbed)',
        specification: '11-13 ft-lbs (15-18 Nm). Verify torque spec 11-13 ft-lbs from OEM service manual.',
        notes: 'Only if you removed the wheel cylinder; standard shoe service does not disturb the hydraulics.',
      },
    ],
    fluidSpecifications: [
      {
        fluid: 'Brake fluid (only if wheel cylinder is replaced or hydraulics disturbed)',
        type: 'DOT 3 (DOT 4 acceptable)',
        capacity: 'N/A for shoe-only service. If the wheel cylinder is replaced, bleed the rear circuit with at least 8 oz of fresh fluid.',
        notes: 'Standard drum-shoe replacement does not require brake fluid or bleeding.',
      },
    ],
    troubleshooting: [
      {
        problem: 'Brake pedal feels low / requires multiple pumps',
        likelyCause: 'Self-adjusters not engaging; shoes too far from drum',
        whatToCheck: 'Do 10-15 reverse stops in a parking lot; recheck pedal',
        fix: 'If pedal does not firm up, tighten the star wheel pre-adjustment further. If still soft, suspect a wheel cylinder leak or air in the rear circuit.',
      },
      {
        problem: 'Brakes pull to one side',
        likelyCause: 'One side has contaminated shoes (oil/grease/fluid), uneven shoe install, or a stuck wheel cylinder piston',
        whatToCheck: 'Pull both drums and inspect shoes for contamination; check wheel cylinder for free movement',
        fix: 'Replace contaminated shoes (do not try to clean — they are scrap). Free or replace the stuck wheel cylinder.',
      },
      {
        problem: 'Squeal or grinding from rear after install',
        likelyCause: 'Shoes installed without grease on backing-plate pads, or hardware kit hold-downs not seated',
        whatToCheck: 'Pull drum, verify the six backing-plate pads have brake grease and the hold-downs are fully captured',
        fix: 'Lubricate the pads, reseat the hold-downs, reinstall.',
      },
      {
        problem: 'Drum will not come off',
        likelyCause: 'Worn lip on the drum or rust between drum and hub; shoes not retracted',
        whatToCheck: 'Use the M8x1.25 extraction holes on the drum face; if no holes, back off the star wheel through the access slot',
        fix: 'Once the shoes are retracted via the star wheel, the drum slides off. Soak the hub center in penetrating oil and tap the drum face (not the edge) with a dead-blow hammer.',
      },
      {
        problem: 'Parking brake will not hold',
        likelyCause: 'Parking-brake lever installed wrong, cable stretched, or shoes pre-adjusted too loose',
        whatToCheck: 'Verify the parking brake actuator link is in place inside the drum; adjust the parking brake cable at the equalizer',
        fix: 'Re-pre-adjust the star wheel tighter; adjust cable equalizer until the parking brake holds with 4-7 clicks of the handle.',
      },
    ],
    proTips: [
      'Buy a complete drum hardware kit even if your old springs look fine — they are cheap and replacing them once is a 5-minute job that saves a future repeat.',
      'If the OEM drum has the M8 extraction-bolt holes, mark and clean those threads with a tap before reinstalling — next time the drum will come off in 30 seconds.',
      'A "Brake Quiet" coating on the back of the shoes (the metal-on-metal contact surface) reduces squeal — apply per the can directions and let it tack up before installing.',
      'When buying drums, weigh them — quality drums are noticeably heavier than the cheapest ones, and lightweight drums are far more prone to warping.',
      'Both sides at once is faster than one at a time — the second side is half the work because you have already learned the spring routing.',
    ],
    commonMistakes: [
      {
        mistake: 'Pressing the brake pedal with the drums off',
        consequence: 'Wheel cylinder pistons can pop out, dump fluid, and require cylinder replacement.',
      },
      {
        mistake: 'Skipping the pre-adjustment',
        consequence: 'Pedal is on the floor for the first 50+ stops while self-adjusters slowly tighten.',
      },
      {
        mistake: 'Reusing old hardware',
        consequence: 'Stretched springs do not retract the shoes — leads to drag, heat, and premature wear.',
      },
      {
        mistake: 'Not greasing backing-plate contact pads',
        consequence: 'Shoes squeal and wear unevenly where they ride on dry metal pads.',
      },
    ],
    successIndicators: [
      'Drum spins by hand with light shoe drag (no metallic scraping)',
      'Brake pedal is firm with consistent travel after 10-15 reverse-stop bed-in',
      'No pulling left or right under firm braking',
      'Parking brake holds the vehicle on a slope with 4-7 clicks of the handle',
      'No squeal, grind, or pulsation from the rear at any speed',
    ],
    whenToSeekHelp:
      'If the pedal stays soft after thorough bed-in and bleeding, suspect a wheel cylinder leak or master cylinder failure — both warrant a shop check before driving in traffic. If the drum cannot be removed even with extraction bolts and full shoe retraction, the drum may be rusted to the hub and a shop press may be needed. If the brake hardware kit looks substantially different from the OEM layout (different number of springs, different routing), stop and consult the Haynes manual or a professional rather than guessing.',
    legalDisclaimer: 'This guide was generated by GarageIQ\'s AI from publicly available sources. Some information may be incorrect or out of date. Verify torque specifications and any safety-critical procedures against the OEM service manual for your vehicle before performing work. Brake service is safety-critical — if anything is unclear, stop and consult a professional. The Haynes Manual tab on this page links to the licensed manual for your vehicle. GarageIQ is not a substitute for a qualified mechanic. By using this guide you agree to GarageIQ\'s Terms of Service and assume responsibility for the work performed.',
    manualSource: {
      title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)',
      url: 'https://www.haynes.com/en-us/wrangler/jeep/2003',
      notes: 'Procedure sourced from public catalogs and forums; cross-reference shoe-set part number, drum discard diameter, and lug torque against your licensed manual before service.',
    },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — FRONT BRAKE CALIPER REPLACEMENT — engine-agnostic
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-brake-caliper-replacement',
    title: 'Front Brake Caliper Replacement',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [{ startYear: 1997, endYear: 2006 }],
    },
    video: {
      youtubeId: 'ilc0H_IYkk8',
      title: 'How to Replace Front Performance Brake Calipers 1997-2006 Jeep Wrangler',
      channel: 'TRQ',
    },
    source: 'Generated by GarageIQ AI from publicly available sources (Mopar parts catalog, Cardone reman caliper documentation, Raybestos and Centric service literature, NHTSA TSBs, owner\'s manual, technician forums and Wrangler community service threads). Cross-checked and structured per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Brakes & Steering',
    difficulty: 'intermediate',
    duration: '1 - 1.5 hours per side (plus bleeding)',
    interval: 'Replace when caliper is leaking, a piston is seized, slide pins are corroded beyond service, or after 100,000+ miles on the original.',
    description:
      'The TJ uses a single-piston floating caliper at each front wheel. The caliper body bolts to a fixed bracket that holds the brake pads and slides on two greased pins. Caliper failure modes are leaking piston seals, seized pistons, and rust-locked slide pins — all of which cause uneven pad wear, pull, or dragging brake. Calipers are sold as remanufactured units (cheap and reliable) or new. This guide covers caliper R&R, hose disconnect, pad and bracket inspection, and the post-install bleed.',
    overview: {
      difficulty: 'Intermediate — straightforward bolt-on but requires careful brake-hose disconnect and proper bleeding',
      estimatedTime: '1 - 1.5 hours per side, plus 30 minutes for a full brake bleed',
      estimatedCost: '$50-90 per side (reman caliper) plus brake fluid; vs $200-300 per side at a shop',
      whyMatters: 'A leaking or seized caliper is a safety issue — it can cause uneven braking, pulling, and in the worst case a stuck-on brake that overheats the rotor. Replacement is far cheaper than rebuild for most DIYers.',
      safetyNote: 'Brakes are safety-critical. Any leaking caliper or contaminated brake fluid is grounds to stop driving until repaired. Use rated jack stands. Brake fluid removes paint instantly — protect bodywork and your eyes.',
    },
    tools: [
      'Floor jack and jack stands',
      '13mm and 18mm sockets (caliper guide pins and bracket bolts)',
      'Lug wrench / 19mm socket',
      'C-clamp or caliper piston compressor (for retracting piston)',
      'Brake hose flare-nut wrench or correctly-sized wrench (typically 15mm or 18mm for the banjo / inverted-flare fitting)',
      'Torque wrench capable of 110 ft-lbs (149 Nm)',
      'Brake bleeder bottle and a length of 1/4" clear hose (or a one-person bleeder kit)',
      'Drain pan',
      'Brake cleaner (1 can)',
      'Disposable nitrile gloves and eye protection',
      'A clean rag',
    ],
    parts: [
      {
        id: 'caliper-front',
        name: 'Front Brake Caliper (loaded or bare)',
        partNumber: 'Cardone 18-4373 (RH) / 18-4374 (LH) — equivalents: Centric 141.66002 / Raybestos FRC11036',
        quantity: 1,
        price: 60,
        category: 'required',
        notes: 'Calipers are side-specific (left vs right). "Loaded" calipers include new pads and hardware — slightly more expensive but easier. Always replace in axle pairs only if both sides are at end-of-life or if you suspect age-related deterioration.',
      },
      {
        id: 'crush-washers',
        name: 'Banjo Bolt Crush Washers (2)',
        partNumber: 'Mopar 6505532AA — generic copper crush washers M10 size',
        quantity: 2,
        price: 2,
        category: 'required',
        notes: 'If the brake hose attaches with a banjo bolt, the two crush washers MUST be replaced — they are sealing washers and reusing them causes leaks. If the hose is an inverted-flare fitting, no washers needed.',
      },
      {
        id: 'brake-fluid-caliper',
        name: 'DOT 3 Brake Fluid (12 oz minimum)',
        partNumber: 'Mopar 4318080 / Valvoline VV632 / Prestone AS400',
        quantity: 1,
        price: 8,
        category: 'required',
        notes: 'Always use fresh brake fluid from a sealed bottle. DOT 3 or DOT 4 is acceptable; do not mix DOT 5 (silicone) which is not compatible with the TJ\'s ABS system.',
      },
      {
        id: 'pad-grease',
        name: 'Caliper Slide-Pin Grease (silicone-based)',
        partNumber: 'Permatex 24110 Ceramic Extreme Brake Parts Lubricant or AGS Sil-Glyde',
        quantity: 1,
        price: 5,
        category: 'required',
        notes: 'Apply only to slide pins, pad backing-plate contact points, and pad ear contact points. Do not get on pad friction surface or rotor.',
      },
    ],
    safetyInformation:
      'Brake fluid is corrosive to paint and harmful to eyes — wear eye protection and protect bodywork with rags or plastic sheeting. Used brake fluid absorbs water and is hazardous waste — dispose at an auto-parts recycling center. Brakes are safety-critical: never test-drive in traffic until you have verified pedal feel and stopping power in an empty parking lot. Use rated jack stands on a flat surface. Caliper pistons are under spring-and-fluid pressure when retracting — keep fingers clear and use the C-clamp or compressor tool, never pry against the rotor. Replace the brake hose crush washers if a banjo bolt is used; reusing them is the #1 cause of post-install leaks.',
    beforeYouStart: [
      'Park on level ground; loosen the front lug nuts before lifting',
      'Have new crush washers, fresh brake fluid, and the correct caliper handy before disconnecting the hose',
      'Confirm whether your hose connection is a banjo bolt (washers required) or inverted-flare fitting (no washers)',
      'Have a helper or a one-person bleeder kit ready — calipers must be bled after install',
      'Take a photo of the caliper and brake hose orientation before removal — banjo angle matters for clearance',
    ],
    steps: [
      {
        number: 1,
        title: 'Raise the Vehicle and Remove the Wheel',
        description:
          'Raise the front of the vehicle and support on jack stands placed under the frame rails (NOT the axle — the axle moves with the steering and you need it stable). Remove the front wheel using a 19mm socket / lug wrench.',
        why: 'Frame-mounted stands keep the front axle free to articulate slightly during caliper service. Axle-mounted stands can shift if the steering is bumped.',
        tip: 'A second jack stand under the lower control arm gives extra security if the frame stand alone feels marginal.',
        commonMistake: 'Standing under the axle. Bumping the steering can shift everything.',
      },
      {
        number: 2,
        title: 'Inspect Before Disassembly',
        description:
          'With the wheel off, inspect: caliper for leaks (any wet area on the body or fitting), slide pins for free motion (the caliper should rock slightly on the bracket), brake pads for thickness (replace if below 1/8" / 3 mm), and rotor for scoring or warping. Decide whether you need pads and rotor at the same time — doing them now saves a second bleed.',
        why: 'A leaking caliper is your reason to be here. But pads and rotor often need attention at the same age — bundle the work.',
        tip: 'A flashlight pointed across the rotor face shows scoring and surface condition better than direct light.',
        commonMistake: 'Replacing only the caliper while pads or rotor are also worn. You will be back doing this job again in 5,000 miles.',
      },
      {
        number: 3,
        title: 'Disconnect the Brake Hose',
        description:
          'Place a drain pan under the caliper. Identify the brake hose connection: most TJ front calipers use a banjo bolt with two crush washers; some have an inverted-flare fitting. For a banjo bolt, hold the hose end with a wrench and turn the banjo bolt counterclockwise with another wrench until removed. Cap the hose end with a vinyl cap or plastic bag to prevent fluid loss while you work. Discard the old crush washers — they are single-use.',
        why: 'Disconnecting the hose lets you remove the caliper completely. A capped hose limits fluid loss to a few ounces and shortens the bleed.',
        tip: 'Place a rag under the caliper to catch drips. Brake fluid removes paint — protect any painted surfaces.',
        commonMistake: 'Reusing the crush washers. Causes a slow leak that ruins the brake job.',
      },
      {
        number: 4,
        title: 'Remove the Caliper and Bracket',
        description:
          'Remove the two 13mm caliper guide-pin bolts from the back of the caliper (these hold the caliper to the bracket). Lift the caliper off the rotor and pads. If you are also replacing the bracket or rotor, remove the two 18mm caliper-bracket bolts that hold the bracket to the steering knuckle. Set the caliper aside in a way that does not stress the (now-disconnected) hose.',
        why: 'Two-bolt removal is the standard floating-caliper procedure. The bracket bolts are larger and torqued higher because they carry braking torque to the suspension.',
        tip: 'A breaker bar may be needed for the 18mm bracket bolts — they are torqued to 100 ft-lbs and may be corroded.',
        commonMistake: 'Removing the bracket bolts when you only need to swap the caliper. The bracket can stay in place if pads are not being replaced.',
      },
      {
        number: 5,
        title: 'Compare Old and New Caliper',
        description:
          'Set the old caliper next to the new one and compare bolt patterns, hose-fitting orientation, piston count (single-piston is correct for the TJ), and overall geometry. Confirm the new caliper is correct for your side (left vs right). The hose fitting on the new caliper should match the angle and clocking of the old one — if it is reversed, double-check you have the correct side.',
        why: 'Wrong-side or wrong-application calipers will not bolt up cleanly and can cause hose routing problems.',
        tip: 'Loaded calipers come with pads and slide-pin grease pre-applied — pop the pads out, set them aside, and reinstall after final bracket positioning.',
        commonMistake: 'Installing without comparing. A mismatched caliper causes hose interference and may not allow the bolts to thread.',
      },
      {
        number: 6,
        title: 'Lubricate Slide Pins and Install Caliper',
        description:
          'Apply a thin layer of brake-grease (silicone-based) to the slide pins. Insert the slide pins into the caliper bracket bushings — they should slide smoothly with no binding. Lift the new caliper into position over the brake pads, align with the bracket, and start both 13mm guide-pin bolts by hand for at least 3 turns. Torque to 11 ft-lbs (15 Nm). Verify torque spec 11 ft-lbs from OEM service manual.',
        why: 'Slide pin lubrication is critical for even pad wear. Without it, the caliper sticks and the inner pad wears far faster than the outer.',
        tip: 'Do not use grease on the threads of the guide-pin bolts — only on the smooth shaft. Threaded grease is messy and not needed.',
        commonMistake: 'Skipping pin lubrication. Inner pad wears out in half the expected mileage.',
      },
      {
        number: 7,
        title: 'Reconnect Brake Hose with NEW Crush Washers',
        description:
          'Position the brake hose at the correct angle for clearance (use your pre-removal photo). Place a NEW crush washer on each side of the banjo fitting (one between bolt head and hose, one between hose and caliper body). Hand-thread the banjo bolt for at least 3 turns to avoid cross-threading. Torque the banjo bolt to 25-30 ft-lbs (34-41 Nm). Verify torque spec 25-30 ft-lbs from OEM service manual.',
        why: 'New crush washers deform under torque to seal the fitting. Reused washers do not deform again and leak.',
        tip: 'Confirm the hose has no twist by sighting along its length before final torque.',
        commonMistake: 'Cross-threading the banjo bolt. Aluminum threads in the caliper body strip easily.',
      },
      {
        number: 8,
        title: 'Bleed the Brakes',
        description:
          'Top off the master cylinder reservoir with fresh DOT 3 fluid. Have a helper press and hold the brake pedal while you crack the bleeder screw on the new caliper (8mm typically) — fluid and air will push out. Close the bleeder before the helper releases the pedal. Repeat 4-6 times until clear, air-free fluid flows. Bleed both fronts (start with the side farther from the master cylinder — typically passenger side on the TJ — then driver side). Keep the master cylinder reservoir topped off — it must NEVER run dry during bleeding or air enters the system.',
        why: 'Disconnecting the hose introduces air into the caliper. Without proper bleeding, the pedal will be soft and braking compromised.',
        tip: 'A one-person bleeder kit (Mityvac, or a check-valve bottle) lets you bleed alone. The check-valve bottle method is the cheapest reliable option.',
        commonMistake: 'Letting the reservoir run dry mid-bleed. Air enters the master cylinder and the bleed must restart from scratch.',
      },
      {
        number: 9,
        title: 'Reinstall Wheel and Verify Pedal',
        description:
          'Reinstall the front wheel and torque lug nuts in a star pattern to 95 ft-lbs (129 Nm). Verify torque spec 95 ft-lbs from OEM service manual. With the vehicle still on jack stands, pump the brake pedal slowly 3-5 times — it should firm up to a normal feel. If the pedal goes to the floor, there is air in the system and bleeding must continue. Check the caliper and banjo fitting for any seepage.',
        why: 'Pumping the pedal seats the caliper piston against the new pads and confirms hydraulic integrity before lowering the vehicle.',
        tip: 'A flashlight at the banjo connection during pedal pump shows seepage immediately. Look for any wetness.',
        commonMistake: 'Lowering the vehicle before verifying pedal. A soft pedal at this stage means air remains.',
      },
      {
        number: 10,
        title: 'Test Drive and Bed-In',
        description:
          'Lower the vehicle. Start the engine and pump the pedal again — power-assist should give a firmer pedal. In an empty parking lot, do 8-10 firm stops from 25 mph to 5 mph (do not come to a complete stop — release as you slow to 5). This bed-in transfers a thin friction-material layer to the rotor surface. Check the caliper for leaks one more time before driving in traffic.',
        why: 'Bed-in seats new pads to the rotor surface. If you skip this, the pads will glaze and braking will be poor for hundreds of miles.',
        tip: 'After bed-in, drive 5-10 miles at moderate speed without heavy braking to let everything cool slowly.',
        commonMistake: 'Coming to full stops during bed-in. Pads can imprint material onto the rotor unevenly and cause pulsation.',
      },
    ],
    torqueSpecifications: [
      {
        fastener: 'Caliper guide-pin bolts (2)',
        specification: '11 ft-lbs (15 Nm). Verify torque spec 11 ft-lbs (15 Nm) from OEM service manual.',
        notes: 'Low torque — these bolts thread into aluminum slide-pin bushings.',
      },
      {
        fastener: 'Caliper bracket-to-knuckle bolts (2)',
        specification: '100 ft-lbs (136 Nm). Verify torque spec 100 ft-lbs (136 Nm) from OEM service manual.',
        notes: 'High torque — carries braking force to the steering knuckle. Use a breaker bar for removal if corroded.',
      },
      {
        fastener: 'Brake hose banjo bolt',
        specification: '25-30 ft-lbs (34-41 Nm). Verify torque spec 25-30 ft-lbs (34-41 Nm) from OEM service manual.',
        notes: 'New copper crush washers required on each side of the banjo fitting.',
      },
      {
        fastener: 'Bleeder screw',
        specification: '7-9 ft-lbs (10-12 Nm). Verify torque spec from OEM service manual.',
        notes: 'Snug only; over-torquing can crack the bleeder boss.',
      },
      {
        fastener: 'Wheel lug nuts',
        specification: '95 ft-lbs (129 Nm). Verify torque spec 95 ft-lbs from OEM service manual.',
        notes: 'Star pattern; final torque with calibrated wrench.',
      },
    ],
    fluidSpecifications: [
      {
        fluid: 'Brake fluid',
        type: 'DOT 3 (DOT 4 acceptable; DO NOT use DOT 5 silicone)',
        capacity: 'Approximately 8-12 oz per side for caliper R&R bleed; full system flush is ~32 oz',
        notes: 'Always use fluid from a sealed bottle. Used brake fluid is hazardous waste.',
      },
    ],
    troubleshooting: [
      {
        problem: 'Pedal goes to the floor after install',
        likelyCause: 'Air in the line; bleeder not closed before pedal release; reservoir ran dry during bleed',
        whatToCheck: 'Reservoir level, bleeder closure timing, and presence of air bubbles in bleed fluid',
        fix: 'Top off the reservoir, repeat bleeding starting at the passenger front then driver front. Stop and continue once clear bubble-free fluid flows.',
      },
      {
        problem: 'Brake fluid leaking at the banjo connection',
        likelyCause: 'Reused crush washers, banjo bolt under-torqued, or cross-threaded',
        whatToCheck: 'Visually confirm new washers were used; re-torque banjo bolt to spec',
        fix: 'Disassemble, replace both crush washers with new, retorque to 25-30 ft-lbs. If threads stripped, the caliper body may need replacement.',
      },
      {
        problem: 'Caliper not centered over rotor; pad rubs',
        likelyCause: 'Slide pins not greased, slide-pin bushings damaged, or wrong-side caliper installed',
        whatToCheck: 'Pull the caliper, verify slide pins move freely; confirm caliper is correct side',
        fix: 'Re-grease pins, replace bushings if torn, swap to correct-side caliper.',
      },
      {
        problem: 'Brake pulls to one side after install',
        likelyCause: 'New caliper not bled fully (air remaining), seized pin, or wrong caliper orientation',
        whatToCheck: 'Bleed once more on the affected side; verify slide pins move freely',
        fix: 'Re-bleed, regrease pins, retest. If pull persists, suspect a contaminated pad or stuck piston in the new caliper (rare on new units, possible on remans).',
      },
      {
        problem: 'Squeal during light braking after bed-in',
        likelyCause: 'Pad ear contact points dry, missing anti-rattle clips, or rotor surface not bedded',
        whatToCheck: 'Pad ears for grease, hardware kit clips for presence',
        fix: 'Apply silicone brake grease to pad ear contact points; replace any missing anti-rattle clips. If squeal continues, repeat bed-in routine.',
      },
    ],
    proTips: [
      'Buy a "loaded" caliper if you also need pads — it comes with new pads, hardware, and slide-pin grease applied. Net cost is $10-15 over a bare caliper but saves time and parts shopping.',
      'Use a one-person check-valve bleeder bottle if you have no helper — they cost $10-15 and turn brake bleeding into a one-person 30-minute job.',
      'Replace BOTH front calipers if one is failing and the other is original-age (15+ years). The cost is small relative to the labor of doing them at separate visits.',
      'Take photos of every step the first time you do this — the photos are your reference for the second side and any future jobs.',
      'After the first 100 miles of driving, recheck the banjo bolt and bleeder for seepage. Catching a slow leak early prevents bigger problems.',
    ],
    commonMistakes: [
      {
        mistake: 'Reusing the crush washers',
        consequence: 'Slow leak develops within a few hundred miles. Brake fluid drips onto the rotor and contaminates pads.',
      },
      {
        mistake: 'Letting the master cylinder reservoir run dry during bleeding',
        consequence: 'Air enters the master and the entire system must be bled fresh. Significantly more time.',
      },
      {
        mistake: 'Skipping slide-pin lubrication',
        consequence: 'Inner pad wears 2-3x faster than the outer; caliper can develop a stuck pin within 6 months.',
      },
      {
        mistake: 'Using DOT 5 silicone fluid',
        consequence: 'Not compatible with the TJ\'s ABS system; must drain entire system and refill with DOT 3.',
      },
    ],
    successIndicators: [
      'Firm brake pedal at engine-on with no creep toward floor',
      'No leaks at the banjo connection or bleeder after 100 miles',
      'No pull or pulsation under firm braking',
      'Even pad wear after 1,000 miles (inner and outer pads at same thickness)',
      'No squeal or grind under any braking condition',
    ],
    whenToSeekHelp:
      'If after a thorough bleed the pedal remains soft, suspect a master cylinder failure or ABS module bleed requirement — both warrant shop diagnosis. If the brake hose threads in the caliper body strip, the new caliper is scrap and must be replaced — do not try to JB Weld or insert a Heli-Coil in a brake-hose fitting. If you discover ABS issues during bleeding (pedal pulsing or warning lamp), the ABS module may need a scan-tool bleed, which requires a shop.',
    legalDisclaimer: 'This guide was generated by GarageIQ\'s AI from publicly available sources. Some information may be incorrect or out of date. Verify torque specifications and any safety-critical procedures against the OEM service manual for your vehicle before performing work. Brake service is safety-critical — if anything is unclear, stop and consult a professional. The Haynes Manual tab on this page links to the licensed manual for your vehicle. GarageIQ is not a substitute for a qualified mechanic. By using this guide you agree to GarageIQ\'s Terms of Service and assume responsibility for the work performed.',
    manualSource: {
      title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)',
      url: 'https://www.haynes.com/en-us/wrangler/jeep/2003',
      notes: 'Procedure sourced from public catalogs and forums; cross-reference caliper-bracket and banjo torque against your licensed manual before service.',
    },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — BRAKE MASTER CYLINDER REPLACEMENT — engine-agnostic
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-brake-master-cylinder-replacement',
    title: 'Brake Master Cylinder Replacement',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [{ startYear: 1997, endYear: 2006 }],
    },
    video: {
      youtubeId: 'YdYSjxB9RX4',
      title: 'Installing Jeep TJ Master Cylinder and Bleeding Brakes',
      channel: 'Rust Revival Garage',
    },
    source: 'Generated by GarageIQ AI from publicly available sources (Mopar parts catalog, Cardone reman master cylinder documentation, Centric and Raybestos service literature, NHTSA TSBs, owner\'s manual, technician forums and Wrangler community service threads). Cross-checked and structured per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Brakes & Steering',
    difficulty: 'intermediate',
    duration: '1.5 - 2.5 hours (including bench bleed and full system bleed)',
    interval: 'Replace when pedal sinks to the floor with no external leak (internal cup seal failure), reservoir contains contamination that flushing cannot resolve, or the master cylinder body itself leaks.',
    description:
      'The brake master cylinder converts pedal force into hydraulic pressure for all four brakes. The TJ uses a tandem master cylinder (two separate circuits — front and rear) that bolts to the brake booster on the firewall. The most common failure is internal cup-seal wear, which lets fluid bypass internally — the pedal sinks slowly to the floor while sitting at a stoplight, with no external leak visible. Replacement requires bench-bleeding the new unit before installing, then a full system bleed afterward. Plan for a half day with bleeding included.',
    overview: {
      difficulty: 'Intermediate — bolt-on but bench bleeding and full system bleed are essential and easy to skip',
      estimatedTime: '1.5 - 2.5 hours including bench bleed and four-corner system bleed',
      estimatedCost: '$50-90 (reman master cylinder + 32 oz brake fluid + new brake-line crush washers if used) vs $250-400 at a shop',
      whyMatters: 'A failing master cylinder is the most insidious brake problem — there is no external leak so it is easy to dismiss, but pedal feel slowly degrades and braking authority drops. Replacement restores full braking confidence.',
      safetyNote: 'Brakes are safety-critical. After this job, pedal feel must be verified in an empty parking lot before driving in traffic. Brake fluid removes paint instantly — protect bodywork and use eye protection.',
    },
    tools: [
      'Two open-end wrenches: 13mm (master cylinder mounting nuts) and 14mm or flare-nut wrench (brake line fittings)',
      'Brake-line flare-nut wrench set (10mm, 12mm, 14mm) — strongly preferred over open-end',
      'Bench bleeder kit (clear hoses + reservoir adapters) — comes with most new master cylinders',
      'Vise (for bench bleeding)',
      'Brake bleeder bottle and 1/4" clear hose (or one-person bleeder kit) for system bleed',
      'Drain pan',
      'Turkey baster or fluid extractor (to empty old reservoir)',
      'Brake cleaner (1 can)',
      'Disposable nitrile gloves and eye protection',
      'Old towels and plastic sheeting (paint protection)',
      'Torque wrench capable of 20 ft-lbs (27 Nm)',
    ],
    parts: [
      {
        id: 'master-cylinder',
        name: 'Brake Master Cylinder',
        partNumber: 'Mopar 5093270AA — equivalents: Cardone 10-3245 (reman) / Centric 130.66022 / Raybestos MC390485',
        quantity: 1,
        price: 65,
        category: 'required',
        notes: 'Confirm ABS vs non-ABS application — the body and ports differ. Match the part number to your VIN-specific application. Reman units are reliable and cost-effective; new units are slightly more expensive.',
      },
      {
        id: 'brake-fluid-mc',
        name: 'DOT 3 Brake Fluid (32 oz minimum)',
        partNumber: 'Mopar 4318080 / Valvoline VV632 / Prestone AS400',
        quantity: 2,
        price: 8,
        category: 'required',
        notes: 'Buy two 12-16 oz bottles. The full bleed will use 24-32 oz. Always use fresh fluid from a sealed bottle.',
      },
      {
        id: 'mc-mounting-gasket',
        name: 'Master Cylinder Mounting Gasket / O-ring (if equipped)',
        partNumber: 'Mopar 4882566 — typically included with new unit',
        quantity: 1,
        price: 3,
        category: 'conditional',
        notes: 'Check whether the new master cylinder includes the booster-side gasket. If not, replace it — a torn gasket allows booster vacuum to bypass.',
      },
    ],
    safetyInformation:
      'Brakes are safety-critical. After installation you must verify pedal feel and stopping power in an empty parking lot BEFORE driving in traffic. Brake fluid is corrosive to paint — protect bodywork with rags or plastic sheeting and wear eye protection. Used brake fluid absorbs water and is hazardous waste — dispose at an auto-parts recycling center. Bench-bleed the new master cylinder before installing — failing to do so makes the system bleed nearly impossible. Never reuse brake fluid that has come out of the system. Never substitute DOT 5 (silicone) for DOT 3 — it is not compatible with the TJ\'s ABS system.',
    beforeYouStart: [
      'Park on level ground with engine off and key out of ignition',
      'Read the new master cylinder\'s instructions for bench-bleed procedure — methods vary by manufacturer',
      'Stage 32+ oz of fresh brake fluid, the bench bleed kit, and a vise before disassembling',
      'Cover the front fender with old towels and plastic — brake fluid removes paint',
      'Have a helper or a one-person bleeder kit ready for the system bleed afterward',
      'Take a photo of brake-line routing and reservoir labeling before disconnecting',
    ],
    steps: [
      {
        number: 1,
        title: 'Empty the Old Reservoir',
        description:
          'Open the master cylinder reservoir cap. Use a turkey baster or fluid extractor to remove as much old brake fluid as possible from the reservoir. Discard the old fluid into a sealed container for hazardous waste disposal — do not pour down a drain.',
        why: 'Removing fluid first reduces spillage during line disconnection.',
        tip: 'Old fluid that comes out very dark or with sediment confirms the master needed replacement and the system needs a full flush.',
        commonMistake: 'Skipping this step. Disconnecting lines with a full reservoir creates a much larger spill.',
      },
      {
        number: 2,
        title: 'Identify and Photograph the Brake Lines',
        description:
          'The master cylinder has two brake lines (front circuit and rear circuit). Trace each one back to its source and identify which goes where. Take photos. The lines may have different fitting sizes — typically the front-circuit line is one size and the rear-circuit line is the other. Note: ABS-equipped TJs have an ABS module between master cylinder and wheel circuits — the master to ABS lines may have specific routing.',
        why: 'Brake lines must reconnect to their original ports on the new master cylinder. Crossing them inverts the brake distribution.',
        tip: 'Tape paper labels onto each line — "FRONT" and "REAR" — so they cannot be confused during install.',
        commonMistake: 'Crossing the lines. Causes wrong-side ABS activation and dangerous braking imbalance.',
      },
      {
        number: 3,
        title: 'Disconnect Brake Lines',
        description:
          'Place a drain pan under the master cylinder. Use a flare-nut wrench (NOT a regular open-end wrench) on the brake line fittings to avoid rounding them off. Loosen each fitting slowly — fluid will dribble out. Once cracked, unthread each line by hand and pull the line away from the master cylinder body. Cap or plug each line with a vinyl cap or plastic bag to prevent fluid loss.',
        why: 'Flare-nut wrenches grip 5 sides of the fitting; open-end wrenches grip only 2 and round off the soft brass quickly.',
        tip: 'A small touch of penetrating oil on the fitting threads (NOT on the seat) can ease a stuck nut. Avoid getting oil into the brake circuit.',
        commonMistake: 'Using an open-end wrench. Rounded fittings often require replacement of the whole brake line.',
      },
      {
        number: 4,
        title: 'Remove the Master Cylinder from the Booster',
        description:
          'Remove the two 13mm nuts that hold the master cylinder to the brake booster. Pull the master cylinder straight forward off the booster studs — do not tilt or pry. Inspect the booster pushrod for grease and free movement. Inspect the booster-side gasket — if torn, replace it.',
        why: 'The two studs are the only mounting; once nuts are off, the master comes free.',
        tip: 'Check the booster vacuum hose at this point — a cracked hose causes hard pedal and can be replaced cheaply while access is good.',
        commonMistake: 'Forgetting to disconnect the brake-warning lamp wire (if equipped). The wire stays attached to the master and breaks if the unit is pulled forward without disconnecting.',
      },
      {
        number: 5,
        title: 'Bench-Bleed the New Master Cylinder',
        description:
          'Clamp the new master cylinder horizontally in a vise (use rubber jaw inserts to avoid crushing the body). Install the bench-bleed kit: short clear hoses from each output port that loop back into the reservoir (above the fluid level on supply, below on return). Fill the reservoir with fresh DOT 3. Slowly press the master cylinder pushrod (use a long screwdriver or wood dowel) and hold for 5 seconds. Release slowly. Watch the bleed-kit hoses for air bubbles. Repeat 10-15 strokes until no bubbles emerge from either port. Cap both output ports with the included plugs to keep the unit primed during transfer to the car.',
        why: 'Internal air in a master cylinder is nearly impossible to remove from the brake circuit once installed. Bench bleeding gets all air out before the unit goes on the car.',
        tip: 'Some bench-bleed kits use threaded plugs that you tighten and loosen with each stroke instead of looped hoses — read your kit\'s instructions.',
        commonMistake: 'Skipping bench bleeding. The system bleed becomes a multi-hour ordeal and the pedal often remains soft.',
      },
      {
        number: 6,
        title: 'Install the New Master Cylinder',
        description:
          'Confirm the booster-side gasket is in place. Lift the bench-bled master cylinder onto the booster studs (keep the output ports plugged until the lines are ready). Hand-thread the two 13mm nuts. Torque to 17-21 ft-lbs (23-28 Nm). Verify torque spec 17-21 ft-lbs from OEM service manual.',
        why: 'Hand-threading prevents cross-threading the booster studs.',
        tip: 'The brake booster pushrod should engage cleanly into the master cylinder primary piston during installation — a slight wiggle helps it center.',
        commonMistake: 'Over-torquing the mounting nuts. Cracks the master cylinder mounting flange.',
      },
      {
        number: 7,
        title: 'Reconnect Brake Lines to Correct Ports',
        description:
          'Remove the port plugs one line at a time. Hand-thread each brake-line fitting into its correct port (front line → front port, rear line → rear port — verify against your photos and labels). Hand-tight at first to confirm no cross-threading, then snug with the flare-nut wrench. Torque to 12-15 ft-lbs (16-20 Nm). Verify torque spec from OEM service manual.',
        why: 'Lines must go to the correct port. The brass fittings are soft — over-torquing distorts them and they leak.',
        tip: 'Going one line at a time keeps the master cylinder primed — only one port is open at a time.',
        commonMistake: 'Over-torquing brass fittings. Distorts the seat and causes a leak that no amount of re-torque will fix.',
      },
      {
        number: 8,
        title: 'System Bleed (Four Corners)',
        description:
          'Top off the reservoir with fresh DOT 3 fluid. Bleed the brakes in the standard sequence: passenger rear, driver rear, passenger front, driver front (farthest to nearest from the master cylinder). At each corner, attach the bleeder hose, have a helper press and hold the pedal, crack the bleeder until fluid flows clear and air-free, close the bleeder before pedal release, repeat 4-6 times. Top off the reservoir between corners — never let it run dry.',
        why: 'Even with a perfect bench bleed, the brake lines themselves may have air. Full system bleed clears all air and confirms hydraulic integrity.',
        tip: 'A one-person check-valve bleeder bottle works well solo. Some TJs with ABS may require an additional scan-tool ABS bleed if the pedal stays soft after standard bleed.',
        commonMistake: 'Bleeding in the wrong sequence. Air migrates back into the master if the closer wheels are bled first.',
      },
      {
        number: 9,
        title: 'Verify Pedal and Static Test',
        description:
          'With engine off, pump the brake pedal slowly 5 times. The pedal should firm up and hold steady at the same height (no slow sink). Hold the pedal under firm pressure for 30 seconds — if it sinks to the floor, there is an internal leak (master cylinder defect or remaining air). Start the engine and pump again — power assist should give a slightly lower but firm pedal that does not creep.',
        why: 'A pedal that holds at the same height for 30 seconds confirms the master cylinder is sealing internally and there are no leaks anywhere in the system.',
        tip: 'A digital phone timer makes the 30-second test exact — anything less than 30 seconds of stable pedal is suspect.',
        commonMistake: 'Skipping the static hold test. A new master cylinder can be defective out of the box; this test catches it.',
      },
      {
        number: 10,
        title: 'Test Drive in Empty Lot, Then Traffic',
        description:
          'In an empty parking lot, do 5-10 firm stops from 25 mph to 5 mph. The pedal should feel firm, even side-to-side, with no pull or pulsation. Drive 5 miles at moderate speed without heavy braking, then check the master cylinder area and all four bleeders for any seepage. Only after this verification should you drive in normal traffic.',
        why: 'A bad master cylinder install is dangerous in traffic. The empty-lot test catches problems before they become emergencies.',
        tip: 'Recheck the reservoir level after the test drive; some settling and topping-up is normal.',
        commonMistake: 'Driving in traffic immediately. If pedal degrades, you may not have room to recover.',
      },
    ],
    torqueSpecifications: [
      {
        fastener: 'Master cylinder-to-booster mounting nuts (2)',
        specification: '17-21 ft-lbs (23-28 Nm). Verify torque spec 17-21 ft-lbs (23-28 Nm) from OEM service manual.',
        notes: 'Hand-thread both nuts before applying torque to avoid cross-threading the booster studs.',
      },
      {
        fastener: 'Brake line fittings at master cylinder',
        specification: '12-15 ft-lbs (16-20 Nm). Verify torque spec 12-15 ft-lbs (16-20 Nm) from OEM service manual.',
        notes: 'Use a flare-nut wrench. Brass fittings — do not over-torque.',
      },
      {
        fastener: 'Bleeder screws (caliper / wheel cylinder)',
        specification: '7-9 ft-lbs (10-12 Nm). Verify torque spec from OEM service manual.',
        notes: 'Snug only.',
      },
    ],
    fluidSpecifications: [
      {
        fluid: 'Brake fluid',
        type: 'DOT 3 (DOT 4 acceptable; DO NOT use DOT 5 silicone)',
        capacity: 'Full system flush: ~32 oz. Plan for 24-32 oz of fresh fluid.',
        notes: 'Always use fluid from a sealed bottle. Used brake fluid is hazardous waste.',
      },
    ],
    troubleshooting: [
      {
        problem: 'Pedal sinks to the floor under sustained pressure',
        likelyCause: 'Internal leak in the new master cylinder, air remaining in the system, or defective unit',
        whatToCheck: 'Repeat full bleed; if pedal still sinks, the new master is likely defective (warranty exchange)',
        fix: 'Bleed all four corners again following correct sequence. If pedal still sinks after a complete bleed with bench-bled master, replace the master cylinder under warranty.',
      },
      {
        problem: 'Pedal is firm but very low (close to floor)',
        likelyCause: 'Drum brakes out of adjustment, or air in rear circuit',
        whatToCheck: 'Verify rear drum brakes are adjusted (do reverse stops); re-bleed rear circuit',
        fix: 'Adjust rear drums via reverse-stop routine; re-bleed if needed. If pedal is still low, suspect a stuck-open ABS valve (requires shop scan-tool).',
      },
      {
        problem: 'Brake fluid leaking at master cylinder fitting',
        likelyCause: 'Cross-threaded fitting, distorted seat, or under-torqued',
        whatToCheck: 'Visually inspect for fluid emerging at the fitting; carefully re-torque',
        fix: 'Disconnect, inspect threads on both sides, and reseat carefully. If the seat is distorted, the brake line may need a new flare or replacement.',
      },
      {
        problem: 'Brake warning lamp on after install',
        likelyCause: 'Brake-warning sensor wire disconnected or pressure differential between front/rear circuits',
        whatToCheck: 'Confirm warning-lamp wire is reconnected; bleed both circuits to equalize pressure',
        fix: 'Reconnect wire. Bleed both circuits fully and recenter the pressure differential valve (some TJs require pressing the brake pedal hard to recenter the valve).',
      },
      {
        problem: 'Pedal feels spongy even after multiple bleeds',
        likelyCause: 'Air trapped in ABS module (TJs with ABS) or in a high point of the brake line',
        whatToCheck: 'Tap the master cylinder body and brake lines while bleeding to dislodge bubbles',
        fix: 'For ABS-equipped TJs, scan-tool ABS bleed at a shop may be required. Some independent garages have the right tool for $30-50.',
      },
    ],
    proTips: [
      'Bench bleeding is non-negotiable — even if the new unit comes with the bleeder caps installed, do the bench bleed yourself to confirm the seal.',
      'A $10 one-person check-valve bleeder bottle pays for itself the first time you use it. Brake bleeding solo becomes a 30-minute job, not 2 hours with a helper.',
      'When you do this job, also flush brake fluid from the entire system — you are bleeding it all anyway. Fresh fluid every 2-3 years extends caliper and master cylinder life.',
      'Photograph the brake-line layout before disassembly. The two lines look similar and crossing them is a real risk.',
      'Buy a flare-nut wrench set if you do not own one — they prevent rounded fittings and cost less than $20.',
    ],
    commonMistakes: [
      {
        mistake: 'Skipping the bench bleed',
        consequence: 'System bleed becomes nearly impossible. Pedal stays soft and air refuses to clear.',
      },
      {
        mistake: 'Using an open-end wrench on brake-line fittings',
        consequence: 'Rounded fitting requires whole-line replacement. Adds hours and parts.',
      },
      {
        mistake: 'Crossing the brake lines (front/rear)',
        consequence: 'Brake distribution is reversed; ABS may behave erratically. Dangerous in panic stops.',
      },
      {
        mistake: 'Letting the reservoir run dry during bleed',
        consequence: 'Air enters the master cylinder. Bleeding must restart and may require re-bench-bleeding.',
      },
    ],
    successIndicators: [
      'Pedal holds firm at the same height under 30-second sustained pressure (no sink)',
      'Pedal is firm and high during normal driving with no slow degradation',
      'No leaks at the master cylinder, brake lines, or bleeders after 100 miles',
      'Even braking left-to-right with no pull',
      'Brake warning lamp off; ABS lamp off (if equipped)',
    ],
    whenToSeekHelp:
      'If after a complete bleed and bench-bled master the pedal still sinks under sustained pressure, the new master is likely defective — warranty exchange and re-bleed. If pedal is spongy and bleeding does not improve it on an ABS-equipped TJ, an ABS scan-tool bleed at a shop may be required. If the brake-line fittings or seats are damaged during removal, a shop with a flaring tool can reflare or replace the line. If the brake warning lamp will not extinguish after pressure-differential recentering, the proportioning valve or warning sensor itself may need attention.',
    legalDisclaimer: 'This guide was generated by GarageIQ\'s AI from publicly available sources. Some information may be incorrect or out of date. Verify torque specifications and any safety-critical procedures against the OEM service manual for your vehicle before performing work. Brake service is safety-critical — if anything is unclear, stop and consult a professional. The Haynes Manual tab on this page links to the licensed manual for your vehicle. GarageIQ is not a substitute for a qualified mechanic. By using this guide you agree to GarageIQ\'s Terms of Service and assume responsibility for the work performed.',
    manualSource: {
      title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)',
      url: 'https://www.haynes.com/en-us/wrangler/jeep/2003',
      notes: 'Procedure sourced from public catalogs and forums; cross-reference torques and ABS bleed requirements against your licensed manual before service.',
    },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — BRAKE HOSE / LINE REPLACEMENT — engine-agnostic
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-brake-hose-line-replacement',
    title: 'Brake Hose & Line Replacement',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [{ startYear: 1997, endYear: 2006 }],
    },
    video: {
      youtubeId: 'R1Ee5syPs4U',
      title: 'How to Replace Front Brake Hoses 1997-2006 Jeep Wrangler',
      channel: 'TRQ',
    },
    source: 'Generated by GarageIQ AI from publicly available sources (Mopar parts catalog, Crown Automotive and Russell Performance hose catalogs, Centric and Raybestos service literature, NHTSA TSBs, owner\'s manual, technician forums and Wrangler community service threads). Cross-checked and structured per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Brakes & Steering',
    difficulty: 'intermediate',
    duration: '1 - 2 hours per hose; longer for hard-line repair',
    interval: 'Inspect annually for cracking, swelling, or corrosion. Replace any hose with surface cracks, bulges, fluid weeping, or chafing damage. Hard lines: replace any section with deep corrosion, kinks, or leak.',
    description:
      'Brake hoses (flexible) connect the hard lines on the chassis to the moving suspension components — there are three on a TJ (one to each front caliper, one to the rear axle T-junction). Hard lines (rigid metal) run along the chassis and rust is the most common failure on a TJ in any salt-belt state. Old hoses crack on the outside, swell on the inside (acting like a one-way valve and trapping a brake on), and weep at the fittings. This guide covers both flexible-hose replacement (a routine job) and hard-line repair via flaring or pre-flared replacement sections.',
    overview: {
      difficulty: 'Intermediate — flexible hoses are bolt-on; hard-line work is more involved and may require a flaring tool',
      estimatedTime: '1-2 hours per flexible hose; 2-4 hours for hard-line section',
      estimatedCost: '$15-30 per OEM-style hose; $40-80 per stainless-braided hose; $20-40 per pre-flared hard line section',
      whyMatters: 'A failed brake hose is one of the few brake problems that can cause sudden total loss of one circuit. Inspection is cheap and quick; replacement is typically under $30 in parts.',
      safetyNote: 'Brakes are safety-critical. Brake fluid removes paint instantly and is harmful to eyes — wear protection. After this job, pedal feel must be verified in an empty parking lot before driving in traffic.',
    },
    tools: [
      'Floor jack and jack stands',
      'Lug wrench / 19mm socket',
      'Flare-nut wrench set (10mm, 11mm, 12mm — typical brake-line sizes)',
      'Standard wrenches (14mm, 17mm) for hose-bracket nuts',
      '15mm or 17mm wrench for banjo bolts',
      'Tube cutter (for hard line work)',
      'Double flaring tool kit (for hard line work) OR pre-flared replacement sections',
      'Brake line bender (optional but very helpful for hard lines)',
      'Brake bleeder bottle and 1/4" clear hose (or one-person bleeder kit)',
      'Drain pan',
      'Brake cleaner',
      'Disposable nitrile gloves and eye protection',
      'Old towels and plastic sheeting',
      'Torque wrench',
    ],
    parts: [
      {
        id: 'front-brake-hose',
        name: 'Front Brake Hose (each side)',
        partNumber: 'Crown 52008402 / 52008403 — equivalents: Centric 150.66032 / Russell stainless braided 672490',
        quantity: 2,
        price: 18,
        category: 'conditional',
        notes: 'Replace in pairs ideally — both fronts are the same age. Stainless-braided hoses are an upgrade ($40-60 each) and resist swelling but are firmer in feel.',
      },
      {
        id: 'rear-brake-hose',
        name: 'Rear Brake Hose (T-block to axle)',
        partNumber: 'Crown 52008404 — equivalent: Centric 150.66033',
        quantity: 1,
        price: 18,
        category: 'conditional',
        notes: 'Single hose feeds the rear T-junction; replacement is a common job after off-road damage or age cracking.',
      },
      {
        id: 'brake-hard-line',
        name: 'Pre-Flared Brake Line Section',
        partNumber: 'Edelmann Steel pre-flared 3/16" lines (cut to length); or copper-nickel flexible-bend tubing',
        quantity: 1,
        price: 25,
        category: 'conditional',
        notes: 'Buy pre-flared sections in standard lengths (12", 24", 36", 48", 60") with fittings included, or buy bulk copper-nickel tubing and flare yourself. Copper-nickel bends easily and resists corrosion.',
      },
      {
        id: 'brake-fluid-hose',
        name: 'DOT 3 Brake Fluid (16 oz minimum)',
        partNumber: 'Mopar 4318080 / Valvoline VV632 / Prestone AS400',
        quantity: 1,
        price: 8,
        category: 'required',
        notes: 'Plan for ~16 oz per hose replaced including bleed. Always use fresh fluid from a sealed bottle.',
      },
      {
        id: 'crush-washers-hose',
        name: 'Banjo Bolt Crush Washers (2 per banjo connection)',
        partNumber: 'Mopar 6505532AA — generic copper M10 crush washers',
        quantity: 4,
        price: 2,
        category: 'conditional',
        notes: 'Required only for banjo-style hose connections. Inverted-flare connections do not need washers.',
      },
    ],
    safetyInformation:
      'Brake fluid is corrosive to paint and harmful to eyes — wear eye protection and protect bodywork. Never reuse banjo crush washers — they are sealing gaskets and reusing them causes leaks. After install, you must verify pedal feel in an empty parking lot before driving in traffic. Do not bend brake lines sharply by hand — use a brake-line bender to avoid kinks. Used brake fluid is hazardous waste — dispose at an auto-parts recycling center. Brake-line fittings are soft brass — use flare-nut wrenches to avoid rounding. If you cannot get a flare nut to break loose, do NOT round it off — apply heat or penetrating oil and try again, or have a shop replace the line.',
    beforeYouStart: [
      'Park on level ground; loosen lug nuts before lifting',
      'Stage all parts: new hose(s), fresh brake fluid, new crush washers (if using banjo connections), and bleeder kit',
      'Cover the area below the hose with old towels — fluid will drip',
      'Identify which fittings are flare-nut vs banjo and confirm wrench sizes',
      'Have a helper or a one-person bleeder kit ready for the post-install bleed',
      'For hard-line work, decide whether to flare or use pre-flared sections — pre-flared is much faster for a one-time repair',
    ],
    steps: [
      {
        number: 1,
        title: 'Raise the Vehicle and Locate the Hose',
        description:
          'Raise the front (or rear) of the vehicle and support on jack stands placed under the frame rails. Remove the wheel for the hose you are replacing. Trace the hose from its caliper or T-junction end to its chassis bracket end so you understand the routing before disassembly.',
        why: 'Understanding the routing before disassembly prevents kinking the new hose during install.',
        tip: 'Take photos of the hose at various angles, especially where it bends near the bracket.',
        commonMistake: 'Skipping the routing study. Reassembly with a twist or kink can chafe the new hose and cause early failure.',
      },
      {
        number: 2,
        title: 'Disconnect the Hose at the Caliper / Wheel End',
        description:
          'Place a drain pan under the connection. For a banjo bolt: hold the hose with one wrench and turn the banjo bolt counterclockwise with another. For an inverted-flare fitting: use a flare-nut wrench. Cap the hose end immediately to prevent fluid loss. Discard old crush washers if it was a banjo connection.',
        why: 'Disconnecting the wheel end first lets the hose drain into the pan rather than down your arm.',
        tip: 'A vinyl cap or a piece of plastic bag tied with a wire works well to cap the hose end.',
        commonMistake: 'Reusing crush washers. This is the #1 cause of post-install leaks at banjo connections.',
      },
      {
        number: 3,
        title: 'Disconnect the Hose at the Chassis (Hard-Line) End',
        description:
          'The chassis end of the flexible hose is held in a bracket on the frame, then connects to the hard line via an inverted-flare nut. Use the flare-nut wrench to crack the connection — this is often the harder of the two ends because it has been undisturbed for years. After cracking, remove the U-clip or bracket clip that holds the hose in the frame, then unthread the line nut from the hard line. Pull the hose free.',
        why: 'The U-clip or bracket clip prevents the hose from rotating when you torque the line nut. Removing it lets you separate the hose from the bracket.',
        tip: 'A wire brush on the bracket area before disassembly removes years of road-grime corrosion and makes the U-clip easier to remove.',
        commonMistake: 'Forgetting the U-clip. Trying to unthread the line nut without removing the clip first will twist the hose and damage it.',
      },
      {
        number: 4,
        title: '[Hard Line Repair Only] Cut and Flare or Splice',
        description:
          'IF you are repairing a damaged hard line section: use a tube cutter to cut the line cleanly back to undamaged metal on each side of the failure. Slide a new compression union or flare-fitting onto the line. Use a double-flaring tool to create a 45-degree double flare on each cut end (follow the tool\'s instructions exactly — the flare must seal against the fitting seat). For a quicker repair, use pre-flared sections with the correct fittings already installed. Bend the new section to match the old routing using a brake-line bender (do not bend by hand — kinks restrict flow).',
        why: 'A proper double flare seals against the brake-line fitting seat. A poor flare leaks under brake-circuit pressure (1,000+ psi).',
        tip: 'Practice the flaring procedure on a scrap piece of line before doing the actual repair — flaring tools can be unforgiving on the first try.',
        commonMistake: 'Single-flaring instead of double-flaring. Single flares fail under brake pressure.',
      },
      {
        number: 5,
        title: 'Install the New Hose at the Chassis End',
        description:
          'Hand-thread the line nut into the hard line for at least 3 turns to confirm no cross-threading. Position the hose into the chassis bracket. Reinstall the U-clip or bracket clip — this stops the hose from rotating during torque. Now torque the line nut to 11-13 ft-lbs (15-18 Nm). Verify torque spec 11-13 ft-lbs from OEM service manual.',
        why: 'The U-clip prevents the hose from twisting as you torque the line nut. Without it, the hose end rotates and the soft brass flare seat can be damaged.',
        tip: 'Sight along the hose at this point — it should hang naturally with no visible twist or bend that was not in the original routing.',
        commonMistake: 'Torquing without the U-clip in place. The hose twists and the seat is damaged before final torque is reached.',
      },
      {
        number: 6,
        title: 'Install the New Hose at the Wheel End',
        description:
          'For a banjo connection: place a NEW crush washer on each side of the banjo fitting (two washers total). Hand-thread the banjo bolt into the caliper or wheel cylinder. Sight to confirm the hose is at the correct angle and is not twisted. Torque the banjo bolt to 25-30 ft-lbs (34-41 Nm). Verify torque spec from OEM service manual. For an inverted-flare connection: hand-thread the line nut, sight to confirm no twist, and torque to 11-13 ft-lbs.',
        why: 'The banjo angle is held only by the crush-washer compression — getting it right at install means no leaks and no chafe.',
        tip: 'A small dab of clean brake fluid on the crush washers helps them seat evenly during torque.',
        commonMistake: 'Twisting the hose during torque. The hose will eventually crack at the twist point.',
      },
      {
        number: 7,
        title: 'Verify Routing and Suspension Travel',
        description:
          'Cycle the suspension through its full travel — turn the steering lock-to-lock, push down on the corner, lift the corner. Watch the new hose for any tight spots, kinks, contact with metal parts (control arms, brackets), or excessive stretch. The hose must have free movement at all suspension positions.',
        why: 'A hose that contacts a moving component will chafe through and fail. A hose that is too short or too taut will stretch and tear.',
        tip: 'Mark any contact spots with a paint pen and reroute through the bracket if needed before bleeding.',
        commonMistake: 'Skipping the suspension cycle test. Contact damage shows up after a few months and the job has to be redone.',
      },
      {
        number: 8,
        title: 'Bleed the Affected Circuit',
        description:
          'Top off the master cylinder reservoir with fresh DOT 3. Have a helper press and hold the brake pedal while you crack the bleeder screw on the affected wheel. Close the bleeder before the pedal releases. Repeat 4-6 times until clear, air-free fluid flows. For a front hose, bleed only that wheel. For the rear hose to the T-junction, bleed both rear wheels (passenger first, then driver).',
        why: 'Replacing a hose introduces air into the line. Without bleeding, the pedal will be soft.',
        tip: 'A one-person bleeder bottle works well for single-wheel bleeds — quicker than recruiting a helper.',
        commonMistake: 'Letting the reservoir run dry. Air enters the master cylinder and the bleed must restart.',
      },
      {
        number: 9,
        title: 'Verify Pedal and Static Test',
        description:
          'With engine off, pump the pedal 5 times — it should firm up to a normal feel. Hold under firm pressure for 30 seconds; the pedal should not sink. Inspect the new hose connections at both ends for any seepage. Start the engine and pump again — pedal should be slightly lower (power assist) but still firm.',
        why: 'The 30-second hold test confirms no leaks anywhere in the circuit and no air remaining.',
        tip: 'A flashlight at each fitting during the static test reveals seepage immediately.',
        commonMistake: 'Skipping the static test. A slow seep at a banjo or line nut may not show until you are 100 miles down the road.',
      },
      {
        number: 10,
        title: 'Reinstall Wheel and Test Drive',
        description:
          'Reinstall the wheel and torque lug nuts in a star pattern to 95 ft-lbs (129 Nm). Verify torque spec 95 ft-lbs from OEM service manual. Lower the vehicle. In an empty parking lot, do 5-10 firm stops from 25 mph. Pedal should feel firm and even. Recheck connections for any seepage after the test.',
        why: 'Empty-lot test confirms braking authority is restored before driving in traffic.',
        tip: 'Drive 50-100 miles before doing a final inspection — slow seeps sometimes only show after vibration and heat cycles.',
        commonMistake: 'Driving in traffic before parking-lot verification. If a connection leaks under braking, you may not have room to recover.',
      },
    ],
    torqueSpecifications: [
      {
        fastener: 'Brake hose-to-hard-line nut (chassis end)',
        specification: '11-13 ft-lbs (15-18 Nm). Verify torque spec 11-13 ft-lbs from OEM service manual.',
        notes: 'U-clip must be installed FIRST to prevent hose rotation during torque.',
      },
      {
        fastener: 'Banjo bolt at caliper / wheel end',
        specification: '25-30 ft-lbs (34-41 Nm). Verify torque spec 25-30 ft-lbs (34-41 Nm) from OEM service manual.',
        notes: 'New crush washers required on both sides of the banjo fitting.',
      },
      {
        fastener: 'Inverted flare nut at hard-line connections',
        specification: '11-13 ft-lbs (15-18 Nm). Verify torque spec from OEM service manual.',
        notes: 'Use a flare-nut wrench to avoid rounding the soft brass.',
      },
      {
        fastener: 'Wheel lug nuts',
        specification: '95 ft-lbs (129 Nm). Verify torque spec 95 ft-lbs from OEM service manual.',
        notes: 'Star pattern; final torque with calibrated wrench.',
      },
    ],
    fluidSpecifications: [
      {
        fluid: 'Brake fluid',
        type: 'DOT 3 (DOT 4 acceptable; DO NOT use DOT 5 silicone)',
        capacity: 'Approximately 8-16 oz per hose for bleed; full system flush is ~32 oz',
        notes: 'Always use fluid from a sealed bottle.',
      },
    ],
    troubleshooting: [
      {
        problem: 'Banjo connection leaks after install',
        likelyCause: 'Reused crush washers, banjo bolt under-torqued, or twisted hose causing seat misalignment',
        whatToCheck: 'Visually confirm new washers were used; re-torque banjo bolt to spec; sight hose for twist',
        fix: 'Disassemble, replace both crush washers, retorque to 25-30 ft-lbs. Confirm hose has no twist.',
      },
      {
        problem: 'Inverted-flare connection leaks',
        likelyCause: 'Cross-threaded fitting, distorted seat, or under-torqued',
        whatToCheck: 'Inspect fitting threads on both sides; check for damaged flare seat',
        fix: 'If threads are clean, retorque carefully. If seat is damaged, the line section may need to be re-flared or replaced.',
      },
      {
        problem: 'Pedal stays soft after bleed',
        likelyCause: 'Air remaining in line; reservoir ran dry; ABS module needs scan-tool bleed',
        whatToCheck: 'Repeat bleed sequence; verify reservoir was kept full; check for ABS warning lamp',
        fix: 'Re-bleed the affected wheel(s) thoroughly. If pedal stays soft on ABS-equipped TJ, scan-tool ABS bleed at a shop may be required.',
      },
      {
        problem: 'New hose chafes against suspension component',
        likelyCause: 'Wrong-application hose (too long or too short), wrong routing, or U-clip not installed',
        whatToCheck: 'Cycle the suspension and watch the hose; compare hose length to OEM',
        fix: 'Reroute the hose. If hose is wrong-application, replace with correct part.',
      },
      {
        problem: 'Hard line crumbles when fitting is loosened',
        likelyCause: 'Severe internal corrosion (common on TJs in salt-belt states)',
        whatToCheck: 'Inspect more of the line for similar corrosion',
        fix: 'Cut back to clean metal, flare or splice in a new section. Consider replacing the entire line section if multiple failure points.',
      },
    ],
    proTips: [
      'On any TJ in a salt-belt state, plan to inspect ALL brake hoses every 2-3 years — they are cheap and replacing them on a schedule is far cheaper than dealing with a sudden failure.',
      'Stainless-braided hoses are a worthwhile upgrade if you do hard off-road or autocross — they do not swell under hard braking, which gives a slightly firmer pedal.',
      'Copper-nickel brake-line tubing (Cunifer) is the best material for hard-line repair — bends easily by hand, resists corrosion, and seals well in flares. Worth the small premium over plain steel.',
      'A Mityvac or check-valve bleeder bottle makes single-wheel bleeds a 5-minute job. Worth owning.',
      'Always replace the U-clip if it is corroded — they are pennies and a missing clip leads to twisted hoses.',
    ],
    commonMistakes: [
      {
        mistake: 'Reusing crush washers at banjo connections',
        consequence: 'Slow leak develops within hundreds of miles. Brake fluid drips onto rotor and contaminates pads.',
      },
      {
        mistake: 'Single-flaring a brake hard line instead of double-flaring',
        consequence: 'Flare fails under brake pressure. Total loss of one circuit.',
      },
      {
        mistake: 'Twisting the hose during install',
        consequence: 'Hose cracks at the twist point within months and fails.',
      },
      {
        mistake: 'Using an open-end wrench on flare fittings',
        consequence: 'Rounded fitting; whole-line replacement required.',
      },
    ],
    successIndicators: [
      'Pedal firm and high, holds steady under 30-second sustained pressure (no sink)',
      'No fluid weeping at any connection after 100 miles of driving',
      'New hose has free movement through full suspension travel with no chafe points',
      'Even braking with no pull, no spongy feel',
      'No brake warning lamp; ABS lamp off (if equipped)',
    ],
    whenToSeekHelp:
      'If you encounter brake lines that crumble when you touch them, this often means major sections need replacement and a shop with a flaring tool and lift access may be the better choice. If a flare fitting will not seal even after re-flaring, the hard line fitting itself may be damaged — replacement of the line section is required. If pedal stays soft after a thorough bleed on an ABS-equipped TJ, an ABS scan-tool bleed at a shop may be needed. If you damage hard-line threads in a caliper or master cylinder body, that component may need replacement.',
    legalDisclaimer: 'This guide was generated by GarageIQ\'s AI from publicly available sources. Some information may be incorrect or out of date. Verify torque specifications and any safety-critical procedures against the OEM service manual for your vehicle before performing work. Brake service is safety-critical — if anything is unclear, stop and consult a professional. The Haynes Manual tab on this page links to the licensed manual for your vehicle. GarageIQ is not a substitute for a qualified mechanic. By using this guide you agree to GarageIQ\'s Terms of Service and assume responsibility for the work performed.',
    manualSource: {
      title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)',
      url: 'https://www.haynes.com/en-us/wrangler/jeep/2003',
      notes: 'Procedure sourced from public catalogs and forums; cross-reference torques and any application-specific hose part numbers against your licensed manual.',
    },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — TRACK BAR REPLACEMENT (Front Panhard) — engine-agnostic
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-track-bar-replacement',
    title: 'Front Track Bar (Panhard Rod) Replacement',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [{ startYear: 1997, endYear: 2006 }],
    },
    video: {
      youtubeId: 'kfTwFua9JPI',
      title: 'Jeep Wrangler TJ/LJ - Got Death Wobble? Change the Front Track Bar',
      channel: 'Power Addicts - FixJeeps',
    },
    source: 'Generated by GarageIQ AI from publicly available sources (Mopar parts catalog, Crown Automotive and Currie Enterprises track bar literature, NHTSA TSBs and Complaints database, owner\'s manual, technician forums and Wrangler community service threads). Cross-checked and structured per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Suspension & Steering',
    difficulty: 'intermediate',
    duration: '1 - 1.5 hours',
    interval: 'Inspect annually for bushing cracks, looseness, or visible play. Replace when bushings are torn, the bar shows lateral free play, or after a "death wobble" event traced to a worn track bar.',
    description:
      'The front track bar (also called a Panhard rod) is a critical TJ steering component — it prevents the front axle from shifting laterally relative to the chassis. The bar runs diagonally from the driver-side frame mount to the passenger-side axle bracket. It is the #1 component blamed for "death wobble" — the violent steering oscillation TJs are infamous for. Bushings wear out from hard off-road use and from the bar\'s constant articulation; the symptoms are loose steering, vague feel on-center, and (eventually) wobble at 45-65 mph after hitting a bump. This guide covers OEM-style and aftermarket track bar replacement with proper torque-at-ride-height technique.',
    overview: {
      difficulty: 'Intermediate — straightforward bolt-on, but the axle-side bolt is often seized and torque-at-ride-height is non-negotiable for bushing life',
      estimatedTime: '1 - 1.5 hours assuming both bolts come loose without a fight',
      estimatedCost: '$60-110 OEM-style replacement; $150-250 for upgraded heim-jointed aftermarket; vs $250-400 at a shop',
      whyMatters: 'A worn track bar is the most common single cause of death wobble on a TJ. It also creates wandering steering and clunks over bumps. Replacement restores precise steering and eliminates this entire failure mode.',
      safetyNote: 'Death wobble can be dangerous at highway speeds. If the vehicle has wobbled, do not drive in traffic until the cause is identified and fixed. Use rated jack stands. The track bar is under preload — do not stand directly under it during disassembly.',
    },
    tools: [
      'Floor jack and jack stands',
      '21mm socket and ratchet (track bar bolts — confirm size against your hardware)',
      '15mm or 18mm wrench (some aftermarket bars use different bolt sizes)',
      'Torque wrench capable of 125 ft-lbs (170 Nm)',
      'Pry bar (for aligning the axle to the chassis if shifted)',
      'PB Blaster or other penetrating oil',
      'Hammer (for breaking corrosion on the bolts)',
      'Wire brush',
      'Tape measure (to compare new vs old bar length)',
      'Disposable gloves',
    ],
    parts: [
      {
        id: 'track-bar-front',
        name: 'Front Track Bar (Panhard Rod)',
        partNumber: 'Mopar 52037793 — equivalents: Crown 52037793 / Currie CE-9905 / TeraFlex 1843070 (heim-jointed adjustable)',
        quantity: 1,
        price: 90,
        category: 'required',
        notes: 'OEM-style replacement is fine for stock-height TJs. For lifted TJs (3"+), an adjustable heim-jointed bar is recommended — it allows centering the axle under the body after a lift. Confirm length matches your application.',
      },
      {
        id: 'track-bar-bolts',
        name: 'Track Bar Bolts (frame and axle ends)',
        partNumber: 'Mopar 6505400 (frame bolt) / 6505401 (axle bolt) — typically reused if not corroded',
        quantity: 2,
        price: 8,
        category: 'conditional',
        notes: 'Reuse the original bolts if they are clean and the threads are good. If corroded or seized during removal (cutting required), replace.',
      },
    ],
    safetyInformation:
      'Death wobble can cause loss of vehicle control at highway speed. Do not drive in traffic with a vehicle that has wobbled until the cause is identified and fixed (track bar is the most common cause but not the only one). Use rated jack stands on level ground — never work under a vehicle held only by a floor jack. The track bar holds the axle laterally; with the bar removed, the axle can shift if the suspension is unloaded — keep the wheels on the ground or support the axle. Final torque MUST be applied at ride height (wheels on ground or axle supported at full ride height) to prevent immediate bushing damage.',
    beforeYouStart: [
      'Park on level ground; loosen front lug nuts before lifting',
      'Note any history of death wobble or steering issues — these inform whether other components also need attention',
      'Apply penetrating oil to both track bar bolt heads 30-60 minutes BEFORE starting the job — these bolts are notorious for being seized',
      'Have a way to support the axle at ride height for final torque (drive-on ramps work; or jack stands under the axle while the front is also on stands)',
      'Compare the new bar length to the old before removing the old bar — they should match (or be adjustable to match for aftermarket)',
      'Have a helper ready to push the axle laterally if it has shifted from center',
    ],
    steps: [
      {
        number: 1,
        title: 'Position the Vehicle for Service',
        description:
          'Drive the vehicle onto two ramps or raise the front of the vehicle and place jack stands under the frame rails (with the axle still hanging by the suspension). For final torque you need the suspension at ride height — driving onto ramps is the easiest way. Chock the rear wheels.',
        why: 'Track bar bushings must be torqued at ride height — that\'s the position the suspension lives in 99% of the time. Torquing at full droop loads the bushings constantly and tears them within months.',
        tip: 'If you do not have ramps, jack the front up, set frame stands, then jack the axle back to ride height with a separate jack so you can torque against ride-height loading.',
        commonMistake: 'Working with the axle hanging at full droop. The track bar bushings will be torqued in the wrong position and tear quickly.',
      },
      {
        number: 2,
        title: 'Inspect the Old Track Bar in Place',
        description:
          'Have a helper rock the steering wheel side-to-side while you watch the track bar bushings. Any visible movement, twisting, or play in the bushings indicates wear. Inspect the bushings themselves for cracks, separation, or oil contamination. Note which end is more worn — usually the axle end fails first because it sees more articulation.',
        why: 'In-place inspection confirms the diagnosis before parts go on. It also tells you whether other steering components might need attention.',
        tip: 'Push and pull on the bar itself with the steering wheel held still — there should be zero perceptible movement at the bushings.',
        commonMistake: 'Replacing the bar without inspecting first. You may miss other worn components (drag link, tie rod ends) that also contribute to wobble.',
      },
      {
        number: 3,
        title: 'Wire-Brush the Bolt Heads',
        description:
          'Use a wire brush to clean rust and dirt from the bolt heads on both the frame end (driver side) and the axle end (passenger side bracket). Apply more penetrating oil to the cleaned area and let it work in for 5 more minutes.',
        why: 'Clean bolt heads let the socket seat fully — without this, you can round the head when applying high torque to break the bolt loose.',
        tip: 'A 6-point socket grips better than a 12-point on rusty fasteners — use 6-point if you have one.',
        commonMistake: 'Putting a 12-point socket on a corroded bolt and trying to break it loose. Rounded head requires drilling out.',
      },
      {
        number: 4,
        title: 'Remove the Frame-End Bolt First',
        description:
          'Position a wrench on the bolt head and a socket on the nut. Apply steady pressure to break the bolt loose. The frame-end bolt threads into a stamped bracket on the chassis and usually is the easier of the two. Once cracked, unthread the nut, but DO NOT pull the bolt all the way out yet — leave it threaded a few turns to keep the bar supported.',
        why: 'Removing the frame-end first leaves the axle-end attached, which keeps the heavy bar supported at the axle while you work on the harder of the two ends.',
        tip: 'A breaker bar gives more leverage than a ratchet — use it for the initial crack, then switch to a ratchet for unthreading.',
        commonMistake: 'Pulling both bolts before either is fully loose. The bar can drop or shift unexpectedly.',
      },
      {
        number: 5,
        title: 'Remove the Axle-End Bolt',
        description:
          'The axle-end bolt is held in a forged bracket on the passenger side of the front axle. This bolt is often more corroded and harder to remove. Apply steady torque; if it does not move, apply more penetrating oil, tap the bolt head with a hammer to shock the corrosion, and try again. As a last resort, heat the bracket area with a propane torch (NOT near brake lines or fluid). Once the nut is off, drive the bolt out with a brass drift if it is stuck in the bushing sleeve.',
        why: 'The axle-end sees more wash and salt; corrosion is heavier here. Patience and penetrating oil prevent breaking the bolt.',
        tip: 'If the bolt is rusted to the bushing sleeve and will not slide out, the entire bolt may need to be cut with a reciprocating saw between the bracket and the bushing.',
        commonMistake: 'Hammering hard on a stuck bolt. You can damage the axle bracket and create a much bigger problem.',
      },
      {
        number: 6,
        title: 'Pull the Frame-End Bolt and Remove the Bar',
        description:
          'Now remove the frame-end bolt fully and lift the old track bar out. Note the orientation — for an aftermarket bar, the longer end goes to the side that needs more length (usually the frame side on lifted TJs). Compare the new bar to the old, end-to-end, and adjust the new bar to match length if it is adjustable.',
        why: 'Length match (or adjustment to match) keeps the axle centered under the body after install. A bar that is 1/4" off can pull the axle to one side and create steering problems.',
        tip: 'Lay the old and new bars side-by-side on the floor and compare. For adjustable bars, set length BEFORE installing — much easier than adjusting in the bracket.',
        commonMistake: 'Installing without length-matching. The axle ends up off-center and steering geometry is compromised.',
      },
      {
        number: 7,
        title: 'Install the New Track Bar — Axle End First',
        description:
          'Slide the new bar into position. Insert the axle-end bolt through the bracket and the bushing sleeve. The bolt should pass through cleanly — if it binds, the bushing may not be seated. Hand-thread the nut on the back side. Snug only — final torque comes later.',
        why: 'Hand-snug only at this stage so the bushings can rotate to neutral position when ride-height torque is applied.',
        tip: 'A small dab of anti-seize on the bolt threads (NOT on the bushing sleeve) makes future removal easier.',
        commonMistake: 'Final-torquing now. The bushings will be loaded in the wrong position.',
      },
      {
        number: 8,
        title: 'Align the Axle and Install the Frame-End Bolt',
        description:
          'The frame-end of the bar must line up with the bracket hole. If the axle has shifted laterally (common after the old bar removed), you will need to push the axle to center. Use a pry bar between the axle and frame, or have a helper push the front of the vehicle sideways to align the holes. Once aligned, slide the frame-end bolt through and hand-thread the nut. Snug only.',
        why: 'Both ends must be hand-snug only before final torque. Forcing the bolt through misaligned holes can damage the bushing sleeve.',
        tip: 'If the holes are stubbornly misaligned, slightly loosen the axle-end bolt, push the axle, then re-snug — this can ease alignment.',
        commonMistake: 'Forcing the frame bolt through misaligned holes with a hammer. Bends the bolt or damages the bushing.',
      },
      {
        number: 9,
        title: 'Lower to Ride Height and Apply Final Torque',
        description:
          'Lower the vehicle off the jack stands so the wheels carry the suspension weight (or have the axle supported at ride height). With the suspension loaded to ride height, torque both track bar bolts: axle-end to 125 ft-lbs (170 Nm) and frame-end to 110 ft-lbs (149 Nm). Verify torque specs from OEM service manual — typical values 125 ft-lbs (axle) and 110 ft-lbs (frame).',
        why: 'Ride-height torque puts the bushings in their neutral position so they do not load when the suspension cycles. This is non-optional for bushing life.',
        tip: 'If you can\'t fit a torque wrench under the vehicle at ride height, raise the vehicle just enough to access the bolts but keep the axle supported on a jack at ride height. The key is the bushing position, not the vehicle position.',
        commonMistake: 'Torquing at full droop. Bushings tear within a few months.',
      },
      {
        number: 10,
        title: 'Test Drive and Verify No Wobble',
        description:
          'Drive a few miles at progressively higher speeds. Test on smooth pavement first, then over bumps. Steering should feel precise and centered with no clunks, no wandering, and no oscillation after hitting bumps. If a "death wobble" still occurs, the track bar may not be the only worn component — also inspect ball joints, tie rod ends, drag link, steering damper, and tire balance.',
        why: 'Test driving confirms the fix and surfaces any other contributing components.',
        tip: 'Recheck both bolts for tightness after 100 miles — settling can sometimes loosen new bushings slightly.',
        commonMistake: 'Driving at highway speed before low-speed verification. If wobble persists, you may not have room to recover.',
      },
    ],
    torqueSpecifications: [
      {
        fastener: 'Track bar axle-end bolt',
        specification: '125 ft-lbs (170 Nm). Verify torque spec 125 ft-lbs (170 Nm) from OEM service manual.',
        notes: 'Apply at ride height with suspension loaded. Critical for bushing life.',
      },
      {
        fastener: 'Track bar frame-end bolt',
        specification: '110 ft-lbs (149 Nm). Verify torque spec 110 ft-lbs (149 Nm) from OEM service manual.',
        notes: 'Apply at ride height with suspension loaded.',
      },
    ],
    fluidSpecifications: [],
    troubleshooting: [
      {
        problem: 'Death wobble persists after track bar replacement',
        likelyCause: 'Other worn components: ball joints, tie rod ends, drag link, steering damper, or unbalanced tires',
        whatToCheck: 'Have a helper rock the steering with the front off the ground — feel each joint for play. Check tire balance.',
        fix: 'Replace any joint with perceptible play. A worn steering damper masks but does not cure underlying wobble. Balance tires if pulsing.',
      },
      {
        problem: 'Steering off-center after install',
        likelyCause: 'Axle shifted laterally during install; new bar set to wrong length',
        whatToCheck: 'Measure from frame to wheel hub on each side — should be equal',
        fix: 'For adjustable bars, lengthen or shorten until axle is centered. For fixed-length bars, recheck the bar matches OEM length.',
      },
      {
        problem: 'Clunk or pop from front when turning or hitting bumps',
        likelyCause: 'Track bar bolts not at proper torque, or bushings worn quickly from droop-position torque',
        whatToCheck: 'Re-torque both bolts at ride height; inspect bushings for tearing',
        fix: 'Re-torque at ride height. If bushings are torn within months, the original install was at droop — replacement may be needed.',
      },
      {
        problem: 'Bolt seized in bushing sleeve during removal',
        likelyCause: 'Years of corrosion bonding the bolt to the sleeve',
        whatToCheck: 'After nut removal, try driving the bolt out with a brass drift; if it does not move, cut between bracket and bushing',
        fix: 'Cut the bolt with a reciprocating saw between the bracket and bushing. Replace bolt and bar.',
      },
      {
        problem: 'New bar contacts oil pan or crossmember',
        likelyCause: 'Wrong-application bar, lifted TJ with stock-length bar, or bar oriented incorrectly',
        whatToCheck: 'Compare new bar shape to old; verify it is correct for your TJ year and lift status',
        fix: 'For lifted TJs, install a "drop bracket" or upgraded relocated bracket. For standard-height TJs, verify part number matches application.',
      },
    ],
    proTips: [
      'On a lifted TJ (3+ inches), upgrade to an adjustable heim-jointed track bar — it lets you re-center the axle precisely after the lift, which is critical for steering geometry.',
      'Apply anti-seize to the track bar bolt threads at install. Future removal will be far easier.',
      'Buy a complete steering rebuild kit (track bar, drag link, tie rod ends, ball joints) if any one component is shot — they tend to age together. Doing them all once costs less than three separate visits.',
      'Drive on ramps for the final torque — easier than juggling jack stands and a separate jack to support the axle.',
      'After 100 miles of driving, recheck both track bar bolts. New bushings can settle slightly and the bolt may need a small additional torque.',
    ],
    commonMistakes: [
      {
        mistake: 'Final-torquing the bolts at full droop',
        consequence: 'Bushings tear within months. Wobble returns and the bar must be replaced again.',
      },
      {
        mistake: 'Installing without matching bar length to old bar',
        consequence: 'Axle ends up off-center under the body. Steering pulls and tire wear is uneven.',
      },
      {
        mistake: 'Forcing a stuck bolt with a hammer',
        consequence: 'Damaged axle bracket. Repair becomes welding or bracket replacement.',
      },
      {
        mistake: 'Replacing the track bar but ignoring other worn steering parts',
        consequence: 'Wobble returns from drag link or tie rod ends. Job has to be repeated.',
      },
    ],
    successIndicators: [
      'Steering is centered and precise — no wandering on smooth roads',
      'No clunks, pops, or oscillation over bumps at any speed',
      'No detectable lateral play in the front axle when rocked from the steering wheel',
      'Both bolts torqued at ride height to spec (125 axle, 110 frame ft-lbs)',
      'Bushings show no tearing or fluid weep after 1,000 miles',
    ],
    whenToSeekHelp:
      'If death wobble persists after track bar replacement and inspection of all other front-end components, the issue may be wheel balance, tire defect, or alignment — a shop with a wheel balancer and alignment rack is the right next step. If the axle bracket is damaged from a stuck bolt or hammer impact, welding or bracket replacement may be needed. If you cannot center the axle even with an adjustable bar, the chassis may be twisted or the lift mounts may be misaligned — a frame shop can diagnose.',
    legalDisclaimer: 'This guide was generated by GarageIQ\'s AI from publicly available sources. Some information may be incorrect or out of date. Verify torque specifications and any safety-critical procedures against the OEM service manual for your vehicle before performing work. Steering and suspension components are safety-critical — if anything is unclear, stop and consult a professional. The Haynes Manual tab on this page links to the licensed manual for your vehicle. GarageIQ is not a substitute for a qualified mechanic. By using this guide you agree to GarageIQ\'s Terms of Service and assume responsibility for the work performed.',
    manualSource: {
      title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)',
      url: 'https://www.haynes.com/en-us/wrangler/jeep/2003',
      notes: 'Procedure sourced from public catalogs and forums; cross-reference torques and any application-specific bar part numbers against your licensed manual.',
    },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — HEADLIGHT BULB / SEALED BEAM REPLACEMENT — engine-agnostic
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-headlight-bulb-replacement',
    title: 'Headlight (Sealed-Beam 7" Round) Replacement',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [{ startYear: 1997, endYear: 2006 }],
    },
    video: {
      youtubeId: 'n-rPW-jp54w',
      title: 'How to Replace Headlights 1997-2006 Jeep Wrangler',
      channel: 'TRQ',
    },
    source: 'Generated by GarageIQ AI from publicly available sources (Mopar parts catalog, Sylvania and Philips bulb cross-reference charts, NHTSA TSBs, owner\'s manual, technician forums and Wrangler community service threads). Cross-checked and structured per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Electrical',
    difficulty: 'beginner',
    duration: '20-30 minutes per side',
    interval: 'Replace when burned out, when the lens becomes deeply yellowed or cracked, or when upgrading to a brighter bulb. Inspect annually for moisture inside the lens.',
    description:
      'The TJ uses a 7-inch round sealed-beam headlight (the same physical size as classic Jeep CJs and many vintage cars). Each headlight is a single self-contained unit — bulb and reflector in one — and the entire unit is replaced when it fails. The standard part is an H6024 sealed beam (halogen). Many owners upgrade to LED or HID conversion kits using the same 7-inch housing for dramatically better night visibility. This guide covers OEM replacement and the basics of an LED/HID upgrade.',
    overview: {
      difficulty: 'Beginner — easiest job in this guide library, takes 20 minutes per side',
      estimatedTime: '20-30 minutes per side; replacing both takes under an hour',
      estimatedCost: '$15-30 per OEM sealed beam (pair); $80-200 for an LED upgrade pair',
      whyMatters: 'Worn or yellowed headlights cut night-driving visibility dramatically. A fresh pair of sealed beams (or an LED upgrade) is the single biggest safety improvement you can make for night driving.',
      safetyNote: 'No serious safety concerns. Disconnect battery if comfortable doing so to avoid accidental short. Sealed beam lenses are glass — handle carefully and dispose of broken glass safely.',
    },
    tools: [
      'Phillips screwdriver (#2)',
      'Flat-blade screwdriver (small)',
      '7mm or 8mm socket / nut driver (some retainer screws)',
      'Clean cotton gloves or microfiber cloth (to handle the bulb without skin oils)',
      'Optional: dielectric grease (for the connector)',
      'A flashlight or headlamp (for working inside the headlight bezel cavity)',
    ],
    parts: [
      {
        id: 'headlight-sealed-beam',
        name: 'Headlight Sealed Beam (7" Round, H6024)',
        partNumber: 'Sylvania H6024 / Philips H6024 / Wagner BP6024 — equivalents fit any 7" round application',
        quantity: 2,
        price: 16,
        category: 'required',
        notes: 'Replace in pairs to keep both headlights with matching brightness and color temperature. The H6024 is the standard halogen sealed-beam.',
      },
      {
        id: 'led-headlight-upgrade',
        name: 'LED Headlight Upgrade Kit (7" Round)',
        partNumber: 'JW Speaker Model 8700 Evolution J / Truck-Lite 27270C / KC HiLites Gravity Pro7',
        quantity: 1,
        price: 180,
        category: 'optional',
        notes: 'Optional upgrade. LED kits provide 2-3x more usable light for the same beam pattern. Confirm DOT approval and beam pattern before buying — some imports do not pattern correctly.',
      },
    ],
    safetyInformation:
      'No major safety concerns. If the vehicle has been running, the headlight housing may be warm — let it cool before handling. Do not touch the glass envelope of a halogen sealed beam with bare skin — finger oils create hot spots that shorten bulb life. Wear cotton or nitrile gloves when handling the lens. If the existing bulb is broken, glass shards can be sharp — wear safety glasses during removal.',
    beforeYouStart: [
      'Park on level ground with engine off',
      'Open the hood for access to the headlight bezel screws (some screws are on the front, others are accessed from inside the engine bay)',
      'Have both new sealed beams ready (replacing in pairs is best practice)',
      'Have a clean rag or gloves available to handle the new bulbs without skin oil contact',
      'Know which side is high beam vs low beam — TJ uses dual-filament sealed beams, but the wiring matters for any LED upgrade',
    ],
    steps: [
      {
        number: 1,
        title: 'Open the Hood and Locate Bezel Screws',
        description:
          'Open the hood. Each headlight is held in place by a chrome retaining ring (bezel) secured with 3-4 small Phillips screws. On the TJ, the screws are typically at the top, sides, and bottom of the chrome ring around the lens. Identify the screws and confirm you have the right driver size.',
        why: 'Identifying the screws first prevents using the wrong tool and stripping the head — Phillips screws on cheaper TJs are often soft.',
        tip: 'A magnetic Phillips bit holds the screws while you remove them. Without the magnet, dropped screws bounce into the engine bay and disappear.',
        commonMistake: 'Using the wrong screwdriver size and stripping the screw heads. Use #2 Phillips, not #1 or #3.',
      },
      {
        number: 2,
        title: 'Remove the Chrome Bezel',
        description:
          'Remove the 3-4 Phillips screws from the chrome bezel. Set them aside in a magnetic tray or small cup. Carefully lift the chrome bezel away from the headlight assembly. The bezel may be rusty or stuck — wiggle gently to free it. Set it aside.',
        why: 'The bezel holds the headlight retainer ring against the body — once the bezel is off, the retainer ring is accessible.',
        tip: 'Note the bezel orientation (it may have an "up" arrow stamped inside) so you can reinstall it correctly.',
        commonMistake: 'Dropping the screws into the engine bay. Magnetic tray or cup is essential.',
      },
      {
        number: 3,
        title: 'Remove the Headlight Retainer Ring',
        description:
          'Behind the bezel is a metal retainer ring that holds the sealed beam against the headlight bucket. The retainer is held by 3 small screws (typically Phillips or 7mm hex-head). Loosen these screws — they DO NOT come fully out, they have slotted holes that let you rotate the retainer to release the headlight. Once the screws are loose, rotate the retainer counterclockwise about 1/4 turn and lift it off.',
        why: 'The retainer ring is designed for quick service — slotted screws mean you only need to loosen them, not remove. Faster install.',
        tip: 'If a retainer screw is rusted and will not budge, apply penetrating oil and let it sit 5 minutes before more force.',
        commonMistake: 'Trying to remove the retainer screws fully. They are designed to stay captive.',
      },
      {
        number: 4,
        title: 'Pull the Sealed Beam Forward and Disconnect the Plug',
        description:
          'With the retainer off, the sealed beam will tilt forward — pull it gently toward you. Behind the bulb is a 3-prong electrical connector. Pinch the lock tab (or pull the plug straight back if no lock) and disconnect the wiring. Inspect the plug pins for corrosion — if green or pitted, clean with a small wire brush or replace the plug.',
        why: 'The 3-prong plug carries low beam, high beam, and ground. Corroded pins cause flickering or dim lights.',
        tip: 'A small dab of dielectric grease in the plug socket before reconnection prevents future corrosion.',
        commonMistake: 'Pulling the plug by the wires. The wires can disconnect from the plug body. Always pull by the plug body.',
      },
      {
        number: 5,
        title: 'Inspect the Bucket and Aiming Springs',
        description:
          'With the bulb out, look into the headlight bucket. Check for water damage, rust, or insects nesting inside. Check the two aiming springs (back of the bucket) for free movement — they let the headlight aim be adjusted up/down and left/right via two adjuster screws. If a spring is broken or rusted in place, aiming will be impossible.',
        why: 'The bucket and aiming hardware affect headlight position. Rust or moisture damage means the new bulb still will not aim properly.',
        tip: 'A small dab of grease on the aiming-screw threads keeps them moving freely.',
        commonMistake: 'Skipping inspection. A broken aiming spring is found AFTER install when aiming is impossible.',
      },
      {
        number: 6,
        title: 'Connect the New Sealed Beam',
        description:
          'Take the new sealed beam out of the box. Wear cotton or nitrile gloves to handle the glass — skin oil contaminates the surface. Plug the 3-prong wiring connector into the back of the new bulb. Confirm a positive click or full insertion. Note the orientation of the bulb — the lens has "TOP" and an arrow indicating which side faces up.',
        why: 'Bulb orientation matters for proper beam pattern. Installing upside-down sends light to the sky and ground in the wrong proportions.',
        tip: 'Test the new bulb electrically (turn on the headlight switch briefly) before fully installing — confirms the bulb works and you have the right wiring orientation.',
        commonMistake: 'Installing with the bulb rotated 180°. The beam pattern is inverted and you blind oncoming traffic.',
      },
      {
        number: 7,
        title: 'Seat the Bulb in the Bucket',
        description:
          'Position the new sealed beam in the headlight bucket with the "TOP" facing up. The bulb should sit with its three locating tabs in the matching slots in the bucket — it can only fit one way. Push it firmly back into the bucket until it is fully seated.',
        why: 'The locating tabs index the bulb to the bucket so that aiming adjustments work correctly. Forcing the bulb without engaging tabs misaligns the beam.',
        tip: 'Look for the small tabs on the back of the bulb edge — they slide into matching slots inside the bucket.',
        commonMistake: 'Forcing the bulb in without engaging the locating tabs. The retainer will not seat properly afterward.',
      },
      {
        number: 8,
        title: 'Reinstall Retainer Ring and Bezel',
        description:
          'Place the retainer ring over the new bulb. Engage the slotted screws and rotate the ring 1/4 turn clockwise to lock it in. Tighten the three retainer screws snugly. Reinstall the chrome bezel with its 3-4 Phillips screws (DO NOT overtighten — the bezel is a thin metal stamping and strips easily).',
        why: 'The retainer ring holds the bulb firmly against the bucket so it does not vibrate loose. Bezel finishes the appearance.',
        tip: 'Snug only on the bezel screws — they only need to hold the chrome trim, not bear any structural load.',
        commonMistake: 'Overtightening bezel screws and stripping the threads in the bucket.',
      },
      {
        number: 9,
        title: 'Test Both Beams and Aiming',
        description:
          'Close the hood. Turn the ignition to "on" but do not start. Turn the headlights on — verify low beams illuminate. Pull the high-beam stalk — verify high beams illuminate. Both filaments should work. Walk in front of the vehicle and visually confirm both headlights are on and aiming roughly forward (not skyward or straight down).',
        why: 'Confirming both filaments before final aim adjustment ensures the new bulb is healthy and wired correctly.',
        tip: 'Park 25 feet from a garage door at night and observe the cutoff line — the low-beam cutoff should be slightly below horizontal so it does not blind oncoming traffic.',
        commonMistake: 'Skipping the aim check. Low beams that point too high blind oncoming traffic; too low and you cannot see far enough.',
      },
      {
        number: 10,
        title: 'Aim the Headlights (Optional but Recommended)',
        description:
          'Park 25 feet from a flat wall (a garage door works). Mark the wall where the centerline of each headlight projects. Turn the low beams on. The brightest part of each beam should fall slightly below the marked centerline (about 2 inches lower at 25 feet). Adjust using the two aiming screws on each headlight bucket — top screw adjusts up/down, side screw adjusts left/right.',
        why: 'Proper aim maximizes road illumination without blinding oncoming traffic. Both safety and courtesy.',
        tip: 'Have the vehicle on level ground with a half tank of gas and the driver in the seat to simulate normal load conditions.',
        commonMistake: 'Aiming too high. Looks bright to the driver but blinds everyone else and is illegal in most states.',
      },
    ],
    torqueSpecifications: [
      {
        fastener: 'Headlight retainer ring screws',
        specification: 'Hand-snug — approximately 12-18 in-lbs (1.4-2.0 Nm). No published torque spec.',
        notes: 'Snug only; these screws stay captive in slotted holes.',
      },
      {
        fastener: 'Chrome bezel screws',
        specification: 'Hand-snug — approximately 12-18 in-lbs (1.4-2.0 Nm). No published torque spec.',
        notes: 'Do not over-tighten — strips the bucket threads easily.',
      },
    ],
    fluidSpecifications: [],
    troubleshooting: [
      {
        problem: 'New headlight does not light up',
        likelyCause: 'Plug not fully seated, plug pins corroded, bulb defective, or fuse blown',
        whatToCheck: 'Reseat plug; check fuse #11 (headlights) under hood; swap with the other side\'s bulb to test',
        fix: 'Reseat plug. Replace fuse if blown. Replace bulb if defective. Clean corroded plug pins with a small wire brush.',
      },
      {
        problem: 'One filament works but not the other (high or low beam dead)',
        likelyCause: 'Filament burned out in the new bulb (defect), or wiring problem in the dimmer switch / multifunction stalk',
        whatToCheck: 'Swap the new bulb to the other side — if the same filament dies, suspect wiring; if the other filament works, the bulb is defective',
        fix: 'Warranty exchange the defective bulb. If wiring is the cause, the multifunction stalk or relay may need attention.',
      },
      {
        problem: 'Beam pattern is upside-down / blinds traffic',
        likelyCause: 'Bulb installed rotated 180°',
        whatToCheck: 'Verify "TOP" arrow on bulb lens is facing up',
        fix: 'Remove and reinstall with TOP up.',
      },
      {
        problem: 'Headlight aim cannot be adjusted',
        likelyCause: 'Aiming spring broken or aiming screws rusted in place',
        whatToCheck: 'Look at the back of the bucket for spring condition; try aiming screws with hand pressure',
        fix: 'Apply penetrating oil to aiming screws. If spring is broken, the bucket assembly may need replacement.',
      },
      {
        problem: 'Moisture / fog inside the new sealed beam',
        likelyCause: 'Defective bulb (rare — sealed beams are factory-sealed) or a broken seal from impact',
        whatToCheck: 'Inspect lens edge for chips or cracks',
        fix: 'Warranty exchange. If the lens is cracked, replace.',
      },
    ],
    proTips: [
      'Replace headlights in PAIRS, not one at a time — keeps both sides matching brightness and color temperature.',
      'For dramatically better night vision, upgrade to a quality LED 7-inch headlight (JW Speaker, Truck-Lite, or KC HiLites are well-regarded). The lens diameter matches OEM exactly.',
      'After install, aim the headlights against a wall — most TJs have headlights that drift over time, especially after off-road bumps.',
      'Apply dielectric grease to the plug pins on install. Prevents corrosion and dimming over years.',
      'Keep one of the old sealed beams in your spare-parts box as a known-working test unit for diagnosing electrical issues.',
    ],
    commonMistakes: [
      {
        mistake: 'Touching the glass envelope of the bulb with bare fingers',
        consequence: 'Skin oils create hot spots that shorten bulb life by 50% or more.',
      },
      {
        mistake: 'Installing the bulb rotated 180° (TOP down)',
        consequence: 'Beam pattern is inverted; blinds oncoming traffic.',
      },
      {
        mistake: 'Overtightening the bezel screws',
        consequence: 'Strips the threads in the headlight bucket. Future bezel installs require larger screws or bucket replacement.',
      },
      {
        mistake: 'Replacing only one headlight when the other is also dim',
        consequence: 'Mismatched brightness creates an off-balance beam pattern. Drivers compensate by steering subtly.',
      },
    ],
    successIndicators: [
      'Both low beams illuminate at the same brightness',
      'Both high beams illuminate at the same brightness',
      'Beam cutoff line is slightly below horizontal at 25 feet (proper aim)',
      'No flickering or dimming when other electrical loads (turn signals, brake lights) are active',
      'No moisture inside either lens after one drive cycle',
    ],
    whenToSeekHelp:
      'If a new bulb does not light and the fuse is good, the multifunction stalk (turn signal / dimmer combination switch) may have a failed contact — diagnosis requires a multimeter and access to the stalk wiring. If a relay clicks but lights stay off, the relay or wiring harness may be the cause. For LED conversion kits that flicker or display a "bulb out" warning, a load resistor or anti-flicker harness may be needed (some kits include this; others sell it separately).',
    legalDisclaimer: 'This guide was generated by GarageIQ\'s AI from publicly available sources. Some information may be incorrect or out of date. Verify torque specifications and any safety-critical procedures against the OEM service manual for your vehicle before performing work. The Haynes Manual tab on this page links to the licensed manual for your vehicle. GarageIQ is not a substitute for a qualified mechanic. By using this guide you agree to GarageIQ\'s Terms of Service and assume responsibility for the work performed.',
    manualSource: {
      title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)',
      url: 'https://www.haynes.com/en-us/wrangler/jeep/2003',
      notes: 'Procedure sourced from public catalogs and forums; cross-reference bulb part numbers and any wiring concerns against your licensed manual.',
    },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — THROTTLE BODY CLEANING — 4.0L I6 (4.0L specific procedure)
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-throttle-body-cleaning-4pt0l-i6',
    title: 'Throttle Body Cleaning (4.0L I6)',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [{ startYear: 1997, endYear: 2006 }],
      engine: '4.0l-i6',
    },
    video: {
      youtubeId: 'XZofQaGyvSc',
      title: 'Cleaning Throttle Body on Jeep TJ 4.0',
      channel: 'YouTube',
    },
    source: 'Generated by GarageIQ AI from publicly available sources (Mopar parts catalog, CRC and Sea Foam product instructions, NHTSA TSBs, owner\'s manual, technician forums and Wrangler community service threads). Cross-checked and structured per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Routine Maintenance & Fluids',
    difficulty: 'beginner',
    duration: '30-45 minutes',
    interval: 'Every 30,000 miles, or whenever rough idle, hesitation, or hard cold start develops on a 4.0L with high mileage. Heavy carbon often correlates with stop-and-go driving.',
    description:
      'The 4.0L I6 throttle body collects carbon and oil residue on the throttle plate and the bore behind it over time, especially around the IAC (idle air control) valve passage. The gunk causes uneven airflow at idle, which the engine compensates for with a higher idle, hunting idle, or stalling at stops. Cleaning the throttle body is one of the cheapest and most effective tune-up tasks on a high-mileage TJ — total parts cost is one $8 can of cleaner. This guide covers a thorough cleaning of the throttle body, throttle plate, and IAC passage without removing the throttle body from the engine.',
    overview: {
      difficulty: 'Beginner — no special tools, no torque-critical fasteners',
      estimatedTime: '30-45 minutes',
      estimatedCost: '$8 (one can of throttle body cleaner)',
      whyMatters: 'Carbon-clogged throttle body is the single most common cause of rough idle and hesitation on a high-mileage 4.0L. Cleaning takes 30 minutes and often eliminates the symptom completely.',
      safetyNote: 'Throttle body cleaner is flammable. Work in a ventilated area. Do not spray directly into the intake manifold while engine is hot. Disconnect the battery negative cable as a precaution against short circuits while working around the IAC and TPS connectors.',
    },
    tools: [
      '8mm and 10mm sockets and ratchet (clamps and brackets)',
      'Phillips screwdriver or T20 Torx (intake hose clamp)',
      'Soft-bristle toothbrush or parts cleaning brush',
      'Clean microfiber rags or shop towels',
      'Disposable nitrile gloves and eye protection',
    ],
    parts: [
      {
        id: 'throttle-body-cleaner',
        name: 'Throttle Body Cleaner (CRC or equivalent)',
        partNumber: 'CRC 05078 (specifically marked "Throttle Body" — DO NOT use carb cleaner on a TBI throttle body with coatings)',
        quantity: 1,
        price: 8,
        category: 'required',
        notes: 'TJ 4.0L throttle bodies have a thin protective coating on the bore. Use a cleaner labeled SPECIFICALLY for throttle body — do not use plain carburetor cleaner, which can strip the coating.',
      },
      {
        id: 'intake-hose-clamps',
        name: 'Intake hose clamps (if old clamps are deformed)',
        partNumber: 'Generic 2-3" stainless hose clamps',
        quantity: 1,
        price: 4,
        category: 'optional',
        notes: 'Replace only if original clamps are stripped, broken, or will not retorque.',
      },
    ],
    safetyInformation:
      'Throttle body cleaner is flammable — work outdoors or in a well-ventilated garage with no open flames. Wear eye protection — overspray can blow back. Disconnect the battery negative cable before working to prevent any accidental short or sensor damage. Do not start the engine with an air intake hose disconnected and a clean throttle body — unmetered air can cause a stall or run condition. Always reattach the air intake before cranking.',
    beforeYouStart: [
      'Park on level ground with engine off and cooled (at least 30 minutes since last run)',
      'Disconnect the battery negative cable as a precaution',
      'Have a microfiber rag or two ready — cleaner residue should be wiped, not air-dried',
      'If the engine is running rough, code-scan first to rule out a vacuum leak or sensor failure that cleaning will not fix',
      'Note: cleaning will reset the idle adaptation — the engine may idle slightly rough for the first 5-10 minutes after the job until the IAC re-learns',
    ],
    steps: [
      {
        number: 1,
        title: 'Remove the Air Intake Hose',
        description:
          'Loosen the hose clamp on the throttle body end of the air intake hose (typically a Phillips or T20 Torx clamp). Loosen the clamp on the air filter box end. Disconnect any vacuum hose tee fittings on the intake hose (note their position). Pull the intake hose off and set aside.',
        why: 'Removing the intake hose exposes the throttle body bore — the entire cleaning surface is now visible.',
        tip: 'Take a quick photo of the vacuum hose connections before removing, so reassembly is straightforward.',
        commonMistake: 'Forgetting to disconnect a vacuum line, then ripping it off when removing the hose.',
      },
      {
        number: 2,
        title: 'Inspect the Throttle Body Bore',
        description:
          'Look into the throttle body bore. You should see the throttle plate (the round disc that opens when the gas pedal is pressed) and a recessed passage around the bore for the IAC valve. Carbon buildup typically appears as a black or dark-brown ring on the bore wall behind the throttle plate, with heavier deposits at the bottom of the bore. Note the severity — a light dusting cleans in one pass; a heavy ring needs multiple passes.',
        why: 'Pre-inspection sets expectations for how thorough the cleaning needs to be.',
        tip: 'A flashlight pointed straight into the bore makes carbon deposits very visible.',
        commonMistake: 'Skipping inspection and cleaning blindly. Heavy buildup needs multiple passes.',
      },
      {
        number: 3,
        title: 'Manually Open the Throttle Plate',
        description:
          'With the engine OFF, reach into the engine bay and locate the throttle linkage on the side of the throttle body. Pull the linkage to open the throttle plate manually. The plate should rotate freely from closed to wide-open with no binding or sticky spots.',
        why: 'Working with the plate held open exposes the back side of the plate and the bore behind it — the dirtiest area.',
        tip: 'Use a small wedge (a clean rag rolled tightly) to hold the linkage open hands-free while you spray.',
        commonMistake: 'Using the gas pedal to hold the plate open. Requires a helper and is awkward.',
      },
      {
        number: 4,
        title: 'Spray Cleaner into the Bore',
        description:
          'With the throttle plate held open, spray throttle body cleaner into the bore. Direct the spray at the bore walls (especially the bottom) and the back side of the throttle plate. Do not spray directly into the IAC passage at this stage. Let the cleaner soak for 30 seconds — it will dissolve carbon.',
        why: 'Soaking lets the cleaner break the carbon bond. Spraying and immediately wiping is less effective.',
        tip: 'Aim the spray slightly downward into the bore so dissolved carbon drips out the bottom of the throttle body, not deeper into the intake.',
        commonMistake: 'Spraying so much cleaner that it drips into the intake manifold. A puddle in the manifold can cause hydrolock if the engine is started — wipe up any pooled cleaner.',
      },
      {
        number: 5,
        title: 'Wipe and Repeat',
        description:
          'Use a microfiber rag wrapped around your finger to wipe the bore wall and both sides of the throttle plate. The rag will come out black on the first pass. Repeat: spray, soak, wipe — until the bore wall is bare metal and the throttle plate is clean on both sides. Heavy buildup may take 3-5 passes.',
        why: 'Iterative cleaning is more thorough than one big spray. Each pass removes the loosened carbon and exposes more for the next pass.',
        tip: 'A soft toothbrush helps reach the corners where the bore meets the casting. Do not use a wire brush — it can scratch the protective coating.',
        commonMistake: 'Stopping too early. Behind the throttle plate (when fully open) is where the heaviest buildup hides — make sure you cleaned that area.',
      },
      {
        number: 6,
        title: 'Clean the IAC Passage',
        description:
          'Locate the IAC (idle air control) valve passage — a small bore in the throttle body wall, often on the side opposite the throttle plate linkage. Spray a short burst of cleaner into the passage. Use the toothbrush or a cotton swab to gently scrub the visible portion. Wipe with a clean rag corner.',
        why: 'IAC passage carbon disrupts idle airflow even more than throttle plate carbon. Cleaning here is often what cures a hunting idle.',
        tip: 'Do NOT remove the IAC valve itself unless you are replacing it — it is sealed with an O-ring and forcing it can damage the seal.',
        commonMistake: 'Spraying excessive cleaner into the IAC — it can foul the valve internals and cause new symptoms.',
      },
      {
        number: 7,
        title: 'Allow Cleaner to Evaporate',
        description:
          'Release the throttle linkage and let the throttle plate return to the closed position. Leave the engine bay open for 5 minutes to let any residual cleaner fully evaporate.',
        why: 'Liquid cleaner left in the throttle bore can puddle into the intake manifold when the engine cranks. Evaporation prevents this.',
        tip: 'A small fan blowing into the engine bay speeds up evaporation. Don\'t use compressed air — it can blow contaminants into sensors.',
        commonMistake: 'Reassembling immediately. Wet cleaner can pool and cause a rough first start.',
      },
      {
        number: 8,
        title: 'Reattach the Air Intake Hose',
        description:
          'Reinstall the intake hose. Reconnect any vacuum lines you removed (consult your photos). Tighten both hose clamps snugly. Confirm the hose is fully seated on the throttle body and the air box.',
        why: 'A leaking intake hose connection lets unmetered air bypass the throttle body and confuses the engine\'s air-fuel ratio calculation.',
        tip: 'Wiggle the hose at both ends after clamp tightening — there should be no movement.',
        commonMistake: 'Loose clamps. Hose can pop off under boost (well, not on the TJ — but on hard throttle).',
      },
      {
        number: 9,
        title: 'Reconnect Battery and Start the Engine',
        description:
          'Reconnect the battery negative cable. Turn the ignition to the run position for 5 seconds (do not start) — this lets the ECM check sensor circuits. Then start the engine. Idle may be rough for 30-60 seconds while the IAC re-learns; this is normal. Let the engine idle for 5 minutes without touching the throttle so the ECM can recalibrate the idle adaptation.',
        why: 'The ECM stores idle adaptation values. Cleaning resets the throttle body geometry slightly, so the ECM needs time to relearn.',
        tip: 'If the idle is still rough after 5 minutes, drive the vehicle through a few stop-and-go cycles — that often completes the relearn.',
        commonMistake: 'Revving the engine immediately to "test" the cleaning. Disrupts the idle relearn.',
      },
      {
        number: 10,
        title: 'Test Drive and Verify',
        description:
          'Drive the vehicle for 15-20 minutes through a mix of city and highway driving. The expected results: smoother idle, faster cold start, better off-idle response, and possibly a slight throttle improvement. If a rough idle or hesitation persists, the cause was likely something other than throttle body buildup (vacuum leak, IAC valve failure, or sensor issue).',
        why: 'Real-world driving is the test. If symptoms persist, additional diagnosis is needed.',
        tip: 'Keep a small notebook log of work done — useful for tracking what fixed what over the years.',
        commonMistake: 'Expecting a transformation. Cleaning fixes carbon-related symptoms; it does not fix sensor or vacuum-leak symptoms.',
      },
    ],
    torqueSpecifications: [
      {
        fastener: 'Air intake hose clamps',
        specification: 'Hand-snug — approximately 25-35 in-lbs (2.8-4.0 Nm). No published torque spec.',
        notes: 'Snug; no need for a torque wrench. The clamp should grip the hose without crushing it.',
      },
    ],
    fluidSpecifications: [],
    troubleshooting: [
      {
        problem: 'Idle is still rough after cleaning',
        likelyCause: 'IAC valve internal failure (cleaning the passage does not fix a defective valve), vacuum leak elsewhere, or sensor failure',
        whatToCheck: 'Spray throttle body cleaner around vacuum hose connections with engine running — if idle changes, you found a leak. Code-scan for sensor faults.',
        fix: 'Replace IAC valve (~$30 part). Repair vacuum leaks. Address sensor codes individually.',
      },
      {
        problem: 'Engine stalls at idle after cleaning',
        likelyCause: 'IAC valve disturbed during cleaning; or air intake hose not seated and unmetered air entering',
        whatToCheck: 'Re-check air intake hose connections. If sealed, drive through an idle relearn cycle.',
        fix: 'Reseat hose clamps. Drive 15+ minutes to allow IAC relearn. If stall persists, replace IAC valve.',
      },
      {
        problem: 'Check engine light comes on after cleaning',
        likelyCause: 'TPS or IAC connector knocked loose, or cleaner sprayed into a sensor',
        whatToCheck: 'Verify all connectors on the throttle body and surrounding sensors are fully seated',
        fix: 'Reseat connectors. Code-scan and clear any stored codes. If a code returns, address that sensor.',
      },
      {
        problem: 'Gasoline smell after cleaning',
        likelyCause: 'Cleaner pooled in the intake manifold; will burn off after a few miles',
        whatToCheck: 'Drive the vehicle 5-10 miles — smell should clear',
        fix: 'Drive vehicle until smell clears. If smell persists, suspect a fuel system issue unrelated to the cleaning.',
      },
      {
        problem: 'Throttle plate sticks or binds',
        likelyCause: 'Cleaner residue dried in the throttle plate hinge, or the hinge itself is worn',
        whatToCheck: 'Manually move the linkage and feel for any binding spot',
        fix: 'Spray a small amount of cleaner directly on the throttle plate hinge while moving the linkage — flushes residue. If binding persists, the throttle body itself may need replacement.',
      },
    ],
    proTips: [
      'Pair this with a fuel system cleaner (Sea Foam or Techron) added to the gas tank — together they restore much of the lost performance on a high-mileage 4.0L.',
      'Take "before" and "after" photos of the throttle body bore. They are surprisingly satisfying to compare and motivate future maintenance.',
      'Schedule throttle body cleaning every 30,000 miles as preventive maintenance rather than waiting for symptoms.',
      'If you are also replacing the IAC valve (a common companion job), remove the IAC FIRST, clean the body and IAC passage, then install the new IAC. Easier access.',
      'Keep your throttle body cleaner can — even after the cleaning, it is useful for finding vacuum leaks (spray around hoses with engine running and listen for idle change).',
    ],
    commonMistakes: [
      {
        mistake: 'Using carburetor cleaner instead of throttle body cleaner',
        consequence: 'Strips the protective coating on the throttle body bore. The bare metal then collects carbon faster.',
      },
      {
        mistake: 'Spraying excessive cleaner that pools in the intake manifold',
        consequence: 'Risk of hydrolock or rough first start. Always wipe up any pooled liquid.',
      },
      {
        mistake: 'Not letting the IAC re-learn after cleaning',
        consequence: 'Rough idle persists even though the cleaning was thorough. Just drive it for 15 minutes.',
      },
      {
        mistake: 'Ignoring vacuum leaks and assuming all rough idle is throttle body carbon',
        consequence: 'Cleaning does not solve the problem. Next step should be vacuum-leak hunt.',
      },
    ],
    successIndicators: [
      'Throttle body bore is clean bare metal (or factory coating) with no visible carbon ring',
      'Idle is smooth and steady at the correct RPM (~750 RPM warm) with no hunting',
      'Cold start is faster and smoother than before',
      'Off-idle throttle response is sharper',
      'No new check-engine light or stored codes',
    ],
    whenToSeekHelp:
      'If after a thorough cleaning the symptoms persist (rough idle, hesitation, stalling), the cause is likely not carbon and a deeper diagnosis is needed: vacuum leak hunt, sensor testing (TPS, MAP, IAC), or fuel system pressure check. A shop with a scan tool can read live data to pinpoint the issue. If the throttle plate itself is loose on its shaft (worn over many miles), the throttle body needs replacement, not cleaning.',
    legalDisclaimer: 'This guide was generated by GarageIQ\'s AI from publicly available sources. Some information may be incorrect or out of date. Verify torque specifications and any safety-critical procedures against the OEM service manual for your vehicle before performing work. The Haynes Manual tab on this page links to the licensed manual for your vehicle. GarageIQ is not a substitute for a qualified mechanic. By using this guide you agree to GarageIQ\'s Terms of Service and assume responsibility for the work performed.',
    manualSource: {
      title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)',
      url: 'https://www.haynes.com/en-us/wrangler/jeep/2003',
      notes: 'Procedure sourced from public catalogs and forums; cross-reference any cleaner compatibility notes with your throttle body coating against your licensed manual.',
    },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — RADIATOR CAP / COOLING SYSTEM PRESSURE TEST — engine-agnostic
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-radiator-cap-pressure-test',
    title: 'Radiator Cap & Cooling System Pressure Test',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [{ startYear: 1997, endYear: 2006 }],
    },
    video: {
      youtubeId: 'tAXBY1ys9dA',
      title: 'Radiator Flush 2001 Jeep Wrangler DIY (cooling system reference)',
      channel: 'Suburban DIY',
    },
    source: 'Generated by GarageIQ AI from publicly available sources (Mopar parts catalog, Stant cooling system tester documentation, ASE technical references, NHTSA TSBs, owner\'s manual, technician forums and Wrangler community service threads). Cross-checked and structured per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Cooling & Heating',
    difficulty: 'beginner',
    duration: '20-30 minutes',
    interval: 'Inspect radiator cap annually or any time you do cooling system service. Pressure-test the system whenever there is unexplained coolant loss, frequent overheating, or before a long road trip on a high-mileage vehicle.',
    description:
      'The radiator cap does more than seal — it pressurizes the cooling system to raise the boiling point of coolant. A 16 psi cap (TJ standard) raises the boiling point from 212°F to about 252°F, which prevents nucleate boiling at the cylinder head where it could cause hot spots and head gasket damage. A weak cap is a $10 part that protects everything else. The cooling system pressure test uses a hand pump to pressurize the system and watch for leaks — the most efficient way to find a slow coolant loss without running the engine. This guide covers cap inspection, replacement, and the full pressure test procedure.',
    overview: {
      difficulty: 'Beginner — diagnostic procedure with no permanent disassembly',
      estimatedTime: '20-30 minutes (cap replacement); 10-15 minutes (pressure test if you own the tool); add 30 minutes if you need to rent the tool',
      estimatedCost: '$10-15 for a new cap; $40-100 to buy a pressure tester (or free rental at many auto-parts stores)',
      whyMatters: 'A weak radiator cap can cause overheating, coolant boil-over, and even mask a head gasket leak. A pressure test catches small external leaks before they become big problems and can identify a head gasket issue early. Both tasks together are the best 30 minutes you can spend on a high-mileage TJ.',
      safetyNote: 'Cooling system can be HOT and PRESSURIZED. NEVER open the radiator cap on a warm engine — coolant can spray with severe burn risk. Always wait for the engine to fully cool (1-2 hours) or test cold from start. Hot coolant burns far worse than equivalent boiling water due to its specific heat and adhesion.',
    },
    tools: [
      'Cooling system pressure tester (Stant 12270 or rental from O\'Reilly / AutoZone / Advance — usually free with deposit)',
      'Cap-only adapter (most pressure testers include one)',
      'Clean rag',
      'Drain pan (to catch any drips)',
      'Disposable nitrile gloves',
      'Flashlight',
      'Mirror on a stick (optional, for inspecting under the radiator)',
    ],
    parts: [
      {
        id: 'radiator-cap',
        name: 'Radiator Cap (16 psi)',
        partNumber: 'Mopar 52028065 — equivalents: Stant 10231 / Gates 31526 / Motorad MGC-933',
        quantity: 1,
        price: 12,
        category: 'required',
        notes: 'TJ\'s use a 16 psi cap. Confirm pressure rating on your existing cap (stamped on top). Caps wear out around 80,000-100,000 miles even when they look fine externally.',
      },
      {
        id: 'coolant-topoff',
        name: 'Coolant for Top-off (matching system)',
        partNumber: 'Mopar 5012394AB (Mopar 50/50 HOAT, OEM yellow) — match what is currently in the system',
        quantity: 1,
        price: 10,
        category: 'conditional',
        notes: 'Have a pint of matching coolant on hand in case the test reveals a low coolant level. NEVER mix coolant types (HOAT, OAT, IAT, G-05) — mixing can cause gel formation.',
      },
    ],
    safetyInformation:
      'Cooling system can be HOT and PRESSURIZED. Never open the radiator cap on a warm engine — wait for full cool-down (engine cool to the touch, at least 1-2 hours from last run). Hot coolant can spray and cause severe burns. Used coolant is toxic to pets and wildlife — clean any spills immediately and dispose at an auto-parts recycling center. The pressure tester applies system pressure manually — do not exceed the cap rating (16 psi for TJ) during testing or you can damage hoses and components. Wear eye protection during pressure testing.',
    beforeYouStart: [
      'Confirm engine is fully cool — radiator and upper hose should be cool to the touch',
      'Park on level ground for accurate coolant-level readings',
      'Check coolant level in the overflow reservoir BEFORE removing the cap (the level should be at the "FULL COLD" mark)',
      'If renting a pressure tester from an auto-parts store, confirm it includes a TJ-compatible cap adapter',
      'Have a pint of matching coolant available for top-off if needed',
      'Read your tester\'s instructions — gauges and pump techniques vary',
    ],
    steps: [
      {
        number: 1,
        title: 'Inspect the Radiator Cap Visually',
        description:
          'With the engine fully cool, remove the radiator cap by pressing down and rotating counterclockwise. Inspect the cap: look at the rubber seal on the underside (should be supple, not cracked or hardened), the secondary seal on the top of the cap, and the spring underneath the seal (should compress smoothly when pressed). Check the pressure rating stamped on top — should be 16 psi for TJ. Any cracking, hardening, or rust on the cap means replacement.',
        why: 'A visual inspection catches the easy failures. A cap with cracked rubber will not seal at full pressure even if it looks fine on the surface.',
        tip: 'Look at the metal lever spring carefully — if it has surface rust, replace the cap regardless of how it pressure-tests.',
        commonMistake: 'Removing the cap on a warm engine. Severe burn risk. Always wait for full cool-down.',
      },
      {
        number: 2,
        title: 'Test the Cap Itself with the Pressure Tester',
        description:
          'Most pressure tester kits include a cap adapter — a fitting that holds the cap and lets you pressurize it directly. Install the cap on the cap adapter, attach the adapter to the pump, and slowly pump until the gauge reads 16 psi. The cap should hold this pressure for at least 1 minute without leaking. If the gauge drops, the cap relief valve is opening too early — replace the cap.',
        why: 'A cap that opens below its rated pressure (e.g., relieves at 10 psi instead of 16) lets coolant boil at lower temperatures and reduces overheat margin.',
        tip: 'If the cap holds pressure for 30 seconds then slowly drops, the relief valve is on the edge of failing — borderline pass. Replace if you have any doubt.',
        commonMistake: 'Skipping the cap test and only pressurizing the system. A bad cap will pass the system test if the cap is not in line.',
      },
      {
        number: 3,
        title: 'Connect the Pressure Tester to the Radiator',
        description:
          'Remove the cap adapter and switch to the standard radiator-fill-neck adapter that came with the tester. Press the adapter onto the radiator filler neck and twist clockwise until it locks (it works like a cap itself). Connect the pump hose to the adapter. Confirm the gauge reads 0 psi.',
        why: 'The adapter creates a sealed connection at the radiator filler neck so the pump can pressurize the entire cooling system through that opening.',
        tip: 'Some adapters need a slight wiggle to seat fully. The lock is positive — when it engages, the adapter will not pull off without unlocking.',
        commonMistake: 'Adapter not fully seated. Pumping then leaks immediately at the adapter and gives a false fault reading.',
      },
      {
        number: 4,
        title: 'Pressurize to 16 PSI',
        description:
          'Slowly pump the tester handle to bring the gauge up to 16 psi (the system\'s rated pressure). Pump slowly so you can watch the gauge climb steadily. Stop at exactly 16 psi — do not exceed.',
        why: 'Pressurizing to system rating tests the cooling system at its real working pressure. Exceeding the rating risks blowing a hose or damaging a heater core.',
        tip: 'If the gauge will not climb at all and you feel no resistance pumping, suspect a major leak (a burst hose or heater core failure). Stop and inspect.',
        commonMistake: 'Pumping past 16 psi. Can cause a weakened hose to burst.',
      },
      {
        number: 5,
        title: 'Hold and Watch the Gauge',
        description:
          'With the gauge at 16 psi, watch it for 10-15 minutes. The pressure should stay at or near 16 psi the entire time. If it drops slowly, there is an external leak somewhere — the next step is to find it. If it drops rapidly, the leak is significant. If it holds rock-steady, the system is sealed.',
        why: 'A holding gauge proves the system has no external leaks. A dropping gauge tells you to investigate further.',
        tip: 'A 1-2 psi drop over 15 minutes is borderline — repeat the test to confirm before condemning anything. Ambient temperature changes can shift pressure slightly.',
        commonMistake: 'Reading the gauge after only 30 seconds. Slow leaks need 10+ minutes of observation.',
      },
      {
        number: 6,
        title: 'If Pressure Drops: Hunt for the Leak',
        description:
          'With the system pressurized, walk around the engine bay with a flashlight and check every cooling-system component: upper and lower radiator hoses, heater hoses (going through the firewall), water pump weep hole (under the pump), thermostat housing, freeze plugs, radiator core (look for green stains in the fins), and overflow reservoir. Any wetness or coolant droplet identifies the leak source. Also look UNDER the vehicle for puddling — a leak might drip from a hidden joint and pool below.',
        why: 'A pressurized system pushes coolant out of any leak — making slow leaks visible that would not show during normal driving.',
        tip: 'A small UV dye additive (Tracerline or AGS) makes leaks visible under a UV flashlight — useful for chasing tiny leaks that do not produce visible drops.',
        commonMistake: 'Only looking on the top side. Leaks often drip down and pool below visible inspection lines.',
      },
      {
        number: 7,
        title: 'Check for Internal Leaks (Head Gasket Indicator)',
        description:
          'If the gauge drops but you find NO external leak, suspect an internal leak — coolant is escaping into the combustion chamber. Confirmation: re-pressurize and watch for bubbles in the radiator filler neck while a helper cranks the engine briefly (do not start). Bubbles during cranking indicate combustion gases pushing into the cooling system through a head gasket breach. A combustion-gas test kit (UView 560000 Block Tester) is the definitive test — it changes a chemical fluid color when CO2 from combustion is present in coolant.',
        why: 'External leaks show up wet. Internal leaks (head gasket, cracked head) only show up via pressure drop with no visible source.',
        tip: 'A simple sniff test at the overflow tank with engine running can reveal exhaust smell — a strong indicator of head gasket failure.',
        commonMistake: 'Assuming pressure drop without external leak means a bad gauge. It usually means an internal leak.',
      },
      {
        number: 8,
        title: 'Release Pressure and Disconnect the Tester',
        description:
          'After the test, release the pressure SLOWLY using the pressure-release valve on the pump (most testers have a small button or lever). Watch the gauge return to 0. Once at 0, twist the radiator adapter counterclockwise to remove and disconnect the tester. Reinstall the radiator cap (new one if you replaced it) — press down and rotate clockwise until it locks.',
        why: 'Slow pressure release prevents coolant from being sucked back into the radiator unevenly, which can cause an air pocket.',
        tip: 'Place a rag under the adapter as you release pressure — a small amount of coolant typically dribbles out.',
        commonMistake: 'Releasing pressure too fast by yanking the adapter. Creates a vacuum spike that can pull air in past the cap seal.',
      },
      {
        number: 9,
        title: 'Replace the Radiator Cap (if it Failed the Test)',
        description:
          'If the cap test failed in step 2, install the new 16 psi cap. Press down on the cap and rotate clockwise until you hear a positive click and feel resistance. The cap should not turn further. Top off coolant in the overflow reservoir to the FULL COLD line if needed.',
        why: 'A new cap restores correct system pressure and protects against overheating.',
        tip: 'Buy the cap from a reputable brand (Stant, Gates, Motorad). Cheap caps often have shorter service life and may not hold rated pressure.',
        commonMistake: 'Installing the cap without fully locking it. Coolant can be lost during driving.',
      },
      {
        number: 10,
        title: 'Verify After a Drive',
        description:
          'Drive the vehicle 15-20 minutes through normal conditions including some highway. After the drive, let the engine cool fully (1+ hours), then check the coolant level. The level should be at FULL COLD on the overflow tank. If it has dropped, there may still be a small leak that did not show during the static pressure test (a leak that only opens under heat expansion).',
        why: 'Some leaks only appear hot. Confirming coolant level after a drive catches them.',
        tip: 'Mark the coolant level with a Sharpie line on the overflow tank — easier to spot small drops over time.',
        commonMistake: 'Checking coolant level while still hot. Hot coolant expands and reads higher than cold level.',
      },
    ],
    torqueSpecifications: [
      {
        fastener: 'Radiator cap',
        specification: 'Hand-tight to the locked position. No torque spec.',
        notes: 'Press down and rotate clockwise until positive click.',
      },
    ],
    fluidSpecifications: [
      {
        fluid: 'Engine coolant (top-off only — no full drain in this procedure)',
        type: 'Match what is currently in the system. TJ OEM is HOAT (yellow Mopar 50/50). DO NOT mix coolant types.',
        capacity: 'Top-off as needed to FULL COLD line on the overflow reservoir. Typical top-off is a few ounces.',
        notes: 'Mixing coolant types can cause gel or sludge in the system.',
      },
    ],
    troubleshooting: [
      {
        problem: 'Pressure tester adapter leaks at the radiator',
        likelyCause: 'Adapter not fully seated, or radiator filler neck is corroded',
        whatToCheck: 'Reseat adapter; inspect filler neck for corrosion',
        fix: 'Reseat firmly with a slight twist. Clean filler neck with a brass brush if corroded. If still leaks, the adapter may not be the right size for the TJ filler neck.',
      },
      {
        problem: 'Pressure drops but no visible external leak',
        likelyCause: 'Internal leak (head gasket, cracked head, intake gasket); leak in heater core (often inside the cabin); leak from overflow tube',
        whatToCheck: 'Smell coolant at the overflow tank with engine running; check passenger floor for wetness; do combustion gas test',
        fix: 'Internal leak requires deeper diagnosis — combustion gas test, compression test, coolant analysis. Heater core leak shows as wet passenger carpet. May need professional diagnosis.',
      },
      {
        problem: 'Cap fails pressure test even when new',
        likelyCause: 'Defective cap (rare but possible); wrong pressure rating',
        whatToCheck: 'Verify cap is rated 16 psi; warranty exchange if defective',
        fix: 'Replace with a different brand or warranty exchange the original.',
      },
      {
        problem: 'System holds pressure but loses coolant during driving',
        likelyCause: 'Leak that only opens under heat or vibration; failed reservoir cap; overflow tube routing issue',
        whatToCheck: 'Check overflow reservoir cap and seal; inspect with engine running and warm (carefully, with engine cooled enough to avoid burns)',
        fix: 'Replace overflow reservoir cap if leaking. Run engine to operating temp and inspect with care. May need a more dynamic test.',
      },
      {
        problem: 'Pressure climbs but feels too easy to pump',
        likelyCause: 'Major leak (burst hose, blown core)',
        whatToCheck: 'Visual inspection of all hoses; look under the vehicle for puddle',
        fix: 'Identify and repair the failed component before continuing testing.',
      },
    ],
    proTips: [
      'Replace the radiator cap every 60,000 miles preventatively, even if it tests fine. The rubber and spring degrade with heat cycles.',
      'Keep a UV dye in your cooling system — when a leak develops, the dye makes it visible under UV light. Make finding leaks much easier.',
      'A pressure tester is one of the cheaper diagnostic tools to own ($40-100). It pays for itself the first time it tells you the head gasket is fine and saves you a $500 unnecessary repair.',
      'Most chain auto-parts stores rent pressure testers free with a deposit — you do not need to buy one for occasional use.',
      'After ANY cooling system work (water pump, hose, thermostat, etc.) do a pressure test to confirm everything seals before driving.',
    ],
    commonMistakes: [
      {
        mistake: 'Removing the radiator cap on a warm engine',
        consequence: 'Severe burn risk from pressurized hot coolant. Always wait for full cool-down.',
      },
      {
        mistake: 'Pumping past 16 psi during the test',
        consequence: 'Can blow a weakened hose or damage the heater core.',
      },
      {
        mistake: 'Skipping the cap test and only pressurizing the system',
        consequence: 'A bad cap masquerades as a system that holds pressure (because the cap is not in line during the test).',
      },
      {
        mistake: 'Topping off with the wrong coolant type',
        consequence: 'Gelling or sludge in the cooling system; reduced flow; possible head gasket failure from poor cooling.',
      },
    ],
    successIndicators: [
      'Pressure gauge holds at 16 psi for 15 minutes with no drop',
      'Cap holds 16 psi on the cap adapter for 1 minute without leaking',
      'No visible coolant drips, stains, or wetness anywhere in the engine bay or under the vehicle',
      'No bubbling in the radiator filler neck during cranking (no head gasket breach)',
      'Coolant level remains at FULL COLD after a 15-20 minute drive and full cool-down',
    ],
    whenToSeekHelp:
      'If pressure drops with no external leak found, the next step is a combustion gas test or block test — this requires a kit ($30-60) or a shop. A confirmed positive test (combustion gases in the coolant) means head gasket repair is in your future. If the heater core is leaking, repair requires removing the dash — typically a shop job. If you cannot find the leak after pressurizing and using UV dye, an exhaust gas test or a borescope inspection of the cylinder head may be needed.',
    legalDisclaimer: 'This guide was generated by GarageIQ\'s AI from publicly available sources. Some information may be incorrect or out of date. Verify torque specifications and any safety-critical procedures against the OEM service manual for your vehicle before performing work. The Haynes Manual tab on this page links to the licensed manual for your vehicle. GarageIQ is not a substitute for a qualified mechanic. By using this guide you agree to GarageIQ\'s Terms of Service and assume responsibility for the work performed. Used coolant is toxic — dispose at an auto-parts recycling center.',
    manualSource: {
      title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)',
      url: 'https://www.haynes.com/en-us/wrangler/jeep/2003',
      notes: 'Procedure sourced from public catalogs and forums; cross-reference cap pressure rating and any cooling-system specifics against your licensed manual.',
    },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — MANUAL TRANSMISSION REMOVAL & INSTALLATION (NV3550 / NSG370 / AX-15)
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-manual-transmission-rr',
    title: 'Manual Transmission Removal & Installation (NV3550 / NSG370 / AX-15)',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [{ startYear: 1997, endYear: 2006 }],
    },
    video: {
      youtubeId: 'OUxOtXGtbNQ',
      title: 'Jeep Wrangler TJ Clutch replacement (transmission removal)',
      channel: 'Power Addicts - FixJeeps',
    },
    source: 'Generated by GarageIQ AI from publicly available sources (Mopar parts catalog, New Venture Gear / Magna Powertrain service literature, Aisin AX-15 documentation, Mopar service bulletins, owner\'s manual, technician forums and Wrangler community service threads). Cross-checked and structured per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Engine & Drivetrain',
    difficulty: 'advanced',
    duration: '4 - 6 hours (experienced); 6 - 10 hours (first-timer)',
    interval: 'As required for clutch service, transmission rebuild, rear main seal replacement, or transmission failure. Not a maintenance interval.',
    description:
      'Removing the manual transmission on a TJ is a cornerstone teardown job — required for clutch replacement, rear main seal, and any transmission rebuild. The TJ used three different manual transmissions over its life: AX-15 (1997-1999), NV3550 (2000-2004), and NSG370 (2005-2006). The procedures share the same general steps: drop the skid plate, separate the transfer case, support and lower the transmission, then remove the bell-housing bolts. The specifics differ by transmission — bolt patterns, bell-housing length, and shift-tower style vary. This guide walks the procedure in a way that applies to all three with notes where they differ.',
    overview: {
      difficulty: 'Advanced — heavy components, awkward angles, multiple adjacent systems must be disconnected. A transmission jack and at least one helper are essential.',
      estimatedTime: '4-6 hours for a second-timer; plan a full weekend for first-timer',
      estimatedCost: '$0-50 if reusing all bolts; budget $30-60 for a transmission jack rental and any seals/bolts that need replacement',
      whyMatters: 'The transmission is the gateway to clutch service, rear main seal, flywheel inspection, and many internal repairs. Doing this job yourself saves $400-800 in labor and is one of the most empowering jobs a DIY-er can accomplish.',
      safetyNote: 'The transmission weighs 90-130 lbs and is unwieldy. NEVER work under it without rated jack stands and a transmission jack. The output shaft alignment is dangerous — fingers can be pinched between the input shaft and the bellhousing. Use mechanical aids, not muscle.',
    },
    tools: [
      'Floor jack and four jack stands (two for vehicle, two for transmission support)',
      'Transmission jack (rental from auto-parts store; do NOT use a regular floor jack)',
      'Socket set: metric (10mm-22mm) and 1/2"-drive sockets',
      'Long extensions (6", 12", 18") and a swivel adapter',
      '13mm, 15mm, 16mm, 18mm wrenches',
      'Torque wrench capable of 100 ft-lbs (136 Nm)',
      'Penetrating oil',
      'Pry bar',
      'Drain pan (for transfer case fluid)',
      'Clutch pilot tool (if doing clutch service)',
      'Engine support bar (over fender) — optional but very helpful',
      'Magnetic parts tray',
      'Nitrile gloves and eye protection',
    ],
    parts: [
      {
        id: 'transfer-case-fluid',
        name: 'Transfer Case Fluid (ATF+4 for NV231/NV241OR, GL-3 for NP231 in some apps)',
        partNumber: 'Mopar 68218057AB (ATF+4)',
        quantity: 1,
        price: 12,
        category: 'required',
        notes: 'You will drain the transfer case during this job. Buy 2-3 quarts of fresh fluid for refill.',
      },
      {
        id: 'transmission-input-seal',
        name: 'Transmission Input Shaft Seal (recommended replacement)',
        partNumber: 'NV3550: Mopar 5252493 / NSG370: Mopar 5085160AA / AX-15: Mopar 4778117',
        quantity: 1,
        price: 18,
        category: 'conditional',
        notes: 'Replace the input shaft seal while the transmission is out — easy access, prevents future leak. Almost always worth the $18.',
      },
      {
        id: 'rear-main-seal-trans',
        name: 'Rear Main Seal (engine side)',
        partNumber: 'Mopar 53020749 (4.0L I6)',
        quantity: 1,
        price: 15,
        category: 'conditional',
        notes: 'If the rear of the engine has any oil weep, replace the rear main seal while the transmission is out. Major access advantage.',
      },
      {
        id: 'bell-housing-bolts',
        name: 'Bell Housing Bolts (replace if corroded)',
        partNumber: 'M10 x 1.5 x 30mm bolts for NV3550/NSG370; check spec for AX-15',
        quantity: 6,
        price: 12,
        category: 'conditional',
        notes: 'Reuse if clean; replace any that show corrosion or thread damage.',
      },
    ],
    safetyInformation:
      'The transmission weighs 90-130 lbs and falls awkwardly when unsupported. Always use a transmission jack — a regular floor jack is too unstable and the trans can slide off and crush a hand or foot. Use rated jack stands on level concrete; never work on an asphalt driveway (jack stands sink under load). The exhaust may be hot — let it cool before working under the vehicle. The driveshafts contain U-joints under preload — disconnect carefully. The transfer case fluid is hot if the vehicle was recently driven; let it cool before draining. Eye protection mandatory — debris falls from above when working under the vehicle.',
    beforeYouStart: [
      'Park on a flat, solid concrete surface; gather all tools BEFORE lifting the vehicle',
      'Have a transmission jack rented or available; do not start without one',
      'Have a helper available for the actual transmission removal/install — solo it is dangerous',
      'Disconnect the battery negative cable (prevents accidental starter engagement, frees up clutch master cylinder access on some years)',
      'Drain the transfer case (saves a mess later) — see the transfer case fluid change guide',
      'Photograph every connection BEFORE disconnecting: shifter linkage, electrical plugs, vacuum lines, exhaust position',
      'Mark the orientation of front and rear driveshafts to their flanges with a paint pen — must reinstall in same orientation for vibration balance',
    ],
    steps: [
      {
        number: 1,
        title: 'Lift Vehicle and Remove Skid Plate',
        description:
          'Raise the vehicle and support on jack stands placed under the frame rails — high enough that you can fit a transmission jack underneath. Remove the transmission/transfer case skid plate (typically four 18mm bolts at the corners, plus two more at the crossmember on some years). Set the skid aside.',
        why: 'Skid plate removal is required for transmission access. Frame-mounted stands keep the suspension free to move while you work.',
        tip: 'Use a paint pen to mark the skid plate orientation if it is not obviously symmetrical.',
        commonMistake: 'Working with the vehicle too low. You need at least 24" of clearance under the transmission area.',
      },
      {
        number: 2,
        title: 'Drain the Transfer Case',
        description:
          'Position a drain pan under the transfer case. Remove the transfer case drain plug (square head 3/8" or 1/2"). Once drained, reinstall the plug. The fluid drained should be reasonably clean ATF+4 (red or amber); if dark or burnt-smelling, the case may have other issues.',
        why: 'Disconnecting the transfer case from the transmission with fluid in it creates a major mess.',
        tip: 'Inspect the drained fluid on a clean rag — metal flakes indicate transfer case wear.',
        commonMistake: 'Skipping this step. Fluid pours out all over the floor when you separate the transfer case.',
      },
      {
        number: 3,
        title: 'Disconnect Front and Rear Driveshafts',
        description:
          'Mark each driveshaft\'s rotational orientation to its companion flange with a paint pen (so you can reinstall in the same balance position). For the rear driveshaft, remove the four 12mm bolts at the rear axle pinion flange and slide the shaft forward off the transfer case yoke. For the front driveshaft, remove four 12mm bolts at the front axle yoke and slide forward off the transfer case yoke. Bag and tape any U-joint bearing caps so they do not fall off the yoke.',
        why: 'Driveshafts must be removed before the transfer case can drop out of the way.',
        tip: 'A small zip-tie around each U-joint bearing cap keeps the needles in place during transit.',
        commonMistake: 'Forgetting to mark orientation. Reinstall in a different rotation and the drivetrain may vibrate.',
      },
      {
        number: 4,
        title: 'Disconnect Transfer Case Linkage and Wiring',
        description:
          'Disconnect the transfer case shift linkage (a single rod with a snap-on socket joint, typically). Disconnect the speed sensor and any backup-light or 4WD-position switch wiring on the transfer case. Note which wire goes where. Disconnect the vent hose at the top of the transfer case.',
        why: 'These connections must be free before the transfer case can be dropped.',
        tip: 'Use a magnetic dish to keep small connector clips from disappearing into the engine bay.',
        commonMistake: 'Forgetting a wiring connector. The case will tug on the wire when lowered and may damage it.',
      },
      {
        number: 5,
        title: 'Remove the Transfer Case from the Transmission',
        description:
          'Support the transfer case with a transmission jack or a separate floor jack with a saddle. Remove the six bolts that secure the transfer case to the transmission adapter (typically 13mm or 15mm). Pull the transfer case rearward off the transmission output shaft and lower it to the ground. The transfer case weighs about 80 lbs.',
        why: 'Removing the transfer case separately reduces what you are wrangling when lowering the transmission.',
        tip: 'Once the bolts are out, the case may need a tap with a rubber mallet to break the gasket seal.',
        commonMistake: 'Pulling the transfer case before all bolts are out. Damages the case housing.',
      },
      {
        number: 6,
        title: 'Disconnect Transmission Mounts and Crossmember',
        description:
          'Support the transmission with the transmission jack, snug to the case but not yet bearing weight. Remove the two transmission mount-to-crossmember bolts (typically 15mm). Remove the four crossmember-to-frame bolts (two per side, typically 18mm). Lower the crossmember out of the way. The transmission is now hanging by the bell-housing bolts and the engine mounts.',
        why: 'The crossmember holds the rear of the transmission to the chassis. Remove it, and the transmission can be lowered.',
        tip: 'Mark the crossmember orientation before removal — it can install backwards.',
        commonMistake: 'Letting the transmission jack take full load before the crossmember is loose. Can damage the rear seal.',
      },
      {
        number: 7,
        title: 'Disconnect Shifter, Slave Cylinder, and Wiring',
        description:
          'Remove the shifter from the transmission — typically four bolts at the top of the case, then lift the shifter assembly straight up. Cap the shifter hole to keep dirt out. Disconnect the slave cylinder from the bell housing (one or two bolts) — DO NOT depress the clutch pedal with the slave disconnected, or the master cylinder will pump empty. Tie the slave cylinder up and out of the way. Disconnect the backup-light switch wiring.',
        why: 'Each of these systems is mounted to the transmission; they must be removed before the transmission can drop.',
        tip: 'Wrap the slave cylinder hose with a rag to prevent fluid drip.',
        commonMistake: 'Pumping the clutch pedal with the slave disconnected. Master cylinder runs empty and you will need to bleed the system later.',
      },
      {
        number: 8,
        title: 'Remove Bell-Housing Bolts and Lower the Transmission',
        description:
          'With the transmission supported on the jack, lower it slightly to expose the bell-housing bolts (typically 6 bolts around the perimeter of the bell housing, mix of 13mm/15mm/18mm depending on year). The top bolts are the hardest to reach — long extensions and a swivel are essential. Loosen all bolts but do not remove the bottom ones first. With all bolts loose, slowly slide the transmission rearward off the input shaft (the input shaft slides through the clutch disc and pilot bushing — pull straight back, do not let the trans hang on the input shaft or you will damage the clutch).',
        why: 'Pulling straight back keeps the input shaft aligned with the clutch disc and pilot bushing. Side load can crack the clutch hub.',
        tip: 'Wiggling slightly side-to-side (NOT up-down) helps the input shaft clear the clutch splines.',
        commonMistake: 'Letting the transmission hang on the input shaft. The clutch disc gets damaged and you have to replace it.',
      },
      {
        number: 9,
        title: 'Lower the Transmission to the Floor',
        description:
          'Once the transmission is fully back from the engine, slowly lower the transmission jack to the floor. Slide the transmission out from under the vehicle. Inspect the input shaft, throwout bearing, and pilot bushing area for wear or damage that may explain why the transmission was removed.',
        why: 'Inspection at this stage informs whether additional parts (clutch, throwout bearing, pilot bushing) need to be replaced before reassembly.',
        tip: 'The throwout bearing should rotate smoothly with no roughness. If gritty, replace it.',
        commonMistake: 'Skipping inspection. You may reinstall a worn throwout bearing only to have it fail in 5,000 miles.',
      },
      {
        number: 10,
        title: 'Reinstall in Reverse Order',
        description:
          'If the clutch is being serviced, do that work now (see the Clutch Replacement guide). To reinstall the transmission: lift it back onto the jack, align the input shaft with the clutch disc and pilot bushing, and slide it forward into the bell housing. The transmission will go fully home only when the input shaft fully engages the pilot bushing — do not force it. If the transmission stops 1/4" short, the input shaft is misaligned with the clutch — wiggle gently and try again. Once flush, install bell-housing bolts hand-tight first, then torque to spec (typically 30-40 ft-lbs / 41-54 Nm). Verify torque spec from OEM service manual. Reinstall crossmember, mount, transfer case, driveshafts, shifter, slave cylinder, wiring, and skid plate. Refill transfer case with fresh ATF+4.',
        why: 'Reverse order ensures every connection is restored correctly. The bell-housing alignment is the critical step — getting the input shaft into the pilot bushing without forcing it.',
        tip: 'If the transmission will not go fully home, the most common cause is the clutch disc not being aligned to the pilot bushing — pull back, use a clutch alignment tool to recenter the disc, and try again.',
        commonMistake: 'Forcing the transmission home with bolts. Cracks the bell housing or warps the clutch hub.',
      },
    ],
    torqueSpecifications: [
      {
        fastener: 'Bell-housing bolts (transmission to engine)',
        specification: '30-40 ft-lbs (41-54 Nm). Verify torque spec 30-40 ft-lbs (41-54 Nm) from OEM service manual.',
        notes: 'Torque in a star pattern across the bell housing.',
      },
      {
        fastener: 'Transfer case to transmission adapter bolts',
        specification: '26 ft-lbs (35 Nm). Verify torque spec 26 ft-lbs (35 Nm) from OEM service manual.',
        notes: 'Apply moderate, even torque to all six bolts.',
      },
      {
        fastener: 'Crossmember to frame bolts',
        specification: '50 ft-lbs (68 Nm). Verify torque spec from OEM service manual.',
        notes: 'Two per side; cross-tighten.',
      },
      {
        fastener: 'Transmission mount to crossmember bolts',
        specification: '50 ft-lbs (68 Nm). Verify torque spec from OEM service manual.',
        notes: 'Standard mid-torque.',
      },
      {
        fastener: 'Driveshaft U-joint flange bolts',
        specification: '16 ft-lbs (22 Nm). Verify torque spec 16 ft-lbs (22 Nm) from OEM service manual.',
        notes: 'Twelve-point bolts; reinstall in original orientation.',
      },
      {
        fastener: 'Skid plate bolts',
        specification: '30 ft-lbs (41 Nm). Verify torque spec from OEM service manual.',
        notes: 'Cross-tighten in star pattern.',
      },
    ],
    fluidSpecifications: [
      {
        fluid: 'Transfer case fluid (NV231 / NV241OR)',
        type: 'ATF+4 (Mopar 68218057AB or equivalent)',
        capacity: 'NV231: 1.25 quarts (1.2 L). NV241OR: 1.6 quarts (1.5 L). Always fill to the fill-plug edge.',
        notes: 'See transfer case fluid change guide for fill procedure.',
      },
    ],
    troubleshooting: [
      {
        problem: 'Transmission will not slide back off the input shaft',
        likelyCause: 'Vehicle is in gear (locks the input shaft), or the input shaft is hung up on a stuck pilot bushing',
        whatToCheck: 'Confirm the transmission is in neutral; gently rock the input shaft side-to-side',
        fix: 'Place transmission in neutral. Try wiggling the case while pulling — never use a pry bar against the bell housing flange.',
      },
      {
        problem: 'Transmission will not slide fully forward into bell housing',
        likelyCause: 'Clutch disc not aligned with pilot bushing, throwout bearing not on the fork properly, or wrong orientation of bell housing',
        whatToCheck: 'Pull transmission back 6 inches; reach in and feel the clutch disc; use alignment tool if needed',
        fix: 'Use a clutch pilot alignment tool to recenter the clutch disc, then try again. Do not force.',
      },
      {
        problem: 'Bell-housing bolts will not go fully home',
        likelyCause: 'Cross-threaded bolt, transmission not fully seated, or wrong-length bolt',
        whatToCheck: 'Verify each bolt threads in by hand at least 3 turns before applying torque',
        fix: 'Hand-thread bolts. If they bind, the case is not fully seated — pull and check input shaft alignment.',
      },
      {
        problem: 'Clutch pedal goes to floor after install',
        likelyCause: 'Clutch master cylinder pumped dry during the job (slave cylinder was disconnected and pedal was pressed)',
        whatToCheck: 'Master cylinder reservoir level',
        fix: 'Top off and bleed the clutch hydraulic system. See Clutch Hydraulics guide.',
      },
      {
        problem: 'Vibration or growl after reinstall',
        likelyCause: 'Driveshaft U-joint orientation reversed, transmission mount damaged, throwout bearing dragging',
        whatToCheck: 'Confirm driveshaft alignment marks; verify mount integrity',
        fix: 'Realign driveshafts to original orientation. Replace mount if cracked. If growl is from the bell housing, suspect throwout bearing.',
      },
    ],
    proTips: [
      'Rent a transmission jack — they are $30-50 for a day at most chains and they make this job 10x safer.',
      'Replace the rear main seal and clutch (if applicable) while the trans is out — re-doing it later means redoing this entire teardown.',
      'Photograph every connection before disconnecting. The shifter, slave cylinder, wiring, and exhaust connections all look similar in the dark.',
      'Mark the driveshaft orientation with a Sharpie or paint pen — vibration is the price of getting it wrong.',
      'Have a helper for the actual transmission lift and align — they steady the input shaft alignment while you operate the jack.',
      'Refill the transfer case BEFORE driving — running it dry destroys the chain and bearings.',
    ],
    commonMistakes: [
      {
        mistake: 'Letting the transmission hang on the input shaft',
        consequence: 'Damages the clutch disc hub and may require clutch replacement.',
      },
      {
        mistake: 'Pumping the clutch pedal with the slave cylinder disconnected',
        consequence: 'Master cylinder runs dry. System must be re-bled.',
      },
      {
        mistake: 'Forgetting to mark driveshaft orientation',
        consequence: 'Drivetrain vibration after reinstall. Some drivelines are very sensitive to clocking.',
      },
      {
        mistake: 'Using a regular floor jack instead of a transmission jack',
        consequence: 'Transmission is unstable; can fall and cause severe injury.',
      },
      {
        mistake: 'Forcing the bell-housing bolts when they do not pull the trans home',
        consequence: 'Cracks the bell housing or breaks the input shaft. Both very expensive.',
      },
    ],
    successIndicators: [
      'Transmission slides home fully without forcing — bell-housing flange flush against engine block',
      'All bell-housing bolts torqued to spec',
      'Clutch pedal feels normal after bleeding',
      'No driveline vibration at any speed during test drive',
      'No fluid leaks at transfer case to transmission joint, slave cylinder, or rear output seal',
      'Backup lights and 4WD indicator function correctly',
    ],
    whenToSeekHelp:
      'If the transmission will not go fully home despite multiple alignment attempts, the clutch disc may have failed during install or the pilot bushing may be seized — both require diagnosis with the trans out. If the clutch hydraulic system will not bleed after the job, suspect a damaged slave cylinder or master cylinder failure. If the transmission shifts poorly after install, the shifter linkage may have been mis-installed; consult a shop or refer back to the shifter portion of the manual.',
    legalDisclaimer: 'This guide was generated by GarageIQ\'s AI from publicly available sources. Some information may be incorrect or out of date. Verify torque specifications and any safety-critical procedures against the OEM service manual for your vehicle before performing work. The Haynes Manual tab on this page links to the licensed manual for your vehicle. GarageIQ is not a substitute for a qualified mechanic. By using this guide you agree to GarageIQ\'s Terms of Service and assume responsibility for the work performed. Heavy components — use rated tools and never work alone.',
    manualSource: {
      title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)',
      url: 'https://www.haynes.com/en-us/wrangler/jeep/2003',
      notes: 'Procedure sourced from public catalogs and forums; cross-reference torques and any year-specific transmission variations against your licensed manual.',
    },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — CLUTCH REPLACEMENT (Disc, Pressure Plate, Throwout Bearing, Pilot Bushing)
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-clutch-replacement',
    title: 'Clutch Replacement (Disc, Pressure Plate, Throwout Bearing, Pilot Bushing)',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [{ startYear: 1997, endYear: 2006 }],
    },
    video: {
      youtubeId: 'OUxOtXGtbNQ',
      title: 'Jeep Wrangler TJ Clutch replacement',
      channel: 'Power Addicts - FixJeeps',
    },
    source: 'Generated by GarageIQ AI from publicly available sources (Mopar parts catalog, LuK / Sachs / Centerforce clutch literature, AX-15 / NV3550 / NSG370 service references, owner\'s manual, technician forums and Wrangler community service threads). Cross-checked and structured per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Engine & Drivetrain',
    difficulty: 'advanced',
    duration: '2 - 3 hours (with transmission already out); add 4-6 hours for transmission R&R',
    interval: 'Replace when slipping under load, chattering on engagement, hard pedal effort, or after 80,000-150,000 miles depending on use. Off-road and towing shorten clutch life significantly.',
    description:
      'Clutch replacement requires the manual transmission to be out (see the Manual Transmission R&R guide). Once exposed, the clutch is straightforward to swap: pressure plate bolts, disc orientation, throwout bearing on the fork, and the pilot bushing in the crankshaft. The TJ\'s 9.125" clutch is shared across most years, with minor variations between AX-15, NV3550, and NSG370 transmissions. While the clutch is out, ALWAYS inspect the flywheel for blue heat-spots or warpage and replace or resurface as needed — a warped flywheel will eat the new clutch within months. This guide assumes the transmission is already removed.',
    overview: {
      difficulty: 'Advanced — alignment of the disc to the pilot bushing is precise; pilot bushing removal requires patience',
      estimatedTime: '2-3 hours assuming transmission is already out',
      estimatedCost: '$120-220 for a quality clutch kit (LuK or Sachs); add $150-250 for flywheel resurfacing if needed',
      whyMatters: 'A worn clutch slips under load (engine rev climbs without speed gain), chatters on engagement, or has a high pedal that grabs near the top. Replacement restores normal driving; ignored, eventually you cannot move the vehicle.',
      safetyNote: 'Clutch dust may contain asbestos in older OEM components — wear a fitted dust mask and wet-clean before disassembly. Pressure plate is under spring tension when bolts are removed unevenly — always loosen in a star pattern. Pilot bushing extraction can launch debris — wear eye protection.',
    },
    tools: [
      'Socket set: 13mm and 15mm sockets (pressure plate bolts vary by year)',
      'Torque wrench capable of 30 ft-lbs (41 Nm)',
      'Clutch alignment tool (typically included in clutch kit; or a spare input shaft)',
      'Pilot bushing puller (slide-hammer style) OR bread-and-grease method',
      'Pilot bushing installer (or appropriate-size socket and brass drift)',
      'Brake cleaner (1 can)',
      'Fine-grit sandpaper (320-400 grit)',
      'High-temp brake/clutch grease (for input shaft splines and throwout bearing collar)',
      'Disposable nitrile gloves and dust mask',
      'Eye protection',
      'A clean rag',
    ],
    parts: [
      {
        id: 'clutch-kit',
        name: 'Clutch Kit (Disc + Pressure Plate + Throwout Bearing + Pilot Tool)',
        partNumber: 'LuK 17-031 (NV3550/NSG370) / LuK 17-032 (AX-15) / Sachs SDK01 / Centerforce CFT361675 (heavy-duty)',
        quantity: 1,
        price: 180,
        category: 'required',
        notes: 'Buy a quality kit (LuK, Sachs, ZF, Centerforce). Cheap kits often have rough engagement and short life. Confirm kit matches your transmission (AX-15 vs NV3550/NSG370).',
      },
      {
        id: 'pilot-bushing',
        name: 'Pilot Bushing (oil-impregnated bronze)',
        partNumber: 'Mopar 53005583 — equivalents: National 9434 / Timken FC65794',
        quantity: 1,
        price: 8,
        category: 'required',
        notes: 'Pilot bushing wears out in service and is a $8 part — always replace with the clutch. A worn pilot causes input-shaft wobble and clutch chatter.',
      },
      {
        id: 'flywheel',
        name: 'Flywheel (only if existing is damaged)',
        partNumber: 'Mopar 53020517 (4.0L I6) / 53020518 (2.4L I4)',
        quantity: 1,
        price: 130,
        category: 'conditional',
        notes: 'Inspect existing flywheel for blue heat spots, deep grooves, or warpage. Resurface ($75-150) or replace ($130 for new). DO NOT reuse a heat-damaged flywheel.',
      },
      {
        id: 'flywheel-bolts',
        name: 'Flywheel Bolts (one-time use on some applications)',
        partNumber: 'Mopar 6034076 (M11 x 1.5 stretch bolts on some years)',
        quantity: 6,
        price: 18,
        category: 'conditional',
        notes: 'TJ flywheel bolts may be torque-to-yield. If unsure, replace as a precaution. Reusing stretch bolts can lead to bolt failure and a launched flywheel.',
      },
    ],
    safetyInformation:
      'Pressure plate is under spring tension. Always loosen and tighten bolts in a star pattern, 1/4 turn at a time, never one-bolt-at-a-time. Clutch dust may contain asbestos in older OEM components — wear a fitted dust mask and wet-clean with brake cleaner before disassembly. Pilot bushing extraction creates debris — wear eye protection. Flywheel weighs 30+ lbs and can fall awkwardly — handle with care. Crankshaft seal is exposed during this work — protect from debris and contamination. Never reuse stretch (torque-to-yield) bolts if your application uses them; consult the OEM manual.',
    beforeYouStart: [
      'Confirm transmission is fully removed (see Manual Transmission R&R guide)',
      'Have new clutch kit, new pilot bushing, and inspection tools staged',
      'Wet-clean the bell housing area with brake cleaner before any disassembly',
      'Inspect the flywheel BEFORE buying a clutch kit — if flywheel needs resurfacing or replacement, plan the trip to the machine shop',
      'Confirm whether your TJ uses torque-to-yield flywheel bolts (year and engine specific) — if yes, buy new bolts',
      'Have a brass drift, slide hammer, or pilot bushing puller available for the bushing',
    ],
    steps: [
      {
        number: 1,
        title: 'Wet-Clean and Inspect the Bell Housing Area',
        description:
          'With the transmission already removed, you can see the pressure plate, clutch disc, throwout bearing fork, and the back of the engine. Spray brake cleaner generously over the area and let drip dry into a pan. Inspect for oil leaks (rear main seal area), throwout bearing wear, fork wear, and pilot bushing condition.',
        why: 'A wet-clean before disassembly suppresses any asbestos-containing dust and lets you see what you are working with.',
        tip: 'If you see oil on the clutch disc, the rear main seal is leaking and you should replace it now (much easier with the trans out).',
        commonMistake: 'Skipping inspection. Reinstalling without addressing a rear main seal leak means the new clutch will be oil-soaked within a month.',
      },
      {
        number: 2,
        title: 'Mark Pressure Plate Position and Loosen Bolts',
        description:
          'Mark one pressure plate bolt and the matching position on the flywheel with a paint pen — pressure plates are usually balanced as a pair, and reinstalling in the same orientation can preserve balance (though the new pressure plate is balanced at the factory). Using a 13mm socket, loosen all six pressure plate bolts in a star pattern, 1/4 turn at a time, until tension is released.',
        why: 'Star-pattern loosening prevents one bolt from carrying all the spring load and getting bent or breaking.',
        tip: 'A flywheel lock tool (or a screwdriver wedged through a flywheel hole and resting on the block) holds the engine from rotating during loosening.',
        commonMistake: 'Loosening one bolt fully at a time. Spring force concentrates on the remaining bolts and can bend them.',
      },
      {
        number: 3,
        title: 'Remove Pressure Plate and Clutch Disc',
        description:
          'Remove all six pressure plate bolts and lift the pressure plate away from the flywheel. The clutch disc will fall out — catch it. Note the orientation: the disc has a "flywheel side" stamping, and the deeper offset of the hub typically faces the transmission (away from the flywheel).',
        why: 'Disc orientation matters — installed backwards, the pressure plate fingers contact the disc hub and the clutch will not release.',
        tip: 'Set the old disc next to the new one and confirm orientation matches.',
        commonMistake: 'Reusing the old disc orientation as gospel. If the previous tech installed it wrong, you will repeat the mistake.',
      },
      {
        number: 4,
        title: 'Inspect the Flywheel',
        description:
          'Wipe the flywheel face with brake cleaner. Look for: blue heat spots (indicates overheating from a slipping clutch), deep radial grooves (more than 0.030" deep), warpage (place a straightedge across the friction surface — light should not pass under), or visible cracks. ANY of these mean the flywheel must be resurfaced (a $75-150 machine shop job) or replaced. Sandpaper alone cannot fix heat damage or warpage.',
        why: 'A bad flywheel will destroy the new clutch in short order. The marginal cost of resurfacing or replacement is small relative to redoing the clutch job.',
        tip: 'Take the flywheel to a machine shop for resurfacing if it shows any wear — they verify flatness and remove the glaze that builds up over time.',
        commonMistake: 'Reusing a heat-damaged flywheel. New clutch slips or chatters from day one.',
      },
      {
        number: 5,
        title: 'Remove the Old Pilot Bushing',
        description:
          'The pilot bushing sits in the center of the crankshaft, supporting the front of the transmission input shaft. Remove using one of: (a) a slide-hammer puller with a pilot bushing tip — this is the cleanest method; (b) the bread-and-grease method — pack the bushing cavity with bread or a thick grease, insert an old input shaft (or a bolt of slightly smaller diameter), and tap it with a hammer. The hydraulic pressure of the grease/bread pushes the bushing out. Once out, clean the cavity with brake cleaner.',
        why: 'A worn pilot bushing causes input-shaft wobble, which destroys a new clutch. Always replace.',
        tip: 'The bread method is messy but works without special tools. Use white bread (gives more compaction) packed tightly. Wear eye protection.',
        commonMistake: 'Trying to drive the bushing further into the crankshaft. There is nowhere for it to go and you damage the crank.',
      },
      {
        number: 6,
        title: 'Install the New Pilot Bushing',
        description:
          'Soak the new oil-impregnated bronze bushing in motor oil for 30 minutes (some come pre-soaked — check packaging). Install the bushing into the crankshaft cavity using a pilot bushing installer or a socket of the same outside diameter as the bushing. Tap the bushing in with a hammer, square to the crankshaft, until it bottoms out flush with or slightly below the crankshaft surface. Do not drive deeper — the input shaft must engage the bushing.',
        why: 'Pre-soaking the bronze in oil ensures the bushing is lubricated for break-in. Square installation prevents binding the input shaft.',
        tip: 'A few light taps are better than one hard hit. The bushing should slide in smoothly with even taps.',
        commonMistake: 'Driving the bushing in cocked. The input shaft will not enter and the transmission will not seat.',
      },
      {
        number: 7,
        title: 'Install the New Clutch Disc with Alignment Tool',
        description:
          'Confirm the new disc orientation: the "flywheel side" stamping faces the flywheel; the deeper hub offset faces the transmission. Insert the clutch alignment tool (included in most clutch kits) through the disc hub. Place the disc against the flywheel and slide the alignment tool into the pilot bushing — this centers the disc.',
        why: 'Proper disc centering is essential — without it, the input shaft will not engage during transmission install.',
        tip: 'Hold the alignment tool steady while bringing the pressure plate up. If the tool wobbles, the disc is not properly seated.',
        commonMistake: 'Installing without an alignment tool. The disc shifts off-center and the transmission will not go home.',
      },
      {
        number: 8,
        title: 'Install the Pressure Plate',
        description:
          'Lift the new pressure plate against the disc. Hand-thread all six bolts (typically with new bolts if the kit includes them). With the alignment tool keeping the disc centered, snug all bolts in a star pattern, 1/4 turn at a time. Once snug, torque to spec (typically 20-25 ft-lbs / 27-34 Nm for the 9.125" clutch). Verify torque spec from OEM service manual.',
        why: 'Star-pattern even tightening keeps the pressure plate flat and the disc centered.',
        tip: 'Once final torque is achieved, remove the alignment tool — it should slide out cleanly with no binding. Binding indicates the disc is off-center.',
        commonMistake: 'Tightening one bolt fully before others. Pressure plate distorts and the new clutch will engage unevenly.',
      },
      {
        number: 9,
        title: 'Lubricate Throwout Bearing and Input Shaft Splines',
        description:
          'Apply a thin film of high-temp brake/clutch grease to the throwout bearing inner collar (where it slides on the input shaft sleeve) and to the input shaft splines (where the disc slides). Do NOT get grease on the disc friction surface or pressure plate. Slide the new throwout bearing onto the input shaft sleeve and confirm it rotates freely.',
        why: 'Spline lubrication lets the disc slide for engagement; throwout bearing lubrication prevents galling against the input shaft sleeve.',
        tip: 'Less is more — a thin film, not a glob. Excess grease can sling onto the disc friction surface and contaminate it.',
        commonMistake: 'Using too much grease. Sling contaminates the disc and the new clutch slips.',
      },
      {
        number: 10,
        title: 'Reinstall Transmission and Test',
        description:
          'Reinstall the transmission per the Manual Transmission R&R guide. Slide the input shaft into the disc and pilot bushing — should slide smoothly. Bell-housing flange must come flush against the engine block before any bolt torque. Once installed, bleed the clutch hydraulics if they were drained or affected. Test drive: clutch engagement should be smooth, no chatter, no slip, and clean shifting through all gears.',
        why: 'Final test confirms the clutch installs correctly and engages properly. New clutches should not need a "break-in" period — they should feel right immediately.',
        tip: 'For the first 500 miles, avoid hard launches and aggressive shifting — let the friction surfaces seat.',
        commonMistake: 'Hard test driving immediately. Glazes the new disc surface.',
      },
    ],
    torqueSpecifications: [
      {
        fastener: 'Pressure plate bolts (6)',
        specification: '20-25 ft-lbs (27-34 Nm). Verify torque spec 20-25 ft-lbs (27-34 Nm) from OEM service manual.',
        notes: 'Star-pattern tightening, 1/4 turn at a time. Some applications may have torque-to-yield bolts — check OEM spec.',
      },
      {
        fastener: 'Flywheel bolts (if removed)',
        specification: '55 ft-lbs + 60 degrees (torque-to-yield, 4.0L I6). Verify torque spec from OEM service manual.',
        notes: 'Use new bolts if the application is torque-to-yield. Star-pattern.',
      },
    ],
    fluidSpecifications: [],
    troubleshooting: [
      {
        problem: 'Transmission will not slide fully home after clutch install',
        likelyCause: 'Disc not centered, pilot bushing not fully seated, or wrong disc orientation',
        whatToCheck: 'Pull transmission back; recheck disc with alignment tool; verify pilot bushing is flush',
        fix: 'Recenter disc with alignment tool. Reseat pilot bushing if cocked. Verify disc orientation.',
      },
      {
        problem: 'New clutch chatters on engagement',
        likelyCause: 'Glazed flywheel (was reused without resurface), pilot bushing dry, or contaminated disc',
        whatToCheck: 'Recheck flywheel surface; confirm pilot bushing was oiled before install',
        fix: 'Resurface flywheel. Replace disc if contaminated with grease or oil. Allow break-in miles.',
      },
      {
        problem: 'New clutch slips under load',
        likelyCause: 'Disc contaminated with oil (rear main seal leak), wrong-application clutch, or worn flywheel',
        whatToCheck: 'Inspect disc for oil contamination; verify clutch matches transmission application',
        fix: 'Replace rear main seal and disc. If wrong application, exchange clutch.',
      },
      {
        problem: 'Clutch pedal does not return / sticks down',
        likelyCause: 'Throwout bearing not properly seated on the fork, slave cylinder issue, or master cylinder failure',
        whatToCheck: 'Pedal feel by hand; reservoir level; visual inspection of fork engagement',
        fix: 'Verify throwout bearing is on fork. Bleed clutch hydraulics. Replace master or slave cylinder if defective.',
      },
      {
        problem: 'Clutch will not disengage (gears grind on shift)',
        likelyCause: 'Disc installed backwards, throwout bearing not contacting pressure plate fingers, or pilot bushing seized',
        whatToCheck: 'Verify disc orientation; confirm fork moves correctly; check pilot bushing rotation',
        fix: 'Remove transmission and verify disc is correct way around. Replace pilot bushing if seized.',
      },
    ],
    proTips: [
      'Always replace the pilot bushing — $8 part that prevents thousands in damage if it fails.',
      'Resurface the flywheel unless it is brand new. Glaze and runout cause chatter even with a perfect clutch install.',
      'Buy a quality clutch kit (LuK, Sachs, ZF, Centerforce). Cheap kits chatter, slip, and have short life.',
      'Take photos of the throwout bearing and fork orientation before disassembly. Reassembly mistakes cause the bearing to dislodge.',
      'After install, do not "ride" the clutch during break-in. Quick, clean engagement seats the disc properly.',
      'If towing or off-roading is in your future, consider a heavy-duty clutch (Centerforce II or similar) — only marginally more expensive and dramatically longer life.',
    ],
    commonMistakes: [
      {
        mistake: 'Reusing a heat-damaged flywheel',
        consequence: 'New clutch chatters or slips from day one. Job has to be redone.',
      },
      {
        mistake: 'Installing the disc backwards',
        consequence: 'Pressure plate fingers hit the hub. Clutch does not release. Trans will not seat.',
      },
      {
        mistake: 'Skipping pilot bushing replacement',
        consequence: 'Worn bushing causes input shaft wobble. New clutch chatters within months.',
      },
      {
        mistake: 'Tightening pressure plate bolts unevenly',
        consequence: 'Pressure plate distorts. Uneven engagement and short life.',
      },
      {
        mistake: 'Greasing the disc friction surface',
        consequence: 'Slippage. Disc must be replaced.',
      },
    ],
    successIndicators: [
      'Clutch engages smoothly with no chatter or grab',
      'No slipping under load (engine RPM tracks vehicle speed)',
      'Clean shifts through all gears with no grinding',
      'Pedal effort feels normal (not unusually high or low)',
      'No oil leaks from the bell housing area',
      'Pedal returns smoothly with no sticking',
    ],
    whenToSeekHelp:
      'If the new clutch chatters, slips, or fails to disengage despite correct installation, suspect a defective new component (pressure plate or disc) — warranty exchange. If the flywheel cannot be resurfaced (deeply pitted, cracked, or below minimum thickness), replacement is required — a machine shop can confirm. If pilot bushing extraction breaks the crankshaft surface, the engine may need professional repair. If clutch hydraulic issues persist after bleeding, see the Clutch Hydraulics guide for diagnosis or consult a shop.',
    legalDisclaimer: 'This guide was generated by GarageIQ\'s AI from publicly available sources. Some information may be incorrect or out of date. Verify torque specifications and any safety-critical procedures against the OEM service manual for your vehicle before performing work. The Haynes Manual tab on this page links to the licensed manual for your vehicle. GarageIQ is not a substitute for a qualified mechanic. By using this guide you agree to GarageIQ\'s Terms of Service and assume responsibility for the work performed. Some flywheel bolts are torque-to-yield (single use); always verify against OEM specs.',
    manualSource: {
      title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)',
      url: 'https://www.haynes.com/en-us/wrangler/jeep/2003',
      notes: 'Procedure sourced from public catalogs and forums; cross-reference torques, bolt specifications, and any year-specific clutch-component variations against your licensed manual.',
    },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — CLUTCH HYDRAULICS (Master Cylinder + Slave Cylinder)
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-clutch-hydraulics-replacement',
    title: 'Clutch Hydraulics Replacement (Master + Slave Cylinder)',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [{ startYear: 1997, endYear: 2006 }],
    },
    video: {
      youtubeId: 'nkloCOeCitA',
      title: 'TJ Wrangler - Replacing Clutch Master & Slave Cylinder - Bleeding Clutch Hydraulics - DIY',
      channel: 'The NotSoGrand Garage',
    },
    source: 'Generated by GarageIQ AI from publicly available sources (Mopar parts catalog, LuK and Sachs hydraulic component literature, owner\'s manual, technician forums and Wrangler community service threads). Cross-checked and structured per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Engine & Drivetrain',
    difficulty: 'intermediate',
    duration: '2 - 3 hours including bleeding',
    interval: 'Replace when clutch pedal sinks or feels soft, fluid is leaking from master or slave, or clutch fails to disengage despite intact mechanical components.',
    description:
      'The TJ uses a hydraulic clutch — pedal force is converted to fluid pressure by the master cylinder (under the dash), routed through a hard line and flexible hose to the slave cylinder, which pushes the throwout bearing fork. The system shares a reservoir with the brake master cylinder (TJ uses a combined reservoir on most years). Common failures: master cylinder internal leak (pedal sinks under sustained pressure with no external leak), slave cylinder external leak (visible fluid at the bell-housing area), or rotted flexible hose. The cylinders are sold as a pre-bled assembly on later years (NSG370 era) — sometimes only as a complete master+slave kit. This guide covers both master and slave service plus full bleed.',
    overview: {
      difficulty: 'Intermediate — straightforward bolt-on but bleeding the clutch system can be finicky',
      estimatedTime: '2-3 hours including the bleed and verification',
      estimatedCost: '$60-130 for master and slave (pre-assembled kit on later years), plus brake fluid',
      whyMatters: 'A failing clutch hydraulic system makes shifting difficult or impossible. Replacement restores normal pedal feel and proper clutch operation.',
      safetyNote: 'Brake fluid is corrosive to paint and harmful to eyes — wear protection and protect bodywork. The clutch master cylinder is in a tight space under the dash; do not use power tools blindly. Bleeding requires accurate technique to avoid leaving air in the system.',
    },
    tools: [
      'Floor jack and jack stands',
      'Lug wrench / 19mm socket',
      '13mm and 14mm sockets and wrenches (master and slave mounting)',
      'Flare-nut wrench (for hydraulic line at master cylinder)',
      'Long extensions for awkward angles under the dash',
      'Brake bleeder bottle and 1/4" clear hose (or one-person bleeder kit)',
      'Drain pan',
      'Brake cleaner (1 can)',
      'Disposable nitrile gloves and eye protection',
      'Old towels and plastic sheeting (paint protection)',
      'Phillips screwdriver (interior trim)',
      'Torque wrench capable of 18 ft-lbs (24 Nm)',
    ],
    parts: [
      {
        id: 'clutch-master-slave-kit',
        name: 'Clutch Master and Slave Cylinder Assembly (pre-bled)',
        partNumber: 'Mopar 52107652AE — equivalents: LuK LMC360 / Sachs SH1377 / Centric 138.66002',
        quantity: 1,
        price: 110,
        category: 'required',
        notes: 'Buy as a pre-bled assembly when available — eliminates the bench-bleed step and is far easier than separate components. Check year-specific compatibility.',
      },
      {
        id: 'clutch-master-only',
        name: 'Clutch Master Cylinder (separate)',
        partNumber: 'Mopar 52107652AB',
        quantity: 1,
        price: 55,
        category: 'optional',
        notes: 'Use only if you are servicing master alone. Most TJ owners replace the assembly together due to coupled aging.',
      },
      {
        id: 'clutch-slave-only',
        name: 'Clutch Slave Cylinder (separate)',
        partNumber: 'Mopar 52107653AB',
        quantity: 1,
        price: 50,
        category: 'optional',
        notes: 'Use only if you are servicing slave alone. Slave cylinder is mounted to the bell housing; if leaking, replace.',
      },
      {
        id: 'brake-fluid-clutch',
        name: 'DOT 3 Brake Fluid (12 oz minimum)',
        partNumber: 'Mopar 4318080 / Valvoline VV632 / Prestone AS400',
        quantity: 1,
        price: 8,
        category: 'required',
        notes: 'TJ clutch hydraulic system uses DOT 3 (DOT 4 is also acceptable). Always use fresh fluid from a sealed bottle.',
      },
    ],
    safetyInformation:
      'Brake fluid is corrosive to paint and harmful to eyes — wear eye protection and protect bodywork with rags or plastic sheeting. Used brake fluid is hazardous waste — dispose at an auto-parts recycling center. The pre-bled assembly comes with a transit clip holding the slave fully extended — DO NOT remove this clip until the assembly is installed and connected, or you will lose the prime. Working under the dash is awkward — go slowly and use a flashlight; do not yank wires when removing trim.',
    beforeYouStart: [
      'Park on level ground; loosen front lug nuts before lifting',
      'If buying a pre-bled assembly, confirm the transit clip is intact',
      'Stage fresh DOT 3 brake fluid and bleeder kit before disassembly',
      'Cover the floor under the brake pedal area with a towel — fluid will drip during master cylinder removal',
      'Have a helper available if you do not own a one-person bleeder kit',
      'Note: the slave cylinder is mounted to the bell housing on the driver\'s side; you must crawl under the vehicle to access',
    ],
    steps: [
      {
        number: 1,
        title: 'Raise Vehicle for Slave Access',
        description:
          'Raise the front of the vehicle and support on jack stands. Remove the driver\'s front wheel for better visibility into the bell-housing area where the slave cylinder lives. Locate the slave cylinder — it bolts to the driver\'s side of the bell housing with two 13mm bolts, and a flexible hydraulic line connects it to the master cylinder hard line.',
        why: 'Wheel-off access is significantly easier than fishing through the wheel well.',
        tip: 'A flashlight or headlamp is essential — the slave is in a dark cavity.',
        commonMistake: 'Trying to access from the engine bay. The slave is reachable only from below.',
      },
      {
        number: 2,
        title: 'Disconnect the Slave Cylinder Hydraulic Line',
        description:
          'Place a drain pan under the slave cylinder. Use a flare-nut wrench (NOT an open-end wrench) to crack the hydraulic-line fitting at the slave. Once cracked, unthread by hand. Cap the line end with a vinyl cap to prevent fluid loss. The slave will now have residual fluid — let it drain into the pan.',
        why: 'Disconnecting at the slave first lets the pan catch the fluid and reduces mess inside the cabin.',
        tip: 'A small touch of penetrating oil on the fitting threads (NOT on the seat) eases stuck nuts.',
        commonMistake: 'Using an open-end wrench. Rounds the fitting and may require new line.',
      },
      {
        number: 3,
        title: 'Remove the Slave Cylinder from Bell Housing',
        description:
          'Remove the two 13mm bolts that hold the slave cylinder to the bell housing. Pull the slave straight off — the pushrod will come with it. Inspect the throwout-bearing fork (visible inside the bell housing) for any damage or wear.',
        why: 'Slave removal exposes the fork, allowing inspection while access is easy.',
        tip: 'Note the orientation of the slave cylinder — pushrod position and bracket clocking matter for reinstall.',
        commonMistake: 'Skipping fork inspection. A worn fork at this point of the job is a 5-minute bonus inspection.',
      },
      {
        number: 4,
        title: 'Access and Disconnect Master Cylinder',
        description:
          'In the driver\'s footwell, remove the lower kick panel or knee bolster (typically 2-3 Phillips screws and clips). The master cylinder is mounted to the firewall, with a pushrod connecting to the clutch pedal. Disconnect the pushrod from the pedal — typically a small clip and a hairpin pin, or a clevis pin. Place a towel under the master to catch fluid.',
        why: 'Pedal pushrod disconnection lets the master come off the firewall studs.',
        tip: 'Take a photo of the pushrod attachment before removing — the clip orientation matters for reinstall.',
        commonMistake: 'Working without a towel. Fluid drips into the carpet and stains.',
      },
      {
        number: 5,
        title: 'Disconnect the Hydraulic Line at the Master',
        description:
          'Use a flare-nut wrench to crack the hydraulic line fitting at the master cylinder. Unthread by hand. Cap the line and the master cylinder fitting to prevent fluid loss. The master cylinder reservoir on TJs is shared with brakes — cap the master with a plastic bag and rubber band to keep brake fluid contained.',
        why: 'Disconnecting at the master frees the line for removal of the assembly.',
        tip: 'Place a wrench under the line fitting to support it during disconnection — prevents bending.',
        commonMistake: 'Bending the line. Hydraulic lines can crack at the bend point.',
      },
      {
        number: 6,
        title: 'Remove Master Cylinder from Firewall',
        description:
          'Remove the two 13mm nuts that hold the master cylinder to the firewall. Pull the master cylinder forward through the firewall hole. The reservoir hose (if separate) may need to be disconnected — note its routing.',
        why: 'Master comes off the firewall via the two stud-and-nut connection.',
        tip: 'A 1/4-drive ratchet with a long extension reaches the firewall nuts in the cramped footwell.',
        commonMistake: 'Forcing the master out without removing the reservoir hose first. Damages the hose.',
      },
      {
        number: 7,
        title: 'Install the New Master Cylinder',
        description:
          'Position the new master cylinder against the firewall. Hand-thread both 13mm nuts to confirm no cross-threading. Torque to 18-22 ft-lbs (24-30 Nm). Verify torque spec from OEM service manual. Reconnect the reservoir hose if separate. Reconnect the pushrod to the clutch pedal with the original clip and pin.',
        why: 'Master goes in first because the hydraulic line connects between master and slave.',
        tip: 'A small touch of grease on the pushrod-pedal pin makes future service easier.',
        commonMistake: 'Cross-threading the firewall nuts. Strips the studs.',
      },
      {
        number: 8,
        title: 'Connect Hydraulic Line to Master',
        description:
          'Hand-thread the hydraulic line into the new master cylinder. Confirm the line is not twisted. Torque to 11-13 ft-lbs (15-18 Nm). Verify torque spec from OEM service manual. Top off the reservoir with fresh DOT 3 fluid.',
        why: 'Master must be connected and full of fluid before slave can be connected — otherwise slave bleeds dry.',
        tip: 'Use a flare-nut wrench to avoid rounding.',
        commonMistake: 'Over-torquing the line nut. Distorts the seat and causes leak.',
      },
      {
        number: 9,
        title: 'Install the Slave Cylinder',
        description:
          'For a pre-bled assembly: with the slave cylinder still secured by its transit clip (pushrod fully extended), bolt the slave to the bell housing using the two 13mm bolts. Torque to 18-22 ft-lbs (24-30 Nm). Connect the hydraulic line at the slave. Once everything is connected and torqued, REMOVE the transit clip — the slave will now retract its pushrod into ready position. For a non-pre-bled separate slave: install slave, connect line, then proceed to bleeding.',
        why: 'The transit clip primes the system pre-bled — it holds the slave at full extension so when removed, the slave retracts and pulls fluid through, self-bleeding.',
        tip: 'Pre-bled systems work brilliantly when used as designed. Removing the clip too early defeats the purpose.',
        commonMistake: 'Removing the transit clip before final assembly. The system loses prime and you must bleed manually.',
      },
      {
        number: 10,
        title: 'Bleed the System and Verify Pedal',
        description:
          'For a non-pre-bled system: top off reservoir; have a helper press and hold the clutch pedal while you crack the bleeder screw on the slave cylinder. Close before pedal release. Repeat 5-10 cycles until clear, air-free fluid emerges. For a pre-bled system: should not need additional bleeding, but check pedal feel — if soft, do a 2-3 cycle bleed. Pedal should feel firm with positive engagement at the typical height. Test driving in a parking lot: shift into first, depress clutch, engage smoothly. Verify clutch fully releases.',
        why: 'Bleeding clears air; verification confirms clutch operation.',
        tip: 'A one-person check-valve bleeder bottle works well solo. Some technicians "gravity bleed" — open the slave bleeder and let fluid drip until air bubbles cease, then top off reservoir and close.',
        commonMistake: 'Letting reservoir run dry mid-bleed. Air enters master and bleed must restart.',
      },
    ],
    torqueSpecifications: [
      {
        fastener: 'Master cylinder firewall mounting nuts (2)',
        specification: '18-22 ft-lbs (24-30 Nm). Verify torque spec 18-22 ft-lbs (24-30 Nm) from OEM service manual.',
        notes: 'Hand-thread both nuts before applying torque.',
      },
      {
        fastener: 'Slave cylinder bell-housing bolts (2)',
        specification: '18-22 ft-lbs (24-30 Nm). Verify torque spec 18-22 ft-lbs (24-30 Nm) from OEM service manual.',
        notes: 'Standard mid-torque.',
      },
      {
        fastener: 'Hydraulic line fittings at master and slave',
        specification: '11-13 ft-lbs (15-18 Nm). Verify torque spec 11-13 ft-lbs (15-18 Nm) from OEM service manual.',
        notes: 'Use flare-nut wrench to avoid rounding.',
      },
      {
        fastener: 'Slave bleeder screw',
        specification: '7-9 ft-lbs (10-12 Nm). Verify torque spec from OEM service manual.',
        notes: 'Snug only; cracking the bleeder boss is expensive.',
      },
    ],
    fluidSpecifications: [
      {
        fluid: 'Clutch hydraulic fluid',
        type: 'DOT 3 (DOT 4 acceptable; DO NOT use DOT 5 silicone)',
        capacity: 'Approximately 8-12 oz total system; 4-6 oz for bleed',
        notes: 'Shared reservoir with brake master on most TJ years.',
      },
    ],
    troubleshooting: [
      {
        problem: 'Clutch pedal goes to floor with no resistance after install',
        likelyCause: 'Air in system, transit clip removed too early, or hydraulic line leak',
        whatToCheck: 'Reservoir level; visual inspection of all fittings for leak; redo bleed',
        fix: 'Bleed thoroughly. If pedal stays soft, suspect a defective master or slave (rare on a new assembly but possible).',
      },
      {
        problem: 'Clutch pedal sinks slowly under sustained pressure',
        likelyCause: 'Internal leak in master cylinder (defective new unit) or insufficient bleed',
        whatToCheck: 'Static pedal hold test — pedal should hold height for 30 seconds',
        fix: 'Re-bleed system. If pedal still sinks, master is defective — warranty exchange.',
      },
      {
        problem: 'Clutch will not fully disengage (gears grind on shift)',
        likelyCause: 'Air in system, slave cylinder pushrod not contacting fork, or wrong-application slave',
        whatToCheck: 'Verify slave pushrod is engaged on fork; confirm slave is correct part number',
        fix: 'Re-bleed. If issue persists, pull slave and confirm pushrod orientation. Replace slave if wrong application.',
      },
      {
        problem: 'Fluid leaks at master cylinder firewall',
        likelyCause: 'Master cylinder body leak (defective new unit) or pushrod seal failure',
        whatToCheck: 'Visually inspect for fluid weeping at master cylinder',
        fix: 'Warranty exchange master cylinder.',
      },
      {
        problem: 'Slave cylinder leaking at bell housing after install',
        likelyCause: 'Slave-to-bell-housing gasket missing or damaged, or slave bolts loose',
        whatToCheck: 'Verify gasket is in place; retorque bolts',
        fix: 'Reinstall with new gasket if needed; retorque to spec.',
      },
    ],
    proTips: [
      'Buy the pre-bled assembly when available — eliminates the most error-prone step (bench bleeding).',
      'A one-person check-valve bleeder bottle ($10) makes solo clutch bleeding trivial. Worth owning.',
      'Replace BOTH master and slave together if either is failing. They are coupled in age and the cost difference is small.',
      'After install, drive 100 miles and recheck the slave for any seepage. Catching early leaks prevents the slave dripping onto the bell housing.',
      'Top off the reservoir to the FULL line — but no higher. Overfull reservoirs slosh fluid out under hard cornering.',
    ],
    commonMistakes: [
      {
        mistake: 'Removing the transit clip on a pre-bled assembly before final install',
        consequence: 'System loses prime. Must do manual bleed.',
      },
      {
        mistake: 'Using an open-end wrench on hydraulic-line fittings',
        consequence: 'Rounded fitting; may require line replacement.',
      },
      {
        mistake: 'Letting the reservoir run dry during bleed',
        consequence: 'Air enters master cylinder. Bleed must restart from scratch.',
      },
      {
        mistake: 'Pumping the clutch pedal with the slave disconnected',
        consequence: 'Master pumps dry; system loses prime.',
      },
      {
        mistake: 'Reusing brake fluid drained from the system',
        consequence: 'Old fluid contains moisture and contaminants. Use only fresh sealed fluid.',
      },
    ],
    successIndicators: [
      'Clutch pedal feels firm with positive engagement at the typical height',
      'Pedal holds steady under 30-second sustained pressure',
      'Clutch fully disengages — gears engage smoothly with no grinding',
      'No fluid leaks at master, slave, or hydraulic line connections after 100 miles',
      'Reservoir level remains at FULL line after a few drive cycles',
    ],
    whenToSeekHelp:
      'If the new master or slave shows defects (internal leak, external leak from body) it is a warranty exchange — return for replacement. If clutch will not bleed despite multiple attempts and a new pre-bled assembly, the issue may be a damaged hydraulic line or a defect in the bell-housing fork that needs trans removal to fix. If clutch pedal falls to the floor with engine running and you cannot pull the parking brake apart, your master cylinder may be the defective unit; warranty.',
    legalDisclaimer: 'This guide was generated by GarageIQ\'s AI from publicly available sources. Some information may be incorrect or out of date. Verify torque specifications and any safety-critical procedures against the OEM service manual for your vehicle before performing work. The Haynes Manual tab on this page links to the licensed manual for your vehicle. GarageIQ is not a substitute for a qualified mechanic. By using this guide you agree to GarageIQ\'s Terms of Service and assume responsibility for the work performed.',
    manualSource: {
      title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)',
      url: 'https://www.haynes.com/en-us/wrangler/jeep/2003',
      notes: 'Procedure sourced from public catalogs and forums; cross-reference torques and any year-specific clutch hydraulic configurations against your licensed manual.',
    },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — CYLINDER HEAD R&R — 4.0L I6
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-cylinder-head-rr-4pt0l-i6',
    title: 'Cylinder Head Removal & Installation (4.0L I6)',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [{ startYear: 1997, endYear: 2006 }],
      engine: '4.0l-i6',
    },
    video: {
      youtubeId: 'tv_dIe42nnw',
      title: 'Removing Wrangler 4.0 Head',
      channel: 'Living Offroad Life',
    },
    source: 'Generated by GarageIQ AI from publicly available sources (Mopar parts catalog, Felpro head gasket installation literature, 4.0L I6 service references, NHTSA TSBs, owner\'s manual, technician forums and Wrangler community service threads). Cross-checked and structured per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Engine',
    difficulty: 'advanced',
    duration: '6 - 10 hours (experienced); 10 - 16 hours (first-timer, including head shop time)',
    interval: 'As required for head gasket failure (coolant in oil, exhaust gases in coolant, white smoke from exhaust), valve job, head crack repair, or major top-end work. Not a maintenance interval.',
    description:
      'Removing the 4.0L I6 cylinder head is the gateway to head gasket replacement, valve service, and head crack repair. The 4.0L head is iron, heavy (~80 lbs), and bolted to the block with 14 head bolts in a torque-to-yield pattern that MUST be replaced when the head comes off (single-use bolts). The 4.0L is non-interference at TDC, so timing chain damage during head removal is unlikely if you follow the procedure. This guide assumes the engine stays in the vehicle. Plan multiple days: day one for teardown, day two (or more) for head shop work and reinstall.',
    overview: {
      difficulty: 'Advanced — many connection points, heavy head, torque-to-yield head bolts (single use), valve timing critical on reassembly',
      estimatedTime: 'Plan 2-3 days: 1 day teardown, 1-2 days at machine shop for head inspection/work, 1 day reinstall',
      estimatedCost: '$80-150 head gasket set; $40-80 head bolts (must be new); $100-300 head shop work (pressure test, valve job if needed)',
      whyMatters: 'Head gasket failure is an existential repair on a 4.0L. Done correctly, the engine returns to factory-fresh top end and continues for many more decades. Done incorrectly, you are doing it again in a year.',
      safetyNote: 'Cylinder head weighs ~80 lbs — use a helper or an engine hoist for removal/install. The exhaust manifold may be hot — let it cool. Coolant is toxic to pets — clean any spills. Used motor oil and coolant are hazardous waste. Engine bay has many sharp edges and clamps.',
    },
    tools: [
      'Engine hoist or two helpers (head is heavy and awkward)',
      'Torque wrench capable of 110 ft-lbs (149 Nm)',
      'Torque-angle meter (for the angle-torque step on head bolts)',
      'Socket set: metric (10mm-22mm)',
      'Long extensions and swivel adapters',
      'Spark plug socket (5/8" or 13/16" depending on application)',
      'Drain pan (for coolant)',
      'Brake cleaner (for cleaning gasket surfaces)',
      'Gasket scraper or razor blade',
      'Straight edge and feeler gauges (for head warpage check)',
      'Plastic bags and labels (for organizing fasteners)',
      'Camera or phone (extensive photos)',
      'Engine support bar (over fender, supports engine if motor mount work needed)',
      'Disposable nitrile gloves and eye protection',
    ],
    parts: [
      {
        id: 'head-gasket-set-4pt0l',
        name: 'Cylinder Head Gasket Set (4.0L I6)',
        partNumber: 'Felpro HS9075PT-1 (head gasket set with intake, exhaust, valve cover gaskets and seals) — equivalents: Mahle HS54450 / Victor Reinz 02-10325-01',
        quantity: 1,
        price: 100,
        category: 'required',
        notes: 'Buy a complete head set, not just the head gasket. Includes intake/exhaust manifold gaskets, valve cover gasket, and necessary seals. Brand matters — Felpro and Mahle are top tier.',
      },
      {
        id: 'head-bolts-4pt0l',
        name: 'Cylinder Head Bolts (one-time use, set of 14)',
        partNumber: 'Mopar 53020476 (set) — equivalents: ARP 145-3603 (premium reusable studs)',
        quantity: 1,
        price: 60,
        category: 'required',
        notes: 'OEM head bolts on the 4.0L are torque-to-yield and MUST be replaced. Some builders upgrade to ARP studs which are reusable and stronger.',
      },
      {
        id: 'coolant-head',
        name: 'Engine Coolant',
        partNumber: 'Mopar 5012394AB (HOAT yellow) — match what is in the system',
        quantity: 2,
        price: 12,
        category: 'required',
        notes: 'Plan for 2 gallons of 50/50 mix to refill the cooling system. Match the coolant type currently in the vehicle.',
      },
      {
        id: 'engine-oil-head',
        name: 'Motor Oil for Reassembly',
        partNumber: '5W-30 or 10W-30 — same grade currently in vehicle',
        quantity: 1,
        price: 10,
        category: 'optional',
        notes: 'A small amount of fresh oil is used for assembly lube on cam lobes and lifters. Not a full oil change.',
      },
    ],
    safetyInformation:
      'Engine work involves heavy components, sharp edges, and hot surfaces if the engine has been running. Allow at least 4 hours of cool-down before starting. The cylinder head weighs ~80 lbs — never lift alone; use an engine hoist or two helpers. NEVER reuse torque-to-yield head bolts — they are designed to stretch once and lose clamping force on second installation. Coolant is toxic to pets and wildlife — clean any spills immediately. Working in the engine bay involves many connection points; it is easy to forget a connection. Photograph extensively before disconnecting anything.',
    beforeYouStart: [
      'Allow engine to cool fully (4+ hours minimum)',
      'Disconnect battery negative cable',
      'Have all parts staged: head gasket set, NEW head bolts (or ARP studs), 2 gallons of coolant, head shop relationship for inspection',
      'Photograph the engine from multiple angles BEFORE disconnecting anything — these photos are your guide for reassembly',
      'Drain coolant from the radiator petcock and the block drain plug (located low on the passenger side of the block)',
      'Have an engine hoist or two helpers available for the actual head lift — solo is dangerous',
      'Plan for the head to spend 1-3 days at a machine shop for pressure testing and any needed work',
      'Make a "fastener map" — small bags labeled with location for each bolt removed',
    ],
    steps: [
      {
        number: 1,
        title: 'Drain Coolant and Disconnect Hoses',
        description:
          'Open the radiator petcock at the bottom of the radiator and drain coolant into a clean pan. Open the block drain plug (passenger side of the block, low) for additional drain — about half of the coolant is in the block and won\'t drain through the radiator alone. Disconnect the upper radiator hose from the thermostat housing on the head, the heater hoses from the head, and any other coolant hoses connected to the head.',
        why: 'Removing coolant before head removal prevents flooding when the head lifts off. Block drain captures coolant trapped in the engine.',
        tip: 'Used coolant is hazardous waste — store in a sealed container for proper disposal.',
        commonMistake: 'Skipping the block drain. Coolant pours out everywhere when the head lifts.',
      },
      {
        number: 2,
        title: 'Disconnect Intake and Exhaust Components',
        description:
          'Disconnect: air intake hose at the throttle body, throttle body itself (4 bolts), fuel line at the rail, vacuum lines (label each), all coolant lines crossing the intake, distributor cap and wires, and the exhaust manifold from the head pipe (typically 3 bolts, often rusted — apply penetrating oil before attempting). Loosen and remove the intake manifold from the head; the intake/exhaust manifolds on the 4.0L share the same gasket so both must come off together (they are bolted to each other and to the head). Set them aside on a workbench.',
        why: 'The intake and exhaust manifolds are bolted to the head and must come off to access the head bolts.',
        tip: 'Mark each vacuum line and electrical connector with masking tape and a Sharpie. Reassembly is much easier with labels.',
        commonMistake: 'Forgetting to disconnect a sensor wire and yanking the wire when removing the manifold.',
      },
      {
        number: 3,
        title: 'Remove Spark Plugs, Valve Cover, and Rocker Assembly',
        description:
          'Remove all six spark plugs (5/8" or 13/16" socket). Set them aside in cylinder order — they reveal valuable diagnostic information about the cylinder condition. Remove the valve cover (ten bolts, perimeter of the cover). Inspect the head visually with the valve cover off — look for sludge, varnish, or damage. Remove the rocker shaft assembly (held by 7 bolts on the 4.0L) and set it aside. Pull the pushrods straight up and out — keep them in cylinder order in a labeled tray.',
        why: 'Pushrods in the wrong cylinder cause valvetrain damage on restart. Order matters.',
        tip: 'A piece of cardboard with 12 holes labeled "1-intake, 1-exhaust, 2-intake, 2-exhaust..." through cylinder 6 keeps pushrods organized.',
        commonMistake: 'Mixing up pushrod order. Bent valve or cam follower damage on first crank.',
      },
      {
        number: 4,
        title: 'Loosen Head Bolts in Reverse Sequence',
        description:
          'The 4.0L head has 14 bolts. Loosen them in REVERSE of the tightening sequence — start at the outside and work toward the center. The OEM tightening sequence is published; loosening in reverse prevents head warpage. Loosen each bolt 1/4 turn at a time in two passes before fully removing.',
        why: 'Even loosening prevents warpage of the head during removal. Just like assembly, disassembly should be even and progressive.',
        tip: 'A torque-bolt loosening sequence diagram is in the Haynes manual or online — print it before starting.',
        commonMistake: 'Removing bolts one at a time fully. Concentrated load on remaining bolts can warp the head.',
      },
      {
        number: 5,
        title: 'Remove the Cylinder Head',
        description:
          'With all 14 head bolts out, the head is held only by friction with the gasket and dowel pins. Confirm everything is disconnected (look for any forgotten wire, hose, or bracket). Use an engine hoist or two helpers to lift the head straight up off the block. The head weighs ~80 lbs and is awkward — use mechanical aids.',
        why: 'Lifting the head straight up clears the dowel pins and gasket without scraping the deck surface.',
        tip: 'Place a wood block on the floor near the engine bay — the head goes there for inspection. Avoid setting it directly on the floor.',
        commonMistake: 'Lifting at an angle. Drags across deck and damages the gasket surface.',
      },
      {
        number: 6,
        title: 'Inspect and Send Head to Machine Shop',
        description:
          'Inspect the removed head: check for cracks (especially around exhaust ports and combustion chambers), warpage with a straight edge across the deck (more than 0.003" warpage means resurface), valve seat condition, and visible damage. Take the head to a machine shop for: pressure test (catches small cracks not visible to the eye), valve seat inspection, valve job if needed, and resurfacing if warped. Budget 1-3 days at the shop and $100-300 depending on what work is needed.',
        why: 'A cracked or warped head will not seal even with new bolts and gasket. The machine shop has the tools to verify the head is reusable.',
        tip: 'Ask the machine shop to magnetic-particle inspect the head — best crack detection method on iron heads.',
        commonMistake: 'Skipping pressure test. Cracks in the water jacket cause coolant loss after reassembly.',
      },
      {
        number: 7,
        title: 'Clean Block Deck and Bolt Holes',
        description:
          'With the head off, clean the block deck surface meticulously. Use a gasket scraper (NOT a metal scraper that gouges aluminum — but the block is iron, so a scraper is fine; use plastic if uncertain) to remove all old gasket material. Then wipe with brake cleaner and a lint-free rag. Run a thread chaser (NOT a tap) down each head bolt hole to clean the threads — debris in the holes will give a false torque reading. Wipe each hole dry with brake cleaner on a small brush.',
        why: 'Even tiny gasket residue or thread debris causes leaks or false torque readings. The deck must be perfectly clean.',
        tip: 'A piece of duct tape over the cylinder bores during cleaning prevents debris from falling into the cylinders.',
        commonMistake: 'Using a tap (which removes metal) instead of a chaser (which only cleans threads). Damages the threads.',
      },
      {
        number: 8,
        title: 'Install New Head Gasket and Lower Head',
        description:
          'Position the new head gasket on the block deck with the correct orientation (most have "TOP" or "FRONT" markings). Confirm the dowel pins are in the block. Lower the (machined-and-cleaned) head onto the gasket using the engine hoist or helpers — straight down, no scraping. Confirm the head is flat against the gasket with no gap.',
        why: 'Correct gasket orientation matters — installed backwards, the coolant and oil passages don\'t align and the engine self-destructs in seconds.',
        tip: 'Align the head with the dowel pins first — once on the dowels, it can only go one way.',
        commonMistake: 'Installing the gasket upside down. Engine fails on first start.',
      },
      {
        number: 9,
        title: 'Install Head Bolts and Torque in Sequence',
        description:
          'Hand-thread all 14 NEW head bolts. Following the OEM torque sequence, tighten in three passes: first to 22 ft-lbs (30 Nm), second to 45 ft-lbs (61 Nm), then a third pass of additional 90-degree torque-angle (this is where the bolts stretch into yield). Verify torque sequence and final spec from OEM service manual — the 4.0L sequence and torque-angle method are published.',
        why: 'Three-pass torque builds clamping force evenly. The final torque-angle ensures all bolts stretch equally — this is what creates the gasket clamp.',
        tip: 'A torque-angle gauge is essential for the final pass. Some torque wrenches have one built in; otherwise buy a $20 angle gauge.',
        commonMistake: 'Reusing old head bolts. They are torque-to-yield and will not give correct clamp on second use.',
      },
      {
        number: 10,
        title: 'Reassemble in Reverse Order, Refill, and Verify',
        description:
          'Reinstall: pushrods (in original order), rocker assembly (torque to spec), valve cover, intake/exhaust manifolds (with new gaskets), spark plugs, distributor cap and wires, fuel line, vacuum lines, all coolant hoses, throttle body, air intake. Refill cooling system with fresh 50/50 coolant. Reconnect battery. Start engine and immediately verify: oil pressure (should be 30-60 psi at idle), coolant temperature stable, no exhaust smoke, no oil/coolant mixing in dipstick or radiator. Run engine for 15 minutes at varying RPM to check for any leaks. Allow to cool fully, retorque head bolts per OEM (some manuals call for retorque after first heat cycle).',
        why: 'Initial verification is critical — many failure modes show up in the first 15 minutes. Catch them before driving in traffic.',
        tip: 'Check oil and coolant levels after the first drive cycle and again after 100 miles. Some settling is normal.',
        commonMistake: 'Driving in traffic before initial verification. A new head gasket failure can cause sudden coolant loss and engine damage.',
      },
    ],
    torqueSpecifications: [
      {
        fastener: 'Cylinder head bolts (4.0L I6, 14 bolts, NEW each time)',
        specification: 'Pass 1: 22 ft-lbs (30 Nm). Pass 2: 45 ft-lbs (61 Nm). Pass 3: +90 degrees angle torque. Verify exact spec and sequence from OEM service manual.',
        notes: 'TORQUE-TO-YIELD — single use only. Must replace bolts every removal. Follow OEM tightening sequence exactly.',
      },
      {
        fastener: 'Rocker shaft assembly bolts (7)',
        specification: '21 ft-lbs (28 Nm). Verify torque spec from OEM service manual.',
        notes: 'Cross-tighten in star pattern.',
      },
      {
        fastener: 'Intake/exhaust manifold bolts',
        specification: '23 ft-lbs (31 Nm). Verify torque spec from OEM service manual.',
        notes: 'Cross-tighten center to outside.',
      },
      {
        fastener: 'Valve cover bolts',
        specification: '85 in-lbs (9.6 Nm). Verify torque spec from OEM service manual.',
        notes: 'Low torque — do not over-tighten.',
      },
      {
        fastener: 'Spark plugs',
        specification: '13 ft-lbs (18 Nm). Verify torque spec from OEM service manual.',
        notes: 'Snug; do not strip the head.',
      },
    ],
    fluidSpecifications: [
      {
        fluid: 'Engine coolant',
        type: 'HOAT (Mopar 5012394AB OEM yellow) — match existing',
        capacity: 'Total cooling system: ~10 quarts. Plan for 2 gallons of 50/50 premixed.',
        notes: 'Bleed cooling system after refill — the 4.0L can airlock if not bled.',
      },
    ],
    troubleshooting: [
      {
        problem: 'Engine misfires after head install',
        likelyCause: 'Pushrod in wrong position, sticking lifter, loose rocker, or bent valve',
        whatToCheck: 'Confirm pushrod order; check rocker torque; compression test',
        fix: 'Reorganize pushrods to original order. Replace bent valves at machine shop. Diagnose lifter issues.',
      },
      {
        problem: 'White smoke from exhaust after install',
        likelyCause: 'Coolant in cylinder from head gasket misalignment or cracked head',
        whatToCheck: 'Pull spark plugs; look for steam-cleaning of one cylinder',
        fix: 'Pressure test the cooling system. If a single cylinder is wet, head gasket may be reversed or head is cracked. Remove and inspect.',
      },
      {
        problem: 'Coolant level drops after first drive',
        likelyCause: 'Air pocket in cooling system being purged, or actual leak',
        whatToCheck: 'Inspect every coolant connection; perform pressure test',
        fix: 'If pressure test passes, top off and continue monitoring. If test fails, locate and repair leak.',
      },
      {
        problem: 'Oil pressure low after install',
        likelyCause: 'Pushrod / lifter not seated, or oil pickup tube disturbed',
        whatToCheck: 'Verify oil pressure at hot idle (30+ psi); inspect for leaks',
        fix: 'If pressure is below 30 psi, stop running and diagnose. May need oil pan removal to inspect pickup.',
      },
      {
        problem: 'Engine knocks or pings under load',
        likelyCause: 'Head was milled too aggressively (compression too high) or wrong gasket thickness',
        whatToCheck: 'Verify head gasket thickness was matched to head milling amount; check ignition timing',
        fix: 'May require a thicker head gasket if head was milled. Consult machine shop on combustion chamber volume.',
      },
    ],
    proTips: [
      'Take the head to a machine shop you trust — pressure test plus mag-particle inspection is the gold standard.',
      'Buy ARP head studs if you can afford them — reusable, stronger, and give more accurate torque readings than torque-to-yield bolts.',
      'Photograph EVERYTHING. Engine bay reassembly without photos is a memory game.',
      'Do not skip the cooling system bleed — the 4.0L can develop a thermostat-housing air pocket that causes overheating.',
      'After 500 miles, retorque the rocker shaft bolts and check valve cover bolts — settling can occur.',
      'A small amount of assembly lube (Lucas or similar) on cam lobes and lifter tops protects during initial start-up before oil pressure builds.',
    ],
    commonMistakes: [
      {
        mistake: 'Reusing torque-to-yield head bolts',
        consequence: 'Bolts stretched once cannot give correct clamping force a second time. Gasket failure within months.',
      },
      {
        mistake: 'Installing the head gasket upside down',
        consequence: 'Coolant and oil passages misalign. Engine self-destructs in seconds at startup.',
      },
      {
        mistake: 'Mixing up pushrod order',
        consequence: 'Wrong pushrod length on a cylinder bends a valve or breaks a rocker.',
      },
      {
        mistake: 'Skipping head pressure test at machine shop',
        consequence: 'Hidden crack causes coolant loss after reassembly. Job has to be redone.',
      },
      {
        mistake: 'Tightening head bolts in wrong sequence',
        consequence: 'Head warpage and gasket failure. Must redo the entire job.',
      },
    ],
    successIndicators: [
      'Engine starts cleanly with no exhaust smoke',
      'Oil pressure 30-60 psi at idle, climbs with RPM',
      'No coolant leaks at any connection after 15 minutes of running',
      'No oil-and-coolant emulsion on the dipstick or in the radiator',
      'Steady idle with no misfires',
      'Coolant temperature stabilizes at thermostat opening temp (typically 195°F)',
      'No knocking, pinging, or rough running under load',
    ],
    whenToSeekHelp:
      'If the head fails the pressure test at the machine shop and is cracked, replacement may be required — used 4.0L heads are available but require pressure testing themselves. If the engine misfires after reassembly and basic diagnostics do not find the cause, a compression test or leak-down test reveals which cylinder has an issue. If oil and coolant mix after install (milky dipstick), the head gasket is not sealing or the head has an undetected crack — pull and reinspect. Many shops will install a customer-supplied head and gasket for half the cost of a complete job.',
    legalDisclaimer: 'This guide was generated by GarageIQ\'s AI from publicly available sources. Some information may be incorrect or out of date. Verify torque specifications and any safety-critical procedures against the OEM service manual for your vehicle before performing work. The Haynes Manual tab on this page links to the licensed manual for your vehicle. GarageIQ is not a substitute for a qualified mechanic. By using this guide you agree to GarageIQ\'s Terms of Service and assume responsibility for the work performed. Used coolant and motor oil are hazardous waste — dispose at an auto-parts recycling center.',
    manualSource: {
      title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)',
      url: 'https://www.haynes.com/en-us/wrangler/jeep/2003',
      notes: 'Procedure sourced from public catalogs and forums; cross-reference torque sequences, head bolt specs, and any year-specific variations against your licensed manual before service.',
    },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — TIMING CHAIN REPLACEMENT — 4.0L I6
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-timing-chain-replacement-4pt0l-i6',
    title: 'Timing Chain Replacement (4.0L I6)',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [{ startYear: 1997, endYear: 2006 }],
      engine: '4.0l-i6',
    },
    video: {
      youtubeId: 'Tlarxiw85wQ',
      title: 'DIY Jeep 4.0 Timing Chain How To',
      channel: 'BSK Garage',
    },
    source: 'Generated by GarageIQ AI from publicly available sources (Mopar parts catalog, Cloyes timing chain literature, 4.0L I6 service references, NHTSA TSBs, owner\'s manual, technician forums and Wrangler community service threads). Cross-checked and structured per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Engine',
    difficulty: 'advanced',
    duration: '4 - 6 hours (experienced); 6 - 10 hours (first-timer)',
    interval: 'Replace when chain stretch is detected (loud rattle on cold start, retarded valve timing, or chain noise at idle). Most 4.0L chains last 200,000+ miles but stretch slowly; symptoms of stretch include hard cold start and check-engine codes for cam/crank correlation.',
    description:
      'The 4.0L I6 uses a single-row timing chain driven from the crankshaft sprocket to the camshaft sprocket. The chain is housed under the front timing cover, which is bolted to the front of the engine block. Replacement is a moderately involved job: remove harmonic balancer, water pump, timing cover, install new chain and sprockets aligned to the timing marks, reseal everything. The 4.0L is a non-interference engine at TDC, so a chain that jumps a tooth or even snaps does not bend valves — but the engine will not run correctly. This guide walks the procedure with the engine in the vehicle.',
    overview: {
      difficulty: 'Advanced — front-of-engine teardown with critical timing alignment; involves harmonic balancer puller and water pump removal',
      estimatedTime: '4-6 hours; first-timer plan a full weekend',
      estimatedCost: '$80-130 for a quality chain set (Cloyes True Roller); $30-50 water pump replacement (recommended while you are in there); $20-40 timing cover gasket and seal',
      whyMatters: 'A worn chain causes retarded cam timing — symptoms include hard starting, lower fuel economy, and check-engine codes (P0344 / P1391 cam/crank correlation). New chain restores precision timing and engine performance.',
      safetyNote: 'Engine work — let cool fully (4+ hours). Harmonic balancer puller bolts thread into the crankshaft — strip them and the crank is damaged. Use only the correct puller and apply force gradually. Coolant is toxic to pets — clean spills.',
    },
    tools: [
      'Floor jack and jack stands',
      'Lug wrench / 19mm socket',
      'Socket set: metric (10mm-22mm)',
      'Long extensions and swivel adapters',
      'Harmonic balancer puller (3-jaw or threaded type)',
      'Crankshaft holding tool OR breaker bar wedged against frame',
      'Torque wrench capable of 130 ft-lbs (176 Nm) for the balancer bolt',
      'Drain pan',
      'Brake cleaner',
      'Gasket scraper or razor blade',
      'RTV silicone (gear-oil compatible — Permatex Ultra Black)',
      'Plastic bags and labels',
      'Camera or phone',
      'Disposable nitrile gloves and eye protection',
    ],
    parts: [
      {
        id: 'timing-chain-set-4pt0l',
        name: 'Timing Chain Set (Cloyes 9-3505SA — chain + cam sprocket + crank sprocket)',
        partNumber: 'Cloyes 9-3505SA (true roller) — equivalents: Mopar 53008275 / Sealed Power KT3-242S',
        quantity: 1,
        price: 95,
        category: 'required',
        notes: 'Buy the complete set with both sprockets, not just the chain. Cloyes True Roller is a commonly recommended upgrade; OEM-style is also fine.',
      },
      {
        id: 'timing-cover-gasket',
        name: 'Timing Cover Gasket Set (cover gasket + crank seal)',
        partNumber: 'Felpro TCS46050 or Mopar 53020743',
        quantity: 1,
        price: 25,
        category: 'required',
        notes: 'Includes the rubber cover gasket and the front crankshaft seal. Replace both whenever the cover comes off.',
      },
      {
        id: 'water-pump-with-chain',
        name: 'Water Pump (recommended replacement during chain service)',
        partNumber: 'Mopar 53020869 — equivalents: Gates 41010 / ACDelco 252-700',
        quantity: 1,
        price: 35,
        category: 'optional',
        notes: 'Strongly recommended — water pump is bolted to the timing cover, so service while everything is open. Adds $35 to the parts cost; saves several hours of future labor.',
      },
      {
        id: 'serpentine-belt-chain',
        name: 'Serpentine Belt (replace if old)',
        partNumber: 'Gates K060840 / Mopar 5066842AA',
        quantity: 1,
        price: 25,
        category: 'optional',
        notes: 'Belt comes off for this job — replace if showing cracks or glazing.',
      },
      {
        id: 'coolant-chain',
        name: 'Engine Coolant',
        partNumber: 'Mopar 5012394AB (HOAT yellow) — match what is in the system',
        quantity: 1,
        price: 12,
        category: 'required',
        notes: '1-2 gallons of 50/50 mix to refill if water pump is replaced. If only chain is being replaced, may not need to drain.',
      },
    ],
    safetyInformation:
      'Allow engine to cool for 4+ hours before work. Disconnect battery negative cable. The harmonic balancer is heavy and the crankshaft bolt requires high torque to remove and install — use a proper balancer puller and a quality torque wrench. The engine cooling fan can rotate suddenly if the engine is bumped while leaning over it. Coolant is toxic to pets and wildlife — clean spills. Allow the timing cover RTV to cure properly before refilling coolant — uncured RTV can be displaced by oil and coolant.',
    beforeYouStart: [
      'Allow engine to cool fully (4+ hours)',
      'Disconnect battery negative cable',
      'Have all parts staged: chain set, gasket set, RTV silicone, water pump if replacing',
      'Photograph the engine front from multiple angles before disassembly',
      'Drain coolant from the radiator petcock if water pump is being replaced',
      'Have a way to hold the crankshaft from rotating during balancer bolt removal — a pneumatic impact gun is the easy method; otherwise use a strap wrench or wedge a breaker bar',
      'Have a torque-angle gauge for the harmonic balancer install (if torque-angle method is used per OEM)',
    ],
    steps: [
      {
        number: 1,
        title: 'Drain Coolant and Remove Cooling Components',
        description:
          'If replacing the water pump, drain coolant from the radiator petcock into a clean pan. Remove the radiator shroud, the cooling fan, and the serpentine belt (mark belt routing first or use a cell phone photo). Remove the upper and lower radiator hoses from the water pump if applicable. Pull the radiator forward or fully out for clearance to the harmonic balancer.',
        why: 'Front access is essential for harmonic balancer and timing cover work. Removing the radiator gives full clearance.',
        tip: 'Loosening the radiator and tilting it forward (without full removal) sometimes provides enough clearance and saves time.',
        commonMistake: 'Trying to work without enough front clearance. Slows the job dramatically.',
      },
      {
        number: 2,
        title: 'Remove Crankshaft Pulley (Harmonic Balancer)',
        description:
          'The harmonic balancer is bolted to the front of the crankshaft with a single large bolt (typically 13/16" or 3/4" — confirm size). Hold the crankshaft from rotating using an impact gun, a strap wrench around the balancer, or a breaker bar wedged against the frame. Once the bolt is out, attach a balancer puller (3-jaw or threaded — the threaded type uses the holes in the balancer face and is preferred). Tighten the puller bolt slowly to draw the balancer off the crank snout. The balancer should come off in 3-5 minutes of slow tension.',
        why: 'The harmonic balancer is press-fit on the crank snout and requires a proper puller. Attempting to pry it off damages the crank and the balancer.',
        tip: 'A drop of penetrating oil between the balancer and crank snout helps the puller. Apply 30 minutes before pulling.',
        commonMistake: 'Trying to pry the balancer off. Damages both the balancer and the crank — and the crank is part of the engine block.',
      },
      {
        number: 3,
        title: 'Remove the Timing Cover',
        description:
          'The timing cover is bolted to the front of the engine block with multiple small bolts (typically 8-10mm) and is also bolted to the oil pan at the bottom (this means oil pan gasket may need to be partially separated — see notes below). Remove all timing cover bolts. The cover is sealed to the block with RTV and may need a gentle tap to break the seal. Lift the cover off carefully — coolant may dribble out if water pump is integrated.',
        why: 'The timing cover encloses the timing chain and sprockets. It must come off for chain access.',
        tip: 'Note that the timing cover meets the oil pan gasket at the bottom — be careful not to tear the oil pan gasket. Some technicians cut a small section of pan gasket and re-RTV that area; others drop the front of the oil pan slightly. Either is acceptable.',
        commonMistake: 'Forcing the cover off without breaking the RTV seal. Bends the cover.',
      },
      {
        number: 4,
        title: 'Inspect the Old Chain and Note Stretch',
        description:
          'With the cover off, the timing chain is visible. Inspect: chain looseness (push down on the slack side — more than 1/2" deflection means severe stretch), sprocket teeth wear (should be even and undamaged), and chain guide condition (4.0L has a tensioner spring on some years). Compare the old chain length laid flat to the new — visible stretch is a problem.',
        why: 'Inspection confirms why you are doing the job and reveals any other components needing replacement.',
        tip: 'Hold the chain at top and bottom — it should not deflect more than 1/4 inch. More than that and timing accuracy is lost.',
        commonMistake: 'Skipping inspection. May miss a loose tensioner or bent guide that affects new chain life.',
      },
      {
        number: 5,
        title: 'Set Engine to TDC #1 and Note Sprocket Marks',
        description:
          'Rotate the crankshaft using a socket on the front of the cam sprocket (or via the now-removed harmonic balancer bolt back in temporarily) until cylinder #1 is at TDC on compression stroke. The crank sprocket has a timing mark (typically a chamfered tooth or a punch mark) that points up. The cam sprocket has a corresponding mark that should point down (toward the crank sprocket mark). Both marks should align between the two sprockets. Photograph this alignment — it is the reference for new chain install.',
        why: 'Setting the engine to TDC #1 with marks aligned ensures the new chain is installed with correct cam timing. Without this, the engine will not run.',
        tip: 'Use the harmonic balancer bolt with a long socket as a turning handle. Or remove the spark plugs to make rotation easier.',
        commonMistake: 'Installing the new chain without verifying cam timing alignment first. Engine will not run, or will run very poorly.',
      },
      {
        number: 6,
        title: 'Remove Cam Sprocket Bolt and Old Chain',
        description:
          'Remove the cam sprocket bolt (typically 13/16" — single bolt at center of cam sprocket). Pull the cam sprocket off the cam dowel pin — it should slide off with light wiggling. The chain comes off with the sprocket. The crank sprocket may need to be pulled off the crank snout with a 3-jaw puller (similar to the harmonic balancer process but smaller).',
        why: 'Both sprockets and the chain come out as a unit on most installations. Some technicians replace only the chain if sprockets show no wear, but matched-set replacement is preferred.',
        tip: 'A small magnetic dish under the work area catches the cam bolt and any other dropped fasteners.',
        commonMistake: 'Dropping the cam bolt into the engine when removing. It can lodge in the chain area or down through the timing cover gap.',
      },
      {
        number: 7,
        title: 'Install New Sprockets and Chain — Verify Timing Marks',
        description:
          'Slip the new chain over both sprockets BEFORE installing them on the engine. Position the cam sprocket so its timing mark points down and the crank sprocket so its mark points up. Now lower the assembly onto the crankshaft and camshaft simultaneously, keeping the marks aligned. The sprockets should slide onto their respective shafts. If timing marks do not align after install, remove and reposition.',
        why: 'Pre-assembling the chain on both sprockets makes alignment easier. Once on the engine, marks alignment is the verification.',
        tip: 'If the cam sprocket does not slide on cleanly, the cam dowel pin may need to be repositioned or the camshaft rotated slightly. Do this gently.',
        commonMistake: 'Misaligned timing marks. Engine will not start or will run very rough.',
      },
      {
        number: 8,
        title: 'Torque Cam Sprocket and Verify Timing',
        description:
          'Install the cam sprocket bolt and torque to spec (typically 50 ft-lbs / 68 Nm). Verify torque spec from OEM service manual. Rotate the crankshaft TWO complete revolutions by hand (using the harmonic balancer bolt as a handle, or a socket on the cam sprocket). After two revolutions, the timing marks should realign exactly. If they do not, the chain is one tooth off — disassemble and reinstall.',
        why: 'Two revolutions verify the timing is correct AND that the chain has full clearance with no binding.',
        tip: 'Mark the chain with a paint pen at the cam sprocket alignment tooth — confirms position visually.',
        commonMistake: 'Skipping the two-revolution verification. Engine starts running on first try and immediately misfires due to one tooth off.',
      },
      {
        number: 9,
        title: 'Reinstall Timing Cover with New Gasket and Seal',
        description:
          'Clean the block and timing cover sealing surfaces with brake cleaner. Apply a thin bead of RTV silicone around the timing cover sealing surface (gear-oil compatible RTV). Place the new gasket on the cover. Lift the cover into position and start all bolts hand-tight first. Cross-tighten in star pattern to spec. Install the new front crankshaft seal in the timing cover (if not pre-installed in the gasket set) using a seal driver. Reinstall the harmonic balancer onto the crank snout — use anti-seize on the bolt threads. Torque to OEM spec (typically 80 ft-lbs initial + torque-angle, or 130 ft-lbs final). Verify torque spec from OEM service manual.',
        why: 'Cover sealing is critical — uncured RTV or skipped seal causes immediate leak. Harmonic balancer torque is high to prevent slip.',
        tip: 'Allow the RTV to cure 30 minutes before refilling oil and coolant. Some RTVs require 1+ hour.',
        commonMistake: 'Refilling fluids before RTV cures. Fluid displaces RTV and the seal fails immediately.',
      },
      {
        number: 10,
        title: 'Reassemble and Verify',
        description:
          'Reinstall water pump if replaced (with new gasket), serpentine belt, radiator and shroud, cooling fan, and any disturbed connections. Refill coolant. Reconnect battery. Start engine — should fire on first try with smooth idle. Check for any oil or coolant leaks at the timing cover. Allow the engine to reach full operating temperature and verify cooling system bleeds correctly (no air pockets).',
        why: 'First start verifies the timing is correct. Smooth idle = correct chain alignment.',
        tip: 'Listen carefully on first start for any abnormal noise. A new chain should be silent; a chain noise typically means tensioner issue.',
        commonMistake: 'Rushing the first start. Always do a full visual check before cranking.',
      },
    ],
    torqueSpecifications: [
      {
        fastener: 'Harmonic balancer (crankshaft pulley) bolt',
        specification: '80 ft-lbs (108 Nm) initial torque + torque angle per OEM, or 130 ft-lbs (176 Nm) final. Verify torque spec from OEM service manual.',
        notes: 'High torque — essential to prevent balancer slip. Apply anti-seize to threads.',
      },
      {
        fastener: 'Camshaft sprocket bolt',
        specification: '50 ft-lbs (68 Nm). Verify torque spec from OEM service manual.',
        notes: 'Standard torque; cam dowel pin keeps sprocket alignment.',
      },
      {
        fastener: 'Timing cover bolts',
        specification: '15-25 ft-lbs (20-34 Nm) — varies by bolt size. Verify spec from OEM service manual.',
        notes: 'Cross-tighten in star pattern.',
      },
      {
        fastener: 'Water pump bolts (if replaced)',
        specification: '24 ft-lbs (33 Nm). Verify torque spec from OEM service manual.',
        notes: 'Cross-tighten.',
      },
    ],
    fluidSpecifications: [
      {
        fluid: 'Engine coolant (top-off or refill if water pump replaced)',
        type: 'HOAT (Mopar 5012394AB OEM yellow) — match existing',
        capacity: 'Full system: ~10 quarts. Top-off only: 1-2 quarts.',
        notes: 'Bleed cooling system after refill.',
      },
    ],
    troubleshooting: [
      {
        problem: 'Engine will not start after install',
        likelyCause: 'Timing marks misaligned (chain off by one tooth)',
        whatToCheck: 'Pull harmonic balancer back off, remove timing cover, verify marks alignment at TDC #1',
        fix: 'Reposition chain on sprockets to correct alignment. Repeat two-revolution verification.',
      },
      {
        problem: 'Engine starts but runs rough / has misfires',
        likelyCause: 'Cam timing slightly off, or fuel/spark connection forgotten during reassembly',
        whatToCheck: 'Verify all spark plug wires, fuel injector connectors, and intake hoses are reconnected',
        fix: 'Reconnect any missed components. If still rough, recheck cam timing alignment.',
      },
      {
        problem: 'Oil leak from timing cover',
        likelyCause: 'RTV not allowed to cure before refilling oil; gasket pinched during install; cover bolt missed',
        whatToCheck: 'Visual inspection of timing cover seam',
        fix: 'Drain oil, remove cover, reseal carefully, allow full cure, refill.',
      },
      {
        problem: 'Coolant leak from timing cover area',
        likelyCause: 'Water pump gasket leaking or block coolant passage misaligned with cover',
        whatToCheck: 'Pressure test cooling system; visual inspection',
        fix: 'Pull cover, inspect water pump gasket; reposition if displaced.',
      },
      {
        problem: 'Loud rattle on cold start (similar to original symptom)',
        likelyCause: 'Loose timing chain — installed without proper tension, or chain set was already at limit',
        whatToCheck: 'Confirm chain set was correct part; verify chain has correct tension',
        fix: 'May indicate tensioner mechanism is worn — replace tensioner. Or chain set is wrong application.',
      },
    ],
    proTips: [
      'Replace the water pump while the cover is off — it is bolted to the timing cover and saves a future tear-down.',
      'Use a Cloyes True Roller chain set if you want the longest service life — a small premium over OEM-style.',
      'Photograph the engine front before, during, and after each step. Reassembly is much easier with photos.',
      'Allow RTV to cure before refilling oil and coolant. The 30-minute wait prevents seal failures.',
      'Always do the two-revolution verification of timing marks after install. Catches one-tooth-off mistakes before you crank.',
      'A torque-angle gauge ($20 tool) is essential for the harmonic balancer torque-angle step. Cheap insurance.',
    ],
    commonMistakes: [
      {
        mistake: 'Skipping timing mark verification after install',
        consequence: 'Engine starts but runs poorly. Or refuses to start. Job has to be partially redone.',
      },
      {
        mistake: 'Trying to pry the harmonic balancer off without a puller',
        consequence: 'Damaged balancer and crank snout. Crank snout damage may require engine work.',
      },
      {
        mistake: 'Refilling oil and coolant before RTV cures',
        consequence: 'Seal fails immediately. Major leak from timing cover.',
      },
      {
        mistake: 'Using the wrong chain set for application',
        consequence: 'Chain too loose (insufficient tension) or wrong sprocket pitch — engine misfires from day one.',
      },
      {
        mistake: 'Not replacing water pump while cover is off',
        consequence: 'Water pump fails 6-12 months later, requiring a second cover removal.',
      },
    ],
    successIndicators: [
      'Engine starts cleanly on first crank',
      'Smooth idle at correct RPM (~750 RPM warm)',
      'No abnormal noise from front of engine',
      'No oil or coolant leaks at timing cover after operating temp',
      'No misfire, hesitation, or poor performance under load',
      'Check engine light does NOT illuminate after extended driving',
    ],
    whenToSeekHelp:
      'If the engine starts with rough idle and timing marks were verified twice, a more involved diagnosis (compression test, leak-down, scope) may be needed. If the harmonic balancer bolt strips the crankshaft snout threads, professional repair (Heli-Coil or crankshaft replacement) is required. If chain noise persists despite a new chain, the chain tensioner mechanism may be worn — replacement requires another teardown of this depth.',
    legalDisclaimer: 'This guide was generated by GarageIQ\'s AI from publicly available sources. Some information may be incorrect or out of date. Verify torque specifications and any safety-critical procedures against the OEM service manual for your vehicle before performing work. The Haynes Manual tab on this page links to the licensed manual for your vehicle. GarageIQ is not a substitute for a qualified mechanic. By using this guide you agree to GarageIQ\'s Terms of Service and assume responsibility for the work performed.',
    manualSource: {
      title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)',
      url: 'https://www.haynes.com/en-us/wrangler/jeep/2003',
      notes: 'Procedure sourced from public catalogs and forums; cross-reference timing marks, torque specs, and any year-specific variations against your licensed manual.',
    },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — TIMING BELT REPLACEMENT — 2.4L I4 (DOHC)
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-timing-belt-replacement-2pt4l-i4',
    title: 'Timing Belt Replacement (2.4L I4 DOHC)',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [{ startYear: 2003, endYear: 2006 }],
      engine: '2.4l-i4',
    },
    video: {
      youtubeId: 'RWrnBz1r6_I',
      title: 'How to Replace Timing Belt & Water Pump 1997-2006 Jeep Wrangler 2.4L',
      channel: 'TRQ',
    },
    source: 'Generated by GarageIQ AI from publicly available sources (Mopar parts catalog, Gates timing belt installation literature, 2.4L DOHC service references, NHTSA TSBs, owner\'s manual, technician forums and Wrangler community service threads). Cross-checked and structured per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Engine',
    difficulty: 'advanced',
    duration: '3 - 5 hours (experienced); 5 - 8 hours (first-timer)',
    interval: 'Replace every 100,000 miles or 7 years, whichever comes first. ALSO replace any time the belt is removed for related work (water pump, etc.). The 2.4L is an interference engine — a broken belt bends valves.',
    description:
      'The 2.4L I4 (DOHC, also called the PowerTech 2.4 or AMC/Chrysler 2.4) used in 2003-2006 TJs uses a rubber timing belt to drive the dual overhead camshafts off the crankshaft. The belt also drives the water pump on this engine, which is why water pump replacement and timing belt replacement are bundled. CRITICAL: the 2.4L is an interference engine — if the belt breaks or jumps a tooth, the valves contact pistons and bend, requiring head removal and valve replacement. Replacement at the manufacturer\'s interval (100k miles / 7 years) is essential. This guide covers belt, water pump, and tensioner replacement together.',
    overview: {
      difficulty: 'Advanced — interference engine demands precise cam timing. A wrong installation = bent valves = $1,500+ in damage',
      estimatedTime: '3-5 hours; first-timer plan a full day with tools and reference guide ready',
      estimatedCost: '$80-150 for timing belt kit (belt + tensioner + idler + water pump)',
      whyMatters: 'A broken timing belt on a 2.4L destroys the engine. The interval (100k miles or 7 years) is non-negotiable. Doing the job yourself saves $400-700 in labor.',
      safetyNote: 'Interference engine — incorrect cam timing causes catastrophic valve-piston damage on first crank. Verify timing alignment THREE times before final install. Engine work — let cool fully (4+ hours). Crankshaft pulley puller and harmonic balancer access required.',
    },
    tools: [
      'Floor jack and jack stands',
      'Lug wrench / 19mm socket',
      'Engine support bar (over-fender style) OR a floor jack with wood block',
      'Socket set: metric (10mm-22mm)',
      'Long extensions and swivel adapters',
      'Harmonic balancer puller (3-jaw)',
      'Crankshaft holding tool OR breaker bar method',
      'Torque wrench capable of 110 ft-lbs (149 Nm)',
      'Cam timing tool / locking pins (consult OEM specs — some applications need a specific tool)',
      'Drain pan',
      'Brake cleaner',
      'Plastic bags and labels',
      'Camera / phone',
      'Disposable nitrile gloves and eye protection',
    ],
    parts: [
      {
        id: 'timing-belt-kit-2pt4l',
        name: 'Timing Belt Kit (Belt + Tensioner + Idler + Water Pump)',
        partNumber: 'Gates TCKWP295 (kit with water pump) — equivalents: ContiTech TB295WP / Mopar 5066809AA',
        quantity: 1,
        price: 130,
        category: 'required',
        notes: 'ALWAYS buy a complete kit — never just the belt. Tensioner and idler bearings are the same age as the belt and fail together. Water pump is driven by the belt — replace simultaneously.',
      },
      {
        id: 'crank-seal-2pt4l',
        name: 'Crankshaft Front Oil Seal',
        partNumber: 'Mopar 53020743 — equivalent: National 9434',
        quantity: 1,
        price: 12,
        category: 'recommended',
        notes: 'Replace while you have access — small additional cost prevents future leak.',
      },
      {
        id: 'cam-seals-2pt4l',
        name: 'Camshaft Front Oil Seals (intake and exhaust cams)',
        partNumber: 'Mopar 53020450 (each) — equivalent: National 8657S',
        quantity: 2,
        price: 8,
        category: 'recommended',
        notes: 'Two seals (one per cam). Replace while accessing the cam sprockets.',
      },
      {
        id: 'coolant-belt',
        name: 'Engine Coolant',
        partNumber: 'Mopar 5012394AB (HOAT yellow) — match what is in the system',
        quantity: 1,
        price: 12,
        category: 'required',
        notes: 'Water pump replacement requires draining coolant. Plan for 1-2 gallons of 50/50 mix.',
      },
    ],
    safetyInformation:
      'INTERFERENCE ENGINE WARNING: incorrect cam timing on the 2.4L causes immediate valve-to-piston contact on first crank. Bent valves require cylinder head removal, valve replacement, and head shop work — easily $1,500+. Triple-check cam timing alignment before any cranking. Allow engine to fully cool. Crankshaft pulley bolt requires high torque — use proper tools. Disconnect battery negative cable. Coolant is toxic — clean spills.',
    beforeYouStart: [
      'Allow engine to cool fully (4+ hours)',
      'Disconnect battery negative cable',
      'Have all parts staged: complete timing belt kit (belt + tensioner + idler + water pump), crank seal, cam seals, fresh coolant',
      'Read the OEM timing belt procedure for the 2.4L — cam timing alignment specifics differ from V6/V8 engines',
      'Have a way to support the engine when the right-side motor mount is removed (engine support bar over fender, or floor jack with wood block under oil pan)',
      'Drain coolant from the radiator petcock',
      'Photograph engine front before disassembly',
      'Know that the 2.4L is interference — verify cam timing alignment three times before final installation',
    ],
    steps: [
      {
        number: 1,
        title: 'Drain Coolant and Remove Front Components',
        description:
          'Drain the cooling system through the radiator petcock. Remove the cooling fan shroud and fan. Remove the serpentine belt (mark routing first or photo). Remove the upper and lower radiator hoses. Remove the radiator if needed for clearance (most 2.4L jobs require radiator removal for puller access). Remove the engine right-side motor mount (the mount near the timing-belt cover).',
        why: 'Front access is essential. Removing the right-side motor mount lets the engine drop slightly for full timing-belt-cover access.',
        tip: 'Before removing the motor mount, support the engine with a support bar or floor jack. The engine will settle when the mount comes off.',
        commonMistake: 'Removing the motor mount without engine support. Engine drops onto components and can damage them.',
      },
      {
        number: 2,
        title: 'Remove the Crankshaft Pulley (Harmonic Balancer)',
        description:
          'Hold the crankshaft from rotating using an impact gun, strap wrench, or breaker bar wedged. Remove the crankshaft pulley bolt (typically 18mm or 21mm, very high torque). Use a 3-jaw harmonic balancer puller to draw the pulley off the crank snout. The pulley should come off cleanly.',
        why: 'The crankshaft pulley must come off to access the timing belt cover.',
        tip: 'Apply penetrating oil between the pulley hub and the crank snout 30 minutes before pulling.',
        commonMistake: 'Trying to lever the pulley off. Damages the pulley and crank.',
      },
      {
        number: 3,
        title: 'Remove the Timing Belt Cover',
        description:
          'The 2.4L timing belt cover is typically a multi-piece plastic cover bolted to the engine. Remove all cover bolts (typically 8mm). Lift the cover sections off carefully — they may have plastic clips that hold them together. Set them aside in order.',
        why: 'Cover removal exposes the timing belt and sprockets.',
        tip: 'Take a photo of the cover orientation before removal — the multi-piece design can be confusing on reassembly.',
        commonMistake: 'Dropping bolts into the engine bay. Use a magnetic tray.',
      },
      {
        number: 4,
        title: 'Set Engine to TDC #1 Compression and Verify Cam Timing Marks',
        description:
          'Rotate the crankshaft using a socket on the (now-removed) crank pulley bolt placed back in temporarily. Rotate clockwise until cylinder #1 reaches TDC compression. The crank sprocket has a timing mark that should align with a mark on the engine block (or oil pump housing). The intake cam sprocket and exhaust cam sprocket each have timing marks that align with the cylinder head or rear timing belt cover bracket. PHOTOGRAPH THIS ALIGNMENT — it is your single most important reference. Some 2.4L applications use cam locking pins or a locking tool that holds the cams at TDC; consult your OEM specs.',
        why: 'TDC alignment with all timing marks visible is the foundation for correct belt installation. Without it, you cannot reinstall correctly.',
        tip: 'A bright LED flashlight pointed at each timing mark while photographing makes the marks more visible.',
        commonMistake: 'Skipping the photo. Reassembly without a reference is error-prone.',
      },
      {
        number: 5,
        title: 'Loosen Tensioner and Remove Old Belt',
        description:
          'Loosen the timing belt tensioner (typically a single bolt that releases tension via spring or hydraulic action). Once the tensioner releases, the old belt becomes slack and can be slipped off the cam sprockets and crank sprocket. Set the old belt aside. Inspect the old belt for cracks, missing teeth, or oil contamination — informs whether other issues exist.',
        why: 'Removing tension lets the belt slip off without forcing.',
        tip: 'Note the direction of belt rotation — it does not matter for the new belt but may inform inspection of the old one.',
        commonMistake: 'Not loosening the tensioner first. Forcing the belt off can damage cam sprockets.',
      },
      {
        number: 6,
        title: 'Replace Tensioner, Idler Pulleys, and Water Pump',
        description:
          'Remove the old tensioner pulley and any idler pulleys, and install new ones from the kit. Now drain any remaining coolant, remove the old water pump (4-6 bolts, typically 13mm, plus any coolant connection), clean the gasket surface with a scraper and brake cleaner, and install the new water pump with new gasket. Torque to spec. The water pump on the 2.4L is driven by the timing belt, so this is the most efficient time to service it.',
        why: 'All belt-driven components share the same age and service interval. Replacing only the belt without the tensioner/idler/pump leads to a future failure that costs another full belt job.',
        tip: 'A new water pump gasket dry-installs (no RTV). If your kit calls for RTV, follow that — but most modern gaskets do not need it.',
        commonMistake: 'Skipping water pump replacement to save money. Pump fails in 12-24 months and requires another timing belt job.',
      },
      {
        number: 7,
        title: 'Verify Cam Timing One More Time',
        description:
          'Before installing the new belt, re-verify that the cam sprockets and crank sprocket are at their TDC marks. If anything has shifted during tensioner / pulley removal, reposition. Some 2.4L applications use cam locking pins that hold the cams in place — install these if available, or use a locking tool, before proceeding.',
        why: 'Cam timing is the make-or-break step. Triple verification is standard practice.',
        tip: 'If you do not have cam locking pins, mark each cam sprocket\'s position with a paint pen on both the sprocket and the cylinder head — visual reference during install.',
        commonMistake: 'Trusting initial alignment. Things shift during work — verify again.',
      },
      {
        number: 8,
        title: 'Install New Belt Following Cam-to-Crank Path',
        description:
          'Start the new belt at the crankshaft sprocket (with crank still at TDC). Route the belt up and over the intake cam sprocket, around the idler, then over the exhaust cam sprocket, around the tensioner pulley, and back to the crank. The belt typically routes counterclockwise as viewed from the front of the engine — verify against your photos and the OEM specification.',
        why: 'Belt routing must follow the OEM path. Wrong routing = wrong cam timing = bent valves.',
        tip: 'Slow and steady. The belt should go on without forcing — if you need to force it, the cam timing has shifted.',
        commonMistake: 'Forcing the belt onto a misaligned sprocket. Bends a tooth or shifts a cam, which causes interference on first crank.',
      },
      {
        number: 9,
        title: 'Tension the Belt and Verify Cam Marks Again',
        description:
          'Apply tension to the new belt by allowing the tensioner to engage (loosen the lock bolt, let the spring or hydraulic action take up slack, then re-tighten the lock bolt to spec). Verify all timing marks STILL align after tensioning. Rotate the crankshaft TWO complete revolutions by hand using the crank bolt (now temporarily reinstalled). After two full revolutions, ALL timing marks must realign exactly. If any do not, the belt is one tooth off — remove tension, reposition, and try again.',
        why: 'Two-revolution verification confirms the belt is installed correctly with no tooth-off errors. This is the final safety check before reassembly.',
        tip: 'Rotate slowly — you should feel resistance from compression. If you feel hard binding, STOP — interference contact may be occurring. Remove and check immediately.',
        commonMistake: 'Skipping the two-revolution verification. Engine cranks first time and bends valves.',
      },
      {
        number: 10,
        title: 'Reassemble, Refill, and Verify',
        description:
          'Reinstall the timing belt cover, crankshaft pulley (anti-seize on threads, torque to spec — typically 105 ft-lbs / 142 Nm). Verify torque spec from OEM service manual. Reinstall motor mount, serpentine belt, radiator hoses, fan shroud, fan, radiator (if removed). Refill cooling system with fresh 50/50 coolant. Bleed cooling system. Reconnect battery. Start engine — should fire on first crank with smooth idle. Verify no abnormal noise, no misfire, no leaks. Allow to reach operating temperature; verify cooling system bleeds properly.',
        why: 'First start is the moment of truth. Smooth idle = correct cam timing. Misfire or no-start = one tooth off, requiring another teardown.',
        tip: 'Have a multimeter or scan tool ready to read codes if there is any roughness — cam/crank correlation codes (P0344, P1391) indicate timing offset.',
        commonMistake: 'Cranking without final inspection. If you missed anything, the consequences on a 2.4L are severe.',
      },
    ],
    torqueSpecifications: [
      {
        fastener: 'Crankshaft pulley (harmonic balancer) bolt',
        specification: '105 ft-lbs (142 Nm). Verify torque spec from OEM service manual.',
        notes: 'Apply anti-seize to threads. High torque — use a quality wrench.',
      },
      {
        fastener: 'Cam sprocket bolts (NOT removed during this job typically)',
        specification: '85 ft-lbs (115 Nm). Verify torque spec from OEM service manual.',
        notes: 'Most jobs do not remove cam sprockets — but spec is here for reference.',
      },
      {
        fastener: 'Tensioner lock bolt',
        specification: '23 ft-lbs (31 Nm). Verify torque spec from OEM service manual.',
        notes: 'Standard mid-torque.',
      },
      {
        fastener: 'Water pump bolts',
        specification: '24 ft-lbs (33 Nm). Verify torque spec from OEM service manual.',
        notes: 'Cross-tighten in star pattern.',
      },
      {
        fastener: 'Timing cover bolts',
        specification: '85 in-lbs (10 Nm). Verify torque spec from OEM service manual.',
        notes: 'Low torque — plastic cover, do not strip threads.',
      },
    ],
    fluidSpecifications: [
      {
        fluid: 'Engine coolant',
        type: 'HOAT (Mopar 5012394AB OEM yellow) — match existing',
        capacity: 'Full system: ~7 quarts. Plan for 1-2 gallons of 50/50 mix.',
        notes: 'Bleed cooling system after refill.',
      },
    ],
    troubleshooting: [
      {
        problem: 'Engine cranks but does not start',
        likelyCause: 'Cam timing one tooth off, or fuel/spark connection forgotten',
        whatToCheck: 'Verify all electrical connections; if fuel and spark are present, recheck cam timing',
        fix: 'Pull timing belt cover, verify cam-to-crank alignment, reposition belt if off.',
      },
      {
        problem: 'Engine starts but runs very rough / misfires',
        likelyCause: 'Cam timing off by one tooth on either intake or exhaust cam',
        whatToCheck: 'Code-scan for P0344 / P1391 cam/crank correlation codes; verify timing marks',
        fix: 'Reposition belt to correct cam timing.',
      },
      {
        problem: 'Engine starts then immediately knocks / no compression',
        likelyCause: 'Bent valves from interference contact during install',
        whatToCheck: 'Compression test all four cylinders',
        fix: 'Cylinder head removal and valve replacement at machine shop. Major repair.',
      },
      {
        problem: 'Coolant leak from water pump area',
        likelyCause: 'Water pump gasket pinched or surface not clean',
        whatToCheck: 'Visual inspection',
        fix: 'Pull water pump, clean surfaces, reinstall with new gasket.',
      },
      {
        problem: 'Whining noise from front of engine',
        likelyCause: 'Belt tensioner over-tightened, idler pulley bearing failed, or belt installed too tight',
        whatToCheck: 'Verify tensioner bolt torque; spin idler pulleys by hand for smooth motion',
        fix: 'Adjust tensioner to spec; replace idler if rough; reinstall belt with correct routing.',
      },
    ],
    proTips: [
      'NEVER replace just the belt on a 2.4L. Always do the complete kit (belt + tensioner + idler + water pump). Saves a future job.',
      'Buy a quality kit (Gates, ContiTech, AISIN). Cheap kits use lesser bearings that fail prematurely.',
      'Photograph everything before disassembly — and photograph the timing marks alignment specifically. Reassembly without photos is error-prone.',
      'Use cam locking pins if available — they eliminate the risk of cam shift during belt install.',
      'Allow extra time for the two-revolution verification. Rushing this step is what causes bent valves.',
      'After install, check belt tension and timing marks one more time before reassembling the cover. Last chance to catch errors.',
    ],
    commonMistakes: [
      {
        mistake: 'Installing belt one tooth off on a cam sprocket',
        consequence: 'Engine misfires badly or bends valves on interference contact. Major repair.',
      },
      {
        mistake: 'Reusing old water pump or tensioner',
        consequence: 'Component failure within 12-24 months requires another full timing belt job.',
      },
      {
        mistake: 'Skipping the two-revolution verification',
        consequence: 'One-tooth-off error not caught. Engine fails on first crank.',
      },
      {
        mistake: 'Cranking without engine support during motor mount removal',
        consequence: 'Engine sags onto components and can damage exhaust, oil pan, or other parts.',
      },
      {
        mistake: 'Forcing the new belt onto a misaligned sprocket',
        consequence: 'Cam shifts during install; final installation is incorrect.',
      },
    ],
    successIndicators: [
      'Engine starts cleanly on first crank',
      'Smooth idle at correct RPM (~750-850 RPM warm)',
      'No knocking, misfire, or unusual noise',
      'Coolant temperature stabilizes at thermostat opening (typically 195°F)',
      'No leaks at water pump or timing cover',
      'No check engine light or cam/crank correlation codes',
    ],
    whenToSeekHelp:
      'If the engine cranks but does not start after replacement and triple-verifying timing, suspect an electronic issue (camshaft position sensor, ignition module) — diagnostic tools needed. If the engine starts and immediately misfires badly, the belt is one tooth off — pull and reposition. If the engine starts and immediately has zero compression, valves may be bent — cylinder head removal required. The 2.4L is unforgiving; if you have any doubt about your install, get a second opinion before cranking.',
    legalDisclaimer: 'This guide was generated by GarageIQ\'s AI from publicly available sources. Some information may be incorrect or out of date. Verify torque specifications and any safety-critical procedures against the OEM service manual for your vehicle before performing work. The 2.4L is an interference engine — incorrect installation can cause severe engine damage. The Haynes Manual tab on this page links to the licensed manual for your vehicle. GarageIQ is not a substitute for a qualified mechanic. By using this guide you agree to GarageIQ\'s Terms of Service and assume responsibility for the work performed.',
    manualSource: {
      title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)',
      url: 'https://www.haynes.com/en-us/wrangler/jeep/2003',
      notes: 'Procedure sourced from public catalogs and forums; cross-reference cam timing marks and any year-specific 2.4L variations against your licensed manual before service.',
    },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — OIL PAN GASKET REPLACEMENT — 4.0L I6 (TJ-specific)
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-oil-pan-gasket-4pt0l-i6',
    title: 'Oil Pan Gasket Replacement (4.0L I6)',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [{ startYear: 1997, endYear: 2006 }],
      engine: '4.0l-i6',
    },
    video: {
      youtubeId: 'Xrj8oIPYSOQ',
      title: 'Jeep 4.0L oil pan installation with tips and tricks',
      channel: 'Burn Oil',
    },
    source: 'Generated by GarageIQ AI from publicly available sources (Mopar parts catalog, Felpro and Mahle oil pan gasket literature, 4.0L I6 service references, NHTSA TSBs, owner\'s manual, technician forums and Wrangler community service threads). Cross-checked and structured per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Engine',
    difficulty: 'advanced',
    duration: '5 - 8 hours (the front axle interference makes this longer than on most vehicles)',
    interval: 'Replace when oil leak is traced to the oil pan gasket. Symptoms: oil drips visible from the bottom of the engine, oil stain forms on the front axle or skid plate. Often coincides with rear main seal leak diagnosis.',
    description:
      'Oil pan gasket replacement on a TJ 4.0L is a job that LOOKS simple — drop the pan, replace the gasket — but is complicated by the front axle, which sits directly under the oil pan and prevents the pan from being lowered straight down. To replace the gasket, the front axle must be either fully dropped out of the way OR the engine must be raised significantly to clear the axle. Both are involved procedures. This guide covers the most common DIY method: dropping the front axle just enough to clear the pan. It is not a fast job, but it is one of the most empowering oil-leak repairs on a TJ.',
    overview: {
      difficulty: 'Advanced — front axle interference makes this a long, awkward job. Multiple support points, careful sequencing.',
      estimatedTime: '5-8 hours; first-timer plan a full weekend',
      estimatedCost: '$15-30 oil pan gasket; plus 5 quarts of oil and a new filter for the post-job oil change',
      whyMatters: 'Oil pan gasket leak causes ongoing oil loss, makes a mess of the engine and skid plate, and is a frequent diagnosis source on a 4.0L. Repaired correctly, the leak is gone for the rest of the engine\'s life.',
      safetyNote: 'Heavy components (front axle) move during this procedure. Use multiple jack stands and never work under unsupported parts. The front coil springs are under load when the axle is dropped — they can launch if not controlled. Engine support during the work is essential.',
    },
    tools: [
      'Floor jack and four jack stands (frame and axle support)',
      'Engine support bar (over fender) — strongly recommended',
      'Lug wrench / 19mm socket',
      'Socket set: metric (10mm-22mm)',
      'Long extensions and swivel adapters',
      'Coil spring compressor (only needed if dropping axle fully)',
      'Torque wrench capable of 110 ft-lbs (149 Nm)',
      'Drain pan (large)',
      'Brake cleaner (for cleaning gasket surfaces)',
      'Gasket scraper and razor blade',
      'RTV silicone (oil-compatible — Permatex Ultra Black)',
      'Plastic bags and labels',
      'Camera / phone',
      'Disposable nitrile gloves and eye protection',
    ],
    parts: [
      {
        id: 'oil-pan-gasket-4pt0l',
        name: 'Oil Pan Gasket (4.0L I6)',
        partNumber: 'Felpro OS30693R-1 — equivalent: Mahle JV5183 / Mopar 4720751',
        quantity: 1,
        price: 22,
        category: 'required',
        notes: 'Buy a one-piece molded gasket — far easier and more reliable than the older multi-piece cork-and-rubber assembly. Felpro is the standard.',
      },
      {
        id: 'oil-pan-bolts-4pt0l',
        name: 'Oil Pan Bolts (replace if corroded)',
        partNumber: 'M6 x 1.0 x 16mm (typical 4.0L pan bolts)',
        quantity: 16,
        price: 8,
        category: 'conditional',
        notes: 'Reuse if clean and not stripped. Replace any showing rust, head damage, or thread damage.',
      },
      {
        id: 'engine-oil-pan',
        name: 'Engine Oil (5 quarts) and New Oil Filter',
        partNumber: 'Match your usual brand and grade',
        quantity: 1,
        price: 25,
        category: 'required',
        notes: 'Required for post-job oil change since you will drain the oil for this work.',
      },
      {
        id: 'rtv-pan',
        name: 'Oil-Compatible RTV Silicone',
        partNumber: 'Permatex 81160 (Black RTV) or Mopar 4318025AC',
        quantity: 1,
        price: 7,
        category: 'required',
        notes: 'Used at the timing-cover-to-block junction and the rear main bearing cap-to-block junction (small dabs at corners).',
      },
    ],
    safetyInformation:
      'Front axle drop is involved — multiple jack stands and careful component support are essential. The front coil springs are under significant load and can launch dangerously if released without control. The engine must be supported during pan removal — without support, the engine can shift and fall. Used motor oil is hazardous waste — dispose at an auto-parts recycling center. Working under a vehicle requires solid jack stands on level concrete.',
    beforeYouStart: [
      'Park on solid concrete with the front of the vehicle accessible',
      'Have all parts staged: new gasket, RTV, fresh oil and filter, RTV',
      'Disconnect the battery negative cable',
      'Drain the engine oil — you will need to remove the pan, so drain first',
      'Have a way to support the engine during pan removal — engine support bar over the fender is the cleanest solution',
      'Photograph the engine bay and underbody before disassembly',
      'Plan the workflow: front axle drop on day 1 (slow), pan replace on day 2',
    ],
    steps: [
      {
        number: 1,
        title: 'Lift Vehicle High and Drain Oil',
        description:
          'Raise the front of the vehicle as high as practical (12-15" of clearance under the front axle minimum) and support on jack stands placed under the frame rails. Remove the front wheels for additional clearance. Drain the engine oil into a pan via the oil pan drain plug. Remove the oil filter.',
        why: 'Vehicle height and wheels off provide working space for the pan removal. Draining oil prevents major mess.',
        tip: 'A second pair of stands under the lower control arms gives extra security.',
        commonMistake: 'Working too low. The front axle drop requires significant clearance under the pan.',
      },
      {
        number: 2,
        title: 'Disconnect Front Driveshaft and Track Bar',
        description:
          'Mark and remove the front driveshaft from the front axle yoke (four 12mm bolts) and slide the shaft off the transfer case yoke. Remove the front track bar (axle-side bolt typically 21mm — see the Track Bar Replacement guide for details). These two components hold the axle in position laterally.',
        why: 'Track bar and driveshaft must be disconnected to allow axle articulation needed for the drop.',
        tip: 'Mark the driveshaft orientation to its companion flange with a paint pen for vibration balance on reinstall.',
        commonMistake: 'Forgetting to mark driveshaft orientation. Vibration on reassembly.',
      },
      {
        number: 3,
        title: 'Disconnect Steering Components and Sway Bar',
        description:
          'Disconnect the drag link from the pitman arm OR the tie rod end at the knuckle (one or the other). Disconnect both front sway bar links from the axle (sway bar can stay on the chassis). Disconnect the front shock absorbers from the axle.',
        why: 'These components tie the axle to the chassis. With them disconnected, the axle can drop down on its coil springs.',
        tip: 'Photograph each connection before disconnecting — the steering and suspension geometry must be reassembled exactly.',
        commonMistake: 'Forcing parts apart with a hammer. Damages tie rod ends and ball joints.',
      },
      {
        number: 4,
        title: 'Support and Lower the Front Axle',
        description:
          'Position a floor jack under the differential center of the front axle. With the engine support bar in place (or a floor jack with wood block under the oil pan, applied carefully), the axle can now be lowered. Lower the axle slowly using the floor jack — the coil springs will extend. Once the axle is below the oil pan level, the pan can be accessed. Some technicians fully remove the axle; others lower it just enough to clear the pan with a few inches of clearance.',
        why: 'Lowering the axle creates the clearance needed to drop the oil pan straight down.',
        tip: 'Lowering 4-6 inches is typically enough; full axle removal is overkill for pan service.',
        commonMistake: 'Letting the axle swing freely. Coil springs can pop the axle sideways and damage other components.',
      },
      {
        number: 5,
        title: 'Remove Oil Pan Bolts and Lower Pan',
        description:
          'With axle clearance, remove the oil pan bolts (typically 16 bolts, 10mm or 13mm). Set them aside in order. The pan is sealed to the block with the gasket and may also have a small RTV bead at the timing-cover-to-block joint and the rear-main-cap-to-block joint. Tap the pan with a rubber mallet to break the gasket seal, then lower the pan. The pan may need to be tilted to clear the oil pickup tube — the tube is fixed to the engine and projects into the pan.',
        why: 'Pan bolts release the pan; the seal must be broken before lifting away.',
        tip: 'A flat blade screwdriver gently tapped into the gasket seam helps break a stuck pan seal — but use minimal force to avoid bending the pan.',
        commonMistake: 'Bending the pan when prying. Bent pans do not seal correctly.',
      },
      {
        number: 6,
        title: 'Clean Pan and Block Sealing Surfaces',
        description:
          'On a workbench, clean the inside and outside of the oil pan thoroughly. Use a gasket scraper to remove every trace of old gasket from both the pan flange and the block. Spray brake cleaner on a rag and wipe each surface until bare metal, dry, and oil-free. Inspect the pan flange for warpage — it should be flat. A bent flange bends back with careful tapping on a flat surface.',
        why: 'A new gasket seals only against clean, flat metal. Residue or warpage causes immediate leak.',
        tip: 'Hold a straightedge across the pan flange — gaps indicate warpage that needs straightening.',
        commonMistake: 'Leaving old gasket residue. New gasket cannot bond, so it leaks within days.',
      },
      {
        number: 7,
        title: 'Apply RTV at Critical Junctions and Install Gasket',
        description:
          'Apply a small dab of RTV silicone at TWO critical points on the engine block: (1) where the timing cover meets the engine block (the front of the oil pan junction), and (2) where the rear main bearing cap meets the engine block (the rear of the oil pan junction). These four corners are not covered by the gasket and need RTV to seal. Install the new gasket on the block face — most one-piece gaskets press onto locating tabs. Confirm gasket is flat and seated.',
        why: 'The four corners (front and rear of the pan junction) have casting transitions that the gasket cannot fully cover. Small RTV dabs fill these gaps.',
        tip: 'Less RTV is more — pea-sized dabs at the four corners. Excess squeezes inside the pan and can contaminate oil.',
        commonMistake: 'Using too much RTV. Excess can clog the oil pickup screen.',
      },
      {
        number: 8,
        title: 'Lift Pan into Position and Install Bolts',
        description:
          'Lift the cleaned oil pan up to the engine block with the oil pickup tube clearing into the pan. Hand-thread all 16 oil pan bolts before applying torque. Working in a star pattern from the center outward, tighten the bolts in stages: first to 60 in-lbs (7 Nm), then to 84 in-lbs (10 Nm), then to 110 in-lbs (12 Nm) for final torque. Verify torque spec from OEM service manual. The progressive star-pattern tightening seats the gasket evenly without crushing it.',
        why: 'Even, progressive torque seats the gasket without distorting the pan or extruding the gasket out from under the bolt heads.',
        tip: 'Start with all bolts hand-tight before any torque application. Then do star-pattern stages.',
        commonMistake: 'Tightening one bolt fully at a time. Pan distorts and gasket leaks.',
      },
      {
        number: 9,
        title: 'Reassemble in Reverse Order',
        description:
          'Lift the front axle back into position using the floor jack — slow and steady, watching for proper coil spring engagement. Reconnect: front shocks, sway bar links, drag link or tie rod (whichever you disconnected), track bar (TORQUE AT RIDE HEIGHT), front driveshaft (with original orientation marks aligned). Refill engine oil to the correct level (5 quarts for the 4.0L). Install new oil filter. Lower the vehicle.',
        why: 'Reverse-order reassembly restores the suspension geometry. Track bar at ride height is critical for bushing life.',
        tip: 'Take a moment to verify each torque value as you reassemble — easy to forget one bolt is final-torqued.',
        commonMistake: 'Forgetting to torque one of the suspension bolts. May cause noise or instability.',
      },
      {
        number: 10,
        title: 'Run Engine and Verify No Leaks',
        description:
          'Reconnect battery. Start engine and let idle for 5 minutes. Check oil level on dipstick (should be at FULL — top off if needed). Visually inspect the oil pan area for any leaks. Allow engine to reach operating temperature, then shut down. Recheck oil level and any leak after 10 minutes of cool-down. Drive for 50-100 miles before final leak check.',
        why: 'Initial verification is critical — small leaks may show only at operating temperature.',
        tip: 'Park on cardboard for 24 hours after the test drive — catches any slow seepage.',
        commonMistake: 'Final-check before operating temperature. Some leaks only show when oil is warm and thinner.',
      },
    ],
    torqueSpecifications: [
      {
        fastener: 'Oil pan bolts (16)',
        specification: 'Final 110 in-lbs (12 Nm) in three progressive passes (60-84-110 in-lbs). Verify torque spec from OEM service manual.',
        notes: 'Star-pattern; progressive torque to prevent gasket extrusion. Low torque — these are small bolts.',
      },
      {
        fastener: 'Track bar bolts (axle and frame ends)',
        specification: '125 ft-lbs axle / 110 ft-lbs frame. APPLY AT RIDE HEIGHT.',
        notes: 'See Track Bar Replacement guide for details.',
      },
      {
        fastener: 'Front shock bolts to axle',
        specification: '85 ft-lbs (115 Nm). Verify torque spec from OEM service manual.',
        notes: 'Mid-torque.',
      },
      {
        fastener: 'Sway bar link bolts',
        specification: '70 ft-lbs (95 Nm). Verify torque spec from OEM service manual.',
        notes: 'Standard.',
      },
      {
        fastener: 'Front driveshaft U-joint flange bolts',
        specification: '16 ft-lbs (22 Nm). Verify torque spec from OEM service manual.',
        notes: 'Twelve-point bolts; reinstall in original orientation.',
      },
    ],
    fluidSpecifications: [
      {
        fluid: 'Engine oil',
        type: 'SAE 5W-30 or 10W-30 (4.0L preference)',
        capacity: '5 quarts (4.0L I6 with new filter)',
        notes: 'Refill after pan reinstall. Do not overfill.',
      },
    ],
    troubleshooting: [
      {
        problem: 'Oil leak at oil pan after install',
        likelyCause: 'Gasket not seated, RTV missed at corners, or pan flange bent',
        whatToCheck: 'Visual inspection of all four pan corners and the gasket seam',
        fix: 'Drain oil, remove pan, clean and reseal carefully. RTV at corners is critical.',
      },
      {
        problem: 'Oil leak at front of engine but not at pan',
        likelyCause: 'Timing cover gasket leaking (separate from pan gasket job)',
        whatToCheck: 'Visual inspection at the timing cover seam',
        fix: 'Replace timing cover gasket — see Timing Chain Replacement guide for cover access.',
      },
      {
        problem: 'Oil leak at rear of engine but not at pan',
        likelyCause: 'Rear main seal leaking (separate from pan gasket job)',
        whatToCheck: 'Visual inspection at the rear main bearing area',
        fix: 'Replace rear main seal — see Rear Main Seal guide. Major job; trans must come out.',
      },
      {
        problem: 'Oil pressure low after pan reinstall',
        likelyCause: 'Oil pickup tube partially blocked by RTV (over-application) or pickup screen damaged',
        whatToCheck: 'Pull pan, inspect pickup tube and screen',
        fix: 'Clean pickup tube. Replace if damaged.',
      },
      {
        problem: 'Vehicle pulls or wanders after reassembly',
        likelyCause: 'Track bar or steering components not at correct torque or alignment',
        whatToCheck: 'Verify track bar torque at ride height; visual inspection of all suspension connections',
        fix: 'Re-torque all suspension components per spec.',
      },
    ],
    proTips: [
      'Use a one-piece molded gasket — far easier than multi-piece cork-and-rubber and rarely leaks.',
      'Apply RTV sparingly at the four pan corners — pea-sized dabs only. Excess clogs oil pickup.',
      'Photograph every step. The reassembly of front suspension is sequence-critical.',
      'Do this job WITH a track bar replacement if both are needed — overlap of axle disassembly saves significant time.',
      'After reassembly, get a front-end alignment. The front axle was moved during this work.',
      'Park on cardboard for the first 100 miles after the job. Slow seeps will show on the cardboard.',
    ],
    commonMistakes: [
      {
        mistake: 'Excess RTV application at pan corners',
        consequence: 'RTV blob clogs oil pickup screen. Oil starvation can damage bearings.',
      },
      {
        mistake: 'Tightening pan bolts unevenly or in single full-torque passes',
        consequence: 'Pan flange distorts. Gasket extrudes from under bolts. Immediate leak.',
      },
      {
        mistake: 'Forgetting to torque track bar at ride height',
        consequence: 'Bushings tear within months. Vehicle develops wobble or wandering.',
      },
      {
        mistake: 'Reusing a bent or distorted oil pan',
        consequence: 'Cannot seal evenly — replacement pan ($30-50) is the only fix.',
      },
      {
        mistake: 'Skipping the post-job alignment',
        consequence: 'Front-end geometry is slightly off; tire wear is uneven and steering is vague.',
      },
    ],
    successIndicators: [
      'No oil leaks at the pan flange, front, or rear of engine after operating temperature',
      'Oil pressure normal (30-60 psi at idle, climbs with RPM)',
      'No noise, vibration, or pull during driving',
      'Steering geometry feels normal',
      'No oil drips on driveway after parking overnight',
    ],
    whenToSeekHelp:
      'If the oil leak persists after a careful reseal, the pan itself may be warped or cracked — replacement pan ($30-50) is the fix. If the oil pickup tube is damaged during the job, the engine may need internal repair. If the front-end alignment cannot be corrected after reassembly, suspect a bent or damaged track bar bushing or worn steering component. Many shops will install a customer-supplied pan and gasket for a fraction of full-job labor cost.',
    legalDisclaimer: 'This guide was generated by GarageIQ\'s AI from publicly available sources. Some information may be incorrect or out of date. Verify torque specifications and any safety-critical procedures against the OEM service manual for your vehicle before performing work. Steering and suspension components are safety-critical — if anything is unclear, stop and consult a professional. The Haynes Manual tab on this page links to the licensed manual for your vehicle. GarageIQ is not a substitute for a qualified mechanic. By using this guide you agree to GarageIQ\'s Terms of Service and assume responsibility for the work performed. Used motor oil is hazardous waste — dispose at an auto-parts recycling center.',
    manualSource: {
      title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)',
      url: 'https://www.haynes.com/en-us/wrangler/jeep/2003',
      notes: 'Procedure sourced from public catalogs and forums; cross-reference torque specs and any year-specific oil pan variations against your licensed manual.',
    },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — REAR MAIN SEAL REPLACEMENT — engine-agnostic procedure
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-rear-main-seal-replacement',
    title: 'Rear Main Seal Replacement (4.0L I6 / 2.4L I4)',
    vehicle: {
      make: 'Jeep',
      model: 'Wrangler',
      generations: [{ startYear: 1997, endYear: 2006 }],
    },
    video: {
      youtubeId: '3MAyclRNa9k',
      title: 'Rear main seal 2002 TJ Jeep Wrangler 4.0L',
      channel: 'Burn Oil',
    },
    source: 'Generated by GarageIQ AI from publicly available sources (Mopar parts catalog, Felpro and National rear main seal literature, 4.0L I6 and 2.4L I4 service references, NHTSA TSBs, owner\'s manual, technician forums and Wrangler community service threads). Cross-checked and structured per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Engine',
    difficulty: 'advanced',
    duration: '6 - 12 hours total (most of the time is transmission removal, which is the prerequisite)',
    interval: 'Replace when oil drips from the bell-housing area or transmission front, oil-soaked clutch disc on inspection, or any oil weep at the rear main seal area.',
    description:
      'The rear main seal sits at the back of the engine block, around the rear of the crankshaft, sealing oil from the crankshaft pulley face. Failure means oil drips down onto the bell housing, contaminates the clutch disc (manual transmission) or torque converter (automatic), and creates a constant mess. Access requires the transmission to be removed (see Manual Transmission R&R guide; for automatic, the procedure is similar but involves the torque converter rather than the clutch). Once the transmission is out, the rear main seal replacement itself is a 1-2 hour job. The bulk of the work is the transmission R&R. This guide assumes the trans is already out.',
    overview: {
      difficulty: 'Advanced — transmission R&R is the prerequisite (see that guide). Rear main seal itself is moderate.',
      estimatedTime: '6-12 hours total: 4-6 hours transmission R&R + 1-2 hours seal replacement + reassembly',
      estimatedCost: '$15-30 for the seal; clutch may need replacement if oil-soaked; may also want to do flywheel work',
      whyMatters: 'A leaking rear main seal causes ongoing oil loss, soaks the clutch disc (forcing eventual clutch replacement), and creates a slip risk on bell-housing components. Replacement stops the leak permanently when done correctly.',
      safetyNote: 'Heavy components — transmission must already be out. Clutch is being removed/inspected during this work. Engine work — let cool fully. Oil residue on bell-housing parts can be slippery — clean before reassembly.',
    },
    tools: [
      'All tools from Manual Transmission R&R guide',
      'Rear main seal installer tool (driver tool that fits the seal OD) OR a large socket of correct size',
      'Hammer (small, for driving the seal)',
      'Brake cleaner (1 can)',
      'Gasket scraper (for any old seal residue)',
      'Clean rags',
      'Disposable nitrile gloves and eye protection',
      'Strong magnetic light for working in the bell-housing cavity',
    ],
    parts: [
      {
        id: 'rear-main-seal-4pt0l',
        name: 'Rear Main Seal (4.0L I6)',
        partNumber: 'Mopar 53020749 — equivalents: Felpro BS40632 / National 9434 / Mahle 67862',
        quantity: 1,
        price: 18,
        category: 'required',
        notes: '4.0L I6 uses a single-piece rear main seal (one-piece design after 1991). Confirm seal matches your engine year.',
      },
      {
        id: 'rear-main-seal-2pt4l',
        name: 'Rear Main Seal (2.4L I4)',
        partNumber: 'Mopar 53020748 — equivalent: Felpro BS40550',
        quantity: 1,
        price: 15,
        category: 'optional',
        notes: 'Use only if working on a 2.4L. The 2.4L seal differs in dimensions from the 4.0L.',
      },
      {
        id: 'clutch-with-seal',
        name: 'Clutch Disc (if oil-soaked from leak)',
        partNumber: 'See Clutch Replacement guide for kit options',
        quantity: 1,
        price: 180,
        category: 'conditional',
        notes: 'If the old seal soaked the clutch disc with oil, the disc cannot be cleaned and must be replaced. Inspect during this job.',
      },
      {
        id: 'oil-pan-gasket-rear',
        name: 'Oil Pan Gasket (if loosening pan corner)',
        partNumber: 'See Oil Pan Gasket Replacement guide',
        quantity: 1,
        price: 22,
        category: 'optional',
        notes: 'Some seal-installation methods require slightly loosening the rear of the oil pan. If the pan gasket is disturbed, plan to replace.',
      },
    ],
    safetyInformation:
      'Engine work — fully cool the engine before starting. Transmission must already be removed (see Manual Transmission R&R guide). The flywheel must be removed, exposing the rear of the crankshaft — eye protection mandatory because debris falls from above when working under the vehicle. Used motor oil is hazardous waste — dispose at an auto-parts recycling center. Oil-soaked clothing can be a flammability risk — change if heavily contaminated.',
    beforeYouStart: [
      'Confirm transmission is fully removed (see Manual Transmission R&R guide)',
      'Inspect the clutch and flywheel — if oil-soaked, plan to replace clutch disc',
      'Have the new seal, installer tool (or appropriate-size socket), and a hammer staged',
      'Wet-clean the entire bell-housing area with brake cleaner before disassembly to suppress any residue',
      'Read the OEM rear main seal procedure for your specific engine — methodology can differ slightly',
      'Know whether your engine uses a one-piece or two-piece seal (4.0L is one-piece on 1991+ TJs)',
    ],
    steps: [
      {
        number: 1,
        title: 'Remove Flywheel (Manual) or Flexplate (Automatic)',
        description:
          'For manual transmissions: Remove the clutch pressure plate and clutch disc (see Clutch Replacement guide). Remove the flywheel — typically held by 6 large bolts (TORQUE-TO-YIELD on 4.0L — buy new bolts before reassembly). Use a 19mm socket and a long breaker bar; hold the crankshaft from rotating with a screwdriver wedged in the flywheel teeth or a flywheel lock tool. For automatic transmissions: Remove the torque converter from the flexplate (4 small bolts), then the flexplate from the crankshaft (6 bolts).',
        why: 'The flywheel/flexplate covers the rear of the crankshaft where the seal sits. Must come off for access.',
        tip: 'Note the flywheel/flexplate orientation — there is typically a small dowel or unique bolt pattern that ensures correct reassembly.',
        commonMistake: 'Trying to use an impact gun on the crankshaft from the front. The flywheel will catch the crankshaft and prevent rotation — but the bolts may galled.',
      },
      {
        number: 2,
        title: 'Inspect the Crankshaft Sealing Surface',
        description:
          'With the flywheel off, the rear of the crankshaft is visible. The crankshaft has a smooth machined surface where the lip of the rear main seal rides. Inspect this surface for grooves, scratches, or wear (a crankshaft "wear groove" from a long-leaking seal). Light wear is acceptable; deep grooves require crankshaft repair (sleeve installation) or replacement.',
        why: 'The seal lip rides on this surface — it must be smooth for the new seal to seal effectively.',
        tip: 'A flashlight at an angle reveals scratches and wear better than direct light.',
        commonMistake: 'Skipping inspection. Installing a new seal on a worn crankshaft surface causes immediate re-leak.',
      },
      {
        number: 3,
        title: 'Remove the Old Seal',
        description:
          'On a one-piece rear main seal (4.0L 1991+), the seal sits in the rear seal retainer (a circular housing bolted to the rear of the block). Remove the retainer\'s bolts (typically 5-6 bolts, 10mm or 13mm). Pull the retainer off the block — the seal is pressed into it. Set the retainer on a bench. Press or punch the old seal out of the retainer from the back side. Some methods use a screwdriver to pry the seal lip and pull it out; others use a slide-hammer tool.',
        why: 'The retainer-mounted seal can be removed cleanly on a workbench rather than fighting it in the engine bay.',
        tip: 'Before removing the retainer, photograph the retainer-to-block alignment — the retainer has a specific orientation.',
        commonMistake: 'Trying to remove the seal in place. Damages the crankshaft surface or the retainer.',
      },
      {
        number: 4,
        title: 'Clean Retainer and Block Sealing Surface',
        description:
          'Clean the retainer with brake cleaner, removing any old seal material from the bore where the new seal will sit. Clean the back of the engine block where the retainer mounts. Inspect the retainer for any cracks or warpage — it should be flat and undamaged. Apply a thin film of fresh oil to the new seal lip just before install — this provides assembly lubrication.',
        why: 'The new seal must seat in a clean retainer with no debris. Block surface must also be clean for the retainer to seal flat.',
        tip: 'A green Scotch-Brite pad gently used on the retainer face removes light residue.',
        commonMistake: 'Skipping cleanup. Old residue prevents the retainer from seating flat against the block.',
      },
      {
        number: 5,
        title: 'Install New Seal in Retainer',
        description:
          'Position the new seal in the retainer with the spring-loaded lip facing INTO the engine (toward the inside of the block, which is where the oil pressure is). Use the seal installer tool (typically a flat-faced driver) or a socket of correct OD to drive the seal evenly into the retainer bore. Tap with a hammer in steady, light blows around the perimeter — even seating is critical. The seal should sit flush with the retainer face.',
        why: 'Even seating is essential. A cocked seal will leak immediately.',
        tip: 'A drop of oil on the OD of the seal helps it slide into the retainer.',
        commonMistake: 'Installing the seal lip backwards (away from the engine). Causes immediate leak.',
      },
      {
        number: 6,
        title: 'Reinstall Retainer to Block',
        description:
          'Apply a thin film of fresh engine oil to the seal lip and to the crankshaft sealing surface. Position the retainer over the rear of the crankshaft — the seal lip must slide over the crank surface without being pushed inside-out. Carefully ease the retainer onto the crank. Once seated, hand-thread all retainer bolts. Cross-tighten in stages to spec (typically 9-13 ft-lbs / 12-18 Nm). Verify torque spec from OEM service manual.',
        why: 'Slow and careful retainer install prevents flipping the seal lip. Cross-tightening seats the retainer evenly.',
        tip: 'A small chamfer on the crankshaft helps the seal lip start. Apply oil first and slide the retainer slowly.',
        commonMistake: 'Forcing the retainer on. Flips the seal lip and causes immediate leak.',
      },
      {
        number: 7,
        title: 'Reinstall Flywheel/Flexplate (NEW Bolts on 4.0L)',
        description:
          'For manual: Reinstall the flywheel using NEW bolts (4.0L bolts are torque-to-yield, single use). Hand-thread all 6 bolts, then torque in star pattern to OEM spec (typically 105 ft-lbs initial + torque-angle, or 55 ft-lbs initial + 60 degrees). Verify torque spec from OEM service manual. For automatic: reinstall flexplate (also TTY bolts on some years) and torque converter.',
        why: 'TTY bolts must be replaced because they stretched into yield on first install. Reusing causes inconsistent clamping force.',
        tip: 'Hold the crankshaft with a flywheel lock tool or screwdriver during torque application.',
        commonMistake: 'Reusing TTY flywheel bolts. Bolt failure can cause flywheel to come loose at high RPM — catastrophic.',
      },
      {
        number: 8,
        title: 'Reinstall Clutch and Pressure Plate',
        description:
          'For manual: Inspect the clutch disc for oil contamination — if soaked, replace (see Clutch Replacement guide). Install the new (or cleaned) clutch disc and pressure plate per the Clutch Replacement guide.',
        why: 'Oil-soaked clutch disc cannot be cleaned and salvaged. Reinstalling it means doing this whole job again in 5,000 miles.',
        tip: 'Use a clutch alignment tool to center the disc before installing the pressure plate.',
        commonMistake: 'Reusing an oil-soaked clutch disc. Slippage and chatter return immediately.',
      },
      {
        number: 9,
        title: 'Reinstall Transmission',
        description:
          'Per the Manual Transmission R&R guide, reinstall the transmission. Slide the input shaft into the disc and pilot bushing. Bell-housing flange must come flush against the engine block.',
        why: 'Transmission reinstall is the long part of this job. Take care with the alignment.',
        tip: 'Use a clutch pilot alignment tool when needed for any clutch issues.',
        commonMistake: 'See the Manual Transmission R&R guide for trans reinstall errors.',
      },
      {
        number: 10,
        title: 'Refill Fluids and Verify',
        description:
          'Refill engine oil if it was drained. Refill transfer case fluid (drained during transmission removal). Reconnect battery. Start engine and let idle for 5 minutes. Verify no oil leaks from the bell-housing area or rear of the engine. Drive 50 miles, then check for any leaks at the rear of the engine.',
        why: 'A new rear main seal should be dry — any oil weep means the seal was installed incorrectly.',
        tip: 'Park on cardboard for 24 hours after the test drive. Catches slow seeps.',
        commonMistake: 'Driving extensively before the first leak check. A failed install gets worse with use.',
      },
    ],
    torqueSpecifications: [
      {
        fastener: 'Rear main seal retainer bolts',
        specification: '9-13 ft-lbs (12-18 Nm). Verify torque spec from OEM service manual.',
        notes: 'Cross-tighten in star pattern.',
      },
      {
        fastener: 'Flywheel bolts (4.0L I6, NEW each time)',
        specification: '55 ft-lbs + 60 degrees (torque-to-yield). Verify torque spec from OEM service manual.',
        notes: 'Use NEW bolts. Apply Loctite or follow OEM bolt prep procedure.',
      },
      {
        fastener: 'Pressure plate bolts (if installed)',
        specification: '20-25 ft-lbs (27-34 Nm). See Clutch Replacement guide.',
        notes: 'Star-pattern.',
      },
    ],
    fluidSpecifications: [
      {
        fluid: 'Engine oil (top-off if drained for related work)',
        type: 'SAE 5W-30 or 10W-30 (4.0L preference)',
        capacity: '5 quarts (4.0L I6 with new filter)',
        notes: 'Confirm oil level before starting engine.',
      },
      {
        fluid: 'Transfer case fluid (refilled after trans reinstall)',
        type: 'ATF+4 (Mopar 68218057AB)',
        capacity: '1.25 - 1.6 quarts depending on transfer case',
        notes: 'Drained during transmission removal. Always refill before driving.',
      },
    ],
    troubleshooting: [
      {
        problem: 'Oil leak persists after seal replacement',
        likelyCause: 'Seal installed cocked or upside-down, retainer not seated flat to block, or crankshaft surface worn',
        whatToCheck: 'Pull transmission again; inspect seal seating',
        fix: 'Reinstall seal correctly. If crankshaft surface is worn, install a Speedi-Sleeve repair sleeve over the seal contact area.',
      },
      {
        problem: 'Oil leak from retainer-to-block joint',
        likelyCause: 'Retainer not torqued evenly or block surface had residue',
        whatToCheck: 'Visual inspection of the retainer perimeter for fluid emerging',
        fix: 'Pull retainer, clean both surfaces, reinstall with even torque. Some applications use a thin gasket between retainer and block — verify required.',
      },
      {
        problem: 'Clutch slips after reinstall',
        likelyCause: 'Oil-soaked clutch disc was reused (still contaminated) or new disc was contaminated during install',
        whatToCheck: 'Inspect disc condition',
        fix: 'Replace disc if contaminated.',
      },
      {
        problem: 'Engine runs rough after reinstall',
        likelyCause: 'Misaligned flywheel/flexplate (rare — they have a unique bolt pattern), or sensor disconnected during work',
        whatToCheck: 'Verify all crankshaft position sensors and electrical connections',
        fix: 'Reconnect any missed sensors. If flywheel/flexplate is misaligned, reposition.',
      },
      {
        problem: 'Crankshaft surface has visible groove from old seal',
        likelyCause: 'Long-term seal leak created a wear groove on the crankshaft sealing surface',
        whatToCheck: 'Feel the crankshaft surface with a fingernail — any catch indicates a groove',
        fix: 'Install a Speedi-Sleeve (a thin sleeve that presses over the crank surface) and use a slightly different seal designed for repair sleeves. Available at parts stores.',
      },
    ],
    proTips: [
      'Replace the rear main seal whenever the transmission is out, even if not currently leaking. Ten minutes of seal install vs. another full transmission R&R later.',
      'Buy quality seals (Felpro, Mahle, National). Cheap seals fail prematurely.',
      'A drop of fresh oil on the seal lip and crankshaft surface during install is critical — dry installation can damage the lip.',
      'Photograph the retainer orientation before removal. Some engine designs are sensitive to retainer clocking.',
      'After reinstall, monitor the bell-housing area for the first 1,000 miles — most install errors show up early.',
    ],
    commonMistakes: [
      {
        mistake: 'Installing the seal with the lip facing the wrong direction',
        consequence: 'Seal cannot hold pressure — leaks immediately.',
      },
      {
        mistake: 'Reusing torque-to-yield flywheel bolts',
        consequence: 'Bolt failure under load. Flywheel can come loose. Catastrophic.',
      },
      {
        mistake: 'Not inspecting the crankshaft sealing surface for wear',
        consequence: 'New seal leaks immediately on a worn surface. Speedi-Sleeve required.',
      },
      {
        mistake: 'Reusing an oil-soaked clutch disc',
        consequence: 'Slippage and chatter return. Must redo clutch.',
      },
      {
        mistake: 'Forcing the retainer onto the crankshaft',
        consequence: 'Flips the seal lip. Causes immediate leak.',
      },
    ],
    successIndicators: [
      'No oil leaks at the rear of the engine after 100 miles of driving',
      'Bell-housing area stays dry on inspection',
      'Engine oil level remains stable',
      'No clutch slip or chatter after reinstall',
      'Engine runs normally with no rough idle or misfire',
    ],
    whenToSeekHelp:
      'If the new seal leaks immediately, the install was incorrect — pull and inspect for cocked or backwards seal lip. If the crankshaft surface is deeply grooved and a Speedi-Sleeve cannot be installed, professional crankshaft repair (welding and machining) or replacement may be required. If the retainer is cracked or warped, replacement is needed (typically $30-50). Many shops will install customer-supplied parts at reduced labor rates given that the trans is the bulk of the job.',
    legalDisclaimer: 'This guide was generated by GarageIQ\'s AI from publicly available sources. Some information may be incorrect or out of date. Verify torque specifications and any safety-critical procedures against the OEM service manual for your vehicle before performing work. The Haynes Manual tab on this page links to the licensed manual for your vehicle. GarageIQ is not a substitute for a qualified mechanic. By using this guide you agree to GarageIQ\'s Terms of Service and assume responsibility for the work performed. Some flywheel bolts are torque-to-yield (single use); always verify against OEM specs.',
    manualSource: {
      title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)',
      url: 'https://www.haynes.com/en-us/wrangler/jeep/2003',
      notes: 'Procedure sourced from public catalogs and forums; cross-reference torque specs and flywheel bolt requirements against your licensed manual.',
    },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — DEATH WOBBLE DIAGNOSTIC
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-death-wobble-diagnostic',
    title: 'Death Wobble Diagnostic (Front-End Oscillation)',
    vehicle: { make: 'Jeep', model: 'Wrangler', generations: [{ startYear: 1997, endYear: 2006 }] },
    video: {
      youtubeId: 'IWq6MC1IrW4',
      title: 'Jeep Death Wobble How To Fix',
      channel: 'KyleSVT',
    },
    source: 'Generated by GarageIQ AI from publicly available sources (NHTSA TSBs, Mopar service bulletins, technician forums and Wrangler community service threads). Cross-checked and structured per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Suspension & Steering',
    difficulty: 'intermediate',
    duration: '1 - 2 hours diagnostic time',
    interval: 'Diagnose immediately when wobble occurs. Do not drive at highway speed until cause is identified and fixed.',
    description:
      '"Death wobble" is the violent, self-reinforcing front-end oscillation that TJs (and other solid-front-axle vehicles) can develop after hitting a bump at 45-65 mph. It is a resonance event — once started, it requires slowing below 30 mph to stop. The cause is almost never one component; it is usually a worn track bar combined with one or more secondary issues (loose tie rod, bad ball joint, unbalanced tire, loose track bar bolt). This guide walks the systematic inspection that finds every contributor.',
    overview: {
      difficulty: 'Intermediate — methodical inspection of every front-end component',
      estimatedTime: '1-2 hours of inspection; repair time depends on findings',
      estimatedCost: '$0 to inspect; replacement parts $20-300+ depending on which components are worn',
      whyMatters: 'Death wobble can cause loss of vehicle control at highway speed. Diagnosing every contributing factor (not just the most obvious) is the only way to permanently eliminate it.',
      safetyNote: 'Do not drive at highway speed until the cause is identified and fixed. If wobble starts, slow gradually below 30 mph — do NOT slam brakes (locks the front end). Use rated jack stands during inspection.',
    },
    tools: [
      'Floor jack and jack stands',
      'Pry bar (16-24 inch)',
      'Helper to rock steering wheel during inspection',
      'Bright flashlight',
      'Tire pressure gauge',
      'Tape measure (for axle centering check)',
      'Torque wrench',
    ],
    parts: [
      {
        id: 'replacement-as-found', name: 'Replacement components based on findings', partNumber: 'See individual guides for track bar, tie rod, ball joints, drag link', quantity: 1, price: 50, category: 'conditional',
        notes: 'Buy parts only after diagnosis identifies which components are worn. Do not preemptively replace everything — start with track bar and ball joints since those are the most common.',
      },
    ],
    safetyInformation:
      'Death wobble is dangerous at highway speed. If your vehicle has wobbled, do not drive in traffic until the cause is identified and fixed. Use rated jack stands on level concrete during inspection. If multiple components show wear, replace them all before test driving.',
    beforeYouStart: [
      'Note when wobble occurs (speed, after bumps, in turns) — informs likely cause',
      'Check tire pressure first — under/over inflation causes wobble independent of mechanical wear',
      'Have a helper available to rock the steering wheel during inspection',
      'Have all front-end inspection guides handy (track bar, ball joints, tie rod, etc.)',
      'Plan to inspect EVERY front-end component, not just the suspected one — wobble is multi-cause',
    ],
    steps: [
      {
        number: 1,
        title: 'Start with Tire Pressure and Balance',
        description: 'Check all four tire pressures — must be at the door-jamb spec (typically 30 psi). Inspect tires for cupping or uneven wear, which indicates balance issues. Spin each tire and watch for wobble; visually check if any tire has a visible bulge or tread separation.',
        why: 'Out-of-balance tires can cause wobble that mimics steering-component issues. Always rule this out first.',
        tip: 'A tire with internal belt separation often appears fine externally but causes severe wobble. Replace any suspect tire.',
        commonMistake: 'Skipping tire inspection. Replacing front-end parts will not fix a tire problem.',
      },
      {
        number: 2,
        title: 'Inspect Track Bar (Most Common Cause)',
        description: 'With front wheels off the ground, have a helper rock the steering wheel side-to-side while you watch the track bar bushings. ANY visible play, twist, or movement at either bushing means the track bar is worn. Also check that both track bar bolts are at proper torque.',
        why: 'Worn track bar is the #1 cause of death wobble. Verify this first.',
        tip: 'Push and pull on the bar itself with the steering held still — there should be zero perceptible movement.',
        commonMistake: 'Replacing only the track bar without checking other components.',
      },
      {
        number: 3,
        title: 'Check Ball Joints',
        description: 'With the wheel off the ground, grab the tire at 12 and 6 o\'clock and rock vertically. Any clunk or visible play indicates worn ball joints. Then grab at 3 and 9 o\'clock and rock — play here can be ball joints OR wheel bearings.',
        why: 'Worn ball joints contribute to wobble and steering vagueness.',
        tip: 'Tighten the ball joint nut to spec before rocking — a loose nut causes false-positive play.',
        commonMistake: 'Confusing ball joint play with wheel bearing play. Vertical rock = ball joint; horizontal rock with wheel spinning freely = bearing.',
      },
      {
        number: 4,
        title: 'Inspect Tie Rod Ends and Drag Link',
        description: 'Have the helper rock the steering wheel back and forth slowly while you watch each tie rod end and the drag link end. Any clunk, gap, or visible movement at the joint means a worn end. Also check for boot tears (lets dirt in, accelerates wear).',
        why: 'Worn tie rods or drag link cause vague steering and contribute to wobble.',
        tip: 'A small pry bar against each joint reveals play that is not visible by eye.',
        commonMistake: 'Missing a torn boot. Once dirt enters, the joint fails quickly.',
      },
      {
        number: 5,
        title: 'Verify Steering Stabilizer (Damper)',
        description: 'Disconnect the steering stabilizer and try to compress/extend it by hand — should give smooth resistance. If it moves freely or has any oil leakage, replace it. Note: a good stabilizer MASKS underlying wobble; the goal is to fix the underlying cause, not rely on the damper.',
        why: 'A worn stabilizer lets minor wobble become major. But never use a new stabilizer to "fix" wobble — it only masks the problem.',
        tip: 'A new stabilizer is cheap insurance after fixing the actual causes — install last.',
        commonMistake: 'Replacing only the stabilizer hoping it cures wobble. Always a temporary fix.',
      },
      {
        number: 6,
        title: 'Check Wheel Bearings',
        description: 'Spin each front wheel by hand and listen for grinding or roughness. Then grab at 3 and 9 o\'clock and rock — play here (with steering not turning) indicates a worn front hub bearing.',
        why: 'Worn front bearings contribute to wobble and cause growl at speed.',
        tip: 'Bearings often fail one side at a time — replace the affected side, not both unless both are worn.',
        commonMistake: 'Confusing bearing play with ball joint or tie rod play. Bearing play feels at the wheel/hub; joint play feels at the joint itself.',
      },
      {
        number: 7,
        title: 'Verify Axle Centering',
        description: 'Measure from the frame to the wheel hub on each side — distances should be equal (within 1/4 inch). If unequal, the axle has shifted laterally — typically from a worn track bar or improper torque-at-droop on a previous track bar install.',
        why: 'An off-center axle creates steering geometry issues that cause wobble.',
        tip: 'For lifted TJs, an adjustable track bar lets you re-center the axle precisely.',
        commonMistake: 'Replacing the track bar without re-centering. New bar with same off-center axle still wobbles.',
      },
      {
        number: 8,
        title: 'Check Control Arm Bushings',
        description: 'Inspect the front lower and upper control arm bushings (4 total — 2 each at axle and frame ends). Any visible cracks, separation, or oil contamination means worn bushings. Push on the axle from front and rear and watch for excessive movement.',
        why: 'Worn control arm bushings let the axle shift fore-and-aft, causing wobble.',
        tip: 'Polyurethane replacement bushings last longer than rubber but are firmer in feel.',
        commonMistake: 'Skipping the control arms because they look "less likely." Worn bushings here cause real wobble.',
      },
      {
        number: 9,
        title: 'Verify All Bolts at Proper Torque',
        description: 'Check torque on: track bar bolts (axle and frame), tie rod nuts, drag link nut, ball joint castle nuts, sway bar links, and all suspension bolts. ANY loose bolt contributes to wobble.',
        why: 'A loose bolt is the easiest fix and most-overlooked cause.',
        tip: 'Use the OEM torque specs, not "feels tight." A torque wrench is essential.',
        commonMistake: 'Trusting visual inspection. A bolt can be loose and still look tight.',
      },
      {
        number: 10,
        title: 'Test Drive After All Repairs',
        description: 'Once all worn components are replaced and all torques verified, test drive at progressively higher speeds. Drive over expansion joints and rough patches. If wobble returns, you missed a component — re-inspect from step 1.',
        why: 'The diagnostic is only complete when wobble does not return.',
        tip: 'A wheel alignment after suspension work is essential — wobble can persist if alignment is off.',
        commonMistake: 'Skipping the test drive at varying speeds. Wobble may only show at specific speeds and over specific bumps.',
      },
    ],
    torqueSpecifications: [
      { fastener: 'Track bar axle-end bolt', specification: '125 ft-lbs (170 Nm). Verify torque spec from OEM service manual.', notes: 'Apply at ride height.' },
      { fastener: 'Track bar frame-end bolt', specification: '110 ft-lbs (149 Nm). Verify torque spec from OEM service manual.', notes: 'Apply at ride height.' },
      { fastener: 'Tie rod / drag link castle nut', specification: '60 ft-lbs (81 Nm). Verify torque spec from OEM service manual.', notes: 'Use new cotter pin.' },
      { fastener: 'Ball joint castle nut', specification: '80 ft-lbs (108 Nm). Verify torque spec from OEM service manual.', notes: 'Use new cotter pin.' },
    ],
    fluidSpecifications: [],
    troubleshooting: [
      { problem: 'Wobble persists after replacing track bar', likelyCause: 'Other components worn (ball joints, tie rods, drag link, stabilizer, bearings)', whatToCheck: 'Repeat full inspection of every front-end component', fix: 'Replace any worn component identified.' },
      { problem: 'Wobble returns after a few weeks', likelyCause: 'Track bar bolts not torqued at ride height; bushings tearing prematurely', whatToCheck: 'Re-inspect track bar bushings; verify torque was applied at ride height', fix: 'Re-torque at ride height; replace bushings if torn.' },
      { problem: 'Vehicle wanders without wobble', likelyCause: 'Worn drag link, tie rod, or alignment off', whatToCheck: 'Inspect drag link and tie rods; check alignment', fix: 'Replace worn parts; get an alignment.' },
      { problem: 'Wobble only on left or right turn', likelyCause: 'Ball joint or wheel bearing failure on one side', whatToCheck: 'Inspect each side independently', fix: 'Replace failed component on affected side.' },
      { problem: 'Wobble at specific speed (45 mph)', likelyCause: 'Tire imbalance or driveshaft balance issue', whatToCheck: 'Have wheels balanced; mark and inspect driveshaft', fix: 'Balance tires; if balance is good, suspect driveshaft.' },
    ],
    proTips: [
      'Death wobble is multi-cause. NEVER replace just one component and assume cured.',
      'Track bar + ball joints replaced together fixes 80% of wobble cases.',
      'A wheel alignment after suspension work is essential.',
      'Steering stabilizer is the LAST thing to replace, not the first.',
      'Document each inspection finding — even "ok" ones — for future reference.',
    ],
    commonMistakes: [
      { mistake: 'Replacing only the track bar', consequence: 'Wobble returns within weeks because secondary components are also worn.' },
      { mistake: 'Trusting a new steering stabilizer to cure wobble', consequence: 'Underlying issues continue to wear; wobble returns when the new damper wears.' },
      { mistake: 'Skipping tire pressure and balance check', consequence: 'Replace front-end parts that were not the cause; wobble persists.' },
    ],
    successIndicators: [
      'No wobble at any speed over expansion joints, bumps, or rough roads',
      'Steering feels precise, returns to center, and tracks straight',
      'No clunks, pops, or vibration from front end',
      'All torques verified at ride height',
      'Wheel alignment within spec',
    ],
    whenToSeekHelp:
      'If after replacing all suspect components and verifying torques the wobble persists, suspect frame or chassis damage — a shop with a frame rack can verify. If you cannot afford to replace everything, prioritize: track bar, ball joints, then tie rod ends. A shop diagnosis costs $50-100 and may identify components you missed.',
    legalDisclaimer: 'This guide was generated by GarageIQ\'s AI from publicly available sources. Some information may be incorrect or out of date. Verify torque specifications and any safety-critical procedures against the OEM service manual for your vehicle before performing work. Steering and suspension components are safety-critical — if anything is unclear, stop and consult a professional. The Haynes Manual tab on this page links to the licensed manual for your vehicle. GarageIQ is not a substitute for a qualified mechanic.',
    manualSource: { title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)', url: 'https://www.haynes.com/en-us/wrangler/jeep/2003', notes: 'Cross-reference inspection procedures and torque specs with your licensed manual.' },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — CRANK-NO-START DIAGNOSTIC
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-crank-no-start-diagnostic',
    title: 'Engine Cranks But Will Not Start (Diagnostic)',
    vehicle: { make: 'Jeep', model: 'Wrangler', generations: [{ startYear: 1997, endYear: 2006 }] },
    video: {
      youtubeId: '4ONu4NIauds',
      title: 'DIY Jeep Wrangler Crank No Start Troubleshooting',
      channel: 'BSK Garage',
    },
    source: 'Generated by GarageIQ AI from publicly available sources (Mopar service bulletins, NHTSA TSBs and Complaints database, owner\'s manual, technician forums and Wrangler community service threads). Cross-checked and structured per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Engine',
    difficulty: 'intermediate',
    duration: '30 minutes - 2 hours diagnostic time',
    interval: 'When the engine cranks normally but does not fire. Diagnostic before throwing parts at the problem.',
    description:
      'A crank-no-start condition means the starter is turning the engine, but the engine is not catching. Three things must be present for ignition: spark, fuel, and compression. This guide walks through testing each in order — quickest tests first. Most TJ no-starts are a failed crankshaft position sensor (CKP), a fuel pump that is not running, or a spark/coil issue. By the end of this diagnostic you should have isolated the cause to one of those three categories.',
    overview: {
      difficulty: 'Intermediate — uses basic diagnostic tools (test light, fuel pressure gauge, spark tester)',
      estimatedTime: '30 minutes - 2 hours depending on how quickly the cause is found',
      estimatedCost: '$0 to diagnose; repair varies by cause ($30 sensor to $200+ fuel pump)',
      whyMatters: 'Diagnosing before replacing prevents wasted parts costs. CKP, fuel pump, and ignition coil/distributor are common causes — each requires different parts.',
      safetyNote: 'Fuel system testing involves fuel pressure — wear eye protection. Disconnect battery before working near ignition components. Do not crank for more than 10 seconds at a time to avoid overheating the starter.',
    },
    tools: [
      'OBD-II scan tool (basic code reader, $20-50)',
      'Spark tester (inline plug, $10)',
      'Fuel pressure gauge with Schrader-valve adapter (matches TJ test port, $30-50)',
      'Test light or multimeter',
      'Noid lights (set, for injector signal, $15)',
      'Compression tester ($25)',
      'Helper to crank engine on demand',
      'Disposable nitrile gloves and eye protection',
    ],
    parts: [
      { id: 'replacement-as-found-2', name: 'Replacement parts based on findings', partNumber: 'See individual guides — CKP, fuel pump, ignition coil, etc.', quantity: 1, price: 50, category: 'conditional', notes: 'Buy specific replacement parts only after diagnosis identifies the failed component.' },
    ],
    safetyInformation:
      'Fuel system contains pressurized fuel — disconnect battery and depressurize before working on fuel components. Use eye protection during fuel pressure testing. Do not crank for more than 10 seconds at a time. Spark tests involve high voltage — keep hands clear of the spark gap.',
    beforeYouStart: [
      'Verify battery is fully charged (12.4+ volts at rest, 13.8+ volts running)',
      'Confirm engine cranks at normal speed (slow cranking = battery or starter issue, not crank-no-start)',
      'Smell for fuel — strong gasoline smell suggests over-fueling; no smell suggests no fuel delivery',
      'Note any check engine light or codes — code-scan first before deeper testing',
      'Confirm the immobilizer / sentry key is recognized (security light should NOT flash on dash with key in run)',
    ],
    steps: [
      {
        number: 1,
        title: 'Read OBD-II Codes First',
        description: 'Plug a scan tool into the OBD-II port (under driver\'s side dash). Read all stored and pending codes. Common no-start codes: P0335 (CKP sensor), P0340 (CMP sensor), P0230 (fuel pump relay), P0500 (VSS).',
        why: 'Codes often point directly to the failed component. Cheapest first step.',
        tip: 'Free code reads at most chain auto parts stores if you do not own a scanner.',
        commonMistake: 'Skipping codes. The ECM may have already identified the problem.',
      },
      {
        number: 2,
        title: 'Verify Spark at the Plug',
        description: 'Pull a spark plug wire from any cylinder. Connect a spark tester between the wire and a good engine ground. Have a helper crank the engine — you should see strong, blue spark jumping the gap. Weak/orange spark or no spark indicates ignition system failure.',
        why: 'Spark is the most common no-start cause on TJs (failed coil, distributor, or CKP not signaling).',
        tip: 'No spark on the I6 with a 4.0L = check distributor cap and rotor first ($30 fix), then CKP sensor.',
        commonMistake: 'Trusting that a hot spark plug means good ignition. Spark must be tested at the wire end.',
      },
      {
        number: 3,
        title: 'Verify Fuel Pump Operation',
        description: 'Turn the ignition key to RUN (not crank). Listen at the gas filler — you should hear a 2-3 second hum from the fuel pump priming the system. If no hum, the fuel pump is not getting power. Check fuse #20 (fuel pump) and the fuel pump relay (under hood).',
        why: 'Fuel pump priming is the easiest fuel-side check.',
        tip: 'A failed fuel pump relay is a $20 fix and presents identically to a failed pump.',
        commonMistake: 'Replacing the fuel pump without checking the relay first.',
      },
      {
        number: 4,
        title: 'Measure Fuel Pressure',
        description: 'Connect a fuel pressure gauge to the Schrader test port on the fuel rail. Turn key to RUN — pressure should snap to 49 psi (4.0L) within 2 seconds. Pressure below 35 psi = fuel pump failing or fuel filter clogged. Pressure above 55 psi = fuel pressure regulator failing.',
        why: 'Pressure tells you if fuel is being delivered at the right level.',
        tip: 'If pressure builds slowly, the pump may be failing or the filter clogging. Replace filter first ($15 vs $200 pump).',
        commonMistake: 'Trusting "I hear the pump" as proof of pressure. Failing pumps still hum but make no pressure.',
      },
      {
        number: 5,
        title: 'Test Injector Signal',
        description: 'Disconnect any one fuel injector electrical connector. Plug a noid light into the connector. Have a helper crank the engine — the noid light should flash rapidly. No flash = ECM is not commanding injectors (suspect CKP, CMP, or wiring) or the injector circuit is broken.',
        why: 'Injector signal is the third leg of "spark, fuel, signal."',
        tip: 'No injector pulse + no spark = CKP sensor is the most likely culprit (very common on TJ 4.0L).',
        commonMistake: 'Skipping noid light test. Injector signal is independent of fuel pressure and spark.',
      },
      {
        number: 6,
        title: 'Check Compression on Cold Engine',
        description: 'Pull all six spark plugs (4.0L) or all four (2.4L). Insert a compression tester in cylinder #1 and crank for 5-7 cycles. Note pressure. Repeat for each cylinder. Healthy compression: 130-180 psi all cylinders, with no more than 15% variation. Low or zero on one cylinder = mechanical issue (head gasket, valve, ring).',
        why: 'Without compression, the engine cannot start. Rare but worth testing if spark and fuel are confirmed good.',
        tip: 'A wet compression test (squirt 1 tsp oil into low cylinder, retest) helps differentiate ring wear vs valve issues.',
        commonMistake: 'Ignoring compression because spark and fuel were tested. Mechanical failure can co-exist.',
      },
      {
        number: 7,
        title: 'Inspect Crankshaft Position Sensor (CKP)',
        description: 'The 4.0L CKP sensor is mounted at the rear of the engine block, near the bell housing. It reads the crankshaft\'s position. CKP failure is THE most common cause of intermittent no-start on TJs. Inspect the connector for corrosion. With key on, measure between the CKP\'s power and signal pins — should see 5V signal or pulse during crank. Replace if defective.',
        why: 'CKP is one of the highest-failure components on a 4.0L TJ. If spark and injector pulse are both missing, this is your first suspect.',
        tip: 'A new CKP is $30. If you have crank-no-start with no spark and no injector pulse, just replace it.',
        commonMistake: 'Diagnosing past the CKP. It has a >50% probability of being the cause for that symptom set.',
      },
      {
        number: 8,
        title: 'Inspect Distributor Cap and Rotor (4.0L Only)',
        description: 'Pull the distributor cap. Inspect inside for: cracked plastic, carbon tracks (black streaks indicating arcing), corroded contacts, or moisture. The rotor should be intact and not show heavy wear at the tip. Replace cap and rotor if any of these are present.',
        why: 'A cracked cap or worn rotor causes intermittent or no spark. $30 fix vs hours of further diagnosis.',
        tip: 'TJs often develop hairline cracks in caps that only show under bright light. Replace as a precaution at 60k miles.',
        commonMistake: 'Skipping cap inspection because spark "seems present." A weak spark fails to ignite a flooded engine.',
      },
      {
        number: 9,
        title: 'Verify Ignition Coil',
        description: 'Disconnect the coil wire from the distributor. Connect a spark tester to the coil wire and a good ground. Crank engine — should see strong blue spark from the coil. Weak/no spark from the coil = coil failure. Test coil resistance (primary 0.5-1 ohm, secondary 8-15k ohms).',
        why: 'Coil failure is less common than CKP but does happen.',
        tip: 'A failing coil often works cold but fails when hot. If your no-start is hot-only, suspect coil.',
        commonMistake: 'Skipping coil test if individual cylinder spark looks weak.',
      },
      {
        number: 10,
        title: 'Recap Findings and Repair',
        description: 'By this step you should have identified one of: failed CKP sensor, failed fuel pump or relay, clogged fuel filter, failed coil, failed cap/rotor, broken wiring, or mechanical issue (compression). Replace the identified component (see the corresponding guide for procedure). After repair, verify the engine starts and runs smoothly.',
        why: 'Diagnosis-then-repair is more efficient than parts cannons.',
        tip: 'Keep a notebook or phone notes log of diagnostic steps and findings.',
        commonMistake: 'Replacing parts without confirming the diagnosis. Wasted money and continued no-start.',
      },
    ],
    torqueSpecifications: [
      { fastener: 'Spark plugs', specification: '13 ft-lbs (18 Nm). Verify torque spec from OEM service manual.', notes: 'Snug only.' },
      { fastener: 'Distributor cap screws', specification: 'Hand-snug.', notes: 'Do not over-tighten.' },
    ],
    fluidSpecifications: [],
    troubleshooting: [
      { problem: 'No codes, no spark, no injector pulse', likelyCause: 'CKP sensor failure or PCM failure', whatToCheck: 'Replace CKP first ($30 vs $300 PCM)', fix: 'Replace CKP sensor.' },
      { problem: 'Low fuel pressure (35 psi)', likelyCause: 'Failing fuel pump or clogged fuel filter', whatToCheck: 'Replace fuel filter first; if pressure still low, replace pump', fix: 'New fuel filter, then pump if needed.' },
      { problem: 'Strong spark and fuel pressure but no start', likelyCause: 'Mechanical issue (compression) or fuel/spark timing mismatch', whatToCheck: 'Compression test all cylinders', fix: 'Major engine repair if compression is low.' },
      { problem: 'Engine starts and dies immediately', likelyCause: 'Sentry key (immobilizer) issue or vacuum leak', whatToCheck: 'Watch for security light flash; check vacuum lines', fix: 'Reprogram key or repair vacuum leak.' },
      { problem: 'Cranks slow, then no-start', likelyCause: 'Battery weak or starter failing — not a true no-start', whatToCheck: 'Battery test and starter draw', fix: 'Replace battery or starter.' },
    ],
    proTips: [
      'CKP failure is the #1 no-start cause on the 4.0L TJ. Buy a spare and keep in glove box.',
      'A failed coil often works cold but dies hot. If no-start is hot-only, suspect coil.',
      'Free code scans at chain parts stores save buying a scanner for occasional use.',
      'Distributor cap and rotor replacement at 60k miles preempts many no-start issues.',
      'Keep a noid light kit in your toolbox — quickest injector circuit test.',
    ],
    commonMistakes: [
      { mistake: 'Replacing parts without diagnosis', consequence: 'Wasted money; problem may not be fixed.' },
      { mistake: 'Skipping OBD-II code scan', consequence: 'Codes often point directly to the issue.' },
      { mistake: 'Cranking continuously to "force" the engine to start', consequence: 'Starter overheats; floods cylinders with fuel.' },
    ],
    successIndicators: [
      'Engine starts on first or second crank',
      'Idle is smooth at correct RPM',
      'No check engine light',
      'No misfire or hesitation under load',
    ],
    whenToSeekHelp:
      'If after testing spark, fuel, and compression you cannot identify the cause, the issue may be electronic (PCM, wiring harness, immobilizer) — a shop with a scan tool that can read live data is the next step. Some no-starts are caused by sentry key (immobilizer) issues — these require dealer or specialized scan tools. PCM failure is rare but possible.',
    legalDisclaimer: 'This guide was generated by GarageIQ\'s AI from publicly available sources. Some information may be incorrect or out of date. Verify torque specifications and any safety-critical procedures against the OEM service manual for your vehicle before performing work. The Haynes Manual tab on this page links to the licensed manual for your vehicle. GarageIQ is not a substitute for a qualified mechanic.',
    manualSource: { title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)', url: 'https://www.haynes.com/en-us/wrangler/jeep/2003', notes: 'Cross-reference diagnostic procedures and component locations with your licensed manual.' },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — HARD COLD START DIAGNOSTIC (4.0L I6)
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-hard-cold-start-diagnostic-4pt0l-i6',
    title: 'Hard Cold Start Diagnostic (4.0L I6)',
    vehicle: { make: 'Jeep', model: 'Wrangler', generations: [{ startYear: 1997, endYear: 2006 }], engine: '4.0l-i6' },
    video: {
      youtubeId: '4ONu4NIauds',
      title: 'DIY Jeep Wrangler Crank No Start Troubleshooting',
      channel: 'BSK Garage',
    },
    source: 'Generated by GarageIQ AI from publicly available sources (Mopar service bulletins, NHTSA TSBs, owner\'s manual, technician forums and Wrangler community service threads). Cross-checked and structured per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Engine',
    difficulty: 'beginner',
    duration: '30 minutes - 1 hour diagnostic',
    interval: 'When cold starts require multiple cranks, extended cranking, or the engine fires-then-dies in cold weather.',
    description:
      'A 4.0L I6 that starts hard when cold (5-30 minutes after a cold soak) but starts fine when warm has a small list of likely culprits: leaking fuel injectors that bleed pressure overnight, a failing CTS (coolant temperature sensor) sending wrong cold-start enrichment, a failing IAC valve, or a worn distributor cap allowing moisture to track at idle. This guide walks the diagnosis in order of likelihood and cost.',
    overview: {
      difficulty: 'Beginner — most checks require basic tools and are visual',
      estimatedTime: '30 minutes - 1 hour',
      estimatedCost: '$0 to diagnose; $20-100 to fix depending on the cause',
      whyMatters: 'Hard cold start is annoying and indicates a small problem that will eventually become a no-start.',
      safetyNote: 'Test in a ventilated area — engine may produce raw fuel smell during diagnosis.',
    },
    tools: [
      'OBD-II scan tool',
      'Fuel pressure gauge',
      'Multimeter',
      'Test light',
      'Spray bottle of water (for finding cracks in distributor cap)',
    ],
    parts: [
      { id: 'replacement-cold-start', name: 'Replacement parts based on findings', partNumber: 'See guides for specific component replacement', quantity: 1, price: 30, category: 'conditional', notes: 'Buy after diagnosis.' },
    ],
    safetyInformation: 'Some testing involves cranking with components disconnected — work in a ventilated area to avoid raw fuel buildup. Disconnect battery before working on ignition components.',
    beforeYouStart: [
      'Note exactly when the hard start occurs (overnight only? after sitting 4 hours? when below 40°F?)',
      'Confirm fuel level is at least 1/4 tank (low fuel makes cold start worse)',
      'Verify engine has no current check engine light',
      'Have a phone-connected scanner ready for live data viewing',
    ],
    steps: [
      { number: 1, title: 'Note Cold-Start Symptoms', description: 'Document exactly: time since last run, ambient temperature, how long it cranks before firing, whether it fires-then-dies, and how it idles after first start. These details narrow the cause.', why: 'Different patterns point to different causes.', tip: 'Phone video the cold start with audio — captures crank duration and exhaust sound.', commonMistake: 'Vague description: "hard to start." Specifics matter.' },
      { number: 2, title: 'Check OBD-II Codes', description: 'Code-scan for any stored codes. P0117 (CTS), P0118 (CTS), P0507 (idle high), P0171 (lean) are common with hard cold start.', why: 'Codes often point directly to the problem.', tip: 'Free scans at chain auto-parts stores.', commonMistake: 'Skipping code check.' },
      { number: 3, title: 'Test Fuel Pressure Hold (Key Indicator)', description: 'Connect a fuel pressure gauge. Turn key to RUN (not start) — pressure builds to 49 psi. Turn key off. Pressure should hold at 40+ psi for 30 minutes. If it drops below 30 psi within 5 minutes, fuel injectors are leaking internally OR check valve in fuel pump is failing.', why: 'Lost fuel pressure overnight is THE most common cause of hard 4.0L cold start.', tip: 'A pressure drop of 10 psi over an hour is normal; a drop to 0 psi in 30 minutes is bad.', commonMistake: 'Skipping the pressure-hold test.' },
      { number: 4, title: 'Verify Coolant Temperature Sensor (CTS)', description: 'With engine cold, view CTS reading on a scan tool live data — should match ambient temperature. If it reads 100°F+ when actually 40°F, the CTS is failing and ECM does not provide cold-start enrichment.', why: 'A failed CTS removes the cold-start fuel enrichment, causing hard start.', tip: 'Cold reading vs ambient is the test; warm vehicle CTS reading does not help.', commonMistake: 'Trusting the dash gauge — that is a separate sender, not the CTS the ECM uses.' },
      { number: 5, title: 'Inspect Distributor Cap and Rotor', description: 'Pull the cap and inspect inside for moisture, cracks, carbon tracks, or corroded contacts. Spray water on the outside of the cap during cold start — if start improves or worsens, moisture is tracking through a crack.', why: 'A cracked cap allows arc-jumping when cold/damp; common 4.0L cold-start issue.', tip: 'Many TJs have hairline cap cracks invisible to direct light. Replace as preventive at 60k.', commonMistake: 'Skipping cap inspection because spark "looks fine" warm.' },
      { number: 6, title: 'Check IAC Valve Function', description: 'On a cold engine start, idle should immediately go to ~1200 RPM (high cold idle), then drop to ~750 RPM as engine warms. If cold idle stays low (~600-700 RPM), the IAC is not commanding the right airflow. Live data on scan tool shows IAC counts — should be high at cold start.', why: 'IAC failure means inadequate cold idle airflow; engine fires then dies.', tip: 'A throttle body cleaning often restores IAC function (see throttle body cleaning guide).', commonMistake: 'Replacing IAC before cleaning the body.' },
      { number: 7, title: 'Test Spark Plug Condition', description: 'Pull all six plugs. Inspect: black/sooty plugs indicate rich condition (CTS or injectors); white plugs indicate lean (vacuum leak or fuel pressure low); oil-fouled plugs indicate engine wear.', why: 'Plug condition reveals air-fuel mixture history.', tip: 'Photograph each plug labeled by cylinder for reference.', commonMistake: 'Replacing plugs without reading them first.' },
      { number: 8, title: 'Check for Vacuum Leaks', description: 'Spray throttle body cleaner around vacuum hoses, intake manifold gasket, and PCV system with engine running at idle. If RPM changes when sprayed, there is a leak at that point.', why: 'A vacuum leak causes lean cold start.', tip: 'Cold start vacuum leaks are worse because the engine is not yet at operating temperature.', commonMistake: 'Spraying directly into the intake (always changes RPM, false positive).' },
      { number: 9, title: 'Verify Battery / Starter Health', description: 'Slow cold cranking from a weak battery mimics hard cold start. Battery must hold 12.4+ volts at rest, and 9.6+ volts during cranking. Replace battery if weak.', why: 'Weak battery looks like hard start. Always rule out first.', tip: 'A battery 4+ years old in cold climate is suspect.', commonMistake: 'Diagnosing engine when battery is the issue.' },
      { number: 10, title: 'Apply Repair Based on Finding', description: 'Most 4.0L hard cold start cases trace to: (a) leaking injectors — replace the leakers, (b) failed CTS — $20 sensor, (c) cracked distributor cap — $20 fix, (d) worn IAC — clean or replace. After repair, do a 12-hour cold soak and verify start improvement.', why: 'Targeted repair is faster and cheaper than parts cannon.', tip: 'Cold-start logs over a week confirm the fix is durable.', commonMistake: 'Calling a fix complete after one good cold start. Test multiple cycles.' },
    ],
    torqueSpecifications: [
      { fastener: 'CTS sensor', specification: '15 ft-lbs (20 Nm). Verify torque spec from OEM service manual.', notes: 'Use thread sealant.' },
      { fastener: 'Distributor cap screws', specification: 'Hand-snug.', notes: 'Do not over-tighten.' },
    ],
    fluidSpecifications: [],
    troubleshooting: [
      { problem: 'Pressure drops to 0 in under 30 min', likelyCause: 'Fuel injector internal leak or pump check valve', whatToCheck: 'Pull plugs after sit; one with fuel-soaked plug = that injector leaks', fix: 'Replace leaking injector.' },
      { problem: 'CTS reads warm when cold', likelyCause: 'Failed sensor', whatToCheck: 'Compare scan tool reading to ambient temp', fix: 'Replace CTS ($20).' },
      { problem: 'Hard start only when damp/raining', likelyCause: 'Cracked distributor cap', whatToCheck: 'Inspect cap; spray water test', fix: 'Replace cap and rotor.' },
      { problem: 'Cold idle too low (under 800 RPM)', likelyCause: 'IAC valve dirty or failed', whatToCheck: 'Throttle body cleaning first; replace IAC if cleaning fails', fix: 'Clean throttle body and IAC; replace if needed.' },
      { problem: 'Plugs are black/wet on cold start', likelyCause: 'Over-fueling — failed CTS or stuck injector', whatToCheck: 'CTS reading; injector spray pattern', fix: 'Replace CTS or leaking injector.' },
    ],
    proTips: [
      'Hard cold start that improves with each crank usually = fuel pressure bleed-down (injectors).',
      'Hard start that gets worse over weeks = often CTS sensor failing.',
      'Hard start in damp/cold weather = distributor cap cracked.',
      'A scan tool with live data is the single most useful diagnostic tool for the 4.0L.',
      'Keep a log of cold start performance — patterns reveal the cause.',
    ],
    commonMistakes: [
      { mistake: 'Replacing fuel pump first', consequence: 'Pump usually is not the issue. Money wasted.' },
      { mistake: 'Skipping fuel pressure hold test', consequence: 'Misses the most common cause.' },
      { mistake: 'Trusting the dash temp gauge as the CTS reading', consequence: 'Dash and ECM use different sensors.' },
    ],
    successIndicators: [
      'Cold start fires within 1-2 cranks',
      'Engine catches and holds idle at high cold idle (~1200 RPM)',
      'Idle drops smoothly to 750 RPM as engine warms',
      'No black smoke or raw fuel smell during start',
      'Multiple cold soaks confirm consistent good start',
    ],
    whenToSeekHelp:
      'If multiple components have been tested and replaced and hard cold start persists, the cause may be wiring (corroded connector at CKP, CTS, or coil) or PCM failure — both require shop diagnosis. Sentry key issues can also masquerade as hard start; a dealer scan can confirm.',
    legalDisclaimer: 'This guide was generated by GarageIQ\'s AI from publicly available sources. Some information may be incorrect or out of date. Verify torque specifications and any safety-critical procedures against the OEM service manual for your vehicle before performing work. The Haynes Manual tab on this page links to the licensed manual for your vehicle. GarageIQ is not a substitute for a qualified mechanic.',
    manualSource: { title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)', url: 'https://www.haynes.com/en-us/wrangler/jeep/2003', notes: 'Cross-reference component locations and live-data ranges with your licensed manual.' },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — ENGINE OVERHEATING DIAGNOSTIC
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-engine-overheating-diagnostic',
    title: 'Engine Overheating Diagnostic',
    vehicle: { make: 'Jeep', model: 'Wrangler', generations: [{ startYear: 1997, endYear: 2006 }] },
    video: {
      youtubeId: 'Mzvd-JpzvGU',
      title: 'Jeep TJ Overheating issues Explained',
      channel: 'BSK Garage',
    },
    source: 'Generated by GarageIQ AI from publicly available sources (Mopar service literature, technician forums, Wrangler community service threads). Cross-checked and structured per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Cooling & Heating',
    difficulty: 'intermediate',
    duration: '30 minutes - 2 hours diagnostic',
    interval: 'Diagnose immediately when temperature gauge climbs above normal (above the ¾ mark on the TJ dash gauge). Continued running causes head gasket damage.',
    description:
      'Engine overheating on a TJ has a small list of likely causes, in roughly this order: low coolant, failed thermostat, clogged radiator, failed water pump, failed fan clutch, head gasket leak, or air pocket in cooling system. This guide walks the systematic diagnosis to identify which.',
    overview: {
      difficulty: 'Intermediate — uses pressure tester, IR thermometer, and basic visual inspection',
      estimatedTime: '30 minutes - 2 hours',
      estimatedCost: '$0 to diagnose; repair varies $20 thermostat to $300+ radiator',
      whyMatters: 'Continued overheating warps the cylinder head and ruins the head gasket. Diagnose-and-fix immediately.',
      safetyNote: 'NEVER open the radiator cap on a hot engine. Wait for full cool-down (1+ hours). Hot coolant can spray with severe burn risk.',
    },
    tools: ['Cooling system pressure tester', 'IR (non-contact) thermometer', 'OBD-II scan tool with live data', 'Combustion gas (block) tester', 'Flashlight'],
    parts: [{ id: 'replacement-overheating', name: 'Replacement parts based on findings', partNumber: 'See thermostat, water pump, radiator, fan clutch guides', quantity: 1, price: 30, category: 'conditional', notes: 'Buy after diagnosis identifies cause.' }],
    safetyInformation: 'NEVER open the radiator cap on a hot engine. Allow 1+ hours of cool-down. Hot coolant burns severely. Wear eye protection during pressure testing. Used coolant is toxic — clean spills.',
    beforeYouStart: ['Allow engine to fully cool', 'Note when overheat occurs (idle? highway? climbing? AC on?)', 'Check coolant level in overflow tank', 'Have pressure tester available'],
    steps: [
      { number: 1, title: 'Verify Coolant Level First', description: 'With engine cold, check coolant level in the overflow reservoir (FULL COLD line) and the radiator (full to neck). Low coolant is the #1 overheat cause and easiest to fix.', why: 'Air in the system cannot transfer heat. Most overheats start as low coolant.', tip: 'If low, top off and inspect for visible leaks.', commonMistake: 'Skipping the basic level check.' },
      { number: 2, title: 'Check Conditions When Overheat Occurs', description: 'IDLE-ONLY overheat = fan clutch or electric fan failure (poor airflow at low speed). HIGHWAY-ONLY = water pump or thermostat. BOTH = radiator clog or head gasket. AC-ON ONLY = aux fan / fan clutch struggling under extra load.', why: 'The pattern narrows the cause significantly.', tip: 'Document the exact conditions with notes or phone notes.', commonMistake: 'Diagnosing without knowing the pattern.' },
      { number: 3, title: 'Pressure Test the Cooling System', description: 'With engine cold, attach pressure tester to radiator filler. Pump to 16 psi. Hold for 15 minutes. Pressure should hold steady. Drop indicates a leak (external or internal) — investigate the source.', why: 'A holding system rules out leaks; a dropping system points to the leak.', tip: 'See radiator cap pressure test guide for full procedure.', commonMistake: 'Skipping pressure test.' },
      { number: 4, title: 'Inspect the Radiator', description: 'Look at the radiator fins from front and rear with a flashlight — bent fins or insect-clogged areas reduce airflow. Internal corrosion shows as green stains on the radiator side tanks. With engine warm, use IR thermometer to measure radiator surface — should be HOT at top inlet, slightly cooler at bottom outlet (normal flow). Cold spots indicate internal blockage.', why: 'A clogged radiator cannot dissipate heat regardless of pump or thermostat health.', tip: 'TJs with original radiators (15+ years old) commonly clog internally — the brass-to-plastic transition tubes corrode.', commonMistake: 'Trusting visual external inspection only.' },
      { number: 5, title: 'Test Thermostat Function', description: 'Watch the upper radiator hose during warm-up. Should be COLD at first (thermostat closed), then suddenly become hot when thermostat opens (around 195°F). If hose stays cold while engine overheats, thermostat is stuck closed. If hose is hot from start, thermostat is stuck open (engine runs cold, but rare).', why: 'A stuck thermostat is one of the most common overheats.', tip: 'A new thermostat is $20 — replace as a routine cost-of-troubleshooting if this test is ambiguous.', commonMistake: 'Trusting that "I just replaced the thermostat" — defective new thermostats happen.' },
      { number: 6, title: 'Verify Water Pump Function', description: 'With engine warm and idling, briefly squeeze the upper radiator hose — you should feel pulses of flow. No pulses = water pump impeller failure. Inspect water pump weep hole (small drain hole on bottom of pump body) for wetness — a leaking pump shows weep.', why: 'A failed pump impeller cannot move coolant.', tip: 'Most TJ water pump failures show weep before catastrophic failure.', commonMistake: 'Replacing pump without confirming impeller failure.' },
      { number: 7, title: 'Check Fan Clutch (Idle Test)', description: 'With engine warm, watch the cooling fan at idle. The fan clutch should engage when temps rise — fan should spin VIGOROUSLY (you can hear loud roar). If it spins slowly even at high temps, fan clutch is failed.', why: 'Failed fan clutch = inadequate airflow at low speeds = idle overheat.', tip: 'A fan clutch test: with engine OFF, spin fan by hand. Cold, it should spin freely; warm engine off, it should resist due to engaged clutch.', commonMistake: 'Diagnosing fan clutch with engine off and cold.' },
      { number: 8, title: 'Test for Head Gasket Leak (Combustion Gas)', description: 'Use a block tester (UView 560000) — attaches to radiator filler, sucks air through a chemical fluid. Blue fluid turning yellow/green confirms combustion gases in coolant = head gasket leak. Alternative: smell exhaust at the tailpipe — sweet smell may indicate coolant burning.', why: 'Internal head gasket leak causes overheating with no external coolant loss.', tip: 'Block tester is $30-60 or available as auto-parts loaner.', commonMistake: 'Assuming overheat without external leak = "must be radiator." It is often head gasket.' },
      { number: 9, title: 'Check for Air Pocket in System', description: 'After any cooling system service, an air pocket can cause overheating. Bleed by: parking nose-up on a hill, opening radiator with engine cold, starting and running with the cap off until air bubbles stop and coolant is full. Some TJs have a bleed screw on the thermostat housing.', why: 'Air pockets prevent coolant flow to the head, causing localized overheat.', tip: 'After ANY cooling work, do a thorough bleed.', commonMistake: 'Assuming the system bled itself.' },
      { number: 10, title: 'Apply Targeted Repair', description: 'Based on findings: thermostat ($20 + 30 min), water pump ($35 + 2 hr), radiator ($150-300 + 1 hr), fan clutch ($60 + 30 min), or head gasket repair (major job). After repair, monitor temperatures over multiple drive cycles to confirm fix.', why: 'Targeted repair fixes the issue without over-spending.', tip: 'Always replace radiator cap during cooling work — $10 cheap insurance.', commonMistake: 'Replacing parts without diagnosis.' },
    ],
    torqueSpecifications: [{ fastener: 'Thermostat housing bolts', specification: '15 ft-lbs (20 Nm). Verify torque spec from OEM service manual.', notes: 'Standard.' }],
    fluidSpecifications: [{ fluid: 'Engine coolant', type: 'HOAT (Mopar 5012394AB)', capacity: 'System: ~10 quarts', notes: 'Match what is in the system.' }],
    troubleshooting: [
      { problem: 'Idle overheats, highway is fine', likelyCause: 'Fan clutch failure', whatToCheck: 'Fan engagement at idle when warm', fix: 'Replace fan clutch.' },
      { problem: 'Highway overheats, idle is fine', likelyCause: 'Water pump or thermostat', whatToCheck: 'Hose pulsing; thermostat function', fix: 'Replace failed component.' },
      { problem: 'Both idle and highway overheat', likelyCause: 'Radiator clog or head gasket', whatToCheck: 'Radiator IR test; combustion gas test', fix: 'Replace radiator or head gasket repair.' },
      { problem: 'Coolant loss but no visible leak', likelyCause: 'Head gasket internal leak', whatToCheck: 'Combustion gas test; check oil for emulsion', fix: 'Head gasket repair.' },
      { problem: 'Overheats only with AC on', likelyCause: 'Aux fan failure or marginal cooling capacity', whatToCheck: 'AC fan operation', fix: 'Replace aux fan or repair wiring.' },
    ],
    proTips: ['Always pressure-test FIRST — narrows external vs internal cause.', 'IR thermometer reveals which part of the cooling path is restricted.', 'A new radiator cap is $10 cheap insurance.', 'Document overheat conditions precisely.', 'Replacing radiator at 15+ years preventatively saves a future tow bill.'],
    commonMistakes: [
      { mistake: 'Continuing to drive while overheating', consequence: 'Head gasket fails. $1,500+ repair.' },
      { mistake: 'Skipping pressure test', consequence: 'Misses small leaks that cause repeat overheat.' },
      { mistake: 'Replacing thermostat as first reflex', consequence: 'Often not the cause; money saved would diagnose other systems.' },
    ],
    successIndicators: ['Coolant temp stable at thermostat opening (~195°F) at idle and highway', 'No coolant loss between drive cycles', 'No combustion gas in coolant (block test passes)', 'Heat works correctly in cabin (good water pump flow)'],
    whenToSeekHelp: 'If diagnosis identifies head gasket failure or a cracked head, professional repair may be warranted depending on engine condition. If pressure test holds but engine still overheats, investigate water pump or radiator further — internal flow is harder to verify visually.',
    legalDisclaimer: 'This guide was generated by GarageIQ\'s AI from publicly available sources. Some information may be incorrect or out of date. Verify torque specifications and any safety-critical procedures against the OEM service manual for your vehicle before performing work. Used coolant is toxic and is hazardous waste.',
    manualSource: { title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)', url: 'https://www.haynes.com/en-us/wrangler/jeep/2003', notes: 'Cross-reference cooling system specs with your licensed manual.' },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — BRAKE PEDAL SINKS DIAGNOSTIC
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-brake-pedal-sinks-diagnostic',
    title: 'Brake Pedal Sinks to Floor (Diagnostic)',
    vehicle: { make: 'Jeep', model: 'Wrangler', generations: [{ startYear: 1997, endYear: 2006 }] },
    video: {
      youtubeId: 'YdYSjxB9RX4',
      title: 'Installing Jeep TJ Master Cylinder and Bleeding Brakes',
      channel: 'Rust Revival Garage',
    },
    source: 'Generated by GarageIQ AI from publicly available sources (Mopar service literature, technician forums). Cross-checked and structured per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Brakes & Steering',
    difficulty: 'beginner',
    duration: '15-30 minutes diagnostic',
    interval: 'When pedal feels soft, sinks under sustained pressure, or goes to floor without firmness.',
    description: 'A brake pedal that sinks to the floor — either with a slow leak feel (degrades over miles) or sudden loss — points to one of: failing master cylinder (internal leak, no external sign), a rear wheel cylinder leak, a brake hose bursting, or air in the system after recent service. This guide isolates which.',
    overview: { difficulty: 'Beginner — visual inspection and a static pedal test', estimatedTime: '15-30 minutes', estimatedCost: '$0 to diagnose; repair varies $30 wheel cylinder to $90 master cylinder', whyMatters: 'Brake pedal sinking is a safety issue. Diagnose and repair before driving in traffic.', safetyNote: 'Brakes are safety-critical. Do not drive in traffic until cause is identified and fixed.' },
    tools: ['Flashlight', 'Brake bleeder kit', 'Drain pan'],
    parts: [{ id: 'replacement-pedal', name: 'Replacement parts based on findings', partNumber: 'See brake master, hose, line, wheel cylinder guides', quantity: 1, price: 50, category: 'conditional', notes: 'Buy after diagnosis.' }],
    safetyInformation: 'Brake fluid is corrosive to paint and harmful to eyes. Brakes are safety-critical — do not drive in traffic until repaired. Used brake fluid is hazardous waste.',
    beforeYouStart: ['Confirm reservoir level — low fluid indicates a leak', 'Note when pedal sinks (always? after sitting? under sustained pressure?)', 'Look under the vehicle for any obvious wet spots or fluid drips'],
    steps: [
      { number: 1, title: 'Check Brake Fluid Reservoir', description: 'Open the master cylinder reservoir cap. If fluid is low (below MIN), there is a leak somewhere. If fluid is at correct level, the leak is internal (master cylinder).', why: 'Reservoir level immediately separates external from internal leak.', tip: 'Note whether fluid is dark brown — old fluid that needs flushing.', commonMistake: 'Topping off without identifying the leak.' },
      { number: 2, title: 'Static Pedal Hold Test', description: 'With engine off, pump pedal until firm. Apply firm steady pressure for 30 seconds. If pedal slowly sinks toward floor with no external leak — internal master cylinder leak. If pedal holds firm — issue is air in system, hose bulging, or one-time leak event.', why: 'The static test isolates the most common failure (master cylinder internal leak).', tip: 'A digital timer makes the 30-second test exact.', commonMistake: 'Trusting a "feels soft" without timed test.' },
      { number: 3, title: 'Inspect All Wheel Cylinders and Calipers', description: 'Crawl under each wheel and inspect: rear wheel cylinders for fluid weep at the boot, front calipers for piston seal leaks (look for fluid behind the piston dust boot), brake hoses for swelling or cracks. Any wetness = leak source.', why: 'Wheel cylinder failure is the second most common cause.', tip: 'A small mirror helps inspect wheel cylinder areas without removing drums.', commonMistake: 'Skipping rear inspection because pedal feels normal — wheel cylinder leaks can be slow.' },
      { number: 4, title: 'Inspect Brake Hoses', description: 'Visually inspect each flexible brake hose. Look for: cracks in outer rubber, swelling under pressure (have a helper press pedal hard while you watch), or fluid weep at the fittings. Replace any damaged hose.', why: 'A failing hose can swell and act as a one-way valve, causing soft pedal.', tip: 'Old hoses crack on the outside but the inner liner can also collapse — sometimes invisible externally.', commonMistake: 'Assuming a hose is fine because it looks ok.' },
      { number: 5, title: 'Inspect Brake Lines (Hard)', description: 'Crawl underneath and inspect the steel brake lines along the chassis. Look for: corroded sections, fluid weep at fittings, kinks, or impact damage. TJs in salt-belt areas have hard lines that fail at the rear shackle area.', why: 'A burst hard line releases pressure suddenly and pedal goes to floor.', tip: 'A small mirror helps inspect the underside of the lines.', commonMistake: 'Missing a hard line failure on the chassis underside.' },
      { number: 6, title: 'Check Brake Master Cylinder', description: 'Inspect the master cylinder for external leaks at the firewall. If reservoir level is correct and pedal sinks under sustained pressure, the master cylinder is leaking internally and must be replaced.', why: 'Internal master leak shows no external sign — only the static pedal test catches it.', tip: 'Master cylinder failure is the most common cause of slow-sink pedal.', commonMistake: 'Replacing wheel cylinders without testing master cylinder.' },
      { number: 7, title: 'Check for Air in System', description: 'If you recently did brake work, air may be in the system. Bleed all four corners in standard sequence (passenger rear, driver rear, passenger front, driver front).', why: 'Air bleeds out and pedal firms up.', tip: 'A one-person check-valve bleeder bottle works well solo.', commonMistake: 'Mistaking air-in-system for master cylinder failure.' },
      { number: 8, title: 'Test ABS System (If Applicable)', description: 'If TJ has ABS and pedal pulses or stays soft after bleeding all four corners, the ABS module may need a scan-tool bleed at a shop.', why: 'ABS module can trap air that manual bleeding cannot remove.', tip: 'Scan-tool ABS bleed at a shop is $50-80.', commonMistake: 'Assuming standard bleed clears all air on ABS-equipped TJ.' },
      { number: 9, title: 'Verify Pedal Returns Properly', description: 'Pedal should return to fully up when released. If it stays partially down or returns slowly, the master cylinder pushrod adjustment may be wrong, or the master cylinder is binding internally.', why: 'A binding master cylinder traps fluid pressure unevenly.', tip: 'Pedal should return to within 1/2 inch of fully up position.', commonMistake: 'Ignoring slow pedal return.' },
      { number: 10, title: 'Apply Targeted Repair', description: 'Based on findings: master cylinder ($90, see Master Cylinder guide), wheel cylinder ($30/side), brake hose ($18), or brake line repair ($25 plus tools). After repair, do a thorough bleed and verify pedal feel before driving.', why: 'Repair specific to diagnosis avoids parts cannon.', tip: 'Always do empty-lot test drive before traffic.', commonMistake: 'Replacing parts without confirming diagnosis.' },
    ],
    torqueSpecifications: [{ fastener: 'Brake bleeder screws', specification: '7-9 ft-lbs (10-12 Nm).', notes: 'Snug only.' }],
    fluidSpecifications: [{ fluid: 'Brake fluid', type: 'DOT 3', capacity: '~32 oz for full bleed', notes: 'Use fresh fluid.' }],
    troubleshooting: [
      { problem: 'Reservoir empty, no visible leak', likelyCause: 'Slow internal leak in master cylinder', whatToCheck: 'Static pedal hold test', fix: 'Replace master cylinder.' },
      { problem: 'One wheel has fluid behind drum/caliper', likelyCause: 'Wheel cylinder or caliper seal failure', whatToCheck: 'Confirm wet area is brake fluid', fix: 'Replace wheel cylinder or caliper.' },
      { problem: 'Pedal pulses when applied', likelyCause: 'ABS issue or warped rotor', whatToCheck: 'ABS warning light', fix: 'ABS scan or rotor service.' },
      { problem: 'Pedal soft only after recent service', likelyCause: 'Air in system from incomplete bleed', whatToCheck: 'Re-bleed all four corners', fix: 'Bleed in correct sequence.' },
      { problem: 'Pedal sinks faster on cold mornings', likelyCause: 'Master cylinder cup seal hardening with age', whatToCheck: 'Static hold test cold and warm', fix: 'Replace master cylinder.' },
    ],
    proTips: ['Static 30-second pedal hold test catches internal master cylinder leaks that have no external sign.', 'Pressure-test the brake system if leak source is unclear.', 'Replace brake fluid every 2-3 years — old fluid contributes to master cylinder failure.', 'Inspect every wheel even if pedal feels OK — slow leaks may not yet be evident.', 'Brake hose age (15+ years) is a real factor on TJs.'],
    commonMistakes: [
      { mistake: 'Driving with sinking pedal', consequence: 'Total brake failure possible. Severe safety risk.' },
      { mistake: 'Topping off fluid without finding leak', consequence: 'Leak continues; problem worsens.' },
      { mistake: 'Replacing master cylinder before checking all wheels for external leaks', consequence: 'Wasted money if wheel cylinder was the cause.' },
    ],
    successIndicators: ['Pedal firm, holds at same height for 30+ seconds under sustained pressure', 'No fluid leaks at any wheel or in engine bay', 'Brake fluid level remains at FULL', 'Pedal returns fully when released', 'No pull, no spongy feel'],
    whenToSeekHelp: 'If pedal stays soft despite multiple bleeds and no leaks, ABS-equipped TJs may need a scan-tool bleed at a shop. If brake hard lines are corroded throughout, professional brake-line replacement may be warranted.',
    legalDisclaimer: 'Brakes are safety-critical. This guide was generated by GarageIQ\'s AI from publicly available sources. Verify torque specifications against OEM. If unclear, consult a professional. Used brake fluid is hazardous waste.',
    manualSource: { title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)', url: 'https://www.haynes.com/en-us/wrangler/jeep/2003', notes: 'Cross-reference brake system diagrams with your licensed manual.' },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — COOLANT LOSS NO VISIBLE LEAK DIAGNOSTIC
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-coolant-loss-no-leak-diagnostic',
    title: 'Coolant Loss with No Visible Leak (Diagnostic)',
    vehicle: { make: 'Jeep', model: 'Wrangler', generations: [{ startYear: 1997, endYear: 2006 }] },
    video: {
      youtubeId: 'Mzvd-JpzvGU',
      title: 'Jeep TJ Overheating issues Explained',
      channel: 'BSK Garage',
    },
    source: 'Generated by GarageIQ AI from publicly available sources. Cross-checked per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Cooling & Heating',
    difficulty: 'intermediate',
    duration: '30 minutes - 1 hour',
    interval: 'When coolant level drops without visible drips on the ground or wetness on the engine.',
    description: 'Coolant disappearing without visible external leak typically points to one of three causes: heater core leak (often inside the cabin, wet passenger floor), head gasket leak (combustion gases push coolant into combustion chamber and out the exhaust), or a small external leak you have not yet found (under the vehicle while parked).',
    overview: { difficulty: 'Intermediate — uses pressure tester and combustion gas test', estimatedTime: '30 minutes - 1 hour', estimatedCost: '$0 to diagnose; head gasket vs heater core repairs vary widely', whyMatters: 'Continued coolant loss eventually causes overheat and engine damage.', safetyNote: 'Allow engine to fully cool before pressure-testing. Used coolant is toxic.' },
    tools: ['Cooling system pressure tester', 'Combustion gas (block) tester', 'UV dye additive (optional)', 'UV flashlight (with dye)', 'Flashlight'],
    parts: [{ id: 'replacement-coolant-loss', name: 'Replacement parts based on findings', partNumber: 'See heater core, head gasket guides', quantity: 1, price: 30, category: 'conditional', notes: 'Buy after diagnosis.' }],
    safetyInformation: 'Allow engine to fully cool before opening the cooling system. Used coolant is toxic. Combustion-gas tester chemicals are also irritating; use eye protection.',
    beforeYouStart: ['Note rate of coolant loss (1 quart per week vs per month)', 'Check for white exhaust smoke', 'Inspect passenger floor for wetness', 'Check oil dipstick for milkshake-like emulsion'],
    steps: [
      { number: 1, title: 'Pressure Test (Cold)', description: 'Pressure-test cooling system to 16 psi when cold. Hold for 15-30 minutes. Pressure dropping with no visible external leak strongly suggests internal leak.', why: 'Pressure drop without external leak is the diagnostic signature.', tip: 'See radiator cap pressure test guide for procedure.', commonMistake: 'Skipping pressure test.' },
      { number: 2, title: 'Inspect Passenger Floor for Heater Core Leak', description: 'Pull back the passenger-side floor mat. Feel for wetness or sweet smell. Heater core leaks puddle in the floor or fog the windshield with sweet-smelling steam during heater use.', why: 'Heater core leak is the most common "invisible" coolant loss.', tip: 'Run heat at full and watch for fogged-up windshield from inside.', commonMistake: 'Ignoring passenger floor.' },
      { number: 3, title: 'Smell the Exhaust', description: 'With engine warm and idling, smell the exhaust at the tailpipe. A sweet, gelatinous smell or wet residue at the tailpipe indicates coolant burning in cylinders = head gasket.', why: 'Coolant in combustion chamber burns and emits sweet smell.', tip: 'A clean white shop towel held at the tailpipe collects coolant residue.', commonMistake: 'Misinterpreting normal exhaust steam (water vapor) for coolant.' },
      { number: 4, title: 'Check Oil for Coolant Emulsion', description: 'Pull dipstick and inspect oil. Coolant in oil shows as a milky, mayonnaise-like film on the dipstick or under the oil cap. This indicates head gasket or intake gasket failure.', why: 'Oil-coolant mix confirms internal leak.', tip: 'Brand-new mayonnaise look needs immediate attention — engine bearing damage starts within hours.', commonMistake: 'Calling small condensation under oil cap "coolant in oil" — condensation alone is normal.' },
      { number: 5, title: 'Combustion Gas Test', description: 'Use a block tester (UView 560000 or similar) attached to radiator filler. The chemical fluid changes color when CO2 from combustion is present in coolant — confirms head gasket leak.', why: 'Definitive test for head gasket leak.', tip: 'Block testers cost $30-60 or available as auto-parts loaner.', commonMistake: 'Skipping the combustion gas test when other signs are ambiguous.' },
      { number: 6, title: 'UV Dye Test for Small External Leak', description: 'Add UV dye to coolant. Drive 100 miles. Inspect engine bay and underbody with UV flashlight at night. Dye glows green/yellow at any leak point — even tiny weeps.', why: 'Small leaks not visible during pressure test may show under driving conditions.', tip: 'A small thermostat-housing or hose-clamp seep is easy to miss without dye.', commonMistake: 'Skipping UV dye when pressure test is ambiguous.' },
      { number: 7, title: 'Inspect Around Thermostat Housing', description: 'Common small-leak point: the thermostat housing gasket seeping. Inspect during pressure test for any moisture at the housing.', why: 'Thermostat housing leaks are easy to miss because the leak path runs down the engine block.', tip: 'A new thermostat gasket is $5.', commonMistake: 'Overlooking the thermostat housing.' },
      { number: 8, title: 'Check Water Pump Weep', description: 'Inspect the small drain hole on the bottom of the water pump body. Wetness here indicates pump seal leak — coolant escapes and may evaporate before reaching the floor.', why: 'Water pump leaks evaporate quickly and may not show on ground.', tip: 'A pump that weeps will eventually catastrophically fail.', commonMistake: 'Missing the weep hole — it is small and inconspicuous.' },
      { number: 9, title: 'Inspect Hose Clamps and Connections', description: 'Slow seeping at hose clamps releases small amounts of coolant that evaporate. Pressure test reveals these — check every clamp during pressurization.', why: 'Loose clamps are common and easy fixes.', tip: 'Re-torque all clamps as preventive maintenance.', commonMistake: 'Trusting visual inspection of clamps without pressure.' },
      { number: 10, title: 'Apply Targeted Repair', description: 'Based on findings: heater core ($60 part + 4-8 hours labor), head gasket repair (major job — see Head R&R guide), small external leak (cheap fix). Confirm with another pressure test after repair.', why: 'Targeted repair fixes the issue without over-spending.', tip: 'Heater core leaks can sometimes be temporarily sealed with stop-leak — but proper repair is needed eventually.', commonMistake: 'Ignoring slow coolant loss until major engine damage.' },
    ],
    torqueSpecifications: [],
    fluidSpecifications: [{ fluid: 'Engine coolant', type: 'HOAT', capacity: 'Top-off as needed', notes: 'Match what is in the system.' }],
    troubleshooting: [
      { problem: 'Wet passenger floor', likelyCause: 'Heater core leak', whatToCheck: 'Carpet/floor mat wetness', fix: 'Replace heater core.' },
      { problem: 'White smoke from exhaust', likelyCause: 'Head gasket / cracked head', whatToCheck: 'Combustion gas test', fix: 'Head gasket repair.' },
      { problem: 'Milky oil', likelyCause: 'Head gasket / intake gasket', whatToCheck: 'Drain oil and inspect', fix: 'Head gasket repair.' },
      { problem: 'No internal evidence, slow loss', likelyCause: 'Tiny external leak (clamp, gasket, hose)', whatToCheck: 'UV dye test', fix: 'Repair specific leak point.' },
      { problem: 'Coolant loss only when driving', likelyCause: 'Pressure-only leak (cap, water pump weep)', whatToCheck: 'Cap test; water pump weep', fix: 'Replace cap or pump.' },
    ],
    proTips: ['UV dye + flashlight is the best small-leak finder.', 'Block test definitively confirms or rules out head gasket.', 'Heater core leak signs: wet floor, sweet smell, foggy windshield.', 'Ignoring slow coolant loss leads to overheat and major engine damage.', 'Pressure test before adding stop-leak — stop-leak masks but does not cure.'],
    commonMistakes: [
      { mistake: 'Adding stop-leak instead of diagnosing', consequence: 'Masks problem temporarily; can clog heater core and radiator.' },
      { mistake: 'Driving until overheat', consequence: 'Head gasket damage. Major repair.' },
      { mistake: 'Ignoring milky oil', consequence: 'Bearing failure within hours of operation.' },
    ],
    successIndicators: ['Coolant level stays stable between drive cycles', 'No wetness on passenger floor', 'No white exhaust smoke', 'No milky oil'],
    whenToSeekHelp: 'Head gasket replacement on a TJ may be a DIY job (see Head R&R guide) but requires significant time and tools. Heater core replacement requires partial dash disassembly — many owners take this to a shop. Block tests at shops are quick and definitive.',
    legalDisclaimer: 'Used coolant is toxic and is hazardous waste. Verify diagnostic procedures with your licensed manual.',
    manualSource: { title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)', url: 'https://www.haynes.com/en-us/wrangler/jeep/2003', notes: 'Cross-reference cooling diagrams with your licensed manual.' },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — BATTERY DRAINS OVERNIGHT DIAGNOSTIC
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-battery-drain-diagnostic',
    title: 'Battery Drains Overnight (Parasitic Draw Diagnostic)',
    vehicle: { make: 'Jeep', model: 'Wrangler', generations: [{ startYear: 1997, endYear: 2006 }] },
    video: {
      youtubeId: '4hhFK1mH8PQ',
      title: 'How to Clean your Jeep Wrangler Battery Terminals (related diagnostic reference)',
      channel: 'Jeep Bros GTA',
    },
    source: 'Generated by GarageIQ AI from publicly available sources. Cross-checked per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Electrical',
    difficulty: 'intermediate',
    duration: '1-2 hours diagnostic',
    interval: 'When battery is dead in the morning despite normal daytime use, or when battery drains over a few days of non-use.',
    description: 'A parasitic draw is current the vehicle pulls when the key is off and everything appears off. Normal draw is under 50 milliamps; over that drains the battery overnight or over a few days. The TJ\'s common culprits: aftermarket stereo, glove box light staying on, alarm/sentry key issue, alternator diode failure, or a stuck relay. This guide finds the offender by progressively pulling fuses.',
    overview: { difficulty: 'Intermediate — requires multimeter and methodical fuse pulling', estimatedTime: '1-2 hours', estimatedCost: '$0 to diagnose; repair varies', whyMatters: 'Repeated drain shortens battery life from 5 years to 1-2 years.', safetyNote: 'Working with battery — sparks possible. Disconnect with care.' },
    tools: ['Digital multimeter with 10A current capability', 'Memory saver (optional, keeps clock/radio settings)', 'Spare fuses', 'Patience'],
    parts: [{ id: 'battery-drain-replacement', name: 'Replacement parts based on findings', partNumber: 'Varies', quantity: 1, price: 30, category: 'conditional', notes: 'Buy after diagnosis.' }],
    safetyInformation: 'Disconnecting battery while diagnostic equipment is in current path can blow your meter\'s 10A fuse. Use proper technique. ECMs and modules have wake-up time of 30+ minutes — wait before measuring.',
    beforeYouStart: ['Confirm battery is healthy (load test passes; voltage 12.6V at rest)', 'Confirm alternator charges to 13.8-14.4V running', 'Note when drain occurs (overnight only, or over days?)', 'Have a multimeter rated for at least 10A current'],
    steps: [
      { number: 1, title: 'Verify Battery and Alternator First', description: 'Load-test battery at parts store (free). Check alternator output: 13.8-14.4V at idle, climbing slightly with load. If battery fails load test = battery is the problem, not parasitic draw. If alternator under-charges = charging issue, not draw.', why: 'Rule out battery/charging issues before chasing parasitic draw.', tip: 'Free testing at most parts stores.', commonMistake: 'Diagnosing draw on a bad battery.' },
      { number: 2, title: 'Set Up Multimeter for Current Measurement', description: 'Set multimeter to 10A DC current. Move red probe to the 10A jack. Disconnect negative battery cable. Connect multimeter between negative cable and battery negative terminal — meter is now in series with all vehicle current.', why: 'In-series setup measures ALL parasitic current.', tip: 'Use clamps or gators on the meter probes — keeps hands free.', commonMistake: 'Using volts setting (1V max) instead of 10A.' },
      { number: 3, title: 'Wait for Modules to Sleep (30 minutes)', description: 'After connecting meter, wait 30 minutes WITHOUT touching anything. The PCM, BCM, and other modules have programmed sleep times — they stay awake for 20-30 minutes after key-off. Initial reading will be high but should drop to under 50 mA after sleep.', why: 'Reading taken too early includes normal module-awake current.', tip: 'A timer on the phone confirms 30 minutes elapsed.', commonMistake: 'Reading immediately and concluding excessive draw.' },
      { number: 4, title: 'Read Steady-State Current', description: 'After 30 min wait, note current reading. Normal: under 50 mA (0.05A). Above 100 mA (0.1A) indicates a problem. Above 500 mA = significant drain.', why: 'Steady-state current is the actual parasitic draw.', tip: 'Note exact reading for comparison after each fuse pull.', commonMistake: 'Dismissing a 200 mA reading as "small."' },
      { number: 5, title: 'Pull Fuses One at a Time', description: 'Working at the under-hood fuse box, pull each fuse one at a time and watch the meter. When you pull the fuse that causes a significant drop in current, you have found the circuit with the draw. Replace each fuse before pulling the next, EXCEPT keep the offending circuit\'s fuse out for testing.', why: 'Process of elimination identifies the bad circuit.', tip: 'Note which fuse drops current and its label (radio, dome light, etc.).', commonMistake: 'Pulling multiple fuses at once. Cannot identify which.' },
      { number: 6, title: 'Identify the Component Within the Circuit', description: 'Once the offending circuit is identified, look at the fuse label. Common: radio (aftermarket stereo with constant memory leak), interior lights (door switch stuck on), alarm/keyless system (sentry key module fault). Check the wire diagram for that fuse to identify components.', why: 'The fuse label tells you the system; further inspection finds the specific component.', tip: 'Aftermarket stereos are the most common TJ draw source.', commonMistake: 'Stopping at the fuse without identifying the actual failed part.' },
      { number: 7, title: 'Test for Glove Box / Dome Light', description: 'Common drain: glove box light or dome light stuck on. Open glove box — switch should click off as door closes; if not, switch is stuck. Manually press dome light switch with door closed — should click off. Replace switch if stuck.', why: 'Stuck interior light switches are a $5-15 fix and account for many "mysterious" drains.', tip: 'Glove box light bulb with door closed: should be cool to touch overnight.', commonMistake: 'Overlooking interior switches.' },
      { number: 8, title: 'Test Alternator Diodes', description: 'A failed alternator diode allows back-feed current that drains battery. With multimeter on AC volts, measure across battery terminals while engine is off — should be near 0 V AC. AC voltage indicates a failed diode.', why: 'Failed alternator diode is a real but less common drain source.', tip: 'AC ripple over 0.1V AC indicates diode issue.', commonMistake: 'Skipping alternator test when fuse-pull does not find drain.' },
      { number: 9, title: 'Inspect Ignition Switch and Sentry Key', description: 'On TJs with sentry key (immobilizer), if the security indicator stays lit with key out, the module may be stuck in a constant draw. Check the dash security light behavior.', why: 'Sentry key module faults are tricky — may require dealer scan.', tip: 'A constantly-lit security light is the indicator.', commonMistake: 'Dismissing a stuck security light.' },
      { number: 10, title: 'Apply Targeted Repair', description: 'Based on findings: replace stuck switch, fix or replace aftermarket stereo, replace alternator (if diode failure), or repair sentry key wiring. After repair, repeat draw test to confirm under 50 mA.', why: 'Targeted repair eliminates the drain.', tip: 'Document the cause for future reference — battery age may be next concern.', commonMistake: 'Calling fixed before verifying with another draw test.' },
    ],
    torqueSpecifications: [{ fastener: 'Battery terminal nuts', specification: '70 in-lbs (8 Nm).', notes: 'Snug — do not crack the post.' }],
    fluidSpecifications: [],
    troubleshooting: [
      { problem: 'Drain only with aftermarket stereo', likelyCause: 'Stereo memory circuit drawing too much', whatToCheck: 'Pull stereo fuse — drain disappears', fix: 'Repair stereo wiring or replace stereo with one with proper sleep behavior.' },
      { problem: 'Drain stops when glove box opened/closed', likelyCause: 'Stuck glove box light switch', whatToCheck: 'Switch click test', fix: 'Replace switch or remove bulb.' },
      { problem: 'AC voltage on battery with engine off', likelyCause: 'Alternator diode failure', whatToCheck: 'Test AC volts at battery terminals', fix: 'Replace alternator.' },
      { problem: 'Drain present even with all fuses pulled', likelyCause: 'Direct short to battery (rare; check for chafed wires near starter)', whatToCheck: 'Visual inspection of battery cables', fix: 'Repair shorted wire.' },
      { problem: 'Drain only after vehicle sits 24+ hours', likelyCause: 'Slow drain from a small parasitic load (BCM, alarm, clock)', whatToCheck: 'Check current after 24-hour sleep cycle', fix: 'Use disconnect switch or trickle charger if drain cannot be eliminated.' },
    ],
    proTips: ['Battery disconnect switch ($15) is a quick fix for low-use vehicles and confirms the issue is parasitic.', 'Aftermarket stereos are TJ\'s #1 parasitic draw source.', '30-minute sleep wait is essential before reading current.', 'Drain over 100 mA = problem; over 500 mA = drains battery overnight.', 'Document fuse-pull results for future reference.'],
    commonMistakes: [
      { mistake: 'Using voltmeter setting instead of 10A current setting', consequence: 'Cannot measure current; meter does not work as intended.' },
      { mistake: 'Reading immediately without sleep wait', consequence: 'False high reading from awake modules.' },
      { mistake: 'Pulling multiple fuses at once', consequence: 'Cannot identify which circuit had the drain.' },
    ],
    successIndicators: ['Steady-state current under 50 mA after 30-minute sleep', 'Battery holds charge over 7 days of non-use', 'No AC voltage on battery with engine off', 'Battery age 3+ years possible without drain issues'],
    whenToSeekHelp: 'If draw cannot be isolated by fuse pull, an experienced shop with module-level diagnostics can identify the culprit. Aftermarket alarms and remote-start systems are common but tricky drain sources — sometimes the installer is the right resource.',
    legalDisclaimer: 'This guide was generated by GarageIQ\'s AI from publicly available sources. Verify procedures with your licensed manual.',
    manualSource: { title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)', url: 'https://www.haynes.com/en-us/wrangler/jeep/2003', notes: 'Cross-reference fuse box layouts and wiring diagrams with your licensed manual.' },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — HIGHWAY VIBRATION DIAGNOSTIC
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-highway-vibration-diagnostic',
    title: 'Vibration at Highway Speed (Diagnostic)',
    vehicle: { make: 'Jeep', model: 'Wrangler', generations: [{ startYear: 1997, endYear: 2006 }] },
    video: {
      youtubeId: 'IWq6MC1IrW4',
      title: 'Jeep Death Wobble How To Fix (related vibration diagnostic)',
      channel: 'KyleSVT',
    },
    source: 'Generated by GarageIQ AI from publicly available sources. Cross-checked per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Suspension & Steering',
    difficulty: 'intermediate',
    duration: '1-2 hours diagnostic plus a road test',
    interval: 'When the vehicle vibrates at specific speeds (typically 55-70 mph) or when the steering wheel shakes.',
    description: 'Highway vibration on a TJ has a small list of causes ranked by likelihood: tire imbalance (most common, easy fix), bent wheel, driveshaft balance issue, worn U-joint, transfer case output yoke, or a stretched timing chain (4.0L misfire under load). This guide isolates which.',
    overview: { difficulty: 'Intermediate — diagnostic includes a controlled road test', estimatedTime: '1-2 hours', estimatedCost: '$0 to diagnose; tire balance ~$20-40, U-joint ~$25 each', whyMatters: 'Untreated vibration causes accelerated wear of bearings, joints, and drivetrain.', safetyNote: 'Test driving at highway speed is required — keep both hands on the wheel and stay alert.' },
    tools: ['Wheel chocks', 'Tape measure', 'Paint pen', 'OBD-II scan tool'],
    parts: [{ id: 'replacement-vibration', name: 'Replacement parts based on findings', partNumber: 'See U-joint, driveshaft, hub assembly guides', quantity: 1, price: 30, category: 'conditional', notes: 'Buy after diagnosis.' }],
    safetyInformation: 'Test drive at highway speed in safe conditions only. Vibration that is severe enough to lose steering control should not be driven.',
    beforeYouStart: ['Note exact speed where vibration occurs', 'Note whether vibration is in steering wheel only, seat only, or whole vehicle', 'Note if it varies with throttle (on/off)'],
    steps: [
      { number: 1, title: 'Note Vibration Pattern', description: 'Define exactly: speed range (e.g., 55-65 mph), location (steering wheel, seat, whole vehicle), throttle dependence (on/off changes), and bumps (worsens or unchanged). Each pattern points to different causes.', why: 'Pattern is the diagnostic key.', tip: 'A ride-along passenger can help you focus on steering vs. seat vibration.', commonMistake: 'Vague description: "shakes." Specifics matter.' },
      { number: 2, title: 'Inspect Tires for Damage', description: 'Visually inspect all four tires: cupping (uneven wear), bulges in sidewall (belt separation), unusual tread wear patterns, or cracks. Check tire pressure (must be at door-jamb spec — 30 psi typical).', why: 'Tire damage is the #1 vibration cause and most common to overlook.', tip: 'A tire with internal belt separation can look fine externally.', commonMistake: 'Skipping tire inspection.' },
      { number: 3, title: 'Have Tires Balanced', description: 'Take vehicle to a tire shop for balancing — most do all four for $40-60. Many "vibrations" are simply out-of-balance tires.', why: 'Static and dynamic balance go off over time.', tip: 'Some shops use Hunter Road Force balancers — better at finding hidden issues.', commonMistake: 'Skipping balance because "tires are not that old."' },
      { number: 4, title: 'Check Wheel for Bent Rim', description: 'With wheel off ground, spin and watch the lip — should rotate evenly. Wobble (lateral or radial run-out) means bent rim. Also check for visible damage.', why: 'A bent rim cannot be balanced and causes steady vibration.', tip: 'Some bent rims can be straightened; many require replacement.', commonMistake: 'Trying to balance around a bent rim.' },
      { number: 5, title: 'Inspect Driveshafts and U-Joints', description: 'With vehicle on jack stands, rotate each driveshaft by hand. Check U-joints for: roughness, free movement, or cracking. Look for paint pen marks (if you marked them during prior service) — verify driveshafts are in original orientation.', why: 'Worn U-joints cause harmonic vibration that varies with speed.', tip: 'A bad U-joint clicks or grunts when rotated by hand under load.', commonMistake: 'Ignoring driveshaft balance marks during R&R.' },
      { number: 6, title: 'Check Transfer Case Output and Yoke', description: 'With drivetrain in neutral, check transfer case output yoke for play (push/pull on driveshaft). Excessive play means worn output yoke or output shaft bearing.', why: 'Output yoke wear creates vibration that worsens with speed.', tip: 'Output yoke replacement may require transfer case service.', commonMistake: 'Confusing output yoke play with U-joint wear.' },
      { number: 7, title: 'Inspect Front Hub Bearings', description: 'With wheels off ground, grab tire at 9 and 3 o\'clock and rock — play here (with wheel spinning freely) indicates worn front hub. Listen for grinding/growling at speed.', why: 'Failing front bearings vibrate and growl at speed.', tip: 'See front hub assembly replacement guide.', commonMistake: 'Diagnosing as "tire vibration" when bearings are the cause.' },
      { number: 8, title: 'Code-Scan for Misfire', description: 'A 4.0L with stretched timing chain or partial misfire under load can cause vibration that feels like driveline. Code-scan for P0301-P0306 (cylinder-specific misfire) or P0344 (cam/crank correlation).', why: 'Engine misfire under load mimics drivetrain vibration.', tip: 'Misfire vibration usually correlates with throttle (on/off changes intensity).', commonMistake: 'Ignoring misfire codes.' },
      { number: 9, title: 'Test Specific to Pattern', description: 'Steering-wheel-only vibration = front tire/hub. Seat-only = rear tire/driveshaft/diff. Whole-vehicle = engine/exhaust hangers/multiple causes. Throttle-on vibration = driveline/U-joints. Throttle-off vibration = wheel/tire.', why: 'The pattern narrows the source by 50% or more.', tip: 'Document each test driver\'s observations.', commonMistake: 'Trying to fix vibration without isolating the source.' },
      { number: 10, title: 'Apply Targeted Repair', description: 'Based on findings: balance tires ($40), replace bent rim ($60-150), replace U-joint ($25 + 1 hr), replace front hub ($100 + 2 hr), or address engine misfire. Verify with a road test.', why: 'Targeted repair fixes the issue without over-spending.', tip: 'Always road test after each repair.', commonMistake: 'Trying multiple fixes at once — cannot tell which worked.' },
    ],
    torqueSpecifications: [{ fastener: 'Wheel lug nuts', specification: '95 ft-lbs (129 Nm).', notes: 'Star pattern.' }],
    fluidSpecifications: [],
    troubleshooting: [
      { problem: 'Vibration at specific speed (e.g., 55-65 mph)', likelyCause: 'Tire imbalance or bent rim', whatToCheck: 'Tire balance', fix: 'Balance tires; replace bent rim.' },
      { problem: 'Vibration in steering wheel only', likelyCause: 'Front tire/wheel/hub', whatToCheck: 'Front-end inspection', fix: 'Address front-specific issue.' },
      { problem: 'Vibration in seat/floor only', likelyCause: 'Rear tire/driveshaft/diff', whatToCheck: 'Rear inspection', fix: 'Address rear-specific issue.' },
      { problem: 'Vibration changes with throttle (on/off)', likelyCause: 'Driveline (U-joints, driveshaft)', whatToCheck: 'U-joint inspection', fix: 'Replace failed U-joint.' },
      { problem: 'Vibration only at specific speeds, gone at others', likelyCause: 'Resonance from specific component (typically tire imbalance)', whatToCheck: 'Tire balance', fix: 'Re-balance tires.' },
    ],
    proTips: ['Tire balance is the most common fix and cheapest place to start.', 'Hunter Road Force balancers find issues regular balancers miss.', 'Driveshaft orientation marks matter — always reinstall original orientation.', 'Throttle-dependent vibration = driveline; speed-dependent = wheel/tire.', 'Replace U-joints in pairs at the same age — they age together.'],
    commonMistakes: [
      { mistake: 'Replacing parts before isolating cause', consequence: 'Money spent without solving issue.' },
      { mistake: 'Ignoring tire balance', consequence: 'Wasted diagnosis on suspected drivetrain issues.' },
      { mistake: 'Driving with worsening vibration', consequence: 'Bearings and joints fail catastrophically.' },
    ],
    successIndicators: ['Smooth ride at all highway speeds', 'No vibration in steering wheel or seat', 'No drivetrain noise during acceleration or coasting', 'Even tire wear over 5,000+ miles after fix'],
    whenToSeekHelp: 'If diagnosis cannot isolate the cause, a shop with a chassis dyno or wheel balancer can identify subtle issues. Some vibrations require road-force balancing or driveshaft balancing at specialty shops.',
    legalDisclaimer: 'This guide was generated by GarageIQ\'s AI from publicly available sources. Verify procedures with your licensed manual.',
    manualSource: { title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)', url: 'https://www.haynes.com/en-us/wrangler/jeep/2003', notes: 'Cross-reference suspension and drivetrain inspection procedures.' },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — ENGINE MISFIRE DIAGNOSTIC
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-engine-misfire-diagnostic',
    title: 'Engine Misfire Diagnostic',
    vehicle: { make: 'Jeep', model: 'Wrangler', generations: [{ startYear: 1997, endYear: 2006 }] },
    video: {
      youtubeId: 'wVrNO9e31ak',
      title: 'Jeep Misfire Diagnosis + Full Ignition Coil Replacement DIY Guide',
      channel: 'Garage Couple',
    },
    source: 'Generated by GarageIQ AI from publicly available sources. Cross-checked per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Engine',
    difficulty: 'intermediate',
    duration: '30 minutes - 2 hours',
    interval: 'When engine runs rough, has check engine light with P030X codes, or has a stumble on acceleration.',
    description: 'A misfire is a cylinder failing to ignite or combust correctly. Causes: failed spark plug, failed ignition wire, failed coil/distributor, failed fuel injector, low compression on one cylinder, or a vacuum leak on a specific intake runner. The OBD-II code (P0300 random or P0301-P0306 cylinder-specific) is the starting point for diagnosis.',
    overview: { difficulty: 'Intermediate — uses OBD-II, spark/fuel testing, compression test', estimatedTime: '30 minutes - 2 hours', estimatedCost: '$0 to diagnose; spark plug replacement $20, coil $40, injector $50', whyMatters: 'Continued misfire damages catalytic converter and reduces fuel economy.', safetyNote: 'Diagnostic uses ignition components — high voltage. Disconnect battery before working on coil/distributor.' },
    tools: ['OBD-II scan tool', 'Spark tester', 'Noid lights', 'Compression tester', 'Vacuum gauge (optional)', 'Spray bottle of water'],
    parts: [{ id: 'replacement-misfire', name: 'Replacement parts based on findings', partNumber: 'Plugs, wires, coil, injector vary', quantity: 1, price: 30, category: 'conditional', notes: 'Buy after diagnosis.' }],
    safetyInformation: 'Disconnect battery before working on ignition components. High voltage in coil and ignition wires can shock. Used spark plugs are sharp.',
    beforeYouStart: ['Code-scan and note specific codes', 'Note when misfire occurs (idle, cruising, acceleration?)', 'Confirm fuel quality (bad gas can mimic misfire)'],
    steps: [
      { number: 1, title: 'Read OBD-II Codes', description: 'Code-scan and note codes. P0300 = random misfire (multiple cylinders); P0301-P0306 = specific cylinder misfire on the 4.0L. Lower-cylinder numbers = front of engine.', why: 'Code identifies which cylinder is misfiring.', tip: 'Random misfire (P0300) often points to ignition or fuel pressure.', commonMistake: 'Skipping code scan.' },
      { number: 2, title: 'Pull and Inspect Spark Plug for Misfiring Cylinder', description: 'Pull the plug from the misfiring cylinder. Compare to the others. Look for: black sooty (rich), white (lean), oil-fouled (engine wear), or damaged electrode.', why: 'Plug condition reveals what is happening in that cylinder.', tip: 'Photograph each plug labeled by cylinder for reference.', commonMistake: 'Skipping plug inspection.' },
      { number: 3, title: 'Replace Spark Plug as First Step', description: 'A failed plug is the most common cause. Replace ALL six plugs (4.0L) — they age together. After plug replacement, clear codes and test drive 30 miles.', why: 'Cheapest first step; often resolves issue.', tip: 'Use Champion REC10YC or NGK ZFR5N copper plugs for the 4.0L.', commonMistake: 'Replacing only one plug — others may be near failure.' },
      { number: 4, title: 'Test Spark Plug Wire for Misfiring Cylinder', description: 'Pull the spark plug wire. Inspect inside the boot for corrosion or burn marks. Test resistance with multimeter — should be 5,000-15,000 ohms per foot. Replace wire if outside range.', why: 'A high-resistance wire reduces spark energy enough to misfire.', tip: 'Replace all plug wires together — they age as a set.', commonMistake: 'Visually approving wires without resistance test.' },
      { number: 5, title: 'Test Coil and Distributor (4.0L)', description: 'Move the wire from the misfiring cylinder to a known-good cylinder. Move the misfire-cylinder wire to the misfire position. Re-clear codes and drive. If misfire moves to the new cylinder = ignition (coil/cap/wire) issue. If misfire stays on original cylinder = fuel or mechanical.', why: 'Wire-swap test isolates ignition vs fuel/mechanical.', tip: 'This is the classic professional diagnostic move.', commonMistake: 'Skipping the wire-swap test.' },
      { number: 6, title: 'Test Fuel Injector with Noid Light', description: 'Pull the injector electrical connector for the misfiring cylinder. Plug a noid light in. Crank engine — light should flash. If no flash, injector circuit is bad. Also: swap the injector with another cylinder. Re-clear codes and drive. If misfire moves with the injector = bad injector.', why: 'Injector swap isolates injector failure.', tip: 'Buy a noid light kit ($15) for permanent diagnostic capability.', commonMistake: 'Replacing injector without swap test.' },
      { number: 7, title: 'Spray for Vacuum Leak (Specific Runner)', description: 'With engine idling, spray throttle body cleaner around the intake manifold gasket near the misfiring cylinder. Idle changes = leak in that runner. Repair gasket if leaking.', why: 'A leak only on one runner causes single-cylinder misfire.', tip: 'A 4.0L intake manifold gasket is a common service item.', commonMistake: 'Spraying directly into intake (always changes RPM).' },
      { number: 8, title: 'Compression Test the Misfiring Cylinder', description: 'Pull all plugs. Insert compression tester in misfiring cylinder. Crank 5-7 cycles. Healthy: 130-180 psi all cylinders, no more than 15% variation. Low on misfiring cylinder = mechanical issue (head gasket, valve, ring).', why: 'Compression rules out mechanical cause.', tip: 'Wet test (1 tsp oil) helps differentiate ring vs valve issue.', commonMistake: 'Skipping compression test.' },
      { number: 9, title: 'Verify No Other Codes', description: 'After repair, drive 30+ miles. Code-scan to confirm no new codes have appeared. P0420/P0430 (catalyst codes) often follow continued misfire.', why: 'Misfire damages catalytic converter — verify no follow-on damage.', tip: 'Check fuel trim values — should be near 0% after fix.', commonMistake: 'Ignoring follow-on cat damage.' },
      { number: 10, title: 'Apply Targeted Repair', description: 'Based on findings: spark plugs ($20), wires ($30), coil ($40), distributor cap/rotor ($30), injector ($50), or major repair (head gasket). After repair, clear codes, test drive, monitor.', why: 'Targeted repair is more efficient than parts cannon.', tip: 'Many TJs need plug + wire + cap/rotor refresh as a 60k-100k tune-up.', commonMistake: 'Replacing one component at a time over months.' },
    ],
    torqueSpecifications: [{ fastener: 'Spark plugs', specification: '13 ft-lbs (18 Nm).', notes: 'Snug.' }],
    fluidSpecifications: [],
    troubleshooting: [
      { problem: 'Misfire only when cold', likelyCause: 'Cap/rotor cracking, plug wire failing on temperature change', whatToCheck: 'Cap visual inspection', fix: 'Replace cap and rotor.' },
      { problem: 'Misfire only at idle', likelyCause: 'Vacuum leak or low fuel pressure', whatToCheck: 'Vacuum leak hunt; fuel pressure', fix: 'Repair vacuum leak; replace fuel filter or pump.' },
      { problem: 'Misfire only under load', likelyCause: 'Weak spark (worn plug, weak coil) or low compression', whatToCheck: 'Compression test', fix: 'Replace plugs; address mechanical issue.' },
      { problem: 'Random misfire (P0300)', likelyCause: 'Multiple plugs/wires aged, low fuel pressure, or wide vacuum leak', whatToCheck: 'Inspect all ignition components', fix: 'Tune-up: plugs, wires, cap, rotor, fuel filter.' },
      { problem: 'Misfire moves with wire swap', likelyCause: 'Ignition (coil, wire, plug)', whatToCheck: 'Wire and plug condition', fix: 'Replace ignition components.' },
    ],
    proTips: ['Wire swap test is the fastest way to differentiate ignition from fuel/mechanical.', 'A "tune-up" — plugs, wires, cap, rotor — fixes many misfires on aging TJs.', 'Misfire damages cat converter — fix promptly.', 'Random misfire (P0300) without specific cylinder = fuel pressure or wide vacuum leak.', 'Document plug condition for future diagnosis.'],
    commonMistakes: [
      { mistake: 'Replacing only one plug', consequence: 'Other plugs near failure cause future misfire.' },
      { mistake: 'Ignoring P0300 — "engine runs OK"', consequence: 'Cat converter damage accumulates.' },
      { mistake: 'Replacing injector before diagnostic swap', consequence: 'Wasted money if injector was fine.' },
    ],
    successIndicators: ['No misfire codes after 100 miles of driving', 'Smooth idle at correct RPM', 'No hesitation under acceleration', 'Even fuel trim values (near 0%)'],
    whenToSeekHelp: 'If misfire persists after replacing all ignition components and confirming compression, the issue may be a failing PCM or wiring harness. Some misfire codes require shop scan tools with live data to diagnose effectively.',
    legalDisclaimer: 'This guide was generated by GarageIQ\'s AI from publicly available sources. Verify procedures with your licensed manual.',
    manualSource: { title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)', url: 'https://www.haynes.com/en-us/wrangler/jeep/2003', notes: 'Cross-reference engine diagnostic procedures with your licensed manual.' },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — CHECK ENGINE LIGHT (CEL) DECODE REFERENCE
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-cel-decode-reference',
    title: 'Check Engine Light Code Decoder (Common TJ OBD-II Codes)',
    vehicle: { make: 'Jeep', model: 'Wrangler', generations: [{ startYear: 1997, endYear: 2006 }] },
    source: 'Generated by GarageIQ AI from publicly available sources (Mopar service literature, OBD-II reference databases, technician forums and Wrangler community service threads). Cross-checked per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Engine',
    difficulty: 'beginner',
    duration: '5 minutes per code lookup',
    interval: 'Whenever the check engine light illuminates. Use this guide to interpret codes before deciding on repair.',
    description: 'A check engine light (CEL) means the powertrain control module (PCM) has detected an out-of-range condition and stored a diagnostic trouble code (DTC). This guide is a reference for the most common TJ codes — what they mean, likely cause, and which guide to use for repair. Always code-scan first; the code is the starting point for any engine diagnostic.',
    overview: { difficulty: 'Beginner — reference guide; uses an OBD-II scanner', estimatedTime: '5 minutes per code lookup; repair time varies', estimatedCost: '$20-50 for an OBD-II scanner; or free at most chain auto-parts stores', whyMatters: 'Codes point directly to the failed component. Saves diagnostic time and parts cannon spending.', safetyNote: 'Codes give a starting point but do not always tell the full story — some codes have multiple possible causes. Always verify before replacing parts.' },
    tools: ['OBD-II scan tool (basic, $20-50; or free read at chain auto-parts stores)'],
    parts: [{ id: 'replacement-cel', name: 'Replacement parts based on code', partNumber: 'Varies by code — see referenced guides', quantity: 1, price: 30, category: 'conditional', notes: 'Use code as starting point; verify with diagnostic before buying.' }],
    safetyInformation: 'CEL codes are diagnostic starting points, not definitive answers. Always verify the issue with hands-on diagnostic before replacing parts.',
    beforeYouStart: ['Buy or borrow an OBD-II scanner', 'Scanner port is under driver\'s side dash', 'Document codes (both stored and pending)'],
    steps: [
      { number: 1, title: 'Connect Scanner to OBD-II Port', description: 'Locate OBD-II port (16-pin connector under driver\'s dash, near the steering column). Plug in scanner. Turn key to RUN (not start). Read codes per scanner instructions.', why: 'Codes are accessed only via OBD-II.', tip: 'Free reads at most chain parts stores.', commonMistake: 'Trying to read codes with key off.' },
      { number: 2, title: 'Note Codes (Both Stored and Pending)', description: 'Stored codes = confirmed faults; pending = detected once but not yet confirmed. Note both. Pending codes that recur become stored.', why: 'Pending codes preview future stored codes.', tip: 'Pending codes can self-clear if the condition does not recur.', commonMistake: 'Ignoring pending codes.' },
      { number: 3, title: 'CKP / CMP Codes (P0335-P0344)', description: 'P0335 = CKP sensor circuit; P0344 = CMP intermittent. Common on TJ 4.0L. Most common cause: failed CKP sensor ($30 fix). See the CKP Sensor guide.', why: 'CKP failure is the most common TJ engine code.', tip: 'A scan tool with live data shows CKP signal strength.', commonMistake: 'Replacing distributor before CKP — symptoms can overlap.' },
      { number: 4, title: 'Misfire Codes (P0300-P0306)', description: 'P0300 = random misfire; P0301-P0306 = specific cylinder. See Engine Misfire Diagnostic guide. Most common: failed plugs/wires/cap/rotor.', why: 'Misfire codes point to ignition or fuel issue.', tip: 'P0300 alone often means tune-up needed; specific cylinder code means single-component issue.', commonMistake: 'Ignoring misfires — damages cat converter.' },
      { number: 5, title: 'Oxygen Sensor Codes (P0130-P0167)', description: 'P0131/P0132 = upstream lean/rich; P0136/P0137 = downstream. Often the sensor itself ($60), but can be vacuum leak or fuel pressure issue.', why: 'O2 sensors degrade over time; replace at 100k miles.', tip: 'Always test fuel pressure before replacing O2 sensors.', commonMistake: 'Replacing O2 sensor when underlying fuel/vacuum issue.' },
      { number: 6, title: 'Catalyst Efficiency Codes (P0420-P0430)', description: 'P0420 = catalyst below threshold (cat converter aging or failed). Often follows extended misfire. Cat replacement is $200+.', why: 'Cat efficiency drops over time.', tip: 'Address ANY misfire before assuming cat is bad — fixes one issue and prevents another.', commonMistake: 'Replacing cat without diagnosing upstream issue.' },
      { number: 7, title: 'EVAP System Codes (P0440-P0455)', description: 'P0455 = large EVAP leak (loose gas cap most common). P0440 = generic. Try tightening gas cap first; if recurs, EVAP component diagnosis.', why: 'EVAP codes are often gas-cap simple.', tip: 'A loose gas cap is the most common P0455 cause.', commonMistake: 'Replacing EVAP canister when gas cap is the issue.' },
      { number: 8, title: 'Coolant / Engine Temperature Codes (P0117-P0118, P0125)', description: 'P0117/P0118 = CTS sensor; P0125 = coolant did not reach operating temp (thermostat stuck open). CTS replacement is $20.', why: 'Cooling-related codes are usually sensor or thermostat.', tip: 'Verify with live data — actual coolant temp vs reading.', commonMistake: 'Replacing parts without confirming via live data.' },
      { number: 9, title: 'Idle / Fuel Trim Codes (P0171, P0507)', description: 'P0171 = system too lean (vacuum leak, fuel pressure low, or O2 sensor); P0507 = idle higher than expected (vacuum leak or IAC). Vacuum leak hunt is first step.', why: 'Lean and idle codes share root causes.', tip: 'Spray throttle body cleaner around vacuum lines to find leaks.', commonMistake: 'Replacing IAC for P0507 without checking vacuum.' },
      { number: 10, title: 'Code Reset After Repair', description: 'After repair, clear codes with the scanner. Drive 50+ miles to verify codes do not return. New codes may appear if repair was incomplete.', why: 'Cleared codes confirm fix.', tip: 'Some scanners can clear codes; some need a code-reset adapter.', commonMistake: 'Calling repair complete before code clears.' },
    ],
    torqueSpecifications: [],
    fluidSpecifications: [],
    troubleshooting: [
      { problem: 'Code returns after repair', likelyCause: 'Underlying issue not fixed; new component defective; or related component is the actual cause', whatToCheck: 'Re-diagnose; verify with live data', fix: 'Re-investigate; warranty exchange if part is defective.' },
      { problem: 'Multiple codes', likelyCause: 'Often a single root cause (e.g., vacuum leak causes P0171 + P0420)', whatToCheck: 'Address most upstream code first', fix: 'Fix root cause; other codes may clear.' },
      { problem: 'Code changes after fix', likelyCause: 'New code uncovered after primary code was hiding it', whatToCheck: 'Investigate new code', fix: 'Continue diagnostic with new code as guide.' },
      { problem: 'CEL on but no codes', likelyCause: 'Pending code may have been read first or scanner connection issue', whatToCheck: 'Re-scan; try different scanner', fix: 'Address any code that appears.' },
    ],
    proTips: ['Free code scans at most chain parts stores — no need to buy a scanner for occasional use.', 'Always note all codes — even pending ones.', 'Use codes as a starting point, not the final answer.', 'Live-data scanners show real-time sensor values — much more useful than just code numbers.', 'Some codes (like P0420) have multiple causes; address upstream issues first.'],
    commonMistakes: [
      { mistake: 'Replacing parts based only on code without verification', consequence: 'Wasted money; problem not fixed.' },
      { mistake: 'Ignoring pending codes', consequence: 'Codes become stored and continue causing issues.' },
      { mistake: 'Clearing codes without fixing root cause', consequence: 'Problem returns; potential damage to other systems.' },
    ],
    successIndicators: ['No active codes after repair', 'Vehicle drives normally', 'Fuel trim values near 0%', 'No new codes after 50+ miles'],
    whenToSeekHelp: 'If a code persists after trying obvious repairs, a shop with live-data scan tool can read sensor values and identify the root cause. Some codes (especially related to PCM, BCM, or transmission) require dealer-level diagnostics.',
    legalDisclaimer: 'This guide was generated by GarageIQ\'s AI from publicly available sources. Codes are diagnostic starting points, not definitive answers. Verify with hands-on diagnostic before replacing parts. Consult OEM service manual for specific code definitions.',
    manualSource: { title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)', url: 'https://www.haynes.com/en-us/wrangler/jeep/2003', notes: 'Cross-reference codes against your licensed manual or use a database like AlldataDIY.' },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — IN-TANK FUEL PUMP REPLACEMENT
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-fuel-pump-replacement',
    title: 'In-Tank Fuel Pump Replacement',
    vehicle: { make: 'Jeep', model: 'Wrangler', generations: [{ startYear: 1997, endYear: 2006 }] },
    video: {
      youtubeId: 's0g1vNNsSdM',
      title: 'JEEP WRANGLER TJ fuel tank removal and fuel pump replacement',
      channel: 'Craft Customs',
    },
    source: 'Generated by GarageIQ AI from publicly available sources (Mopar parts catalog, Bosch and Delphi fuel pump literature, NHTSA TSBs, owner\'s manual, technician forums and Wrangler community service threads). Cross-checked and structured per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Engine',
    difficulty: 'advanced',
    duration: '3-5 hours',
    interval: 'Replace when fuel pressure tests fail (low or no pressure), pump runs but no flow, or pump does not run at all. Original pumps typically last 100,000-200,000 miles.',
    description: 'The TJ\'s fuel pump is mounted inside the fuel tank, integrated with the fuel level sender as a single module. Replacement requires dropping the fuel tank — a major job. The pump assembly is a complete unit (pump + sender + strainer) and is typically replaced as one piece. Plan a full Saturday for this job. Always replace the pump strainer (sock filter) at the same time.',
    overview: { difficulty: 'Advanced — fuel tank drop is involved; fuel safety practices essential', estimatedTime: '3-5 hours', estimatedCost: '$120-200 for a quality fuel pump assembly (Bosch, Delphi, or AC Delco)', whyMatters: 'Failed fuel pump = no-start. Replacement restores normal operation.', safetyNote: 'Fuel and fumes are flammable. NEVER smoke or have open flames near work area. Disconnect battery. Depressurize fuel system before opening any line. Work outdoors or in well-ventilated garage with NO ignition sources.' },
    tools: ['Floor jack and jack stands', 'Lug wrench / 19mm socket', 'Socket set: metric (10-19mm)', 'Fuel pump module retainer wrench (Lisle 13400 or similar — fits the locking ring on top of the tank)', 'Fuel line disconnect tool set', 'Drain pan (large — 5+ gallons capacity for fuel drain)', 'Fuel transfer pump (siphon-style, manual)', 'Brake cleaner', 'Disposable nitrile gloves and eye protection', 'Fire extinguisher within reach', 'Floor mat or cardboard (catches drips)'],
    parts: [
      { id: 'fuel-pump-assembly', name: 'Fuel Pump Assembly (with sender and strainer)', partNumber: 'Bosch 69527 / Delphi FG0411 / AC Delco MU1410', quantity: 1, price: 160, category: 'required', notes: 'Buy a complete assembly, not just the pump element. Quality matters — Bosch and Delphi are top tier.' },
      { id: 'fuel-tank-strap-bolts', name: 'Fuel tank strap bolts (replace if corroded)', partNumber: 'M10 x 1.5 grade-8', quantity: 4, price: 6, category: 'conditional', notes: 'Tank strap bolts are often corroded.' },
      { id: 'fuel-tank-gasket', name: 'Fuel pump module O-ring / seal', partNumber: 'Mopar 52127601AA — equivalent: included in most pump assemblies', quantity: 1, price: 8, category: 'conditional', notes: 'Verify O-ring is included with new pump; if not, buy separately.' },
    ],
    safetyInformation: 'CRITICAL FIRE SAFETY: Fuel and fumes ignite explosively. Disconnect battery negative cable. Depressurize fuel system (loosen fuel cap, then start engine and let it stall — this empties the rail). Work outdoors or in a ventilated garage with NO ignition sources (water heaters, smokers, open flames). Have a fire extinguisher within reach. Wear nitrile gloves and eye protection. Used fuel is hazardous waste. Tank dropping requires multiple jack stands and careful support — tank weighs 60-100 lbs depending on fuel level.',
    beforeYouStart: ['Run fuel level low — under 1/4 tank to reduce weight', 'Disconnect battery negative cable', 'Depressurize fuel rail (start engine, pull fuel pump fuse, let engine stall)', 'Have a 5+ gallon drain pan and a fuel transfer pump for any remaining fuel', 'Stage all tools and parts before lifting vehicle', 'Plan to work in ventilated area with no ignition sources nearby'],
    steps: [
      { number: 1, title: 'Depressurize Fuel System', description: 'With engine off, remove the fuel pump fuse (under hood, fuse box). Start the engine — it will run for a few seconds then stall as fuel pressure drops. Crank for another 2-3 seconds to fully depressurize. Replace fuse after.', why: 'Fuel rail under pressure sprays fuel when disconnected. Depressurization prevents this.', tip: 'Loosen the fuel cap before depressurizing — relieves tank pressure too.', commonMistake: 'Skipping depressurization. Fuel sprays everywhere.' },
      { number: 2, title: 'Lift Vehicle and Drain Tank (If Possible)', description: 'Raise vehicle on jack stands. Locate the fuel filler hose and drain plug (some TJs have a tank drain). If no drain, you must work with whatever fuel remains in the tank — lower fuel level is much easier.', why: 'Less fuel = lighter, safer tank drop.', tip: 'Use a hand-pumped fuel siphon to remove most of the fuel through the filler neck.', commonMistake: 'Trying to drop a full tank. Heavy and dangerous.' },
      { number: 3, title: 'Disconnect Fuel Filler Hose and Vent Lines', description: 'Locate the fuel filler hose (large rubber hose from gas cap area to tank). Remove the clamp and disconnect. Disconnect any vent lines or breather hoses. Note the orientation for reinstall.', why: 'Filler hose holds the tank to the body — must come off before tank drops.', tip: 'Fuel filler hose is rubber and may be stuck — wiggle and pry gently.', commonMistake: 'Forcing the hose off. Damages it; may need replacement.' },
      { number: 4, title: 'Disconnect Fuel Lines at Tank', description: 'Locate the fuel supply and return lines (and EVAP line) at the tank. Use fuel line disconnect tool to release each. Cap the lines with vinyl caps to prevent contamination. Note: a small amount of fuel will dribble — have rags ready.', why: 'Lines must be disconnected before tank can drop.', tip: 'Disconnect tool sets cost $10-15 and pay for themselves on the first use.', commonMistake: 'Forcing the lines off. Damages the quick-connect fittings.' },
      { number: 5, title: 'Disconnect Electrical Connector at Pump', description: 'A wiring harness runs to the top of the fuel pump module. Disconnect the connector. The connector is locking — press the lock tab and pull straight back.', why: 'Wiring must be free before tank drops.', tip: 'Photograph the connector orientation for reassembly.', commonMistake: 'Pulling the wires instead of the connector body.' },
      { number: 6, title: 'Support Tank and Remove Straps', description: 'Position a floor jack with a wide saddle (or wood block) under the fuel tank to support its weight. Have a helper hold one end. Remove the two tank strap bolts (typically 13mm or 15mm — heavily corroded; apply penetrating oil). Once straps are loose, slowly lower the tank.', why: 'Tank straps hold the tank to the chassis.', tip: 'Lower in small increments — verify all connections are free as you go.', commonMistake: 'Letting the jack drop the tank without checking for stuck connections.' },
      { number: 7, title: 'Remove Fuel Pump Locking Ring', description: 'On top of the (now-lowered) tank is the fuel pump module retained by a locking ring. Use the special spanner wrench (Lisle 13400 or similar) to rotate the ring counterclockwise. The ring will release and the pump module can be lifted out. Pull the module straight up — it has a long fuel level float arm that must clear the tank.', why: 'The locking ring is the only retention for the pump module.', tip: 'A flathead screwdriver and hammer can rotate the ring as a last resort, but the proper spanner is much easier.', commonMistake: 'Forcing the module out without rotating ring. Damages the ring or module.' },
      { number: 8, title: 'Install New Pump Module', description: 'Compare new module to old — confirm correct part. Apply a thin film of fuel-resistant grease to the new O-ring (if separate). Lower the new module into the tank, with the float arm clearing inside surfaces. Install the locking ring and rotate clockwise until tight.', why: 'Module must seat fully and ring must lock to prevent leak.', tip: 'The float arm orientation matters — it should follow the original direction.', commonMistake: 'Cross-threading the locking ring.' },
      { number: 9, title: 'Reinstall Tank in Reverse Order', description: 'Reverse the steps: lift tank with jack, install straps and bolts (torque to spec, typically 30 ft-lbs), reconnect filler hose with clamp, reconnect fuel supply/return/vent lines (verify each clicks), reconnect electrical, lower vehicle.', why: 'Reverse order ensures everything is restored.', tip: 'Hand-thread strap bolts before applying torque.', commonMistake: 'Forgetting a connection. Fuel leak or no-start result.' },
      { number: 10, title: 'Refuel, Prime, and Test', description: 'Add at least 2 gallons of fuel. Reconnect battery. Cycle the key from OFF to RUN three times (do not start) — pump primes each time. Then start engine — should fire on first try after a longer-than-normal crank. Verify no fuel leaks at any connection. Drive 25 miles and recheck.', why: 'Priming primes the system; first crank verifies operation.', tip: 'A small amount of crank time is normal as fuel reaches the rail.', commonMistake: 'Cranking continuously without priming. Wears starter; may not start.' },
    ],
    torqueSpecifications: [
      { fastener: 'Fuel tank strap bolts', specification: '30 ft-lbs (41 Nm). Verify torque spec from OEM service manual.', notes: 'Cross-tighten.' },
      { fastener: 'Fuel pump locking ring', specification: 'Hand-tight per spanner. No published torque.', notes: 'Rotate clockwise until tight; do not over-torque.' },
    ],
    fluidSpecifications: [{ fluid: 'Gasoline (87 octane minimum)', type: 'Regular unleaded', capacity: 'Tank capacity 19 gallons (4.0L) / 15 gallons (early TJ)', notes: 'Refill after install.' }],
    troubleshooting: [
      { problem: 'No-start after install', likelyCause: 'System not primed, electrical not reconnected, or quick-connect not seated', whatToCheck: 'Verify fuel pressure; check electrical connector seating', fix: 'Cycle key 3x to prime; verify connections.' },
      { problem: 'Fuel leak at tank top after install', likelyCause: 'O-ring damaged or locking ring not fully tight', whatToCheck: 'Visual inspection at locking ring', fix: 'Disassemble, replace O-ring, reseat properly.' },
      { problem: 'Fuel gauge reads incorrectly after install', likelyCause: 'Float arm orientation off, sender wiring connection bad', whatToCheck: 'Verify float arm position; check wiring', fix: 'Disassemble and reorient float; secure wiring.' },
      { problem: 'Pump runs but pressure low', likelyCause: 'Pump assembly defective, fuel filter clogged, or hose pinched', whatToCheck: 'Test fuel pressure with gauge', fix: 'Replace filter; verify pump works; warranty new pump if defective.' },
      { problem: 'Fuel smell after install', likelyCause: 'Loose connection somewhere', whatToCheck: 'Inspect every connection', fix: 'Tighten or reseat as needed.' },
    ],
    proTips: ['Replace fuel filter at the same time — 10-minute additional job that prevents future issues.', 'Use Bosch or Delphi quality pump — cheap pumps fail in 1-2 years.', 'Photograph all connections before disassembly.', 'Have fire extinguisher nearby — non-negotiable.', 'Drop tank with low fuel — easier and safer.'],
    commonMistakes: [
      { mistake: 'Working with full tank', consequence: 'Heavy and dangerous to drop.' },
      { mistake: 'Skipping system depressurization', consequence: 'Fuel sprays when lines disconnect.' },
      { mistake: 'Buying cheap pump', consequence: 'Fails in 1-2 years; redo entire job.' },
    ],
    successIndicators: ['Engine starts and runs smoothly after install', 'Fuel pressure within spec (49 psi for 4.0L)', 'No fuel leaks anywhere', 'Fuel gauge reads correctly', 'No pump noise or grinding'],
    whenToSeekHelp: 'If pump fails to prime even with new module, suspect a wiring fault between PCM and pump. If tank shell is corroded, professional tank repair or replacement may be needed. Some TJs have multi-part tank straps that require a tank-removal kit to safely lower.',
    legalDisclaimer: 'This guide was generated by GarageIQ\'s AI from publicly available sources. Verify torque specifications against OEM. Fuel system work involves significant fire and fume hazards — work outdoors or in well-ventilated area with no ignition sources.',
    manualSource: { title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)', url: 'https://www.haynes.com/en-us/wrangler/jeep/2003', notes: 'Cross-reference fuel system layout and torque specs with your licensed manual.' },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — FUEL PRESSURE REGULATOR REPLACEMENT
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-fuel-pressure-regulator-replacement',
    title: 'Fuel Pressure Regulator Replacement (4.0L I6)',
    vehicle: { make: 'Jeep', model: 'Wrangler', generations: [{ startYear: 1997, endYear: 2006 }], engine: '4.0l-i6' },
    video: {
      youtubeId: 'Dha-4sZvmkc',
      title: 'Jeep TJ Wrangler Losing Fuel Pressure When Off - Long Crank to Start',
      channel: 'Taboo Customs',
    },
    source: 'Generated by GarageIQ AI from publicly available sources. Cross-checked per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Engine',
    difficulty: 'beginner',
    duration: '30-45 minutes',
    interval: 'Replace when fuel pressure is too high (over 55 psi at idle), too low (under 35 psi), or fluctuating; or when raw fuel is found in the vacuum line connected to the regulator.',
    description: 'The fuel pressure regulator (FPR) on the 4.0L is mounted on the fuel rail and uses engine vacuum to maintain ~49 psi differential between fuel pressure and manifold pressure. A failed regulator leaks fuel into the vacuum line (causes lean/rich condition) or holds wrong pressure. Replacement is straightforward.',
    overview: { difficulty: 'Beginner — direct R&R with no torque-critical fasteners', estimatedTime: '30-45 minutes', estimatedCost: '$30-60 for OEM-style regulator', whyMatters: 'Bad regulator causes fuel issues that mimic injector or pump problems.', safetyNote: 'Depressurize fuel system before opening rail. Fuel is flammable — work outdoors or ventilated area.' },
    tools: ['Fuel pressure gauge', 'Socket set (10mm)', 'Fuel line disconnect tool', 'Drain pan', 'Disposable nitrile gloves', 'Eye protection'],
    parts: [{ id: 'fuel-pressure-regulator', name: 'Fuel Pressure Regulator', partNumber: 'Mopar 53013811AB — equivalent: Standard PR175 / Bosch 0280160503', quantity: 1, price: 45, category: 'required', notes: 'Confirm correct part for application year.' }],
    safetyInformation: 'Fuel system pressurized — depressurize before opening. Fuel and vapors are flammable.',
    beforeYouStart: ['Verify fuel pressure issue with gauge', 'Have new regulator and fresh O-ring/seal staged', 'Depressurize fuel system before starting'],
    steps: [
      { number: 1, title: 'Depressurize Fuel System', description: 'Pull fuel pump fuse, start engine until it stalls.', why: 'Prevents fuel spray.', tip: 'Loosen fuel cap first.', commonMistake: 'Skipping this step.' },
      { number: 2, title: 'Locate Fuel Pressure Regulator', description: 'On 4.0L, the regulator is mounted on the fuel rail at the rear of the engine. Has a vacuum hose attached.', why: 'Visual identification.', tip: 'Trace the vacuum line to the manifold.', commonMistake: 'Confusing FPR with another sensor.' },
      { number: 3, title: 'Disconnect Vacuum Line', description: 'Pull the vacuum line off the regulator. Inspect for fuel inside — wet/raw fuel = regulator diaphragm failed.', why: 'Wet vacuum line is a positive ID of regulator failure.', tip: 'Cap the vacuum line.', commonMistake: 'Tearing the vacuum line.' },
      { number: 4, title: 'Disconnect Fuel Return Line', description: 'The regulator has a fuel return line. Use disconnect tool to release. Cap line.', why: 'Required to remove regulator.', tip: 'Have a rag ready for residual fuel.', commonMistake: 'Forcing the connection.' },
      { number: 5, title: 'Remove Regulator from Rail', description: 'Two small bolts (typically 10mm) hold the regulator to the rail. Remove and pull regulator straight off — has an O-ring at the rail interface.', why: 'Regulator presses into rail with O-ring seal.', tip: 'Note O-ring location — may stay in rail or come off with regulator.', commonMistake: 'Losing the O-ring.' },
      { number: 6, title: 'Install New Regulator', description: 'Apply small amount of clean fuel to the new O-ring. Press new regulator into rail squarely — it should seat with light pressure. Install bolts hand-tight.', why: 'Even seating prevents leaks.', tip: 'Do not crush the O-ring.', commonMistake: 'Forcing without alignment.' },
      { number: 7, title: 'Torque Bolts', description: 'Tighten regulator bolts to spec (typically 65 in-lbs / 7 Nm). Verify torque spec from OEM service manual.', why: 'Even torque seats the regulator.', tip: 'Low torque — do not over-tighten.', commonMistake: 'Stripping bolt holes.' },
      { number: 8, title: 'Reconnect Fuel Return and Vacuum', description: 'Reconnect the fuel return line until it clicks. Reconnect the vacuum line to the regulator.', why: 'Restores fuel and vacuum paths.', tip: 'Confirm click on the disconnect fitting.', commonMistake: 'Not fully clicking the fuel line.' },
      { number: 9, title: 'Restore Fuel System and Test', description: 'Replace the fuel pump fuse. Cycle key 3x to prime. Start engine — should idle smoothly. Check for any fuel leaks.', why: 'Initial verification before driving.', tip: 'Watch the regulator and fittings for any seepage.', commonMistake: 'Driving without leak check.' },
      { number: 10, title: 'Verify Fuel Pressure', description: 'Connect a fuel pressure gauge to the rail test port. Engine running: pressure should be 49 psi at idle. Pull vacuum line off regulator — pressure should rise to 55+ psi (no vacuum = full pressure). Reconnect — drops back to 49 psi.', why: 'Regulator function test confirms install.', tip: 'A 6 psi rise/drop with vacuum on/off confirms regulator working.', commonMistake: 'Skipping pressure verification.' },
    ],
    torqueSpecifications: [{ fastener: 'Fuel pressure regulator bolts', specification: '65 in-lbs (7 Nm). Verify spec from OEM service manual.', notes: 'Low torque.' }],
    fluidSpecifications: [],
    troubleshooting: [
      { problem: 'Pressure still wrong after install', likelyCause: 'Defective new regulator or pump issue', whatToCheck: 'Verify pump pressure first', fix: 'Warranty exchange regulator or address pump.' },
      { problem: 'Fuel leak at regulator', likelyCause: 'O-ring not seated or damaged', whatToCheck: 'Visual inspection', fix: 'Re-seat O-ring; replace if damaged.' },
      { problem: 'Vacuum line still has fuel', likelyCause: 'Either regulator was not the issue OR new regulator is also defective', whatToCheck: 'Confirm leak location', fix: 'If fuel returns, warranty new regulator.' },
    ],
    proTips: ['Inspect vacuum line BEFORE buying regulator — wet line confirms FPR failure.', 'Always replace O-ring during install.', 'Quality matters — Bosch and Standard are reliable brands.'],
    commonMistakes: [{ mistake: 'Replacing FPR for symptoms it does not cause', consequence: 'Wasted $45.' }, { mistake: 'Not checking vacuum line first', consequence: 'May replace FPR when problem is elsewhere.' }, { mistake: 'Skipping post-install pressure test', consequence: 'May not catch defective new regulator.' }],
    successIndicators: ['Fuel pressure at 49 psi at idle', '6 psi rise with vacuum disconnected', 'No fuel leaks', 'Engine runs smoothly with normal fuel trim'],
    whenToSeekHelp: 'If pressure remains wrong after FPR replacement, suspect pump or rail. If fuel rail is damaged, professional repair may be needed.',
    legalDisclaimer: 'Fuel system work involves fire risk. Verify procedures with licensed manual.',
    manualSource: { title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)', url: 'https://www.haynes.com/en-us/wrangler/jeep/2003', notes: 'Cross-reference fuel rail layout.' },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — FUEL INJECTOR CLEANING / REPLACEMENT
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-fuel-injector-cleaning-replacement',
    title: 'Fuel Injector Cleaning and Replacement (4.0L I6)',
    vehicle: { make: 'Jeep', model: 'Wrangler', generations: [{ startYear: 1997, endYear: 2006 }], engine: '4.0l-i6' },
    video: {
      youtubeId: 'ajBYBg76cdk',
      title: 'Install Jeep Wrangler TJ Fuel Injectors',
      channel: 'HNX Media',
    },
    source: 'Generated by GarageIQ AI from publicly available sources. Cross-checked per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Engine',
    difficulty: 'intermediate',
    duration: '2-3 hours for replacement; 30 minutes for in-place cleaning',
    interval: 'Clean every 60,000 miles or when running rough. Replace when one or more injectors fail (no flow, leak, or stuck).',
    description: 'The 4.0L uses six injectors mounted on the fuel rail at the back of the intake. Cleaning is via fuel additive (in-tank) or a professional rail flush. Replacement requires fuel rail removal — a 2-3 hour job. Bad injectors cause misfires, hard cold start (leaking), or poor fuel economy.',
    overview: { difficulty: 'Intermediate — fuel rail R&R with attention to injector orientation', estimatedTime: '30 min cleaning, 2-3 hours replacement', estimatedCost: '$10 fuel additive, $30-60 per replacement injector', whyMatters: 'Fuel injector issues cause misfire, hard start, and fuel economy loss.', safetyNote: 'Depressurize fuel system. Work in ventilated area. No ignition sources.' },
    tools: ['Fuel pressure gauge', 'Socket set', 'Fuel line disconnect tool', 'Multimeter (test injector resistance)', 'Noid lights', 'Drain pan', 'Disposable gloves', 'Eye protection'],
    parts: [
      { id: 'fuel-system-cleaner', name: 'Fuel System Cleaner (Techron, Sea Foam, BG44K)', partNumber: 'Chevron Techron / Sea Foam / BG44K', quantity: 1, price: 10, category: 'optional', notes: 'For cleaning only — added to fuel tank.' },
      { id: 'fuel-injector-replacement', name: 'Fuel Injector (each, replace as needed)', partNumber: 'Mopar 53030342AB — equivalents: Bosch 0280155845 / Standard FJ100', quantity: 1, price: 50, category: 'conditional', notes: 'Identify failed injector(s) before buying. May need to replace all six if multiple failing.' },
      { id: 'injector-orings', name: 'Injector O-rings (replace at any reseat)', partNumber: 'Felpro ES72720 set or generic 7.5x1.5mm Viton O-rings', quantity: 12, price: 8, category: 'required', notes: 'Two per injector (top and bottom). Always replace when injector is removed.' },
    ],
    safetyInformation: 'Fuel and fumes are flammable. Depressurize fuel system. Wear eye protection.',
    beforeYouStart: ['Identify which injector(s) are failing — code-scan, listen with stethoscope, do balance test', 'Have new O-rings ready', 'Depressurize system before opening rail'],
    steps: [
      { number: 1, title: 'Try Fuel Additive Cleaning First', description: 'Add a quality fuel system cleaner to a partially-empty tank, then fill up. Drive 100+ miles. Many "rough running" issues clear up with this.', why: 'Cheapest first step.', tip: 'Techron and BG44K are top-rated.', commonMistake: 'Replacing injectors before trying cleaning.' },
      { number: 2, title: 'Identify Failing Injectors', description: 'Code-scan for cylinder-specific misfire (P0301-P0306). Use a stethoscope on each injector — should hear a steady tick. Or do an injector balance test (specialized scan tool).', why: 'Identifies which injectors need attention.', tip: 'A failed injector often produces a wet plug at that cylinder.', commonMistake: 'Replacing all without identifying which.' },
      { number: 3, title: 'Test Injector Electrical', description: 'Disconnect each injector connector. Measure resistance with multimeter — should be 11-15 ohms. Out of range = electrical failure.', why: 'Electrical failure = replace injector.', tip: 'Cold injectors test more accurately.', commonMistake: 'Skipping electrical test.' },
      { number: 4, title: 'Depressurize and Disconnect Rail', description: 'Pull fuel pump fuse, run engine until stall. Disconnect fuel supply line at rail. Disconnect rail electrical connectors at injectors.', why: 'Rail removal requires depressurization.', tip: 'Cap fuel line.', commonMistake: 'Cracking line under pressure.' },
      { number: 5, title: 'Remove Fuel Rail', description: 'Two-three bolts (10mm) hold the rail to the manifold. Remove. Lift rail straight up — injectors come with it.', why: 'Rail with injectors lifts as a unit.', tip: 'Catch the small return clips that hold injectors.', commonMistake: 'Pulling injectors out of head while rail still bolted.' },
      { number: 6, title: 'Replace Failed Injectors', description: 'Each injector has retainer clips. Pop the clip, pull injector out of rail. Discard old O-rings. Install new injector with NEW O-rings (top and bottom). A drop of clean motor oil on each O-ring helps install.', why: 'O-rings are single use.', tip: 'Note injector orientation — connector faces specific direction.', commonMistake: 'Reusing O-rings.' },
      { number: 7, title: 'Reinstall Rail', description: 'Lower rail back onto manifold. Each injector seats into its bore. Bolt rail down — torque to spec. Reconnect electrical at each injector.', why: 'Rail must seat squarely.', tip: 'Verify each injector is fully seated before bolt-down.', commonMistake: 'Cocking the rail.' },
      { number: 8, title: 'Reconnect Fuel Supply', description: 'Reconnect supply line until it clicks. Confirm seal.', why: 'Required for fuel flow.', tip: 'Listen for click.', commonMistake: 'Partial connection.' },
      { number: 9, title: 'Restore Fuel System and Prime', description: 'Replace fuel pump fuse. Cycle key 3x to prime. Inspect for leaks at every injector and rail connection.', why: 'Fuel rail under pressure reveals leaks.', tip: 'Use a flashlight on each injector.', commonMistake: 'Starting engine without leak check.' },
      { number: 10, title: 'Test Drive and Verify Codes', description: 'Start engine — should idle smoothly. Test drive 25 miles. Code-scan for any new codes. Successful job = no misfires, smooth idle, normal fuel trim.', why: 'Confirms fix.', tip: 'Document fuel economy improvement.', commonMistake: 'Calling done without code recheck.' },
    ],
    torqueSpecifications: [{ fastener: 'Fuel rail bolts', specification: '85 in-lbs (10 Nm). Verify torque spec from OEM service manual.', notes: 'Low torque.' }],
    fluidSpecifications: [],
    troubleshooting: [
      { problem: 'Fuel leak at injector after install', likelyCause: 'O-ring damaged or not lubed', whatToCheck: 'Visual inspection', fix: 'Replace O-ring with lube.' },
      { problem: 'Misfire returns', likelyCause: 'Wrong injector identified or other issue', whatToCheck: 'Code-scan; balance test', fix: 'Re-diagnose.' },
      { problem: 'Rough idle after install', likelyCause: 'Injector not fully seated', whatToCheck: 'Pull rail, verify seating', fix: 'Reseat.' },
    ],
    proTips: ['Try fuel additive cleaning before replacing.', 'Always replace O-rings.', 'Buy Bosch or OEM injectors — generic ones often fail prematurely.', 'Photograph rail orientation before removal.'],
    commonMistakes: [{ mistake: 'Reusing O-rings', consequence: 'Fuel leak.' }, { mistake: 'Replacing one injector when multiple failing', consequence: 'Returns to shop.' }, { mistake: 'Skipping additive cleaning', consequence: 'May replace good injectors.' }],
    successIndicators: ['Smooth idle', 'No misfire codes', 'Even fuel trim', 'No fuel leaks at rail or injectors'],
    whenToSeekHelp: 'Professional injector cleaning service (rail flush) is $100-150 and can restore failing injectors without replacement. Recommended before final replacement decision.',
    legalDisclaimer: 'Fuel work involves fire risk. Verify with licensed manual.',
    manualSource: { title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)', url: 'https://www.haynes.com/en-us/wrangler/jeep/2003', notes: 'Cross-reference rail layout.' },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — FUEL PRESSURE TEST
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-fuel-pressure-test',
    title: 'Fuel Pressure Test (Diagnostic)',
    vehicle: { make: 'Jeep', model: 'Wrangler', generations: [{ startYear: 1997, endYear: 2006 }] },
    video: {
      youtubeId: 'Dha-4sZvmkc',
      title: 'Jeep TJ Wrangler Losing Fuel Pressure When Off - Long Crank to Start',
      channel: 'Taboo Customs',
    },
    source: 'Generated by GarageIQ AI from publicly available sources. Cross-checked per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Engine',
    difficulty: 'beginner',
    duration: '15-30 minutes',
    interval: 'Test whenever a fuel-related issue is suspected: hard start, no-start, misfire, low power, poor fuel economy.',
    description: 'The fuel pressure test is one of the most useful diagnostic tools for any modern vehicle. It tells you if the fuel pump, regulator, and overall system are working correctly. A simple gauge with a Schrader-valve adapter is all you need.',
    overview: { difficulty: 'Beginner — connect gauge and read', estimatedTime: '15-30 minutes', estimatedCost: '$30-50 for a fuel pressure gauge with Schrader adapter', whyMatters: 'Pressure test confirms or rules out fuel system as cause of any drivability issue.', safetyNote: 'Fuel under pressure — work outdoors or ventilated area. Wear eye protection.' },
    tools: ['Fuel pressure gauge with Schrader-valve adapter', 'Drain pan', 'Disposable gloves', 'Eye protection'],
    parts: [],
    safetyInformation: 'Fuel under pressure can spray when gauge is disconnected. Wear eye protection. Disconnect with care.',
    beforeYouStart: ['Confirm vehicle has Schrader test port on fuel rail (most TJs do)', 'Have gauge with correct adapter', 'Note current fuel-related symptoms'],
    steps: [
      { number: 1, title: 'Locate Schrader Test Port', description: 'On 4.0L, the Schrader port is on the fuel rail at the rear of the engine. Has a small cap that unscrews.', why: 'Test port allows pressure measurement without disassembly.', tip: 'Looks like a tire valve.', commonMistake: 'Looking on wrong location.' },
      { number: 2, title: 'Connect Gauge', description: 'Unscrew the cap. Connect the gauge with appropriate adapter. Snug but do not over-tighten.', why: 'Connection seals against the Schrader.', tip: 'A drop of fuel may emerge — have rag ready.', commonMistake: 'Stripping the Schrader.' },
      { number: 3, title: 'Key On — Engine Off Test', description: 'Turn key to RUN (not start). Pump primes for 2-3 seconds. Pressure should snap to 49 psi (4.0L spec).', why: 'Initial pressure check at static condition.', tip: 'Cycle key 3x for full prime.', commonMistake: 'Reading too quickly — wait for prime.' },
      { number: 4, title: 'Hold Test (Leak Down)', description: 'After pump stops priming, watch the gauge. Pressure should hold within 5 psi of peak for 5+ minutes. Rapid drop = leak (injectors, regulator, or check valve).', why: 'Hold test reveals fuel pressure leakdown — the cause of hard cold starts.', tip: 'A 10 psi drop over 30 minutes is normal; 20+ psi drop in 5 minutes is bad.', commonMistake: 'Skipping leak-down test.' },
      { number: 5, title: 'Engine Running Test', description: 'Start engine. Pressure should drop to 39-44 psi at idle (vacuum from intake reduces pressure differential per regulator). At idle, watch for stable pressure.', why: 'Running pressure verifies regulator function.', tip: 'Pressure dropping with vacuum is correct behavior.', commonMistake: 'Misinterpreting running pressure as "low."' },
      { number: 6, title: 'Vacuum Test the Regulator', description: 'With engine running, pull the vacuum line off the fuel pressure regulator. Pressure should jump up to ~49 psi (no vacuum reference). Reconnect and pressure drops back. A regulator that does not change with vacuum is failed.', why: 'Tests regulator function.', tip: 'Pressure swing with vacuum on/off should be ~5-7 psi.', commonMistake: 'Skipping regulator function test.' },
      { number: 7, title: 'WOT Test (If Suspected Pump Issue)', description: 'With engine running, have a helper hold accelerator at 3000 RPM. Pressure should remain stable at 49 psi. A drop indicates failing pump or clogged filter.', why: 'Identifies pumps that work at idle but fail under demand.', tip: 'Real-world issue often requires WOT test to reveal.', commonMistake: 'Only testing at idle.' },
      { number: 8, title: 'Note Pressure Pattern', description: 'Document: key-on pressure, idle pressure, regulator vacuum response, WOT pressure, leak-down rate. The pattern points to specific failure.', why: 'Pattern identifies cause.', tip: 'Phone notes are easier than memory.', commonMistake: 'Skipping documentation.' },
      { number: 9, title: 'Disconnect Gauge Carefully', description: 'Cycle key off. Wait 5 minutes for pressure to bleed down. Slowly unscrew the gauge — fuel will dribble. Catch with rag.', why: 'Pressurized fuel sprays when disconnected.', tip: 'Place rag under the connection.', commonMistake: 'Hot disconnect — sprays fuel.' },
      { number: 10, title: 'Interpret Results', description: 'Low pressure (<35 psi) = pump failing or fuel filter clogged. High pressure (>55 psi) = regulator stuck. Leak-down (drops to 0 in <30 min) = injectors leaking or check valve failed. No pressure = pump dead. Unstable = electrical issue.', why: 'Pattern guides next step.', tip: 'Reference back to specific guides for fixes.', commonMistake: 'Calling pressure "weird" without noting specifics.' },
    ],
    torqueSpecifications: [],
    fluidSpecifications: [],
    troubleshooting: [
      { problem: 'No pressure at all', likelyCause: 'Failed pump, blown fuse, or relay', whatToCheck: 'Listen for pump priming sound', fix: 'Check fuse #20 and relay; replace pump if dead.' },
      { problem: 'Pressure low (<35 psi)', likelyCause: 'Clogged filter or weakening pump', whatToCheck: 'Replace filter first, then test again', fix: 'New filter; if still low, replace pump.' },
      { problem: 'Pressure high (>55 psi)', likelyCause: 'Regulator stuck closed or return line restricted', whatToCheck: 'Replace regulator', fix: 'New regulator.' },
      { problem: 'Pressure drops fast at rest', likelyCause: 'Injector leaking or check valve failed', whatToCheck: 'Pull plugs after sit; one wet = leaking injector', fix: 'Replace bad injector or pump assembly.' },
    ],
    proTips: ['A fuel pressure gauge is the best $40 you can spend on diagnostic tools.', 'Leak-down test catches hard-cold-start cause that idle test misses.', 'Document all readings — pattern is the diagnosis.', 'Always test before replacing pump or regulator.'],
    commonMistakes: [{ mistake: 'Skipping leak-down test', consequence: 'Misses hard-cold-start cause.' }, { mistake: 'Replacing parts without test', consequence: 'Wasted money.' }],
    successIndicators: ['49 psi key-on (4.0L)', 'Stable at idle', 'Regulator vacuum test passes', 'Holds 30+ min'],
    whenToSeekHelp: 'A diagnostic shop can do additional tests (smoke testing, scope) if pressure test alone does not isolate the cause.',
    legalDisclaimer: 'Fuel work involves fire risk. Verify with licensed manual.',
    manualSource: { title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)', url: 'https://www.haynes.com/en-us/wrangler/jeep/2003', notes: 'Cross-reference fuel pressure specs.' },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — FUEL TANK R&R (P3 — concise)
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-fuel-tank-rr',
    title: 'Fuel Tank Removal and Installation',
    vehicle: { make: 'Jeep', model: 'Wrangler', generations: [{ startYear: 1997, endYear: 2006 }] },
    video: {
      youtubeId: 's0g1vNNsSdM',
      title: 'JEEP WRANGLER TJ fuel tank removal and fuel pump replacement',
      channel: 'Craft Customs',
    },
    source: 'Generated by GarageIQ AI from publicly available sources. Cross-checked per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Engine',
    difficulty: 'advanced',
    duration: '4-6 hours',
    interval: 'Replace when tank is rusted, leaking, or damaged. Remove for fuel pump service or major fuel system repair.',
    description: 'Fuel tank removal is required for fuel pump service, internal cleaning, or tank replacement. The TJ tank is a steel construction held to the chassis by two metal straps. Plan a full afternoon for the job — most of the time is fuel transfer and reassembly verification.',
    overview: { difficulty: 'Advanced — heavy component, fuel safety critical', estimatedTime: '4-6 hours', estimatedCost: '$150-300 for replacement tank if needed', whyMatters: 'Damaged or rusted tanks must be replaced — there is no patch repair for fuel tanks.', safetyNote: 'Fire/fume hazard. Drain tank fully before removal. No ignition sources.' },
    tools: ['Floor jack and jack stands', 'Fuel transfer pump', 'Sockets (10-19mm)', 'Fuel line disconnect tool', 'Drain pan (5+ gallons)', 'Fire extinguisher', 'Eye protection and gloves'],
    parts: [{ id: 'fuel-tank', name: 'Fuel Tank (replacement)', partNumber: 'Mopar 52127601 / equivalent', quantity: 1, price: 250, category: 'conditional', notes: 'Buy quality tank — coatings matter.' }, { id: 'tank-strap-bolts-2', name: 'Tank strap bolts', partNumber: 'M10x1.5 grade 8', quantity: 4, price: 6, category: 'conditional', notes: 'Often corroded; replace.' }],
    safetyInformation: 'Fuel and fumes ignite explosively. Drain fully. Work outdoors or ventilated. NO ignition sources. Fire extinguisher within reach.',
    beforeYouStart: ['Drain tank to empty via pump or run dry', 'Disconnect battery', 'Depressurize fuel system'],
    steps: [
      { number: 1, title: 'Drain Tank Completely', description: 'Use a fuel transfer pump to remove all fuel from the tank into approved containers. Or run vehicle until out of fuel.', why: 'Empty tank is much safer to handle.', tip: 'Stop pumping when air starts coming through.', commonMistake: 'Working with fuel still in tank.' },
      { number: 2, title: 'Lift Vehicle and Stage Drain Pan', description: 'Raise vehicle on stands, position drain pan below tank.', why: 'Catches any residual.', tip: 'Even after pumping, residual fuel is in the tank lines.', commonMistake: 'Skipping drain pan.' },
      { number: 3, title: 'Disconnect Fuel Filler and Vent Hoses', description: 'Remove clamps and disconnect filler hose and vent lines.', why: 'Tank must be free of body connections.', tip: 'Filler hose may stick — wiggle gently.', commonMistake: 'Tearing hoses.' },
      { number: 4, title: 'Disconnect Fuel Lines at Tank', description: 'Use disconnect tool on supply, return, and EVAP lines. Cap each.', why: 'Frees tank from fuel system.', tip: 'Cap immediately to prevent contamination.', commonMistake: 'Forcing fittings.' },
      { number: 5, title: 'Disconnect Pump Electrical', description: 'Disconnect pump module connector at top of tank.', why: 'Frees electrical.', tip: 'Press lock tab.', commonMistake: 'Pulling wires not connector.' },
      { number: 6, title: 'Support Tank with Jack', description: 'Position floor jack with wide saddle under tank center.', why: 'Tank weighs 60-100 lbs.', tip: 'Have helper steady one end.', commonMistake: 'Inadequate support.' },
      { number: 7, title: 'Remove Strap Bolts', description: 'Two strap bolts (typically 13mm/15mm). Apply penetrating oil if corroded. Remove slowly.', why: 'Straps hold tank to chassis.', tip: 'Replace bolts if corroded.', commonMistake: 'Letting tank drop unevenly.' },
      { number: 8, title: 'Lower and Remove Tank', description: 'Lower jack slowly, watching all connections clear. Once free, slide tank out from under vehicle.', why: 'Slow lower prevents catching.', tip: 'A second helper helps with awkward angles.', commonMistake: 'Forcing tank out.' },
      { number: 9, title: 'Inspect / Service / Replace Tank', description: 'If servicing pump only, work on tank on floor. If replacing, transfer pump module to new tank using new O-ring.', why: 'Tank service done now while accessible.', tip: 'Photograph everything.', commonMistake: 'Skipping inspection of tank interior.' },
      { number: 10, title: 'Reverse for Reinstall', description: 'Lift tank into position with jack. Hand-thread strap bolts. Reconnect electrical, fuel lines, vent, filler hose. Add 2+ gallons of fuel. Cycle key 3x. Start and verify no leaks.', why: 'Reverse order restores everything.', tip: 'Take time on each connection.', commonMistake: 'Forgetting a connection.' },
    ],
    torqueSpecifications: [{ fastener: 'Fuel tank strap bolts', specification: '30 ft-lbs (41 Nm).', notes: 'Cross-tighten.' }],
    fluidSpecifications: [{ fluid: 'Gasoline', type: '87 octane unleaded', capacity: '19 gallons (TJ standard tank)', notes: 'Refill after install.' }],
    troubleshooting: [
      { problem: 'Fuel leak at filler hose', likelyCause: 'Hose torn or clamp loose', whatToCheck: 'Visual inspection', fix: 'Replace hose; tighten clamps.' },
      { problem: 'Tank drops unevenly during install', likelyCause: 'Strap bolt threading is off', whatToCheck: 'Verify bolt threads', fix: 'Hand-thread carefully.' },
    ],
    proTips: ['Drain completely before tank drop.', 'Photograph everything.', 'Have helper for tank handling.', 'Replace strap bolts if corroded.'],
    commonMistakes: [{ mistake: 'Working with fuel in tank', consequence: 'Heavy and dangerous.' }, { mistake: 'Letting tank drop without helper', consequence: 'Damaged tank or pinched connections.' }],
    successIndicators: ['Tank installed and secure', 'No fuel leaks', 'Pump primes and engine runs'],
    whenToSeekHelp: 'If tank shell is damaged or rusted through, replacement is required. Some shops will install customer-supplied tank for reduced cost.',
    legalDisclaimer: 'Major fire hazard work. Verify with licensed manual.',
    manualSource: { title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)', url: 'https://www.haynes.com/en-us/wrangler/jeep/2003', notes: 'Cross-reference tank layout.' },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — FUEL RAIL R&R (P3 — concise)
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-fuel-rail-rr',
    title: 'Fuel Rail Removal and Installation (4.0L I6)',
    vehicle: { make: 'Jeep', model: 'Wrangler', generations: [{ startYear: 1997, endYear: 2006 }], engine: '4.0l-i6' },
    video: {
      youtubeId: 'ajBYBg76cdk',
      title: 'Install Jeep Wrangler TJ Fuel Injectors (fuel rail removal)',
      channel: 'HNX Media',
    },
    source: 'Generated by GarageIQ AI from publicly available sources. Cross-checked per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Engine',
    difficulty: 'intermediate',
    duration: '1.5-2 hours',
    interval: 'Remove for injector service, intake manifold work, or fuel rail leak.',
    description: 'The 4.0L fuel rail mounts on the back of the intake manifold and holds all six fuel injectors. Removal is straightforward but requires fuel system depressurization and care with O-rings during reinstall.',
    overview: { difficulty: 'Intermediate — careful with O-rings and fuel pressure', estimatedTime: '1.5-2 hours', estimatedCost: '$0 (no new parts unless replacing injectors)', whyMatters: 'Required for many injector or intake jobs.', safetyNote: 'Fuel under pressure. Depressurize first.' },
    tools: ['Sockets (10mm)', 'Fuel line disconnect tool', 'Drain pan', 'Eye protection'],
    parts: [{ id: 'injector-orings-2', name: 'Injector O-rings (12 pieces)', partNumber: 'Felpro ES72720 set', quantity: 1, price: 8, category: 'required', notes: 'Always replace.' }],
    safetyInformation: 'Fuel pressurized. Depressurize first. Wear eye protection.',
    beforeYouStart: ['Depressurize fuel system', 'Disconnect battery', 'Have new O-rings staged'],
    steps: [
      { number: 1, title: 'Depressurize Fuel System', description: 'Pull fuel pump fuse, run engine until stall.', why: 'Prevents spray.', tip: 'Loosen fuel cap.', commonMistake: 'Skipping.' },
      { number: 2, title: 'Disconnect Fuel Supply Line', description: 'Disconnect at the rail end with disconnect tool. Cap line.', why: 'Frees rail.', tip: 'Have rag for residual fuel.', commonMistake: 'Forcing.' },
      { number: 3, title: 'Disconnect Injector Electrical Connectors', description: 'Pull connector at each injector (6 total). Press lock tab.', why: 'Frees electrical.', tip: 'Photograph orientation.', commonMistake: 'Pulling wires.' },
      { number: 4, title: 'Remove Rail Mounting Bolts', description: 'Two-three 10mm bolts hold rail to manifold.', why: 'Rail mounting.', tip: 'Note bolt order.', commonMistake: 'Stripping.' },
      { number: 5, title: 'Lift Rail with Injectors', description: 'Lift rail straight up — injectors come with it.', why: 'Rail with injectors lifts as unit.', tip: 'Catch return clips.', commonMistake: 'Pulling injectors out separately.' },
      { number: 6, title: 'Service Injectors If Needed', description: 'Pop injector clips, replace O-rings on each.', why: 'O-rings are single use.', tip: 'Lube new O-rings with motor oil.', commonMistake: 'Reusing O-rings.' },
      { number: 7, title: 'Reinstall Rail with New O-rings', description: 'Each injector seats into its bore. Press rail down evenly.', why: 'Even seating.', tip: 'No cocking.', commonMistake: 'Cocked rail.' },
      { number: 8, title: 'Bolt Rail Down', description: 'Hand-thread bolts. Torque to spec (85 in-lbs / 10 Nm).', why: 'Even torque.', tip: 'Cross-tighten.', commonMistake: 'Stripping.' },
      { number: 9, title: 'Reconnect Electrical and Fuel', description: 'All six injector connectors. Fuel supply line until click.', why: 'Restores function.', tip: 'Verify each connection.', commonMistake: 'Missing one.' },
      { number: 10, title: 'Restore System and Verify', description: 'Replace fuel pump fuse. Cycle key 3x. Inspect for leaks. Start engine.', why: 'Verifies install.', tip: 'Look for any seepage.', commonMistake: 'Skipping leak check.' },
    ],
    torqueSpecifications: [{ fastener: 'Fuel rail bolts', specification: '85 in-lbs (10 Nm).', notes: 'Low torque.' }],
    fluidSpecifications: [],
    troubleshooting: [
      { problem: 'Fuel leak at injector', likelyCause: 'O-ring damaged', whatToCheck: 'Visual', fix: 'Replace O-ring with lube.' },
      { problem: 'Engine misfires', likelyCause: 'Injector connector forgot', whatToCheck: 'Verify all 6 connected', fix: 'Reconnect.' },
    ],
    proTips: ['Always replace O-rings.', 'Lube O-rings before install.', 'Photograph electrical connector positions.'],
    commonMistakes: [{ mistake: 'Reusing O-rings', consequence: 'Fuel leak.' }],
    successIndicators: ['No leaks', 'Smooth idle', 'No misfire codes'],
    whenToSeekHelp: 'If rail is damaged, replacement is needed.',
    legalDisclaimer: 'Fuel work involves fire risk.',
    manualSource: { title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)', url: 'https://www.haynes.com/en-us/wrangler/jeep/2003', notes: 'Cross-reference rail layout.' },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — EXHAUST MANIFOLD REPLACEMENT (4.0L I6 — #6 cyl crack)
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-exhaust-manifold-replacement-4pt0l-i6',
    title: 'Exhaust Manifold Replacement (4.0L I6) — Famous #6 Cylinder Crack',
    vehicle: { make: 'Jeep', model: 'Wrangler', generations: [{ startYear: 1997, endYear: 2006 }], engine: '4.0l-i6' },
    video: {
      youtubeId: 'IF4L8UgIDTg',
      title: 'Jeep Wrangler TJ 4.0 Exhaust Manifold Replacement',
      channel: 'Redneck Garage',
    },
    source: 'Generated by GarageIQ AI from publicly available sources (Mopar parts catalog, Dorman and Banks aftermarket exhaust manifold literature, NHTSA TSBs and Complaints database, owner\'s manual, technician forums and Wrangler community service threads). Cross-checked and structured per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Engine',
    difficulty: 'advanced',
    duration: '4-6 hours',
    interval: 'Replace when manifold cracks (loud tick noise, exhaust leak smell, blackened heat shield) or when warpage causes leaking gasket. The 4.0L is famous for cracking at the cylinder #6 runner — a known weakness.',
    description: 'The 4.0L I6 cast-iron exhaust manifold is famously prone to cracking at the rear (cylinder #6) runner due to thermal cycling. Symptoms: loud ticking on cold start that sometimes quiets when warm (gap closes from heat expansion), exhaust smell, and blackening at the manifold-to-head joint. Replacement requires the intake manifold to come off too (they share head bolts). Aftermarket performance manifolds (Banks, Borla, Dorman) often fix the crack issue with stronger casting and a stress-relief slot.',
    overview: { difficulty: 'Advanced — manifold and intake share head bolts; thermal/rust corrosion makes bolt removal hard', estimatedTime: '4-6 hours; longer if studs break', estimatedCost: '$150-300 for OEM-style replacement; $250-400 for performance manifold (Banks, Borla, Dorman)', whyMatters: 'Cracked manifold sounds awful and may eventually cause exhaust gas to enter cabin via ventilation system. Repair is not a quick fix — many owners delay until vehicle inspection fails.', safetyNote: 'Manifold gets very hot — let cool fully (4+ hours) before working. Exhaust bolts often break — have a stud extractor ready. Wear eye protection — debris falls from above when working under hood.' },
    tools: ['Floor jack and jack stands', 'Sockets (10-22mm) with long extensions and swivels', 'Penetrating oil (lots of it)', 'Heat gun or propane torch (for stuck bolts)', 'Stud extractor / EZ-out kit', 'Drill and bits (for broken bolts)', 'Brake cleaner', 'Gasket scraper', 'Torque wrench', 'Eye protection and gloves'],
    parts: [
      { id: 'exhaust-manifold-4pt0l', name: 'Exhaust Manifold (OEM-style or performance)', partNumber: 'Mopar 53010195AB (OEM) — performance: Dorman 674-152 or Banks 51309', quantity: 1, price: 220, category: 'required', notes: 'Performance manifolds (Dorman, Banks) often fix the crack issue with stronger material and stress-relief design.' },
      { id: 'exhaust-manifold-gasket-set', name: 'Intake/Exhaust Manifold Gasket Set', partNumber: 'Felpro MS96005 — equivalent: Mahle MS19354', quantity: 1, price: 35, category: 'required', notes: 'Always replace gasket. Includes intake-side gasket since intake comes off too.' },
      { id: 'exhaust-manifold-bolts', name: 'Manifold Bolts and Studs', partNumber: 'Mopar 6504062 — generic M10x1.25 stud kit', quantity: 1, price: 40, category: 'recommended', notes: 'Original studs often corroded or broken. New stud kit prevents future issues.' },
      { id: 'exhaust-pipe-gasket', name: 'Exhaust Pipe-to-Manifold Gasket (donut)', partNumber: 'Mopar 53010198 — generic 2.25" donut gasket', quantity: 1, price: 8, category: 'required', notes: 'Replace whenever the pipe is disconnected.' },
    ],
    safetyInformation: 'Exhaust components reach 1,000°F+ during operation. Allow 4+ hours of cool-down. Penetrating oil should be applied 24+ hours before disassembly for best results. If a bolt breaks, drilling out is involved — be patient. Eye protection mandatory — chip fragments from broken bolts fly. Wear nitrile gloves.',
    beforeYouStart: ['Allow engine to fully cool', 'Apply penetrating oil to all manifold bolts the day before', 'Have all replacement gaskets and stud kit staged', 'Disconnect battery negative cable', 'Drain coolant if accessing thermostat housing as part of intake removal', 'Photograph everything before disassembly'],
    steps: [
      { number: 1, title: 'Lift Vehicle and Disconnect Exhaust at Manifold', description: 'Raise vehicle on stands. Locate the connection between the exhaust manifold and the down-pipe (3 bolts, 17mm typical). Apply more penetrating oil. Loosen and remove. The donut gasket goes between manifold and pipe.', why: 'Frees pipe from manifold for removal.', tip: 'Heat the bolts with a torch if penetrating oil alone is not enough.', commonMistake: 'Forcing rusted bolts. They break.' },
      { number: 2, title: 'Disconnect O2 Sensor and Heat Shield', description: 'Disconnect upstream O2 sensor electrical connector. Remove heat shield over the manifold (typically 4-6 small bolts). The heat shield protects underhood components from manifold heat.', why: 'Heat shield blocks bolt access; O2 sensor wire must be free.', tip: 'Photograph heat shield orientation.', commonMistake: 'Yanking O2 wire.' },
      { number: 3, title: 'Remove Throttle Body and Air Intake', description: 'Remove the air intake hose at the throttle body. Disconnect throttle body wiring and throttle linkage. Remove throttle body (4 bolts).', why: 'Frees intake manifold for removal.', tip: 'Photograph all electrical and hose connections.', commonMistake: 'Forgetting a connection.' },
      { number: 4, title: 'Disconnect Fuel Lines, Vacuum Lines, and Coolant Hoses at Intake', description: 'Disconnect fuel rail, all vacuum lines, MAP sensor, IAC valve, IAT sensor, and coolant lines crossing the intake. Label everything.', why: 'Intake must come off completely.', tip: 'Bag and label every disconnection.', commonMistake: 'Yanking wires.' },
      { number: 5, title: 'Remove Intake/Exhaust Manifold Bolts', description: 'The 4.0L intake and exhaust manifolds share the same head bolts (alternating). Loosen all manifold bolts in star pattern, 1/4 turn at a time, until tension releases. The intake and exhaust will separate from the head together.', why: 'Star-pattern release prevents warpage.', tip: 'Some bolts may shear — be ready to extract broken studs.', commonMistake: 'Forcing rusted bolts. Use heat and patience.' },
      { number: 6, title: 'Lift Manifolds Off and Separate', description: 'Lift the combined intake/exhaust assembly off the head. The intake and exhaust are bolted together — separate them on a workbench (typically 4-6 small bolts).', why: 'Working on bench is easier than in engine bay.', tip: 'Note the orientation of the intake-to-exhaust mounting.', commonMistake: 'Trying to separate in place.' },
      { number: 7, title: 'Clean Head Mating Surfaces', description: 'Use a gasket scraper to remove all old gasket material from both the cylinder head face and the back of the intake manifold. Spray brake cleaner. Inspect the head for corrosion or damage.', why: 'Clean surfaces are essential for new gasket seal.', tip: 'A green Scotch-Brite pad cleans corrosion gently.', commonMistake: 'Leaving residue. Gasket leaks.' },
      { number: 8, title: 'Install New Manifold and Gaskets', description: 'Apply the new gasket onto the head. Carefully position the new exhaust manifold (and intake manifold) onto the head. Hand-thread all manifold bolts. Cross-tighten in star pattern, in stages, to spec (typically 23 ft-lbs / 31 Nm). Verify torque spec from OEM service manual.', why: 'Even progressive torque seats gasket without distortion.', tip: 'Use anti-seize on stud threads — future removal is easier.', commonMistake: 'Cross-threading studs.' },
      { number: 9, title: 'Reconnect Down-Pipe and All Auxiliaries', description: 'Reattach exhaust down-pipe with new donut gasket. Reattach throttle body, all electrical, fuel lines, vacuum lines, and coolant hoses. Reattach heat shield. Reconnect O2 sensor.', why: 'Reverse order of disassembly.', tip: 'Check off each connection from your photos.', commonMistake: 'Missing a vacuum line.' },
      { number: 10, title: 'Reconnect Battery and Test', description: 'Reconnect battery. Start engine — should be quiet (no exhaust tick). Listen for any exhaust leak at the manifold-to-head joint (faint hissing/popping). Verify O2 sensor codes do not appear.', why: 'Initial verification.', tip: 'A small soapy water spray on the manifold-to-head joint reveals leaks (bubbles).', commonMistake: 'Driving without leak check.' },
    ],
    torqueSpecifications: [
      { fastener: 'Exhaust manifold to head bolts', specification: '23 ft-lbs (31 Nm). Verify torque spec from OEM service manual.', notes: 'Cross-tighten center-to-outside in star pattern.' },
      { fastener: 'Exhaust pipe to manifold bolts', specification: '20 ft-lbs (27 Nm). Verify torque spec from OEM service manual.', notes: 'Cross-tighten three bolts.' },
      { fastener: 'Heat shield bolts', specification: '85 in-lbs (10 Nm). Verify torque spec from OEM service manual.', notes: 'Low torque.' },
    ],
    fluidSpecifications: [{ fluid: 'Engine coolant (top-off if disturbed)', type: 'Match existing', capacity: 'Top-off as needed', notes: 'Coolant lines may have been disconnected.' }],
    troubleshooting: [
      { problem: 'Exhaust tick remains after replacement', likelyCause: 'Gasket not seating, manifold warped, or new manifold cracked from shipping', whatToCheck: 'Visual inspection; soapy water test for leak location', fix: 'Re-torque bolts; if leak persists, pull and reinspect.' },
      { problem: 'O2 sensor code (P0130 series)', likelyCause: 'Sensor connector loose or sensor disturbed during R&R', whatToCheck: 'Reseat connector; test sensor', fix: 'Reconnect; replace sensor if defective.' },
      { problem: 'Bolt sheared during removal', likelyCause: 'Severe corrosion', whatToCheck: 'Identify broken bolt location', fix: 'Drill and extract; or use easy-out. Replace with new stud.' },
      { problem: 'Engine misfires after install', likelyCause: 'Vacuum line forgotten or hose pinched', whatToCheck: 'Verify all vacuum connections', fix: 'Restore connections.' },
    ],
    proTips: ['Performance aftermarket manifolds (Banks, Dorman) fix the crack issue with stronger casting.', 'Apply penetrating oil 24+ hours before disassembly.', 'Heat is essential for stuck bolts — use a propane torch if needed.', 'Replace ALL studs preemptively — old corroded ones will eventually fail.', 'Apply anti-seize to new studs — saves future removal hassle.'],
    commonMistakes: [{ mistake: 'Forcing rusted bolts without penetrating oil and heat', consequence: 'Bolts shear off in the head — major repair.' }, { mistake: 'Reusing old gaskets', consequence: 'Exhaust leak; manifold tick returns.' }, { mistake: 'Skipping anti-seize on new studs', consequence: 'Future removal is just as hard.' }],
    successIndicators: ['No exhaust tick on cold or warm start', 'No exhaust smell', 'No O2 sensor codes', 'No leak detected by soapy water test', 'Engine runs smoothly'],
    whenToSeekHelp: 'If multiple bolts shear during removal, consider professional repair — drilling broken studs in cylinder heads requires skill and cylinder head can be damaged. Some shops will rent stud-extractor tools or do this specific job for reasonable cost.',
    legalDisclaimer: 'This guide was generated by GarageIQ\'s AI from publicly available sources. Verify torque specifications against OEM. Exhaust components are very hot — work only when fully cooled.',
    manualSource: { title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)', url: 'https://www.haynes.com/en-us/wrangler/jeep/2003', notes: 'Cross-reference manifold torques and bolt patterns with your licensed manual.' },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — CATALYTIC CONVERTER REPLACEMENT
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-catalytic-converter-replacement',
    title: 'Catalytic Converter Replacement',
    vehicle: { make: 'Jeep', model: 'Wrangler', generations: [{ startYear: 1997, endYear: 2006 }] },
    video: {
      youtubeId: 'rQpAVP1g3_c',
      title: 'How to replace catalytic converter on Jeep TJ with 4.0',
      channel: 'Shockwave Outdoors',
    },
    source: 'Generated by GarageIQ AI from publicly available sources. Cross-checked per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Engine',
    difficulty: 'intermediate',
    duration: '1-2 hours',
    interval: 'Replace when P0420/P0430 codes appear, vehicle fails emissions test, cat is rattling internally, or extreme back-pressure (low power) is found.',
    description: 'The TJ catalytic converter (cat) is welded into the exhaust just downstream of the manifold. Replacement involves either bolt-in or weld-in, depending on the original setup. CARB-compliant replacements are required in some states; OEM-style is acceptable elsewhere. Most TJs fail the cat after 100,000+ miles of service.',
    overview: { difficulty: 'Intermediate — requires either welding or bolt-in fitment', estimatedTime: '1-2 hours', estimatedCost: '$120-280 for direct-fit cat (varies by state requirements)', whyMatters: 'Failed cat causes P0420/P0430, fails emissions test, and can clog and reduce engine performance.', safetyNote: 'Cat is hot during operation — let cool fully. Exhaust bolts often rusted.' },
    tools: ['Floor jack and jack stands', 'Penetrating oil', 'Sockets (15mm-19mm)', 'Reciprocating saw or angle grinder (if cutting weld-in section)', 'Welder (if welding new section)', 'Eye protection and gloves'],
    parts: [
      { id: 'catalytic-converter', name: 'Catalytic Converter (direct-fit)', partNumber: 'Magnaflow 49234 (CARB) / Walker 50300 (Federal) — varies by state requirement', quantity: 1, price: 200, category: 'required', notes: 'CARB-compliant if you live in CA or other emissions-strict states.' },
      { id: 'exhaust-clamps', name: 'Exhaust Clamps and Hangers', partNumber: '2.25" or 2.5" muffler clamps', quantity: 4, price: 12, category: 'recommended', notes: 'Replace clamps and hangers if old.' },
    ],
    safetyInformation: 'Exhaust hot during operation. Allow 4+ hours cooling. Used cats may contain harmful materials — handle carefully.',
    beforeYouStart: ['Diagnose cat failure with code-scan and back-pressure test', 'Verify state requires CARB or just Federal-grade replacement', 'Apply penetrating oil to exhaust bolts day before'],
    steps: [
      { number: 1, title: 'Lift Vehicle and Inspect Cat', description: 'Raise on stands. Locate cat. Inspect for: rattling internal substrate, melted internal honeycomb, or external damage.', why: 'Confirms replacement need.', tip: 'A rattling cat has broken internal substrate.', commonMistake: 'Replacing cat without confirming failure.' },
      { number: 2, title: 'Disconnect Downstream O2 Sensor', description: 'Trace wire from O2 sensor (post-cat) and disconnect connector. Remove sensor with O2 socket if needed for clearance.', why: 'Sensor wire must be free.', tip: 'Apply anti-seize on sensor threads when reinstalling.', commonMistake: 'Pulling wire.' },
      { number: 3, title: 'Disconnect Exhaust at Manifold-Side', description: 'Remove the 3 bolts at the cat-to-manifold flange (or cut if welded).', why: 'Frees front of cat.', tip: 'Heat may be needed.', commonMistake: 'Forcing rusted bolts.' },
      { number: 4, title: 'Disconnect Exhaust at Tailpipe-Side', description: 'Loosen the muffler clamp or cut where appropriate. Slide cat back away from muffler/intermediate pipe.', why: 'Frees rear of cat.', tip: 'Mark cuts cleanly if weld-in.', commonMistake: 'Cutting too close to good pipe.' },
      { number: 5, title: 'Remove Old Cat', description: 'Slide cat out from under vehicle.', why: 'Removed for replacement.', tip: 'Photograph orientation.', commonMistake: 'Forgetting orientation.' },
      { number: 6, title: 'Position and Install New Cat', description: 'Position new cat in same orientation. For bolt-in: install bolts at flange. For weld-in: align ends with pipes.', why: 'Direct-fit replacement.', tip: 'Loosely fit before final tighten.', commonMistake: 'Tight before alignment.' },
      { number: 7, title: 'Bolt or Weld New Cat', description: 'Bolt-in: torque flange bolts to spec (20 ft-lbs / 27 Nm). Weld-in: weld smooth joints, both upstream and downstream.', why: 'Final secure connection.', tip: 'Welder needed for weld-in cats — consider hiring out if no welder.', commonMistake: 'Loose bolts or rough welds.' },
      { number: 8, title: 'Reinstall O2 Sensor', description: 'Apply anti-seize. Install sensor. Reconnect electrical.', why: 'Restores O2 monitoring.', tip: 'Cross-thread risk on aluminum threads — start by hand.', commonMistake: 'Cross-threading.' },
      { number: 9, title: 'Verify Connections and Lower', description: 'Inspect all clamps, bolts, welds. Lower vehicle.', why: 'Final check before drive.', tip: 'Soapy water test reveals leaks.', commonMistake: 'Skipping inspection.' },
      { number: 10, title: 'Drive and Verify No Codes', description: 'Reset codes. Drive 50+ miles to allow cat to warm up and PCM to evaluate. Verify no P0420 or P0430 codes return.', why: 'Cat efficiency monitoring.', tip: 'A new cat may need 50+ miles before PCM accepts it.', commonMistake: 'Calling fixed before drive cycle.' },
    ],
    torqueSpecifications: [
      { fastener: 'Exhaust flange bolts', specification: '20 ft-lbs (27 Nm). Verify torque spec from OEM service manual.', notes: 'Cross-tighten.' },
      { fastener: 'O2 sensor', specification: '30 ft-lbs (41 Nm). Apply anti-seize.', notes: 'Hand-start to prevent cross-thread.' },
    ],
    fluidSpecifications: [],
    troubleshooting: [
      { problem: 'P0420 returns after install', likelyCause: 'Cat defective, O2 sensor failed, or exhaust leak between manifold and cat', whatToCheck: 'Inspect cat connections; test O2 sensor', fix: 'Address leak or replace defective sensor.' },
      { problem: 'Exhaust leak at flange', likelyCause: 'Loose bolts or warped flange', whatToCheck: 'Visual inspection; soapy water', fix: 'Re-torque or replace gasket.' },
      { problem: 'Engine runs poorly', likelyCause: 'Wrong-size cat causing back-pressure', whatToCheck: 'Verify cat is correct size for application', fix: 'Replace with correct cat.' },
    ],
    proTips: ['CARB-compliant cats are required in CA and similar states.', 'Check state emissions requirements before buying.', 'Always replace upstream O2 sensor at the same time if it is original (often the actual cause of P0420).', 'Anti-seize on O2 threads is essential.'],
    commonMistakes: [{ mistake: 'Buying federal cat in CARB state', consequence: 'Vehicle fails emissions; have to replace.' }, { mistake: 'Skipping O2 sensor replacement', consequence: 'P0420 returns.' }],
    successIndicators: ['No P0420/P0430 codes after 50+ miles', 'No exhaust leaks', 'No cat rattle', 'Engine runs normally'],
    whenToSeekHelp: 'If you do not have welding equipment for weld-in cats, exhaust shops often install customer-supplied cats for reasonable cost.',
    legalDisclaimer: 'Many states require CARB-compliant cats. Verify state requirements. Tampering with emissions controls is federal law violation.',
    manualSource: { title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)', url: 'https://www.haynes.com/en-us/wrangler/jeep/2003', notes: 'Cross-reference exhaust system layout.' },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — MUFFLER REPLACEMENT (P3 — concise)
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-muffler-replacement',
    title: 'Muffler Replacement',
    vehicle: { make: 'Jeep', model: 'Wrangler', generations: [{ startYear: 1997, endYear: 2006 }] },
    video: {
      youtubeId: 'NXRTpQknSuI',
      title: 'Jeep TJ muffler replacement',
      channel: 'Problem Solver Garage',
    },
    source: 'Generated by GarageIQ AI from publicly available sources. Cross-checked per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Engine',
    difficulty: 'intermediate',
    duration: '1-2 hours',
    interval: 'Replace when rusted through, leaking exhaust noise, internal baffles rattling, or for sound upgrade.',
    description: 'Muffler replacement on a TJ is straightforward — remove old muffler from clamps or weld points, install new in same orientation. Aftermarket mufflers (Magnaflow, Flowmaster) offer different sound characters.',
    overview: { difficulty: 'Intermediate — exhaust corrosion can complicate', estimatedTime: '1-2 hours', estimatedCost: '$60-200 for muffler depending on brand', whyMatters: 'Rusted muffler leaks exhaust gases. New muffler restores quiet operation.', safetyNote: 'Exhaust hot during operation. Cool before working.' },
    tools: ['Floor jack and jack stands', 'Sockets, wrenches', 'Reciprocating saw or angle grinder', 'Penetrating oil', 'Eye protection'],
    parts: [{ id: 'muffler', name: 'Muffler (direct-fit)', partNumber: 'Magnaflow 13257 / Flowmaster 942554 / Walker 17710', quantity: 1, price: 120, category: 'required', notes: 'Direct-fit mufflers replace OEM with no welding (use clamps).' }, { id: 'muffler-clamps', name: 'Muffler clamps', partNumber: '2.25" muffler clamps', quantity: 2, price: 8, category: 'required', notes: 'Always replace.' }],
    safetyInformation: 'Hot exhaust. Cool fully. Wear eye protection.',
    beforeYouStart: ['Confirm muffler failure (rust, leak)', 'Apply penetrating oil to clamps', 'Have new muffler and clamps ready'],
    steps: [
      { number: 1, title: 'Lift Vehicle', description: 'Raise on stands.', why: 'Access.', tip: 'High enough for under-vehicle work.', commonMistake: 'Inadequate clearance.' },
      { number: 2, title: 'Disconnect Tailpipe', description: 'Loosen muffler-to-tailpipe clamp.', why: 'Frees rear of muffler.', tip: 'Hammer-and-screwdriver to crack rust.', commonMistake: 'Forcing.' },
      { number: 3, title: 'Disconnect Cat-Side', description: 'Loosen muffler-to-cat clamp or cut if welded.', why: 'Frees front of muffler.', tip: 'Mark cuts cleanly.', commonMistake: 'Cutting too short.' },
      { number: 4, title: 'Remove Old Muffler', description: 'Slide muffler out.', why: 'Removed.', tip: 'Photograph orientation.', commonMistake: 'Forgetting orientation.' },
      { number: 5, title: 'Compare and Position New Muffler', description: 'Compare new vs old. Slide new muffler in.', why: 'Confirm fit.', tip: 'Direct-fit should match exactly.', commonMistake: 'Wrong-fit muffler.' },
      { number: 6, title: 'Install New Clamps', description: 'Install new clamps at both ends. Snug only at first.', why: 'Loose fit allows alignment.', tip: 'Position before tightening.', commonMistake: 'Tight before alignment.' },
      { number: 7, title: 'Verify Alignment', description: 'Sight muffler position — should be parallel to ground, hangers engaged. Adjust as needed.', why: 'Proper hang prevents rattle.', tip: 'Hand-bend hangers if needed.', commonMistake: 'Misaligned muffler.' },
      { number: 8, title: 'Tighten Clamps', description: 'Torque clamps to spec (typically 20-25 ft-lbs).', why: 'Final secure.', tip: 'Cross-pattern.', commonMistake: 'Stripping clamp threads.' },
      { number: 9, title: 'Verify No Contact', description: 'Confirm muffler is not contacting any chassis or driveline component.', why: 'Contact causes vibration noise.', tip: 'Push muffler — should not touch anything.', commonMistake: 'Contact with chassis.' },
      { number: 10, title: 'Lower and Test', description: 'Lower vehicle. Start engine. Listen for any leaks. Drive and verify.', why: 'Confirms install.', tip: 'A soapy water spray on connections shows leaks.', commonMistake: 'Skipping leak check.' },
    ],
    torqueSpecifications: [{ fastener: 'Muffler clamps', specification: '25 ft-lbs (34 Nm).', notes: 'Cross-tighten.' }],
    fluidSpecifications: [],
    troubleshooting: [
      { problem: 'Exhaust leak at clamps', likelyCause: 'Loose or worn clamp', whatToCheck: 'Visual; soapy water', fix: 'Re-torque or new clamp.' },
      { problem: 'Rattle from muffler', likelyCause: 'Loose internal baffle or hanger contact', whatToCheck: 'Push and listen', fix: 'Re-position; replace muffler if internal damage.' },
    ],
    proTips: ['Replace clamps with muffler.', 'Magnaflow gives moderate growl; Flowmaster is louder.', 'Align before final tightening.'],
    commonMistakes: [{ mistake: 'Reusing old clamps', consequence: 'Loose connection; exhaust leak.' }],
    successIndicators: ['No exhaust leaks', 'No rattle', 'Quiet operation'],
    whenToSeekHelp: 'Exhaust shops install mufflers for reasonable cost if you do not want to lift the vehicle.',
    legalDisclaimer: 'Verify state noise regulations before installing aftermarket muffler.',
    manualSource: { title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)', url: 'https://www.haynes.com/en-us/wrangler/jeep/2003', notes: 'Cross-reference exhaust layout.' },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — EXHAUST HANGER / CLAMP SERVICE (P3 — concise)
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-exhaust-hanger-clamp-service',
    title: 'Exhaust Hanger and Clamp Replacement',
    vehicle: { make: 'Jeep', model: 'Wrangler', generations: [{ startYear: 1997, endYear: 2006 }] },
    video: {
      youtubeId: 'NXRTpQknSuI',
      title: 'Jeep TJ muffler replacement (hanger reference)',
      channel: 'Problem Solver Garage',
    },
    source: 'Generated by GarageIQ AI from publicly available sources. Cross-checked per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Engine',
    difficulty: 'beginner',
    duration: '30-60 minutes',
    interval: 'Replace when hangers crack/break or clamps rust through, causing exhaust to drag or hang loose.',
    description: 'TJ exhaust hangers are rubber isolators that hold the exhaust to the chassis. Common failure modes: rubber cracks/tears (drops the exhaust), or rust at the metal mount. Clamps similarly fail from rust. Quick, cheap fix.',
    overview: { difficulty: 'Beginner — direct R&R', estimatedTime: '30-60 minutes', estimatedCost: '$5-15 per hanger; $5-10 per clamp', whyMatters: 'Failed hanger lets exhaust drag; failed clamp lets exhaust separate.', safetyNote: 'Exhaust hot. Cool first.' },
    tools: ['Sockets', 'Penetrating oil', 'Pry bar (for removing rubber hangers)', 'Eye protection'],
    parts: [{ id: 'exhaust-hanger', name: 'Exhaust hanger (rubber isolator)', partNumber: 'Mopar 53008927 / generic 2.25" hanger', quantity: 2, price: 8, category: 'required', notes: 'Buy in pairs.' }, { id: 'exhaust-clamp-2', name: 'Exhaust clamps', partNumber: '2.25" clamps', quantity: 2, price: 6, category: 'required', notes: 'Replace any rusted clamp.' }],
    safetyInformation: 'Exhaust hot. Cool fully. Eye protection.',
    beforeYouStart: ['Identify failed hangers/clamps', 'Apply penetrating oil to bolts'],
    steps: [
      { number: 1, title: 'Lift Vehicle', description: 'Raise on stands for access.', why: 'Access.', tip: 'High enough for under-vehicle.', commonMistake: 'Inadequate clearance.' },
      { number: 2, title: 'Locate Failed Hanger or Clamp', description: 'Inspect each hanger and clamp. Identify cracked rubber, broken metal mount, or rusted clamps.', why: 'Confirms which to replace.', tip: 'Push exhaust — failed hanger lets it droop.', commonMistake: 'Replacing parts that are still good.' },
      { number: 3, title: 'Remove Old Hanger', description: 'Pry hanger off the chassis mount and exhaust pin.', why: 'Removal.', tip: 'A small pry bar makes it easier.', commonMistake: 'Forcing without pry bar.' },
      { number: 4, title: 'Inspect Mount Points', description: 'Check chassis mount and exhaust pin for damage. Repair if needed.', why: 'Mount points must be intact.', tip: 'Cracked mount may need welding.', commonMistake: 'Reinstalling on damaged mount.' },
      { number: 5, title: 'Install New Hanger', description: 'Slide new rubber isolator onto chassis pin and exhaust pin.', why: 'Replacement.', tip: 'A drop of soap eases stiff rubber over pin.', commonMistake: 'Forcing dry.' },
      { number: 6, title: 'Verify Alignment', description: 'Confirm exhaust hangs naturally, not stretched or compressed.', why: 'Proper hang.', tip: 'Push and pull on exhaust — should move slightly.', commonMistake: 'Too tight.' },
      { number: 7, title: 'Replace Clamps If Needed', description: 'Loosen old clamp, slide off, install new clamp at same position. Snug.', why: 'Replacement.', tip: 'Position for clearance.', commonMistake: 'Wrong position.' },
      { number: 8, title: 'Torque Clamps', description: 'Torque to spec (25 ft-lbs / 34 Nm).', why: 'Secure.', tip: 'Cross-tighten.', commonMistake: 'Over-torque.' },
      { number: 9, title: 'Verify No Contact', description: 'Push exhaust at various points — should not contact chassis or driveline.', why: 'Prevents rattle.', tip: 'Have helper engine run while you watch under.', commonMistake: 'Contact.' },
      { number: 10, title: 'Test Drive', description: 'Drive and verify no rattle, no exhaust drag.', why: 'Final check.', tip: 'Speed bumps test exhaust position.', commonMistake: 'Skipping test.' },
    ],
    torqueSpecifications: [{ fastener: 'Exhaust clamps', specification: '25 ft-lbs (34 Nm).', notes: 'Cross-tighten.' }],
    fluidSpecifications: [],
    troubleshooting: [
      { problem: 'Exhaust drags at speed bumps', likelyCause: 'Hanger too low or stretched', whatToCheck: 'Adjust position', fix: 'Reposition or replace.' },
      { problem: 'Exhaust rattles', likelyCause: 'Hanger missing or contact with chassis', whatToCheck: 'Visual', fix: 'Replace hanger or reposition.' },
    ],
    proTips: ['Replace all hangers at the same time on aging exhaust.', 'A drop of soap on hanger pin eases install.', 'Hangers fail in pairs — once one goes, others soon follow.'],
    commonMistakes: [{ mistake: 'Reusing rusted clamps', consequence: 'Loose connection.' }],
    successIndicators: ['Exhaust hangs evenly', 'No drag, no rattle, no leaks', 'Suspension does not cause exhaust contact'],
    whenToSeekHelp: 'Exhaust shop will replace hangers and clamps for nominal cost.',
    legalDisclaimer: 'Verify with licensed manual.',
    manualSource: { title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)', url: 'https://www.haynes.com/en-us/wrangler/jeep/2003', notes: 'Cross-reference exhaust hanging points.' },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — BLOWER MOTOR REPLACEMENT
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-blower-motor-replacement',
    title: 'Heater Blower Motor Replacement',
    vehicle: { make: 'Jeep', model: 'Wrangler', generations: [{ startYear: 1997, endYear: 2006 }] },
    video: {
      youtubeId: 'DT_-TxkrZxM',
      title: 'Jeep Wrangler TJ Replacing the Blower Motor',
      channel: 'GearTube TV',
    },
    source: 'Generated by GarageIQ AI from publicly available sources. Cross-checked per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Cooling & Heating',
    difficulty: 'beginner',
    duration: '30-60 minutes',
    interval: 'Replace when blower fails to operate at any speed, runs slow, or makes noise (squealing, grinding).',
    description: 'The TJ blower motor sits behind the glove box on the passenger side, accessed by removing the lower glove box trim. Replacement is straightforward — disconnect electrical, remove a few screws, swap motor. Most TJ blower motor failures are bearings (squealing) or motor itself (won\'t spin).',
    overview: { difficulty: 'Beginner — visible component, simple R&R', estimatedTime: '30-60 minutes', estimatedCost: '$30-60 for OEM-style motor', whyMatters: 'No blower = no heat or A/C airflow.', safetyNote: 'Disconnect battery before working on motor.' },
    tools: ['Phillips screwdriver', 'Sockets (10mm, 7mm)', 'Flashlight', 'Multimeter (test motor)', 'Eye protection'],
    parts: [{ id: 'blower-motor', name: 'Heater Blower Motor', partNumber: 'Mopar 4882721 — equivalents: VDO PM3905 / Four Seasons 35053', quantity: 1, price: 45, category: 'required', notes: 'Confirm correct part for application year.' }],
    safetyInformation: 'Disconnect battery negative cable before working on electrical components.',
    beforeYouStart: ['Disconnect battery', 'Confirm motor failure (no operation, slow, or noise)', 'Test fuse #11 (HVAC) before replacing motor — bad fuse mimics bad motor'],
    steps: [
      { number: 1, title: 'Disconnect Battery and Remove Lower Glove Box', description: 'Disconnect battery negative. Open glove box and remove the few Phillips screws holding the lower glove box trim. Lower trim drops down.', why: 'Access to motor.', tip: 'Photograph trim orientation.', commonMistake: 'Forcing trim.' },
      { number: 2, title: 'Locate Blower Motor Behind Glove Box', description: 'The motor is the cylindrical assembly with a fan inside, mounted to the HVAC plenum. You will see the motor body and electrical connector.', why: 'Visual identification.', tip: 'Flashlight helps in tight space.', commonMistake: 'Confusing other components.' },
      { number: 3, title: 'Disconnect Motor Electrical Connector', description: 'Press the lock tab and pull the connector straight back.', why: 'Frees motor electrically.', tip: 'Photograph the connector.', commonMistake: 'Pulling wires.' },
      { number: 4, title: 'Remove Motor Mounting Screws', description: 'Three screws (typically 7mm or Phillips) hold the motor to the plenum. Remove all three.', why: 'Frees motor for removal.', tip: 'Note screw locations.', commonMistake: 'Stripping screws.' },
      { number: 5, title: 'Pull Motor Out', description: 'Carefully pull motor straight out of the plenum housing. The fan cage is fragile — handle gently.', why: 'Removed.', tip: 'Some debris may have collected — clean before installing new.', commonMistake: 'Bending fan.' },
      { number: 6, title: 'Install New Motor', description: 'Position new motor in the same orientation. Push into plenum until seated.', why: 'Replacement.', tip: 'Match orientation to old.', commonMistake: 'Cocked install.' },
      { number: 7, title: 'Reinstall Mounting Screws', description: 'Hand-thread, then snug. Do not over-tighten.', why: 'Secure motor.', tip: 'Plastic plenum strips easily.', commonMistake: 'Over-torque.' },
      { number: 8, title: 'Reconnect Electrical', description: 'Reconnect connector until it clicks.', why: 'Restores power.', tip: 'Confirm click.', commonMistake: 'Partial connection.' },
      { number: 9, title: 'Reinstall Glove Box Trim', description: 'Reattach lower trim with screws.', why: 'Cosmetic restoration.', tip: 'Tighten gradually.', commonMistake: 'Trim not seated.' },
      { number: 10, title: 'Reconnect Battery and Test', description: 'Reconnect battery. Turn ignition to RUN, set HVAC to high blower. Should hear motor running. Test all speeds.', why: 'Verifies install.', tip: 'Test cool, warm, AC modes.', commonMistake: 'Not testing all speeds.' },
    ],
    torqueSpecifications: [{ fastener: 'Blower motor mounting screws', specification: 'Hand-snug, approximately 25 in-lbs.', notes: 'Plastic — do not over-tighten.' }],
    fluidSpecifications: [],
    troubleshooting: [
      { problem: 'New motor does not run', likelyCause: 'Connector not seated, blown fuse, or defective motor', whatToCheck: 'Reseat connector; check fuse; test motor with 12V', fix: 'Reconnect; replace fuse; warranty motor.' },
      { problem: 'Motor runs only on high speed', likelyCause: 'Blower resistor failed (separate component)', whatToCheck: 'Resistor is mounted nearby on the plenum', fix: 'Replace blower resistor.' },
      { problem: 'Motor noisy', likelyCause: 'Debris in fan or worn bearings (defective new unit)', whatToCheck: 'Inspect fan for clearance; warranty if defective', fix: 'Clean debris or warranty exchange.' },
    ],
    proTips: ['Test fuse first — saves replacing the motor unnecessarily.', 'Clean any debris from the plenum during install.', 'A new motor restores full speed range.'],
    commonMistakes: [{ mistake: 'Skipping fuse check', consequence: 'Replace motor when fuse was the issue.' }],
    successIndicators: ['Blower runs at all speeds', 'No noise', 'Air flow strong from vents'],
    whenToSeekHelp: 'If motor and resistor both replaced and blower still issues, suspect HVAC control module or wiring.',
    legalDisclaimer: 'Verify procedures with licensed manual.',
    manualSource: { title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)', url: 'https://www.haynes.com/en-us/wrangler/jeep/2003', notes: 'Cross-reference HVAC layout.' },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — HEATER CONTROL VALVE REPLACEMENT (P3)
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-heater-control-valve',
    title: 'Heater Control Valve Replacement',
    vehicle: { make: 'Jeep', model: 'Wrangler', generations: [{ startYear: 1997, endYear: 2006 }] },
    video: {
      youtubeId: 'vgAc_TQNSGM',
      title: 'Jeep Wrangler TJ - Heater Core (heater control valve service)',
      channel: 'FreeAutoMechanic',
    },
    source: 'Generated by GarageIQ AI from publicly available sources. Cross-checked per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Cooling & Heating',
    difficulty: 'beginner',
    duration: '45-60 minutes',
    interval: 'Replace when no heat from vents (despite engine warm), heat constantly on (cannot turn off), or coolant leaking from valve.',
    description: 'The heater control valve regulates coolant flow through the heater core. On the TJ, it is a vacuum-operated valve in the heater hose between the engine and heater core. Failure modes: stuck closed (no heat), stuck open (always hot), or leaking coolant. Located in the engine bay on the passenger side firewall.',
    overview: { difficulty: 'Beginner — direct hose replacement', estimatedTime: '45-60 minutes', estimatedCost: '$15-30 for valve', whyMatters: 'Failed valve means no heat or always-on heat, both annoying.', safetyNote: 'Coolant is hot when engine is warm. Cool engine before working.' },
    tools: ['Hose clamp pliers (or screwdriver)', 'Drain pan', 'Brake cleaner', 'Eye protection'],
    parts: [{ id: 'heater-control-valve', name: 'Heater Control Valve', partNumber: 'Mopar 5012585AA — equivalent: Four Seasons 74803', quantity: 1, price: 22, category: 'required', notes: 'Verify correct part for application.' }, { id: 'coolant-heater-valve', name: 'Engine Coolant (top-off)', partNumber: 'Mopar 5012394AB', quantity: 1, price: 12, category: 'recommended', notes: 'Some coolant lost during job.' }],
    safetyInformation: 'Coolant is hot. Allow 1+ hours cooling. Used coolant is toxic — pet hazard.',
    beforeYouStart: ['Allow engine to cool', 'Drain coolant level slightly to prevent overflow', 'Have new valve and clamps ready'],
    steps: [
      { number: 1, title: 'Locate Heater Control Valve', description: 'Open hood. The valve is on the passenger-side firewall, between the engine\'s heater hose outlet and the heater core inlet. Has a vacuum line attached.', why: 'Visual identification.', tip: 'Trace the heater hoses from the firewall.', commonMistake: 'Confusing other valves.' },
      { number: 2, title: 'Position Drain Pan', description: 'Place a drain pan under the valve location to catch coolant.', why: 'Coolant will dribble.', tip: 'A few cups will spill.', commonMistake: 'Skipping pan.' },
      { number: 3, title: 'Disconnect Vacuum Line', description: 'Pull vacuum line off the valve. Note its connection point on the manifold or vacuum tee.', why: 'Frees electrical/vacuum.', tip: 'Photograph for reassembly.', commonMistake: 'Tearing line.' },
      { number: 4, title: 'Disconnect Heater Hoses', description: 'Loosen hose clamps and pull both heater hoses off the valve. Coolant will drain.', why: 'Frees valve from hoses.', tip: 'Have rags ready.', commonMistake: 'Yanking hoses.' },
      { number: 5, title: 'Remove Old Valve', description: 'Slide valve out of its bracket if applicable.', why: 'Removed.', tip: 'Inspect old valve for failure mode.', commonMistake: 'Forgetting bracket.' },
      { number: 6, title: 'Install New Valve', description: 'Position new valve in same orientation. Insert into bracket.', why: 'Replacement.', tip: 'Note orientation arrows on valve.', commonMistake: 'Reversed installation.' },
      { number: 7, title: 'Reconnect Heater Hoses', description: 'Push both hoses onto valve fittings. Tighten clamps.', why: 'Restore coolant flow.', tip: 'Use new clamps if old are corroded.', commonMistake: 'Loose clamps.' },
      { number: 8, title: 'Reconnect Vacuum Line', description: 'Reattach vacuum line.', why: 'Restores valve actuation.', tip: 'Verify line is fully on.', commonMistake: 'Partial connection.' },
      { number: 9, title: 'Top Off Coolant', description: 'Add fresh coolant to overflow reservoir to FULL COLD line.', why: 'Replaces lost coolant.', tip: 'Bleed cooling system if low.', commonMistake: 'Skipping coolant top-off.' },
      { number: 10, title: 'Test Heat Operation', description: 'Start engine. Set HVAC to heat. After warm-up, verify hot air from vents. Switch to cold setting — verify air goes cold.', why: 'Confirms valve operation.', tip: 'Vacuum-operated valve responds within seconds.', commonMistake: 'Not testing both modes.' },
    ],
    torqueSpecifications: [],
    fluidSpecifications: [{ fluid: 'Coolant', type: 'HOAT (match existing)', capacity: '1 quart top-off', notes: 'Bleed system if needed.' }],
    troubleshooting: [
      { problem: 'No heat after install', likelyCause: 'Vacuum line not connected, or valve installed reversed', whatToCheck: 'Vacuum connection; valve orientation', fix: 'Reconnect; reorient valve.' },
      { problem: 'Coolant leak at valve', likelyCause: 'Hose clamp loose or fitting damage', whatToCheck: 'Visual inspection', fix: 'Re-tighten clamps; replace fitting if damaged.' },
    ],
    proTips: ['Replace clamps with valve.', 'Note valve orientation arrows.', 'Verify vacuum line is intact (not cracked).'],
    commonMistakes: [{ mistake: 'Reversed installation', consequence: 'Heat does not work properly.' }],
    successIndicators: ['Hot air at heat setting', 'Cool air at cold setting', 'No coolant leak'],
    whenToSeekHelp: 'If valve replaced and heat still issues, suspect heater core blockage or HVAC blend door.',
    legalDisclaimer: 'Coolant is toxic; clean spills.',
    manualSource: { title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)', url: 'https://www.haynes.com/en-us/wrangler/jeep/2003', notes: 'Cross-reference HVAC vacuum diagram.' },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — A/C SYSTEM RECHARGE (P3)
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-ac-system-recharge',
    title: 'A/C System Recharge (R-134a)',
    vehicle: { make: 'Jeep', model: 'Wrangler', generations: [{ startYear: 1997, endYear: 2006 }] },
    video: {
      youtubeId: 'OW9iym3JZeQ',
      title: 'How to AC PRO a/c recharge DIY 2004 JEEP WRANGLER',
      channel: 'Craft Customs',
    },
    source: 'Generated by GarageIQ AI from publicly available sources. Cross-checked per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Cooling & Heating',
    difficulty: 'beginner',
    duration: '30 minutes',
    interval: 'Recharge when A/C blows warm air, refrigerant level is low. Note: a leaking system needs leak repair before recharge — recharge alone is a temporary fix.',
    description: 'The TJ A/C system uses R-134a refrigerant. Recharging is straightforward with a recharge kit from any parts store. However, recharging masks underlying leaks — if the system needs frequent recharging, find and fix the leak.',
    overview: { difficulty: 'Beginner — recharge kit with built-in gauge', estimatedTime: '30 minutes', estimatedCost: '$30-50 for recharge kit (refrigerant + dye + gauge)', whyMatters: 'Restores A/C cooling for low-refrigerant condition.', safetyNote: 'Refrigerant is pressurized. Eye protection mandatory. Skin frostbite risk if direct contact. Refrigerant venting to atmosphere is illegal — recharge only with proper recovery equipment if removing.' },
    tools: ['Recharge kit (with gauge and refrigerant)', 'Eye protection', 'Gloves'],
    parts: [{ id: 'ac-recharge-kit', name: 'A/C Recharge Kit (R-134a)', partNumber: 'AC Pro / Quest / Idemitsu kits', quantity: 1, price: 35, category: 'required', notes: 'Buy a kit with gauge and dye for first-time recharge.' }],
    safetyInformation: 'Refrigerant is pressurized and very cold (-15°F). Skin contact causes frostbite. Eye protection mandatory.',
    beforeYouStart: ['Confirm A/C compressor is engaging (or low-pressure switch will not allow charge)', 'Have eye protection and gloves on'],
    steps: [
      { number: 1, title: 'Locate Low-Pressure Service Port', description: 'On TJ, the low-side service port is on the larger refrigerant line near the firewall (typically passenger side). Has a blue or black plastic cap. R-134a fittings are unique to that refrigerant — kits will only fit the correct port.', why: 'Recharge port identification.', tip: 'Low side = larger diameter line.', commonMistake: 'Charging into high side. Damages can.' },
      { number: 2, title: 'Connect Recharge Kit', description: 'Remove cap from low-pressure port. Connect kit hose. Listen for click — it is a quick-connect.', why: 'Sealed connection.', tip: 'Kit fits only one way (R-134a coupler).', commonMistake: 'Forcing wrong fitting.' },
      { number: 3, title: 'Start Engine and Turn A/C On Maximum', description: 'Start engine. Set HVAC to A/C, max cold, max blower.', why: 'Compressor running pulls refrigerant in.', tip: 'Verify compressor clutch is engaging — listen for click.', commonMistake: 'Not running A/C during recharge.' },
      { number: 4, title: 'Read Initial Pressure', description: 'Look at gauge on recharge kit. Note pressure. Below the green/cool zone = low refrigerant. Above the green = overcharged or other issue.', why: 'Initial pressure tells current state.', tip: 'Pressure depends on ambient temp — kit gauge accounts for this.', commonMistake: 'Skipping reading.' },
      { number: 5, title: 'Add Refrigerant Slowly', description: 'Hold can upright. Squeeze trigger to add small bursts. Watch the gauge climb. Stop when needle is in the green/cool zone.', why: 'Slow charging prevents overcharge.', tip: 'Shake can periodically per kit instructions.', commonMistake: 'Adding too fast.' },
      { number: 6, title: 'Verify Cool Air at Vents', description: 'After charge, feel air from vents. Should be noticeably colder. A thermometer should read 35-50°F at the center vent on a hot day.', why: 'Confirms A/C function.', tip: 'Allow 5-10 minutes for full cooling effect.', commonMistake: 'Stopping before air is cool.' },
      { number: 7, title: 'Watch for Pressure Stabilization', description: 'Pressure should stabilize in green/cool zone with engine running and A/C on. Continued climbing = overcharge.', why: 'Stable in green = correct charge.', tip: 'Some bouncing is normal as compressor cycles.', commonMistake: 'Adding past stabilization.' },
      { number: 8, title: 'Disconnect Recharge Kit', description: 'Stop adding. Disconnect kit hose from service port. Replace cap.', why: 'Job complete.', tip: 'A small puff of refrigerant escapes — eye protection on.', commonMistake: 'Skipping cap reinstall.' },
      { number: 9, title: 'Test A/C Performance', description: 'Drive 15-20 minutes. Verify A/C cools cabin to comfortable temperature on a hot day.', why: 'Real-world test.', tip: 'On 90°F day, vents should blow ~45°F.', commonMistake: 'Calling fixed without test.' },
      { number: 10, title: 'Monitor for Re-Loss', description: 'Mark service date. If A/C goes warm again in weeks/months, system has a leak — needs leak diagnosis at a shop.', why: 'Leak detection.', tip: 'A UV dye kit reveals leaks.', commonMistake: 'Repeating recharge without fixing leak — illegal in some states.' },
    ],
    torqueSpecifications: [],
    fluidSpecifications: [{ fluid: 'R-134a refrigerant', type: 'R-134a only (DO NOT mix with R-12 or R-1234yf)', capacity: 'TJ system holds approximately 22 oz total. Recharge usually uses 4-12 oz.', notes: 'OEM amount on the underhood label.' }],
    troubleshooting: [
      { problem: 'Pressure climbs too fast', likelyCause: 'Compressor not running or system overcharged', whatToCheck: 'Verify compressor clutch engaging', fix: 'Stop charging; address compressor or system issue.' },
      { problem: 'A/C still warm after charge', likelyCause: 'Compressor failure, blocked condenser, or wrong-side charge', whatToCheck: 'Compressor function; condenser airflow', fix: 'Diagnose at shop; this guide covers recharge only.' },
      { problem: 'Compressor cycles on/off rapidly', likelyCause: 'Low refrigerant or low-pressure switch issue', whatToCheck: 'Add small amount more', fix: 'Recharge slightly; if cycling persists, shop diagnosis.' },
    ],
    proTips: ['Recharge with UV dye to spot future leaks.', 'A/C system that loses charge in weeks has a real leak — fix it.', 'Federal law requires recovery equipment for refrigerant removal.', 'Cabin air filter cleanliness affects A/C performance.'],
    commonMistakes: [{ mistake: 'Charging into high-pressure port', consequence: 'Damages can and possibly system.' }, { mistake: 'Charging without verifying compressor running', consequence: 'Refrigerant accumulates as gas, no actual flow.' }],
    successIndicators: ['Vent temp 35-50°F on hot day', 'Compressor cycles normally (not constantly)', 'Pressure in green zone'],
    whenToSeekHelp: 'A/C systems with significant leak require professional repair — leak detection, vacuum, and recharge equipment.',
    legalDisclaimer: 'Refrigerant venting is environmentally harmful and illegal in many jurisdictions. Use only for proper recharge.',
    manualSource: { title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)', url: 'https://www.haynes.com/en-us/wrangler/jeep/2003', notes: 'Cross-reference A/C system specifications.' },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — A/C COMPRESSOR REPLACEMENT (P3)
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-ac-compressor-replacement',
    title: 'A/C Compressor Replacement',
    vehicle: { make: 'Jeep', model: 'Wrangler', generations: [{ startYear: 1997, endYear: 2006 }] },
    video: {
      youtubeId: '_RUzhXcdoRg',
      title: 'Jeep Wrangler TJ AC compressor replacement and AC System recharge',
      channel: 'FreeAutoMechanic',
    },
    source: 'Generated by GarageIQ AI from publicly available sources. Cross-checked per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Cooling & Heating',
    difficulty: 'advanced',
    duration: '3-4 hours',
    interval: 'Replace when compressor seized, clutch fails, or compressor produces metal contamination in system. After compressor failure, system requires flush and complete recharge — this is a major repair.',
    description: 'Compressor replacement requires the A/C system to be evacuated of refrigerant by a shop with recovery equipment, then the compressor is replaced and system is flushed, evacuated, and recharged. While the DIY part is straightforward, the system service before and after requires shop equipment. Plan to take vehicle to A/C shop for evacuation and recharge.',
    overview: { difficulty: 'Advanced — requires shop service for refrigerant recovery and recharge', estimatedTime: '3-4 hours DIY + shop time', estimatedCost: '$200-400 for compressor + shop service ($100-200)', whyMatters: 'Failed compressor = no A/C. Replacement restores cooling.', safetyNote: 'Refrigerant must be recovered legally before opening system. Wear eye protection during install.' },
    tools: ['Wrenches (12-19mm)', 'Sockets', 'Refrigerant oil (PAG 46 or appropriate for compressor)', 'A/C drier (always replace with compressor)', 'Safety glasses and gloves'],
    parts: [{ id: 'ac-compressor', name: 'A/C Compressor', partNumber: 'Mopar 55037617AC — equivalents: Four Seasons 68252 / Denso 471-0226', quantity: 1, price: 280, category: 'required', notes: 'Confirm correct part for application.' }, { id: 'ac-drier', name: 'A/C Drier (Receiver-Drier)', partNumber: 'Four Seasons 33094', quantity: 1, price: 25, category: 'required', notes: 'Always replace drier with compressor.' }, { id: 'ac-refrigerant-oil', name: 'PAG Refrigerant Oil (PAG 46)', partNumber: 'Generic PAG 46', quantity: 1, price: 8, category: 'required', notes: 'New compressor requires fresh oil.' }, { id: 'r134a-recharge', name: 'R-134a refrigerant for recharge', partNumber: 'R-134a', quantity: 1, price: 30, category: 'required', notes: 'Shop will charge with proper amount.' }],
    safetyInformation: 'Refrigerant must be recovered before disassembly — illegal to vent to atmosphere. Compressor heavy. Oil contamination requires system flush at shop.',
    beforeYouStart: ['Have shop evacuate refrigerant before starting', 'Stage all parts: compressor, drier, oil', 'Plan for vehicle to be at shop for recharge after repair'],
    steps: [
      { number: 1, title: 'Verify Refrigerant Has Been Recovered', description: 'Confirm with shop that refrigerant has been evacuated. System should be at zero pressure.', why: 'Required before opening lines.', tip: 'Zero pressure on gauges.', commonMistake: 'Opening system with pressure.' },
      { number: 2, title: 'Remove Serpentine Belt', description: 'Loosen belt tensioner; slide belt off compressor pulley.', why: 'Frees compressor.', tip: 'Mark belt routing first.', commonMistake: 'Forgetting belt routing.' },
      { number: 3, title: 'Disconnect Compressor Electrical', description: 'Unplug clutch wiring connector at compressor.', why: 'Frees electrical.', tip: 'Press lock tab.', commonMistake: 'Yanking wire.' },
      { number: 4, title: 'Disconnect Refrigerant Lines', description: 'Two refrigerant lines (high and low pressure) connect to compressor. Loosen line bolts and pull lines off. Cap immediately.', why: 'Frees lines.', tip: 'Use line wrenches.', commonMistake: 'Cross-threading.' },
      { number: 5, title: 'Unbolt Compressor from Bracket', description: 'Three or four bolts hold compressor to bracket. Remove. Lift compressor off.', why: 'Removed.', tip: 'Heavy — 20+ lbs.', commonMistake: 'Dropping.' },
      { number: 6, title: 'Add Oil to New Compressor', description: 'New compressors come dry. Add 2-4 oz of PAG 46 oil through the suction port (low side). Rotate compressor by hand 10-15 times to distribute oil internally.', why: 'Compressor needs oil for first-start lubrication.', tip: 'Specific oil amount per compressor instructions.', commonMistake: 'Skipping oil. Compressor seizes on first start.' },
      { number: 7, title: 'Install New Compressor', description: 'Position new compressor on bracket. Hand-thread bolts. Torque to spec.', why: 'Replacement secure.', tip: 'Match orientation.', commonMistake: 'Cross-threading bolts.' },
      { number: 8, title: 'Replace A/C Drier', description: 'Replace the receiver-drier (separate component, also in the system) — opens system to moisture, requires drier replacement.', why: 'Drier removes moisture; opening system saturates it.', tip: 'Drier is mandatory replacement after any system opening.', commonMistake: 'Skipping drier.' },
      { number: 9, title: 'Reconnect Lines and Clutch Wiring', description: 'Reconnect refrigerant lines (with new O-rings if applicable). Reconnect clutch wiring.', why: 'Restore connections.', tip: 'Use O-ring lubricant.', commonMistake: 'Damaging O-rings.' },
      { number: 10, title: 'Take to Shop for Vacuum and Recharge', description: 'Vehicle must be vacuumed (pulled below 30 inHg for 30 min to remove moisture) and then recharged with R-134a per OEM specification. Shop will perform.', why: 'System cannot operate without proper vacuum and charge.', tip: 'Confirm shop will work on customer-installed compressor.', commonMistake: 'Trying to charge without vacuum.' },
    ],
    torqueSpecifications: [{ fastener: 'A/C compressor mounting bolts', specification: '40 ft-lbs (54 Nm). Verify torque spec from OEM service manual.', notes: 'Cross-tighten.' }, { fastener: 'Refrigerant line bolts', specification: '15 ft-lbs (20 Nm).', notes: 'Use new O-rings.' }],
    fluidSpecifications: [{ fluid: 'Refrigerant oil', type: 'PAG 46', capacity: '4-6 oz total system; 2-4 oz to new compressor', notes: 'Shop will adjust during recharge.' }, { fluid: 'R-134a refrigerant', type: 'R-134a only', capacity: '22 oz (per underhood label)', notes: 'Shop charges to spec.' }],
    troubleshooting: [
      { problem: 'A/C does not cool after install', likelyCause: 'System not vacuumed; refrigerant low; or new compressor defective', whatToCheck: 'Confirm vacuum and recharge done; verify compressor running', fix: 'Have shop verify charge.' },
      { problem: 'Compressor noise or seizure', likelyCause: 'Insufficient oil; debris in system from old compressor failure', whatToCheck: 'Inspect system flush', fix: 'System flush and re-install.' },
    ],
    proTips: ['Always replace drier with compressor.', 'Add oil to new compressor before install.', 'Have shop do flush if previous compressor seized.'],
    commonMistakes: [{ mistake: 'Skipping drier replacement', consequence: 'Moisture damage; new compressor fails.' }, { mistake: 'Skipping oil in new compressor', consequence: 'Seizure on first start.' }],
    successIndicators: ['A/C cools to 35-50°F at vent', 'No noise from compressor', 'No leaks at line connections'],
    whenToSeekHelp: 'A/C work requires shop equipment for evacuation, vacuum, recharge, and flush. DIY removal/install with shop service is feasible; full DIY without shop equipment is not.',
    legalDisclaimer: 'Refrigerant work involves federal regulations. Recovery and recharge must be done with proper equipment. Refrigerant venting is illegal.',
    manualSource: { title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)', url: 'https://www.haynes.com/en-us/wrangler/jeep/2003', notes: 'Cross-reference A/C system specs.' },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — HEATER CORE FLUSH (P3)
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-heater-core-flush',
    title: 'Heater Core Flush (Restore Heat without Replacement)',
    vehicle: { make: 'Jeep', model: 'Wrangler', generations: [{ startYear: 1997, endYear: 2006 }] },
    video: {
      youtubeId: 'vgAc_TQNSGM',
      title: 'Jeep Wrangler TJ - Heater Core service reference',
      channel: 'FreeAutoMechanic',
    },
    source: 'Generated by GarageIQ AI from publicly available sources. Cross-checked per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Cooling & Heating',
    difficulty: 'beginner',
    duration: '30-45 minutes',
    interval: 'When heat is weak (despite engine warm and thermostat working) due to clogged heater core. Try flush before replacement.',
    description: 'Over years, heater cores accumulate scale and rust internally, restricting flow. A flush — disconnecting the heater hoses and running clean water through the core — often restores flow without replacement (a much bigger job). Cheap and easy first step.',
    overview: { difficulty: 'Beginner — disconnect hoses, flush with water', estimatedTime: '30-45 minutes', estimatedCost: '$0-15 (water + new clamps)', whyMatters: 'Saves expensive heater core replacement (4-8 hours, dash removal).', safetyNote: 'Coolant hot. Cool engine before working.' },
    tools: ['Hose clamp pliers', 'Drain pan', 'Garden hose with adapter', 'Eye protection', 'Coolant for top-off'],
    parts: [{ id: 'heater-core-flush-kit', name: 'Heater Core Flush Solution (optional)', partNumber: 'Prestone Super Flush AS-105 or vinegar', quantity: 1, price: 8, category: 'optional', notes: 'Some flushes use cleaning solution; plain water is often sufficient.' }, { id: 'coolant-flush', name: 'Coolant for Refill', partNumber: 'Mopar 5012394AB', quantity: 1, price: 12, category: 'required', notes: 'Some coolant lost; refill needed.' }],
    safetyInformation: 'Coolant is hot. Cool engine for 1+ hour. Coolant is toxic.',
    beforeYouStart: ['Allow engine to cool', 'Have garden hose available', 'Have new clamps if old are damaged'],
    steps: [
      { number: 1, title: 'Locate Heater Hoses at Firewall', description: 'Engine bay, passenger side firewall — two heater hoses go through to the heater core.', why: 'Visual identification.', tip: 'One inlet, one outlet.', commonMistake: 'Confusing other hoses.' },
      { number: 2, title: 'Position Drain Pan', description: 'Place a drain pan under the firewall area.', why: 'Catches coolant.', tip: 'A few cups will spill.', commonMistake: 'Skipping pan.' },
      { number: 3, title: 'Disconnect Both Heater Hoses', description: 'Loosen clamps on both hoses, disconnect from heater core nipples on the firewall.', why: 'Frees core for flushing.', tip: 'Coolant will dribble.', commonMistake: 'Damaging hoses.' },
      { number: 4, title: 'Connect Garden Hose to Heater Core', description: 'Use a hose adapter to connect garden hose to one heater core nipple. Direct other nipple to drain pan.', why: 'Water flows through core.', tip: 'Plumber\'s tape on the connection prevents leaks.', commonMistake: 'Loose adapter.' },
      { number: 5, title: 'Run Water Through Core', description: 'Turn on water at low flow. Initial flow will be brown/dirty. Continue until water runs clear (5-10 minutes typically).', why: 'Removes scale and debris.', tip: 'Reverse direction halfway through for thorough flush.', commonMistake: 'Stopping too early.' },
      { number: 6, title: 'Apply Cleaning Solution (Optional)', description: 'For heavy buildup, follow flush solution instructions: introduce solution, let sit 5-15 minutes, flush thoroughly.', why: 'Stronger cleaning if water alone insufficient.', tip: 'Vinegar diluted 1:4 is a budget alternative.', commonMistake: 'Not flushing solution out completely.' },
      { number: 7, title: 'Reverse Flow and Flush Again', description: 'Switch hose to other nipple. Flush in opposite direction.', why: 'Reaches different parts of core.', tip: 'Reverse flow dislodges different debris.', commonMistake: 'Single-direction flush only.' },
      { number: 8, title: 'Drain and Reconnect Hoses', description: 'Disconnect garden hose. Drain remaining water. Reconnect heater hoses with old or new clamps.', why: 'Restores system.', tip: 'New clamps are cheap insurance.', commonMistake: 'Reusing damaged clamps.' },
      { number: 9, title: 'Top Off Coolant', description: 'Add fresh coolant to overflow reservoir to FULL COLD line.', why: 'Replaces water-flushed amount.', tip: 'Bleed system if needed.', commonMistake: 'Insufficient coolant.' },
      { number: 10, title: 'Test Heat Operation', description: 'Start engine. After warm-up, verify heat from vents.', why: 'Confirms flow restoration.', tip: 'Heat should be noticeably better than before.', commonMistake: 'Skipping test.' },
    ],
    torqueSpecifications: [],
    fluidSpecifications: [{ fluid: 'Coolant top-off', type: 'HOAT (match existing)', capacity: '1-2 quarts after flush', notes: 'Bleed system.' }],
    troubleshooting: [
      { problem: 'Heat still weak after flush', likelyCause: 'Severe blockage that flush did not clear, or heater core leak', whatToCheck: 'Pressure test', fix: 'Heater core replacement may be needed.' },
      { problem: 'Leak at hose connection', likelyCause: 'Loose clamp', whatToCheck: 'Visual', fix: 'Tighten or replace.' },
    ],
    proTips: ['Try flush before replacing heater core.', 'Vinegar dilution is budget alternative to commercial flush.', 'Reverse flow flush is more effective.', 'Ensure cooling system is properly bled after job.'],
    commonMistakes: [{ mistake: 'Stopping flush too early', consequence: 'Partial flow restoration; heat remains weak.' }],
    successIndicators: ['Strong heat from vents', 'Water flushed runs clear', 'No coolant leaks'],
    whenToSeekHelp: 'If heat remains weak after flush, heater core replacement is the next step (involved job — see heater core replacement guide).',
    legalDisclaimer: 'Coolant is toxic.',
    manualSource: { title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)', url: 'https://www.haynes.com/en-us/wrangler/jeep/2003', notes: 'Cross-reference cooling system bleeding procedure.' },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — CABIN BLEND DOOR / ACTUATOR (P3)
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-cabin-blend-door-actuator',
    title: 'Cabin Blend Door Actuator Replacement',
    vehicle: { make: 'Jeep', model: 'Wrangler', generations: [{ startYear: 1997, endYear: 2006 }] },
    source: 'Generated by GarageIQ AI from publicly available sources. Cross-checked per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Cooling & Heating',
    difficulty: 'intermediate',
    duration: '1-2 hours',
    interval: 'Replace when air is hot or cold but cannot mix to comfortable temperature, or when blend door knob spins freely without changing air temp.',
    description: 'The blend door controls how much hot vs cold air goes to the vents. The actuator is a small motor that moves the door per cabin temperature setting. Failure modes: actuator motor dies, gears strip, or door binding. Located behind the dashboard.',
    overview: { difficulty: 'Intermediate — partial dash removal needed', estimatedTime: '1-2 hours', estimatedCost: '$30-60 for actuator', whyMatters: 'Without actuator, A/C and heat blend cannot be controlled.', safetyNote: 'Disconnect battery before working on dash.' },
    tools: ['Phillips screwdriver', 'Sockets (7-10mm)', 'Trim removal tool', 'Flashlight', 'Eye protection'],
    parts: [{ id: 'blend-door-actuator', name: 'Blend Door Actuator', partNumber: 'Mopar 5083044AA — equivalent: Standard EW0001', quantity: 1, price: 45, category: 'required', notes: 'Confirm correct part for application.' }],
    safetyInformation: 'Disconnect battery negative cable. Working under dash is awkward.',
    beforeYouStart: ['Disconnect battery', 'Confirm actuator failure (knob spins free, no temp change)', 'Have flashlight'],
    steps: [
      { number: 1, title: 'Disconnect Battery and Remove Lower Dash Trim', description: 'Disconnect battery negative. Remove lower dash trim panel under steering wheel and passenger side. Phillips screws and clips.', why: 'Access to actuator.', tip: 'Photograph trim before removal.', commonMistake: 'Forcing clips.' },
      { number: 2, title: 'Locate Blend Door Actuator', description: 'Look for a small electric motor mounted to the HVAC plenum, typically on the driver or passenger side under the dash.', why: 'Visual ID.', tip: 'Has 3-4 wire connector.', commonMistake: 'Confusing other actuators.' },
      { number: 3, title: 'Disconnect Actuator Connector', description: 'Press lock tab and unplug.', why: 'Frees electrical.', tip: 'Photograph orientation.', commonMistake: 'Pulling wire.' },
      { number: 4, title: 'Remove Mounting Screws', description: 'Two-three screws hold actuator to plenum.', why: 'Frees actuator.', tip: 'Note screw locations.', commonMistake: 'Stripping plastic.' },
      { number: 5, title: 'Pull Actuator Out', description: 'Pull straight off — actuator engages a small shaft on the blend door.', why: 'Removed.', tip: 'Note shaft alignment.', commonMistake: 'Forcing.' },
      { number: 6, title: 'Manually Verify Door Movement', description: 'Move the blend door shaft manually with finger. Should rotate smoothly. If binding, the door itself is the issue (much bigger job).', why: 'Confirms actuator vs door issue.', tip: 'Manual rotation should be 90 degrees of arc.', commonMistake: 'Skipping this check.' },
      { number: 7, title: 'Install New Actuator', description: 'Align new actuator with shaft, slide on. Ensure tab/key engagement.', why: 'Replacement.', tip: 'Cycle actuator a few times by hand.', commonMistake: 'Misalignment.' },
      { number: 8, title: 'Reinstall Mounting Screws', description: 'Hand-thread, snug only.', why: 'Secure.', tip: 'Plastic plenum strips easily.', commonMistake: 'Over-tightening.' },
      { number: 9, title: 'Reconnect Electrical', description: 'Plug in connector.', why: 'Restores function.', tip: 'Confirm click.', commonMistake: 'Partial connection.' },
      { number: 10, title: 'Reconnect Battery and Test', description: 'Reconnect battery. Cycle through full hot/cold range on HVAC controls. Verify air temp changes.', why: 'Confirms operation.', tip: 'Listen for actuator motor running.', commonMistake: 'Not testing full range.' },
    ],
    torqueSpecifications: [{ fastener: 'Actuator mounting screws', specification: 'Hand-snug, 25 in-lbs.', notes: 'Plastic.' }],
    fluidSpecifications: [],
    troubleshooting: [
      { problem: 'No air temp change after replacement', likelyCause: 'Door binding (different issue) or wiring issue', whatToCheck: 'Manually rotate door; verify wiring', fix: 'Repair door if binding; trace wiring.' },
      { problem: 'Clicking sound from actuator', likelyCause: 'Internal gear slipping', whatToCheck: 'Replace actuator (defective new unit)', fix: 'Warranty exchange.' },
    ],
    proTips: ['Actuator kits often include the gear sets that wear out.', 'Manually verify door movement before assuming actuator is the issue.', 'Photograph everything for reassembly.'],
    commonMistakes: [{ mistake: 'Skipping manual door check', consequence: 'Replace actuator when door is the issue.' }],
    successIndicators: ['Air temp adjusts across full range', 'No clicking from actuator', 'Cabin temp control responsive'],
    whenToSeekHelp: 'If door is binding mechanically, the dashboard may need partial disassembly — bigger job, may warrant shop service.',
    legalDisclaimer: 'Verify with licensed manual.',
    manualSource: { title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)', url: 'https://www.haynes.com/en-us/wrangler/jeep/2003', notes: 'Cross-reference HVAC system layout.' },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — TAIL LIGHT BULB REPLACEMENT
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-tail-light-bulb-replacement',
    title: 'Tail Light Bulb Replacement',
    vehicle: { make: 'Jeep', model: 'Wrangler', generations: [{ startYear: 1997, endYear: 2006 }] },
    video: {
      youtubeId: 'fHQJsMS4bus',
      title: 'Jeep TJ Tail Light Bulb Replacement & Housing Removal',
      channel: 'KyleSVT',
    },
    source: 'Generated by GarageIQ AI from publicly available sources. Cross-checked per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Electrical',
    difficulty: 'beginner',
    duration: '15 minutes',
    interval: 'Replace when bulb burns out (visible from outside vehicle), brake light not working, or warning lamp on dash indicates bulb out.',
    description: 'TJ tail lights are accessed from outside the vehicle through the lens, then bulb sockets twist out from inside. Standard 1157 (brake/tail combo) and 1156 (turn signal/reverse) bulbs.',
    overview: { difficulty: 'Beginner — quick R&R', estimatedTime: '15 minutes', estimatedCost: '$3-5 per bulb', whyMatters: 'Burned out tail light is illegal in most states.', safetyNote: 'Hot bulb after recent use — let cool.' },
    tools: ['Phillips screwdriver', 'Eye protection'],
    parts: [{ id: 'tail-light-bulb-1157', name: 'Tail/Brake Light Bulb (1157)', partNumber: 'Sylvania 1157 / equivalent', quantity: 2, price: 4, category: 'required', notes: 'Replace in pairs.' }, { id: 'tail-light-bulb-1156', name: 'Turn/Reverse Light Bulb (1156)', partNumber: 'Sylvania 1156 / equivalent', quantity: 2, price: 4, category: 'optional', notes: 'Single-filament for turn/reverse.' }],
    safetyInformation: 'Bulb may be hot. Wear gloves to avoid skin oil contamination of new bulb.',
    beforeYouStart: ['Identify burned-out bulb', 'Have replacement ready'],
    steps: [
      { number: 1, title: 'Open Tailgate or Liftgate', description: 'Tail lights are accessible from inside vehicle through the rear cargo area or directly from outside.', why: 'Access.', tip: 'Some TJs have lens that comes off; others access from inside.', commonMistake: 'Wrong access path.' },
      { number: 2, title: 'Remove Tail Light Lens or Access Cover', description: 'Two Phillips screws hold the lens to the body. Remove and pull lens off carefully.', why: 'Access to bulbs.', tip: 'Lens may stick — pry gently.', commonMistake: 'Cracking lens.' },
      { number: 3, title: 'Twist Bulb Socket Out', description: 'Each bulb socket has a 1/4 turn lock. Press and rotate counterclockwise.', why: 'Removes bulb assembly.', tip: 'Bulb stays in socket.', commonMistake: 'Yanking.' },
      { number: 4, title: 'Pull Bulb from Socket', description: '1157 bulbs push and twist; 1156 bulbs pull straight out.', why: 'Removes bulb.', tip: 'Use cloth to handle.', commonMistake: 'Skin oil contamination.' },
      { number: 5, title: 'Inspect Socket Contacts', description: 'Look for corrosion or burn marks. Wipe with brake cleaner if dirty.', why: 'Clean contacts ensure good connection.', tip: 'Dielectric grease helps.', commonMistake: 'Skipping inspection.' },
      { number: 6, title: 'Insert New Bulb', description: 'Push and twist 1157 into socket; push 1156 straight in.', why: 'New bulb installation.', tip: 'Confirm seat.', commonMistake: 'Cocked bulb.' },
      { number: 7, title: 'Reinsert Socket', description: 'Insert socket into housing, twist 1/4 turn clockwise to lock.', why: 'Secures socket.', tip: 'Confirm lock.', commonMistake: 'Loose socket.' },
      { number: 8, title: 'Test Before Reassembling Lens', description: 'Turn on lights — verify all functions: tail, brake, turn, reverse.', why: 'Catches install errors.', tip: 'Have helper press brake / turn signal.', commonMistake: 'Reassembling before test.' },
      { number: 9, title: 'Reinstall Lens', description: 'Position lens on body, install screws.', why: 'Restoration.', tip: 'Hand-tight.', commonMistake: 'Over-torquing screws.' },
      { number: 10, title: 'Final Verification', description: 'Walk around vehicle and verify all lights work from outside.', why: 'Final check.', tip: 'Have helper.', commonMistake: 'Skipping.' },
    ],
    torqueSpecifications: [],
    fluidSpecifications: [],
    troubleshooting: [
      { problem: 'New bulb does not light', likelyCause: 'Bulb defective, fuse blown, or socket corrosion', whatToCheck: 'Test bulb in another socket; check fuse', fix: 'Replace defective bulb or fuse.' },
      { problem: 'Bulb burns out quickly', likelyCause: 'Loose socket connection or moisture in housing', whatToCheck: 'Tighten socket; check for moisture', fix: 'Reseat or seal housing.' },
    ],
    proTips: ['Replace in pairs for matching brightness.', 'Dielectric grease prevents corrosion.', 'LED bulbs are an option but may need load resistor for proper flash rate.'],
    commonMistakes: [{ mistake: 'Touching bulb glass', consequence: 'Skin oil shortens life.' }],
    successIndicators: ['All tail/brake/turn/reverse functions work', 'Even brightness left to right'],
    whenToSeekHelp: 'If circuit dead despite new bulb and good fuse, suspect wiring fault.',
    legalDisclaimer: 'Verify with licensed manual.',
    manualSource: { title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)', url: 'https://www.haynes.com/en-us/wrangler/jeep/2003', notes: 'Cross-reference electrical layout.' },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — IGNITION COIL REPLACEMENT (2.4L I4)
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-ignition-coil-replacement-2pt4l-i4',
    title: 'Ignition Coil Replacement (2.4L I4)',
    vehicle: { make: 'Jeep', model: 'Wrangler', generations: [{ startYear: 2003, endYear: 2006 }], engine: '2.4l-i4' },
    video: {
      youtubeId: 'yCW4jPLIN-s',
      title: 'How to change your ignition coil and spark plug wires (Jeep Wrangler 2.4)',
      channel: 'Jeep 2.4',
    },
    source: 'Generated by GarageIQ AI from publicly available sources. Cross-checked per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Engine',
    difficulty: 'beginner',
    duration: '30 minutes',
    interval: 'Replace when one or more cylinders misfire and the failure is traced to the coil pack.',
    description: 'The 2.4L uses a coil-pack ignition (no distributor) — typically a single coil pack with multiple outputs feeding spark plug wires to each cylinder. Coil failure causes specific cylinder misfire that follows the wire to the affected cylinder.',
    overview: { difficulty: 'Beginner — direct R&R', estimatedTime: '30 minutes', estimatedCost: '$60-100 for OEM-style coil pack', whyMatters: 'Failed coil = misfire, rough running, check engine light.', safetyNote: 'Disconnect battery before work.' },
    tools: ['Sockets (8mm, 10mm)', 'Multimeter', 'Eye protection'],
    parts: [{ id: 'ignition-coil-2pt4l', name: 'Ignition Coil Pack', partNumber: 'Mopar 4609080AC — equivalents: Standard UF270 / Bosch 0221503406', quantity: 1, price: 75, category: 'required', notes: 'Confirm correct part for application.' }],
    safetyInformation: 'High voltage on ignition. Disconnect battery. No work near sparks.',
    beforeYouStart: ['Disconnect battery', 'Confirm coil failure (test resistance, swap test)', 'Have new coil ready'],
    steps: [
      { number: 1, title: 'Disconnect Battery', description: 'Negative cable off.', why: 'Safety.', tip: 'Standard.', commonMistake: 'Skipping.' },
      { number: 2, title: 'Locate Coil Pack', description: 'On 2.4L, coil pack is mounted on top of the engine, has spark plug wires going to each cylinder.', why: 'Visual ID.', tip: 'Multiple wires fan out from the coil.', commonMistake: 'Wrong location.' },
      { number: 3, title: 'Disconnect Spark Plug Wires from Coil', description: 'Pull each plug wire off the coil. Note which goes where (cylinder order matters).', why: 'Frees coil.', tip: 'Photograph or label each wire.', commonMistake: 'Mixing up wires.' },
      { number: 4, title: 'Disconnect Coil Electrical Connector', description: 'Press lock tab; pull connector off.', why: 'Frees power/ground.', tip: 'Photograph.', commonMistake: 'Yanking wires.' },
      { number: 5, title: 'Remove Coil Mounting Bolts', description: 'Two-three bolts hold coil to engine bracket. Remove.', why: 'Frees coil.', tip: 'Note bolt locations.', commonMistake: 'Stripping.' },
      { number: 6, title: 'Lift Old Coil Off', description: 'Pull straight up.', why: 'Removed.', tip: 'Compare to new before discarding.', commonMistake: 'Forcing.' },
      { number: 7, title: 'Install New Coil', description: 'Position new coil. Hand-thread bolts. Torque snug.', why: 'Replacement.', tip: 'Match orientation.', commonMistake: 'Cocking.' },
      { number: 8, title: 'Reconnect Plug Wires in Correct Order', description: 'Each wire goes to specific cylinder. Press each wire onto coil terminal until clicks.', why: 'Cylinder order critical.', tip: 'Use your photo or label.', commonMistake: 'Mixed up wires.' },
      { number: 9, title: 'Reconnect Electrical Connector', description: 'Plug in. Confirm click.', why: 'Power restored.', tip: 'Lock tab.', commonMistake: 'Partial.' },
      { number: 10, title: 'Reconnect Battery and Test', description: 'Reconnect. Start engine. Should idle smoothly. Code-scan for any new codes.', why: 'Confirms install.', tip: 'Drive 25 miles before clearing codes.', commonMistake: 'Calling done before drive.' },
    ],
    torqueSpecifications: [{ fastener: 'Coil mounting bolts', specification: '85 in-lbs (10 Nm).', notes: 'Low torque.' }],
    fluidSpecifications: [],
    troubleshooting: [
      { problem: 'Misfire returns', likelyCause: 'Wires mixed up or new coil defective', whatToCheck: 'Verify wire order; test new coil', fix: 'Reorder wires; warranty new coil.' },
      { problem: 'Engine cranks but won\'t start', likelyCause: 'Connector not seated', whatToCheck: 'Reseat connector', fix: 'Reconnect.' },
    ],
    proTips: ['Mark each plug wire by cylinder before disconnecting.', 'Test old coil resistance before assuming failure.', 'Replace plug wires together with coil if both old.'],
    commonMistakes: [{ mistake: 'Mixing up plug wire order', consequence: 'Engine misfires badly or won\'t run.' }],
    successIndicators: ['Smooth idle', 'No misfire codes', 'No CEL'],
    whenToSeekHelp: 'If misfire persists, suspect plug wires or specific plugs.',
    legalDisclaimer: 'Verify with licensed manual.',
    manualSource: { title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)', url: 'https://www.haynes.com/en-us/wrangler/jeep/2003', notes: 'Cross-reference 2.4L ignition system.' },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — WIPER MOTOR REPLACEMENT
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-wiper-motor-replacement',
    title: 'Wiper Motor Replacement',
    vehicle: { make: 'Jeep', model: 'Wrangler', generations: [{ startYear: 1997, endYear: 2006 }] },
    video: {
      youtubeId: 'OQMfhei2-Es',
      title: 'Jeep Wrangler TJ - Wiper Motor Replacement',
      channel: 'Frank\'s Garage',
    },
    source: 'Generated by GarageIQ AI from publicly available sources. Cross-checked per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Electrical',
    difficulty: 'intermediate',
    duration: '1-2 hours',
    interval: 'Replace when wipers do not operate, run only at one speed, or work intermittently.',
    description: 'The TJ wiper motor is mounted at the base of the windshield, accessed by removing the wiper arms and the cowl panel. Linkage connects motor to wiper transmissions. Replacement is moderately involved due to access.',
    overview: { difficulty: 'Intermediate — cowl panel removal needed', estimatedTime: '1-2 hours', estimatedCost: '$60-100 for motor', whyMatters: 'No wipers = unsafe in rain.', safetyNote: 'Disconnect battery before work.' },
    tools: ['Sockets (10-13mm)', 'Trim removal tool', 'Phillips screwdriver', 'Eye protection'],
    parts: [{ id: 'wiper-motor', name: 'Wiper Motor', partNumber: 'Mopar 55155587AB — equivalents: Cardone 40-411 / ACI 86700', quantity: 1, price: 80, category: 'required', notes: 'Confirm correct part.' }],
    safetyInformation: 'Disconnect battery. Cowl removal exposes some sharp edges.',
    beforeYouStart: ['Disconnect battery', 'Note wiper arm rest position before removal', 'Have new motor ready'],
    steps: [
      { number: 1, title: 'Disconnect Battery and Remove Wiper Arms', description: 'Disconnect battery negative. Lift wiper arm covers (small flip-up tabs); remove the 13mm nut at the base of each arm. Wiggle each arm off the splined shaft.', why: 'Access to cowl.', tip: 'Mark arm rest position with paint pen.', commonMistake: 'Forgetting position.' },
      { number: 2, title: 'Remove Cowl Panel', description: 'The cowl panel covers the area between hood and windshield. Remove the few clips/screws holding it. Lift cowl off.', why: 'Access to motor.', tip: 'Photograph clip orientation.', commonMistake: 'Forcing clips.' },
      { number: 3, title: 'Locate Motor and Linkage', description: 'Motor is mounted on cowl frame, with a linkage rod to wiper transmissions.', why: 'Visual ID.', tip: 'Note motor orientation.', commonMistake: 'Wrong component.' },
      { number: 4, title: 'Disconnect Motor Electrical Connector', description: 'Press lock tab and unplug.', why: 'Frees power.', tip: 'Photograph.', commonMistake: 'Yanking.' },
      { number: 5, title: 'Disconnect Motor Linkage', description: 'A small clip holds linkage to motor crank arm. Pop clip and disconnect.', why: 'Frees mechanical.', tip: 'Mark clip position.', commonMistake: 'Losing clip.' },
      { number: 6, title: 'Remove Motor Mounting Bolts', description: 'Three or four bolts hold motor to cowl frame.', why: 'Frees motor.', tip: 'Note bolt locations.', commonMistake: 'Stripping.' },
      { number: 7, title: 'Lift Out Old Motor', description: 'Lift motor straight out.', why: 'Removed.', tip: 'Compare to new.', commonMistake: 'Damaging.' },
      { number: 8, title: 'Install New Motor', description: 'Position new motor in cowl. Hand-thread bolts, torque snug.', why: 'Replacement.', tip: 'Match orientation.', commonMistake: 'Cocking.' },
      { number: 9, title: 'Reconnect Linkage and Electrical', description: 'Reattach linkage with clip. Plug in connector.', why: 'Restoration.', tip: 'Test motor by hand before final assembly.', commonMistake: 'Linkage backwards.' },
      { number: 10, title: 'Reinstall Cowl, Wipers, Test', description: 'Reattach cowl. Install wiper arms (in marked rest position). Test wipers (low, high, intermittent, washer).', why: 'Verifies install.', tip: 'Test all speeds and washer fluid.', commonMistake: 'Skipping function test.' },
    ],
    torqueSpecifications: [{ fastener: 'Wiper motor mounting bolts', specification: '85 in-lbs (10 Nm).', notes: 'Standard.' }, { fastener: 'Wiper arm nuts', specification: '17 ft-lbs (23 Nm).', notes: 'Standard.' }],
    fluidSpecifications: [],
    troubleshooting: [
      { problem: 'Wipers do not return to rest position', likelyCause: 'Motor installed incorrectly or rest position wrong', whatToCheck: 'Power to motor with delay relay', fix: 'Verify motor at correct park position when installed.' },
      { problem: 'Wipers run too fast or slow', likelyCause: 'Wrong-application motor or linkage off', whatToCheck: 'Motor rating and linkage seat', fix: 'Verify part number; reseat linkage.' },
    ],
    proTips: ['Mark wiper arm position before removal.', 'Photograph cowl clip locations.', 'Test motor function before final cowl install.'],
    commonMistakes: [{ mistake: 'Forgetting wiper rest position', consequence: 'Wipers stop in wrong location.' }],
    successIndicators: ['All wiper speeds work', 'Wipers return to rest', 'Washer fluid sprays', 'No noise from motor'],
    whenToSeekHelp: 'If motor is fine but wipers don\'t move, suspect linkage mechanism issues.',
    legalDisclaimer: 'Verify with licensed manual.',
    manualSource: { title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)', url: 'https://www.haynes.com/en-us/wrangler/jeep/2003', notes: 'Cross-reference wiper system.' },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — HEADLIGHT RELAY UPGRADE
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-headlight-relay-upgrade',
    title: 'Headlight Relay Upgrade',
    vehicle: { make: 'Jeep', model: 'Wrangler', generations: [{ startYear: 1997, endYear: 2006 }] },
    video: {
      youtubeId: 'YAhgBwyoKIw',
      title: 'Headlight Harness Upgrade',
      channel: 'BleepinJeep',
    },
    source: 'Generated by GarageIQ AI from publicly available sources. Cross-checked per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Electrical',
    difficulty: 'intermediate',
    duration: '1-2 hours',
    interval: 'Install when headlight switch is hot to touch (excessive current through switch), or when upgrading to high-wattage / LED headlights.',
    description: 'The TJ headlight switch directly carries headlight current — over time this stresses the switch and causes it to fail. A relay harness upgrade routes high-current through dedicated relays, with the switch only sending a low-current signal. Improves longevity and headlight brightness.',
    overview: { difficulty: 'Intermediate — wiring work, multiple connections', estimatedTime: '1-2 hours', estimatedCost: '$30-50 for relay harness kit', whyMatters: 'Prevents headlight switch failure; brighter lights with reduced wire-resistance loss.', safetyNote: 'Disconnect battery before electrical work.' },
    tools: ['Wire strippers and crimpers', 'Multimeter', 'Heat shrink tubing', 'Drill (for mounting if needed)', 'Sockets', 'Eye protection'],
    parts: [{ id: 'headlight-relay-kit', name: 'Headlight Relay Harness Kit', partNumber: 'Painless 30810 / aftermarket relay harness', quantity: 1, price: 40, category: 'required', notes: 'Pre-made kits include 2 relays, fuses, and wiring.' }],
    safetyInformation: 'Disconnect battery before electrical work. Use proper crimping technique on connections.',
    beforeYouStart: ['Disconnect battery', 'Read kit-specific instructions', 'Have replacement bulbs if upgrading'],
    steps: [
      { number: 1, title: 'Disconnect Battery', description: 'Negative cable off.', why: 'Safety.', tip: 'Standard.', commonMistake: 'Skipping.' },
      { number: 2, title: 'Plan Relay Mounting Location', description: 'Choose a clean, dry spot near the battery — typically on inner fender or firewall.', why: 'Mounting plan.', tip: 'Avoid heat sources.', commonMistake: 'Cramped location.' },
      { number: 3, title: 'Mount Relays', description: 'Use included mounting bracket or self-adhesive pad. Attach two relays.', why: 'Stable mounting.', tip: 'Drill carefully if needed.', commonMistake: 'Loose mounting.' },
      { number: 4, title: 'Connect Relay Power Input', description: 'Run heavy gauge (10AWG) wire from battery positive (with inline fuse) to each relay\'s power input pin.', why: 'Direct battery feed.', tip: 'Inline 30A fuse per relay.', commonMistake: 'No inline fuse.' },
      { number: 5, title: 'Connect Relay Output to Headlights', description: 'Run wire from each relay output to corresponding headlight (low and high beam).', why: 'Power to headlights.', tip: 'Use proper waterproof connectors at headlights.', commonMistake: 'Open connections.' },
      { number: 6, title: 'Connect Trigger Wires to Existing Headlight Wiring', description: 'Tap into existing headlight wires (low and high beam) at the headlight connector to provide the relay trigger signal. Now the switch sends a small signal that activates the relay.', why: 'Switch becomes signal-only.', tip: 'Use scotch-locks or solder connections.', commonMistake: 'Improper tap.' },
      { number: 7, title: 'Connect Ground', description: 'Ground each relay to clean chassis ground point.', why: 'Required for relay coil.', tip: 'Use star washer.', commonMistake: 'Poor ground connection.' },
      { number: 8, title: 'Verify All Connections', description: 'Inspect every connection for security and weatherproofing. Heat-shrink any exposed wire.', why: 'Long-term reliability.', tip: 'Crimps should be tight.', commonMistake: 'Weak connections.' },
      { number: 9, title: 'Reconnect Battery and Test', description: 'Reconnect battery. Test low beam (headlights on), then high beam.', why: 'Confirms install.', tip: 'Test from outside vehicle.', commonMistake: 'Not testing both modes.' },
      { number: 10, title: 'Verify Improved Brightness', description: 'Compare to before-install conditions. Headlights should be noticeably brighter due to reduced voltage drop.', why: 'Confirms benefit.', tip: 'Park 25 ft from wall to compare.', commonMistake: 'Skipping comparison.' },
    ],
    torqueSpecifications: [],
    fluidSpecifications: [],
    troubleshooting: [
      { problem: 'Headlights do not work after install', likelyCause: 'Wiring connection issue or relay misconfigured', whatToCheck: 'Voltage at each connection point', fix: 'Trace and repair connections.' },
      { problem: 'Only one beam works', likelyCause: 'Relay or wiring on one circuit', whatToCheck: 'Test relay; verify wiring', fix: 'Replace relay or fix wiring.' },
    ],
    proTips: ['Use 10AWG wire for power; 14-16AWG for trigger.', 'Inline fuses are essential for safety.', 'Heat-shrink connections for water resistance.', 'Document the upgrade for future troubleshooting.'],
    commonMistakes: [{ mistake: 'No inline fuses', consequence: 'Wire short = vehicle fire risk.' }],
    successIndicators: ['Headlights visibly brighter', 'Headlight switch no longer hot', 'No flicker or intermittent operation'],
    whenToSeekHelp: 'If wiring is unfamiliar, hire automotive electrical professional.',
    legalDisclaimer: 'Improper wiring can cause fire. Verify all connections before use.',
    manualSource: { title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)', url: 'https://www.haynes.com/en-us/wrangler/jeep/2003', notes: 'Cross-reference electrical diagrams.' },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — MULTIFUNCTION SWITCH (Turn Signal / Dimmer Stalk)
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-multifunction-switch-replacement',
    title: 'Multifunction Switch Replacement (Turn Signal / Dimmer)',
    vehicle: { make: 'Jeep', model: 'Wrangler', generations: [{ startYear: 1997, endYear: 2006 }] },
    video: {
      youtubeId: '-Z6q0Qs-rPs',
      title: '\'99 Jeep TJ Turn Signals FIXED! Replacing the Multifunction Switch',
      channel: 'Beyond 50',
    },
    source: 'Generated by GarageIQ AI from publicly available sources. Cross-checked per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Electrical',
    difficulty: 'intermediate',
    duration: '1-2 hours',
    interval: 'Replace when turn signals fail to operate, dimmer switch fails, or stalk feels broken/loose.',
    description: 'The multifunction switch (combo switch) on the steering column controls turn signals, headlight dimmer, and wipers. Failure typically affects one or more of these functions. Replacement requires removing the steering column lower cover and disconnecting wiring.',
    overview: { difficulty: 'Intermediate — column work', estimatedTime: '1-2 hours', estimatedCost: '$60-100 for switch', whyMatters: 'Turn signals legally required.', safetyNote: 'Steering column work — disconnect battery; do not disturb airbag (TJ may not have one but be cautious).' },
    tools: ['Phillips screwdriver', 'Sockets (7-10mm)', 'Trim removal tool', 'Eye protection'],
    parts: [{ id: 'multifunction-switch', name: 'Multifunction Switch', partNumber: 'Mopar 56021207AB — equivalent: Standard CBS1086', quantity: 1, price: 80, category: 'required', notes: 'Confirm correct part.' }],
    safetyInformation: 'Disconnect battery before steering column work. Do not disturb airbag system if applicable.',
    beforeYouStart: ['Disconnect battery', 'Have new switch ready'],
    steps: [
      { number: 1, title: 'Disconnect Battery', description: 'Negative cable off.', why: 'Safety.', tip: 'Standard.', commonMistake: 'Skipping.' },
      { number: 2, title: 'Remove Steering Column Cover', description: 'Lower cover under steering wheel held by 3-4 Phillips screws. Lift away.', why: 'Access.', tip: 'Photograph clip orientation.', commonMistake: 'Damaging cover.' },
      { number: 3, title: 'Remove Upper Cover', description: 'Upper cover held by clips. Pry off carefully.', why: 'Access to switch.', tip: 'Tilt steering wheel down for clearance.', commonMistake: 'Cracking plastic.' },
      { number: 4, title: 'Disconnect Switch Wiring', description: 'Switch has 1-2 multi-pin connectors. Press lock and disconnect.', why: 'Frees electrical.', tip: 'Photograph orientation.', commonMistake: 'Yanking.' },
      { number: 5, title: 'Remove Switch Mounting Screws', description: 'Three Phillips screws hold switch to column. Remove.', why: 'Frees switch.', tip: 'Note positions.', commonMistake: 'Stripping.' },
      { number: 6, title: 'Remove Old Switch', description: 'Pull switch off column.', why: 'Removed.', tip: 'Compare to new.', commonMistake: 'Forcing.' },
      { number: 7, title: 'Install New Switch', description: 'Position new switch on column. Hand-thread screws.', why: 'Replacement.', tip: 'Verify orientation.', commonMistake: 'Cocked install.' },
      { number: 8, title: 'Reconnect Wiring', description: 'Plug in connectors per orientation photos.', why: 'Restores function.', tip: 'Click into place.', commonMistake: 'Mixed up connectors.' },
      { number: 9, title: 'Reinstall Steering Column Covers', description: 'Reattach upper cover, then lower cover with screws.', why: 'Cosmetic restore.', tip: 'Hand-tight.', commonMistake: 'Over-torque.' },
      { number: 10, title: 'Reconnect Battery and Test All Functions', description: 'Reconnect battery. Test: left turn, right turn, headlight dimmer (low/high), wipers (off/intermittent/low/high), wipers/washer.', why: 'Verifies all switch functions.', tip: 'Test outside vehicle for visual.', commonMistake: 'Not testing all functions.' },
    ],
    torqueSpecifications: [],
    fluidSpecifications: [],
    troubleshooting: [
      { problem: 'Turn signals do not work', likelyCause: 'Wrong-application switch or connector not seated', whatToCheck: 'Reseat connector; verify part match', fix: 'Reconnect; warranty exchange if defective.' },
      { problem: 'Wipers don\'t work but turn signals do', likelyCause: 'Wiper portion of switch defective', whatToCheck: 'Inspect connection; warranty exchange', fix: 'Replace defective new switch.' },
    ],
    proTips: ['Photograph wiring before disassembly.', 'Test all functions before reassembling covers.', 'Quality matters — cheap switches fail prematurely.'],
    commonMistakes: [{ mistake: 'Mixing up wiring connectors', consequence: 'Functions don\'t work or wrong functions activated.' }],
    successIndicators: ['All turn, dimmer, wiper functions work', 'Stalk feels solid', 'No flickering'],
    whenToSeekHelp: 'If TJ has airbag and column work seems risky, dealer or specialist may be safer.',
    legalDisclaimer: 'Steering column work has airbag implications on some vehicles. Verify before disassembly.',
    manualSource: { title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)', url: 'https://www.haynes.com/en-us/wrangler/jeep/2003', notes: 'Cross-reference electrical and column.' },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — FUSE BOX / RELAY BOX REFERENCE
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-fuse-box-relay-box-reference',
    title: 'Fuse Box and Relay Box Reference',
    vehicle: { make: 'Jeep', model: 'Wrangler', generations: [{ startYear: 1997, endYear: 2006 }] },
    source: 'Generated by GarageIQ AI from publicly available sources (Mopar service literature, owner\'s manual). Cross-checked per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Electrical',
    difficulty: 'beginner',
    duration: '5 minutes per check',
    interval: 'Reference for finding and testing fuses or relays during electrical diagnosis.',
    description: 'The TJ has two fuse/relay boxes: under-hood (engine bay, near battery) for high-current circuits, and inside cabin (driver\'s side kick panel) for accessory circuits. This reference identifies common circuits.',
    overview: { difficulty: 'Beginner — locating and inspecting fuses/relays', estimatedTime: '5 min per check', estimatedCost: '$2-15 per fuse; $5-25 per relay (depending on type)', whyMatters: 'Knowing fuse/relay locations enables quick diagnosis of dead circuits.', safetyNote: 'Battery should be disconnected before pulling/installing relays in cabin/engine bay.' },
    tools: ['Fuse puller (often included in fuse box)', 'Multimeter', 'Spare fuses (assortment)', 'Eye protection'],
    parts: [{ id: 'fuse-assortment', name: 'Fuse Assortment Kit', partNumber: 'ATC/ATO blade fuses 5A-30A', quantity: 1, price: 12, category: 'recommended', notes: 'Keep onboard for emergency.' }],
    safetyInformation: 'Pulling fuses while in use can spark. Disconnect battery for relay work.',
    beforeYouStart: ['Identify circuit you need to diagnose', 'Locate fuse box (under hood or in cabin)'],
    steps: [
      { number: 1, title: 'Locate Underhood Fuse / Relay Box', description: 'Engine bay, driver\'s side near battery. Has plastic cover with diagram on inside.', why: 'High-current circuits.', tip: 'Diagram on inside of cover.', commonMistake: 'Looking in wrong location.' },
      { number: 2, title: 'Locate Cabin Fuse Box', description: 'Driver\'s side kick panel (lower left, near foot). Pry off small access panel.', why: 'Cabin accessory circuits.', tip: 'Owner\'s manual has full diagram.', commonMistake: 'Not finding.' },
      { number: 3, title: 'Identify Common Circuits in Underhood Box', description: 'Common fuses: #11 (headlights), #20 (fuel pump), #4 (radiator fan), #13 (horn). Common relays: fuel pump relay, A/C compressor relay, fan relay.', why: 'Reference.', tip: 'Confirm against your specific year.', commonMistake: 'Generic assumption — diagram on cover is authoritative.' },
      { number: 4, title: 'Identify Common Circuits in Cabin Box', description: 'Common fuses: turn signals, dome light, accessory power, radio, blower motor (#11 typically).', why: 'Reference.', tip: 'Diagram on cover.', commonMistake: 'Skipping cover diagram.' },
      { number: 5, title: 'Pull Fuse for Inspection', description: 'Use fuse puller. Visually inspect — broken metal strip = blown fuse.', why: 'Inspection.', tip: 'Hold up to light to see filament.', commonMistake: 'Replacing without inspection.' },
      { number: 6, title: 'Test Fuse with Multimeter', description: 'Set multimeter to continuity. Touch each leg of fuse — should beep / show continuity.', why: 'Confirms fuse condition.', tip: 'Some fuses look fine but are open.', commonMistake: 'Trusting visual only.' },
      { number: 7, title: 'Replace with Same Amperage', description: 'Always replace with EXACT same amperage. Higher = fire risk.', why: 'Safety.', tip: 'Amperage marked on fuse.', commonMistake: 'Using wrong amperage.' },
      { number: 8, title: 'Test Relay by Swap', description: 'Swap suspect relay with same-type known-good relay (e.g., from horn). If problem moves, the relay is bad.', why: 'Functional test.', tip: 'Most relays in the box are interchangeable.', commonMistake: 'Replacing without swap test.' },
      { number: 9, title: 'Test Relay with Multimeter', description: 'Test relay coil resistance (60-100 ohms typical) and contacts (open with no power, closed with 12V to coil).', why: 'Verifies relay function.', tip: 'Apply 12V from battery to coil terminals; should hear click.', commonMistake: 'Skipping.' },
      { number: 10, title: 'Document Findings', description: 'Note which fuse/relay was bad and what it controls. Reset codes or restart system.', why: 'Documentation.', tip: 'Notes help future troubleshooting.', commonMistake: 'No notes.' },
    ],
    torqueSpecifications: [],
    fluidSpecifications: [],
    troubleshooting: [
      { problem: 'Fuse blows immediately on replacement', likelyCause: 'Short in circuit', whatToCheck: 'Look for damaged wire or component on circuit', fix: 'Repair short before replacing fuse.' },
      { problem: 'Relay fails after replacement', likelyCause: 'Underlying circuit issue', whatToCheck: 'Test current draw on circuit', fix: 'Repair root cause.' },
    ],
    proTips: ['Always replace fuses with same amperage.', 'Keep an assortment of fuses in your TJ.', 'A swap test is the fastest relay diagnostic.', 'Photograph fuse box on day one for future reference.'],
    commonMistakes: [{ mistake: 'Using higher amperage fuse', consequence: 'Wire overload risk; possible fire.' }],
    successIndicators: ['Circuit functions after fuse/relay replacement', 'No repeat blowing'],
    whenToSeekHelp: 'If fuse blows repeatedly, the circuit has a short — track and repair before continuing.',
    legalDisclaimer: 'Verify with licensed manual.',
    manualSource: { title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)', url: 'https://www.haynes.com/en-us/wrangler/jeep/2003', notes: 'Cross-reference fuse box layout.' },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — SENTRY KEY / IMMOBILIZER RESET
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-sentry-key-immobilizer',
    title: 'Sentry Key / Immobilizer Reset',
    vehicle: { make: 'Jeep', model: 'Wrangler', generations: [{ startYear: 1999, endYear: 2006 }] },
    video: {
      youtubeId: 'mFEuGnI1X_U',
      title: 'Dodge/Chrysler/Jeep WCM sentry key RFID immobilizer reference',
      channel: 'Jacob Aikey',
    },
    source: 'Generated by GarageIQ AI from publicly available sources. Cross-checked per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Electrical',
    difficulty: 'intermediate',
    duration: '15-30 minutes (DIY) or shop service',
    interval: 'When sentry key warning lamp stays lit, key not recognized, or vehicle won\'t start due to immobilizer fault.',
    description: 'Some TJs (typically 1999+) have the Sentry Key Theft Deterrent System (SKTDS) — an immobilizer that allows engine start only with a programmed key. If the system fails or new keys need programming, this is the procedure. Note: full programming often requires a dealer scan tool.',
    overview: { difficulty: 'Intermediate — DIY for basic reset; dealer/locksmith for programming', estimatedTime: '15-30 minutes DIY; 30-60 min at dealer/locksmith', estimatedCost: '$0 DIY; $50-150 at dealer/locksmith', whyMatters: 'Without working immobilizer, vehicle cannot start.', safetyNote: 'Procedures vary by year. Verify before attempting.' },
    tools: ['Two valid programmed sentry keys', 'OBD-II scanner (some)', 'Stopwatch'],
    parts: [{ id: 'sentry-key-blank', name: 'New Sentry Key (blank, requires programming)', partNumber: 'Mopar OEM key', quantity: 1, price: 30, category: 'optional', notes: 'New keys must be programmed by dealer/locksmith. Two original keys are required for self-programming.' }],
    safetyInformation: 'Without two valid keys, dealer programming is required. Do not attempt without correct procedure.',
    beforeYouStart: ['Have at least two valid programmed sentry keys', 'Read OEM procedure for your specific year', 'Battery should be fully charged'],
    steps: [
      { number: 1, title: 'Verify Sentry Key System Indicator', description: 'Sentry key indicator on dash should flash briefly when key is in RUN, then turn off if recognized.', why: 'Indicator confirms system status.', tip: 'Continuous flashing = key not recognized.', commonMistake: 'Confusing battery indicator with sentry indicator.' },
      { number: 2, title: 'Insert First Programmed Key', description: 'Insert valid sentry key. Turn to RUN (not start). Hold for 5 seconds.', why: 'First step in self-programming.', tip: 'Indicator should stop flashing.', commonMistake: 'Wrong key.' },
      { number: 3, title: 'Switch to Off, Then Insert Second Key', description: 'Turn key OFF. Quickly remove. Insert second valid key. Turn to RUN within 10 seconds.', why: 'Two-key sequence required.', tip: 'Quick switch.', commonMistake: 'Too slow between keys.' },
      { number: 4, title: 'Hold Second Key in Run', description: 'Hold second key in RUN for 5 seconds. Indicator should chime and stop flashing.', why: 'Confirms second key recognition.', tip: 'Wait for chime.', commonMistake: 'Switching too early.' },
      { number: 5, title: 'Switch Off, Insert New (Blank) Key', description: 'Turn off, quickly remove second key. Insert new (uncut/programmed) key. Turn to RUN.', why: 'Programs new key.', tip: 'Quick switch.', commonMistake: 'Too slow.' },
      { number: 6, title: 'Wait for Chime / Programming Complete', description: 'A chime should indicate new key is now programmed.', why: 'Successful programming.', tip: 'May take 30-60 seconds.', commonMistake: 'Calling programmed too early.' },
      { number: 7, title: 'Test Start with New Key', description: 'Start vehicle with new key. Should fire normally.', why: 'Confirms key works.', tip: 'If fails, restart procedure.', commonMistake: 'Skipping start test.' },
      { number: 8, title: 'Test All Programmed Keys', description: 'Verify all keys still start vehicle.', why: 'Verifies no other keys deprogrammed.', tip: 'Test each key in sequence.', commonMistake: 'Only testing new key.' },
      { number: 9, title: 'If Self-Programming Fails: Take to Dealer/Locksmith', description: 'Procedures vary by year — some require scan tool. Dealer or locksmith can program new key with security code lookup.', why: 'Fall-back option.', tip: 'Mobile locksmiths often cheaper than dealer.', commonMistake: 'Trying repeatedly.' },
      { number: 10, title: 'Document Programmed Keys', description: 'Note which keys are valid and where they are stored.', why: 'Future reference.', tip: 'Key fobs may also need programming.', commonMistake: 'Not documenting.' },
    ],
    torqueSpecifications: [],
    fluidSpecifications: [],
    troubleshooting: [
      { problem: 'Sentry key light continues flashing', likelyCause: 'Programming did not complete', whatToCheck: 'Repeat procedure', fix: 'Try again or use dealer.' },
      { problem: 'Vehicle won\'t start with any key', likelyCause: 'SKIM module failure or wiring', whatToCheck: 'Diagnostic at dealer/locksmith', fix: 'Module replacement or repair.' },
    ],
    proTips: ['Two valid keys required for self-programming.', 'Mobile locksmiths often cheaper than dealer.', 'Keep one key as a permanent spare.'],
    commonMistakes: [{ mistake: 'Self-programming with wrong sequence', consequence: 'Programming fails; may need dealer reset.' }],
    successIndicators: ['Sentry indicator off with key in RUN', 'Vehicle starts on first try', 'All valid keys work'],
    whenToSeekHelp: 'If self-programming fails, dealer or locksmith with proper scan tool can program. Some failures (SKIM module) require full module replacement.',
    legalDisclaimer: 'Sentry key procedures vary. Verify against OEM service manual for specific year.',
    manualSource: { title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)', url: 'https://www.haynes.com/en-us/wrangler/jeep/2003', notes: 'Cross-reference sentry key procedure.' },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — MAP SENSOR REPLACEMENT
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-map-sensor-replacement',
    title: 'MAP Sensor Replacement (Manifold Absolute Pressure)',
    vehicle: { make: 'Jeep', model: 'Wrangler', generations: [{ startYear: 1997, endYear: 2006 }], engine: '4.0l-i6' },
    video: {
      youtubeId: 'M4lQQIBNQVo',
      title: 'Map Sensor Replacement on Jeep Wrangler',
      channel: 'WhitedGutterSolutions',
    },
    source: 'Generated by GarageIQ AI from publicly available sources. Cross-checked per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Engine',
    difficulty: 'beginner',
    duration: '15-20 minutes',
    interval: 'Replace when P0107/P0108 codes appear, fuel trim is significantly off, or engine runs poorly under load.',
    description: 'The MAP sensor reads manifold pressure (vacuum) and feeds it to the PCM for fuel calculation. Located on the intake manifold or attached to a vacuum line. Failure causes lean/rich mixture and poor performance.',
    overview: { difficulty: 'Beginner — direct R&R', estimatedTime: '15-20 minutes', estimatedCost: '$25-50', whyMatters: 'Bad MAP causes wrong air-fuel mixture.', safetyNote: 'Disconnect battery before electrical work.' },
    tools: ['Sockets (10mm)', 'Phillips screwdriver', 'Multimeter', 'Eye protection'],
    parts: [{ id: 'map-sensor', name: 'MAP Sensor', partNumber: 'Mopar 56029405AC — equivalent: Standard AS78', quantity: 1, price: 35, category: 'required', notes: 'Confirm part for application.' }],
    safetyInformation: 'Disconnect battery negative cable.',
    beforeYouStart: ['Confirm sensor failure (codes P0107/P0108)', 'Have new sensor ready'],
    steps: [
      { number: 1, title: 'Disconnect Battery', description: 'Negative cable off.', why: 'Safety.', tip: 'Standard.', commonMistake: 'Skipping.' },
      { number: 2, title: 'Locate MAP Sensor', description: '4.0L: typically on the intake manifold or mounted in the engine bay with a vacuum hose to the intake.', why: 'Visual ID.', tip: 'Has 3-pin electrical connector and vacuum hose.', commonMistake: 'Confusing with TPS.' },
      { number: 3, title: 'Disconnect Connector', description: 'Press lock tab; pull connector off.', why: 'Frees electrical.', tip: 'Photograph.', commonMistake: 'Yanking.' },
      { number: 4, title: 'Disconnect Vacuum Hose', description: 'Pull hose off sensor port.', why: 'Frees sensor.', tip: 'Note hose orientation.', commonMistake: 'Tearing.' },
      { number: 5, title: 'Remove Sensor Mounting Screws', description: 'Two screws or one bolt, depending on year. Remove.', why: 'Frees sensor.', tip: 'Don\'t lose screw.', commonMistake: 'Stripping.' },
      { number: 6, title: 'Install New Sensor', description: 'Position new sensor. Hand-thread screws/bolt. Snug.', why: 'Replacement.', tip: 'Match orientation.', commonMistake: 'Cocked.' },
      { number: 7, title: 'Reconnect Vacuum Hose', description: 'Push hose onto port.', why: 'Restores function.', tip: 'Confirm full insertion.', commonMistake: 'Loose hose.' },
      { number: 8, title: 'Reconnect Connector', description: 'Plug in until clicks.', why: 'Power restored.', tip: 'Lock tab engaged.', commonMistake: 'Partial.' },
      { number: 9, title: 'Reconnect Battery and Clear Codes', description: 'Reconnect battery. Clear codes with scanner if available.', why: 'Reset PCM.', tip: 'PCM relearns.', commonMistake: 'Not clearing codes.' },
      { number: 10, title: 'Test Drive and Verify', description: 'Drive 25 miles. Verify no MAP-related codes return.', why: 'Confirms install.', tip: 'Code-scan after drive.', commonMistake: 'Calling fixed before drive.' },
    ],
    torqueSpecifications: [{ fastener: 'MAP sensor mounting bolts', specification: '85 in-lbs (10 Nm).', notes: 'Low torque.' }],
    fluidSpecifications: [],
    troubleshooting: [{ problem: 'Code returns', likelyCause: 'Sensor defective or wiring', whatToCheck: 'Test sensor signal with multimeter', fix: 'Warranty exchange or repair wiring.' }],
    proTips: ['Quality matters — Bosch and Mopar OEM are reliable.'],
    commonMistakes: [{ mistake: 'Reusing old vacuum hose if cracked', consequence: 'Bad reading.' }],
    successIndicators: ['No MAP codes', 'Smooth idle', 'Normal fuel trim'],
    whenToSeekHelp: 'If codes persist with new sensor and confirmed wiring, suspect PCM.',
    legalDisclaimer: 'Verify with licensed manual.',
    manualSource: { title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)', url: 'https://www.haynes.com/en-us/wrangler/jeep/2003', notes: 'Cross-reference sensor location.' },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — IAT SENSOR REPLACEMENT
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-iat-sensor-replacement',
    title: 'IAT Sensor Replacement (Intake Air Temperature)',
    vehicle: { make: 'Jeep', model: 'Wrangler', generations: [{ startYear: 1997, endYear: 2006 }] },
    source: 'Generated by GarageIQ AI from publicly available sources. Cross-checked per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Engine',
    difficulty: 'beginner',
    duration: '10-15 minutes',
    interval: 'Replace when P0112/P0113 codes appear or air temperature reading is incorrect.',
    description: 'The IAT sensor measures intake air temperature, allowing the PCM to adjust fuel mixture for air density. Typically integrated with the air filter housing or mounted in the intake hose.',
    overview: { difficulty: 'Beginner — direct R&R', estimatedTime: '10-15 minutes', estimatedCost: '$15-25', whyMatters: 'Wrong air temp reading affects fuel mixture.', safetyNote: 'Disconnect battery.' },
    tools: ['Sockets', 'Phillips screwdriver', 'Multimeter'],
    parts: [{ id: 'iat-sensor', name: 'IAT Sensor', partNumber: 'Mopar 56028364 — equivalent: Standard AX72', quantity: 1, price: 18, category: 'required', notes: 'Verify for application year.' }],
    safetyInformation: 'Disconnect battery.',
    beforeYouStart: ['Confirm sensor failure', 'Have new sensor'],
    steps: [
      { number: 1, title: 'Disconnect Battery', description: 'Negative cable off.', why: 'Safety.', tip: 'Standard.', commonMistake: 'Skipping.' },
      { number: 2, title: 'Locate IAT Sensor', description: 'Typically on the intake hose or air filter housing, with 2-pin connector.', why: 'Visual ID.', tip: 'Small two-pin sensor.', commonMistake: 'Confusing with MAP.' },
      { number: 3, title: 'Disconnect Connector', description: 'Press lock; pull off.', why: 'Frees electrical.', tip: 'Photograph.', commonMistake: 'Yanking.' },
      { number: 4, title: 'Remove Sensor', description: 'Twist or unscrew sensor from housing.', why: 'Frees sensor.', tip: 'Don\'t lose retention clip.', commonMistake: 'Forcing.' },
      { number: 5, title: 'Install New Sensor', description: 'Insert and twist/screw to secure.', why: 'Replacement.', tip: 'Hand-tight.', commonMistake: 'Over-torque.' },
      { number: 6, title: 'Reconnect Connector', description: 'Plug in.', why: 'Power.', tip: 'Click.', commonMistake: 'Partial.' },
      { number: 7, title: 'Reconnect Battery', description: 'Reconnect.', why: 'System power.', tip: 'Standard.', commonMistake: 'Skipping.' },
      { number: 8, title: 'Clear Codes', description: 'Use scanner.', why: 'Reset.', tip: 'PCM relearns.', commonMistake: 'No reset.' },
      { number: 9, title: 'Drive and Verify', description: 'Test drive 25 miles.', why: 'Confirms.', tip: 'Code-scan after.', commonMistake: 'Skipping.' },
      { number: 10, title: 'Verify Live Data', description: 'On scan tool, IAT should match ambient temp at cold start.', why: 'Confirms reading.', tip: 'Compare to outside temperature.', commonMistake: 'Skipping.' },
    ],
    torqueSpecifications: [],
    fluidSpecifications: [],
    troubleshooting: [{ problem: 'Code returns', likelyCause: 'Defective new sensor or wiring', whatToCheck: 'Test signal', fix: 'Warranty exchange.' }],
    proTips: ['IAT and MAP often paired in code; inspect both.'],
    commonMistakes: [{ mistake: 'Damaging connector', consequence: 'Replace harness.' }],
    successIndicators: ['No IAT codes', 'Reading matches ambient cold'],
    whenToSeekHelp: 'If sensor and wiring confirmed good and code returns, suspect PCM.',
    legalDisclaimer: 'Verify with licensed manual.',
    manualSource: { title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)', url: 'https://www.haynes.com/en-us/wrangler/jeep/2003', notes: 'Cross-reference sensor location.' },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — COOLANT TEMP SENSOR (CTS)
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-cts-replacement',
    title: 'Coolant Temperature Sensor (CTS) Replacement',
    vehicle: { make: 'Jeep', model: 'Wrangler', generations: [{ startYear: 1997, endYear: 2006 }] },
    video: {
      youtubeId: 'Kj8Kr7glz7Y',
      title: 'Jeep Wrangler P0118/P0128 Diagnosis and coolant temperature sensor replacement',
      channel: 'S&E\'s Garage',
    },
    source: 'Generated by GarageIQ AI from publicly available sources. Cross-checked per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Engine',
    difficulty: 'beginner',
    duration: '15-30 minutes',
    interval: 'Replace when P0117/P0118 codes appear, hard cold start, or coolant temp reading on scan tool is incorrect.',
    description: 'The CTS reads engine coolant temperature for the PCM. The TJ has separate CTS for ECM and dashboard gauge. Located on the engine block (4.0L: side of head; 2.4L: thermostat housing). Failure causes wrong cold-start enrichment and poor running.',
    overview: { difficulty: 'Beginner — minor coolant loss', estimatedTime: '15-30 minutes', estimatedCost: '$15-30', whyMatters: 'CTS failure = hard cold start, poor mixture.', safetyNote: 'Coolant hot when engine warm. Cool first.' },
    tools: ['Wrench (typically 3/4" or 19mm)', 'Drain pan', 'Multimeter', 'Eye protection'],
    parts: [{ id: 'cts', name: 'Coolant Temperature Sensor', partNumber: 'Mopar 56027873AC — equivalent: Standard TX46', quantity: 1, price: 18, category: 'required', notes: 'Verify part — TJ has multiple temp sensors.' }],
    safetyInformation: 'Coolant hot. Allow cooling. Drain catches coolant; coolant is toxic.',
    beforeYouStart: ['Allow engine cooling', 'Position drain pan', 'Have new sensor with thread sealant'],
    steps: [
      { number: 1, title: 'Cool Engine', description: 'Allow engine to fully cool to ambient temperature.', why: 'Prevents burns and pressure.', tip: '1+ hours.', commonMistake: 'Working hot.' },
      { number: 2, title: 'Locate CTS', description: '4.0L: side of cylinder head, near thermostat. 2.4L: thermostat housing.', why: 'Visual ID.', tip: 'Has 2-pin connector.', commonMistake: 'Confusing with dash sender.' },
      { number: 3, title: 'Disconnect Connector', description: 'Press lock; unplug.', why: 'Frees electrical.', tip: 'Photograph.', commonMistake: 'Yanking.' },
      { number: 4, title: 'Position Drain Pan', description: 'Catches coolant when sensor removed.', why: 'A few oz spill.', tip: 'Have rags.', commonMistake: 'Skipping pan.' },
      { number: 5, title: 'Remove Old Sensor', description: 'Unscrew with wrench. Thread is reverse — counterclockwise to loosen.', why: 'Removed.', tip: 'Some coolant emerges.', commonMistake: 'Stripping threads.' },
      { number: 6, title: 'Apply Thread Sealant', description: 'Apply Teflon tape or thread sealant to new sensor threads.', why: 'Prevents coolant leak.', tip: 'Single wrap of Teflon.', commonMistake: 'No sealant.' },
      { number: 7, title: 'Install New Sensor', description: 'Hand-thread, then snug with wrench. Torque to 12-15 ft-lbs.', why: 'Secure with seal.', tip: 'Don\'t over-torque.', commonMistake: 'Cross-thread.' },
      { number: 8, title: 'Reconnect Connector', description: 'Plug in until clicks.', why: 'Power restored.', tip: 'Confirm.', commonMistake: 'Partial.' },
      { number: 9, title: 'Top Off Coolant', description: 'Add fresh coolant to reservoir.', why: 'Replace lost.', tip: 'Bleed if needed.', commonMistake: 'Insufficient.' },
      { number: 10, title: 'Test and Verify', description: 'Start engine. After warm, scan tool live data should show ~195°F. No CTS codes.', why: 'Confirms operation.', tip: 'Verify no leak.', commonMistake: 'Skipping leak check.' },
    ],
    torqueSpecifications: [{ fastener: 'CTS sensor', specification: '12-15 ft-lbs (16-20 Nm). Verify torque spec from OEM service manual.', notes: 'Use thread sealant.' }],
    fluidSpecifications: [{ fluid: 'Coolant top-off', type: 'HOAT', capacity: '~1 cup', notes: 'Match existing.' }],
    troubleshooting: [{ problem: 'Code returns', likelyCause: 'Defective sensor or wiring', whatToCheck: 'Test resistance', fix: 'Warranty exchange.' }],
    proTips: ['Apply thread sealant.', 'Verify temp reading on scan tool live data.'],
    commonMistakes: [{ mistake: 'No thread sealant', consequence: 'Coolant leak.' }],
    successIndicators: ['No CTS codes', 'Correct temp reading', 'No leak'],
    whenToSeekHelp: 'If sensor and wiring confirmed and code persists, PCM may need attention.',
    legalDisclaimer: 'Coolant is toxic.',
    manualSource: { title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)', url: 'https://www.haynes.com/en-us/wrangler/jeep/2003', notes: 'Cross-reference sensor location.' },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — EVAP CANISTER / PURGE VALVE
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-evap-canister-purge-valve',
    title: 'EVAP Canister and Purge Valve Service',
    vehicle: { make: 'Jeep', model: 'Wrangler', generations: [{ startYear: 1997, endYear: 2006 }] },
    video: {
      youtubeId: 'DSamNJdkLAc',
      title: 'How to Replace Vapor Canister Purge Valve 1997-2006 Jeep Wrangler',
      channel: 'TRQ',
    },
    source: 'Generated by GarageIQ AI from publicly available sources. Cross-checked per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Engine',
    difficulty: 'intermediate',
    duration: '1-2 hours',
    interval: 'Replace when P0440/P0455 EVAP system codes appear and gas cap tightening does not resolve.',
    description: 'The EVAP system captures fuel vapors and routes them to the engine for combustion. The canister (usually under the rear of the vehicle) and purge valve (engine bay) are common failure points. Replacement clears codes when gas cap is not the issue.',
    overview: { difficulty: 'Intermediate — multiple components', estimatedTime: '1-2 hours', estimatedCost: '$30-80 for valve and/or canister', whyMatters: 'EVAP codes fail emissions tests.', safetyNote: 'Working with fuel vapors. Disconnect battery.' },
    tools: ['Sockets', 'Pliers', 'Drain pan', 'Eye protection'],
    parts: [{ id: 'evap-purge-valve', name: 'EVAP Purge Valve', partNumber: 'Mopar 4861512AB', quantity: 1, price: 35, category: 'required', notes: 'Located in engine bay, vacuum-controlled.' }, { id: 'evap-canister', name: 'EVAP Canister (full assembly)', partNumber: 'Mopar 5083196AA', quantity: 1, price: 50, category: 'optional', notes: 'Replace if canister itself is damaged.' }],
    safetyInformation: 'Fuel vapors. Disconnect battery. Avoid ignition sources.',
    beforeYouStart: ['Tighten gas cap and clear codes first', 'Confirm code returns', 'Have replacement parts'],
    steps: [
      { number: 1, title: 'Confirm Gas Cap Is Not the Issue', description: 'Tighten gas cap to clicking. Clear EVAP code. Drive 50 miles. If returns, then proceed.', why: 'Most common P0455 cause.', tip: 'Replace gas cap if old.', commonMistake: 'Skipping this step.' },
      { number: 2, title: 'Locate EVAP Purge Valve', description: 'Engine bay, on intake manifold or near it. Has vacuum line and electrical connector.', why: 'Visual ID.', tip: 'Solenoid valve, small.', commonMistake: 'Wrong component.' },
      { number: 3, title: 'Disconnect Connector and Vacuum Lines', description: 'Press lock, unplug. Pull off vacuum lines.', why: 'Frees valve.', tip: 'Note line orientation.', commonMistake: 'Tearing lines.' },
      { number: 4, title: 'Remove Mounting Bracket', description: 'Valve held by bracket; unbolt.', why: 'Frees valve.', tip: 'Note location.', commonMistake: 'Stripping.' },
      { number: 5, title: 'Install New Purge Valve', description: 'Mount new valve in same orientation. Reconnect lines.', why: 'Replacement.', tip: 'Verify line attachment.', commonMistake: 'Reversed lines.' },
      { number: 6, title: 'If Replacing Canister: Lift Vehicle', description: 'For canister replacement, raise vehicle to access undercarriage.', why: 'Canister mounted under.', tip: 'Use jack stands.', commonMistake: 'Inadequate access.' },
      { number: 7, title: 'Disconnect Canister Lines', description: 'Multiple vapor and vacuum lines on canister. Mark each.', why: 'Multiple connections.', tip: 'Photograph.', commonMistake: 'Mixing up lines.' },
      { number: 8, title: 'Replace Canister', description: 'Unbolt mounting; install new canister; reconnect lines per photos.', why: 'Replacement.', tip: 'Same orientation.', commonMistake: 'Reversed lines.' },
      { number: 9, title: 'Reconnect Battery and Clear Codes', description: 'Standard reset.', why: 'PCM relearn.', tip: 'Drive 50+ miles for code reset.', commonMistake: 'Not driving.' },
      { number: 10, title: 'Verify No Codes Return', description: 'After 50+ miles, scan for codes.', why: 'Confirms fix.', tip: 'EVAP monitor takes time.', commonMistake: 'Calling done early.' },
    ],
    torqueSpecifications: [],
    fluidSpecifications: [],
    troubleshooting: [{ problem: 'P0455 returns after canister replacement', likelyCause: 'Loose vapor line or other leak', whatToCheck: 'Smoke test of EVAP system', fix: 'Locate and seal leak.' }],
    proTips: ['Always tighten gas cap first.', 'Smoke test reveals leaks quickly.'],
    commonMistakes: [{ mistake: 'Mixing up vapor lines', consequence: 'EVAP doesn\'t work; codes persist.' }],
    successIndicators: ['No EVAP codes after 50+ miles', 'Gas cap tightening alone wouldn\'t solve'],
    whenToSeekHelp: 'EVAP smoke testing is best done at a shop with smoke generator.',
    legalDisclaimer: 'Fuel vapors are flammable.',
    manualSource: { title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)', url: 'https://www.haynes.com/en-us/wrangler/jeep/2003', notes: 'Cross-reference EVAP system layout.' },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — KNOCK SENSOR REPLACEMENT
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-knock-sensor-replacement',
    title: 'Knock Sensor Replacement',
    vehicle: { make: 'Jeep', model: 'Wrangler', generations: [{ startYear: 1997, endYear: 2006 }], engine: '4.0l-i6' },
    source: 'Generated by GarageIQ AI from publicly available sources. Cross-checked per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Engine',
    difficulty: 'intermediate',
    duration: '30-45 minutes',
    interval: 'Replace when P0325 code appears or engine pings without explanation.',
    description: 'The knock sensor detects engine pinging and lets PCM retard timing to prevent damage. Mounted on the cylinder block (4.0L typically near cylinder #4-5 area on the side of the block).',
    overview: { difficulty: 'Intermediate — confined space access', estimatedTime: '30-45 minutes', estimatedCost: '$20-40', whyMatters: 'Knock sensor failure = PCM cannot detect knock = potential engine damage.', safetyNote: 'Disconnect battery.' },
    tools: ['Sockets, deep sockets', 'Long extensions', 'Multimeter', 'Eye protection'],
    parts: [{ id: 'knock-sensor', name: 'Knock Sensor', partNumber: 'Mopar 56028351 — equivalent: Standard KS62', quantity: 1, price: 30, category: 'required', notes: 'Verify part for application.' }],
    safetyInformation: 'Disconnect battery.',
    beforeYouStart: ['Disconnect battery', 'Confirm code P0325', 'Have new sensor'],
    steps: [
      { number: 1, title: 'Disconnect Battery', description: 'Negative cable off.', why: 'Safety.', tip: 'Standard.', commonMistake: 'Skipping.' },
      { number: 2, title: 'Locate Knock Sensor', description: 'Side of cylinder block, typically passenger side near middle. Has 1-pin connector.', why: 'Visual ID.', tip: 'May need to remove starter or other components for access.', commonMistake: 'Wrong location.' },
      { number: 3, title: 'Disconnect Connector', description: 'Press lock; unplug.', why: 'Frees electrical.', tip: 'Photograph.', commonMistake: 'Yanking.' },
      { number: 4, title: 'Remove Sensor', description: 'Sensor threads into block. Unscrew with deep socket.', why: 'Frees sensor.', tip: 'May need long extension.', commonMistake: 'Cocking.' },
      { number: 5, title: 'Install New Sensor', description: 'Hand-thread, then torque to 16 ft-lbs (22 Nm). Use thread compound if needed.', why: 'Secure mount.', tip: 'Verify torque spec.', commonMistake: 'Stripping threads.' },
      { number: 6, title: 'Reconnect Connector', description: 'Plug in.', why: 'Power.', tip: 'Click.', commonMistake: 'Partial.' },
      { number: 7, title: 'Reconnect Battery', description: 'Reconnect.', why: 'System.', tip: 'Standard.', commonMistake: 'Skipping.' },
      { number: 8, title: 'Clear Codes', description: 'Scanner.', why: 'PCM reset.', tip: 'Standard reset.', commonMistake: 'Not clearing.' },
      { number: 9, title: 'Drive and Verify', description: 'Drive 25 miles.', why: 'Confirm fix.', tip: 'Code-scan after.', commonMistake: 'Skipping.' },
      { number: 10, title: 'Listen for Knock', description: 'Verify no engine knock or ping under load.', why: 'Function check.', tip: 'Knock is audible at full throttle.', commonMistake: 'Ignoring sounds.' },
    ],
    torqueSpecifications: [{ fastener: 'Knock sensor', specification: '16 ft-lbs (22 Nm). Verify torque spec from OEM service manual.', notes: 'Standard.' }],
    fluidSpecifications: [],
    troubleshooting: [{ problem: 'Code returns', likelyCause: 'Defective sensor or wiring', whatToCheck: 'Test signal', fix: 'Warranty exchange.' }],
    proTips: ['Verify torque spec against service manual.'],
    commonMistakes: [{ mistake: 'Loose sensor', consequence: 'Faulty signal.' }],
    successIndicators: ['No P0325', 'No engine knock'],
    whenToSeekHelp: 'If knock continues with new sensor, suspect mechanical engine issue (e.g., compression, fuel quality).',
    legalDisclaimer: 'Verify with licensed manual.',
    manualSource: { title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)', url: 'https://www.haynes.com/en-us/wrangler/jeep/2003', notes: 'Cross-reference sensor location.' },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — VSS / SPEED SENSOR REPLACEMENT
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-vss-speed-sensor-replacement',
    title: 'Vehicle Speed Sensor (VSS) Replacement',
    vehicle: { make: 'Jeep', model: 'Wrangler', generations: [{ startYear: 1997, endYear: 2006 }] },
    source: 'Generated by GarageIQ AI from publicly available sources. Cross-checked per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Drivetrain',
    difficulty: 'beginner',
    duration: '20-30 minutes',
    interval: 'Replace when P0500/P0501 codes appear, speedometer is erratic or dead, or transmission shifting is rough.',
    description: 'The VSS reads vehicle speed from the transfer case output and sends signal to PCM, speedometer, and transmission. Failure causes speedometer issues, hard transmission shifts, and check engine codes.',
    overview: { difficulty: 'Beginner — direct R&R on transfer case', estimatedTime: '20-30 minutes', estimatedCost: '$25-50', whyMatters: 'VSS failure affects multiple systems.', safetyNote: 'Disconnect battery.' },
    tools: ['Sockets (10mm)', 'Drain pan', 'Eye protection'],
    parts: [{ id: 'vss-sensor', name: 'Vehicle Speed Sensor', partNumber: 'Mopar 56027902 — equivalent: Standard SC234', quantity: 1, price: 30, category: 'required', notes: 'Verify part.' }],
    safetyInformation: 'Disconnect battery. Some transfer case fluid loss.',
    beforeYouStart: ['Disconnect battery', 'Position drain pan under transfer case', 'Have new sensor'],
    steps: [
      { number: 1, title: 'Disconnect Battery', description: 'Negative cable off.', why: 'Safety.', tip: 'Standard.', commonMistake: 'Skipping.' },
      { number: 2, title: 'Locate VSS', description: 'On the transfer case output (rear). Has connector and small bolt mounting.', why: 'Visual ID.', tip: 'Underneath rear of transfer case.', commonMistake: 'Wrong location.' },
      { number: 3, title: 'Disconnect Connector', description: 'Press lock; pull off.', why: 'Frees electrical.', tip: 'Photograph.', commonMistake: 'Yanking.' },
      { number: 4, title: 'Remove Mounting Bolt', description: 'Single bolt holds sensor in place.', why: 'Frees sensor.', tip: 'Note bolt position.', commonMistake: 'Stripping.' },
      { number: 5, title: 'Pull Sensor Out', description: 'Twist and pull. Some fluid may emerge.', why: 'Removed.', tip: 'Drain pan ready.', commonMistake: 'Forcing.' },
      { number: 6, title: 'Install New Sensor', description: 'Insert new sensor. Hand-thread bolt, snug.', why: 'Replacement.', tip: 'Verify alignment.', commonMistake: 'Cocking.' },
      { number: 7, title: 'Reconnect Connector', description: 'Plug in.', why: 'Power.', tip: 'Click.', commonMistake: 'Partial.' },
      { number: 8, title: 'Top Off Transfer Case Fluid', description: 'If fluid level dropped, top off through fill plug.', why: 'Replace lost.', tip: 'Use ATF+4.', commonMistake: 'Insufficient.' },
      { number: 9, title: 'Reconnect Battery', description: 'Reconnect.', why: 'System.', tip: 'Standard.', commonMistake: 'Skipping.' },
      { number: 10, title: 'Test Drive', description: 'Drive — verify speedometer works, no codes, smooth shifting.', why: 'Confirms install.', tip: 'Code-scan after.', commonMistake: 'Skipping.' },
    ],
    torqueSpecifications: [{ fastener: 'VSS mounting bolt', specification: '13 ft-lbs (18 Nm).', notes: 'Standard.' }],
    fluidSpecifications: [{ fluid: 'Transfer case fluid', type: 'ATF+4', capacity: 'Top-off as needed', notes: 'Match existing.' }],
    troubleshooting: [{ problem: 'Speedometer still not working', likelyCause: 'Defective new sensor or wiring', whatToCheck: 'Test signal at PCM', fix: 'Warranty or wiring repair.' }],
    proTips: ['Have drain pan ready.', 'Verify no leaks at VSS area.'],
    commonMistakes: [{ mistake: 'Wrong sensor for application', consequence: 'Doesn\'t work.' }],
    successIndicators: ['Speedometer works', 'No VSS codes', 'Smooth shifting'],
    whenToSeekHelp: 'If speedometer still issues, instrument cluster may need attention.',
    legalDisclaimer: 'Verify with licensed manual.',
    manualSource: { title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)', url: 'https://www.haynes.com/en-us/wrangler/jeep/2003', notes: 'Cross-reference transfer case layout.' },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — STEERING STABILIZER (DAMPER) REPLACEMENT
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-steering-stabilizer-replacement',
    title: 'Steering Stabilizer (Damper) Replacement',
    vehicle: { make: 'Jeep', model: 'Wrangler', generations: [{ startYear: 1997, endYear: 2006 }] },
    video: {
      youtubeId: '3t3t3rJlQpg',
      title: 'DIY / HOW TO: Install Rough Country Steering Stabilizer Jeep TJ / LJ Wrangler',
      channel: 'Colorado Overland DIY',
    },
    source: 'Generated by GarageIQ AI from publicly available sources. Cross-checked per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Suspension & Steering',
    difficulty: 'beginner',
    duration: '30-45 minutes',
    interval: 'Replace when fluid leaking, no resistance to compression by hand, or after death wobble events. Note: stabilizer masks but does not cure underlying causes.',
    description: 'The steering stabilizer is a hydraulic damper that resists rapid steering motion (helps mask wobble). Mounted between drag link and axle. Failure: external leak or no internal damping resistance.',
    overview: { difficulty: 'Beginner — direct R&R', estimatedTime: '30-45 minutes', estimatedCost: '$30-100 depending on brand', whyMatters: 'Reduces vibration; supplements proper front-end repair.', safetyNote: 'Steering component — secure properly.' },
    tools: ['Sockets (15-19mm)', 'Penetrating oil', 'Eye protection'],
    parts: [{ id: 'steering-stabilizer', name: 'Steering Stabilizer', partNumber: 'Bilstein 24-006262 / Rough Country / Old Man Emu', quantity: 1, price: 60, category: 'required', notes: 'Quality matters — Bilstein is recommended.' }],
    safetyInformation: 'Steering safety-critical. Verify all fasteners after install.',
    beforeYouStart: ['Confirm wobble cause is fixed first (track bar, ball joints) — stabilizer is a supplement', 'Have new stabilizer ready', 'Apply penetrating oil to fasteners'],
    steps: [
      { number: 1, title: 'Lift Vehicle', description: 'Raise on stands.', why: 'Access.', tip: 'High enough.', commonMistake: 'Inadequate clearance.' },
      { number: 2, title: 'Locate Stabilizer', description: 'Mounted horizontally between drag link end and axle bracket.', why: 'Visual ID.', tip: 'Looks like a small shock.', commonMistake: 'Confusing with other components.' },
      { number: 3, title: 'Remove Axle-Side Bolt', description: 'Single bolt holds end to bracket. Remove with wrench.', why: 'Frees end.', tip: 'Use anti-seize on threads.', commonMistake: 'Stripping.' },
      { number: 4, title: 'Remove Drag-Link-Side Bolt', description: 'Other end bolts to drag link bracket.', why: 'Frees other end.', tip: 'Apply penetrating oil.', commonMistake: 'Forcing rusted.' },
      { number: 5, title: 'Compare Old to New', description: 'Verify length and bracket pattern match.', why: 'Wrong-application is common error.', tip: 'Match exactly.', commonMistake: 'Wrong stabilizer.' },
      { number: 6, title: 'Install New Stabilizer', description: 'Slide new stabilizer in same orientation. Hand-thread bolts.', why: 'Replacement.', tip: 'Verify orientation.', commonMistake: 'Backwards installation.' },
      { number: 7, title: 'Torque Bolts', description: 'Torque bolts to spec (typically 70 ft-lbs).', why: 'Secure.', tip: 'Anti-seize.', commonMistake: 'Cross-threading.' },
      { number: 8, title: 'Verify No Interference', description: 'Cycle steering full lock-to-lock. Stabilizer should not contact other components.', why: 'Prevents damage.', tip: 'Watch carefully during cycle.', commonMistake: 'Skipping check.' },
      { number: 9, title: 'Test Drive', description: 'Drive — steering should feel firmer with less vibration.', why: 'Confirms function.', tip: 'Drive over expansion joints.', commonMistake: 'Skipping test.' },
      { number: 10, title: 'Re-Torque After 100 Miles', description: 'Re-torque both bolts after first 100 miles.', why: 'Settles in.', tip: 'Catches loosening.', commonMistake: 'No re-torque.' },
    ],
    torqueSpecifications: [{ fastener: 'Stabilizer bolts', specification: '70 ft-lbs (95 Nm). Verify torque spec from OEM service manual.', notes: 'Cross-tighten.' }],
    fluidSpecifications: [],
    troubleshooting: [
      { problem: 'New stabilizer not effective', likelyCause: 'Underlying cause not fixed', whatToCheck: 'Re-inspect track bar, ball joints, etc.', fix: 'Address other components.' },
      { problem: 'Stabilizer leaks', likelyCause: 'Defective new unit', whatToCheck: 'Visual', fix: 'Warranty exchange.' },
    ],
    proTips: ['Stabilizer is supplement, not cure.', 'Bilstein quality matters.', 'Re-torque after 100 miles.'],
    commonMistakes: [{ mistake: 'Relying on stabilizer to fix wobble', consequence: 'Wobble returns.' }],
    successIndicators: ['Reduced vibration', 'No stabilizer leaks', 'Bolts tight'],
    whenToSeekHelp: 'If wobble persists after stabilizer + other repairs, alignment or balance may be the issue.',
    legalDisclaimer: 'Steering safety-critical.',
    manualSource: { title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)', url: 'https://www.haynes.com/en-us/wrangler/jeep/2003', notes: 'Cross-reference steering layout.' },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — STEERING GEAR BOX REPLACEMENT
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-steering-gear-box-replacement',
    title: 'Steering Gear Box Replacement',
    vehicle: { make: 'Jeep', model: 'Wrangler', generations: [{ startYear: 1997, endYear: 2006 }] },
    video: {
      youtubeId: '6bBYBP8uxYk',
      title: 'How To Replace The Power Steering Gear Box On A Jeep Wrangler TJ',
      channel: 'How to Automotive',
    },
    source: 'Generated by GarageIQ AI from publicly available sources. Cross-checked per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Suspension & Steering',
    difficulty: 'advanced',
    duration: '3-4 hours',
    interval: 'Replace when gearbox leaks fluid, has internal slop (steering wheel turns without front wheels turning), or pitman arm is loose on output shaft.',
    description: 'The TJ uses a recirculating-ball steering gear box (not rack-and-pinion). Failure modes: leaking fluid, internal slop (worn input shaft / sector), or external damage. Replacement is involved — heavy component, multiple connections.',
    overview: { difficulty: 'Advanced — heavy gearbox; pitman arm puller required', estimatedTime: '3-4 hours', estimatedCost: '$200-400 for gear box + $20-30 for pitman puller rental', whyMatters: 'Failed gear box causes vague steering and is safety-critical.', safetyNote: 'Heavy component — get help. Pitman arm under pressure when removed.' },
    tools: ['Floor jack and jack stands', 'Sockets (large — 36mm for pitman nut)', 'Pitman arm puller', 'Drain pan', 'Power steering fluid', 'Penetrating oil', 'Eye protection'],
    parts: [
      { id: 'steering-gear-box', name: 'Steering Gear Box', partNumber: 'Mopar 52106293AB — aftermarket: PSC SG-851 / Borgeson', quantity: 1, price: 280, category: 'required', notes: 'Aftermarket performance gear boxes available for upgraded handling.' },
      { id: 'power-steering-fluid', name: 'Power Steering Fluid', partNumber: 'Mopar MS-9602 / ATF+4', quantity: 1, price: 8, category: 'required', notes: 'TJ uses ATF+4 as power steering fluid.' },
    ],
    safetyInformation: 'Heavy component. Get help. Steering safety-critical. Verify all torques.',
    beforeYouStart: ['Have new gear box, fluid, and pitman puller staged', 'Apply penetrating oil to pitman arm castle nut day before', 'Position drain pan'],
    steps: [
      { number: 1, title: 'Lift Vehicle and Drain Power Steering', description: 'Raise vehicle on stands. Drain power steering fluid from reservoir using pump or syringe.', why: 'Reduces spillage during disconnect.', tip: 'A turkey baster pulls most fluid.', commonMistake: 'Skipping drain.' },
      { number: 2, title: 'Disconnect Power Steering Lines', description: 'Two hydraulic lines connect to gear box. Use flare wrench. Cap to prevent contamination.', why: 'Frees gear box.', tip: 'Drips will occur.', commonMistake: 'Open lines causing contamination.' },
      { number: 3, title: 'Disconnect Steering Shaft from Gear Box', description: 'A coupler joins the steering shaft to the gear input shaft. Single bolt.', why: 'Frees input shaft.', tip: 'Note alignment.', commonMistake: 'Forcing.' },
      { number: 4, title: 'Remove Pitman Arm from Output Shaft', description: 'Large castle nut and cotter pin hold pitman arm to gear box output shaft. Remove cotter, then 36mm nut. Use pitman arm puller to draw arm off the splined shaft.', why: 'Frees pitman arm.', tip: 'Penetrating oil overnight.', commonMistake: 'Hammering pitman arm. Damages.' },
      { number: 5, title: 'Unbolt Gear Box from Frame', description: 'Three bolts hold gear box to frame rail. Remove. Heavy — support gear box during removal.', why: 'Frees gear box from frame.', tip: 'Get helper.', commonMistake: 'Dropping gear box.' },
      { number: 6, title: 'Lift Gear Box Off and Compare to New', description: 'Lift old gear box out. Compare to new unit — input shaft length, output shaft splines, mounting holes match.', why: 'Confirm correct part.', tip: 'Photograph if differences.', commonMistake: 'Wrong-application gear box.' },
      { number: 7, title: 'Install New Gear Box', description: 'Position new gear box. Hand-thread frame bolts. Torque to spec (typically 95-100 ft-lbs).', why: 'Replacement.', tip: 'Use anti-seize.', commonMistake: 'Cross-threading.' },
      { number: 8, title: 'Reinstall Pitman Arm', description: 'Slide pitman arm onto output splines (alignment matters). Install nut. Torque to 185 ft-lbs and install new cotter pin.', why: 'Restoration.', tip: 'New cotter pin.', commonMistake: 'Reusing cotter.' },
      { number: 9, title: 'Reconnect Power Steering Lines', description: 'Reconnect lines with new sealing washers. Torque flare nuts to spec.', why: 'Restores hydraulics.', tip: 'Hand-thread first.', commonMistake: 'Cross-threading.' },
      { number: 10, title: 'Refill Fluid, Bleed System, and Test', description: 'Refill power steering reservoir. Start engine and turn steering full lock-to-lock 5-10 times to bleed air. Top off reservoir. Test drive.', why: 'System operation.', tip: 'Whining = air in system; continue bleeding.', commonMistake: 'Skipping bleed.' },
    ],
    torqueSpecifications: [
      { fastener: 'Gear box-to-frame bolts', specification: '95-100 ft-lbs (129-136 Nm). Verify torque spec from OEM service manual.', notes: 'Cross-tighten.' },
      { fastener: 'Pitman arm castle nut', specification: '185 ft-lbs (251 Nm). Verify torque spec from OEM service manual.', notes: 'New cotter pin.' },
      { fastener: 'Power steering hydraulic lines', specification: '20 ft-lbs (27 Nm).', notes: 'Use new sealing washers.' },
    ],
    fluidSpecifications: [{ fluid: 'Power steering fluid', type: 'ATF+4', capacity: '~1.5 quarts', notes: 'Bleed system after fill.' }],
    troubleshooting: [
      { problem: 'Steering whines', likelyCause: 'Air in system', whatToCheck: 'Bleed system more', fix: 'Continue cycling steering lock-to-lock.' },
      { problem: 'Fluid leaks at gear box', likelyCause: 'Fitting not torqued or sealing washer issue', whatToCheck: 'Visual', fix: 'Re-torque or new washer.' },
    ],
    proTips: ['Aftermarket performance gear boxes (PSC, Borgeson) tighten steering response.', 'Always replace cotter pin.', 'Bleed system thoroughly — air causes whine.'],
    commonMistakes: [{ mistake: 'Hammering pitman arm', consequence: 'Damages steering shaft.' }],
    successIndicators: ['No fluid leaks', 'Smooth steering with no slop', 'No whining'],
    whenToSeekHelp: 'If pitman arm seized to shaft, professional gear-box service may be required. Some shops will install customer-supplied gear box at reasonable cost.',
    legalDisclaimer: 'Steering safety-critical. Verify all torques.',
    manualSource: { title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)', url: 'https://www.haynes.com/en-us/wrangler/jeep/2003', notes: 'Cross-reference torque specs.' },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — PITMAN ARM REPLACEMENT
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-pitman-arm-replacement',
    title: 'Pitman Arm Replacement',
    vehicle: { make: 'Jeep', model: 'Wrangler', generations: [{ startYear: 1997, endYear: 2006 }] },
    video: {
      youtubeId: 'MNdoqbzIvBY',
      title: 'Jeep Pitman Arm Removal',
      channel: 'Homeowner Repair',
    },
    source: 'Generated by GarageIQ AI from publicly available sources. Cross-checked per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Suspension & Steering',
    difficulty: 'intermediate',
    duration: '1 hour',
    interval: 'Replace when pitman arm is bent, ball end is worn, or for lift kit installation requiring drop pitman.',
    description: 'The pitman arm transfers gear box output to the drag link. Made of forged steel; failure is rare but possible from impact or wear. Lift kits often require a drop pitman arm to maintain steering geometry.',
    overview: { difficulty: 'Intermediate — pitman puller required', estimatedTime: '1 hour', estimatedCost: '$50-120', whyMatters: 'Bent pitman = bad steering geometry.', safetyNote: 'Steering safety-critical.' },
    tools: ['Sockets', '36mm wrench (pitman nut)', 'Pitman arm puller', 'Pliers (for cotter)', 'Eye protection'],
    parts: [{ id: 'pitman-arm', name: 'Pitman Arm', partNumber: 'Mopar 52037900 — equivalent: Crown 52037900', quantity: 1, price: 70, category: 'required', notes: 'Drop pitmans for lifted TJs.' }, { id: 'pitman-cotter-pin', name: 'Cotter Pin', partNumber: 'Standard 1/8" cotter pin', quantity: 1, price: 1, category: 'required', notes: 'Always replace.' }],
    safetyInformation: 'Steering safety-critical.',
    beforeYouStart: ['Apply penetrating oil to nut overnight', 'Have pitman puller available'],
    steps: [
      { number: 1, title: 'Lift Vehicle', description: 'Raise on stands.', why: 'Access.', tip: 'High enough.', commonMistake: 'Inadequate clearance.' },
      { number: 2, title: 'Remove Drag Link from Pitman Arm', description: 'Cotter pin and castle nut. Drag link end pulls free.', why: 'Frees end.', tip: 'New cotter for reinstall.', commonMistake: 'Reusing cotter.' },
      { number: 3, title: 'Remove Pitman Arm Castle Nut', description: '36mm nut on output shaft. Apply penetrating oil; use breaker bar.', why: 'Frees pitman.', tip: 'Significant torque required.', commonMistake: 'Stripping nut.' },
      { number: 4, title: 'Use Pitman Puller', description: 'Apply pitman puller. Tighten slowly to draw arm off splines.', why: 'Splined fit requires puller.', tip: 'Patience — gradual tension.', commonMistake: 'Hammering.' },
      { number: 5, title: 'Compare Old to New', description: 'Verify dimensions match.', why: 'Wrong part doesn\'t fit.', tip: 'Compare lengths.', commonMistake: 'Wrong application.' },
      { number: 6, title: 'Install New Pitman Arm', description: 'Slide onto splines. Verify alignment marks if present.', why: 'Replacement.', tip: 'Hand-press first.', commonMistake: 'Cocked install.' },
      { number: 7, title: 'Torque Castle Nut', description: '185 ft-lbs (251 Nm). Install new cotter pin.', why: 'Secure mount.', tip: 'New cotter is non-negotiable.', commonMistake: 'Reusing cotter.' },
      { number: 8, title: 'Reinstall Drag Link', description: 'Reconnect drag link to pitman with castle nut and new cotter pin.', why: 'Restoration.', tip: 'Standard.', commonMistake: 'Old cotter.' },
      { number: 9, title: 'Test Steering', description: 'Cycle steering lock-to-lock. Check for proper alignment.', why: 'Function check.', tip: 'Visual confirms.', commonMistake: 'Skipping.' },
      { number: 10, title: 'Test Drive and Get Alignment', description: 'Drive carefully. Get front-end alignment.', why: 'Steering geometry changed.', tip: 'Alignment after pitman work is essential.', commonMistake: 'Skipping alignment.' },
    ],
    torqueSpecifications: [{ fastener: 'Pitman castle nut', specification: '185 ft-lbs (251 Nm).', notes: 'New cotter pin.' }, { fastener: 'Drag link castle nut', specification: '60 ft-lbs (81 Nm).', notes: 'New cotter pin.' }],
    fluidSpecifications: [],
    troubleshooting: [{ problem: 'Steering pulls or wanders', likelyCause: 'Alignment off or wrong-length pitman', whatToCheck: 'Get alignment', fix: 'Alignment service.' }],
    proTips: ['Get alignment after pitman work.', 'Drop pitmans for lifted TJs.', 'Always replace cotter pins.'],
    commonMistakes: [{ mistake: 'Hammering off pitman without puller', consequence: 'Damages steering shaft.' }],
    successIndicators: ['Smooth steering', 'Aligned front end', 'No play in pitman'],
    whenToSeekHelp: 'If gearbox shaft is damaged during pitman removal, professional gear box service needed.',
    legalDisclaimer: 'Steering safety-critical.',
    manualSource: { title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)', url: 'https://www.haynes.com/en-us/wrangler/jeep/2003', notes: 'Cross-reference steering layout.' },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — DRAG LINK REPLACEMENT
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-drag-link-replacement',
    title: 'Drag Link Replacement',
    vehicle: { make: 'Jeep', model: 'Wrangler', generations: [{ startYear: 1997, endYear: 2006 }] },
    video: {
      youtubeId: 'jCZt-zLXuKM',
      title: 'DIY Jeep Wrangler Drag Link Replacement',
      channel: 'Full Range 4x4',
    },
    source: 'Generated by GarageIQ AI from publicly available sources. Cross-checked per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Suspension & Steering',
    difficulty: 'intermediate',
    duration: '1 hour',
    interval: 'Replace when ends are worn (visible play, torn boots), bent, or as part of comprehensive front-end refresh.',
    description: 'The drag link connects the pitman arm to the steering knuckle. Has tie rod ends at each end that can wear independently. Replace when ends are loose or boots torn.',
    overview: { difficulty: 'Intermediate — multiple end fasteners', estimatedTime: '1 hour', estimatedCost: '$60-120', whyMatters: 'Worn drag link causes wandering and contributes to wobble.', safetyNote: 'Steering safety-critical.' },
    tools: ['Sockets', 'Pliers', 'Tie rod end puller (or pickle fork)', 'Eye protection'],
    parts: [{ id: 'drag-link', name: 'Drag Link', partNumber: 'Mopar 52037797 — equivalent: Crown 52037797', quantity: 1, price: 80, category: 'required', notes: 'Buy complete drag link.' }, { id: 'drag-link-cotters', name: 'Cotter Pins', partNumber: '1/8" cotter pins', quantity: 2, price: 1, category: 'required', notes: 'New cotters required.' }],
    safetyInformation: 'Steering safety-critical.',
    beforeYouStart: ['Apply penetrating oil to fasteners', 'Have new drag link and cotters'],
    steps: [
      { number: 1, title: 'Lift Vehicle', description: 'Raise on stands.', why: 'Access.', tip: 'Standard.', commonMistake: 'Inadequate.' },
      { number: 2, title: 'Disconnect Drag Link from Pitman Arm', description: 'Cotter pin, castle nut, then separate end with puller or pickle fork.', why: 'Frees end.', tip: 'Penetrating oil overnight.', commonMistake: 'Hammering directly.' },
      { number: 3, title: 'Disconnect Drag Link from Steering Knuckle', description: 'Same procedure on other end.', why: 'Frees other end.', tip: 'Note orientation.', commonMistake: 'Forcing.' },
      { number: 4, title: 'Remove Old Drag Link', description: 'Lift away.', why: 'Removed.', tip: 'Compare to new.', commonMistake: 'Different length.' },
      { number: 5, title: 'Install New Drag Link', description: 'Position in same orientation. Hand-thread castle nuts.', why: 'Replacement.', tip: 'Verify match.', commonMistake: 'Reverse orientation.' },
      { number: 6, title: 'Torque Castle Nuts', description: '60 ft-lbs at each end. Install new cotter pins.', why: 'Secure.', tip: 'Standard.', commonMistake: 'Reusing cotter.' },
      { number: 7, title: 'Verify Adjustment if Adjustable', description: 'Some drag links are adjustable. Verify length matches old.', why: 'Steering geometry.', tip: 'Lock nut after adjustment.', commonMistake: 'Wrong length.' },
      { number: 8, title: 'Test Steering', description: 'Cycle full lock-to-lock.', why: 'Function check.', tip: 'No binding.', commonMistake: 'Skipping.' },
      { number: 9, title: 'Test Drive Carefully', description: 'Drive carefully at low speeds.', why: 'Verify.', tip: 'Build up speed gradually.', commonMistake: 'Aggressive driving.' },
      { number: 10, title: 'Get Alignment', description: 'Schedule front-end alignment.', why: 'Steering geometry changed.', tip: 'Required after drag link work.', commonMistake: 'Skipping.' },
    ],
    torqueSpecifications: [{ fastener: 'Drag link castle nuts', specification: '60 ft-lbs (81 Nm).', notes: 'New cotter pins.' }],
    fluidSpecifications: [],
    troubleshooting: [{ problem: 'Steering wanders', likelyCause: 'Alignment off', whatToCheck: 'Alignment', fix: 'Get alignment.' }],
    proTips: ['Get alignment after drag link work.', 'Replace tie rod ends if rebuilding rather than buying complete.', 'New cotters mandatory.'],
    commonMistakes: [{ mistake: 'Skipping alignment', consequence: 'Pulls and tire wear.' }],
    successIndicators: ['Smooth steering', 'No play', 'Aligned'],
    whenToSeekHelp: 'If alignment cannot be set, suspect bent component.',
    legalDisclaimer: 'Steering safety-critical.',
    manualSource: { title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)', url: 'https://www.haynes.com/en-us/wrangler/jeep/2003', notes: 'Cross-reference layout.' },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — POWER STEERING HOSE / PUMP REBUILD
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-power-steering-hose-pump',
    title: 'Power Steering Hose and Pump Service',
    vehicle: { make: 'Jeep', model: 'Wrangler', generations: [{ startYear: 1997, endYear: 2006 }] },
    video: {
      youtubeId: 'swJkqBk1-4U',
      title: 'Jeep Cherokee XJ and TJ 4.0L Power Steering Pump Replacement',
      channel: 'Mark',
    },
    source: 'Generated by GarageIQ AI from publicly available sources. Cross-checked per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Suspension & Steering',
    difficulty: 'intermediate',
    duration: '2-3 hours',
    interval: 'Replace pump when whining persists despite fluid; replace hoses when leaking or aged 15+ years.',
    description: 'The power steering pump and high-pressure hoses fail with age. Pump whining can mean low fluid, air, or pump itself. Hoses crack and leak with age.',
    overview: { difficulty: 'Intermediate — multiple hydraulic connections', estimatedTime: '2-3 hours', estimatedCost: '$80-160 pump; $30-80 hoses', whyMatters: 'Power steering enables comfortable steering.', safetyNote: 'Hydraulic pressure system. Drain before opening.' },
    tools: ['Sockets', 'Flare nut wrenches', 'Drain pan', 'Power steering fluid', 'Belt tool', 'Eye protection'],
    parts: [{ id: 'power-steering-pump', name: 'Power Steering Pump', partNumber: 'Mopar 52088313AC — equivalent: Cardone 21-5328', quantity: 1, price: 130, category: 'conditional', notes: 'Replace when pump-internal failure.' }, { id: 'power-steering-hoses', name: 'Power Steering Hoses', partNumber: 'Mopar 52088282 / 52088283 (high/low pressure)', quantity: 2, price: 50, category: 'conditional', notes: 'Replace if leaking.' }, { id: 'power-steering-fluid-2', name: 'Power Steering Fluid', partNumber: 'ATF+4', quantity: 1, price: 8, category: 'required', notes: 'TJ uses ATF+4.' }],
    safetyInformation: 'Hydraulic pressure. Drain before opening. Hot fluid possible.',
    beforeYouStart: ['Drain power steering reservoir', 'Have new pump or hoses staged', 'Position drain pan'],
    steps: [
      { number: 1, title: 'Drain Reservoir', description: 'Use turkey baster or pump to remove fluid from reservoir.', why: 'Reduces spillage.', tip: 'Most fluid will drain when system disconnected.', commonMistake: 'Skipping drain.' },
      { number: 2, title: 'Disconnect Power Steering Hoses', description: 'Two lines: high-pressure (output) and low-pressure (return). Use flare wrench.', why: 'Frees hydraulic system.', tip: 'Cap lines.', commonMistake: 'Cross-threading.' },
      { number: 3, title: 'Loosen Pump Mounting', description: 'Pump mounted on bracket. Loosen tensioner if applicable. Remove drive belt.', why: 'Frees pump.', tip: 'Mark belt routing.', commonMistake: 'Forgetting routing.' },
      { number: 4, title: 'Remove Pump Mounting Bolts', description: 'Three or four bolts hold pump. Remove.', why: 'Frees pump.', tip: 'Note bolts.', commonMistake: 'Stripping.' },
      { number: 5, title: 'Lift Pump Off', description: 'Pump comes off — heavy. Get help if needed.', why: 'Removed.', tip: 'Compare to new.', commonMistake: 'Dropping.' },
      { number: 6, title: 'Install New Pump', description: 'Position new pump. Hand-thread bolts. Cross-tighten and torque.', why: 'Replacement.', tip: 'Pulley alignment.', commonMistake: 'Cocked install.' },
      { number: 7, title: 'Reconnect Hoses', description: 'Reconnect hoses with new sealing washers if applicable.', why: 'Restores hydraulic system.', tip: 'Hand-thread first.', commonMistake: 'Cross-threading.' },
      { number: 8, title: 'Reinstall Belt and Tensioner', description: 'Slide belt onto pulleys per routing. Tighten tensioner.', why: 'Drive restored.', tip: 'Verify proper tension.', commonMistake: 'Wrong tension.' },
      { number: 9, title: 'Refill Fluid and Bleed System', description: 'Fill reservoir. Start engine. Cycle steering full lock-to-lock 5-10 times. Top off as fluid level drops with bleed.', why: 'Removes air.', tip: 'Whining stops when bled.', commonMistake: 'Skipping bleed.' },
      { number: 10, title: 'Test and Verify', description: 'Drive — steering should be smooth and quiet. Check for leaks.', why: 'Confirms install.', tip: 'Re-check fluid level after drive.', commonMistake: 'Skipping leak check.' },
    ],
    torqueSpecifications: [
      { fastener: 'Pump mounting bolts', specification: '23 ft-lbs (31 Nm).', notes: 'Cross-tighten.' },
      { fastener: 'Hydraulic line fittings', specification: '20 ft-lbs (27 Nm).', notes: 'Use flare wrench.' },
    ],
    fluidSpecifications: [{ fluid: 'Power steering fluid', type: 'ATF+4', capacity: '~1.5 quarts', notes: 'Bleed thoroughly.' }],
    troubleshooting: [
      { problem: 'Whining persists', likelyCause: 'Air in system', whatToCheck: 'Continue bleeding', fix: 'Cycle steering until quiet.' },
      { problem: 'Leak at pump', likelyCause: 'Loose fitting or seal', whatToCheck: 'Re-torque', fix: 'Tighten or replace seal.' },
    ],
    proTips: ['ATF+4 is correct fluid.', 'Bleed thoroughly to eliminate whine.', 'Replace hoses with pump if old.'],
    commonMistakes: [{ mistake: 'Wrong fluid type', consequence: 'Damages seals.' }],
    successIndicators: ['Quiet steering', 'No leaks', 'Smooth assist'],
    whenToSeekHelp: 'If pump replaced and whine persists despite proper bleed, suspect rack/gear box.',
    legalDisclaimer: 'Hydraulic pressure system.',
    manualSource: { title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)', url: 'https://www.haynes.com/en-us/wrangler/jeep/2003', notes: 'Cross-reference power steering layout.' },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — BRAKE BOOSTER REPLACEMENT
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-brake-booster-replacement',
    title: 'Brake Booster Replacement',
    vehicle: { make: 'Jeep', model: 'Wrangler', generations: [{ startYear: 1997, endYear: 2006 }] },
    video: {
      youtubeId: '946dCyFGjeA',
      title: 'JEEP TJ HOW TO REPLACE BRAKE BOOSTER',
      channel: 'BSK Garage',
    },
    source: 'Generated by GarageIQ AI from publicly available sources. Cross-checked per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Brakes & Steering',
    difficulty: 'advanced',
    duration: '2-3 hours',
    interval: 'Replace when brake pedal becomes very hard (no power assist), brake booster has internal vacuum leak (engine idles rough or pedal causes idle change), or external damage.',
    description: 'The brake booster is mounted to the firewall and uses engine vacuum to multiply pedal force. Failed boosters cause hard pedal effort. Replacement is involved due to firewall mounting.',
    overview: { difficulty: 'Advanced — firewall work, master cylinder R&R required', estimatedTime: '2-3 hours', estimatedCost: '$80-180', whyMatters: 'No power assist makes braking dangerous.', safetyNote: 'Brake system safety-critical. Bleed thoroughly.' },
    tools: ['Sockets (10-15mm)', 'Master cylinder remover (with line caps)', 'Trim removal tool', 'Eye protection'],
    parts: [{ id: 'brake-booster', name: 'Brake Booster', partNumber: 'Mopar 5083258AA — equivalent: Cardone 53-9050', quantity: 1, price: 130, category: 'required', notes: 'Verify part for application year.' }, { id: 'brake-booster-gasket', name: 'Booster Gasket', partNumber: 'Mopar 4882566', quantity: 1, price: 4, category: 'required', notes: 'Replace.' }],
    safetyInformation: 'Brake safety-critical. Bleed system thoroughly after.',
    beforeYouStart: ['Disconnect battery', 'Have new booster, master cylinder service supplies'],
    steps: [
      { number: 1, title: 'Disconnect Battery', description: 'Negative cable off.', why: 'Safety.', tip: 'Standard.', commonMistake: 'Skipping.' },
      { number: 2, title: 'Remove Master Cylinder from Booster', description: 'Per Master Cylinder R&R guide — remove master.', why: 'Booster behind master.', tip: 'Cap brake lines.', commonMistake: 'Spilling fluid.' },
      { number: 3, title: 'Disconnect Vacuum Hose', description: 'Pull vacuum hose off check valve on booster.', why: 'Frees vacuum.', tip: 'Inspect hose for cracks.', commonMistake: 'Tearing hose.' },
      { number: 4, title: 'Disconnect Pushrod from Pedal', description: 'In cabin, remove kick panel. Disconnect booster pushrod from clutch pedal arm.', why: 'Frees mechanical.', tip: 'Photograph orientation.', commonMistake: 'Not finding clip.' },
      { number: 5, title: 'Unbolt Booster from Firewall', description: 'Four nuts hold booster to firewall studs.', why: 'Frees booster.', tip: 'Long extensions help.', commonMistake: 'Stripping.' },
      { number: 6, title: 'Pull Booster Out', description: 'Booster pulls forward through firewall hole.', why: 'Removed.', tip: 'May need finagling for clearance.', commonMistake: 'Forcing.' },
      { number: 7, title: 'Install New Booster', description: 'Install new gasket on firewall side. Position new booster. Hand-thread firewall nuts.', why: 'Replacement.', tip: 'Match orientation.', commonMistake: 'Reverse install.' },
      { number: 8, title: 'Torque Mounting Nuts', description: 'Torque to spec (typically 22 ft-lbs / 30 Nm).', why: 'Secure.', tip: 'Cross-tighten.', commonMistake: 'Cross-threading.' },
      { number: 9, title: 'Reconnect Pedal, Vacuum, Master Cylinder', description: 'Reconnect pushrod to pedal. Reconnect vacuum. Reinstall master cylinder per Master Cylinder guide.', why: 'Restoration.', tip: 'Verify all connections.', commonMistake: 'Forgetting connection.' },
      { number: 10, title: 'Bleed and Test', description: 'Bleed brake system thoroughly. Test pedal — should be firm with power assist when engine running. Test drive in empty lot before traffic.', why: 'Safety verification.', tip: 'Critical step.', commonMistake: 'Skipping bleed.' },
    ],
    torqueSpecifications: [{ fastener: 'Booster mounting nuts', specification: '22 ft-lbs (30 Nm).', notes: 'Cross-tighten.' }],
    fluidSpecifications: [{ fluid: 'Brake fluid', type: 'DOT 3', capacity: '~32 oz for system bleed', notes: 'Fresh fluid.' }],
    troubleshooting: [
      { problem: 'Pedal still hard', likelyCause: 'Vacuum hose leak or check valve fail', whatToCheck: 'Vacuum at booster', fix: 'Replace hose or check valve.' },
      { problem: 'Engine idle rough after install', likelyCause: 'Vacuum hose leak', whatToCheck: 'Inspect connection', fix: 'Re-seat or replace.' },
    ],
    proTips: ['Replace vacuum hose with booster.', 'Bleed system thoroughly.', 'Empty-lot test before traffic.'],
    commonMistakes: [{ mistake: 'Skipping bleed', consequence: 'Soft pedal; dangerous.' }],
    successIndicators: ['Firm pedal with engine running', 'No vacuum leak', 'Bleeding complete'],
    whenToSeekHelp: 'Pedal-firmness work safety-critical. If unfamiliar, professional service is safer.',
    legalDisclaimer: 'Brake safety-critical. Verify procedures.',
    manualSource: { title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)', url: 'https://www.haynes.com/en-us/wrangler/jeep/2003', notes: 'Cross-reference brake system layout.' },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — ABS MODULE BLEED / REPLACEMENT
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-abs-module-bleed-replacement',
    title: 'ABS Module Bleed and Service',
    vehicle: { make: 'Jeep', model: 'Wrangler', generations: [{ startYear: 1997, endYear: 2006 }] },
    video: {
      youtubeId: 'a8Xf4IaFPRI',
      title: 'How to Bleed Brakes 1997-2006 Jeep Wrangler',
      channel: 'TRQ',
    },
    source: 'Generated by GarageIQ AI from publicly available sources. Cross-checked per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Brakes & Steering',
    difficulty: 'intermediate',
    duration: '1-2 hours diagnostic; service requires shop scan tool',
    interval: 'Service ABS when ABS warning light on, brakes pulse without skidding, or after major brake work that introduced air to ABS module.',
    description: 'TJs equipped with ABS have a hydraulic control unit (HCU) that meters brake pressure. Air can enter during normal brake service and require scan-tool bleed to remove. Some modules also fail electrically.',
    overview: { difficulty: 'Intermediate — diagnostic; full service requires shop tool', estimatedTime: '1-2 hours diagnostic', estimatedCost: '$50-100 for shop ABS bleed; $300-600 for module replacement', whyMatters: 'Failed ABS reduces emergency stopping capability.', safetyNote: 'Brake safety-critical.' },
    tools: ['OBD-II scanner with ABS capability', 'Brake bleeder kit', 'Multimeter', 'Eye protection'],
    parts: [{ id: 'abs-module', name: 'ABS Hydraulic Control Unit', partNumber: 'Mopar varies by year', quantity: 1, price: 400, category: 'conditional', notes: 'Replace only if failed.' }, { id: 'brake-fluid-abs', name: 'DOT 3 Brake Fluid', partNumber: 'DOT 3', quantity: 1, price: 8, category: 'required', notes: 'Fresh fluid.' }],
    safetyInformation: 'Brake safety-critical.',
    beforeYouStart: ['Code-scan for ABS codes (C-codes)', 'Confirm ABS issue before module replacement'],
    steps: [
      { number: 1, title: 'Read ABS Codes', description: 'Use scanner with ABS capability. Note any C-codes (chassis codes).', why: 'Identifies ABS issue.', tip: 'Free ABS scans at some parts stores.', commonMistake: 'Skipping scan.' },
      { number: 2, title: 'Verify Brake Fluid Level and Quality', description: 'Top off if low. Replace if dark/contaminated.', why: 'Foundation.', tip: 'Old fluid contributes to module issues.', commonMistake: 'Skipping basics.' },
      { number: 3, title: 'Try Standard Manual Bleed First', description: 'Bleed all four corners in standard sequence (passenger rear, driver rear, passenger front, driver front).', why: 'Sometimes manual bleed clears air.', tip: 'See brake bleeding guide.', commonMistake: 'Going to ABS bleed first.' },
      { number: 4, title: 'Test Pedal After Manual Bleed', description: 'If pedal firm = solved. If still soft, ABS module bleed needed.', why: 'Determines next step.', tip: 'Static hold test.', commonMistake: 'Calling done without test.' },
      { number: 5, title: 'Schedule Shop ABS Bleed', description: 'Take vehicle to shop with scan tool. They activate ABS valves while bleeding to release trapped air.', why: 'Required for ABS-specific air.', tip: '$50-80 typically.', commonMistake: 'Trying without proper tool.' },
      { number: 6, title: 'Verify ABS Function', description: 'After bleed, ABS warning should clear. Test ABS by panic stop on loose surface — should pulse and remain controllable.', why: 'Confirms function.', tip: 'Empty parking lot.', commonMistake: 'Skipping test.' },
      { number: 7, title: 'If Module Itself Failed', description: 'If ABS warning persists with proper bleeding, module may be failed (electrical or hydraulic). Replacement is involved.', why: 'Module failure scenario.', tip: 'Verify with shop diagnosis.', commonMistake: 'Replacing module without diagnosis.' },
      { number: 8, title: 'Module Replacement (If Applicable)', description: 'Disconnect battery. Remove module mounting bolts. Disconnect electrical and brake lines.', why: 'For replacement.', tip: 'Cap lines.', commonMistake: 'Bending lines.' },
      { number: 9, title: 'Install New Module', description: 'Reverse — mount module, connect lines, electrical.', why: 'Replacement.', tip: 'Verify orientation.', commonMistake: 'Crossed lines.' },
      { number: 10, title: 'Bleed and Verify with Scan Tool', description: 'Take to shop for ABS bleed and module reset.', why: 'Required after install.', tip: 'New module needs initialization.', commonMistake: 'Skipping shop step.' },
    ],
    torqueSpecifications: [{ fastener: 'ABS module mounting bolts', specification: '15 ft-lbs (20 Nm).', notes: 'Standard.' }],
    fluidSpecifications: [{ fluid: 'Brake fluid', type: 'DOT 3', capacity: '~32 oz', notes: 'Fresh.' }],
    troubleshooting: [{ problem: 'ABS light stays on', likelyCause: 'Failed module or wiring', whatToCheck: 'Diagnostic scan', fix: 'Module replacement or wiring repair.' }],
    proTips: ['Try manual bleed first.', 'Shop ABS bleed is $50-80.', 'Module rarely fails — usually air or wiring.'],
    commonMistakes: [{ mistake: 'Replacing module without diagnosis', consequence: 'Wasted $400+.' }],
    successIndicators: ['ABS warning off', 'Firm pedal', 'ABS engages on panic stop'],
    whenToSeekHelp: 'ABS work usually requires shop scan tool.',
    legalDisclaimer: 'Brake safety-critical.',
    manualSource: { title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)', url: 'https://www.haynes.com/en-us/wrangler/jeep/2003', notes: 'Cross-reference ABS layout.' },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — REAR WHEEL CYLINDER REPLACEMENT
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-rear-wheel-cylinder-replacement',
    title: 'Rear Wheel Cylinder Replacement',
    vehicle: { make: 'Jeep', model: 'Wrangler', generations: [{ startYear: 1997, endYear: 2006 }] },
    video: {
      youtubeId: 'nh0awGDLQsc',
      title: 'How to Replace Rear Wheel Cylinders 1997-2006 Jeep Wrangler',
      channel: 'TRQ',
    },
    source: 'Generated by GarageIQ AI from publicly available sources. Cross-checked per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Brakes & Steering',
    difficulty: 'intermediate',
    duration: '1-1.5 hours per side',
    interval: 'Replace when wheel cylinder leaks (fluid behind dust boot or behind drum), or when piston seizes.',
    description: 'The rear wheel cylinder pushes brake shoes apart against the drum. Failure: external leak from boot, internal piston seize. Replacement requires drum removal.',
    overview: { difficulty: 'Intermediate — drum brake R&R required', estimatedTime: '1-1.5 hours per side', estimatedCost: '$25-40 per cylinder + brake fluid', whyMatters: 'Leaking cylinder contaminates new shoes.', safetyNote: 'Brake safety-critical.' },
    tools: ['Sockets (13mm, 19mm)', 'Brake spring tool', 'Hold-down spring tool', 'Brake bleeder kit', 'Drain pan', 'Brake cleaner', 'Eye protection'],
    parts: [{ id: 'wheel-cylinder', name: 'Rear Wheel Cylinder', partNumber: 'Mopar 5083222AA — equivalent: Centric 134.66005', quantity: 1, price: 30, category: 'required', notes: 'Replace in pairs (both sides) ideally.' }, { id: 'brake-fluid-cyl', name: 'DOT 3 Brake Fluid', partNumber: 'DOT 3', quantity: 1, price: 8, category: 'required', notes: 'Fresh.' }],
    safetyInformation: 'Brake safety-critical. Bleed thoroughly.',
    beforeYouStart: ['Have new cylinder and fresh brake fluid', 'Lift vehicle and remove drum'],
    steps: [
      { number: 1, title: 'Lift Vehicle and Remove Wheel/Drum', description: 'Per Rear Drum Brake guide — lift, remove wheel, remove drum.', why: 'Access.', tip: 'See drum brake guide.', commonMistake: 'Forcing drum.' },
      { number: 2, title: 'Disconnect Brake Line at Cylinder', description: 'Use flare-nut wrench. Cap line.', why: 'Frees hydraulic.', tip: 'Have rag ready.', commonMistake: 'Cross-threading.' },
      { number: 3, title: 'Remove Brake Shoes Around Cylinder', description: 'Per Rear Drum Brake guide — disassemble shoe assembly.', why: 'Access to cylinder bolts.', tip: 'Photograph spring layout.', commonMistake: 'Disassembling without photos.' },
      { number: 4, title: 'Remove Cylinder Mounting Bolts', description: 'Two 13mm bolts hold cylinder to backing plate. Remove.', why: 'Frees cylinder.', tip: 'Note bolt locations.', commonMistake: 'Stripping.' },
      { number: 5, title: 'Remove Old Cylinder', description: 'Pull cylinder off backing plate.', why: 'Removed.', tip: 'Compare to new.', commonMistake: 'Forcing.' },
      { number: 6, title: 'Install New Cylinder', description: 'Position new cylinder. Hand-thread bolts. Torque to spec.', why: 'Replacement.', tip: 'Match orientation.', commonMistake: 'Cocked install.' },
      { number: 7, title: 'Reconnect Brake Line', description: 'Hand-thread fitting; torque with flare wrench.', why: 'Restore hydraulic.', tip: 'Don\'t over-torque brass.', commonMistake: 'Cross-threading.' },
      { number: 8, title: 'Reassemble Brake Shoes', description: 'Per Rear Drum Brake guide — reassemble springs.', why: 'Drum brake reassembly.', tip: 'Use photos.', commonMistake: 'Wrong spring positions.' },
      { number: 9, title: 'Reinstall Drum and Wheel', description: 'Slide drum on. Install wheel; torque lugs.', why: 'Restoration.', tip: '95 ft-lbs.', commonMistake: 'Over-torque.' },
      { number: 10, title: 'Bleed Brakes and Test', description: 'Bleed system. Test pedal. Test drive in empty lot before traffic.', why: 'Safety.', tip: 'Static hold test.', commonMistake: 'Skipping bleed.' },
    ],
    torqueSpecifications: [{ fastener: 'Wheel cylinder bolts', specification: '11 ft-lbs (15 Nm).', notes: 'Standard.' }, { fastener: 'Brake line fitting', specification: '11-13 ft-lbs (15-18 Nm).', notes: 'Flare wrench.' }, { fastener: 'Lug nuts', specification: '95 ft-lbs (129 Nm).', notes: 'Star pattern.' }],
    fluidSpecifications: [{ fluid: 'Brake fluid', type: 'DOT 3', capacity: '~16 oz for bleed', notes: 'Fresh.' }],
    troubleshooting: [
      { problem: 'Leak at fitting', likelyCause: 'Cross-threaded or under-torqued', whatToCheck: 'Visual', fix: 'Re-torque or replace.' },
      { problem: 'Soft pedal after bleeding', likelyCause: 'Air remains', whatToCheck: 'More bleed', fix: 'Continue.' },
    ],
    proTips: ['Replace in pairs.', 'New shoes if old are oil-soaked.', 'Bleed thoroughly.'],
    commonMistakes: [{ mistake: 'Reusing oil-soaked shoes', consequence: 'Brakes won\'t hold.' }],
    successIndicators: ['Firm pedal', 'No leak', 'Brakes hold'],
    whenToSeekHelp: 'If bleed cannot achieve firm pedal, suspect master cylinder.',
    legalDisclaimer: 'Brake safety-critical.',
    manualSource: { title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)', url: 'https://www.haynes.com/en-us/wrangler/jeep/2003', notes: 'Cross-reference drum brake layout.' },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — FRAME RUST REPAIR (Rear Shackle Area)
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-frame-rust-repair',
    title: 'Frame Rust Repair (Rear Shackle Area)',
    vehicle: { make: 'Jeep', model: 'Wrangler', generations: [{ startYear: 1997, endYear: 2006 }] },
    video: {
      youtubeId: 'LBVdEBmGxCc',
      title: 'Need to Repair Your Rusty Jeep TJ Frame?',
      channel: 'Rust Revival Garage',
    },
    source: 'Generated by GarageIQ AI from publicly available sources (Mopar service literature, NHTSA TSBs and recall information, technician forums and Wrangler community service threads). Cross-checked and structured per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Suspension & Steering',
    difficulty: 'advanced',
    duration: '4-12 hours depending on extent',
    interval: 'Repair when rust is visible at the rear shackle mount, frame is soft to tap, or visible holes through frame rail. Critical safety inspection annually for any TJ in salt-belt areas.',
    description: 'TJ frames famously rust at the rear shackle mount area — a structural failure point that is a known safety concern. Symptoms: rust bubbles, soft frame to tap, holes, or sagging at rear corner. Repair options range from welding patches (DIY-able with welding skill) to professional frame welding to full frame swap. This guide covers patch welding for moderate rust.',
    overview: { difficulty: 'Advanced — welding required; frame integrity critical', estimatedTime: '4-12 hours', estimatedCost: '$50-150 for materials; $500-1500 for professional welding', whyMatters: 'Failed frame causes catastrophic suspension failure.', safetyNote: 'Welding hazards (eye, fire, fumes). Frame is structural — improper welds compromise safety.' },
    tools: ['MIG welder (110V or 220V)', 'Angle grinder with wire wheel and cutoff disc', 'Steel patch material (1/8" steel)', 'Welding helmet, gloves, jacket', 'Fire extinguisher', 'Drill with hole saw', 'Marker', 'Measuring tape', 'Wire brush'],
    parts: [
      { id: 'frame-patch-steel', name: 'Steel Patch Material (1/8" steel plate)', partNumber: '12" x 12" steel sheet, 1/8" thick', quantity: 1, price: 25, category: 'required', notes: 'Use grade A36 mild steel; matches OEM frame.' },
      { id: 'frame-paint-undercoat', name: 'Frame Paint and Undercoating', partNumber: 'Por-15 Rust Preventive Coating', quantity: 1, price: 30, category: 'required', notes: 'Apply after weld and grind to prevent re-rust.' },
      { id: 'welding-wire', name: 'MIG Welding Wire (.030 or .035)', partNumber: 'ER70S-6', quantity: 1, price: 25, category: 'required', notes: 'For mild steel.' },
    ],
    safetyInformation: 'WELDING HAZARDS: arc burns eyes; weld helmet mandatory. Sparks ignite fuel and oil — work in clean area, fire extinguisher within reach. Welding fumes are toxic — ventilate. Frame is structural; bad welds = catastrophic failure under load.',
    beforeYouStart: ['Verify rust extent — extensive rust (multiple holes, large soft areas) requires professional service or frame replacement', 'Have all welding equipment and materials ready', 'Clear work area of fuel, oil, flammables', 'Disconnect battery'],
    steps: [
      { number: 1, title: 'Lift Vehicle and Inspect Frame', description: 'Raise on stands. Inspect entire frame from front to rear. Identify all rust areas — rear shackle mounts are typical TJ failure points.', why: 'Plan repair scope.', tip: 'Tap with hammer — soft = severely rusted.', commonMistake: 'Repairing only visible damage; missing other rust.' },
      { number: 2, title: 'Document and Measure', description: 'Photograph extent of rust. Measure rusted area dimensions. Plan patch piece size — overlap good metal by 2-3 inches on each side.', why: 'Plan and document.', tip: 'Photos help insurance/inspection.', commonMistake: 'Skipping documentation.' },
      { number: 3, title: 'Cut Out Rusted Section', description: 'Use cut-off wheel to remove all rusted material. Cut beyond the rust line into solid metal — typically 1-2 inches of cut-back.', why: 'Patch must weld to solid metal.', tip: 'Square or rectangular cut is easier to patch.', commonMistake: 'Leaving rust at the seam.' },
      { number: 4, title: 'Wire-Brush Surrounding Area', description: 'Use wire wheel on grinder to remove all paint, rust, and oil from area extending 4-6 inches beyond the patch zone.', why: 'Welding requires clean metal.', tip: 'Bare metal at the weld area; rust beyond reduces weld penetration.', commonMistake: 'Welding on dirty metal.' },
      { number: 5, title: 'Cut Patch Piece to Match', description: 'Cut steel plate to match the hole size with a slight overlap (1/4" on each side typical). Test fit.', why: 'Patch piece.', tip: 'A simple rectangle is easier than complex shapes.', commonMistake: 'Wrong-sized patch.' },
      { number: 6, title: 'Tack-Weld Patch into Place', description: 'Position patch in place. Tack-weld at corners and midpoints to hold position.', why: 'Tack first prevents distortion.', tip: 'Multiple tacks before continuous weld.', commonMistake: 'Heavy weld at one spot — distorts.' },
      { number: 7, title: 'Continuous Weld', description: 'Weld continuously around the entire perimeter. Move slowly for full penetration. Listen for the "frying" sound that indicates proper weld.', why: 'Structural seal.', tip: 'Don\'t blow holes — back off if metal turns red.', commonMistake: 'Blowing through; cold welds.' },
      { number: 8, title: 'Grind Welds and Inspect', description: 'After welds cool, grind smooth with cutoff disc or wire wheel. Inspect for any pinholes or incomplete welds.', why: 'Smooth surface; quality check.', tip: 'Don\'t over-grind — leaves weld too thin.', commonMistake: 'Grinding through weld.' },
      { number: 9, title: 'Apply Rust Preventive Coating', description: 'Spray Por-15 or similar rust preventive on welded area and surrounding bare metal.', why: 'Prevents re-rust.', tip: 'Multiple coats per product instructions.', commonMistake: 'Skipping coating.' },
      { number: 10, title: 'Verify Structural Integrity', description: 'Tap entire welded area with hammer. Should ring like solid metal. Inspect from above and below for any soft spots. If integrity is in doubt, do not return to road service until verified.', why: 'Frame is structural.', tip: 'Have a welder evaluate if uncertain.', commonMistake: 'Returning to service without verification.' },
    ],
    torqueSpecifications: [],
    fluidSpecifications: [],
    troubleshooting: [
      { problem: 'Cold welds (poor penetration)', likelyCause: 'Cold metal, dirty metal, wrong amperage', whatToCheck: 'Weld penetration on test piece', fix: 'Increase amperage, clean better, preheat slightly.' },
      { problem: 'Frame sags after repair', likelyCause: 'Inadequate weld; rust extends beyond patch', whatToCheck: 'Inspect for additional rust', fix: 'Extend repair area; consider professional service.' },
      { problem: 'Re-rust occurs after coating', likelyCause: 'Inadequate surface prep; missing areas', whatToCheck: 'Bare metal coverage', fix: 'Reapply coating with thorough prep.' },
    ],
    proTips: ['Frame work needs welding skill — practice on scrap before frame.', 'Por-15 coating is the gold standard for rust prevention.', 'TJ frames in salt-belt areas should be inspected annually.', 'Extensive rust = frame replacement is more cost-effective than patching.'],
    commonMistakes: [
      { mistake: 'Welding without removing all rust', consequence: 'Re-rust at weld; eventual structural failure.' },
      { mistake: 'Returning to service without verification', consequence: 'Catastrophic suspension failure possible.' },
      { mistake: 'Patching extensively rusted frame', consequence: 'Repeated repairs; frame replacement recommended.' },
    ],
    successIndicators: ['Solid weld with no pinholes', 'Coated surface even and complete', 'No visible rust', 'Frame solid to tap test', 'Inspect after first 1,000 miles'],
    whenToSeekHelp: 'Frame welding is structural and safety-critical. If unfamiliar with welding, professional service is the safer choice. Severely rusted frames warrant frame replacement (used or new) rather than extensive patching.',
    legalDisclaimer: 'Frame is structural. Improper repair compromises vehicle safety. Welding hazards include fire, fumes, and arc burns. This guide is informational; professional service may be safer for serious rust.',
    manualSource: { title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)', url: 'https://www.haynes.com/en-us/wrangler/jeep/2003', notes: 'Cross-reference frame layout. NHTSA recall information available for known issues.' },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — FLOOR PAN RUST REPAIR
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-floor-pan-rust-repair',
    title: 'Floor Pan Rust Repair',
    vehicle: { make: 'Jeep', model: 'Wrangler', generations: [{ startYear: 1997, endYear: 2006 }] },
    video: {
      youtubeId: 'LBVdEBmGxCc',
      title: 'Jeep TJ Frame/Body Rust Repair Reference',
      channel: 'Rust Revival Garage',
    },
    source: 'Generated by GarageIQ AI from publicly available sources. Cross-checked per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Suspension & Steering',
    difficulty: 'advanced',
    duration: '6-12 hours per floor pan',
    interval: 'Repair when floor pan has rust holes, soft spots, or visible damage. Common in salt-belt TJs.',
    description: 'TJ floor pans rust through, especially driver/passenger footwell areas. Repair options: weld in patch panel, install pre-formed replacement panel, or full panel replacement. Replacement panels available aftermarket for many areas.',
    overview: { difficulty: 'Advanced — welding required; carpet removal, interior strip', estimatedTime: '6-12 hours per floor pan', estimatedCost: '$80-200 for replacement panels; $30-50 for patch material', whyMatters: 'Floor pan rust lets water and exhaust enter cabin.', safetyNote: 'Welding hazards.' },
    tools: ['MIG welder', 'Angle grinder with cutoff and wire wheel', 'Floor panel replacement (aftermarket)', 'Welding helmet and gloves', 'Drill', 'Body file', 'Eye protection'],
    parts: [{ id: 'floor-pan-patch', name: 'Floor Pan Patch Panel', partNumber: 'Crown-AmericanX MS-15 (driver) / MS-16 (passenger)', quantity: 1, price: 90, category: 'required', notes: 'Aftermarket replacement panels.' }, { id: 'floor-pan-undercoat', name: 'Por-15 Rust Coating', partNumber: 'Por-15', quantity: 1, price: 30, category: 'required', notes: 'Prevention.' }],
    safetyInformation: 'Welding hazards. Cabin interior must be removed.',
    beforeYouStart: ['Remove front seats', 'Remove carpet and pad', 'Verify panel availability for your area'],
    steps: [
      { number: 1, title: 'Strip Cabin Interior', description: 'Remove seats, carpet, padding, sound deadener.', why: 'Access to floor.', tip: 'Photograph wiring harness routing.', commonMistake: 'Forgetting wiring locations.' },
      { number: 2, title: 'Inspect and Document Damage', description: 'Identify rust extent. Photo. Plan repair area.', why: 'Plan.', tip: 'Mark hole edges with marker.', commonMistake: 'Underestimating extent.' },
      { number: 3, title: 'Cut Out Rusted Material', description: 'Use cutoff wheel to remove all rust. Cut beyond rust into solid metal.', why: 'Patch foundation.', tip: 'Generous cut-back.', commonMistake: 'Insufficient cut.' },
      { number: 4, title: 'Test-Fit Replacement Panel', description: 'Position pre-formed panel. Trim if needed for fit.', why: 'Fit verification.', tip: 'Snug fit before welding.', commonMistake: 'Forcing panel into wrong shape.' },
      { number: 5, title: 'Wire-Brush Mating Surfaces', description: 'Clean both old metal and new panel for welding.', why: 'Clean welds.', tip: 'Bare metal both sides.', commonMistake: 'Dirty mating.' },
      { number: 6, title: 'Tack-Weld Panel', description: 'Tack weld at corners and midpoints.', why: 'Hold position.', tip: 'Multiple tacks before continuous.', commonMistake: 'One heavy tack distorts.' },
      { number: 7, title: 'Continuous Weld', description: 'Weld continuously around perimeter.', why: 'Structural seal.', tip: 'Move slowly.', commonMistake: 'Cold welds.' },
      { number: 8, title: 'Grind and Smooth', description: 'Grind welds smooth with body grinder.', why: 'Cosmetic.', tip: 'Don\'t grind through.', commonMistake: 'Over-grinding.' },
      { number: 9, title: 'Apply Rust Coating', description: 'Coat top and underside of repair area with Por-15 or similar.', why: 'Prevention.', tip: 'Underside critical.', commonMistake: 'Skipping underside.' },
      { number: 10, title: 'Reinstall Carpet and Interior', description: 'Reattach carpet, padding, seats, harness.', why: 'Restoration.', tip: 'Verify all wiring.', commonMistake: 'Forgetting wires.' },
    ],
    torqueSpecifications: [],
    fluidSpecifications: [],
    troubleshooting: [
      { problem: 'Water leak at repair', likelyCause: 'Pinhole in weld', whatToCheck: 'Visual inspection from above and below', fix: 'Re-weld pinhole.' },
      { problem: 'Re-rust after months', likelyCause: 'Inadequate undercoating', whatToCheck: 'Coverage', fix: 'Apply additional coats.' },
    ],
    proTips: ['Aftermarket pre-formed panels save welding time.', 'Por-15 lasts decades on properly prepped metal.', 'Welding skill required — practice first.'],
    commonMistakes: [{ mistake: 'Insufficient rust removal', consequence: 'Re-rust quickly.' }],
    successIndicators: ['Solid weld', 'Smooth finish', 'No water leak', 'Coated surfaces'],
    whenToSeekHelp: 'Body shops can do floor pan work efficiently. Major rust may warrant body shop service.',
    legalDisclaimer: 'Welding hazards.',
    manualSource: { title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)', url: 'https://www.haynes.com/en-us/wrangler/jeep/2003', notes: 'Cross-reference body diagrams.' },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — SOFT TOP INSTALLATION
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-soft-top-installation',
    title: 'Soft Top Installation / Replacement',
    vehicle: { make: 'Jeep', model: 'Wrangler', generations: [{ startYear: 1997, endYear: 2006 }] },
    video: {
      youtubeId: 'BtUEO_n9Fhk',
      title: 'How to Replace Soft Top on a Jeep Wrangler TJ (97-06)',
      channel: 'KyleSVT',
    },
    source: 'Generated by GarageIQ AI from publicly available sources. Cross-checked per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Suspension & Steering',
    difficulty: 'intermediate',
    duration: '2-3 hours',
    interval: 'Replace when fabric is torn, windows clouded/cracked, or seasonal swap from hardtop.',
    description: 'TJ soft top consists of frame (multiple bows), fabric top, and side/rear windows. Installation requires assembly of the frame, attaching fabric, securing with snaps and clamps. Replacement tops available from Bestop, Mopar, and aftermarket.',
    overview: { difficulty: 'Intermediate — multi-step assembly', estimatedTime: '2-3 hours', estimatedCost: '$200-600 for top + frame', whyMatters: 'Weather protection; security.', safetyNote: 'Lifting heavy bows — get help.' },
    tools: ['Phillips and flathead screwdrivers', 'Sockets (10-13mm)', 'Helper recommended', 'Eye protection'],
    parts: [{ id: 'soft-top-replacement', name: 'Soft Top Kit', partNumber: 'Bestop Trail Master / Pavement Ends Replay', quantity: 1, price: 400, category: 'required', notes: 'Multiple brands; Bestop is highly regarded.' }, { id: 'soft-top-frame', name: 'Soft Top Frame (if needed)', partNumber: 'Mopar 55174670 / Bestop frame', quantity: 1, price: 250, category: 'optional', notes: 'Replace if bent.' }],
    safetyInformation: 'Bows are heavy. Get helper.',
    beforeYouStart: ['Remove old top', 'Verify all parts in kit', 'Read kit-specific instructions'],
    steps: [
      { number: 1, title: 'Position Vehicle and Tools', description: 'Park indoors or shade for working. Have all parts staged.', why: 'Workspace.', tip: 'Indoors better for quality work.', commonMistake: 'Outdoor in wind.' },
      { number: 2, title: 'Install Frame Bows', description: 'Position the rear bow first, working forward. Each bow has specific position.', why: 'Frame foundation.', tip: 'Use kit instructions.', commonMistake: 'Wrong order.' },
      { number: 3, title: 'Connect Frame Hinges', description: 'Frame bows pivot at side hinges. Bolt or clip per kit.', why: 'Articulation.', tip: 'Verify smooth motion.', commonMistake: 'Binding hinges.' },
      { number: 4, title: 'Position Fabric Top on Frame', description: 'Lay fabric top over frame, aligned with windshield.', why: 'Visual fit.', tip: 'Top oriented correctly per markings.', commonMistake: 'Reversed.' },
      { number: 5, title: 'Secure Front Header to Windshield', description: 'Front of top has header that bolts to windshield top — typically 4-6 bolts.', why: 'Front anchor.', tip: 'Hand-thread first.', commonMistake: 'Cross-threading.' },
      { number: 6, title: 'Attach Side Channels', description: 'Side channels secure top fabric. Slide fabric edges into channels and lock.', why: 'Edge securing.', tip: 'Even tension.', commonMistake: 'Lopsided.' },
      { number: 7, title: 'Snap Rear and Side Windows', description: 'Plastic windows attach with snaps along frame.', why: 'Window attachment.', tip: 'Start at corners.', commonMistake: 'Stretching fabric.' },
      { number: 8, title: 'Secure Tailgate Bar', description: 'Bar at base of rear window slides into tailgate brackets.', why: 'Rear close-up.', tip: 'Verify alignment.', commonMistake: 'Misaligned.' },
      { number: 9, title: 'Verify Fit and Tension', description: 'Walk around vehicle. Top should be evenly tight; no flapping.', why: 'Quality check.', tip: 'Heat fabric in sun and re-tension if needed.', commonMistake: 'Loose fit.' },
      { number: 10, title: 'Test in Various Conditions', description: 'Drive at various speeds to confirm top stays secure.', why: 'Real-world test.', tip: 'High speed reveals loose areas.', commonMistake: 'Skipping test.' },
    ],
    torqueSpecifications: [],
    fluidSpecifications: [],
    troubleshooting: [
      { problem: 'Top flaps in wind', likelyCause: 'Loose channel or snaps', whatToCheck: 'Walk-around inspection', fix: 'Tighten snaps; reseat channels.' },
      { problem: 'Water leaks', likelyCause: 'Misalignment at seams', whatToCheck: 'Inspect during rain', fix: 'Adjust position; apply seam sealer if needed.' },
    ],
    proTips: ['Heat fabric in sun before final fitting — material relaxes.', 'Bestop is durable and well-fitting.', 'Replace soft top windows if cracked.'],
    commonMistakes: [{ mistake: 'Installing in cold weather', consequence: 'Fabric stiff; difficult to fit.' }],
    successIndicators: ['Tight fit', 'No flap', 'No leak', 'Easy to operate'],
    whenToSeekHelp: 'First-time installers may benefit from professional install or video tutorials specific to brand.',
    legalDisclaimer: 'Verify with kit-specific instructions.',
    manualSource: { title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)', url: 'https://www.haynes.com/en-us/wrangler/jeep/2003', notes: 'Cross-reference soft top assembly.' },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — HARDTOP REMOVAL/INSTALLATION
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-hardtop-removal-install',
    title: 'Hardtop Removal and Installation',
    vehicle: { make: 'Jeep', model: 'Wrangler', generations: [{ startYear: 1997, endYear: 2006 }] },
    video: {
      youtubeId: 'Gmy3D-zIFdY',
      title: 'How to Remove the Hardtop on a Jeep Wrangler TJ',
      channel: 'mikegolden Games',
    },
    source: 'Generated by GarageIQ AI from publicly available sources. Cross-checked per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Suspension & Steering',
    difficulty: 'intermediate',
    duration: '30 minutes - 1 hour',
    interval: 'Remove for top-off summer driving; install for winter/security.',
    description: 'TJ hardtop is a fiberglass shell that bolts to the body via several screws and bolts. Removal/installation typically takes 30-60 minutes with a helper. Storage stand recommended.',
    overview: { difficulty: 'Intermediate — heavy, awkward, helper required', estimatedTime: '30 minutes - 1 hour', estimatedCost: '$0 (if you have it); $1500-3000 for replacement', whyMatters: 'Hardtop swap is seasonal common.', safetyNote: 'Heavy — get help. Risk of dropping/scratching.' },
    tools: ['T50 Torx bit', 'Phillips screwdriver', 'Helper(s)', 'Storage stand or padded floor area', 'Eye protection'],
    parts: [{ id: 'hardtop-bolts', name: 'Hardtop Mounting Bolts (replacement set)', partNumber: 'Generic T50 hardtop bolts', quantity: 8, price: 12, category: 'optional', notes: 'Replace if corroded.' }],
    safetyInformation: 'Hardtop weighs 60-100 lbs. Get help to avoid dropping or strain injury.',
    beforeYouStart: ['Have helper(s)', 'Have storage area or stand ready', 'Disconnect rear defroster wiring if applicable'],
    steps: [
      { number: 1, title: 'Disconnect Rear Defroster (If Equipped)', description: 'Inside cargo area, locate rear defroster wire. Disconnect.', why: 'Wire would tear during removal.', tip: 'Photograph for reinstall.', commonMistake: 'Forgetting wire.' },
      { number: 2, title: 'Remove Front Header Bolts', description: 'Inside above windshield, remove 4-6 bolts (T50 typical) holding front header to windshield top.', why: 'Front anchor.', tip: 'Use proper Torx bit.', commonMistake: 'Stripping.' },
      { number: 3, title: 'Remove Rear Mounting Bolts', description: 'Tailgate area — 2-4 bolts hold rear of top to tub.', why: 'Rear anchor.', tip: 'Note bolt locations.', commonMistake: 'Missing bolts.' },
      { number: 4, title: 'Remove Side Mounting Bolts', description: 'Side rails — 4-6 bolts each side (2-3 per side).', why: 'Side anchors.', tip: 'Inside doorframe.', commonMistake: 'Missing bolts.' },
      { number: 5, title: 'Verify All Bolts Removed', description: 'Walk around — confirm all mounting bolts removed.', why: 'Pre-lift check.', tip: 'Photograph or list locations.', commonMistake: 'Missing one.' },
      { number: 6, title: 'Lift Top with Helper', description: 'Position helper(s). Lift top straight up off body.', why: 'Removal.', tip: 'Lift from edges; avoid pushing on glass.', commonMistake: 'Solo lift; dropping.' },
      { number: 7, title: 'Set Top on Storage Stand', description: 'Place top on padded stand or floor.', why: 'Protection.', tip: 'Cover top with sheet to prevent scratching.', commonMistake: 'Bare floor.' },
      { number: 8, title: 'Inspect Top for Damage', description: 'While off, inspect interior, glass, weatherstripping.', why: 'Maintenance opportunity.', tip: 'Replace weatherstrip if cracked.', commonMistake: 'Skipping inspection.' },
      { number: 9, title: 'Install (Reverse Order)', description: 'Lift top onto body. Position correctly. Hand-thread all mounting bolts. Cross-tighten.', why: 'Replacement.', tip: 'Don\'t over-tighten — fiberglass cracks.', commonMistake: 'Over-torquing.' },
      { number: 10, title: 'Test Doors and Windows', description: 'After install, verify all doors close properly and windows seal.', why: 'Function check.', tip: 'Adjust if doors don\'t close.', commonMistake: 'Skipping check.' },
    ],
    torqueSpecifications: [{ fastener: 'Hardtop mounting bolts', specification: '15 ft-lbs (20 Nm).', notes: 'Don\'t over-tighten fiberglass.' }],
    fluidSpecifications: [],
    troubleshooting: [
      { problem: 'Doors don\'t close after install', likelyCause: 'Top not centered', whatToCheck: 'Inspect alignment', fix: 'Loosen bolts, reposition, retighten.' },
      { problem: 'Water leak at edges', likelyCause: 'Weatherstrip damaged or top misaligned', whatToCheck: 'Inspect strip', fix: 'Replace weatherstrip; adjust top.' },
    ],
    proTips: ['Use storage stand to protect top off-vehicle.', 'Inspect weatherstripping yearly.', 'Two helpers makes job easier.'],
    commonMistakes: [{ mistake: 'Solo lifting', consequence: 'Dropped top; possible body damage.' }],
    successIndicators: ['Top fits flush', 'Doors close normally', 'No leaks'],
    whenToSeekHelp: 'For solo install, hardtop hoists are available — make solo install possible.',
    legalDisclaimer: 'Verify hardware specifications.',
    manualSource: { title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)', url: 'https://www.haynes.com/en-us/wrangler/jeep/2003', notes: 'Cross-reference hardtop layout.' },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — SOFT TOP WINDOW REPLACEMENT (P3 — concise)
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-soft-top-window-replacement',
    title: 'Soft Top Window Replacement',
    vehicle: { make: 'Jeep', model: 'Wrangler', generations: [{ startYear: 1997, endYear: 2006 }] },
    video: {
      youtubeId: 'BtUEO_n9Fhk',
      title: 'Soft Top install reference (window replacement)',
      channel: 'KyleSVT',
    },
    source: 'Generated by GarageIQ AI from publicly available sources. Cross-checked per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Suspension & Steering',
    difficulty: 'beginner',
    duration: '30-60 minutes',
    interval: 'Replace when windows cracked, clouded, or torn from fabric.',
    description: 'Soft top windows (rear and side) attach to the soft top fabric with snaps and zippers. Replacement windows available individually.',
    overview: { difficulty: 'Beginner — direct replacement', estimatedTime: '30-60 minutes', estimatedCost: '$30-90 per window', whyMatters: 'Cracked windows obstruct view; clouded windows reduce visibility.', safetyNote: 'Standard care.' },
    tools: ['Phillips screwdriver', 'Pliers (if snaps stuck)', 'Eye protection'],
    parts: [{ id: 'soft-top-window', name: 'Soft Top Window (per location)', partNumber: 'Bestop replacement window kits', quantity: 1, price: 60, category: 'required', notes: 'Match brand and style.' }],
    safetyInformation: 'Standard.',
    beforeYouStart: ['Confirm window matches your top model'],
    steps: [
      { number: 1, title: 'Identify Window Location', description: 'Rear, driver side, or passenger side.', why: 'Selection.', tip: 'Photograph current.', commonMistake: 'Wrong location.' },
      { number: 2, title: 'Open Zipper or Disconnect Window', description: 'Most TJ soft top windows zip out from the fabric.', why: 'Removal.', tip: 'Open fully.', commonMistake: 'Forcing.' },
      { number: 3, title: 'Disconnect Snaps', description: 'Snap window edge from frame.', why: 'Frees window.', tip: 'Pop each snap individually.', commonMistake: 'Tearing fabric.' },
      { number: 4, title: 'Remove Window', description: 'Lift window away.', why: 'Removed.', tip: 'Compare to new.', commonMistake: 'Wrong replacement.' },
      { number: 5, title: 'Install New Window', description: 'Position new window. Snap perimeter into frame.', why: 'Replacement.', tip: 'Even tension.', commonMistake: 'Lopsided.' },
      { number: 6, title: 'Zip Window into Top', description: 'Reconnect zipper to soft top.', why: 'Securing.', tip: 'Smooth zipper motion.', commonMistake: 'Snagged zipper.' },
      { number: 7, title: 'Verify Tightness', description: 'Window should be tight to frame, no flap.', why: 'Function.', tip: 'Heat in sun for relaxation if too tight.', commonMistake: 'Loose window.' },
      { number: 8, title: 'Test Open and Close', description: 'Verify window opens and closes smoothly.', why: 'Function.', tip: 'Standard.', commonMistake: 'Force.' },
      { number: 9, title: 'Apply Window Treatment (Optional)', description: 'Some plastics benefit from UV protectant.', why: 'Longevity.', tip: 'Plexus or similar.', commonMistake: 'Wrong product.' },
      { number: 10, title: 'Verify Weather Seal', description: 'Confirm window seals against weather.', why: 'Final check.', tip: 'Test in rain or hose.', commonMistake: 'Skipping.' },
    ],
    torqueSpecifications: [],
    fluidSpecifications: [],
    troubleshooting: [{ problem: 'Window doesn\'t close fully', likelyCause: 'Wrong size', whatToCheck: 'Match part number', fix: 'Exchange.' }],
    proTips: ['Bestop is well-fitting.', 'UV protectant extends life.'],
    commonMistakes: [{ mistake: 'Wrong window for top brand', consequence: 'Fits poorly.' }],
    successIndicators: ['Tight fit', 'No leak', 'Smooth operation'],
    whenToSeekHelp: 'Soft top specialist if window install is difficult.',
    legalDisclaimer: 'Verify product specifications.',
    manualSource: { title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)', url: 'https://www.haynes.com/en-us/wrangler/jeep/2003', notes: 'Cross-reference soft top layout.' },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — DOOR HINGE RESTORATION
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-door-hinge-restoration',
    title: 'Door Hinge Restoration / Replacement',
    vehicle: { make: 'Jeep', model: 'Wrangler', generations: [{ startYear: 1997, endYear: 2006 }] },
    video: {
      youtubeId: 'rgnhztpnmrM',
      title: 'Delrin Door Hinge Pin Bushing Replacement - 2004 Jeep Wrangler TJ Rubicon',
      channel: 'Motoring Adventures',
    },
    source: 'Generated by GarageIQ AI from publicly available sources. Cross-checked per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Suspension & Steering',
    difficulty: 'intermediate',
    duration: '1-2 hours per door',
    interval: 'Restore when door sags, hinges squeak, or pin/bushing wear creates play.',
    description: 'TJ door hinges have pins and bushings that wear over time, causing door sag. Restoration involves replacing pins and bushings; full replacement available.',
    overview: { difficulty: 'Intermediate — door alignment work', estimatedTime: '1-2 hours per door', estimatedCost: '$15-40 per hinge kit; $40-80 for full hinges', whyMatters: 'Sagging door doesn\'t close properly.', safetyNote: 'Door is heavy. Get helper.' },
    tools: ['Sockets', 'Hammer and punch', 'Helper to support door', 'Eye protection'],
    parts: [{ id: 'door-hinge-kit', name: 'Door Hinge Pin/Bushing Kit', partNumber: 'Mopar 5083227 — equivalent: Crown HINGE-PIN', quantity: 2, price: 25, category: 'required', notes: 'For pin/bushing rebuild.' }, { id: 'door-hinge-full', name: 'Full Door Hinge (if pin/bushing insufficient)', partNumber: 'Mopar 55176344 (upper) / 55176343 (lower)', quantity: 2, price: 60, category: 'optional', notes: 'Full hinge if structure damaged.' }],
    safetyInformation: 'Door heavy. Get helper.',
    beforeYouStart: ['Have replacement parts', 'Helper to support door'],
    steps: [
      { number: 1, title: 'Open Door and Support', description: 'Open door. Have helper support weight.', why: 'Hinge access.', tip: 'Cardboard or stand.', commonMistake: 'Solo handling.' },
      { number: 2, title: 'Remove Hinge Pin (Pin Replacement)', description: 'Tap hinge pin out with punch and hammer (typically downward direction).', why: 'Pin removal.', tip: 'PB Blaster on pin overnight.', commonMistake: 'Forcing rusted pin.' },
      { number: 3, title: 'Inspect Bushings', description: 'Bushings inside hinge knuckles. Replace if worn.', why: 'Wear assessment.', tip: 'Bushings are typically nylon or oil-impregnated bronze.', commonMistake: 'Reusing worn bushings.' },
      { number: 4, title: 'Install New Bushings', description: 'Tap new bushings into hinge knuckles.', why: 'Replacement.', tip: 'Use proper-size driver.', commonMistake: 'Cocking bushings.' },
      { number: 5, title: 'Lubricate Bushings', description: 'Apply white lithium grease.', why: 'Smooth operation.', tip: 'Don\'t over-grease.', commonMistake: 'Dry install.' },
      { number: 6, title: 'Install New Hinge Pin', description: 'Tap new pin in.', why: 'Restoration.', tip: 'Oriented same as old.', commonMistake: 'Wrong direction.' },
      { number: 7, title: 'Verify Door Alignment', description: 'Close door, observe alignment with body.', why: 'Function check.', tip: 'Door should close evenly with no rubbing.', commonMistake: 'Misalignment.' },
      { number: 8, title: 'Adjust If Necessary', description: 'If full hinge replaced, may need to adjust door bolts on body side.', why: 'Alignment.', tip: 'Loosen, adjust, retighten.', commonMistake: 'Forcing fit.' },
      { number: 9, title: 'Test Door Operation', description: 'Open and close door multiple times. Should swing smoothly.', why: 'Function.', tip: 'No bind or squeak.', commonMistake: 'Skipping test.' },
      { number: 10, title: 'Apply Anti-Seize to Pin', description: 'For future easy removal.', why: 'Maintenance.', tip: 'Standard.', commonMistake: 'Forgetting.' },
    ],
    torqueSpecifications: [],
    fluidSpecifications: [],
    troubleshooting: [{ problem: 'Door still sags after pin/bushing', likelyCause: 'Hinge structural wear', whatToCheck: 'Visual', fix: 'Replace full hinge.' }],
    proTips: ['White lithium grease on bushings.', 'Anti-seize on pins.', 'Replace hinges if structural wear.'],
    commonMistakes: [{ mistake: 'Reusing pin if bent', consequence: 'Door sags again.' }],
    successIndicators: ['Smooth door operation', 'No squeak', 'Even alignment'],
    whenToSeekHelp: 'Body shop for door alignment if full hinge replacement.',
    legalDisclaimer: 'Verify alignment after work.',
    manualSource: { title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)', url: 'https://www.haynes.com/en-us/wrangler/jeep/2003', notes: 'Cross-reference door hinges.' },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — BODY MOUNT INSPECTION / REPLACEMENT
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-body-mount-inspection-replacement',
    title: 'Body Mount Inspection and Replacement',
    vehicle: { make: 'Jeep', model: 'Wrangler', generations: [{ startYear: 1997, endYear: 2006 }] },
    video: {
      youtubeId: 'kVpYvHq-5yM',
      title: 'TJ Body Bushings',
      channel: '4wdCliffhangers',
    },
    source: 'Generated by GarageIQ AI from publicly available sources. Cross-checked per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Suspension & Steering',
    difficulty: 'advanced',
    duration: '4-6 hours',
    interval: 'Inspect annually for cracks/separation. Replace when crushed, torn, or rusted through.',
    description: 'TJ body sits on the frame via 7 rubber body mounts. Mounts compress and crack with age. Replacement requires lifting the body slightly.',
    overview: { difficulty: 'Advanced — body lifting required', estimatedTime: '4-6 hours', estimatedCost: '$60-120 for body mount kit', whyMatters: 'Failed mounts cause rattles and uneven body height.', safetyNote: 'Body lifting requires multiple jacks/stands.' },
    tools: ['Multiple floor jacks and stands', 'Sockets (large)', 'Pry bar', 'Eye protection'],
    parts: [{ id: 'body-mount-kit', name: 'Body Mount Kit (set of 7)', partNumber: 'Mopar 5066850AA — equivalent: Daystar KJ04001BK (poly upgrade)', quantity: 1, price: 90, category: 'required', notes: 'Polyurethane upgrades available.' }],
    safetyInformation: 'Body lifting safety-critical. Multiple stands required.',
    beforeYouStart: ['Have body mount kit', 'Multiple jacks and stands'],
    steps: [
      { number: 1, title: 'Lift Body Slightly', description: 'Use multiple floor jacks to lift body 1-2 inches at locations near mounts.', why: 'Access to mounts.', tip: 'One side at a time may be safer.', commonMistake: 'Insufficient support.' },
      { number: 2, title: 'Inspect All 7 Mount Locations', description: 'Document condition of each mount.', why: 'Plan.', tip: 'Most TJs need all 7 replaced.', commonMistake: 'Skipping inspection.' },
      { number: 3, title: 'Remove Mount Bolts (One at a Time)', description: 'Each mount has bolts through body and frame. Remove.', why: 'Frees mount.', tip: 'Apply penetrating oil.', commonMistake: 'Stripping.' },
      { number: 4, title: 'Lift Body Slightly More', description: 'Lift body just enough to remove old mount.', why: 'Mount clearance.', tip: 'Don\'t lift too high.', commonMistake: 'Excessive lift.' },
      { number: 5, title: 'Remove Old Mount', description: 'Pull old mount and washers out.', why: 'Removed.', tip: 'Note orientation.', commonMistake: 'Forgetting orientation.' },
      { number: 6, title: 'Install New Mount', description: 'Position new mount with washers per kit instructions.', why: 'Replacement.', tip: 'Verify orientation.', commonMistake: 'Reversed.' },
      { number: 7, title: 'Lower Body Onto New Mount', description: 'Slowly lower body onto new mount.', why: 'Settling.', tip: 'Verify alignment.', commonMistake: 'Hasty drop.' },
      { number: 8, title: 'Install Mount Bolts', description: 'Hand-thread, then torque to spec.', why: 'Securing.', tip: 'Cross-tighten.', commonMistake: 'Cross-threading.' },
      { number: 9, title: 'Repeat for All 7 Mounts', description: 'Process is identical for each mount.', why: 'Comprehensive replacement.', tip: 'Work side by side.', commonMistake: 'Skipping mounts.' },
      { number: 10, title: 'Verify and Test', description: 'After all mounts replaced, lower vehicle. Verify body sits evenly. Test drive — should feel quieter and tighter.', why: 'Confirms.', tip: 'Re-torque after first 100 miles.', commonMistake: 'Skipping re-torque.' },
    ],
    torqueSpecifications: [{ fastener: 'Body mount bolts', specification: '47 ft-lbs (64 Nm).', notes: 'Verify torque spec from OEM service manual.' }],
    fluidSpecifications: [],
    troubleshooting: [{ problem: 'Body still rattles', likelyCause: 'Missed mount or incorrect torque', whatToCheck: 'Re-inspect each', fix: 'Address each.' }],
    proTips: ['Polyurethane mounts last longer.', 'Replace all 7 at once.', 'Re-torque after 100 miles.'],
    commonMistakes: [{ mistake: 'Replacing only some mounts', consequence: 'Uneven body settling.' }],
    successIndicators: ['Even body height', 'No rattles', 'Tight feel'],
    whenToSeekHelp: 'Body lifting requires care; professional service available.',
    legalDisclaimer: 'Body lifting hazardous if not properly supported.',
    manualSource: { title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)', url: 'https://www.haynes.com/en-us/wrangler/jeep/2003', notes: 'Cross-reference body mount layout.' },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — HOOD RELEASE CABLE
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-hood-release-cable',
    title: 'Hood Release Cable Replacement',
    vehicle: { make: 'Jeep', model: 'Wrangler', generations: [{ startYear: 1997, endYear: 2006 }] },
    video: {
      youtubeId: '8B6i9833n_Y',
      title: 'Jeep Wrangler TJ/LJ - aluminum hood latch replacement 1997-2006',
      channel: 'ATEM OFFROAD',
    },
    source: 'Generated by GarageIQ AI from publicly available sources. Cross-checked per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Suspension & Steering',
    difficulty: 'beginner',
    duration: '30-45 minutes',
    interval: 'Replace when hood release pull does not open hood, cable broken, or stuck.',
    description: 'TJ hood release uses a cable from cabin handle to hood latch. Cable can break or stretch. Simple replacement.',
    overview: { difficulty: 'Beginner — direct cable swap', estimatedTime: '30-45 minutes', estimatedCost: '$15-30', whyMatters: 'Without working release, hood can\'t open.', safetyNote: 'Standard.' },
    tools: ['Phillips screwdriver', 'Trim removal tool', 'Eye protection'],
    parts: [{ id: 'hood-release-cable', name: 'Hood Release Cable', partNumber: 'Mopar 55117204AB', quantity: 1, price: 25, category: 'required', notes: 'Verify part for application.' }],
    safetyInformation: 'Standard.',
    beforeYouStart: ['If hood is stuck closed, may require alternative methods to open first', 'Have new cable'],
    steps: [
      { number: 1, title: 'Open Hood (If Possible)', description: 'If cable still partially works, pull and have helper push hood up while pulling. If totally broken, may need to access from underneath.', why: 'Initial access.', tip: 'Some clever methods exist for accessing latch from under.', commonMistake: 'Forcing damage.' },
      { number: 2, title: 'Locate Cable at Latch (Engine Bay)', description: 'Cable connects to latch mechanism at front of engine bay.', why: 'Visual ID.', tip: 'Trace cable.', commonMistake: 'Wrong cable.' },
      { number: 3, title: 'Disconnect Cable at Latch', description: 'Cable typically clips into latch lever.', why: 'Frees end.', tip: 'Note orientation.', commonMistake: 'Damaging clip.' },
      { number: 4, title: 'Locate Cable at Cabin Handle', description: 'Driver\'s side kick panel or under dash.', why: 'Other end.', tip: 'Remove kick panel for access.', commonMistake: 'Wrong location.' },
      { number: 5, title: 'Disconnect Cable at Handle', description: 'Cable pulls out of handle assembly.', why: 'Frees other end.', tip: 'Photograph.', commonMistake: 'Forcing.' },
      { number: 6, title: 'Pull Cable Through Firewall', description: 'Pull cable from cabin side to remove.', why: 'Removed.', tip: 'Note grommets.', commonMistake: 'Damaging grommets.' },
      { number: 7, title: 'Install New Cable', description: 'Feed new cable through firewall and route to handle and latch.', why: 'Replacement.', tip: 'Use grommets to seal.', commonMistake: 'Loose seal.' },
      { number: 8, title: 'Connect at Handle', description: 'Insert cable end into handle.', why: 'Cabin connection.', tip: 'Verify seat.', commonMistake: 'Loose connection.' },
      { number: 9, title: 'Connect at Latch', description: 'Clip cable into latch lever.', why: 'Latch connection.', tip: 'Verify orientation.', commonMistake: 'Wrong direction.' },
      { number: 10, title: 'Test Operation', description: 'Pull handle. Hood should release. Test 5-10 times to verify.', why: 'Function.', tip: 'Smooth operation.', commonMistake: 'One-time test only.' },
    ],
    torqueSpecifications: [],
    fluidSpecifications: [],
    troubleshooting: [{ problem: 'Cable doesn\'t release latch', likelyCause: 'Incorrect routing or end connections', whatToCheck: 'Inspect both ends', fix: 'Re-route or reconnect.' }],
    proTips: ['Apply lubricant to cable for smooth operation.', 'Verify routing follows OEM path.'],
    commonMistakes: [{ mistake: 'Wrong-application cable', consequence: 'Doesn\'t fit.' }],
    successIndicators: ['Hood releases on first pull', 'Smooth operation'],
    whenToSeekHelp: 'If hood is stuck and cable can\'t be replaced, locksmith or specialist may be needed to open.',
    legalDisclaimer: 'Verify routing.',
    manualSource: { title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)', url: 'https://www.haynes.com/en-us/wrangler/jeep/2003', notes: 'Cross-reference cable layout.' },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — BUMPER REPLACEMENT
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-bumper-replacement',
    title: 'Bumper Replacement (Front or Rear)',
    vehicle: { make: 'Jeep', model: 'Wrangler', generations: [{ startYear: 1997, endYear: 2006 }] },
    video: {
      youtubeId: 'ujGr2A6J7cQ',
      title: 'Bumper install on Jeep Wrangler TJ',
      channel: 'Jeep 2.4',
    },
    source: 'Generated by GarageIQ AI from publicly available sources. Cross-checked per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Suspension & Steering',
    difficulty: 'intermediate',
    duration: '1-2 hours',
    interval: 'Replace when bent, rusted, or upgrading to off-road steel bumper.',
    description: 'TJ bumpers (front and rear) are bolted to the frame. Replacement is simple R&R — useful for upgrade to steel bumpers (Smittybilt, ARB, etc.).',
    overview: { difficulty: 'Intermediate — heavy steel bumpers need helper', estimatedTime: '1-2 hours', estimatedCost: '$200-800 depending on style', whyMatters: 'Off-road bumpers improve approach/departure angles and add winch mounting.', safetyNote: 'Heavy.' },
    tools: ['Sockets (large)', 'Penetrating oil', 'Helper(s)', 'Eye protection'],
    parts: [{ id: 'bumper-replacement', name: 'Bumper (Steel or OEM-style)', partNumber: 'Smittybilt SRC / ARB DELUXE / Mopar replacement', quantity: 1, price: 400, category: 'required', notes: 'Many options available.' }],
    safetyInformation: 'Bumpers heavy. Get helper. Drop hazard.',
    beforeYouStart: ['Have bumper, mounting hardware', 'Helper(s)'],
    steps: [
      { number: 1, title: 'Lift Vehicle (If Helpful)', description: 'Optional — gives more access.', why: 'Access.', tip: 'Stands.', commonMistake: 'Inadequate clearance.' },
      { number: 2, title: 'Disconnect Auxiliary (If Applicable)', description: 'Lights, winch, license plate.', why: 'Frees connections.', tip: 'Photograph wiring.', commonMistake: 'Tearing wires.' },
      { number: 3, title: 'Remove Bumper Mounting Bolts', description: 'Typically 4 bolts, 2 each side. Apply penetrating oil.', why: 'Frees bumper.', tip: 'Note bolt sizes.', commonMistake: 'Forcing.' },
      { number: 4, title: 'Lift Bumper Off (Helper Required)', description: 'Steel bumpers heavy. Get helper.', why: 'Removal.', tip: 'Lift evenly.', commonMistake: 'Solo.' },
      { number: 5, title: 'Compare Old to New', description: 'Verify mounting holes match.', why: 'Pre-install check.', tip: 'Adapter brackets sometimes needed.', commonMistake: 'Wrong-fit bumper.' },
      { number: 6, title: 'Position New Bumper', description: 'Lift new bumper into position.', why: 'Install.', tip: 'Helper.', commonMistake: 'Solo.' },
      { number: 7, title: 'Install Mounting Bolts', description: 'Hand-thread bolts, cross-tighten.', why: 'Secure.', tip: 'Anti-seize.', commonMistake: 'Cross-threading.' },
      { number: 8, title: 'Torque to Spec', description: '70-90 ft-lbs typical.', why: 'Final.', tip: 'Cross-pattern.', commonMistake: 'Uneven.' },
      { number: 9, title: 'Reconnect Auxiliary', description: 'Lights, winch, license plate.', why: 'Restore function.', tip: 'Test each.', commonMistake: 'Forgetting.' },
      { number: 10, title: 'Test Drive', description: 'Verify bumper secure. Test all functions.', why: 'Confirm.', tip: 'No movement.', commonMistake: 'Skipping.' },
    ],
    torqueSpecifications: [{ fastener: 'Bumper mounting bolts', specification: '70-90 ft-lbs (95-122 Nm).', notes: 'Verify torque spec from OEM service manual.' }],
    fluidSpecifications: [],
    troubleshooting: [{ problem: 'Bumper sags', likelyCause: 'Loose bolts', whatToCheck: 'Re-torque', fix: 'Tighten.' }],
    proTips: ['Anti-seize on bolts.', 'Helper saves drops.', 'Off-road bumpers add weight; affects suspension.'],
    commonMistakes: [{ mistake: 'Wrong bolt torque', consequence: 'Bumper loose.' }],
    successIndicators: ['Even fit', 'Secure', 'All functions work'],
    whenToSeekHelp: 'Major bumper upgrades may need bracket modifications.',
    legalDisclaimer: 'Verify with installation instructions.',
    manualSource: { title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)', url: 'https://www.haynes.com/en-us/wrangler/jeep/2003', notes: 'Cross-reference bumper layout.' },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — TUB CORROSION REPAIR
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-tub-corrosion-repair',
    title: 'Tub (Body) Corrosion Repair',
    vehicle: { make: 'Jeep', model: 'Wrangler', generations: [{ startYear: 1997, endYear: 2006 }] },
    video: {
      youtubeId: 'LBVdEBmGxCc',
      title: 'Jeep TJ Tub/Frame Rust Repair Reference',
      channel: 'Rust Revival Garage',
    },
    source: 'Generated by GarageIQ AI from publicly available sources. Cross-checked per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Suspension & Steering',
    difficulty: 'advanced',
    duration: 'Varies — 6-20 hours depending on extent',
    interval: 'Repair when tub shows visible rust, holes, or compromised structure.',
    description: 'TJ tub (the body shell) corrodes in salt-belt areas. Repair from minor patch to full tub replacement. Major rust may warrant body replacement.',
    overview: { difficulty: 'Advanced — body work, welding', estimatedTime: '6-20 hours', estimatedCost: '$50-300 materials; $1500+ professional', whyMatters: 'Tub rust compromises structure.', safetyNote: 'Welding hazards.' },
    tools: ['MIG welder', 'Angle grinder', 'Body filler', 'Body file', 'Steel patch material', 'Por-15 coating', 'Welding helmet', 'Eye protection'],
    parts: [{ id: 'tub-patch-steel', name: 'Steel Patch Material', partNumber: '12x12 sheet 18 gauge', quantity: 1, price: 25, category: 'required', notes: 'Match thickness to original.' }, { id: 'tub-coating', name: 'Por-15 Rust Coating', partNumber: 'Por-15', quantity: 1, price: 30, category: 'required', notes: 'Prevention.' }, { id: 'tub-filler', name: 'Body Filler', partNumber: 'Bondo', quantity: 1, price: 15, category: 'optional', notes: 'For cosmetic finish.' }],
    safetyInformation: 'Welding hazards.',
    beforeYouStart: ['Assess rust extent', 'Plan repair scope'],
    steps: [
      { number: 1, title: 'Inspect Tub Thoroughly', description: 'Check all areas: rocker panels, corners, floor edges, around windshield.', why: 'Plan.', tip: 'Photograph.', commonMistake: 'Surface inspection only.' },
      { number: 2, title: 'Determine Repair Scope', description: 'Minor surface = grind, prime, paint. Holes = patch and weld. Major = professional or body replacement.', why: 'Direction.', tip: 'Be honest about skill.', commonMistake: 'Underestimating.' },
      { number: 3, title: 'Cut Out Rusted Material', description: 'Use cutoff wheel beyond rust into solid metal.', why: 'Foundation.', tip: 'Generous cut.', commonMistake: 'Insufficient.' },
      { number: 4, title: 'Wire-Brush Surrounding Area', description: 'Clean around repair to bare metal.', why: 'Welding prep.', tip: 'Bare metal essential.', commonMistake: 'Welding on dirty.' },
      { number: 5, title: 'Cut Patch Piece', description: 'Match repair area shape with patch.', why: 'Patch.', tip: 'Test fit.', commonMistake: 'Wrong size.' },
      { number: 6, title: 'Tack-Weld Patch', description: 'Tack at corners and midpoints.', why: 'Hold.', tip: 'Multiple tacks.', commonMistake: 'Heavy tack.' },
      { number: 7, title: 'Continuous Weld', description: 'Weld around perimeter.', why: 'Seal.', tip: 'Slow movement.', commonMistake: 'Cold welds.' },
      { number: 8, title: 'Grind and Smooth', description: 'Grind welds smooth.', why: 'Cosmetic.', tip: 'Don\'t over-grind.', commonMistake: 'Through metal.' },
      { number: 9, title: 'Apply Body Filler if Needed', description: 'Smooth surface with body filler. Sand smooth.', why: 'Cosmetic.', tip: 'Multiple coats.', commonMistake: 'Heavy single coat.' },
      { number: 10, title: 'Prime, Paint, and Coat', description: 'Prime, paint to match. Apply Por-15 underside.', why: 'Protection.', tip: 'Color match TJ.', commonMistake: 'Skipping primer.' },
    ],
    torqueSpecifications: [],
    fluidSpecifications: [],
    troubleshooting: [{ problem: 'Re-rust quickly', likelyCause: 'Inadequate prep or coating', whatToCheck: 'Coverage', fix: 'Reapply coating.' }],
    proTips: ['Por-15 is gold standard.', 'Body work requires patience.', 'Major rust may not be cost-effective to repair.'],
    commonMistakes: [{ mistake: 'Patching extensive rust', consequence: 'Repeated repairs.' }],
    successIndicators: ['Solid welds', 'Even paint', 'No visible rust', 'Coated underside'],
    whenToSeekHelp: 'Body shop for cosmetic finish work.',
    legalDisclaimer: 'Welding hazards.',
    manualSource: { title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)', url: 'https://www.haynes.com/en-us/wrangler/jeep/2003', notes: 'Cross-reference body diagrams.' },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — AUTO TRANSMISSION PAN GASKET / FILTER (32RH/42RLE)
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-auto-trans-pan-gasket-filter',
    title: 'Automatic Transmission Pan Gasket and Filter Service',
    vehicle: { make: 'Jeep', model: 'Wrangler', generations: [{ startYear: 1997, endYear: 2006 }] },
    video: {
      youtubeId: 'llW2ktoiinQ',
      title: 'How to do a transmission fluid flush and filter change jeep wrangler TJ',
      channel: 'YouTube',
    },
    source: 'Generated by GarageIQ AI from publicly available sources. Cross-checked per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Engine & Drivetrain',
    difficulty: 'intermediate',
    duration: '1-2 hours',
    interval: 'Service every 30,000 miles or after fluid is dark/burnt-smelling. Critical preventive maintenance for automatic transmission longevity.',
    description: 'TJ automatic transmissions (32RH 3-speed early, 42RLE 4-speed later) accumulate metal wear particles in the pan and filter. Periodic pan drop, filter replacement, and fresh fluid extends transmission life significantly.',
    overview: { difficulty: 'Intermediate — fluid spillage, RTV sealing', estimatedTime: '1-2 hours', estimatedCost: '$30-60 for filter, gasket, fluid', whyMatters: 'Preventive maintenance for transmission longevity.', safetyNote: 'Fluid is hot when warm. Allow cooling.' },
    tools: ['Sockets (10-13mm)', 'Drain pan (large)', 'RTV silicone (for some pans)', 'Gasket scraper', 'Eye protection'],
    parts: [{ id: 'auto-trans-filter', name: 'Auto Trans Filter Kit', partNumber: 'Mopar 5013470 (32RH) / 4799879 (42RLE)', quantity: 1, price: 25, category: 'required', notes: 'Includes gasket and filter.' }, { id: 'atf-fluid', name: 'ATF+4 Transmission Fluid', partNumber: 'Mopar 68218057AB', quantity: 4, price: 8, category: 'required', notes: 'Plan for 4-6 quarts.' }],
    safetyInformation: 'Fluid hot. Allow cooling.',
    beforeYouStart: ['Park on level ground', 'Have drain pan ready', 'Stage parts'],
    steps: [
      { number: 1, title: 'Lift Vehicle', description: 'Raise on stands.', why: 'Access.', tip: 'Even.', commonMistake: 'Uneven.' },
      { number: 2, title: 'Position Drain Pan', description: 'Under transmission.', why: 'Catch fluid.', tip: 'Large pan.', commonMistake: 'Skipping.' },
      { number: 3, title: 'Loosen Pan Bolts (Don\'t Remove)', description: 'Loosen all bolts halfway. Tilt pan to drain.', why: 'Controlled drain.', tip: 'Don\'t remove all at once.', commonMistake: 'Sudden flood.' },
      { number: 4, title: 'Remove Pan and Filter', description: 'Once drained, remove all bolts. Pan comes off.', why: 'Access to filter.', tip: 'Note bolt locations.', commonMistake: 'Stripping.' },
      { number: 5, title: 'Replace Filter', description: 'Pull old filter off (small clip or bolts). Install new filter.', why: 'Clean filter.', tip: 'Note O-ring location.', commonMistake: 'Reusing filter.' },
      { number: 6, title: 'Clean Pan and Magnet', description: 'Clean pan thoroughly. Inspect magnet for metal accumulation.', why: 'Quality check.', tip: 'Some metal normal; lots = trans wear.', commonMistake: 'Skipping inspection.' },
      { number: 7, title: 'Install New Gasket', description: 'Apply gasket per filter kit instructions. Some require RTV; most are dry-fit.', why: 'Seal.', tip: 'Verify gasket type.', commonMistake: 'Mixing methods.' },
      { number: 8, title: 'Reinstall Pan', description: 'Hand-thread bolts. Cross-tighten in stages to spec (typically 12 ft-lbs).', why: 'Even sealing.', tip: 'Star pattern.', commonMistake: 'Uneven torque.' },
      { number: 9, title: 'Refill Transmission', description: 'Add 4-6 quarts ATF+4 through dipstick tube. Start engine, cycle through gears.', why: 'Fill.', tip: 'Hot check level on dipstick.', commonMistake: 'Cold check.' },
      { number: 10, title: 'Check for Leaks and Verify', description: 'Drive 25 miles. Inspect pan area for leaks.', why: 'Verify.', tip: 'Park on cardboard for slow drips.', commonMistake: 'Skipping leak check.' },
    ],
    torqueSpecifications: [{ fastener: 'Transmission pan bolts', specification: '12 ft-lbs (16 Nm). Verify torque spec from OEM service manual.', notes: 'Cross-tighten.' }],
    fluidSpecifications: [{ fluid: 'Automatic Transmission Fluid', type: 'ATF+4 (Mopar)', capacity: '4-6 quarts (pan drop only; full system 9 quarts)', notes: 'Use ONLY ATF+4.' }],
    troubleshooting: [{ problem: 'Leak after install', likelyCause: 'Uneven torque or damaged gasket', whatToCheck: 'Visual inspection', fix: 'Re-torque or replace gasket.' }, { problem: 'Trans shifts roughly', likelyCause: 'Low fluid or wrong fluid type', whatToCheck: 'Verify level and type', fix: 'Top off; flush if wrong type used.' }],
    proTips: ['Service every 30k miles.', 'ATF+4 is mandatory.', 'Magnet inspection reveals trans health.', 'Hot check fluid level — cold reads inaccurately.'],
    commonMistakes: [{ mistake: 'Using non-ATF+4 fluid', consequence: 'Damages transmission seals.' }],
    successIndicators: ['No leaks', 'Smooth shifting', 'Correct fluid level'],
    whenToSeekHelp: 'Major trans issues require professional diagnosis.',
    legalDisclaimer: 'Verify fluid specifications against OEM.',
    manualSource: { title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)', url: 'https://www.haynes.com/en-us/wrangler/jeep/2003', notes: 'Cross-reference transmission service.' },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — SLIP YOKE ELIMINATOR (SYE) INSTALL
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-slip-yoke-eliminator-install',
    title: 'Slip Yoke Eliminator (SYE) Installation',
    vehicle: { make: 'Jeep', model: 'Wrangler', generations: [{ startYear: 1997, endYear: 2006 }] },
    video: {
      youtubeId: 'eiTpIHIh2yA',
      title: 'How to Install a Slip Yoke Eliminator and Driveshaft for a Jeep Wrangler TJ',
      channel: 'Quadratec',
    },
    source: 'Generated by GarageIQ AI from publicly available sources. Cross-checked per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Engine & Drivetrain',
    difficulty: 'advanced',
    duration: '4-6 hours',
    interval: 'Install after lifting TJ 3+ inches to correct rear driveshaft angles, eliminate vibration, and prevent transfer case damage.',
    description: 'SYE replaces the slip yoke on the rear of the transfer case with a fixed yoke that uses a CV-style driveshaft. Eliminates pinion angle issues on lifted TJs.',
    overview: { difficulty: 'Advanced — transfer case work', estimatedTime: '4-6 hours', estimatedCost: '$300-500 for SYE kit + $150-300 for CV driveshaft', whyMatters: 'Prevents drivetrain damage on lifted TJs.', safetyNote: 'Heavy components.' },
    tools: ['Sockets', 'Snap ring pliers', 'Drift pin', 'Hammer', 'Drain pan', 'Eye protection'],
    parts: [{ id: 'sye-kit', name: 'Slip Yoke Eliminator Kit', partNumber: 'JB Conversions / TeraFlex / Advance Adapters', quantity: 1, price: 350, category: 'required', notes: 'Quality matters.' }, { id: 'cv-driveshaft', name: 'CV-style Rear Driveshaft', partNumber: 'Tom Wood / Adams', quantity: 1, price: 250, category: 'required', notes: 'CV joint at transfer case end.' }],
    safetyInformation: 'Transfer case fluid and components. Heavy.',
    beforeYouStart: ['Drain transfer case fluid', 'Have SYE kit and CV driveshaft', 'Read kit instructions'],
    steps: [
      { number: 1, title: 'Lift Vehicle and Drain Transfer Case', description: 'Raise. Drain transfer case fluid.', why: 'Required.', tip: 'See transfer case service guide.', commonMistake: 'Forgetting drain.' },
      { number: 2, title: 'Disconnect Rear Driveshaft', description: 'Remove from rear axle and transfer case yoke.', why: 'Frees driveshaft.', tip: 'Mark orientation.', commonMistake: 'Forgetting marks.' },
      { number: 3, title: 'Remove Old Slip Yoke', description: 'Slip yoke pulls off transfer case output. Snap ring may secure.', why: 'Frees yoke.', tip: 'Pliers for snap ring.', commonMistake: 'Forcing.' },
      { number: 4, title: 'Open Transfer Case', description: 'Per SYE kit instructions, may need to open transfer case to install new fixed yoke.', why: 'Replacement.', tip: 'Follow kit specifically.', commonMistake: 'Generic procedure.' },
      { number: 5, title: 'Install New Fixed Yoke', description: 'Install per kit instructions. May involve new bearings, snap rings.', why: 'Replacement.', tip: 'Follow instructions.', commonMistake: 'Skipping steps.' },
      { number: 6, title: 'Reseal Transfer Case', description: 'Apply RTV or new gasket per kit.', why: 'Sealing.', tip: 'Cure time.', commonMistake: 'Skipping cure.' },
      { number: 7, title: 'Install CV Driveshaft', description: 'Attach CV shaft to fixed yoke and rear axle.', why: 'New driveshaft.', tip: 'Verify CV orientation.', commonMistake: 'Reversed.' },
      { number: 8, title: 'Refill Transfer Case', description: 'Add ATF+4 to fill plug.', why: 'Operation.', tip: 'See guide.', commonMistake: 'Insufficient.' },
      { number: 9, title: 'Adjust Pinion Angle', description: 'After install, verify pinion angle. Adjust shims if needed.', why: 'Geometry.', tip: 'Reference angles.', commonMistake: 'Wrong angle.' },
      { number: 10, title: 'Test Drive', description: 'Drive — verify no vibration. Adjust if needed.', why: 'Confirm.', tip: 'Various speeds.', commonMistake: 'Calling done early.' },
    ],
    torqueSpecifications: [],
    fluidSpecifications: [{ fluid: 'Transfer case fluid', type: 'ATF+4', capacity: '1.25-1.6 quarts', notes: 'Refill after drain.' }],
    troubleshooting: [{ problem: 'Vibration after install', likelyCause: 'Pinion angle off', whatToCheck: 'Measure angle', fix: 'Adjust shims.' }],
    proTips: ['SYE kit + CV driveshaft together is the right way.', 'Pinion angle measurement is essential.', 'Quality CV driveshaft (Tom Wood, Adams) lasts longer.'],
    commonMistakes: [{ mistake: 'Using stock driveshaft with SYE', consequence: 'Vibration; damage.' }],
    successIndicators: ['No vibration', 'Smooth highway driving', 'Properly angled pinion'],
    whenToSeekHelp: 'SYE work is involved. Professional install available.',
    legalDisclaimer: 'Verify with kit instructions and OEM.',
    manualSource: { title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)', url: 'https://www.haynes.com/en-us/wrangler/jeep/2003', notes: 'Cross-reference transfer case.' },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — TRANSFER CASE SHIFT LINKAGE
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-transfer-case-shift-linkage',
    title: 'Transfer Case Shift Linkage Adjustment',
    vehicle: { make: 'Jeep', model: 'Wrangler', generations: [{ startYear: 1997, endYear: 2006 }] },
    video: {
      youtubeId: 'LCe83Wgva7c',
      title: 'Project TJ Wrangler: How to Adjust 4WD Shift Linkage',
      channel: 'UndergroundTrev',
    },
    source: 'Generated by GarageIQ AI from publicly available sources. Cross-checked per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Engine & Drivetrain',
    difficulty: 'beginner',
    duration: '30-45 minutes',
    interval: 'Adjust when transfer case lever is hard to shift, doesn\'t fully engage 4WD positions, or rattles.',
    description: 'The transfer case shift linkage connects the cabin lever to the case. Wear or misadjustment causes shifting issues.',
    overview: { difficulty: 'Beginner — adjustment only', estimatedTime: '30-45 minutes', estimatedCost: '$0-30 for new bushings if needed', whyMatters: 'Proper engagement of 4WD modes.', safetyNote: 'Standard.' },
    tools: ['Sockets', 'Pliers', 'Eye protection'],
    parts: [{ id: 'shift-linkage-bushings', name: 'Shift Linkage Bushings', partNumber: 'Mopar 5083227', quantity: 2, price: 12, category: 'optional', notes: 'Replace if worn.' }],
    safetyInformation: 'Standard.',
    beforeYouStart: ['Test current shifter behavior', 'Have new bushings if old worn'],
    steps: [
      { number: 1, title: 'Lift Vehicle for Access', description: 'Raise to access linkage.', why: 'Visibility.', tip: 'Stands.', commonMistake: 'Inadequate clearance.' },
      { number: 2, title: 'Identify Linkage Components', description: 'Cabin lever connects via rod and bushings to transfer case lever.', why: 'Visual ID.', tip: 'Trace the rod.', commonMistake: 'Wrong linkage.' },
      { number: 3, title: 'Inspect Bushings', description: 'Check rubber bushings at each pivot for wear/tear.', why: 'Diagnosis.', tip: 'Worn bushings cause sloppy shift.', commonMistake: 'Skipping.' },
      { number: 4, title: 'Replace Worn Bushings (If Needed)', description: 'Pop old bushings, install new. Lubricate.', why: 'Restoration.', tip: 'White lithium grease.', commonMistake: 'Dry install.' },
      { number: 5, title: 'Adjust Linkage Length', description: 'If linkage is adjustable, set length to neutral (center) position so cabin lever aligns with detents.', why: 'Calibration.', tip: 'Verify each position engages cleanly.', commonMistake: 'Misalignment.' },
      { number: 6, title: 'Lock Adjustment', description: 'Tighten any locking nuts.', why: 'Secures setting.', tip: 'Don\'t overtighten.', commonMistake: 'Loose.' },
      { number: 7, title: 'Test Each Position', description: 'Cabin: 2H, 4H, N, 4L. Each should engage cleanly with no excess slop.', why: 'Verification.', tip: 'Drive in each position briefly.', commonMistake: 'Test only one.' },
      { number: 8, title: 'Verify Engagement', description: 'In 4H, both axles should turn together; visible lock-up.', why: 'Function.', tip: 'Test on loose surface.', commonMistake: 'No actual engagement test.' },
      { number: 9, title: 'Re-adjust If Needed', description: 'If positions don\'t fully engage, fine-tune length.', why: 'Iteration.', tip: 'Small adjustments.', commonMistake: 'Big changes.' },
      { number: 10, title: 'Verify No Rattle or Slop', description: 'Driver should feel solid detent at each position.', why: 'Final.', tip: 'Quality of feel.', commonMistake: 'Skipping.' },
    ],
    torqueSpecifications: [],
    fluidSpecifications: [],
    troubleshooting: [{ problem: '4WD doesn\'t engage', likelyCause: 'Adjustment off or internal transfer case', whatToCheck: 'Linkage motion at case', fix: 'Adjust or transfer case service.' }],
    proTips: ['White lithium grease on bushings.', 'Quality of detent matters.', 'Linkage adjustment is iterative.'],
    commonMistakes: [{ mistake: 'Skipping bushing replacement', consequence: 'Sloppy shift returns.' }],
    successIndicators: ['Solid detents', 'Clean engagement', 'No rattle'],
    whenToSeekHelp: 'If linkage adjustment doesn\'t resolve, internal transfer case may need service.',
    legalDisclaimer: 'Verify procedures.',
    manualSource: { title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)', url: 'https://www.haynes.com/en-us/wrangler/jeep/2003', notes: 'Cross-reference transfer case linkage.' },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — DIFFERENTIAL CARRIER / SPIDER GEAR SERVICE
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-differential-carrier-spider-gears',
    title: 'Differential Carrier and Spider Gear Service',
    vehicle: { make: 'Jeep', model: 'Wrangler', generations: [{ startYear: 1997, endYear: 2006 }] },
    source: 'Generated by GarageIQ AI from publicly available sources. Cross-checked per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Engine & Drivetrain',
    difficulty: 'advanced',
    duration: '4-8 hours',
    interval: 'Service when differential clicks or whines, axle shaft pulls easily out (broken spider), or planning carrier upgrade (locker install).',
    description: 'The differential carrier holds the spider gears that allow wheels to turn at different speeds. Failure modes: chipped or broken spider gears, worn axle splines, or cracked carrier. Service is involved — differential R&R required.',
    overview: { difficulty: 'Advanced — gear adjustment requires precision', estimatedTime: '4-8 hours', estimatedCost: '$80-150 for spider gear kit; $300-600 for full carrier replacement', whyMatters: 'Failed spiders cause vehicle immobility.', safetyNote: 'Heavy gears, precision adjustment.' },
    tools: ['Sockets', 'Bearing puller and installer', 'Dial indicator', 'Drift pin', 'Bearing press', 'Eye protection'],
    parts: [{ id: 'spider-gear-kit', name: 'Spider Gear Kit', partNumber: 'Mopar 5083449 (Dana 30) — equivalent: Yukon GP-D30', quantity: 1, price: 100, category: 'required', notes: 'Match axle type.' }, { id: 'carrier-bearings', name: 'Carrier Bearings', partNumber: 'Timken HM89446', quantity: 2, price: 30, category: 'recommended', notes: 'Replace with carrier work.' }],
    safetyInformation: 'Heavy components. Precision required.',
    beforeYouStart: ['Drain differential fluid', 'Have all parts staged', 'Tools and shop manual'],
    steps: [
      { number: 1, title: 'Drain and Disassemble Differential', description: 'Per Differential service guides — drain, remove cover, remove carrier.', why: 'Access.', tip: 'See related guides.', commonMistake: 'Skipping.' },
      { number: 2, title: 'Remove Carrier from Housing', description: 'Mark side bearings; remove side cap bolts.', why: 'Access to carrier.', tip: 'Mark sides.', commonMistake: 'Forgetting marks.' },
      { number: 3, title: 'Disassemble Carrier (For Spider Gear Service)', description: 'Pinion shaft pin holds spider gears. Drive out pin and remove gears.', why: 'Spider access.', tip: 'Note orientations.', commonMistake: 'Confusing.' },
      { number: 4, title: 'Inspect Components', description: 'Check spider gears, side gears, axle splines for damage.', why: 'Diagnosis.', tip: 'Replace any chipped.', commonMistake: 'Reusing damaged.' },
      { number: 5, title: 'Reassemble with New Spiders', description: 'Install new spider gears, side gears with new shims as needed.', why: 'Replacement.', tip: 'Lubricate gears with assembly oil.', commonMistake: 'Dry install.' },
      { number: 6, title: 'Install Pinion Shaft Pin', description: 'Drive pin back in.', why: 'Securing.', tip: 'New pin if old damaged.', commonMistake: 'Reusing bent pin.' },
      { number: 7, title: 'Install Carrier with Original Bearings', description: 'Side bearings on each end of carrier. Use original side caps and shims if existing setup was correct.', why: 'Reassembly.', tip: 'Backlash adjustment may be needed.', commonMistake: 'Wrong shim placement.' },
      { number: 8, title: 'Verify Backlash with Dial Indicator', description: 'Backlash should be 0.005-0.009". Adjust shims if outside range.', why: 'Critical adjustment.', tip: 'Precision tool.', commonMistake: 'Skipping measurement.' },
      { number: 9, title: 'Reassemble Differential', description: 'Reattach cover with RTV. Refill fluid.', why: 'Closure.', tip: 'See related guides.', commonMistake: 'Insufficient sealing.' },
      { number: 10, title: 'Test Drive', description: 'Drive carefully; listen for noise. Verify normal operation.', why: 'Confirm.', tip: 'Several drive cycles.', commonMistake: 'Aggressive driving.' },
    ],
    torqueSpecifications: [{ fastener: 'Side cap bolts', specification: '60 ft-lbs (81 Nm).', notes: 'Verify torque spec from OEM service manual.' }],
    fluidSpecifications: [{ fluid: 'Differential gear oil', type: '75W-90 GL-5', capacity: '1-2.5 quarts depending on differential', notes: 'See diff fluid guide.' }],
    troubleshooting: [{ problem: 'Differential noise after service', likelyCause: 'Backlash off or wrong shim placement', whatToCheck: 'Re-measure backlash', fix: 'Adjust shims.' }],
    proTips: ['Backlash measurement is critical.', 'Dial indicator essential.', 'Spider gear kits include all wear items.'],
    commonMistakes: [{ mistake: 'Skipping backlash adjustment', consequence: 'Differential noise; premature wear.' }],
    successIndicators: ['Quiet differential', 'Backlash within spec', 'No leaks'],
    whenToSeekHelp: 'Differential gear setup is precision work. Professional rebuild may be safer than DIY for first-timers.',
    legalDisclaimer: 'Precision work — improper setup damages differential.',
    manualSource: { title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)', url: 'https://www.haynes.com/en-us/wrangler/jeep/2003', notes: 'Cross-reference differential service.' },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — COOLANT OVERFLOW / RESERVOIR TANK
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-coolant-overflow-tank-replacement',
    title: 'Coolant Overflow / Reservoir Tank Replacement',
    vehicle: { make: 'Jeep', model: 'Wrangler', generations: [{ startYear: 1997, endYear: 2006 }] },
    video: {
      youtubeId: 'ZNVn0v3aOWA',
      title: 'How to Replace Coolant Reservoir 1997-2006 Jeep Wrangler',
      channel: 'TRQ',
    },
    source: 'Generated by GarageIQ AI from publicly available sources. Cross-checked per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Cooling & Heating',
    difficulty: 'beginner',
    duration: '20-30 minutes',
    interval: 'Replace when tank cracks (visible leak) or becomes brittle/yellowed beyond practical use.',
    description: 'TJ overflow reservoir is a plastic tank that catches expanding coolant from the radiator. Cracks with age cause coolant loss.',
    overview: { difficulty: 'Beginner — direct R&R', estimatedTime: '20-30 minutes', estimatedCost: '$25-40', whyMatters: 'Cracked tank loses coolant.', safetyNote: 'Allow engine to cool.' },
    tools: ['Sockets (10mm)', 'Hose clamp pliers', 'Drain pan', 'Eye protection'],
    parts: [{ id: 'coolant-overflow-tank', name: 'Coolant Overflow Tank', partNumber: 'Mopar 55174691', quantity: 1, price: 30, category: 'required', notes: 'OEM-style replacement.' }],
    safetyInformation: 'Coolant hot when warm. Allow cooling.',
    beforeYouStart: ['Allow engine cooling', 'Have new tank'],
    steps: [
      { number: 1, title: 'Allow Cooling', description: 'Engine cool to touch.', why: 'Pressure release.', tip: 'Hour minimum.', commonMistake: 'Working hot.' },
      { number: 2, title: 'Locate Overflow Tank', description: 'Engine bay, mounted to fender or near radiator.', why: 'Visual ID.', tip: 'Translucent plastic.', commonMistake: 'Confusing.' },
      { number: 3, title: 'Disconnect Tank Hose', description: 'Hose connects tank to radiator filler. Loosen clamp, pull off.', why: 'Frees tank.', tip: 'Fluid will drip.', commonMistake: 'Damaging hose.' },
      { number: 4, title: 'Remove Tank Mounting Bolts', description: '2-3 bolts hold tank to bracket.', why: 'Frees tank.', tip: 'Note.', commonMistake: 'Stripping.' },
      { number: 5, title: 'Lift Tank Out', description: 'Lift away.', why: 'Removed.', tip: 'Drain remaining fluid into pan.', commonMistake: 'Spilling.' },
      { number: 6, title: 'Install New Tank', description: 'Position. Hand-thread bolts.', why: 'Replacement.', tip: 'Don\'t overtighten plastic.', commonMistake: 'Cracking.' },
      { number: 7, title: 'Reconnect Hose', description: 'Slide hose, tighten clamp.', why: 'Restore flow.', tip: 'Snug only.', commonMistake: 'Crushed clamp.' },
      { number: 8, title: 'Refill Coolant', description: 'Fill tank to FULL COLD line.', why: 'Operation.', tip: 'Use correct coolant.', commonMistake: 'Wrong fluid.' },
      { number: 9, title: 'Test for Leaks', description: 'Start engine. After warm-up, inspect tank area.', why: 'Verify.', tip: 'Pressure test ideal.', commonMistake: 'Skipping.' },
      { number: 10, title: 'Drive and Verify Level Holds', description: 'Drive 25 miles. Check level after cool-down.', why: 'Confirms.', tip: 'Park on cardboard for slow drips.', commonMistake: 'Skipping verification.' },
    ],
    torqueSpecifications: [],
    fluidSpecifications: [{ fluid: 'Coolant top-off', type: 'Match existing', capacity: '~1 quart', notes: 'Fresh fluid recommended.' }],
    troubleshooting: [{ problem: 'Leak at hose', likelyCause: 'Loose clamp', whatToCheck: 'Tighten', fix: 'Re-tighten or replace.' }],
    proTips: ['Replace clamps with tank.', 'Inspect cap and verify rating.'],
    commonMistakes: [{ mistake: 'Cracking tank during install', consequence: 'New tank waste.' }],
    successIndicators: ['No leaks', 'Coolant level holds'],
    whenToSeekHelp: 'If cooling system has other issues, address those.',
    legalDisclaimer: 'Coolant toxic.',
    manualSource: { title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)', url: 'https://www.haynes.com/en-us/wrangler/jeep/2003', notes: 'Cross-reference cooling layout.' },
  },

  // ─────────────────────────────────────────────────────────────
  // V10.1 — COMPREHENSIVE PRE-TRIP / PRE-PURCHASE INSPECTION
  // ─────────────────────────────────────────────────────────────
  {
    id: 'tj-pre-trip-pre-purchase-inspection',
    title: 'Comprehensive Pre-Trip / Pre-Purchase Inspection',
    vehicle: { make: 'Jeep', model: 'Wrangler', generations: [{ startYear: 1997, endYear: 2006 }] },
    video: {
      youtubeId: 'WvpKHqZ2KKo',
      title: 'WHAT TO LOOK FOR WHEN BUYING A JEEP TJ',
      channel: 'YouTube',
    },
    source: 'Generated by GarageIQ AI from publicly available sources (Mopar service literature, NHTSA TSBs and recall information, used-vehicle inspection checklists, technician forums and Wrangler community service threads). Cross-checked and structured per the GarageIQ Automotive Repair Guide Generation System v10.1 methodology.',
    category: 'Routine Maintenance & Fluids',
    difficulty: 'beginner',
    duration: '1 - 2 hours (full inspection); 30 minutes (quick pre-trip)',
    interval: 'Perform before every long road trip and before purchasing any used TJ. Quick version (fluids, tires, lights) before any extended drive; full version monthly or before significant journeys.',
    description:
      'A single thorough inspection that catches the most common TJ failure points before they leave you stranded — and reveals the hidden issues that turn a "great deal" into a money pit. Walks through 27 checkpoints covering the engine bay, fluids, tires and brakes, steering and suspension, drivetrain, frame and underbody (including the famous TJ rear-shackle rust point), lights and electrical, interior, test drive observations, and OBD-II code scan. Use the full version before buying a used TJ or before a multi-day trip. Use the abbreviated version (steps marked QUICK) before any extended drive.',
    overview: {
      difficulty: 'Beginner — visual inspection plus a few simple tests. No disassembly required.',
      estimatedTime: '1-2 hours full; 30 minutes quick pre-trip',
      estimatedCost: '$0 (uses tools you should already own); $20 for an OBD-II scanner if not borrowing one',
      whyMatters: 'For pre-trip: catches the issues most likely to leave you stranded (low fluids, worn tires, weak battery, brake issues). For pre-purchase: surfaces hidden TJ-specific issues (frame rust, death wobble symptoms, head gasket signs, transmission wear) that cost thousands to repair after the fact.',
      safetyNote: 'Inspection only — no work under a moving vehicle. If lifting the vehicle for underbody inspection, use rated jack stands on level concrete. Engine should be cool for fluid checks. Have a flashlight and gloves handy. Bring a friend for second opinions on test drive observations.',
    },
    tools: [
      'Tire pressure gauge (digital or quality dial)',
      'Tire tread depth gauge (or a US penny / quarter)',
      'Flashlight or headlamp (LED, bright)',
      'OBD-II scanner (basic $20 reader; chain auto-parts stores read codes free)',
      'Multimeter (for battery and electrical testing)',
      'Floor jack and one set of jack stands (for underbody portions)',
      'Mirror on a stick (small, for inspecting hidden underbody areas)',
      'Tape measure',
      'Magnetic parts tray (for inspecting drained fluids)',
      'Clean white paper towels (for fluid inspection)',
      'Phone or notebook (for documentation)',
      'Disposable nitrile gloves',
    ],
    parts: [
      { id: 'inspection-supplies', name: 'Inspection Supplies (none required)', partNumber: 'N/A', quantity: 1, price: 0, category: 'optional', notes: 'No replacement parts needed for inspection. Have a fluid top-off ready if levels are low.' },
      { id: 'engine-oil-topoff', name: 'Engine Oil (1 quart for top-off if needed)', partNumber: '5W-30 or 10W-30', quantity: 1, price: 6, category: 'optional', notes: 'Useful to have on hand if oil level is low.' },
      { id: 'coolant-topoff-2', name: 'Coolant (1 quart for top-off if needed)', partNumber: 'Mopar HOAT 5012394AB', quantity: 1, price: 6, category: 'optional', notes: 'Match what is in the system.' },
    ],
    safetyInformation:
      'Inspection only — no disassembly involves moving parts under load. For underbody inspection, use rated jack stands on level concrete. Allow engine to cool fully before checking coolant level (1+ hours). Used coolant and motor oil are toxic — clean any spills. For pre-purchase inspection, bring a friend; second opinions catch issues you might miss. Do not drive a vehicle with confirmed safety issues (worn brakes, ball joint play, frame rust, etc.) — address findings before extended driving.',
    beforeYouStart: [
      'Park on level concrete with the engine cool to the touch (last drove 2+ hours ago for accurate fluid checks)',
      'Have all tools and supplies staged before starting',
      'For pre-purchase: verify VIN matches title and registration; confirm seller has clear title in hand',
      'For pre-purchase: ask for service records — frequency of oil changes is a strong proxy for overall care',
      'For pre-trip: do a "walk-around" mental inventory of any concerns from recent drives (noises, vibrations, leaks)',
      'Have a notebook or phone ready to document findings — this is the record of the inspection',
      'Plan 1-2 hours; do not rush',
    ],
    steps: [
      // ── PHASE 1: DOCUMENTATION & INITIAL OBSERVATIONS ─────────────
      {
        number: 1,
        title: 'Document Vehicle Identification (Pre-Purchase Only)',
        description:
          'Record the VIN from three locations: dashboard (visible through windshield), driver door jamb sticker, and frame stamp (on driver-side frame rail near firewall — TJs have a stamped VIN here). All three should match. Photograph each. Compare against the title document. Mismatched or scratched VINs are a major red flag — could indicate stolen or salvage vehicle. For pre-trip, skip this step.',
        why: 'VIN matching catches title fraud and salvage history. The frame VIN is the hardest to forge.',
        tip: 'Use a free VIN check at NHTSA.gov for recall history; pay for a Carfax/AutoCheck for theft and accident history.',
        commonMistake: 'Skipping this step. Stolen-and-cloned TJs are a real problem in the used market.',
      },
      {
        number: 2,
        title: 'Cold Start Observation (Pre-Purchase Only — IMPORTANT)',
        description:
          'For pre-purchase: ALWAYS request the vehicle be cold (sat overnight) when you arrive. Sellers will sometimes "warm up" a vehicle to mask cold-start issues. With key off, lift the hood and feel the engine — should be ambient temperature. Then have the seller start it. Note: how many cranks before fire? Any white/blue smoke from exhaust? Any rough running for the first 30-60 seconds? Any unusual ticks or knocks from the engine? Listen for 2 minutes. A healthy 4.0L starts in 1-2 cranks, idles smoothly within 30 seconds, and has minimal smoke. White smoke that does not clear within a few minutes can indicate head gasket leak. Blue smoke means oil burning. Rough idle that persists indicates fuel or sensor issue.',
        why: 'Cold start exposes more issues than any other single observation. Sellers know this and will often hide it.',
        tip: 'If seller refuses cold start access, walk away. There is something to hide.',
        commonMistake: 'Inspecting a warm vehicle. Cold start issues will not show.',
      },
      {
        number: 3,
        title: 'Initial Walk-Around (QUICK)',
        description:
          'Walk around the vehicle slowly. Note: paint condition (color match between panels — mismatch indicates accident repair), panel gaps (uneven gaps mean body work), tire wear pattern (uneven wear indicates alignment or suspension issues), visible rust (especially on the body tub corners and rocker panels), missing bolts or fasteners, aftermarket modifications. Note overall maintenance level — clean engine bay, fresh fluids, properly mounted accessories suggest care; greasy buildup, mismatched parts, and crude modifications suggest neglect.',
        why: 'First impressions reveal a lot. Walking slowly with eyes open catches issues that close inspection might miss.',
        tip: 'Photograph any concerns. Side-by-side panel comparison shots reveal color mismatch.',
        commonMistake: 'Skipping the walk-around in favor of deep inspection.',
      },

      // ── PHASE 2: ENGINE BAY ───────────────────────────────────────
      {
        number: 4,
        title: 'Check Engine Oil Level and Condition (QUICK)',
        description:
          'With engine cool, pull dipstick. Wipe clean, reinsert fully, pull again. Level should be between MIN and MAX marks. Inspect oil color: clean amber to dark brown is normal. Black is overdue for change. Milky/foamy white indicates coolant intrusion (head gasket failure or cracked head). Metal flakes indicate engine wear. Smell oil — burnt smell indicates overheating history. Place a drop on a clean white paper towel and watch it spread — even spread means clean oil; ring of contamination means dirty.',
        why: 'Oil tells the engine\'s story. Milky oil is a death sentence; metal flakes are a warning.',
        tip: 'Check under the oil cap too — milky residue here also indicates coolant in oil.',
        commonMistake: 'Just checking level without inspecting condition.',
      },
      {
        number: 5,
        title: 'Check Coolant Level and Condition (QUICK)',
        description:
          'NEVER open radiator cap on hot engine. With engine cool, check the overflow reservoir level — should be at FULL COLD line. Inspect coolant color through the reservoir: should be clean (typically yellow Mopar HOAT, green, pink, or orange depending on type). Brown/rusty coolant indicates corrosion. Oily film on coolant indicates head gasket or transmission cooler leak. With engine cool, briefly remove radiator cap and check for: oil floating on top (head gasket leak), brown sludge (corrosion), or low level despite full reservoir (system leak).',
        why: 'Coolant condition and level reveal cooling system health. Bad coolant predicts overheating.',
        tip: 'A used Mopar HOAT coolant should still be yellow/amber; if dark, system needs flushing.',
        commonMistake: 'Opening cap on a hot engine. Hot coolant burns severely.',
      },
      {
        number: 6,
        title: 'Check Brake Fluid Level and Condition',
        description:
          'Open hood and find the brake master cylinder reservoir (on driver side, near firewall, attached to brake booster). Reservoir is translucent — read level from outside without opening cap (opening introduces moisture). Level should be at MAX line. Low fluid indicates brake pad wear (more pad material out = more fluid in calipers) OR a leak. Inspect color: fresh brake fluid is clear amber; used fluid darkens to brown; very dark fluid is overdue for flush (every 2-3 years). Black fluid indicates internal master cylinder wear.',
        why: 'Brake fluid is hygroscopic — absorbs water over time. Dark fluid has water content that lowers boiling point.',
        tip: 'On TJ, brake reservoir is shared with clutch master cylinder — a single low level can mean brake or clutch issue.',
        commonMistake: 'Opening cap unnecessarily. Introduces moisture.',
      },
      {
        number: 7,
        title: 'Check Power Steering Fluid (TJ Uses ATF+4)',
        description:
          'Locate power steering reservoir on engine bay (typically passenger side). On TJ, ATF+4 is the correct fluid. Pull dipstick or check sight glass. Level should be at FULL when COLD or HOT line as appropriate. Inspect color: clean ATF+4 is red. Brown indicates old fluid. Black or burnt smell indicates pump damage. Check for air bubbles or foam — indicates air in system or low fluid. Smell — burnt smell indicates overheated pump.',
        why: 'Power steering issues often start as low fluid or air introduction.',
        tip: 'TJ owners sometimes mistakenly add power steering fluid (PSF). ATF+4 is correct — using PSF damages seals.',
        commonMistake: 'Adding wrong fluid type.',
      },
      {
        number: 8,
        title: 'Check Automatic Transmission Fluid (Auto Trans Only)',
        description:
          'For automatic transmissions: warm engine, transmission in PARK, parking brake on, engine running. Pull transmission dipstick. Wipe, reinsert, pull again. Level should be in HOT range. Inspect: clean ATF+4 is red. Brown = old fluid (overdue service). Black = burnt (transmission damage). Metal flakes = serious internal wear. Smell — burnt = transmission overheated. For manual transmissions: skip this step (no dipstick on TJ manual transmissions).',
        why: 'Trans fluid condition is the best diagnostic for transmission health on auto-equipped TJs.',
        tip: 'Burnt-smelling trans fluid + slipping = transmission close to failure. Walk away on used purchases.',
        commonMistake: 'Checking cold. Auto trans fluid level reads accurately only when warm.',
      },
      {
        number: 9,
        title: 'Inspect Belts and Hoses',
        description:
          'Engine OFF. Inspect serpentine belt: look for cracks, glazing (shiny smooth surface), missing chunks, or fraying. Press on the belt — should have firm tension with about 1/2" deflection. Inspect upper and lower radiator hoses by squeezing — should be firm, not soft or rock-hard. Look for swelling, cracks, or coolant residue at clamps. Inspect heater hoses (smaller hoses going through firewall). Inspect bypass and overflow hoses. Any soft hose, swollen hose, or visible cracking = needs replacement before a long trip.',
        why: 'Belt/hose failures are common on-trip breakdowns.',
        tip: 'Squeeze test reveals internal hose collapse that visual inspection misses.',
        commonMistake: 'Visual-only inspection. Internal hose failure shows as soft spots when squeezed.',
      },
      {
        number: 10,
        title: 'Inspect Battery and Test Charging System',
        description:
          'With engine off, check battery date sticker (most batteries last 4-5 years). Inspect terminals for corrosion (white/blue/green powder) — clean if needed. Check terminal tightness (snug, no rocking). Look for case cracks or bulging — replace immediately if present. Use multimeter at battery terminals: should read 12.4-12.7V at rest. Start engine. Voltage should rise to 13.8-14.4V (alternator charging). If voltage stays below 13.5V, alternator is weak. If voltage exceeds 15V, voltage regulator may be failing.',
        why: 'Battery age + condition + alternator output predicts starting reliability.',
        tip: 'Many auto-parts stores test batteries for free. Bring battery for definitive test.',
        commonMistake: 'Trusting voltage alone — a weak battery may show 12.4V at rest but fail under cranking load.',
      },
      {
        number: 11,
        title: 'Inspect Engine Bay for Leaks',
        description:
          'Use flashlight to inspect entire engine bay for fluid leaks. Look for: oil at valve cover (gasket leak), oil at front of engine (front main seal or timing cover), oil at rear of engine bay (rear main seal — visible at bell housing), coolant residue (white/green crust at hose clamps, water pump, thermostat housing), power steering fluid (red residue), brake fluid (clear, removes paint). Any wet area or fluid stain = active leak. Photograph for documentation.',
        why: 'Active leaks are reasons to negotiate price (purchase) or schedule repair (pre-trip).',
        tip: 'A spotless engine bay can hide leaks (recently steam-cleaned). Prefer a moderately dirty engine bay so leaks are visible.',
        commonMistake: 'Trusting a steam-cleaned engine bay. Common seller trick to hide leaks.',
      },

      // ── PHASE 3: TIRES AND WHEELS ─────────────────────────────────
      {
        number: 12,
        title: 'Tire Pressure and Tread Depth (QUICK)',
        description:
          'Check air pressure on all four tires plus spare. Spec is on driver door jamb sticker (typically 30 psi for stock TJ). Note any tire significantly lower than the others — slow leak. Use tread depth gauge (or US penny — Lincoln\'s head should not be visible above tread). Tread depth: 4/32" or more = good for trips; 3/32" = approaching replacement; 2/32" = legal minimum, replace immediately. Inspect each tire for: cupping (uneven dish-shaped wear — alignment or worn shocks), feathering (one edge worn — alignment issue), sidewall bulges (belt separation — replace immediately), cracks, embedded objects.',
        why: 'Tire condition is the #1 contributor to trip safety. Worn tires = poor handling, hydroplaning, blowout risk.',
        tip: 'For purchase: matched tires (same brand, age, tread depth on all 4) suggests care.',
        commonMistake: 'Eyeball estimate of tread. Use a gauge.',
      },
      {
        number: 13,
        title: 'Inspect Wheels and Lug Nuts',
        description:
          'Check each wheel: bent rim (visible wobble when spinning), curb damage, missing center caps. Use lug wrench to verify all lug nuts are tight (do not unscrew, just verify they do not move with hand pressure). On the spare tire (TJ rear-mount): same checks plus check the carrier mount for rust and bolt tightness. On the spare tire jack and tools: confirm presence and operability.',
        why: 'A loose lug, bent rim, or missing spare can ruin a trip.',
        tip: 'Listen for unusual sounds when spinning each wheel — bearing noise indicates failure.',
        commonMistake: 'Skipping spare tire inspection.',
      },

      // ── PHASE 4: BRAKES ───────────────────────────────────────────
      {
        number: 14,
        title: 'Inspect Front Brakes (Visible Through Wheel)',
        description:
          'Look through the wheel spokes at each front caliper. Inspect: caliper for fluid leaks (wet area on caliper body), pad thickness (look at the friction material in the caliper — should be 1/4" or thicker; thinner means due for replacement). Inspect rotor: should be smooth shiny metal with light wear lines. Deep grooves, blue heat spots, or rust pits indicate replacement needed. Check rotor edge — significant lip (more than 1/16") indicates rotor near minimum thickness. Test caliper sliding pins by pushing on caliper — it should rock slightly on its pins.',
        why: 'Front brakes do most of the stopping. Worn fronts = compromised emergency stopping.',
        tip: 'A flashlight pointing across the rotor reveals scoring better than direct light.',
        commonMistake: 'Visual inspection only without checking pad thickness through the caliper opening.',
      },
      {
        number: 15,
        title: 'Inspect Rear Brakes and Test Pedal Feel (QUICK)',
        description:
          'TJ rear brakes are typically drums (non-Rubicon). Visible inspection of drums shows their exterior condition. Look for excessive rust, cracks, or deep gouges on drum surface. Check parking brake cable and lever movement. Engine OFF, key OFF: press brake pedal firmly and hold for 30 seconds. Pedal should hold steady at the same height — slow sink indicates internal master cylinder leak. Engine ON: press pedal — should feel slightly softer (power assist) but still firm. Soft/spongy pedal indicates air in system or worn components. Pedal that goes to floor = serious safety issue.',
        why: 'Brake pedal hold test catches internal master cylinder failure that has no external sign.',
        tip: 'Pump brake hard 5 times with engine off, then test pedal with engine on — pedal should drop slightly when engine starts (power assist activated).',
        commonMistake: 'Skipping the pedal hold test.',
      },

      // ── PHASE 5: STEERING AND SUSPENSION ──────────────────────────
      {
        number: 16,
        title: 'Steering Component Inspection (Death Wobble Prevention)',
        description:
          'Have helper rock steering wheel side to side while you watch underneath. Check track bar bushings (axle and frame ends) for visible movement — any play means worn bushings. Check tie rod ends and drag link ends for play, torn boots, or grease leakage. Check ball joints by pushing on tire at 12 and 6 o\'clock — any vertical play indicates worn ball joints. Check steering stabilizer (damper) for fluid leaks. ANY play in these components on a TJ predicts death wobble.',
        why: 'Death wobble is the most-feared TJ issue. This inspection catches it before it starts.',
        tip: 'Push and pull on the track bar itself with steering held still — should be ZERO movement.',
        commonMistake: 'Skipping inspection because "steering feels OK." Wear progresses gradually.',
      },
      {
        number: 17,
        title: 'Suspension Components — Shocks, Springs, Wheel Bearings',
        description:
          'With wheels off ground if possible: check each shock absorber for fluid leaks (oily residue on shock body) — replace if leaking. Push down on each corner of the vehicle and release — should rebound once and settle. Multiple bounces = worn shocks. Inspect coil springs for cracks or sagging. Spin each front wheel by hand — should be silent. Grinding, growling, or roughness = wheel bearing failure (front hub assembly on TJ). Grab tire at 9 and 3 o\'clock with vehicle lifted and rock — play here = wheel bearing wear.',
        why: 'Worn shocks contribute to wobble and poor handling. Failing bearings are a tow-truck event.',
        tip: 'Bearing failure often produces a constant growl that gets louder at certain speeds.',
        commonMistake: 'Confusing ball joint play with bearing play. Vertical rock = ball joint, horizontal rock = bearing.',
      },

      // ── PHASE 6: DRIVETRAIN ───────────────────────────────────────
      {
        number: 18,
        title: 'Inspect U-Joints and Driveshafts',
        description:
          'TJ has front and rear driveshafts each with two U-joints (some have CV joints in front). Visually inspect each U-joint for: rust on the cap area (indicates seal failure), grease leakage (cap bearing failure), missing snap rings. Try to rotate each driveshaft by hand at the U-joint — should be smooth without clicks or binding. Check for play by attempting to wiggle the shaft against the joint — any play = U-joint replacement needed. Worn U-joints cause vibration and eventually break, dropping the driveshaft.',
        why: 'U-joints are wear items. Failed U-joints can damage transmission output, transfer case, and differential pinion.',
        tip: 'Rust at the U-joint cap is the earliest visible failure indicator.',
        commonMistake: 'Skipping U-joint inspection because "the driveshaft looks fine."',
      },
      {
        number: 19,
        title: 'Inspect Differential and Transfer Case Fluid Levels',
        description:
          'Front differential: locate fill plug on the differential housing (typically 3/8" square drive). Remove plug — fluid should be level with the bottom of the fill hole. If significantly low, system has a leak. Repeat for rear differential. Transfer case: locate fill plug on the case body — fluid should be at the fill hole edge. Inspect any fluid that comes out: clean amber/gold for diff fluid; clean red ATF+4 for transfer case. Milky fluid = water intrusion (off-road water crossings). Metallic content = wear.',
        why: 'Low differential or transfer case fluid leads to catastrophic component failure on a long drive.',
        tip: 'Have replacement crush washer or thread sealant ready if you remove fill plugs.',
        commonMistake: 'Forcing the fill plug. Apply penetrating oil first if rusted.',
      },
      {
        number: 20,
        title: 'Inspect Axle Vent Hoses (TJ-Specific)',
        description:
          'TJ front and rear axles have small vent hoses that allow pressure equalization without water intrusion. Originally short, these often need extensions for off-road use. Inspect: are vent hoses in place? Routed up away from water level? Clogged or pinched? Cracked? Without proper vents, pressure builds in axle housing and water gets sucked past seals during water crossings or even rain.',
        why: 'TJ-specific issue. Failed vents lead to water in differential — milky gear oil and bearing damage.',
        tip: 'Long axle vent hose extensions ($10-15) route the vent above water level and prevent the issue.',
        commonMistake: 'Skipping this check; many owners do not know about it.',
      },

      // ── PHASE 7: UNDERBODY AND FRAME ──────────────────────────────
      {
        number: 21,
        title: 'Frame Inspection — Rear Shackle Area (CRITICAL TJ ISSUE)',
        description:
          'TJ frames famously rust at the rear shackle mount area — known structural failure point. Lift vehicle on stands. Inspect both sides of the frame near the rear leaf spring shackle mounts. Look for: rust bubbles, soft metal when tapped with a hammer, visible holes or pitting, sagging at the rear shackle. Use a screwdriver to GENTLY tap and probe — soft metal should feel different from solid frame. Critical inspection point for any TJ in salt-belt areas. Severely rusted frames are a safety issue and often require frame replacement.',
        why: 'TJ frame rust at the rear shackle is a known safety issue. Salt-belt TJs often have it.',
        tip: 'Use a flashlight and inspect from multiple angles; rust often hides on the inside surface of the frame rail.',
        commonMistake: 'Trusting a black-painted frame. New paint over rust is a common seller trick.',
      },
      {
        number: 22,
        title: 'Inspect Brake Lines, Fuel Lines, and Exhaust',
        description:
          'Underbody inspection: trace each brake hard line and flexible hose along the chassis. Look for rust, kinks, or fluid weep. Replace any hose with cracks or swelling. Inspect fuel lines for similar issues. Inspect exhaust system: rust holes, broken hangers, loose clamps. Tap exhaust with screwdriver — solid sounds OK; tinny sounds indicate thinning metal. Look for catalytic converter — should be present and not heavily damaged.',
        why: 'Failed brake line = no brakes. Failed fuel line = fire risk. Failed exhaust = noise + possible CO leak into cabin.',
        tip: 'For purchase: missing catalytic converter is a $300+ replacement and a state emissions issue.',
        commonMistake: 'Visual inspection only. Tap exhaust to find thin/rusted sections.',
      },
      {
        number: 23,
        title: 'Inspect Body Mounts and Skid Plates',
        description:
          'TJ body mounts (7 total) hold the body to the frame. Inspect each: cracked rubber, crushed/compressed rubber, or rusted metal cup. Skid plates (transmission/transfer case skid, gas tank skid): check mounting bolts, cracks, holes. Inspect rocker panels and floor edges for rust through. Check the inside of the rear wheel wells for visible rust on body or frame.',
        why: 'Failed body mounts cause rattles. Damaged skid plates expose vital components.',
        tip: 'A rusted-through floor pan is a serious project; price accordingly.',
        commonMistake: 'Skipping floor pan inspection. Carpet hides serious rust.',
      },

      // ── PHASE 8: LIGHTS, ELECTRICAL, INTERIOR ─────────────────────
      {
        number: 24,
        title: 'Test All Lights and Wipers (QUICK)',
        description:
          'With key in RUN: test headlights (low and high beam), parking lights, fog lights if equipped. Brake lights: have helper press pedal while you walk around. Turn signals: front and rear, both sides. Hazard lights. Reverse lights: with engine off, key on, shift to reverse. License plate light. Interior dome light. Wipers: low, high, intermittent settings, washer fluid spray. Horn. All gauge cluster warning lights should illuminate at key-on then extinguish when engine starts.',
        why: 'Failed lights are a stop ticket and a safety issue. Burned-out brake lights are a common failure.',
        tip: 'Bring a friend for brake light test — much faster than getting in/out.',
        commonMistake: 'Skipping reverse and license plate lights. Often missed in basic inspections.',
      },
      {
        number: 25,
        title: 'Interior Inspection and HVAC Test',
        description:
          'Test all interior controls: A/C (cool air at vent within 3-5 minutes), heat (warm air after engine warms), all blower speeds, mode controls (vent/floor/defrost), recirculate. Test all power accessories (windows if power, mirrors, radio, accessory ports). Inspect seat belts: each retracts and locks under jerk. Inspect dashboard for cracks (TJ dash is known to crack from sun exposure). Check for mildew or wet smell — indicates leak through soft top, hardtop, or door seals. Inspect floor mats and under them for water damage.',
        why: 'Interior issues range from cosmetic (cracked dash) to safety (failed seat belt).',
        tip: 'Wet floor under passenger side = heater core leak; under driver side = windshield seal failure.',
        commonMistake: 'Skipping seat belt jerk test. Failed retractor or locking mechanism is a safety issue.',
      },

      // ── PHASE 9: TEST DRIVE ───────────────────────────────────────
      {
        number: 26,
        title: 'Active Test Drive — 15-30 Minutes',
        description:
          'Drive the vehicle 15-30 minutes through a mix of conditions: city, highway, hills if possible. Note: cold start observations (already done in step 2). Engine response under acceleration — should be smooth, no hesitation, no surge. Transmission shifts (auto): smooth, prompt, no flare or harsh engagement. Manual transmission: clutch engagement smooth, no slip under load, no grinding into gears. Brakes: stops straight without pulling, no pulsation through pedal. Steering: tracks straight at highway speed, returns to center, no wandering, no death wobble over bumps. Listen for: clunks, rattles, vibration, growling. Hit a few expansion joints at highway speed to test wobble susceptibility. Try to find a parking lot with bumpy gravel or uneven surface — many issues only show on rough terrain.',
        why: 'Test drive reveals dynamic issues (transmission slip, alignment pull, wheel bearing growl) that static inspection misses.',
        tip: 'For purchase: drive both empty and (ask permission) loaded. Towing capability stresses different components.',
        commonMistake: 'Short test drive. 5 minutes around the block does not stress the vehicle enough to reveal issues.',
      },
      {
        number: 27,
        title: 'OBD-II Code Scan and Verification',
        description:
          'Plug OBD-II scanner into port (under driver dash near steering column). Read all codes — both stored and pending. Note ANY codes. Common TJ codes: P0335 (CKP), P0420 (catalyst), P0171 (lean), P0301-P0306 (specific cylinder misfire). Pending codes are early warnings. Cleared codes can mean issues that the seller cleared just before showing — note that scanner data shows when codes were last cleared (number of drive cycles since reset). 0 drive cycles since reset on a "no codes" scan is suspicious. Verify the dashboard does not show CHECK ENGINE, ABS warning, or other warning lights.',
        why: 'Computer codes reveal issues even when symptoms are subtle. Recently-cleared codes are a red flag for hidden problems.',
        tip: 'Free code reads at most chain auto-parts stores if you do not own a scanner.',
        commonMistake: 'Trusting "no codes" without checking when codes were last reset.',
      },
      {
        number: 28,
        title: 'Compile Findings and Make Decision',
        description:
          'Go through your notes and categorize findings by severity: SAFETY (must fix immediately — bad brakes, frame rust, severe steering play), MAINTENANCE (due/overdue services — fluids, filters, brakes nearing wear limit), COSMETIC (paint, dashboard cracks, soft top wear), CONCERN (minor issues to monitor). For pre-trip: address all SAFETY items before departing; address MAINTENANCE items if time permits. For pre-purchase: total estimated repair cost = negotiation leverage. Compare against asking price. A "great deal" with $3,000 of immediate repairs is a poor deal. For pre-purchase: walk away from vehicles with unresolved SAFETY findings.',
        why: 'Inspection is only useful if you act on findings. Document, prioritize, and execute.',
        tip: 'For pre-purchase: a Carfax + this inspection + a final $100 mechanic verification = strong confidence in purchase decision.',
        commonMistake: 'Doing the inspection but ignoring concerning findings because you "want this vehicle."',
      },
    ],
    torqueSpecifications: [
      { fastener: 'Lug nuts (verification only — should be at)', specification: '95 ft-lbs (129 Nm).', notes: 'No torque application during inspection. Visual/hand check only.' },
      { fastener: 'Differential fill plugs (when removed for level check)', specification: '24-30 ft-lbs (33-40 Nm).', notes: 'Reinstall after level check.' },
    ],
    fluidSpecifications: [
      { fluid: 'Engine oil', type: '5W-30 or 10W-30 (4.0L); 5W-30 (2.4L)', capacity: '6 qt (4.0L) / 4.5 qt (2.4L)', notes: 'Top-off only during inspection.' },
      { fluid: 'Coolant', type: 'HOAT (Mopar 5012394AB)', capacity: '~10 qt total system', notes: 'Top-off only.' },
      { fluid: 'Brake fluid', type: 'DOT 3', capacity: 'N/A (top-off only)', notes: 'Use sealed bottle.' },
      { fluid: 'Power steering fluid', type: 'ATF+4 (NOT generic PSF)', capacity: '~1.5 qt', notes: 'Match OEM spec.' },
      { fluid: 'Differential fluid', type: '75W-90 GL-5', capacity: '~1-2.5 qt depending on diff', notes: 'Top-off only.' },
      { fluid: 'Transfer case fluid', type: 'ATF+4', capacity: '~1.25-1.6 qt', notes: 'Top-off only.' },
    ],
    troubleshooting: [
      { problem: 'Multiple safety findings on used purchase', likelyCause: 'Vehicle has been neglected', whatToCheck: 'Total repair estimate vs vehicle value', fix: 'Walk away or negotiate aggressively. Some "deals" are not.' },
      { problem: 'Cold start reveals white smoke that does not clear', likelyCause: 'Head gasket failure or cracked head', whatToCheck: 'Combustion gas test on cooling system', fix: 'Major repair — factor into pricing or pass.' },
      { problem: 'Frame rust at rear shackle', likelyCause: 'Salt-belt vehicle without rust prevention', whatToCheck: 'Tap test for severity; visible holes', fix: 'Frame patch repair or frame replacement. Estimate $500-2,000+ professional.' },
      { problem: 'Death wobble during test drive', likelyCause: 'Multiple worn front-end components', whatToCheck: 'See Death Wobble Diagnostic guide', fix: 'Track bar, ball joints, tie rod ends — full front-end refresh typically $400-800 in parts.' },
      { problem: 'Transmission flares or slips on test drive', likelyCause: 'Internal transmission wear', whatToCheck: 'ATF condition (burnt smell)', fix: 'Major repair. Walk away on used purchase unless price reflects.' },
      { problem: 'Multiple OBD-II codes after recent reset', likelyCause: 'Seller cleared codes to hide issues', whatToCheck: 'Drive cycles since last reset', fix: 'Walk away; recently-cleared codes are a red flag.' },
      { problem: 'Brake pedal sinks during 30-second hold', likelyCause: 'Internal master cylinder leak', whatToCheck: 'Visible reservoir level vs slow sink', fix: 'Master cylinder replacement (~$90 part, see Brake Master Cylinder guide).' },
      { problem: 'Coolant looks like chocolate milk', likelyCause: 'Transmission cooler in radiator has failed (auto trans only)', whatToCheck: 'ATF should match — check both', fix: 'Major issue. Both radiator AND transmission affected. Walk away.' },
      { problem: 'Heavy oil on rear of engine bay', likelyCause: 'Rear main seal leak', whatToCheck: 'Bell housing area for fluid', fix: 'Rear main seal replacement requires transmission removal — major job.' },
    ],
    proTips: [
      'For pre-purchase: spend $100 at an independent mechanic for a final inspection. They have a lift and can see things you cannot.',
      'Carfax/AutoCheck is worth $30 — reveals accident history, title issues, and odometer rollback.',
      'A folder of service records is the single best indicator of vehicle care.',
      'For pre-trip: do this inspection 1-2 weeks before departure, not the night before. Catches issues with time to repair.',
      'Take photos and video as you inspect — useful for documentation, second opinions, and negotiation.',
      'For TJs in particular: frame condition, head gasket history, and steering component wear are the three top-cost surprise issues.',
      'Check NHTSA recall database (free at nhtsa.gov) — there are known TJ recalls (frame rust on some years).',
      'Wear an old shirt and bring a creeper — you will be on your back under the vehicle for parts of this inspection.',
    ],
    commonMistakes: [
      { mistake: 'Inspecting a warm vehicle (pre-purchase)', consequence: 'Misses cold-start issues that sellers know to hide.' },
      { mistake: 'Not lifting the vehicle for underbody inspection', consequence: 'Misses frame rust, leaks, and suspension wear.' },
      { mistake: 'Skipping test drive on rough roads', consequence: 'Misses death wobble, suspension issues, and alignment.' },
      { mistake: 'Trusting "no codes" without checking when last reset', consequence: 'Recently-cleared codes hide ongoing issues.' },
      { mistake: 'Buying a TJ with frame rust to "fix later"', consequence: 'Frame work is expensive and structural; "later" usually means $2,000+ surprise.' },
      { mistake: 'Skipping the OBD-II scan', consequence: 'Misses sensor and emissions issues that fail state inspection.' },
    ],
    successIndicators: [
      'All fluids at correct level and color',
      'Tire pressure spec on all 4 + spare; tread depth 4/32" or more',
      'Brake pedal firm; holds 30+ seconds under sustained pressure',
      'No visible fluid leaks anywhere',
      'No play in steering components; no death wobble on test drive',
      'No wheel bearing growl, no driveline vibration',
      'Frame solid to tap test, especially at rear shackle area',
      'All lights functional; all interior systems operational',
      'No OBD-II codes (or only codes you understand and accept)',
      'Cold start in 1-2 cranks with smooth idle within 30 seconds',
      'Test drive: smooth shifts, even braking, tracks straight, no warning lights',
    ],
    whenToSeekHelp:
      'For pre-purchase decisions, an independent mechanic\'s pre-purchase inspection ($75-150) provides a professional second opinion and access to a lift for thorough underbody inspection. For pre-trip inspections that reveal multiple findings, a shop can address them efficiently with proper tools. For frame rust assessment specifically, a body shop or frame specialist can give you a repair vs replace estimate. Never drive a vehicle with confirmed safety issues — towing to a shop is cheaper than the consequences of a failure on the highway.',
    legalDisclaimer: 'This guide was generated by GarageIQ\'s AI from publicly available sources and is for informational purposes only. A pre-purchase inspection by a qualified mechanic is recommended before any vehicle purchase. This guide is not a substitute for professional inspection, especially for safety-critical findings. The Haynes Manual tab on this page links to the licensed manual for your vehicle. GarageIQ is not responsible for purchase decisions or trip-readiness assessments based on this guide.',
    manualSource: {
      title: 'Haynes Repair Manual — Jeep Wrangler 1987-2017 (Haynes 50030)',
      url: 'https://www.haynes.com/en-us/wrangler/jeep/2003',
      notes: 'Cross-reference inspection points with your licensed manual. NHTSA.gov has recall information specific to TJ years.',
    },
  },
];

export const tjGuidesAll = [...tjGuidesP1, ...tjGuidesP2, ...tjGuidesP3];
